// ═══════════════════════════════════════════════════════════════════
// THE MESSAGE CATALOGS (#310a).
//
// Norwegian bokmål is the SOURCE catalog and the fallback. That is not a
// default anyone has to remember: `nb` defines the key type, so a key that
// does not exist in Norwegian does not exist at all, and every other catalog
// is checked against it by the compiler.
//
// KEYS ARE NAMESPACED BY SURFACE (`auth.login.title`), not by English text.
// A key named after its English wording has to be renamed the moment the
// wording changes, and a key named `save` collides the moment two surfaces
// disagree about what saving means.
//
// WHICH ENGLISH WORD: docs/architecture/I18N_GLOSSARY.md. It settles the terms
// that recur — `kamp` is a MATCH and never a «game», because a game is part of
// a set; `seriespill` is a LEAGUE; «KM» must not survive into English, where it
// reads as kilometres. Read it before adding a key, not after a reviewer asks.
//
// WHAT DOES NOT BELONG HERE: anything the API produces. Error CODES cross the
// wire untranslated and the UI maps them to a message (`errors.*` below), so
// the server never has to know which language a browser is in, and a
// translated string is never persisted as business data.
// ═══════════════════════════════════════════════════════════════════

export const nb = {
  // ── «Serveren svarer ikke» (#537) ─────────────────────────────────
  'serverDown.title': 'Serveren svarer ikke akkurat nå.',
  'serverDown.body':
    'Den starter sannsynligvis på nytt etter en oppdatering. Vent litt — ikke last siden på nytt, da mister du det du holder på med.',
  'serverDown.doNotReload': 'ikke last siden på nytt',
  // ── «Ny versjon tilgjengelig» (#537) ──────────────────────────────
  'newBuild.available': 'En ny versjon av appen er lagt ut. Last inn på nytt når det passer deg.',
  'newBuild.reload': 'Last inn på nytt',
  'newBuild.refusedRead':
    'Denne versjonen av appen kan ikke lenger hente kalenderen og listene. Last inn på nytt for å se dem.',
  // ── «Noe kunne ikke hentes» (#1247) ───────────────────────────────
  'listLoad.title': 'Noe av klubbens data kunne ikke hentes.',
  'listLoad.body':
    'Disse kan se tomme ut uten å være det: {lists}. Last inn siden på nytt når serveren svarer.',
  'listLoad.bookings': 'bookinger',
  'listLoad.activities': 'aktiviteter',
  'listLoad.singleMatches': 'enkeltkamper',
  'listLoad.plannedMatches': 'planlagte kamper',
  // ── /sa «Min konto» — the staff password form (#383 surface) ──────
  //
  // ON THE CATALOG EVEN THOUGH /sa IS NOT MIGRATED. The surrounding
  // `SuperadminRoute.tsx` is on the frozen legacy list, but these keys belong
  // to a NEW file — and the rule is that new text goes through the catalog.
  // Adding a new file to a list that is meant to shrink would be the wrong
  // direction for the sake of eleven strings.
  // ── /sa → Rettigheter: who a custom role is offered to (#535) ─────
  //
  // BORN ON THE CATALOG, for the reason `saAccount` records above: this is a
  // NEW file, and adding one to a list that is meant to shrink would be the
  // wrong direction. The page around it is still unmigrated, and that is fine —
  // the legacy list is a frozen set of files, not a property of the screen.
  'saScope.title': 'Tilgjengelig for',
  'saScope.platform': 'Hele plattformen',
  'saScope.selected': 'Valgte organisasjoner',
  'saScope.search': 'Søk organisasjon',
  'saScope.add': 'Legg til',
  'saScope.addCount': 'Legg til {count}',
  'saScope.staged': '{count} valgt, ikke lagt til ennå',
  'saScope.allAdded': 'Alle aktuelle organisasjoner er lagt til.',
  'saScope.noMatch': 'Ingen treff.',
  'saScope.centre': 'senter',
  'saScope.club': 'klubb',
  'saScope.none': 'Ingen valgt — rollen kan ikke tildeles noe sted.',
  'saScope.remove': 'Fjern {name}',
  'saScope.save': 'Lagre tilgjengelighet',
  'saScope.pendingPlatform': 'Blir tilgjengelig for alle organisasjoner.',
  'saScope.pendingSelected': '{count} organisasjon(er) etter lagring.',
  // ── /sa → Plattformbrukere: the Entra-first step guide, and who holds which role (#975) ──
  'saUsers.title': 'Plattformbrukere',
  'saUsers.lead':
    'Microsoft Entra ID avgjør hvem som kan logge inn som StarPoint-ansatt. Her avgjør du hva de får gjøre: en plattformrolle gis til en konto som allerede har logget inn. Rollene gir tilgang i dette panelet — ikke i klubbportalen. Kundekontoer ligger under klubben sin i «Organisasjoner»; søk her for å finne en av dem.',
  'saUsers.guide.title': 'Gi tilgang til /sa',
  'saUsers.guide.lead':
    'Tre steg, i denne rekkefølgen. Det første gjøres i Microsoft Entra, det siste her.',
  'saUsers.guide.step1.title': 'Gi personen tilgang i Microsoft Entra ID',
  'saUsers.guide.step1.body':
    'Tildel personen StarPoints app for ansatte i Microsoft Entra: Enterprise-programmer → appen → Brukere og grupper → Legg til. Entra bekrefter hvem personen er, med Microsoft-pålogging og MFA. Dette steget kan ikke gjøres fra StarPoint.',
  'saUsers.guide.step1.link': 'Åpne Microsoft Entra administrasjonssenter ↗',
  'saUsers.guide.step2.title': 'Personen logger inn på /sa med Microsoft én gang',
  'saUsers.guide.step2.body':
    'Den første innloggingen oppretter kontoen her. Disse har logget inn med Microsoft, men har ennå ingen plattformrolle:',
  'saUsers.guide.step2.empty':
    'Ingen venter på en rolle. Når personen har logget inn, dukker hen opp her.',
  'saUsers.guide.step2.refresh': 'Oppdater',
  'saUsers.guide.step2.signedIn': 'logget inn {date}',
  'saUsers.guide.step3.title': 'Gi en plattformrolle',
  'saUsers.guide.step3.body':
    'Velg personen og rollen. Rollen kan endres eller fjernes i listen under.',
  'saUsers.guide.step3.person': 'Person',
  'saUsers.guide.step3.choosePerson': 'Velg person …',
  'saUsers.guide.step3.role': 'Rolle',
  'saUsers.guide.step3.chooseRole': 'Velg rolle …',
  'saUsers.guide.step3.submit': 'Gi rollen',
  'saUsers.guide.step3.nobody': 'Ingen å velge ennå — se steg 2.',
  'saUsers.guide.step3.readOnly': 'Du kan se hvem som venter, men ikke gi roller.',
  'saUsers.directory.title': 'Hvem har hvilken plattformrolle',
  'saUsers.staff.title': 'Logger inn med Microsoft',
  'saUsers.staff.lead':
    'StarPoint-ansatte med en plattformrolle. Trykk på en rolle for å gi eller fjerne den.',
  'saUsers.staff.empty': 'Ingen som logger inn med Microsoft har en plattformrolle ennå.',
  'saUsers.local.title': 'Kontoer som ikke logger inn med Microsoft',
  'saUsers.local.lead':
    'Disse har en plattformrolle, men ingen Microsoft-pålogging — for eksempel gamle demokontoer og utviklerinngangen på dev. Nye ansatte legges ikke til her. Fjern rollene som ikke lenger skal være der.',
  'saUsers.local.empty': 'Ingen — alle med en plattformrolle logger inn med Microsoft.',
  'saUsers.local.noRole': 'Ingen plattformrolle.',
  'saUsers.local.revoke': 'Fjern',
  'saUsers.local.revokeLabel': 'Fjern {role} fra {name}',
  'saUsers.granted': '{name} har fått rollen {role}.',
  'saUsers.noEntraIdentity':
    '{name} har ingen Microsoft-konto i Starpoint, så en plattformrolle ville aldri virke. Personen må logge inn i /sa med Microsoft først.',
  'saUsers.revokedLast': '{name} har ikke lenger noen plattformrolle, og står nå under steg 2.',
  'saUsers.refused': 'Serveren avslo endringen.',
  'saUsers.roleFailed': 'Kunne ikke endre rollen.',
  'saUsers.readOnly':
    'Du har lesetilgang. Å gi og fjerne plattformroller krever rettigheten til å administrere roller.',
  'saUsers.status.disabled': 'Kontoen er deaktivert. {count} pålogging(er) ble avsluttet.',
  'saUsers.status.enabled': 'Kontoen er aktivert igjen.',
  'saUsers.status.failed': 'Kunne ikke endre kontoens status.',
  'saUsers.search.placeholder': 'Søk på navn eller e-post',
  'saUsers.search.busy': 'Søker…',
  'saUsers.search.submit': 'Søk',
  'saUsers.search.clear': 'Tilbake til oversikten',
  'saUsers.search.scope':
    'Søket omfatter også kundekontoer. «Tilbake til oversikten» viser bare StarPoints egne folk.',
  'saUsers.truncated': 'Viser de første treffene — søk for å snevre inn.',
  'saUsers.state.noApi': 'API-et er ikke koblet til i denne versjonen ennå.',
  'saUsers.state.forbidden': 'Du har ikke tilgang til plattformbrukere.',
  'saUsers.state.error': 'Kunne ikke hente brukere.',
  'saUsers.state.errorStatus': 'Kunne ikke hente brukere (HTTP {status}).',
  'saUsers.state.empty': 'Ingen kontoer matchet.',
  'saUsers.customer': 'Kundekonto',
  'saUsers.noName': '(uten navn)',
  'saUsers.noSignIn': 'ingen innlogging',
  'saUsers.clubs': '{count} klubb(er)',
  'saUsers.provider.ENTRA': 'Microsoft',
  'saUsers.provider.LOCAL': 'E-post',
  'saUsers.account.enable': 'Aktiver kontoen',
  'saUsers.account.disable': 'Deaktiver kontoen',
  'saUsers.account.disableConfirm': 'Bekreft: deaktiver',
  'saUsers.account.cancel': 'Avbryt',
  'saUsers.account.disableHint': 'Avslutter alle påloggingene til kontoen med én gang.',
  // ── CLOSING AN ACCOUNT ON THE PERSON'S BEHALF (#1062) ────────────
  'saUsers.account.closed': 'Kontoen er avsluttet',
  'saUsers.account.close': 'Avslutt kontoen …',
  'saUsers.account.closeReason': 'Årsak',
  'saUsers.account.closeReason.owner_request': 'Personen ba om det',
  'saUsers.account.closeReason.compromised': 'Kontoen er kompromittert',
  'saUsers.account.closeReason.deceased': 'Personen er død',
  'saUsers.account.closeReference': 'Saksnummer',
  'saUsers.account.closeReferenceHint':
    'Bare bokstaver, tall og ._:#/- — ikke navn eller e-postadresse.',
  'saUsers.account.closeConfirm': 'Bekreft: avslutt kontoen',
  'saUsers.account.closeHint':
    'Kontoen kan ikke logge inn igjen. Alt annet beholdes. Kan ikke angres her.',
  'saUsers.status.closedNow': 'Kontoen er avsluttet. {count} pålogging(er) ble avsluttet.',
  'saUsers.status.closeInvalid': 'Velg en årsak og skriv et gyldig saksnummer.',
  // ── /sa for a signed-out visitor with no dev door (#789) ──────────
  //
  // NEUTRAL ON PURPOSE: it names no panel and lists nothing, because the
  // person reading it has not signed in. Born on the catalog like `saScope`.
  'saEntry.title': 'Logg inn',
  'saEntry.checking': 'Sjekker innlogging …',
  'saEntry.redirecting': 'Sender deg til Microsoft-innlogging …',
  'saEntry.notCompleted': 'Innloggingen ble ikke fullført.',
  'saEntry.signIn': 'Logg inn med Microsoft',
  'saEntry.unavailable': 'Innlogging er ikke tilgjengelig her.',
  // ── /sa on a session the platform does not honour (#1030) ─────────
  //
  // The API answers `403 entra_required` when platform authority is asked of
  // a session that Microsoft did not prove — an e-mailed code or a passkey.
  // The code is the server's; this is the sentence.
  'saEntraRequired.title': 'Logg inn med Microsoft',
  'saEntraRequired.body':
    'Du er logget inn med e-post eller passnøkkel. Plattformtilgang i Starpoint krever innlogging med Microsoft-kontoen din.',
  'saEntraRequired.signIn': 'Logg inn med Microsoft',
  'saEntraRequired.unavailable': 'Microsoft-innlogging er ikke tilgjengelig i dette miljøet.',
  // ── /sa → Organisasjoner → Inviter ny bruker (#1009) ──────────────
  //
  // Passwords were retired in #754: the invitation tells the person to sign in
  // with the address and a one-time code. These replace three sentences that
  // still promised a password link.
  'saOrgInvite.lead':
    'Personen får en e-post og logger inn med e-postadressen sin og en engangskode.',
  'saOrgInvite.createdMailed':
    'Konto opprettet. Invitasjonen er sendt — personen logger inn med e-postadressen og en engangskode.',
  'saOrgInvite.createdNotMailed':
    'Konto opprettet. Ingen e-post er sendt fra dette miljøet — gi personen beskjed selv. De logger inn med e-postadressen sin og en engangskode.',
  // ── /sa → Spillere → Ratingkorrigering (#793) ─────────────────────
  //
  // BORN ON THE CATALOG, like `saScope` above. The correction is platform
  // authority (#197): one shared rating, so a change here is a change in every
  // club — and the copy says so, because that is what the button costs.
  // ── /sa → Rettigheter → Oversikt (owner decisions 1A–3A, 2026-10-19) ──
  //
  // Roles as rows, FAGOMRÅDER as columns, the LEVEL in each cell. A level is a
  // name for a set of rights; «Tilpasset» means the role holds a set no level
  // names. Level names are generic on purpose — the area is in the column.
  'saAreas.role': 'Rolle',
  'saAreas.none': 'Ingen',
  'saAreas.custom': 'Tilpasset ({held} av {total})',
  'saAreas.unsaved': 'Ikke lagret',
  'saAreas.cellTitle': '{role}: {area}',
  'saAreas.area.organization': 'Organisasjonen',
  'saAreas.area.court': 'Baner og anlegg',
  'saAreas.area.booking': 'Booking',
  'saAreas.area.activity': 'Aktiviteter',
  'saAreas.area.tournament': 'Turneringer',
  'saAreas.area.member': 'Medlemmer',
  'saAreas.area.messaging': 'Kommunikasjon',
  'saAreas.area.finance': 'Økonomi',
  'saAreas.area.other': 'Annet',
  'saAreas.level.read': 'Lesetilgang',
  'saAreas.level.user': 'Bruker',
  'saAreas.level.daily': 'Daglig drift',
  'saAreas.level.operate': 'Drift',
  'saAreas.level.rules': 'Regler og priser',
  'saAreas.level.results': 'Resultatfører',
  'saAreas.level.lead': 'Turneringsleder',
  'saAreas.level.responsible': 'Ansvarlig',
  'saAreas.level.admin': 'Administrator',
  'saAreas.level.owner': 'Eier',
  'saAreas.panel.title': '{role} — {area}',
  'saAreas.panel.close': 'Lukk',
  'saAreas.panel.level': 'Nivå',
  'saAreas.panel.rights': 'Nøyaktig hva det gir',
  'saAreas.panel.save': 'Lagre',
  'saAreas.panel.saving': 'Lagrer …',
  'saAreas.panel.pending': '{added} lagt til, {removed} fjernet — ikke lagret ennå',
  'saAreas.panel.readonly': 'Du har lesetilgang. Kun en global administrator kan endre rollene.',
  // ── one page, no tabs (owner 1A/2A/3A, 2026-10-20) ──
  'saAreas.view.toDetail': 'Vis den gamle tabellen med faner',
  'saAreas.view.toOverview': '← Tilbake til oversikten',
  'saAreas.search': 'Søk etter rolle',
  'saAreas.search.none': 'Ingen roller passer søket.',
  'saAreas.legend': 'Nivå: {one} lavest · {two} middels · {three} høyest · – ingen',
  'saAreas.group.both': 'For både senter og klubb',
  'saAreas.group.both.lead': 'Gis i begge typer organisasjoner.',
  'saAreas.group.centre': 'Bare for senter',
  'saAreas.group.centre.lead': 'Kan bare gis hos et senter.',
  'saAreas.group.club': 'Bare for klubb',
  'saAreas.group.club.lead': 'Kan bare gis i en klubb.',
  'saAreas.group.empty': 'Ingen roller her ennå.',
  'saAreas.role.open': 'Vis alle rettighetene til {role}',
  'saAreas.role.allRights': 'Alle rettighetene til {role}',
  'saAreas.role.custom': 'Egen rolle',
  'saAreas.section.organizations': 'Roller i sentre og klubber',
  'saAreas.section.organizations.lead':
    'Hver rolle per fagområde. Klikk på en rute for å endre ett område, eller på rollens navn for alt.',
  'saAreas.section.platform': 'Starpoint-roller',
  'saAreas.section.platform.lead': 'Roller for Starpoints egne folk i dette panelet.',
  'saAreas.section.link': 'Samarbeid',
  'saAreas.section.link.lead': 'Det en kobling gir den ene organisasjonen lov til hos den andre.',
  'saAreas.section.empty': 'Ingen roller her ennå. Lag en med «Ny rolle».',
  // ── /sa → Revisjon (#1041) ──
  'saAudit.menuGroup': 'Revisjon',
  'saAudit.menu': 'Revisjonslogg',
  'saAudit.title': 'Revisjonslogg',
  'saAudit.lead':
    'Hvem gjorde hva, når og for hvilken organisasjon — på tvers av hele plattformen. Bare lesing: ingenting her kan endres.',
  'saAudit.logged': 'Hvert søk loggføres selv, med filtrene du brukte.',
  'saAudit.filter.organization': 'Organisasjon',
  'saAudit.filter.actor': 'Aktør',
  'saAudit.filter.action': 'Handling',
  'saAudit.filter.from': 'Fra',
  'saAudit.filter.to': 'Til',
  'saAudit.filter.impersonation': 'Impersoneringsøkt',
  'saAudit.filter.subject': 'Gjelder (ID)',
  'saAudit.filter.subjectType': 'Gjelder (type)',
  'saAudit.hint.id': 'En ID, for eksempel fra en rad nedenfor.',
  'saAudit.hint.actor': 'Konto-ID — eller spiller-, enhets-ID.',
  'saAudit.hint.action':
    'Nøyaktig, eller alt som starter med noe: avslutt med punktum eller stjerne, som organization_role.*',
  'saAudit.hint.to': 'Til, ikke med.',
  'saAudit.hint.subjectType': 'For eksempel organization eller user.',
  'saAudit.search': 'Søk',
  'saAudit.searching': 'Søker …',
  'saAudit.clear': 'Tøm filtrene',
  'saAudit.idle': 'Velg filtre og trykk Søk. Uten filtre får du de nyeste hendelsene.',
  'saAudit.empty': 'Ingen hendelser passer filtrene.',
  'saAudit.count': '{count} hendelser vist, nyeste først.',
  'saAudit.more': 'Vis flere',
  'saAudit.table.caption': 'Hendelser i revisjonsloggen, nyeste først',
  'saAudit.col.when': 'Tidspunkt',
  'saAudit.col.who': 'Hvem',
  'saAudit.col.what': 'Hva',
  'saAudit.col.organization': 'Organisasjon',
  'saAudit.col.assurance': 'Sikkerhetsnivå',
  'saAudit.col.impersonation': 'Impersonering',
  'saAudit.col.detail': 'Detaljer, som lagret',
  'saAudit.subject': 'Gjelder {type}: {id}',
  'saAudit.platformWide': 'Plattformen',
  'saAudit.none': '—',
  'saAudit.showContext': 'Vis alt i økten',
  'saAudit.showContext.label': 'Vis alt som ble gjort i impersoneringsøkten {id}',
  'saAudit.actorType.MEMBER': 'Medlem',
  'saAudit.actorType.CLUB_USER': 'Klubbruker',
  'saAudit.actorType.DEVICE': 'Baneskjerm',
  'saAudit.actorType.PLATFORM': 'Starpoint',
  'saAudit.actorType.OPERATOR': 'Operatør (skript)',
  'saAudit.actorType.PROVIDER': 'Betalingsleverandør',
  'saAudit.actorType.SYSTEM': 'Systemet',
  'saAudit.error.invalidFilter': 'Filteret er ikke gyldig. Rett det og søk igjen.',
  'saAudit.error.forbidden': 'Du har ikke tilgang til revisjonsloggen.',
  'saAudit.error.entraRequired':
    'Revisjonsloggen krever innlogging med Microsoft (Entra). Logg inn på nytt.',
  'saAudit.error.failed': 'Klarte ikke å hente revisjonsloggen. Prøv igjen.',
  'saAudit.error.noApi': 'API-et er ikke koblet til i denne versjonen.',
  // ── /sa «Betalingsavvik» (#1131) ──────────────────────────────────
  'saPayEx.menuGroup': 'Betalinger',
  'saPayEx.menu': 'Betalingsavvik',
  'saPayEx.title': 'Betalingsavvik',
  'saPayEx.lead':
    'Betalinger systemet ikke kunne gjøre opp selv, og hendelser fra betalingsleverandøren som ikke passet noen betaling. Bare lesing: hva som skal gjøres med hver rad, står i rutinen for betalingsavvik. Hver visning logges.',
  'saPayEx.read': 'Hent listen',
  'saPayEx.refresh': 'Oppdater',
  'saPayEx.loading': 'Henter …',
  'saPayEx.payments.heading': 'Betalinger med avvikskode ({count})',
  'saPayEx.payments.caption': 'Betalinger med avvikskode, de mest hastende først',
  'saPayEx.payments.empty': 'Ingen betalinger har en avvikskode.',
  'saPayEx.unmatched.heading': 'Hendelser uten betaling ({count})',
  'saPayEx.unmatched.caption': 'Hendelser fra betalingsleverandøren som ikke passet noen betaling',
  'saPayEx.unmatched.empty': 'Ingen hendelser står uten betaling.',
  'saPayEx.truncated':
    'Viser de {count} første. Det finnes flere — hele listen står i rutinens SQL.',
  'saPayEx.level.0': 'Løst / info',
  'saPayEx.level.1': 'Venter på leverandøren',
  'saPayEx.level.2': 'Venter på en beslutning',
  'saPayEx.level.3': 'Krever handling',
  'saPayEx.level.unknown': 'Ukjent — krever handling',
  'saPayEx.col.level': 'Vurdering',
  'saPayEx.col.organization': 'Organisasjon',
  'saPayEx.col.reason': 'Avvikskode',
  'saPayEx.col.state': 'Status',
  'saPayEx.col.amount': 'Beløp',
  'saPayEx.col.booking': 'Booking',
  'saPayEx.col.updated': 'Sist endret',
  'saPayEx.col.event': 'Hendelse',
  'saPayEx.col.provider': 'Leverandør',
  'saPayEx.col.process': 'Prosess',
  'saPayEx.col.deliveries': 'Leveranser',
  'saPayEx.col.firstSeen': 'Først sett',
  'saPayEx.col.lastSeen': 'Sist sett',
  'saPayEx.booking.gone': 'Bookingen er slettet',
  'saPayEx.error.forbidden': 'Du har ikke tilgang til betalingsavvikene.',
  'saPayEx.error.entraRequired':
    'Betalingsavvikene krever innlogging med Microsoft (Entra). Logg inn på nytt.',
  'saPayEx.error.failed': 'Klarte ikke å hente betalingsavvikene. Prøv igjen.',
  'saPayEx.error.noApi': 'API-et er ikke koblet til i denne versjonen.',
  'saReports.menuGroup': 'Rapporter',
  'saReports.menu': 'Plattformtall',
  'saReports.title': 'Plattformtall',
  'saReports.lead':
    'Summer for hele plattformen per måned eller per organisasjon: organisasjoner, bookinger, aktiviteter, innlogginger og kortbetalinger. Bare tall, aldri personer.',
  'saReports.logged': 'Hver visning loggføres, med perioden du valgte.',
  'saReports.idle': 'Velg en periode og trykk «Vis rapport».',
  'saReports.period': 'Periode',
  'saReports.mode.month': 'Måned',
  'saReports.mode.quarter': 'Kvartal',
  'saReports.mode.custom': 'Egendefinert',
  'saReports.month': 'Måned',
  'saReports.year': 'År',
  'saReports.quarter': 'Kvartal',
  'saReports.quarterN': '{n}. kvartal',
  'saReports.from': 'Fra og med',
  'saReports.to': 'Til og med',
  'saReports.hint.to': 'Høyst 24 måneder.',
  'saReports.groupBy': 'Vis per',
  'saReports.groupBy.month': 'Måned',
  'saReports.groupBy.organization': 'Organisasjon',
  'saReports.show': 'Vis rapport',
  'saReports.loading': 'Henter …',
  'saReports.showing': 'Periode: {from} – {to} (norsk tid).',
  'saReports.csv': 'Last ned CSV',
  'saReports.caption.month': 'Plattformtall per måned, med sum for perioden',
  'saReports.caption.organization': 'Plattformtall per organisasjon, med sum for perioden',
  'saReports.col.month': 'Måned',
  'saReports.col.organization': 'Organisasjon',
  'saReports.col.kind': 'Type',
  'saReports.col.active': 'Aktiv',
  'saReports.col.centres': 'Sentre',
  'saReports.col.activeCentres': 'Aktive sentre',
  'saReports.col.clubs': 'Klubber',
  'saReports.col.activeClubs': 'Aktive klubber',
  'saReports.col.bookings': 'Bookinger',
  'saReports.col.courtHours': 'Banetimer',
  'saReports.col.activities': 'Aktiviteter',
  'saReports.col.signups': 'Påmeldinger',
  'saReports.col.signedIn': 'Innloggede kontoer',
  'saReports.col.newAccounts': 'Nye kontoer',
  'saReports.col.gross': 'Kortbetalt (kr)',
  'saReports.col.refunded': 'Refundert (kr)',
  'saReports.col.platformFees': 'Plattformavgifter (kr)',
  'saReports.kind.CENTRE': 'Senter',
  'saReports.kind.CLUB': 'Klubb',
  'saReports.yes': 'Ja',
  'saReports.no': 'Nei',
  'saReports.total': 'Sum',
  'saReports.totalOrganizations': 'Sum ({count} organisasjoner)',
  'saReports.activeCount': '{count} aktive',
  'saReports.partialMonth': '{month} ({from}.–{to}.)',
  'saReports.note.accountsPlatformWide':
    'Kontoer hører ikke til én organisasjon, så de vises for hele plattformen: {signedIn} innloggede kontoer og {created} nye kontoer i perioden.',
  'saReports.note.distinct':
    'Innloggede kontoer og aktive organisasjoner telles én gang per rad. Summen for perioden er derfor ikke summen av månedene.',
  'saReports.note.money':
    'Kortbetalinger er hentet fra betalingsloggen alene. Pris og betalingsstatus på gamle bookinger er ikke tatt med.',
  'saReports.note.fees':
    'Plattformavgifter vises først når betalingsoppgjøret skriver dem. Til da er tallet 0.',
  'saReports.note.otherCurrency':
    '{count} betalingsposter i annen valuta enn NOK er ikke regnet med i beløpene.',
  'saReports.error.invalidPeriod': 'Perioden er ikke gyldig. Velg en fra-dato og en til-dato.',
  'saReports.error.tooLong': 'Perioden kan være høyst 24 måneder.',
  'saReports.error.forbidden': 'Du har ikke tilgang til plattformtallene.',
  'saReports.error.entraRequired':
    'Plattformtallene krever innlogging med Microsoft (Entra). Logg inn på nytt.',
  'saReports.error.failed': 'Klarte ikke å hente rapporten. Prøv igjen.',
  'saReports.error.noApi': 'API-et er ikke koblet til i denne versjonen.',
  'saRating.menuGroup': 'Spillere',
  'saPlayerProfile.menu': 'Spillerprofil',
  'saPlayerProfile.title': 'Rett en spillers profil',
  'saPlayerProfile.intro':
    'En spiller med konto endrer navn, e-post, telefon og kjønn selv, og klubbene kan ikke. Her retter support det når spilleren ikke kan. Begrunnelsen og hvilke felt som ble endret, logges.',
  'saPlayerProfile.loading': 'Henter spillerne …',
  'saPlayerProfile.noApi': 'Denne siden krever API-et.',
  'saPlayerProfile.loadFailed': 'Kunne ikke hente spillerne. Last siden på nytt.',
  'saPlayerProfile.search': 'Søk etter navn',
  'saPlayerProfile.name': 'Navn',
  'saPlayerProfile.email': 'E-post på profilen',
  'saPlayerProfile.phone': 'Telefon',
  'saPlayerProfile.gender': 'Kjønn',
  'saPlayerProfile.genderUnset': 'Ikke oppgitt',
  'saPlayerProfile.genderMale': 'Mann',
  'saPlayerProfile.genderFemale': 'Kvinne',
  'saPlayerProfile.reason': 'Begrunnelse (påkrevd)',
  'saPlayerProfile.save': 'Lagre rettelsen',
  'saPlayerProfile.saving': 'Lagrer …',
  'saPlayerProfile.saved': 'Lagret. {count} felt ble endret.',
  'saPlayerProfile.noChange': 'Ingenting å endre: profilen har allerede disse verdiene.',
  'saPlayerProfile.uncertain':
    'Fikk ikke et sikkert svar. Last listen på nytt og sjekk profilen før du prøver igjen.',
  'saRating.menu': 'Ratingkorrigering',
  'saRating.title': 'Korriger en spillers rating',
  'saRating.lead':
    'Ratingen er felles for hele plattformen. En korrigering gjelder i alle klubber, og loggføres med begrunnelsen og hvem som gjorde den.',
  'saRating.load.loading': 'Henter spillere og korrigeringslogg …',
  'saRating.load.noApi': 'API-et er ikke koblet til i denne versjonen.',
  'saRating.load.refused': 'Serveren ga ikke tilgang til spillerdatabasen.',
  'saRating.load.failed': 'Klarte ikke å hente spillerne. Prøv igjen.',
  'saRating.load.retry': 'Prøv igjen',
  'saRating.load.reload': 'Hent listen på nytt',
  'saRating.search.label': 'Finn spiller',
  'saRating.search.hint': 'Navn eller spiller-ID, minst to tegn.',
  'saRating.search.noMatch': 'Ingen spillere passer søket.',
  'saRating.search.results': 'Treff',
  'saRating.search.resultMeta': 'Spiller-ID {id} · rating {elo}',
  'saRating.search.more': '{count} treff til. Skriv mer for å snevre inn.',
  'saRating.player.none': 'Velg en spiller for å se ratingen og korrigeringsloggen.',
  'saRating.player.id': 'Spiller-ID {id}',
  'saRating.player.rating': 'Nåværende rating',
  'saRating.player.level': 'Nivå',
  'saRating.player.matches': 'Kamper spilt',
  'saRating.history.title': 'Tidligere korrigeringer',
  'saRating.history.empty': 'Ratingen har aldri blitt korrigert manuelt.',
  'saRating.history.when': 'Tidspunkt',
  'saRating.history.from': 'Fra',
  'saRating.history.to': 'Til',
  'saRating.history.change': 'Endring',
  'saRating.history.reason': 'Begrunnelse',
  // Rows written while a reason was optional. History, so it is shown as it is.
  'saRating.history.noReason': 'Ingen begrunnelse oppgitt',
  'saRating.form.title': 'Ny rating',
  'saRating.form.lead':
    'Bruk dette bare for å rette en feil, for eksempel en spiller som ble registrert med feil startrating.',
  'saRating.form.newRating': 'Ny rating',
  // TRUE TODAY, and pinned by the API's characterization test: the correction
  // moves the rating only. Said here so nobody expects the level to follow.
  'saRating.form.levelUnchanged': 'Nivået endres ikke.',
  'saRating.form.reason': 'Begrunnelse',
  'saRating.form.reasonHint': 'Påkrevd, minst {minimum} tegn. Lagres i korrigeringsloggen.',
  'saRating.form.submit': 'Korriger rating',
  'saRating.form.saving': 'Lagrer …',
  'saRating.form.confirm':
    'Sette ratingen til {name} fra {from} til {to}? Det gjelder i alle klubber.',
  'saRating.form.confirmYes': 'Ja, korriger',
  'saRating.form.readOnly':
    'Du kan se ratingen og loggen. Å korrigere krever retten til å sette rating manuelt.',
  'saRating.outcome.saved': 'Ratingen til {name} er endret fra {from} til {to}.',
  // `no_change` (409) — an answer, not a failure (#793).
  'saRating.outcome.noChange':
    'Ratingen er allerede {elo}. Ingenting ble endret, og ingenting ble loggført.',
  'saRating.error.reasonRequired': 'Skriv en begrunnelse på minst {minimum} tegn.',
  'saRating.error.invalidRating': 'Skriv inn ratingen som et tall.',
  'saRating.error.notFound': 'Spilleren finnes ikke lenger. Hent listen på nytt.',
  'saRating.error.forbidden': 'Kontoen din har ikke retten til å korrigere rating.',
  'saRating.error.reauthenticate':
    'Innloggingen din må bekreftes på nytt. Logg inn igjen og prøv på nytt.',
  'saRating.error.server':
    'Serveren svarte med en feil. Hent listen på nytt for å se om korrigeringen ble lagret, før du prøver igjen.',
  'saRating.error.noAnswer':
    'Fikk ikke svar fra serveren. Hent listen på nytt for å se om korrigeringen ble lagret.',
  // ── A banepad changed the tournament (#556) ───────────────────────
  'padRefresh.waiting': 'Et resultat er registrert på banepaden.',
  'padRefresh.holding': 'Du har en score som ikke er sendt inn, så ingenting er endret her ennå.',
  'padRefresh.accept': 'Hent inn resultatet',
  // ── shell ─────────────────────────────────────────────────────────
  'app.tagline': 'Booking- og turneringsplattform',
  'app.signOut': 'Logg ut',
  'app.signedInAs': 'Innlogget som {name}',
  'app.club': 'Klubb',
  'app.changeClub': 'Bytt klubb',
  // ── dev-only staff sign-in (/sa, #445) ────────────────────────────
  'devLogin.title': 'Utviklerinnlogging',
  'devLogin.explainer':
    'Kun på utviklingsmiljøet. Tilgang bestemmes av Starpoint på vanlig måte — innlogging bekrefter bare hvem du er.',
  'devLogin.email': 'E-post',
  'devLogin.sendCode': 'Send kode',
  'devLogin.sending': 'Sender …',
  'devLogin.code': 'Kode fra e-post',
  'devLogin.codeSent': 'Vi har sendt en kode til {email} hvis kontoen finnes.',
  'devLogin.again': 'Bruk en annen e-post',
  'devLogin.submit': 'Logg inn',
  'devLogin.submitting': 'Logger inn…',
  'devLogin.refused': 'Innlogging avvist.',
  'devLogin.unavailable': 'Innlogging er ikke tilgjengelig her.',
  'app.loading': 'Laster …',
  'app.viewLoadFailed': 'Denne siden kunne ikke lastes. Last siden på nytt.',
  'app.reload': 'Last på nytt',
  // «WE COULD NOT ASK» (#1145): a restore that could not reach the server.
  'app.offline.title': 'Får ikke kontakt med serveren',
  'app.offline.body':
    'Vi fikk ikke sjekket om du er logget inn. Vi prøver igjen automatisk når forbindelsen er tilbake.',
  'app.offline.retry': 'Prøv igjen',

  // ── auth ──────────────────────────────────────────────────────────
  'auth.login.title': 'Logg inn',
  'auth.login.lead': 'Velkommen tilbake til klubbportalen.',
  'auth.login.email': 'E-post',
  // The password path is still REACHABLE and no longer the default (#695) —
  // this is what somebody presses when they have no passkey on this device.
  // Deliberately NEUTRAL — it names no mechanism. The point of the disclosure
  // is that the signed-out screen does not list what it has (#704).
  'auth.login.otherWays': 'Andre måter å logge inn på',
  'auth.login.usePassword': 'Logg inn med passord',
  'auth.login.staff': 'Ansatt-innlogging',
  // ── Signing in with an e-mail code (#679) ────────────────────────
  //
  // THE COPY MUST NOT KNOW MORE THAN THE SERVER SAYS. The API answers the same
  // way whether or not an address has an account, so every sentence here is
  // written to be true either way: «if there is an account, a code is on its
  // way», never «check your inbox». Warmer wording would assert the account
  // exists and turn this form into a membership oracle for club
  // administrators' addresses — the same rule `auth.forgot.*` follows.
  // ── Passkeys (#680) ──────────────────────────────────────────────
  //
  // «Passkey» is the term the platforms use in Norwegian too — Apple and Google
  // both ship it untranslated — so translating it would teach a name nobody
  // else uses. The sentences around it are Norwegian; the noun is not.
  'account.passkeys': 'Passkeys',
  'account.passkeys.lead':
    'Logg inn uten passord, med fingeravtrykk, ansikt eller PIN-koden på enheten din.',
  'account.passkeys.add': 'Legg til passkey',
  'account.passkeys.adding': 'Følg instruksjonene på enheten …',
  'account.passkeys.labelLabel': 'Navn på enheten (valgfritt)',
  'account.passkeys.labelPlaceholder': 'Jobb-PC',
  'account.passkeys.added': 'Passkey lagt til.',
  'account.passkeys.addedNotBackedUp':
    'Passkey lagt til. Den finnes bare på denne enheten — mister du den, mister du passkeyen.',
  'account.passkeys.cancelled': 'Avbrutt. Ingen passkey ble lagt til.',
  'account.passkeys.alreadyRegistered': 'Denne enheten har allerede en passkey for kontoen.',
  'account.passkeys.unsupported': 'Denne nettleseren støtter ikke passkeys.',
  'account.passkeys.failed': 'Klarte ikke å legge til passkey. Prøv igjen.',
  'account.passkeys.network': 'Fikk ikke kontakt med serveren. Prøv igjen.',
  'account.passkeys.stepUp':
    'Bekreft at det er deg først. Du må logge inn på nytt før du kan legge til eller fjerne en passkey.',
  // ── «INNLOGGINGER» (#1141) ─────────────────────────────────────────
  'account.sessions': 'Innlogginger',
  'account.sessions.loading': 'Henter …',
  'account.sessions.listFailed': 'Klarte ikke å hente innloggingene dine.',
  'account.sessions.onlyHere': 'Du er bare logget inn her.',
  'account.sessions.elsewhere': 'Du er logget inn her og {count} andre steder.',
  'account.sessions.signOutOthers': 'Logg ut alle andre',
  'account.sessions.confirm': 'Logge ut alle andre steder enn her?',
  'account.sessions.yes': 'Ja, logg ut',
  'account.sessions.no': 'Avbryt',
  'account.sessions.working': 'Logger ut …',
  'account.sessions.done': 'Logget ut {count} andre steder.',
  'account.sessions.stepUp':
    'Bekreft at det er deg først. Du må logge inn på nytt før du kan logge ut andre steder.',
  'account.sessions.failed': 'Klarte ikke å logge ut de andre. Prøv igjen.',
  // ── THE LIST (#675 slice 10) ──────────────────────────────────────
  // Revoking is not offered yet: the step-up policy for removing a credential
  // is still open, so the screen shows what exists without claiming it can be
  // taken away.
  'account.passkeys.listLoading': 'Henter …',
  'account.passkeys.listEmpty': 'Du har ingen passkeys ennå.',
  'account.passkeys.listFailed': 'Klarte ikke å hente passkeyene dine.',
  'account.passkeys.unnamed': 'Uten navn',
  'account.passkeys.addedOn': 'Lagt til {date}',
  'account.passkeys.lastUsed': 'Sist brukt {date}',
  'account.passkeys.neverUsed': 'Ikke brukt ennå',
  // ── REMOVING ONE (#675 slice 10) ──────────────────────────────────
  // No step-up: the session is already VERIFIED, and removing a credential
  // cannot lock anybody out while the e-mail code exists. The confirm step is
  // about SLIPS, not about proving identity again.
  'account.passkeys.remove': 'Fjern',
  'account.passkeys.removeConfirm': 'Fjern denne?',
  'account.passkeys.removeYes': 'Ja, fjern',
  'account.passkeys.removeNo': 'Avbryt',
  'account.passkeys.removing': 'Fjerner …',
  'account.passkeys.removed': 'Passkeyen ble fjernet.',
  'account.passkeys.removeGone': 'Den var allerede borte. Listen er oppdatert.',
  'account.passkeys.removeFailed': 'Fikk ikke fjernet passkeyen. Prøv igjen.',
  // ── Signing in with a passkey (#680) ─────────────────────────────
  //
  // `cancelled` COVERS TWO THINGS THAT CANNOT BE TOLD APART. When no credential
  // on the device matches this site, the browser throws the same error it throws
  // when somebody presses cancel — deliberately, so a page cannot learn whether
  // you hold a passkey for it. So the sentence has to be true of both and claim
  // neither: «avbrutt, ELLER denne enheten har ingen passkey».
  'auth.passkey.link': 'Logg inn med passkey',
  'auth.passkey.working': 'Følg instruksjonene på enheten …',
  'auth.passkey.cancelled': 'Avbrutt, eller denne enheten har ingen passkey for Starpoint.',
  'auth.passkey.refused': 'Passkeyen ble ikke godtatt. Prøv en annen måte å logge inn på.',
  'auth.passkey.network': 'Fikk ikke kontakt med serveren. Prøv igjen.',
  // ── Logg inn i spiller-appen (#710) ───────────────────────────────
  //
  // The tagline is what `PlayerSignInScreen` shows under the logo. The rest of
  // this block belonged to `MemberSignInScreen`, retired with its door (#750).
  'memberSignIn.tagline': 'Booking og turneringer for spillere',
  'memberSignIn.remember': 'Husk meg på denne enheten',
  'memberSignIn.rememberHint':
    'Du forblir innlogget i 30 dager. La den stå tom på en datamaskin andre også bruker.',
  // ── SIGNING IN AS A PLAYER, WITHOUT A CLUB (#710) ──────────────────
  //
  // Player-first: en ny eller tilfeldig spiller skal kunne identifisere seg med
  // en e-postkode og delta, uten at klubbmedlemskap er forutsetningen for å ha
  // en StarPoint-identitet. Denne døren spør derfor IKKE om klubb.
  // ── «LOGG INN», IKKE «LOGG INN SOM SPILLER» ───────────────────────
  //
  // Hele appen ER spillerappen, så «som spiller» sier ingenting — og den som
  // leser det er allerede en spiller. Dette er DEN døren nå, ikke én av to.
  'playerSignIn.title': 'Logg inn',
  // ── NEVNER IKKE KLUBB (#710-korreksjonen) ─────────────────────────
  //
  // Sto «… du trenger ingen klubb for å komme i gang», som var beroligende da
  // det fantes en konkurrerende dør som KREVDE klubb. Nå finnes den ikke, og
  // setningen var det eneste stedet på skjermen som løftet opp et begrep
  // skjermen finnes for å gjøre irrelevant.
  'playerSignIn.lead': 'Skriv e-postadressen din, så sender vi en engangskode.',
  'playerSignIn.emailLabel': 'E-post',
  'playerSignIn.send': 'Send engangskode',
  'playerSignIn.sending': 'Sender …',
  'playerSignIn.sentTitle': 'Sjekk e-posten din',
  // SIER INGENTING OM ADRESSEN. Serveren svarer 202 for ukjent, kjent og
  // deaktivert adresse likt, og denne setningen må ikke avsløre mer enn den.
  'playerSignIn.sentLead':
    'Vi har sendt en engangskode til {email}. Den er gyldig i {minutes} minutter.',
  'playerSignIn.codeLabel': 'Engangskode',
  'playerSignIn.verify': 'Logg inn',
  'playerSignIn.verifying': 'Logger inn …',
  'playerSignIn.refused': 'Feil eller utløpt kode.',
  'playerSignIn.network': 'Fikk ikke kontakt med serveren. Prøv igjen.',
  'playerSignIn.unavailable': 'Innlogging er ikke tilgjengelig her.',
  'playerSignIn.resend': 'Send koden på nytt',
  'playerSignIn.resent': 'Vi har sendt en ny kode. Den forrige virker ikke lenger.',
  'playerSignIn.wrongAddress': 'Feil e-postadresse? Start på nytt',
  // ── Owner decision 3A (2026-10-31): a code sign-in accepts the club's
  // invitation to this address, and the person is TOLD, once, before the app
  // opens. One line per club.
  'playerSignIn.joinedTitle': 'Du er nå medlem av {club}',
  'playerSignIn.joinedLead':
    'Klubben hadde invitert denne e-postadressen, og innloggingen din bekreftet den.',
  'playerSignIn.joinedContinue': 'Fortsett',
  // ── Å GODTA EN KLUBBS INVITASJON (#1069) ──────────────────────────
  //
  // Lenken i e-posten beviser at den kom fram; koden beviser postkassen. Først
  // da kobles personen til klubbens spiller (#197 Q4: samtykke). `refused.*`
  // brukes når koden var riktig men invitasjonen ikke kan brukes — adressen er
  // da bekreftet, og setningen må ikke si «feil kode».
  'memberInvitation.title': 'Invitasjon fra {club}',
  'memberInvitation.titleGeneric': 'Invitasjon',
  'memberInvitation.opening': 'Åpner invitasjonen …',
  'memberInvitation.leadMember':
    '{club} inviterer deg som medlem, som spilleren «{player}». Vi har sendt en engangskode til adressen invitasjonen ble sendt til. Den er gyldig i {minutes} minutter.',
  'memberInvitation.leadLink':
    '{club} vil koble deg til spilleren «{player}». Vi har sendt en engangskode til adressen invitasjonen ble sendt til. Den er gyldig i {minutes} minutter.',
  'memberInvitation.codeLabel': 'Engangskode',
  'memberInvitation.accept': 'Godta invitasjonen',
  'memberInvitation.accepting': 'Godtar …',
  'memberInvitation.resend': 'Send koden på nytt',
  'memberInvitation.resent': 'Vi har sendt en ny kode. Den forrige virker ikke lenger.',
  'memberInvitation.notNow': 'Ikke nå',
  'memberInvitation.toSignIn': 'Til innlogging',
  'memberInvitation.notValid': 'Denne invitasjonen kan ikke brukes lenger. Be klubben sende en ny.',
  'memberInvitation.wrongCode': 'Feil eller utløpt kode.',
  'memberInvitation.identityAmbiguous':
    'Adressen tilhører en konto som ikke logger inn med e-post (for eksempel med Microsoft). Be klubben om hjelp.',
  'memberInvitation.mailFailed': 'Vi fikk ikke sendt koden. Prøv igjen om litt.',
  'memberInvitation.network': 'Fikk ikke kontakt med serveren. Prøv igjen.',
  'memberInvitation.unavailable': 'Invitasjoner kan ikke godtas her.',
  'memberInvitation.unknown': 'Noe gikk galt. Prøv igjen.',
  'memberInvitation.refused.title': 'Adressen din er bekreftet',
  'memberInvitation.refused.playerClaimed':
    'Men invitasjonen kan ikke brukes: spilleren er allerede koblet til en annen konto.',
  'memberInvitation.refused.accountLinked':
    'Men invitasjonen kan ikke brukes: kontoen din er allerede koblet til en annen spiller.',
  'memberInvitation.refused.otherRoster':
    'Men invitasjonen kan ikke brukes: spilleren er nå medlem i en annen klubb.',
  'memberInvitation.refused.notValid':
    'Men invitasjonen kan ikke brukes lenger. Be klubben sende en ny.',
  // ── «Prøv igjen om …» — the actual wait on a throttled door (#778 Q7) ──
  //
  // ONE FAMILY FOR EVERY THROTTLED SURFACE: sign-in, registration, passkey,
  // step-up, join requests. Whole sentences per unit and plural form, chosen
  // by `i18n/retryWait.ts`, which also owns the rounding. `unknown` is for a
  // refusal whose wait we could not read — never an invented number.
  'retryWait.unknown': 'For mange forsøk. Prøv igjen om litt.',
  'retryWait.seconds.one': 'For mange forsøk. Prøv igjen om {count} sekund.',
  'retryWait.seconds.other': 'For mange forsøk. Prøv igjen om {count} sekunder.',
  'retryWait.minutes.one': 'For mange forsøk. Prøv igjen om {count} minutt.',
  'retryWait.minutes.other': 'For mange forsøk. Prøv igjen om {count} minutter.',
  'retryWait.aboutAnHour': 'For mange forsøk. Prøv igjen om omtrent en time.',
  'retryWait.hours.other': 'For mange forsøk. Prøv igjen om {count} timer.',
  // FJERNET: «Jeg er medlem i en klubb». Det var veien tilbake til den andre
  // døren, og det finnes ingen andre dør å gå tilbake til — spørsmålet «hva
  // slags bruker er du?» er det vi slutter å stille.

  // ── SPILLERENS HJEM (#710) ────────────────────────────────────────
  //
  // Formålet med StarPoint er å spille padel, ikke å bli klubbmedlem. Hjemmet
  // handler derfor om PERSONEN og om å spille — klubbforhold er nyttig
  // kontekst, ikke identiteten til en nyregistrert spiller.
  //
  // «Du er ikke medlem i noen klubb» var hovedbudskapet før. Det er flyttet
  // ned til en linje, og bare når serveren faktisk har svart.
  // ── FINNE EN KLUBB (#710 — dogfooding fant feilen) ────────────────
  //
  // «Finn en klubb» åpnet REGISTRERINGSSKJERMEN, som ba en innlogget spiller om
  // navn og e-post på nytt. Oppdagelse og medlemssøknad er to forskjellige
  // ting: katalogen er offentlig og spør om ingenting, søknaden er et
  // autentisert valg man tar etterpå.
  'clubDiscovery.title': 'Finn en klubb',
  'clubDiscovery.lead': 'Søk etter klubben du vil spille i.',
  'clubDiscovery.searchLabel': 'Klubb',
  'clubDiscovery.placeholder': 'Skriv navnet på klubben',
  'clubDiscovery.empty': 'Ingen klubber matchet søket.',
  'clubDiscovery.ask': 'Søk om medlemskap',
  'clubDiscovery.asking': 'Sender …',
  'clubDiscovery.pending': 'Venter på godkjenning',
  // SØKNAD ER IKKE MEDLEMSKAP. Klubben må godkjenne, og det sier vi.
  'clubDiscovery.asked': 'Klubben har fått søknaden din. Du er med når de godkjenner den.',
  'clubDiscovery.alreadyAsked': 'Du har allerede en søknad hos denne klubben.',
  'clubDiscovery.alreadyMember': 'Du er allerede medlem her.',
  'clubDiscovery.noPlayer': 'Vi fant ingen spillerprofil på kontoen din ennå.',
  'clubDiscovery.tooMany': 'Du har for mange søknader som venter på svar.',
  'clubDiscovery.closed': 'Klubben tar ikke imot søknader nå.',
  'clubDiscovery.notYet': 'Du kan søke her igjen senere.',
  // MED DATOEN (#1302): klubben har bestemt når, og det er det som gjør svaret
  // til noe man kan gjøre noe med. {date} er en VISNINGSDATO, aldri en nøkkel.
  // Ingen punktum etter: «2. des.» har allerede ett, og det avslutter setningen.
  'clubDiscovery.notYetFrom': 'Du kan søke igjen fra {date}',
  // «ALDRI IGJEN» (#729) sagt slik klubben valgte det — ikke «ikke nå».
  'clubDiscovery.blocked':
    'Du kan ikke søke her igjen. Bare en invitasjon fra klubben slipper deg inn.',
  'clubDiscovery.withdraw': 'Trekk søknaden',
  'clubDiscovery.withdrawing': 'Trekker …',
  'clubDiscovery.withdrawn': 'Søknaden er trukket. Du kan søke igjen når du vil.',
  'clubDiscovery.withdrawAnswered': 'Klubben har allerede svart på søknaden.',
  'clubDiscovery.failed': 'Det gikk ikke. Prøv igjen.',
  // ── SPILLERPROFIL, NÅR SØKNADEN TRENGER EN (#790 review) ──────────
  //
  // Kontoen er en PERSON, spillerprofilen er en PROFIL, og medlemskap er et
  // KLUBBFORHOLD. De holdes fra hverandre — men å søke om medlemskap krever en
  // profil, og en ny konto har ingen. Her er det eneste stedet vi trenger
  // navnet, så det er her vi spør om det.
  'clubDiscovery.needsProfileTitle': 'Hva skal du hete?',
  'clubDiscovery.needsProfileLead':
    'Klubben ser navnet ditt på spillerlister, bookinger og resultater.',
  'clubDiscovery.nameLabel': 'Navn',
  'clubDiscovery.createAndApply': 'Opprett profil og søk',
  'clubDiscovery.creating': 'Oppretter …',
  'clubDiscovery.nameRequired': 'Skriv inn navnet ditt.',
  'clubDiscovery.cancel': 'Avbryt',
  'clubDiscovery.back': 'Tilbake',
  'clubDiscovery.unavailable': 'Søket svarte ikke. Prøv igjen.',
  'clubDiscovery.select': 'Velg',
  // ── A PLACE, BEFORE YOU APPLY (#833) ──────────────────────────────
  //
  // A search result opens the place's own page. The button on the right is
  // unchanged.
  'clubDiscovery.open': 'Åpne {name}',
  'clubDiscovery.kindCentre': 'Senter',
  'clubDiscovery.kindClub': 'Klubb',
  // IKKE OPPFUNNET FUNKSJONALITET. Det finnes ingen API for banesøk, kamper
  // eller turneringer for en spiller uten klubb ennå, så hjemmet lover ikke
  // noe det ikke kan gjøre — det sier hva som finnes nå.
  // EN LINJE, IKKE EN OVERSKRIFT — og bare når vi vet det.
  'clubPicker.placeholder': 'Skriv navnet på klubben',
  'clubPicker.pickOne': 'Velg klubben fra listen.',
  // ── «Søknader om å bli medlem» (#710) ─────────────────────────────
  //
  // THE THREE POLICY OPTIONS ARE WRITTEN AS WHAT THEY MEAN to the person who
  // asked, not as the field names: a club is choosing a consequence, not
  // filling in an enum.
  'joinQueue.title': 'Søknader om å bli medlem ({count})',
  'joinQueue.lead': 'Spillere som har bedt om å bli med i klubben.',
  'joinQueue.asked': 'Søkte {date}',
  'joinQueue.openProfile': 'Åpne profilen til {name}',
  'joinQueue.card.ratingLabel': 'Nivå og rating',
  'joinQueue.card.rating': 'Nivå {level} — {name} · ELO {elo}',
  'joinQueue.card.noRating': 'Ingen rating ennå',
  'joinQueue.card.contactLabel': 'Kontakt',
  'joinQueue.card.notInList':
    'Spilleren er ikke i klubbens spillerliste ennå. Full profil med historikk kommer når søknaden er godkjent.',
  'joinQueue.noContact': 'Ingen kontaktinfo registrert',
  'joinQueue.approve': 'Godkjenn',
  'joinQueue.approving': 'Godkjenner …',
  'joinQueue.reject': 'Avslå',
  'joinQueue.rejecting': 'Avslår …',
  'joinQueue.rejectTitle': 'Avslå søknaden fra {name}',
  'joinQueue.rejectLead':
    'Velg hva avslaget betyr. Forhåndsvalgt er at de kan søke igjen om {days} dager.',
  'joinQueue.policy.allowed': 'De kan søke igjen når som helst',
  'joinQueue.policy.after': 'De kan søke igjen fra en dato',
  'joinQueue.policy.blocked':
    'De kan ikke søke igjen. Bare en invitasjon fra klubben slipper dem inn.',
  'joinQueue.fromDate': 'Kan søke igjen fra',
  'joinQueue.confirmReject': 'Avslå',
  'joinQueue.cancel': 'Avbryt',
  'joinQueue.alreadyDecided': 'Noen andre i klubben rakk å svare først. Listen er oppdatert.',
  'joinQueue.gone': 'Søknaden finnes ikke lenger. Listen er oppdatert.',
  'joinQueue.askedBefore': 'Har søkt før',
  'joinQueue.alreadyMember': '{name} er allerede medlem. Listen er oppdatert.',
  'joinQueue.rejected.show': 'Vis avslåtte søknader ({count})',
  'joinQueue.rejected.hide': 'Skjul avslåtte søknader',
  'joinQueue.rejected.on': 'Avslått {date}',
  'joinQueue.rejected.policyAllowed': 'Kan søke igjen når som helst',
  'joinQueue.rejected.policyAfter': 'Kan søke igjen fra {date}',
  'joinQueue.rejected.policyBlocked': 'Kan ikke søke igjen',
  'joinQueue.rejected.reopen': 'Åpne igjen',
  'joinQueue.rejected.reopening': 'Åpner …',
  'joinQueue.rejected.approve': 'Godkjenn likevel',
  'joinQueue.notAllowed': 'Du har ikke tilgang til å svare på søknader i denne klubben.',
  'joinQueue.datePast': 'Velg en dato fram i tid — tidligst i morgen.',
  'joinQueue.policyRefused': 'Serveren godtok ikke valget. Velg på nytt.',
  'joinQueue.protocol':
    'Vi forstod ikke svaret fra serveren, så listen kan være ufullstendig. Last siden på nytt.',
  'joinQueue.unreachable': 'Fikk ikke kontakt med serveren. Prøv igjen.',
  // ── «Medlemmer» — the club's roster, beside the join queue ────────
  'clubMembers.title': 'Medlemmer ({count})',
  'clubMembers.lead': 'Spillere som er medlem av klubben og kan bruke den i appen.',
  'clubMembers.empty': 'Ingen er medlem av klubben ennå.',
  'clubMembers.since': 'Medlem siden {date}',
  'clubMembers.search': 'Søk i medlemmer',
  'clubMembers.noMatch': 'Ingen medlemmer passer søket.',
  'clubMembers.showAll': 'Vis alle ({count})',
  'clubMembers.showFewer': 'Vis færre',
  // ── «Last opp medlemsliste» — a club's own list becomes its roster ──
  //
  // The outcomes are the SERVER's vocabulary said as what they mean to the
  // club: who is new, who it already knew, who gets which mail. Nothing here
  // goes back over the wire.
  'memberImport.open': 'Last opp medlemsliste',
  'memberImport.title': 'Last opp medlemsliste',
  'memberImport.lead':
    'Lim inn navn og e-postadresser — én person per linje — eller velg en fil (CSV). Alle på listen blir medlemmer av klubben og får en e-post med lenke for å ta i bruk Starpoint.',
  'memberImport.pasteLabel': 'Medlemsliste',
  'memberImport.pastePlaceholder': 'Kari Nordmann, kari@eksempel.no\nOla Hansen; ola@eksempel.no',
  'memberImport.file': 'Velg fil …',
  'memberImport.fileRead': 'Leste {count} linjer fra {name}.',
  'memberImport.rowsFound': '{count} personer på listen',
  'memberImport.empty': 'Fant ingen navn og e-postadresser i teksten.',
  'memberImport.tooMany': 'Listen kan ha høyst {max} personer om gangen. Del den opp.',
  'memberImport.preview': 'Sjekk listen',
  'memberImport.previewing': 'Sjekker listen …',
  'memberImport.previewLead':
    'Slik blir listen behandlet. Ingenting er sendt ennå — se over og bekreft.',
  'memberImport.colName': 'Navn',
  'memberImport.colEmail': 'E-post',
  'memberImport.colOutcome': 'Hva skjer',
  'memberImport.plan.new': 'Ny — blir medlem og får invitasjon',
  'memberImport.plan.member_invited': 'Medlem allerede — får invitasjon til å ta i bruk kontoen',
  'memberImport.plan.invited': 'Kjent spiller — får invitasjon til å bli medlem',
  'memberImport.plan.already_member': 'Medlem allerede, med konto — ingenting å gjøre',
  'memberImport.plan.pending': 'Har allerede en invitasjon som venter',
  'memberImport.plan.ambiguous': 'Flere spillere kan være denne personen — hoppes over',
  'memberImport.plan.failed': 'Kunne ikke legges til',
  'memberImport.plan.invalid': 'Ugyldig linje',
  'memberImport.plan.invalid.name_required': 'Mangler navn',
  'memberImport.plan.invalid.email_invalid': 'Ugyldig e-postadresse',
  'memberImport.plan.invalid.duplicate_in_upload': 'Samme e-post står flere ganger på listen',
  'memberImport.matchedByName': 'Kobles til spilleren «{name}» (samme navn)',
  'memberImport.matchedByEmail': 'Dette er spilleren «{name}»',
  'memberImport.confirm': 'Legg til {count} og send invitasjoner',
  'memberImport.confirmNothing': 'Ingen på listen trenger å legges til.',
  'memberImport.confirming': 'Legger til og sender …',
  'memberImport.confirmingProgress': 'Legger til og sender … {done} av {total}',
  'memberImport.stoppedPartway':
    'Stoppet etter {done} av {total}. Det som er gjort, er beholdt — trykk på knappen igjen for å fortsette. Ingen får invitasjonen to ganger.',
  'memberImport.back': 'Tilbake',
  'memberImport.cancel': 'Avbryt',
  'memberImport.close': 'Lukk',
  'memberImport.doneTitle': 'Ferdig: {added} nye medlemmer, {sent} invitasjoner sendt',
  'memberImport.doneHint': 'De som er lagt til, finner du under «Medlemmer» og «Alle spillere».',
  'memberImport.done.sent': 'Invitasjon sendt',
  'memberImport.done.notSent':
    'Lagt til, men e-posten kunne ikke sendes — send invitasjon på nytt fra spillerlisten',
  'memberImport.done.failed': 'Kunne ikke legges til — prøv igjen',
  'memberImport.unreachable': 'Fikk ikke kontakt med serveren. Prøv igjen.',
  'memberImport.protocol': 'Vi forstod ikke svaret fra serveren. Last siden på nytt og prøv igjen.',
  // ── «E-post — kode — du er inne» (#710) ───────────────────────────
  //
  // The journey for somebody with no account: register, read a code, ask a club
  // to let them in. NOTE WHAT THE LAST STEP DOES NOT SAY — «velkommen inn».
  // Asking to join is not joining: a pending request grants nothing, and a club
  // has to approve it before there is anything to sign in to.
  'onboarding.tagline': 'Booking og turneringer for spillere',
  'onboarding.haveCode': 'Jeg har fått en kode fra klubben',
  'onboarding.network': 'Fikk ikke kontakt med serveren. Prøv igjen.',
  'onboarding.protocol': 'Vi forstod ikke svaret fra serveren. Prøv igjen.',
  'onboarding.grantExpired':
    'Det har gått for lang tid, eller siden ble lastet på nytt. Start på nytt — du får en ny kode på e-post.',
  'onboarding.register.title': 'Opprett konto',
  'onboarding.register.lead': 'Vi sender deg en kode på e-post. Du trenger ikke passord.',
  'onboarding.register.nameLabel': 'Navn',
  'onboarding.register.emailLabel': 'E-post',
  'onboarding.register.submit': 'Send kode',
  'onboarding.register.sending': 'Sender …',
  'onboarding.register.invalidEmail': 'Dette ser ikke ut som en e-postadresse.',
  'onboarding.register.nameRequired': 'Skriv inn navnet ditt.',
  'onboarding.code.title': 'Sjekk e-posten din',
  'onboarding.code.lead':
    'Vi har sendt en kode til {email}. Den er gyldig i {minutes} minutter og kan bare brukes én gang.',
  'onboarding.code.label': 'Kode',
  'onboarding.code.submit': 'Bekreft',
  'onboarding.code.verifying': 'Bekrefter …',
  'onboarding.code.refused': 'Feil eller utløpt kode.',
  'onboarding.code.wrongAddress': 'Feil e-postadresse? Start på nytt',
  'onboarding.join.title': 'Bli med i en klubb',
  'onboarding.join.lead': 'Velg klubben du spiller i. Klubben må godkjenne deg før du kommer inn.',
  'onboarding.join.leadNamed':
    'Kontoen din er klar, {name}. Velg klubben du spiller i — klubben må godkjenne deg før du kommer inn.',
  'onboarding.join.clubLabel': 'Klubb',
  'onboarding.join.submit': 'Søk om å bli med',
  'onboarding.join.asking': 'Sender …',
  'onboarding.join.unknownClub': 'Finner ingen klubb med dette navnet.',
  'onboarding.join.alreadyMember': 'Du er allerede medlem i denne klubben.',
  'onboarding.join.blocked': 'Denne klubben tar ikke imot søknaden din. Ta kontakt med klubben.',
  'onboarding.join.notYet': 'Du kan ikke søke denne klubben igjen ennå. Ta kontakt med klubben.',
  'onboarding.join.closed': 'Søknaden din er allerede behandlet. Ta kontakt med klubben.',
  'onboarding.join.tooManyPending': 'Du har for mange søknader som venter på svar.',
  'onboarding.done.title': 'Søknaden er sendt',
  'onboarding.done.lead': '{club} har fått søknaden din.',
  // NO E-MAIL IS PROMISED (#733 review). The club side approves
  // transactionally and notifies nobody — so «du får en e-post» was a sentence
  // no code keeps. Tracked as #734; until it exists, this says what is true.
  'onboarding.done.next':
    'Noen i klubben må godkjenne den. Når det er gjort, kommer du inn ved å logge inn med engangskode på e-postadressen din.',
  'onboarding.done.back': 'Tilbake',
  'auth.forgot.title': 'Glemt passord',
  'auth.forgot.lead': 'Skriv e-postadressen din, så sender vi en lenke for å velge nytt passord.',
  'auth.forgot.submit': 'Send lenke',
  'auth.forgot.working': 'Sender …',
  'auth.forgot.sent':
    'Hvis adressen finnes hos oss, har vi sendt en e-post med lenke for å velge nytt passord.',
  'auth.forgot.back': 'Tilbake til innlogging',
  'auth.scope.title': 'Velg klubb',
  'auth.scope.lead': 'Du har tilgang til flere klubber. Velg hvilken du vil jobbe i.',
  'auth.noScope.title': 'Ingen klubbtilgang',
  'auth.noScope.lead': 'Kontoen din er ikke knyttet til en klubb ennå. Ta kontakt med klubben din.',

  // ── account ───────────────────────────────────────────────────────
  'account.title': 'Min konto',
  // ── dine rettigheter (2026-10-14) ─────────────────────────────────
  // ROLE NAMES, not capability keys: a person reads «Administrator i Oslo
  // Padel». What a role may do is decided in /sa, so the sentence says so.
  'account.rights.title': 'Dine rettigheter',
  'account.rights.lead':
    'Rollene kontoen din har. Hva hver rolle gir tilgang til, bestemmes av Starpoint og organisasjonen.',
  'account.rights.platform': 'Hos Starpoint',
  'account.rights.organizations': 'I organisasjoner',
  'account.rights.none': 'Kontoen har ingen rolle i noen organisasjon ennå.',
  'account.rights.loading': 'Henter rettighetene …',
  // Shown for a grant `/me` reports that the sign-in snapshot cannot name. The
  // right is real; which organization it is in is what we do not know (#817).
  'account.rights.unknownOrg': 'Ukjent organisasjon',
  // ── «BEKREFT AT DU ER DER» (#750 M2b-3) ──────────────────────────
  //
  // A stale session is not an error and not a sign-out: the person is who they
  // were, and they are being asked to prove it again before administrative
  // work. The words say that, rather than apologising for a failure.
  // ── «BYTT VISNING» (owner, 2026-10-20/21; grew out of #752) ──────
  //
  // Switches who you act as: «Spiller» (renamed from #752's «Personlig» at
  // the owner's request), or an organization — named by the ORGANIZATION,
  // with «Senter» / «Klubb» (`auth.scope.kind.*`) beneath. Never by a role:
  // within an organization the roles combine, and a role name is data a club
  // can rename.
  'roleSwitcher.label': 'Bytt visning',
  'roleSwitcher.player': 'Spiller',
  'roleSwitcher.place.player': 'Spillerappen',
  'stepUp.title': 'Bekreft at det er deg',
  'stepUp.lead':
    'Du er fortsatt innlogget. Før du får tilgang til administrative oppgaver må vi bekrefte at det er deg,',
  'stepUp.lead.hours': 'og du logget inn for mer enn {hours} timer siden.',
  'stepUp.lead.minutes': 'og du logget inn for mer enn {minutes} minutter siden.',
  'stepUp.lead.noWindow': 'fordi det er en stund siden du logget inn.',
  'stepUp.lead.done': 'Bekreftet. Du kan fortsette der du var — prøv handlingen på nytt.',
  'stepUp.usePasskey': 'Bruk passkey',
  'stepUp.sendCode': 'Send kode på e-post',
  'stepUp.codeLabel': 'Engangskode',
  'stepUp.verify': 'Bekreft',
  'stepUp.resend': 'Send koden på nytt',
  'stepUp.resent': 'Vi har sendt en ny kode.',
  'stepUp.continue': 'Fortsett',
  'stepUp.working': 'Et øyeblikk …',
  'stepUp.error.invalid_code': 'Feil eller utløpt kode.',
  'stepUp.error.no_passkey': 'Denne kontoen har ingen passkey. Bruk e-postkoden.',
  'stepUp.error.no_local_credential':
    'Denne kontoen kan ikke bekreftes med kode. Kontakt Starpoint.',
  'stepUp.error.session_changed': 'Innloggingen endret seg underveis. Logg inn på nytt.',
  'stepUp.error.generic': 'Det gikk ikke å bekrefte nå. Prøv igjen.',
  'account.rights.unavailable': 'Kunne ikke hente rettighetene akkurat nå.',
  'account.kind.CENTRE': 'Senter',
  'account.kind.CLUB': 'Klubb',
  // ── TILGANGER — the organization's own access page (slice 3, 2026-10-18) ──
  //
  // The entry in the organization's settings. Drawn only for an access
  // manager (decision 4A); the sentence says what the page is for, in the
  // product's words — roles, not capabilities.
  'clubSettings.access.title': 'Tilganger',
  'clubSettings.access.lead':
    'Gi og ta roller for folk i organisasjonen — hvem som er administrator, turneringsleder, resepsjon og så videre.',
  'clubSettings.access.open': 'Administrer tilganger',
  // The equipment entry beside it (the owner, 2026-10-21): the registry the
  // booking calendar's admin bar opens, reachable from the settings too.
  'clubSettings.equipment.title': 'Utstyr',
  'clubSettings.equipment.lead':
    'Ballmaskiner og annet utstyr som kan bookes ved siden av banene — med priser og regler.',
  'clubSettings.equipment.open': 'Administrer utstyr',
  // The page. «Du kan bare gi og ta roller du selv har rettighetene til» is
  // rule 2B said once, up front, so the greyed-out roles below need no
  // explaining when they are met.
  'access.title': 'Tilganger',
  'access.lead':
    'Hvem som har hvilke roller i {organization}. Du kan bare gi og ta roller du selv har rettighetene til.',
  'access.thisOrganization': 'organisasjonen',
  'access.back': 'Tilbake til innstillinger',
  'access.loading': 'Henter tilgangene …',
  'access.forbidden': 'Du administrerer ikke tilganger i denne organisasjonen.',
  'access.unavailable': 'Kunne ikke hente tilgangene akkurat nå.',
  'access.retry': 'Prøv igjen',
  // People: role holders and roster members with an account (rule 1A). The
  // lead says where NEW people come from, because this page has no invitation
  // and somebody will look for one.
  'access.people.title': 'Personer',
  'access.people.lead':
    'Alle som har en rolle her, og alle på spillerlisten som har en konto. Nye personer legges til av Starpoint.',
  'access.people.none': 'Ingen personer er knyttet til organisasjonen ennå.',
  'access.people.you': 'deg',
  'access.people.disabled': 'Deaktivert konto',
  'access.people.onRoster': 'På spillerlisten',
  'access.people.noRoles': 'Ingen rolle',
  'access.grant.title': 'Gi rolle',
  'access.grant.person': 'Person',
  'access.grant.role': 'Rolle',
  'access.grant.choosePerson': 'Velg person …',
  'access.grant.chooseRole': 'Velg rolle …',
  'access.grant.submit': 'Gi rollen',
  'access.grant.working': 'Lagrer …',
  'access.grant.done': '{name} har nå rollen {role}.',
  'access.grant.alreadyHeld': '{name} hadde rollen {role} allerede.',
  'access.revoke.question': 'Ta rollen {role} fra {name}?',
  'access.revoke.confirm': 'Ta bort rollen',
  'access.people.unnamed': 'Konto uten navn ({id})',
  'access.revoke.done': '{name} har ikke lenger rollen {role}.',
  // Rule 2B as a label. The help names the rights, because «you may not» with
  // no reason is the kind of refusal people write in about.
  'access.beyondYou': 'Utenfor din myndighet',
  'access.beyondYou.help': 'Rollen gir rettigheter du ikke har selv: {capabilities}',
  'access.roles.title': 'Roller som kan gis her',
  'access.roles.lead':
    'Hva hver rolle gir, bestemmes av Starpoint. En rolle merket «utenfor din myndighet» kan bare gis av noen som selv har rettighetene den gir.',
  'access.roles.custom': 'Egen rolle',
  // ── EGNE ROLLER — the organization's own roles on «Tilganger» (2026-11-01) ──
  'access.ownRoles.title': 'Egne roller',
  'access.ownRoles.lead':
    'Roller organisasjonen definerer selv. En egen rolle kan bare gi rettigheter organisasjonen har og som du har selv – aldri å dele ut roller, inngå samarbeid eller styre betalingskontoen; det gjør Starpoints roller.',
  'access.ownRoles.none': 'Organisasjonen har ingen egne roller ennå.',
  'access.ownRoles.new': 'Ny rolle',
  'access.ownRoles.name': 'Navn',
  'access.ownRoles.description': 'Beskrivelse (valgfritt)',
  'access.ownRoles.create': 'Opprett rollen',
  'access.ownRoles.created': 'Rollen {role} er opprettet.',
  'access.ownRoles.editRights': 'Endre rettigheter',
  'access.ownRoles.rename': 'Endre navn',
  'access.ownRoles.saveName': 'Lagre navnet',
  'access.ownRoles.renamed': 'Rollen heter nå {role}.',
  'access.ownRoles.saveRights': 'Lagre rettighetene',
  'access.ownRoles.saved': 'Rettighetene til {role} er lagret.',
  'access.ownRoles.saving': 'Lagrer …',
  'access.ownRoles.cancel': 'Avbryt',
  'access.ownRoles.delete': 'Slett rollen',
  'access.ownRoles.deleteQuestion': 'Slette rollen {role}?',
  'access.ownRoles.deleted': 'Rollen {role} er slettet.',
  'access.ownRoles.holders': '{n} har rollen',
  'access.ownRoles.inUse': 'Kan ikke slettes mens noen har den: ta rollen fra dem først.',
  'access.ownRoles.rights': '{n} rettigheter',
  'access.ownRoles.noRights': 'Ingen rettigheter ennå',
  'access.ownRoles.nothingOffered': 'Det finnes ingen rettigheter du kan gi en egen rolle.',
  'access.ownRoles.notYours': 'Du har ikke denne selv, så du kan ikke endre den',
  'access.ownRoles.pending': '{added} lagt til, {removed} fjernet – ikke lagret ennå',
  'access.ownRoles.error.role_in_use':
    'Rollen kan ikke slettes mens {n} har den. Ta rollen fra dem først.',
  'access.ownRoles.error.beyond_organization':
    'Organisasjonen har ikke disse rettighetene selv, så en egen rolle kan ikke gi dem: {capabilities}.',
  'access.ownRoles.error.reserved_capability':
    'En egen rolle kan ikke gi «{capabilities}» – det gjør bare Starpoints roller.',
  'access.ownRoles.error.beyond_own_authority':
    'Du kan ikke gi eller ta rettigheter du ikke har selv: {capabilities}.',
  'access.ownRoles.error.last_holder':
    'Da ville ingen som deler ut roller her ha disse rettighetene: {capabilities}. Gi noen andre dem først.',
  'access.ownRoles.error.name_is_system_role':
    'Navnet brukes av en av Starpoints roller. Velg et annet navn.',
  'access.ownRoles.error.name_has_no_usable_key': 'Navnet må inneholde bokstaver eller tall.',
  'access.ownRoles.error.not_found': 'Rollen finnes ikke lenger. Listen er hentet på nytt.',
  // The short name of each organization right, as /sa's matrix names it.
  'capability.organization.read': 'Se data',
  'capability.organization.write': 'Endre oppsett og data',
  'capability.organization.rename': 'Endre navn',
  'capability.organization.access.manage': 'Dele ut roller',
  'capability.organization.links.manage': 'Inngå samarbeid',
  'capability.organization.audit.read': 'Lese organisasjonens logg',
  'capability.court.manage': 'Opprette og endre baner',
  'capability.asset.manage': 'Opprette og endre utstyr',
  'capability.court.set_out_of_service': 'Sette bane ute av drift',
  'capability.court.override_out_of_service': 'Booke på stengt bane',
  'capability.court.pair_device': 'Koble til Banepad',
  'capability.court.screen.control': 'Styre skjermene',
  'capability.booking.create': 'Booke for andre',
  'capability.booking.modify': 'Endre andres booking',
  'capability.booking.delete': 'Avbestille andres booking',
  'capability.booking.override_constraints': 'Booke utenom reglene',
  'capability.booking.rules.manage': 'Bookingregler og priser',
  'capability.activity.manage': 'Drifte aktiviteter',
  'capability.planned_match.manage': 'Sette opp kamper',
  'capability.tournament.manage': 'Drifte turneringer og serier',
  'capability.result.record': 'Føre resultat',
  'capability.result.correct': 'Rette ført resultat',
  'capability.rating.commit': 'Slå resultat inn i rating',
  'capability.member.manage': 'Administrere medlemmer',
  'capability.member.contact.read': 'Se kontaktinfo',
  'capability.member.export': 'Eksportere medlemslisten',
  'capability.membership.approve': 'Godkjenne medlemskap',
  'capability.membership.pricing.manage': 'Medlemspris og medlemstilbud',
  'capability.messaging.send_as_organization': 'Svare som organisasjonen',
  'capability.messaging.broadcast': 'Sende til alle medlemmer',
  'capability.messaging.moderate': 'Moderere meldinger',
  'capability.messaging.disable_organization': 'Slå av meldinger',
  'capability.bug.manage': 'Behandle feilmeldinger',
  'capability.finance.read': 'Se økonomi og rapporter',
  'capability.payment.refund': 'Refundere',
  'capability.payment.manual_action': 'Gripe inn i en betaling',
  'capability.payment.account.manage': 'Betalingskontoen',
  // The server's codes for this page, as sentences. The codes themselves are
  // never shown and never stored translated — `errorMessage` maps them here.
  'errors.not_in_organization':
    'Personen er ikke knyttet til denne organisasjonen. Nye personer legges til av Starpoint.',
  'errors.unknown_role': 'Rollen kan ikke gis i denne organisasjonen.',
  'errors.beyond_own_authority':
    'Rollen gir rettigheter du ikke har selv, så du kan verken gi eller ta den.',
  'errors.last_access_manager':
    'Dette er den siste som kan administrere tilganger her. Gi noen andre den rollen først.',
  // #976 decision 1: the remaining access managers would lose rights only this
  // role's holder has — in practice, the last Eier stepping down.
  'errors.last_holder':
    'Da ville ingen som deler ut roller her ha alle rettighetene denne rollen gir. Gi noen andre den rollen først.',
  // #976 decision 2: role grants want a sign-in from the last 15 minutes.
  'errors.reauthentication_required': 'Logg inn på nytt for å fortsette.',
  // ── SAMARBEID — the organization's own co-operation page (slice 4a, 2026-10-20) ──
  //
  // The owner's rule, decision 5: both sides say yes, and each chooses what it
  // gives. The page says it in those words — «dere gir», «dere får» — rather
  // than in capability names, and says once, up front, that a link grants
  // nothing yet: no route uses what a link gives before the link booking
  // command (slice 4c).
  'clubSettings.links.title': 'Samarbeid',
  'clubSettings.links.lead':
    'Samarbeid mellom senter og klubb: hva dere gir den andre, og hva dere får tilbake.',
  'clubSettings.links.open': 'Administrer samarbeid',
  // ── LOGG — the organization's own audit trail (#935, 2026-11-01) ──
  //
  // Read by Eier and Administrator by default. The page says once what it
  // does NOT show, so nobody mistakes the absence of sign-ins for a gap.
  'clubSettings.auditLog.title': 'Logg',
  'clubSettings.auditLog.lead':
    'Hvem som har gjort hva i organisasjonen — roller, bookinger, innstillinger og det Starpoint har gjort for dere.',
  'clubSettings.auditLog.open': 'Se loggen',
  'auditLog.title': 'Logg',
  'auditLog.lead': 'Hvem som har gjort hva i {organization}, nyeste først.',
  'auditLog.thisOrganization': 'organisasjonen',
  'auditLog.notShown':
    'Innlogginger og andres egne kontoendringer vises ikke, heller ikke fritekst og navn på Starpoint-ansatte. Det at du ser loggen, logges også.',
  'auditLog.back': 'Tilbake til innstillinger',
  'auditLog.loading': 'Henter loggen …',
  'auditLog.forbidden': 'Du har ikke tilgang til loggen i denne organisasjonen.',
  'auditLog.unavailable': 'Kunne ikke hente loggen akkurat nå.',
  'auditLog.empty': 'Ingenting er logget i denne perioden.',
  'auditLog.more': 'Vis flere',
  'auditLog.period': 'Periode',
  'auditLog.periodDays': 'Siste {days} dager',
  'auditLog.areaLabel': 'Område',
  'auditLog.area.all': 'Alt',
  'auditLog.area.roles': 'Roller',
  'auditLog.area.bookings': 'Bookinger',
  'auditLog.area.activities': 'Aktiviteter',
  'auditLog.area.members': 'Medlemmer',
  'auditLog.area.courts': 'Baner',
  'auditLog.area.settings': 'Innstillinger',
  'auditLog.area.payments': 'Betaling',
  'auditLog.area.starpoint': 'Starpoint-support',
  'auditLog.actor.starpoint': 'Starpoint',
  'auditLog.actor.member': 'Et medlem (appen)',
  'auditLog.actor.device': 'Et banenettbrett',
  'auditLog.actor.system': 'Systemet',
  'auditLog.actor.provider': 'Betalingsleverandøren',
  'auditLog.actor.unknownPerson': 'En bruker',
  'auditLog.subject.user': 'en bruker',
  'links.title': 'Samarbeid',
  'links.lead.centre':
    'Klubbene {organization} samarbeider med. Senteret foreslår og velger hva klubben får hos dere; klubben sier ja og velger hva dere får hos den.',
  'links.lead.club':
    'Sentrene {organization} samarbeider med. Senteret foreslår og velger hva dere får der; dere sier ja og velger hva senteret får hos dere.',
  'links.thisOrganization': 'organisasjonen',
  'links.back': 'Tilbake til innstillinger',
  'links.loading': 'Henter samarbeidene …',
  'links.forbidden': 'Du administrerer ikke samarbeid i denne organisasjonen.',
  'links.unavailable': 'Kunne ikke hente samarbeidene akkurat nå.',
  'links.retry': 'Prøv igjen',
  'links.inUse':
    'Begge retninger er i bruk: partnerklubbens aktivitets- og turneringsledere kan booke baner hos senteret fra klubbens forside, og senterets aktivitetsansvarlige kan arrangere aktiviteter i klubbens navn fra senterets forside.',
  'links.none.centre': 'Ingen samarbeid ennå. Foreslå et nedenfor.',
  'links.none.club': 'Ingen samarbeid ennå. Et senter foreslår, og forslaget dukker opp her.',
  'links.status.proposed': 'Venter på svar',
  'links.status.proposedToYou': 'Forslag til dere',
  'links.status.active': 'Aktivt',
  'links.status.suspended': 'Satt på pause',
  'links.gives': 'Dere gir',
  'links.receives': 'Dere får',
  'links.nothing': 'Ingenting',
  'links.notAnswered': 'Bestemmes når klubben sier ja',
  'links.capabilities.none': 'Gir ingen rettigheter.',
  'links.capability.link.booking.create': 'booke senterets baner på vegne av klubben',
  'links.capability.link.booking.extended_horizon': 'booke lenger frem i kalenderen enn vanlig',
  'links.capability.link.booking.partner_rate': 'booke til avtalt partnerpris',
  'links.capability.link.activity.manage': 'opprette og drifte aktiviteter i den andres regi',
  'links.rate': 'Avtalt banepris',
  'links.rate.value': '{amount} kr per time',
  'links.rate.list': 'Senterets ordinære pris',
  'links.rate.input': 'Avtalt pris per time (kr, tomt = ordinær pris)',
  'links.rate.invalid': 'Prisen må være et helt antall kroner, 0 eller mer.',
  'links.save': 'Lagre',
  'links.saving': 'Lagrer …',
  'links.saved': 'Endringen er lagret.',
  'links.accept.choose': 'Hva får senteret hos dere?',
  'links.accept': 'Si ja',
  'links.accepted': 'Samarbeidet med {name} er i gang.',
  'links.decline': 'Si nei',
  'links.decline.question': 'Si nei til forslaget fra {name}?',
  'links.withdraw': 'Trekk tilbake',
  'links.withdraw.question': 'Trekke tilbake forslaget til {name}?',
  'links.end': 'Avslutt samarbeidet',
  'links.end.question': 'Avslutte samarbeidet med {name}?',
  'links.ended': 'Samarbeidet med {name} er avsluttet.',
  'links.propose.title': 'Foreslå samarbeid',
  'links.propose.lead':
    'Finn klubben, velg hva den får hos dere, og send forslaget. Klubben svarer på sin side.',
  'links.propose.club': 'Klubb',
  'links.propose.gives': 'Klubben får',
  'links.propose.submit': 'Send forslag',
  'links.propose.done': 'Forslaget er sendt. Klubben svarer på sin side.',
  // The server's codes for this page, as sentences.
  'errors.only_a_centre_proposes':
    'Det er senteret som foreslår et samarbeid. En klubb svarer på forslag.',
  'errors.only_the_club_accepts': 'Det er klubben som sier ja til et forslag.',
  'errors.not_proposed': 'Forslaget er allerede besvart.',
  'errors.not_accepted': 'Klubben velger hva den gir når den sier ja.',
  'errors.terms_are_the_centres': 'Den avtalte prisen er senterets å sette.',
  'errors.link_exists': 'Dere har allerede et samarbeid eller et forslag med denne klubben.',
  'errors.unknown_club': 'Fant ikke klubben.',
  'errors.not_a_club':
    'Det er et senter, ikke en klubb. Et samarbeid inngås mellom et senter og en klubb.',
  'errors.unknown_link_role': 'Det valget kan ikke gis i et samarbeid.',
  // ── «Book baner hos senteret» — a club books a centre's courts through a
  // co-operation (slice 4c, 2026-10-20). The player owes nothing; the lead
  // says so, because «free» on a court is otherwise read as a mistake.
  'partnerCourts.title': 'Baner hos partnersenter',
  'partnerCourts.lead': 'Klubben samarbeider med et senter og kan booke baner der.',
  'partnerCourts.open': 'Book baner hos {centre}',
  'linkBooking.title': 'Book baner hos {centre}',
  'linkBooking.lead':
    'Bookingen gjøres for {club}. Spilleren betaler ikke — senteret fakturerer klubben.',
  'linkBooking.leadPending': 'Henter ledige tider …',
  'linkBooking.back': 'Tilbake',
  'linkBooking.loading': 'Henter ledige tider …',
  'linkBooking.forbidden': 'Du kan ikke booke baner hos dette senteret for klubben.',
  'linkBooking.unavailable': 'Kunne ikke hente ledige tider akkurat nå.',
  'linkBooking.retry': 'Prøv igjen',
  'linkBooking.terms.partner': 'Prisen er den avtalte partnerprisen.',
  'linkBooking.terms.list': 'Prisen er senterets ordinære pris.',
  'linkBooking.terms.window': 'Klubben kan booke {days} dager frem.',
  'linkBooking.date': 'Dag',
  'linkBooking.duration': 'Varighet',
  'linkBooking.minutes': '{minutes} min',
  'linkBooking.noSlots': 'Ingen ledige baner denne dagen med denne varigheten.',
  'linkBooking.charge': '{amount} kr til klubben',
  'linkBooking.court': 'Bane {court}',
  'linkBooking.booked': 'Bane {court} er booket {date} kl. {time}.',
  'linkBooking.cancelled': 'Bookingen er avbestilt.',
  'linkBooking.mine.title': 'Bookinger for {club}',
  'linkBooking.mine.none': 'Klubben har ingen kommende bookinger her.',
  'linkBooking.cancel.question': 'Avbestille bookingen {date} kl. {time}?',
  'linkBooking.cancel.confirm': 'Avbestill',
  'links.window': 'Partnerklubben kan booke',
  'links.window.value': '{days} dager frem',
  'links.window.ordinary': 'Senterets vanlige grense',
  'links.window.input': 'Dager frem klubben kan booke (tomt = senterets vanlige grense)',
  'links.window.invalid': 'Antall dager må være et helt tall fra 1 til 365.',
  'errors.link_not_active': 'Samarbeidet er ikke aktivt lenger.',
  'errors.date_outside_booking_window': 'Den dagen kan ikke bookes ennå.',
  'errors.slot_not_available': 'Tiden er ikke ledig lenger. Velg en annen.',
  'errors.slot_in_the_past': 'Den tiden har allerede begynt.',
  'errors.duration_not_allowed': 'Den varigheten tilbys ikke her.',
  'errors.court_out_of_service': 'Banen er ute av drift.',
  'errors.booking_started': 'Bookingen har startet. Kontakt senteret for å endre den.',
  'errors.link_home_required': 'Velg hvilken klubb bookingen gjelder.',
  'errors.request_id_reused': 'Bookingen ble sendt to ganger med ulikt innhold. Prøv igjen.',
  'errors.booking_rejected': 'Bookingen ble avvist av senterets regler.',
  'errors.invalid_booking_window': 'Antall dager må være et helt tall fra 1 til 365.',
  'errors.booking_has_payment': 'Bookingen har en betaling knyttet til seg. Kontakt senteret.',
  // A staff delete cancels any live payment at Stripe first (#1310).
  'errors.payment_cancel_unconfirmed':
    'Stripe bekreftet ikke at den åpne betalingen ble avbrutt – medlemmet kan ha betalt. Bookingen er ikke slettet. Prøv igjen om noen minutter.',
  'errors.booking_payment_open': 'Bookingen har en betaling som pågår. Prøv igjen om et øyeblikk.',
  'errors.payment_cancel_unavailable':
    'Bookingen har en åpen betaling, og kortbetaling er ikke satt opp her, så den kan ikke avbrytes.',
  // ── «Aktiviteter hos klubben» — a centre arranges activities in a club's
  // name through a co-operation (slice 4d, 2026-10-21; §4k 1A–4A).
  'partnerActivities.title': 'Aktiviteter hos partnerklubb',
  'partnerActivities.lead': 'Senteret samarbeider med en klubb og kan arrangere aktiviteter der.',
  'partnerActivities.open': 'Aktiviteter hos {club}',
  'linkActivities.title': 'Aktiviteter hos {club}',
  'linkActivities.lead':
    '{centre} arrangerer disse i klubbens navn. Klubbens spillere melder seg på i klubben.',
  'linkActivities.back': 'Tilbake',
  'linkActivities.loading': 'Henter aktiviteter …',
  'linkActivities.forbidden': 'Du kan ikke arrangere aktiviteter hos denne klubben for senteret.',
  'linkActivities.unavailable': 'Kunne ikke hente aktivitetene akkurat nå.',
  'linkActivities.retry': 'Prøv igjen',
  'linkActivities.courts':
    'Baner bookes av senteret på vanlig måte — en aktivitet her reserverer ingen bane.',
  'linkActivities.new.title': 'Ny aktivitet',
  'linkActivities.edit.title': 'Endre aktivitet',
  'linkActivities.edit.stop': 'Avbryt endring',
  'linkActivities.field.name': 'Navn',
  'linkActivities.field.kind': 'Aktivitetstype',
  'linkActivities.field.date': 'Dato',
  'linkActivities.field.capacity': 'Maks antall deltakere (tomt = ubegrenset)',
  'linkActivities.field.start': 'Starter',
  'linkActivities.field.end': 'Slutter',
  'linkActivities.field.description': 'Beskrivelse',
  'linkActivities.kind.arrangement': 'Arrangement',
  'linkActivities.kind.coaching': 'Trening',
  'linkActivities.create': 'Opprett aktivitet',
  'linkActivities.save': 'Lagre endringer',
  'linkActivities.created': '«{name}» er opprettet hos klubben.',
  'linkActivities.saved': '«{name}» er lagret.',
  'linkActivities.cancelled': '«{name}» er avlyst.',
  'linkActivities.none': 'Senteret har ingen aktiviteter hos klubben ennå.',
  'linkActivities.status.cancelled': 'Avlyst',
  'linkActivities.signups': '{count} påmeldt',
  'linkActivities.signupsOf': '{count} av {max} påmeldt',
  'linkActivities.waitlist': 'venteliste',
  'linkActivities.edit': 'Endre',
  'linkActivities.cancel': 'Avlys',
  'linkActivities.cancel.question': 'Avlyse «{name}»? De påmeldte ser at den er avlyst.',
  'linkActivities.cancel.confirm': 'Avlys',
  'linkActivities.invalid.name': 'Aktiviteten må ha et navn.',
  'linkActivities.invalid.date': 'Velg en dato.',
  'linkActivities.invalid.capacity': 'Maks antall må være et helt tall.',
  'errors.invalid_name': 'Aktiviteten må ha et navn (høyst 120 tegn).',
  'errors.invalid_date': 'Datoen er ugyldig.',
  'errors.invalid_time': 'Klokkeslettet er ugyldig, eller slutten er før starten.',
  'errors.invalid_capacity': 'Maks antall deltakere må være et helt tall fra 1 og oppover.',
  'errors.invalid_kind': 'Den aktivitetstypen kan ikke velges her.',
  'errors.invalid_description': 'Beskrivelsen er for lang.',
  'errors.date_in_the_past': 'Datoen har allerede vært.',
  'errors.activity_cancelled': 'Aktiviteten er avlyst og kan ikke endres.',
  'errors.activity_not_found': 'Aktiviteten finnes ikke lenger.',
  // The system roles, as the person sees them. A custom role is shown by its key.
  'role.PLATFORM_SUPERADMIN': 'Global administrator',
  'role.PLATFORM_SUPPORT': 'Support',
  'role.PLATFORM_FINANCE': 'Økonomi',
  'role.PLATFORM_OPERATIONS': 'Drift',
  'role.PLATFORM_READONLY': 'Lesetilgang (plattform)',
  'role.TENANT_OWNER': 'Eier',
  'role.TENANT_ADMIN': 'Administrator',
  'role.TENANT_MANAGER': 'Daglig leder',
  'role.TENANT_STAFF': 'Ansatt',
  'role.TENANT_READONLY': 'Lesetilgang',
  // The module roles (#843) — named as `SYSTEM_ROLES` names them (#861).
  'role.TENANT_RECEPTION': 'Resepsjon',
  'role.TENANT_VENUE': 'Anlegg',
  'role.TENANT_TOURNAMENTS': 'Turneringsleder',
  'role.TENANT_ACTIVITIES': 'Aktivitetsleder',
  'role.TENANT_MEMBERSHIP': 'Medlemsansvarlig',
  'role.TENANT_COMMUNICATION': 'Kommunikasjon',
  'role.TENANT_FINANCE': 'Økonomiansvarlig',
  // The two LINK roles (slice 4a): what a centre gives a club, and what a club gives a centre.
  'role.LINK_PARTNER_CLUB': 'Partnerklubb',
  'role.LINK_HOST_CENTRE': 'Vertssenter',
  'account.language': 'Språk',
  // CONDITIONAL, so the sentence says the RULE rather than one state (#310).
  // «Lagres i denne nettleseren» stopped being true the moment a signed-in
  // choice started going to the account — and «lagres på kontoen din» would
  // be just as wrong on the two screens that reach Min konto with no club
  // session. Both halves, in both catalogs; pinned by
  // tests/i18n-account-locale.test.tsx.
  'account.language.help':
    'Når du er innlogget, følger valget kontoen din. Ellers lagres det bare i denne nettleseren.',
  // ── the language picker's own names (#310) ────────────────────────
  // ENDONYMS: each language written in itself, identical in every catalog.
  // The one control that must work for somebody who cannot read the current
  // UI — see components/ui/LanguagePicker.tsx.
  'language.endonym.nb': 'Norsk',
  'language.endonym.en': 'English',
  'account.language.nb': 'Norsk (bokmål)',
  'account.language.en': 'Engelsk',
  'account.email.unknown': 'Ukjent e-post',

  // Why the session ended. The gate hands the SCREEN a code, never a sentence —
  // see LoginScreen's `notice` prop.
  'auth.login.sessionExpired': 'Økten er utløpt. Logg inn på nytt.',
  'auth.login.needsServer': 'Innlogging krever tilkobling til serveren.',
  'auth.forgot.checkEmail': 'Sjekk e-posten din',
  'auth.noScope.notFound': 'Fant ikke tilgangene dine',
  'auth.noScope.none': 'Ingen tilgang enda',
  'auth.scope.selectOrg': 'Velg organisasjon',
  'auth.scope.kind.centre': 'Senter',
  'auth.scope.kind.club': 'Klubb',
  'app.footer': '{brand} · Klubbplattform',
  // The DEV menu — development tooling, shown only where the API offers dev
  // personas. New file, so it goes through the catalog rather than onto the
  // frozen legacy list.
  'dev.menu.title': 'DEV',
  'dev.menu.lead': 'Utviklerverktøy',
  'dev.menu.buildUnknown': 'Ukjent bygg — ingen commit-info.',
  'dev.menu.rights': 'Rettigheter — bytt rolle',
  'dev.menu.report': 'Rapportér bug eller forslag',
  'dev.menu.archive': 'Se alle bugs og forslag',
  'dev.menu.titleCounts': '{bugs} åpne bugs, {features} forslag',
  'dev.menu.titleEmpty': 'Utviklerverktøy — roller, bug og forslag',
  // Who is signed in and which role they hold here (2026-10-19). The persona
  // names say which test account; these lines say what it may do.
  'dev.menu.signedInAs': 'Innlogget som {name}',
  'dev.menu.unknownAccount': 'ukjent konto',
  'dev.menu.roleIn': 'Rolle: {roles} i {organization}',
  'dev.menu.noRoleIn': 'Ingen rolle i {organization}',
  'dev.menu.noOrganization': 'Ingen organisasjon valgt',
  'dev.menu.roleLoading': 'Henter rollen …',
  'dev.menu.roleUnknown': 'Kunne ikke hente rollen akkurat nå',
  // The RBAC scenario builder (#1024): any organizations, any of their roles,
  // applied to an isolated dev identity. Role and organization NAMES are data
  // from the database; only the tool's own words are here.
  'dev.rbac.open': 'RBAC-scenario …',
  'dev.rbac.presets': 'Faste personaer',
  'dev.rbac.title': 'RBAC-scenario',
  'dev.rbac.lead':
    'Velg roller i én eller flere organisasjoner. En egen dev-identitet får nøyaktig dette — det den hadde fra før fjernes — og du logges inn som den.',
  'dev.rbac.close': 'Lukk',
  'dev.rbac.identity': 'Dev-identitet',
  'dev.rbac.player': 'Med spillerprofil',
  'dev.rbac.filter': 'Filtrer organisasjoner',
  'dev.rbac.ownRole': 'egen rolle',
  'dev.rbac.selected': '{count} roller valgt',
  'dev.rbac.clear': 'Tøm valget',
  'dev.rbac.apply': 'Bruk og logg inn',
  'dev.rbac.applying': 'Bytter …',
  'dev.rbac.loading': 'Henter organisasjoner og roller …',
  'dev.rbac.unavailable': 'Scenario-verktøyet er ikke tilgjengelig her.',
  'dev.rbac.union.title': 'Effektive rettigheter nå',
  'dev.rbac.union.lead': 'Slik serveren svarer for {name} (fra /me).',
  'dev.rbac.union.none': 'Ingen organisasjonsroller.',
  'dev.rbac.union.loading': 'Henter …',
  'dev.rbac.union.unknown': 'Kunne ikke hente rettighetene akkurat nå.',
  'dev.rbac.union.roles': 'Roller: {roles}',
  'dev.rbac.union.from': 'fra {roles}',
  'dev.rbac.error.role': 'En valgt rolle hører ikke til organisasjonen. Ingenting ble endret.',
  'dev.rbac.error.organization': 'En valgt organisasjon finnes ikke lenger. Ingenting ble endret.',
  'dev.rbac.error.changed': 'Scenarioet ble endret samtidig. Prøv igjen.',
  'dev.rbac.error.identity':
    'Dev-identiteten er ikke en reservert dev-konto. Ingenting ble endret.',
  'dev.rbac.error.generic': 'Kunne ikke bruke scenarioet.',
  'app.signOutBrowser': 'Logg ut av denne nettleseren.',
  'app.retry': 'Prøv igjen',
  'app.version': 'App-versjon',
  'app.clubLogo': 'Klubblogo',
  'app.messages': 'Meldinger',
  'app.messages.unread': 'Meldinger ({count} uleste)',
  // The header bell — what waits for whoever runs the organization (owner,
  // 2026-10-20). One sentence per action kind; the destination is the nav
  // item's own name so the menu and the menu bar agree.
  'app.pendingActions': 'Varsler',
  'app.pendingActions.waiting': 'Varsler ({count} venter på deg)',
  'app.pendingActions.heading': 'Venter på deg',
  'app.pendingActions.none': 'Ingenting venter på deg akkurat nå.',
  'app.pendingActions.joinRequests.one': '{count} søknad om medlemskap',
  'app.pendingActions.joinRequests.other': '{count} søknader om medlemskap',
  'app.pendingActions.goTo': 'Gå til {destination}',
  'app.noClub': 'Ingen klubb',
  'app.courts': '{count} baner',
  'app.clubSettings': '{club} — klikk for å redigere klubbinnstillinger',
  // No club yet — the same control, but an invitation rather than a label.
  'app.clubSettings.setup': 'Sett opp klubbinformasjon',
  'app.activeTournament': 'Aktiv',
  'app.cancel': 'Avbryt',
  // Inline confirmation for destructive actions (#589). `window.confirm` is
  // blocked in an iframe and simply does nothing, so the question is asked in
  // the page — see components/ui/ConfirmButton.tsx.
  'ui.confirm.confirm': 'Ja, fortsett',
  'ui.confirm.cancel': 'Avbryt',
  'ui.confirm.question': 'Er du sikker?',
  'ui.sortableHeader.sortBy': 'Sortér etter {column}',
  'tournament.wizard.setupSaveFailed': 'Oppsettet ble ikke lagret. {reason}',
  // A tournament write the server refused or did not confirm (#1305). Owner-
  // approved wording, 2026-11-02. The action re-reads what is STORED and shows
  // that — it never re-sends the copy that was refused.
  'tournament.saveFailed.message': 'Turneringen ble ikke lagret — prøv igjen',
  'tournament.saveFailed.reload': 'Last inn på nytt',
  'auth.forgot.throttled': 'For mange forsøk. Prøv igjen om {minutes} minutter.',
  'auth.forgot.network': 'Fikk ikke kontakt med serveren. Prøv igjen.',
  'auth.forgot.acceptedLead':
    'Finnes det en konto for denne adressen, er en lenke for å velge nytt passord på vei. Lenken virker i {minutes} minutter.',
  // The hint under the field, before anything is typed — shorter on purpose
  // than the refusal above, which explains a failure that already happened.
  'auth.noScope.lookupFailed':
    'Du er logget inn{as}, men vi klarte ikke å hente hvilke organisasjoner du har tilgang til. Prøv igjen.',
  'auth.noScope.noOrg':
    'Du er logget inn{as}, men kontoen din er ikke koblet til noen organisasjon. Be en administrator om tilgang.',
  'auth.noScope.as': ' som {email}',
  'auth.toLogin': 'Til innlogging',
  'auth.scope.leadShort': 'Du har tilgang til flere. Velg hvilken du vil jobbe i.',

  // ── shared UI primitives (components/ui, #310) ────────────────────
  'ui.nav.menu': 'Meny',
  'ui.nav.pinLeft': 'Fest meny til venstre',
  'ui.nav.unpin': 'Frigjør meny',
  'ui.nav.item.home': 'Hjem',
  'ui.nav.item.createTournament': 'Opprett turnering',
  'ui.nav.item.bookings': 'Banebooking',
  'ui.nav.item.activities': 'Aktiviteter',
  // A product name, identical in every language — see the contract test's
  // SAME_ON_PURPOSE list.
  'ui.nav.item.banepad': 'Banepad',
  'ui.nav.item.mobile': 'Mobilapp',
  'ui.nav.item.messages': 'Meldinger',
  'ui.nav.item.players': 'Spillerdatabase',
  'ui.nav.item.archive': 'Turneringsarkiv',
  'ui.nav.item.reporting': 'Reporting',
  'ui.nav.appLabel': '{brand}-app',
  'ui.nav.changelog': 'Endringslogg',
  'ui.nav.changelogTitle': 'Endringslogg for {brand}-appen',
  'ui.nav.engineTitle': 'Om {engine} — algoritmer, ELO, trekning',
  'ui.status.ready': 'Klar til start',
  'ui.status.running': 'Pågår',
  'ui.status.paused': 'Pauset',
  'ui.status.finals': 'Finaler',
  'ui.status.done': 'Ferdig',
  'ui.progress.label': 'Fremgang',
  'ui.progress.matches': '{done}/{total} kamper · {pct}%',
  'ui.separator.newDay': 'Ny dag',
  'ui.separator.start': 'Start {time}',
  'ui.separator.minutes': '{count} minutter',
  'ui.separator.gap': 'Opphold',
  'ui.separator.minutesShort': '{count} min',
  'ui.numInput.lockedHint': 'Låst — styres av valgt oppsett',
  'ui.numInput.decrease': 'Minus',
  'ui.numInput.increase': 'Pluss',
  'ui.setupNumber.decrease': 'Mindre',
  'ui.setupNumber.increase': 'Mer',
  'ui.lockedField.edit': 'Endre',
  'ui.lockedField.lock': 'Lås',
  // The shared modal's close control (#781 slice 9). An `<X/>` glyph has no
  // accessible name of its own, so the dialog primitive gives it one.
  'ui.dialog.close': 'Lukk',
  'ui.stepper.decrease': 'Reduser',
  'ui.stepper.increase': 'Øk',
  // The club-championship and single-match modals (#900): names for controls
  // that were an icon, a bare number or an unlabelled field.
  'km.datePicker.label': 'Velg dato',
  'km.divBadge.title': 'Divisjon {division}',
  'km.datePicker.prevMonth': 'Forrige måned',
  'km.datePicker.nextMonth': 'Neste måned',
  'km.playerModal.recordResult': 'Registrer resultat: {home} mot {away}',
  'singleMatches.picker.clearSlot': 'Fjern {player}',
  'singleMatches.add.date': 'Dato',
  'singleMatches.add.time': 'Klokkeslett',
  // The bug archive's ticket dialog (#900): its comment field had only a
  // placeholder for a name.
  'bugs.detail.newComment': 'Ny kommentar',
  // Bug reports open to every user (owner decision 2026-10-22): why a report
  // was not kept, by the server's refusal code; which club a report came from
  // in the administrators' cross-club archive; and the member app's door.
  'bugs.report.refused.closed': 'Rapporten ble ikke lagret: innmelding av feil er ikke åpnet her.',
  'bugs.report.refused.rateLimited':
    'Rapporten ble ikke lagret: du har sendt mange rapporter på kort tid. Prøv igjen senere.',
  'bugs.report.refused.invalid':
    'Rapporten ble ikke lagret: noe i den kunne ikke tas imot. Sjekk tittel, beskrivelse og bilde.',
  'bugs.report.refused.tooLarge':
    'Rapporten ble ikke lagret: den er for stor. Prøv et mindre bilde.',
  'bugs.report.refused.failed': 'Rapporten ble ikke lagret. Prøv igjen om litt.',
  'bugs.archive.club': 'Klubb: {name}',
  'bugs.archive.unknownClub': 'ukjent klubb',
  'bugs.archive.reporter': 'Meldt av {name}',
  'mobile.profile.reportBug': 'Meld feil eller forslag',
  'mobile.profile.reportBugHint': 'Fortell oss om noe som ikke virker, eller som kunne vært bedre',
  // The tournament archive (#900): its search field and its row checkboxes
  // had no name.
  'archive.searchLabel': 'Søk i arkivet',
  'archive.selectRow': 'Velg {name}',
  // /sa's payment-account field (#900): it had only its placeholder.
  'platform.organizations.stripeAccountLabel': 'Betalingskonto',
  // /sa → Organisasjoner → Inviter (#545): the address belongs to an account
  // that signs in through ANOTHER provider (Entra), and inviting it would have
  // made the person a second, separate account. The server refuses with the
  // code `identity_ambiguous`; this is the sentence. Said plainly because the
  // operator may search the directory anyway. Linking the two sign-ins is a
  // later decision (#437), so it offers no remedy the system does not have.
  //
  // Since #1047 the same code also answers an address that ANOTHER ACCOUNT
  // already carries without an e-mail sign-in for it — so the sentence says
  // «another account», with Entra as the usual example, not Entra alone.
  'platform.organizations.invite.identityAmbiguous':
    'Adressen tilhører allerede en annen konto som ikke logger inn med e-post (for eksempel med Entra). Ingen ny konto ble opprettet, og ingen tilgang ble gitt.',
  // `email_in_use` (#1047): another account took the address between the
  // server's check and its insert, and the database refused the second holder.
  // Rare — a race. Trying again shows which case it is.
  'platform.organizations.invite.emailInUse':
    'Adressen ble nettopp tatt i bruk av en annen konto. Ingen ny konto ble opprettet, og ingen tilgang ble gitt. Prøv igjen for å se hvilken konto den tilhører.',
  'paymentAccount.title': 'Betaling',
  'paymentAccount.lead':
    'Organisasjonen er selger når noen betaler for det den tilbyr. Betalingskontoen settes opp hos Stripe, her i StarPoint.',
  'paymentAccount.ownerOnly':
    'Bare organisasjonens eier kan sette opp og administrere betalingskontoen.',
  'paymentAccount.notAvailable': 'Betaling er ikke slått på i dette miljøet.',
  'paymentAccount.unreachable': 'Fikk ikke kontakt med serveren. Prøv igjen.',
  'paymentAccount.providerUnavailable': 'Stripe svarer ikke akkurat nå. Prøv igjen om litt.',
  'paymentAccount.providerRefused': 'Stripe avviste forespørselen ({code}).',
  'paymentAccount.reauth':
    'Bekreft at det er deg, så kan du fortsette. Oppsettet krever at du har logget inn de siste 15 minuttene.',
  'paymentAccount.error': 'Noe gikk galt ({code}).',
  'paymentAccount.loading': 'Henter status fra Stripe…',
  'paymentAccount.notConnected': 'Organisasjonen har ingen betalingskonto ennå.',
  'paymentAccount.open': 'Opprett betalingskonto',
  'paymentAccount.opening':
    'Et tidligere forsøk på å opprette kontoen ble ikke fullført. Trykk for å fullføre det — det opprettes ikke en ny konto.',
  'paymentAccount.finish': 'Fullfør opprettelsen',
  'paymentAccount.ambiguous':
    'Stripe har mer enn én konto for organisasjonen. StarPoint må koble til riktig konto før du kan fortsette — ta kontakt med oss.',
  'paymentAccount.inProgress': 'Kontoen opprettes akkurat nå. Prøv igjen om et øyeblikk.',
  'paymentAccount.working': 'Et øyeblikk…',
  'paymentAccount.continue': 'Fortsett oppsettet',
  'paymentAccount.manage': 'Administrer betalingskontoen',
  'paymentAccount.close': 'Lukk',
  'paymentAccount.refresh': 'Oppdater status',
  'paymentAccount.stripeSignIn':
    'Stripe ber deg logge inn i et eget vindu. Det er Stripe, ikke StarPoint, som tar vare på identitet og bankopplysninger.',
  'paymentAccount.native':
    'Betalingskontoen settes opp i en nettleser. Åpne StarPoint i en nettleser på PC eller mobil.',
  'paymentAccount.noKey': 'Betalingsnøkkelen mangler i denne versjonen av appen.',
  'paymentAccount.loadingStripe': 'Laster Stripe…',
  'paymentAccount.status.charges': 'Kan ta imot betaling',
  'paymentAccount.status.payouts': 'Utbetaling til bank',
  'paymentAccount.status.yes': 'Ja',
  'paymentAccount.status.no': 'Nei',
  'paymentAccount.status.due': 'Opplysninger som mangler: {count}',
  'paymentAccount.status.pastDue': 'Forfalt: {count}. Betaling kan være stanset til de er levert.',
  'paymentAccount.status.pending': 'Stripe kontrollerer opplysningene.',
  'paymentAccount.status.ready': 'Kontoen er klar til å ta imot betaling.',
  'paymentAccount.payouts.open': 'Utbetalinger',
  'paymentAccount.payouts.lead':
    'Saldo, utbetalingsplan og utbetalingene til organisasjonens bankkonto. Utbetalingsplanen endres ikke her.',
  'paymentAccount.payments.open': 'Betalinger og tvister',
  'paymentAccount.payments.lead':
    'Betalingene organisasjonen har tatt imot, og tvister du kan svare på. Tilbakebetalinger gjøres i StarPoint, ikke her.',
  'paymentAccount.afterSetup':
    'Utbetalinger, betalinger og tvister blir tilgjengelige her når oppsettet hos Stripe er sendt inn.',
  'ui.tennisBall.server': 'Server',
  // ELO WEIGHT — the SHARED wording. Every adjustment surface (single match,
  // KM, americano …) describes a given weight with the same sentence, which
  // is why these live in one place rather than next to each slider.
  'ui.eloWeight.label': 'ELO-vekt',
  'ui.eloWeight.lockHint': 'Klikk for å låse — beskytter mot utilsiktet endring',
  'ui.eloWeight.unlockHint': 'Klikk for å låse opp slideren',
  'ui.eloWeight.unlocked': '🔓 Låst opp',
  'ui.eloWeight.edit': '🔒 Endre',
  'ui.eloWeight.recommended': '(anbefalt: {percent}%)',
  'ui.eloWeight.level0.short': 'Treningskamp',
  'ui.eloWeight.level0.desc': 'Treningskamp — teller ikke på ELO. For trening og uformelt spill.',
  'ui.eloWeight.level50.short': 'Americano-stil',
  'ui.eloWeight.level50.desc': 'Americano-stil — korte, roterende kamper. Halv ELO-effekt.',
  'ui.eloWeight.level100.short': 'Best av 1 sett',
  'ui.eloWeight.level100.desc': 'Best av 1 sett — ett avgjørende sett. Standard ELO-effekt.',
  'ui.eloWeight.level150.short': 'Avkortet (super-tiebreak)',
  'ui.eloWeight.level150.desc':
    'Avkortet kamp — super-tiebreak i stedet for fullt 3. sett. Forhøyet ELO-effekt.',
  'ui.eloWeight.level200.short': 'Full best av 3',
  'ui.eloWeight.level200.desc':
    'Full kamp — best av 3 hele sett. Full ELO-effekt; kampen vektes tyngst.',
  'ui.eloWeight.mixShort': 'Mix: {below} / {above}',
  'ui.eloWeight.mixDesc': 'Mellom «{below}» ({belowPct} %) og «{above}» ({abovePct} %).',
  'ui.eloWeight.customDesc': '{percent} % ELO-vekt.',
  // ═══ BOOKING SURFACE (#310) — one contiguous block ═══
  // ── booking: shared controls ──────────────────────────────────────
  'booking.cancel': 'Avbryt',
  'booking.close': 'Lukk',
  'ui.toast.dismiss': 'Lukk meldingen',
  'ui.skipToContent': 'Hopp til innholdet',
  // ── booking: court facility icons ─────────────────────────────────
  'booking.court.type.single': 'Single-bane (1v1)',
  'booking.court.type.double': 'Dobbel-bane (2v2)',
  'booking.court.runoff': 'Mulig å løpe ut av banen',
  'booking.court.streaming': 'Streaming-utstyr',
  // ── booking: mock payment terminal ────────────────────────────────
  'booking.payment.title': 'Betaling',
  'booking.payment.mockNote': 'Mock-betalingsterminal — ingen faktisk transaksjon utføres.',
  'booking.payment.payingFor': 'Du betaler for',
  'booking.payment.total': 'Totalt',
  'booking.payment.amountKr': '{amount} kr',
  'booking.payment.declined': 'Betalingen ble avvist. Prøv igjen eller velg annen metode.',
  'booking.payment.processing': 'Behandler betaling …',
  'booking.payment.checkVipps': 'Sjekk Vipps-appen',
  'booking.payment.contactBank': 'Kontakter banken',
  'booking.payment.chooseMethod': 'Velg betalingsmetode',
  'booking.payment.vippsHint': 'Du får et varsel på mobilen for å bekrefte',
  'booking.payment.card': 'Kort',
  'booking.payment.cancelling': 'Avbryt …',
  // ── booking: the rules explainer ──────────────────────────────────
  'booking.help.title': 'Slik fungerer banebooking',
  'booking.help.steps.title': '📅 Bestilling i tre steg',
  'booking.help.steps.one.before': 'Velg',
  'booking.help.steps.one.date': 'dato',
  'booking.help.steps.two.before': 'Velg',
  'booking.help.steps.two.start': 'starttid',
  'booking.help.steps.two.after':
    'klikk på et ledig tidspunkt i kalenderen, eller bla deg gjennom «Ledige tider»-fanen.',
  'booking.help.steps.three.before': 'I vinduet som åpnes: velg',
  'booking.help.steps.three.duration': 'varighet',
  'booking.help.steps.three.and': 'og',
  'booking.help.steps.three.court': 'bane',
  'booking.help.steps.three.after': 'og betal.',
  'booking.help.times.title': '🎯 Tillatte starttider og varigheter',
  'booking.help.times.before': 'Bookinger starter alltid på',
  'booking.help.times.halfHour': 'hel eller halv time',
  'booking.help.times.examples': '({open}, 07:30, 08:00 osv.). Du kan velge',
  'booking.help.times.durationsPrefix': 'varigheter på',
  'booking.help.times.durationsValue': '{list} minutter',
  'booking.help.times.durationsAny': 'varigheter etter behov',
  'booking.help.times.open': 'Senteret er åpent',
  'booking.help.gap.title': '⚖ Gap-regelen (forklart)',
  'booking.help.gap.offBefore': 'Gap-regelen er',
  'booking.help.gap.offStrong': 'slått av',
  'booking.help.gap.offAfter':
    'Bookinger kan ha hvilken som helst luft mellom seg (inkludert ingen).',
  'booking.help.gap.onBefore': 'Hull mellom to bookinger på samme bane må enten være',
  'booking.help.gap.onZero': '0 minutter',
  'booking.help.gap.onMiddle': '(rygg-mot-rygg) eller',
  'booking.help.gap.onMore': 'større enn {min} minutter',
  'booking.help.gap.onAfter':
    'Dette hindrer at det blir små døde hull i kalenderen som ingen kan bruke.',
  'booking.help.gap.exampleTitle': 'Eksempel — bestående booking 12:00–13:00:',
  'booking.help.gap.ok': '✓ OK',
  'booking.help.gap.rejected': '✗ Avvist',
  'booking.help.gap.example1': 'Ny booking 13:00–14:30 (rett etter, 0 min hull)',
  'booking.help.gap.example2':
    'Ny booking 13:30–14:30 (30 min hull — for lite til at noen annen får plass)',
  'booking.help.gap.example3':
    'Ny booking 14:30–16:00 (90 min hull etter — perfekt plass for en annen booking)',
  'booking.help.smart.title': '💚 Hvorfor kan jeg ikke alltid velge alle baner?',
  'booking.help.smart.p1Before':
    'Noen ganger er en bane ledig på papiret, men ikke valgbar. Det er fordi vi',
  'booking.help.smart.p1Strong': 'fordeler bookinger smart',
  'booking.help.smart.p1After':
    'hvis tiden din passer tettere på en annen ledig bane, styres du dit.',
  'booking.help.smart.p2':
    'Et eksempel: du vil booke 90 minutter. Bane 1 er ledig i et to-timers vindu, mens bane 2 har akkurat 90 minutter ledig. Da legges du på bane 2 — slik blir det ikke et lite, ubrukelig hull igjen på bane 1, og noen andre kan fortsatt bruke det store vinduet senere.',
  'booking.help.smart.p3Before': 'Resultatet:',
  'booking.help.smart.p3Strong': 'flest mulig får plass til å spille',
  'booking.help.smart.p3After':
    'Du betaler det samme uansett hvilken bane du får — banene er like gode.',
  'booking.help.multi.title': '🏟 Når flere baner er ledige',
  'booking.help.multi.body':
    'Vi viser alle baner som er ledige på det tidspunktet du har valgt. Velg den du vil. Hvis bare én bane er ledig fordi de andre ville skapt små døde hull, vil den banen vises som det eneste alternativet.',
  'booking.help.price.title': '💰 Pris og prissoner',
  'booking.help.price.p1a': 'Pris regnes ut fra en',
  'booking.help.price.p1Default': 'standardpris',
  'booking.help.price.p1b': '(grunnpris per time) pluss eventuelle',
  'booking.help.price.p1Zones': 'prissoner',
  'booking.help.price.p1c': 'som admin har definert. Du ser alltid totalprisen — eller',
  'booking.help.price.p1Free': '«Gratis»',
  'booking.help.price.p1d': '— før du bekrefter.',
  'booking.help.price.defaultLabel': 'Standardpris:',
  'booking.help.price.defaultText':
    'gjelder all tid som ikke dekkes av en sone. Kan settes til 0 (gratis).',
  'booking.help.price.zoneLabel': 'Prissone:',
  'booking.help.price.zoneText1':
    'en periode (f.eks. primetime kveld) med egen pris. En sone kan enten',
  'booking.help.price.zoneReplace': 'erstatte',
  'booking.help.price.zoneText2': 'standardprisen eller',
  'booking.help.price.zoneOnTop': 'legges på toppen',
  'booking.help.price.zoneText3': 'av den.',
  'booking.help.price.holidayLabel': 'Helligdager:',
  'booking.help.price.holidayText':
    'hver sone kan ta med, hoppe over, eller gjelde kun på helligdager. Helligdagssettet (NO/SE/DK) styres sentralt.',
  'booking.help.price.rangeLabel': 'Tidsrom:',
  'booking.help.price.rangeText':
    'en sone uten tidsrom gjelder hele døgnet. Sett et tidsrom (f.eks. 17:00–21:00) for at den bare skal gjelde primetime.',
  'booking.help.price.example':
    'Eksempel: standardpris 0 (gratis) + primetime-sone 200 kr/t fra 17:00. En booking 16:00–18:00 koster da 0 for 16–17 og 200 for 17–18 = 200 kr.',
  'booking.help.facilities.title': '🎾 Banefasiliteter',
  'booking.help.facilities.lead':
    'Hver bane viser ikoner for hva den støtter. Det hjelper deg å velge riktig bane:',
  'booking.help.facilities.double': 'Dobbel-bane (2v2) — eller',
  'booking.help.facilities.single': 'single-bane (1v1)',
  'booking.help.facilities.runoff': 'Mulig å løpe ut bak banen',
  'booking.help.facilities.streaming': 'Streaming-utstyr montert',
  'booking.help.calendar.title': '📋 Kalendervisning',
  'booking.help.calendar.p1a':
    'Kalendervisningen viser alle baner som rader og tiden bortover. En rød',
  'booking.help.calendar.nowLine': '«nå»-strek',
  'booking.help.calendar.p1b':
    'viser nåtidspunktet (kun i dag), og passerte bookinger er gråtonet. Bla bortover for å se hele dagen — banenavnene står fast til venstre.',
  'booking.help.calendar.p2a': 'Bookingene er',
  'booking.help.calendar.p2Strong': 'fargekodet etter hvem som har laget dem:',
  'booking.help.calendar.legend.user': 'Vanlig bruker',
  'booking.help.calendar.legend.admin': 'Senter / admin',
  'booking.help.calendar.legend.tournament': 'Turnering',
  'booking.help.calendar.legend.series': 'Gjentakende (serie)',
  'booking.help.recurring.title': '🔁 Gjentakende bookinger',
  'booking.help.recurring.p1a': 'Admin kan lage',
  'booking.help.recurring.p1Strong': 'gjentakende bookinger',
  'booking.help.recurring.p1b':
    '(f.eks. «hver mandag 18:00» eller «annenhver dag»). Disse merkes med et 🔁-ikon og hører sammen som en serie.',
  'booking.help.recurring.p2a':
    'Klikker du på en serie-booking, blir du spurt om du vil endre/kansellere',
  'booking.help.recurring.p2One': 'kun den ene forekomsten',
  'booking.help.recurring.p2Or': 'eller',
  'booking.help.recurring.p2All': 'hele serien',
  'booking.help.recurring.p2b':
    'Når du endrer hele serien kan du også justere selve mønsteret (hvor ofte, hvilke dager, når det slutter).',
  'booking.help.price2.title': '💰 Pris',
  'booking.help.price2.p1a': 'Pris regnes ut fra',
  'booking.help.price2.zones': 'prissoner',
  'booking.help.price2.p1b':
    'som admin har definert. Soner kan bestemme pris per ukedag, tidsrom (f.eks. primetime kveld), og helligdager. Du ser totalprisen før du betaler.',
  'booking.help.admin.title': '⚙ Admin-modus',
  'booking.help.admin.body':
    'Når admin-modus er på (haket av i toppen), hoppes betalingsflyten over. Admin kan da legge inn bookinger på vegne av spillere, booke flere baner samtidig, lage gjentakende serier, overstyre regler, og styre status (gratis, venter på betaling, betalt) direkte.',
  // Weekday shorthands, for DISPLAY only — never a stored value or a
  // comparison key (docs/architecture/DOMAIN_RULES.md §4).
  'booking.weekday.short.sun': 'søn',
  'booking.weekday.short.mon': 'man',
  'booking.weekday.short.tue': 'tir',
  'booking.weekday.short.wed': 'ons',
  'booking.weekday.short.thu': 'tor',
  'booking.weekday.short.fri': 'fre',
  'booking.weekday.short.sat': 'lør',
  // ── booking: the price-zone editor ───────────────────────────────
  'booking.zones.title': 'Pris-soner',
  'booking.zones.lead':
    'Definér hva en bane koster på ulike tidspunkt. Høyere prioritet vinner ved overlapp.',
  'booking.zones.holidayPopup.title': 'Helligdager ({country})',
  'booking.zones.holidayPopup.lead':
    'Kommende helligdager i det aktive settet. Endre land og dager i Innstillinger.',
  'booking.zones.holidayPopup.empty': 'Ingen kommende helligdager i settet.',
  'booking.zones.holidayPopup.fallbackName': 'Helligdag',
  'booking.zones.defaultPrice': 'Standardpris',
  'booking.zones.defaultPriceHelp': 'Gjelder all tid som ikke dekkes av en sone under.',
  'booking.zones.defaultPriceFree': 'Nå: gratis.',
  'booking.zones.free': 'Gratis',
  'booking.zones.perHour': 'kr/t',
  'booking.zones.holidaysToggle': '📅 Helligdager ({country})',
  'booking.zones.hide': '▲ Skjul',
  'booking.zones.showDays': '▼ Vis dager',
  'booking.zones.holidaysInlineLead': 'Settet styres i Innstillinger. Kommende dager:',
  'booking.zones.holidaysInlineEmpty': 'Ingen kommende helligdager.',
  'booking.zones.customHoliday': 'Egendefinert',
  // #1312 (owner decision 2026-11-03): no zone, no court price — the default
  // price only fills the time BETWEEN zones.
  'booking.zones.empty':
    'Ingen pris-soner ennå. Prisen er ikke satt, og ingenting kan betales før du legger til minst én sone.',
  'booking.zones.collapseZone': 'Minimer sone',
  'booking.zones.expandZone': 'Utvid sone',
  'booking.zones.zoneName': 'Sonenavn',
  'booking.zones.zoneNamePlaceholder': 'F.eks. Primetime',
  'booking.zones.price': 'Pris',
  'booking.zones.priorityHelp': 'Hvis to soner overlapper, vinner den med høyest prioritet',
  'booking.zones.priority': 'Prio.',
  'booking.zones.priorityInputHelp': 'Høyere tall vinner ved overlapp',
  'booking.zones.confirmDelete': 'Bekreft sletting',
  'booking.zones.delete': 'Slett',
  'booking.zones.deleteZone': 'Slett sone',
  'booking.zones.summary.onlyHolidays': 'Kun helligdager',
  'booking.zones.summary.allDays': 'Alle dager',
  'booking.zones.summary.allDay': 'Hele døgnet',
  'booking.zones.summary.modeAdd': '+ standard',
  'booking.zones.summary.modeReplace': 'erstatter standard',
  'booking.zones.priceMode': 'Hvordan prisen virker',
  'booking.zones.priceModeReplace': 'Erstatt standardpris',
  'booking.zones.priceModeAdd': 'I tillegg til standard',
  'booking.zones.priceModeReplaceHelp':
    'I disse timene koster det {price} kr/t — standardprisen ignoreres.',
  'booking.zones.priceModeAddHelp': 'I disse timene koster det standardpris + {price} kr/t.',
  'booking.zones.weekdays': 'Ukedager (tomt = alle)',
  'booking.zones.holidays': 'Helligdager',
  'booking.zones.showDaysCountry': 'Vis dager ({country})',
  'booking.zones.onlyHolidaysNoteBefore': 'Denne sonen gjelder',
  'booking.zones.onlyHolidaysNoteStrong': 'kun på helligdager',
  'booking.zones.onlyHolidaysNoteAfter': '— ukedagene over ignoreres.',
  'booking.zones.holidayWhen': 'Når en valgt dag lander på en helligdag:',
  'booking.zones.holidayInclude': 'Ta med',
  'booking.zones.holidaySkip': 'Hopp over',
  'booking.zones.onlyHolidaysCheckbox': 'Egen sone kun for helligdager',
  'booking.zones.tipBefore': 'Tips: sett prisen over til',
  'booking.zones.tipStrong': '0 / Gratis',
  'booking.zones.tipAfter':
    'for å gjøre det gratis å booke på helligdager. Eller en egen helligdagspris.',
  'booking.zones.timeRanges': 'Tidsrom (tomt = hele døgnet)',
  'booking.zones.rangeFrom': 'Fra',
  'booking.zones.rangeTo': 'Til',
  'booking.zones.removeRange': 'Fjern tidsrom',
  'booking.zones.addTimeRange': '+ Legg til tidsrom',
  'booking.zones.noRangeBefore': '⚠ Uten tidsrom gjelder denne sonen',
  'booking.zones.noRangeStrong': 'hele døgnet',
  'booking.zones.noRangeAfter':
    'og overstyrer standardprisen på alle timer. Legg til et tidsrom (f.eks. 17:00–21:00) for at den bare skal gjelde primetime.',
  'booking.zones.addZone': '+ Ny pris-sone',
  'booking.zones.saveAll': 'Lagre alle endringer',
  // ── booking: the booking-rules editor ────────────────────────────
  'booking.config.title': 'Booking-innstillinger',
  'booking.config.lead': 'Styr regler for bookinger og hvilke baner som er åpne.',
  'booking.config.tab.general': '⚙️ Generelt',
  'booking.config.tab.courts': '🎾 Baner',
  'booking.config.on': 'På',
  'booking.config.off': 'Av',
  'booking.config.minutes': '{n} min',
  'booking.config.days': '{n} dager',
  // Custom choices in the three option pickers (gap, durations, booking window).
  'booking.config.custom.addMinutes': 'Legg til eget antall minutter',
  'booking.config.custom.addDays': 'Legg til eget antall dager',
  'booking.config.custom.placeholderMinutes': 'min',
  'booking.config.custom.placeholderDays': 'dager',
  'booking.config.custom.add': 'Legg til',
  'booking.config.custom.remove': 'Fjern {v}',
  'booking.config.custom.errInvalid': 'Skriv inn et helt tall.',
  'booking.config.custom.errRange': 'Må være mellom {min} og {max}.',
  'booking.config.custom.errDuplicate': 'Dette valget finnes allerede.',
  'booking.config.courtLabel': 'Bane {n}',
  'booking.config.gap.label': 'Minste tillatte hull',
  // ONE STRING PER DESCRIPTION, not a sentence chopped into keys around its
  // bold words. Fragments freeze Norwegian word order into every catalog, and
  // a translator cannot move a clause that arrives in six pieces.
  'booking.config.gap.help':
    'To bookinger på samme bane må enten ligge rett etter hverandre, eller ha minst denne avstanden mellom seg. Slik unngår du små glipper ingen får booket.',
  'booking.config.gap.none': 'Av (ingen begrensning)',
  'booking.config.smart.label': 'Smart booking',
  'booking.config.smart.sub': '(på toppen av gap-regelen)',
  'booking.config.smart.help':
    'Maksimerer utnyttelsen av banene: brukeren velger ikke bane selv, systemet tar den som etterlater minst ubrukt tid. En 90-min-booking havner ikke i en 2-timers åpning når en annen bane har akkurat 90 min ledig. Gap-regelen gjelder uansett.',
  'booking.config.direct.label': 'Tillat direkte booking fra kalender',
  'booking.config.direct.help':
    'På: brukeren booker ved å klikke rett i bane-matrisen. Av: matrisen er kun visning, og booking skjer via «Ledige tider». Admin booker alltid direkte.',
  'booking.config.durations.label': 'Tillatte varigheter',
  'booking.config.durations.count': '{n} valgt',
  'booking.config.durations.help': 'Varigheter kunden kan velge. Admin har alltid alle.',
  'booking.config.durations.required': '⚠ Minst én varighet må være valgt.',
  'booking.config.hours.label': 'Åpningstider',
  'booking.config.hours.help': 'Når på døgnet baner kan bookes.',
  'booking.config.hours.unsaved':
    'Åpningstidene er ikke lagret ennå. Booking åpner når du lagrer dem – også om du beholder tidene som står her.',
  'booking.config.hours.allDay': 'Døgnåpent (24/7)',
  'booking.config.hours.open': 'Åpner',
  'booking.config.hours.close': 'Stenger',
  'booking.config.hours.overMidnight':
    '↳ Stenger neste dag kl {close} (åpent {hours} timer i strekk).',
  'booking.config.availability.label': 'Tidsrom for ledighets-indikator',
  'booking.config.availability.help':
    'Hvilke timer fargeprikkene på dag-knappene tar med. Er senteret døgnåpent, kan du utelate natten, så «ledig» gjenspeiler timene folk faktisk spiller. Tomt = åpningstidene.',
  'booking.config.availability.from': 'Fra',
  'booking.config.availability.to': 'Til',
  'booking.config.availability.reset': 'Nullstill (bruk åpningstider)',
  'booking.config.window.label': 'Hvor langt frem kan det bookes',
  'booking.config.window.help':
    'Hvor mange dager frem vanlige brukere kan booke, fra og med i dag. Dager utenfor låses. Admin booker uten begrensning.',
  // #1135: how late a member may cancel their own booking (default 4 h).
  'booking.config.cancellation.label': 'Avbestillingsfrist',
  'booking.config.cancellation.help':
    'Hvor lenge før start en spiller selv kan avbestille bookingen sin i appen. Etter fristen må spilleren kontakte klubben.',
  'booking.config.cancellation.hours': '{n} t før',
  'booking.config.cancellation.untilStart': 'Helt til start',
  'booking.config.holidays.label': 'Helligdager',
  'booking.config.holidays.help':
    'Hvilket lands helligdager som gjelder — for helligdag-prissoner og «hopp over helligdager» i gjentakende bookinger.',
  'booking.config.holidays.no': '🇳🇴 Norske',
  'booking.config.holidays.se': '🇸🇪 Svenske',
  'booking.config.holidays.dk': '🇩🇰 Danske',
  'booking.config.holidays.empty': 'Ingen kommende helligdager i settet.',
  'booking.config.holidays.custom': 'Egendefinert',
  'booking.config.holidays.remove': 'Fjern',
  'booking.config.holidays.add': 'Legg til',
  'booking.config.holidays.reset': 'Nullstill egne endringer',
  'booking.config.courts.count': 'Antall baner',
  'booking.config.courts.countValue': '{n} baner',
  'booking.config.courts.help':
    'Hvor mange baner systemet håndterer. Trenger ikke matche de {n} som er registrert.',
  'booking.config.courts.option.one': '{n} bane',
  'booking.config.courts.option.other': '{n} baner',
  'booking.config.facilities.label': 'Fasiliteter (standard for alle baner)',
  'booking.config.facilities.help':
    'Standard for alle baner. Kan overstyres per bane lenger ned. Vises som ikoner der banen er synlig.',
  'booking.config.facilities.courtType': 'Banetype',
  'booking.config.facilities.double': 'Dobbel (2v2)',
  'booking.config.facilities.single': 'Single (1v1)',
  'booking.config.facilities.runoff': 'Mulig å løpe ut av banen',
  'booking.config.facilities.streaming': 'Streaming-utstyr',
  'booking.config.perCourt.label': 'Tilpass per bane (navn, sponsor, fasiliteter)',
  'booking.config.perCourt.help':
    'Gi banen kallenavn (f.eks. «Senterbanen») og eventuelt sponsor med logo. Logo limes inn som URL eller lastes opp.',
  'booking.config.perCourt.nicknamePlaceholder': 'Kallenavn (standard: Bane {n})',
  'booking.config.perCourt.sponsorPlaceholder': 'Sponsornavn (valgfritt)',
  'booking.config.perCourt.logoPlaceholder': 'Logo-URL (valgfritt)',
  'booking.config.perCourt.nicknameLabel': 'Kallenavn for bane {n}',
  'booking.config.perCourt.sponsorLabel': 'Sponsornavn for bane {n}',
  'booking.config.perCourt.logoLabel': 'Logo-URL for bane {n}',
  'booking.config.perCourt.upload': 'Last opp',
  'booking.config.perCourt.removeLogo': 'Fjern logo',
  'booking.config.perCourt.facilities': 'Fasiliteter',
  'booking.config.perCourt.resetOverrides': '↺ Tilbakestill til standard ({n} overstyrt)',
  'booking.config.perCourt.defaultOption': 'Standard ({value})',
  'booking.config.perCourt.type': 'Type',
  'booking.config.perCourt.runoff': 'Utløp',
  'booking.config.perCourt.streaming': 'Streaming',
  'booking.config.perCourt.doubleShort': 'Dobbel',
  'booking.config.perCourt.singleShort': 'Single',
  'booking.config.activeCourts.label': 'Aktive baner',
  'booking.config.activeCourts.all': 'Alle ({n})',
  'booking.config.activeCourts.someOf': '{n} av {total}',
  'booking.config.activeCourts.help':
    'Klikk for å stenge en bane for booking (f.eks. vedlikehold).',
  'booking.config.resetAll': 'Tilbakestill til standardverdier',
  'booking.config.save': 'Lagre innstillinger',
  // ── booking: the new/edit booking modal ─────────────────────────
  'booking.edit.titleCreate': 'Ny booking',
  'booking.edit.titleEdit': 'Rediger booking',
  'booking.edit.confirm.title': 'Kansellere booking?',
  'booking.edit.confirm.seriesTitle': 'Kansellere hele serien?',
  'booking.edit.confirm.body':
    'Denne bookingen blir fjernet permanent. Handlingen kan ikke angres.',
  'booking.edit.confirm.seriesBody':
    'Dette kansellerer alle {count} bookingene i serien. Handlingen kan ikke angres.',
  'booking.edit.confirm.confirm': 'Ja, kanseller',
  'booking.edit.confirm.withAssets': 'Utstyret som er booket til den ({n}) fjernes også.',
  // Avbestill og refunder (#284): personalet avbestiller en BETALT booking, og
  // kunden får hele betalingen tilbake. Beløpet er betalingens, fra serveren.
  'booking.edit.refund.open': 'Avbestill og refunder',
  'booking.edit.refund.openAmount': 'Avbestill og refunder {amount}',
  'booking.edit.refund.title': 'Avbestille og refundere?',
  'booking.edit.refund.body':
    'Bookingen avbestilles og banen blir ledig. Hele betalingen, {amount}, betales tilbake til kunden.',
  'booking.edit.refund.bodyNoAmount':
    'Bookingen avbestilles og banen blir ledig. Hele betalingen betales tilbake til kunden.',
  'booking.edit.refund.withAssets': 'Utstyret som er booket til den ({n}) avbestilles også.',
  'booking.edit.refund.irreversible': 'En refusjon kan ikke angres.',
  'booking.edit.refund.confirm': 'Ja, avbestill og refunder',
  'booking.edit.refund.working': 'Refunderer …',
  'booking.edit.refund.retry': 'Prøv igjen',
  'booking.edit.refund.keep': 'Behold bookingen',
  'booking.edit.refund.close': 'Lukk',
  'booking.edit.refund.done': 'Avbestilt. {amount} er refundert til kunden.',
  'booking.edit.refund.alreadyCancelled': 'Bookingen er allerede avbestilt.',
  'booking.edit.refund.unconfirmed':
    'Refusjonen er sendt, men ikke bekreftet ennå. Bookingen står til betalingen er avstemt – trykk igjen senere for å sjekke.',
  'booking.edit.refund.inProgress': 'Betalingen blir fortsatt bekreftet. Vent litt og prøv igjen.',
  'booking.edit.refund.failed':
    'Betalingsleverandøren avviste refusjonen, så bookingen er beholdt. Prøv igjen.',
  'booking.edit.refund.stateMoved':
    'Bookingen ble endret mens du trykket, så ingenting er refundert. Lukk, åpne den på nytt og prøv igjen.',
  'booking.edit.refund.unavailable':
    'Refusjon er ikke tilgjengelig – ingen betalingsleverandør er satt opp.',
  'booking.edit.refund.notFound': 'Fant ikke bookingen. Den kan allerede være avbestilt.',
  'booking.edit.refund.notPaid':
    'Bookingen har ingen betaling å refundere. Bruk «Kanseller booking» i stedet.',
  'booking.edit.refund.reauth':
    'Bekreft at det er deg i vinduet som åpnet seg, og trykk «Prøv igjen».',
  'booking.edit.refund.forbidden': 'Du har ikke tilgang til å refundere for denne klubben.',
  'booking.edit.refund.error':
    'Fikk ikke noe svar. Ingenting er bekreftet refundert – sjekk bookingen før du prøver igjen.',
  'booking.edit.scope.seriesBefore': 'Endrer',
  'booking.edit.scope.seriesStrong': 'hele serien',
  'booking.edit.scope.seriesAfter':
    '({count} bookinger). Tid, varighet og navn oppdateres på alle — dato og bane beholdes per forekomst.',
  'booking.edit.scope.occBefore': 'Endrer',
  'booking.edit.scope.occStrong': 'kun denne forekomsten',
  'booking.edit.scope.occAfter': 'av serien.',
  'booking.edit.date': 'Dato',
  'booking.edit.start': 'Start',
  'booking.edit.startHour': 'Time',
  'booking.edit.startMinute': 'Minutt',
  'booking.edit.duration': 'Varighet',
  'booking.edit.until': 'til',
  'booking.edit.noneFree': 'Ingen ledige',
  'booking.edit.durationOption': '{n} min',
  'booking.edit.durationOptionBusy': '{n} min — ingen ledig bane',
  'booking.edit.durationOptionAdmin': '{n} min (admin)',
  'booking.edit.durationOptionAdminBusy': '{n} min (admin) — ingen ledig',
  'booking.edit.customEnd': 'Egendefinert sluttid',
  'booking.edit.suggest.body':
    '{duration} min er ikke mulig {start} på noen bane. {suggest} min passer derimot her.',
  'booking.edit.suggest.adjust': 'Juster til {suggest} min',
  'booking.edit.warn.betterBefore':
    'Bane {court} er ledig, men ville etterlate unødvendig mye tomtid her.',
  'booking.edit.warn.betterCourt': 'Bane {court}',
  'booking.edit.warn.betterAfter':
    'passer tettere — slik får flere plass. Velg en grønn bane under.',
  'booking.edit.warn.gapOf': 'et {min}-minutters',
  'booking.edit.warn.gapSmall': 'et lite',
  'booking.edit.warn.deadGap':
    'Bane {court} er ledig {start}, men en booking til {end} ville etterlate {gap} hull som ingen andre får brukt. Velg en annen bane under, eller juster tid/varighet.',
  'booking.edit.warn.busy':
    'Bane {court} er ikke ledig {start}–{end}. Velg en annen bane under, eller endre tid/varighet.',
  'booking.edit.adminOverride.label': 'Admin-overstyring:',
  'booking.edit.adminOverride.better':
    'Bane {court} er valgt, men bane {better} ville gitt tettere plassering. Du kan booke denne likevel.',
  'booking.edit.adminOverride.deadGap':
    'Denne bookingen etterlater {gap} hull som ingen andre får brukt. Du kan booke den likevel.',
  'booking.edit.adminOverride.smart':
    'Dette valget bryter smart booking-reglene, men du kan booke det likevel.',
  'booking.edit.chooseCourt': 'Velg bane',
  // #949: the override is sent only by those holding «Booke utenom reglene».
  'booking.override.withheld':
    'Klubbens bookingregler stoppet {count} av {total} reservasjoner ({reason}). Du har ikke retten «Booke utenom reglene», så reglene kan ikke overstyres herfra.',
  'booking.override.refused':
    'Du har ikke retten «Booke utenom reglene», så ingenting ble reservert. Be en administrator om å reservere, eller om å gi deg retten.',
  'booking.edit.chooseCourtCount': 'Velg bane ({n} valgt)',
  'booking.edit.multiCourtHint': 'Klikk flere baner for å booke alle samtidig.',
  'booking.edit.smartInfo.summary': 'Padelsenteret bruker Smart booking',
  'booking.edit.smartInfo.question': 'Hvorfor er en bane låst selv om det er ledig tid der?',
  'booking.edit.smartInfo.p1':
    'Når du booker, ser vi på alle banene samtidig og plasserer deg slik at det blir minst mulig «døde» hull — korte mellomrom som er for små til at noen andre får booket. Det betyr at en bane kan være ledig akkurat nå, men likevel låst, fordi en annen bane gir en tettere plassering.',
  'booking.edit.smartInfo.p2Before': 'Slik får',
  'booking.edit.smartInfo.p2Strong': 'flest mulig plass til å spille',
  'booking.edit.smartInfo.p2After':
    'i løpet av dagen. Du betaler det samme uansett hvilken bane du får — banene er like gode. Velg en av de grønne banene under.',
  'booking.edit.nextFree': 'Neste ledig kl {time}',
  'booking.edit.noneLeftToday': 'Ingen ledige tider igjen i dag',
  'booking.edit.outOfService': 'Ute av drift',
  'booking.edit.courtFree': 'Ledig',
  'booking.edit.courtBusy': 'Opptatt',
  'booking.edit.betterBadge': 'Bane {court} bedre',
  'booking.edit.betterBadgeAdmin': '⚠ Bane {court} bedre',
  'booking.edit.gapBadge': 'Lager hull',
  'booking.edit.gapBadgeAdmin': '⚠ Lager hull',
  'booking.edit.outOfServiceNote': 'Banen er merket ute av drift',
  'booking.edit.outOfServiceNoteReason': 'Banen er merket ute av drift — {reason}',
  'booking.edit.sponsoredBy': 'Sponset av {sponsor}',
  'booking.edit.smartLockedAdmin':
    'Lager et lite hull. Bane {court} er smartere — men du kan velge denne.',
  'booking.edit.smartLocked': 'Ledig, men gir et lite hull her. Bane {court} passer tettere.',
  'booking.edit.gapMinutes': '{min} min',
  'booking.edit.gapSmallHole': 'et lite hull',
  'booking.edit.deadGapAdmin': 'Etterlater {gap} — du kan likevel velge denne.',
  'booking.edit.deadGap':
    'Ledig nå, men denne bookingen ville etterlate {gap} som ingen andre får brukt.',
  'booking.edit.noCourtsFree': 'Ingen baner ledige på dette tidspunktet. Velg en annen tid.',
  'booking.edit.summary': 'Booking varer fra {start} til {end} ({duration} min).',
  'booking.edit.price': 'Pris',
  'booking.edit.free': 'Gratis',
  'booking.edit.priceNotSet': 'Pris ikke satt',
  'booking.edit.priceNotSetHint':
    'Klubben har ingen pris-soner ennå. Bookingen lagres uten betaling.',
  'booking.edit.priceKr': '{n} kr',
  'booking.edit.priceRow': '{from}–{to} · {zone}: {kr} kr',
  'booking.edit.titleAdmin': 'Tittel (admin)',
  'booking.edit.titlePlaceholder': 'Eks: Vedlikehold, Trening A-lag, Sperret',
  'booking.edit.titleHelp': 'Vises på bookingen så det er tydelig at den er satt av admin.',
  'booking.edit.bookedBy': 'Booket av (navn)',
  'booking.edit.bookedByPlaceholder': 'Eks: Anders Olsen',
  'booking.edit.phone': 'Telefon',
  'booking.edit.email': 'E-post',
  'booking.edit.optional': '(valgfritt)',
  'booking.edit.loggedInBooker': 'Booking registreres på din innloggede bruker.',
  'booking.edit.notes': 'Notater',
  'booking.edit.notesCourtHint':
    'Notatet vises på skjermen ved banen. Ikke skriv personopplysninger her.',
  'booking.edit.match.register': 'Registrer en kamp på denne bookingen',
  'booking.edit.match.registerHelp':
    'Enkeltmatch (4 spillere). Velg om du legger inn spillerne selv, eller kjører åpen påmelding via Aktiviteter.',
  'booking.edit.match.modeLocal': 'Legg inn spillere selv',
  'booking.edit.match.modeLocalSub': 'Du velger de fire nå',
  'booking.edit.match.modeActivity': 'Via aktiviteter',
  'booking.edit.match.modeActivitySub': 'Åpen påmelding + betaling',
  'booking.edit.match.rules':
    'Matchregler — standard for live registrering på Banepad (kan endres underveis der).',
  'booking.edit.match.format': 'Format',
  'booking.edit.match.format.super': 'Super-tiebreak',
  'booking.edit.match.format.superSub': 'Best av 3 — avgjørende super-tiebreak til 10',
  'booking.edit.match.format.full': '3 fulle sett',
  'booking.edit.match.format.fullSub': 'Best av 3 — fullt avgjørende sett til 6',
  'booking.edit.match.deuce': 'Tellemåte ved 40–40',
  'booking.edit.match.pickFour':
    'Velg fire ulike spillere for å registrere kampen. Bookingen lagres uansett.',
  'booking.edit.match.pricePerPlayer': 'Pris per spiller:',
  'booking.edit.match.priceNote': '(banepris delt på 4)',
  'booking.edit.match.deregisterHours': 'Avmeldingsfrist (timer)',
  'booking.edit.match.signupCloseHours': 'Påmelding stenger (timer før)',
  'booking.edit.match.levelReq': 'Nivåkrav — hvem kan melde seg på',
  'booking.edit.match.levelFrom': 'Fra nivå',
  'booking.edit.match.levelTo': 'Til nivå',
  'booking.edit.match.levelAll': 'Alle',
  'booking.edit.match.level': 'Nivå {n}',
  'booking.edit.match.levelHelp': 'Tomt = alle nivåer kan melde seg på.',
  'booking.edit.match.payment': 'Betaling',
  'booking.edit.match.paySplit': 'Hver spiller betaler for seg',
  'booking.edit.match.paySplitSub':
    'Hver av de fire betaler {n} kr under Aktiviteter. Du belastes ikke hele baneprisen nå.',
  'booking.edit.match.payAll': 'Jeg betaler for alle',
  'booking.edit.match.payAllSub':
    'Du betaler hele baneprisen nå. De andre gjør opp {n} kr med deg.',
  'booking.edit.match.autoCancel':
    'Aktiviteten avlyses automatisk hvis det er færre enn fire påmeldte ved fristen. Banen din beholdes.',
  'booking.edit.match.tooLate':
    'Påmeldingsfristen er allerede passert for denne bookingen ({hours} t før start). Velg et senere tidspunkt, kortere påmeldingsfrist, eller legg inn spillerne selv.',
  'booking.edit.paymentStatus': 'Betalingsstatus',
  'booking.edit.status.free': 'Gratis',
  'booking.edit.status.pending': 'Venter',
  'booking.edit.status.paid': 'Betalt',
  'booking.edit.markPaid': '✓ Marker som betalt ({n} kr)',
  'booking.paymentLink.title': 'Betalingslenke',
  'booking.paymentLink.help':
    'Velg medlemmet bookingen er for. De får en e-post med lenke og betaler med kort i appen. Bookingen blir stående til den er betalt.',
  'booking.paymentLink.member': 'Medlem',
  'booking.paymentLink.choose': 'Velg medlem …',
  'booking.paymentLink.send': 'Send betalingslenke',
  'booking.paymentLink.sending': 'Sender …',
  'booking.paymentLink.loadingMembers': 'Henter medlemmer …',
  'booking.paymentLink.membersUnavailable': 'Fant ikke medlemslisten. Prøv igjen senere.',
  'booking.paymentLink.noMembers': 'Klubben har ingen medlemmer å sende lenken til.',
  'booking.paymentLink.sent': 'Betalingslenke sendt til {name} ({amount} kr).',
  'booking.paymentLink.linkedNoMail':
    'Bookingen er knyttet til {name} og kan betales i appen ({amount} kr), men e-posten ble ikke sendt. Si fra til medlemmet.',
  'booking.paymentLink.error.payments_unavailable': 'Kortbetaling er ikke satt opp her ennå.',
  'booking.paymentLink.error.not_on_roster': 'Personen er ikke medlem av klubben.',
  'booking.paymentLink.error.player_has_no_account':
    'Medlemmet har ingen konto og kan ikke logge inn for å betale.',
  'booking.paymentLink.error.booking_not_found': 'Fant ikke bookingen. Den kan være slettet.',
  'booking.paymentLink.error.already_paid': 'Bookingen er allerede betalt.',
  'booking.paymentLink.error.nothing_to_pay': 'Det er ingenting å betale for denne bookingen.',
  'booking.paymentLink.error.booking_not_linkable':
    'Denne bookingen er gjort av en annen organisasjon og kan ikke sendes som betalingslenke.',
  'booking.paymentLink.error.booking_linked_to_another_player':
    'Bookingen er allerede knyttet til et annet medlem.',
  'booking.paymentLink.error.member_payment_window':
    'Medlemmet har booket selv og holder på å betale. Vent til betalingsfristen er ute.',
  'booking.paymentLink.error.organization_not_connected':
    'Klubben har ikke koblet til en betalingskonto ennå (Klubbinnstillinger → Betaling).',
  'booking.paymentLink.error.unpriceable': 'Prisen for denne tiden kan ikke regnes ut.',
  'booking.paymentLink.error.price_mismatch':
    'Prisen på bookingen er ikke den medlemmet ville blitt belastet etter prissonene (f.eks. serierabatt). Lenken ble ikke sendt.',
  'booking.paymentLink.error.equipment_not_payable':
    'Bookingen har utstyr med pris som ikke kan betales med lenken. Lenken ble ikke sendt.',
  'booking.paymentLink.error.awaiting_verification':
    'En betaling for bookingen venter på bekreftelse. Prøv igjen om noen minutter.',
  'booking.paymentLink.error.reservation_not_payable': 'Bookingen kan ikke betales lenger.',
  'booking.paymentLink.error.state_moved':
    'Bookingen ble endret mens lenken ble sendt. Lukk og åpne den igjen, og prøv på nytt.',
  'booking.paymentLink.error.forbidden': 'Du har ikke tilgang til å endre denne bookingen.',
  'booking.paymentLink.error.unknown': 'Kunne ikke sende betalingslenken. Prøv igjen.',
  // «Trekk tilbake betalingslenke» (#1310): the booking goes back to the club.
  'booking.paymentLink.withdraw': 'Trekk tilbake betalingslenke',
  'booking.paymentLink.withdrawing': 'Trekker tilbake …',
  'booking.paymentLink.withdrawn':
    'Betalingslenken er trukket tilbake. Bookingen er klubbens igjen.',
  'booking.paymentLink.withdrawnCancelled':
    'Betalingslenken er trukket tilbake, og den åpne betalingen er avbrutt. Bookingen er klubbens igjen.',
  'booking.paymentLink.error.not_linked': 'Bookingen har ingen betalingslenke å trekke tilbake.',
  'booking.paymentLink.error.payment_unconfirmed':
    'Stripe bekreftet ikke at betalingen ble avbrutt – medlemmet kan ha betalt. Ingenting er endret. Sjekk betalingen om noen minutter.',
  'booking.paymentLink.error.payment_open':
    'Medlemmet åpnet betalingen på nytt akkurat nå. Prøv igjen.',
  'booking.paymentLink.error.withdrawUnknown':
    'Kunne ikke trekke tilbake betalingslenken. Prøv igjen.',
  'booking.dispute.title': 'Betalingen er bestridt: {state}',
  'booking.dispute.amount': 'Bestridt beløp {amount}, åpnet {date}.',
  'booking.dispute.dueBy': 'Svarfrist: {date}. Eieren svarer under Klubbinnstillinger → Betaling.',
  'booking.dispute.withdrawn': '{amount} er trukket fra saldoen, pluss {fee} i gebyr.',
  'booking.dispute.reinstated': '{amount} er ført tilbake til saldoen.',
  'booking.dispute.paymentUnchanged':
    'Betalingsstatusen over er ikke endret av tvisten. Hva en tapt tvist betyr for oppgjøret, er ikke avgjort ennå.',
  'booking.dispute.state.inquiryNeedsResponse': 'forespørsel venter på svar',
  'booking.dispute.state.inquiryUnderReview': 'forespørsel besvart, til vurdering',
  'booking.dispute.state.inquiryClosed': 'forespørsel lukket',
  'booking.dispute.state.needsResponse': 'venter på svar',
  'booking.dispute.state.underReview': 'besvart, til vurdering',
  'booking.dispute.state.won': 'vunnet',
  'booking.dispute.state.lost': 'tapt',
  'booking.dispute.state.prevented': 'avverget',
  'booking.dispute.state.unrecognised': 'ukjent status',
  'booking.edit.recur.toggle': '🔁 Gjentakende booking',
  'booking.edit.recur.toggleSeries': '🔁 Gjentakende booking — endre mønster',
  'booking.edit.recur.rebuildWarning':
    'Endrer du mønsteret, bygges hele serien på nytt fra startdatoen. Tidligere endringer på enkelt-forekomster går tapt.',
  'booking.edit.recur.repeats': 'Gjentas',
  'booking.edit.recur.daily': 'Daglig',
  'booking.edit.recur.weekly': 'Ukentlig',
  'booking.edit.recur.monthly': 'Månedlig',
  'booking.edit.recur.yearly': 'Årlig',
  'booking.edit.recur.every': 'Hver',
  'booking.edit.recur.unitDay': 'dag(er)',
  'booking.edit.recur.unitWeek': 'uke(r)',
  'booking.edit.recur.unitMonth': 'måned(er)',
  'booking.edit.recur.unitYear': 'år',
  'booking.edit.recur.onDays': 'På dager',
  'booking.edit.recur.day.mon': 'Man',
  'booking.edit.recur.day.tue': 'Tir',
  'booking.edit.recur.day.wed': 'Ons',
  'booking.edit.recur.day.thu': 'Tor',
  'booking.edit.recur.day.fri': 'Fre',
  'booking.edit.recur.day.sat': 'Lør',
  'booking.edit.recur.day.sun': 'Søn',
  'booking.edit.recur.daysHelp': 'Tom = samme ukedag som startdatoen.',
  'booking.edit.recur.ends': 'Slutter',
  'booking.edit.recur.endCount': 'Etter antall',
  'booking.edit.recur.endDate': 'På dato',
  'booking.edit.recur.endNever': 'Aldri',
  'booking.edit.recur.occurrences': 'forekomster',
  'booking.edit.recur.neverCap': 'Begrenses til maks 366 forekomster av sikkerhetshensyn.',
  'booking.edit.recur.holidayWhen': 'Når en dato lander på en helligdag',
  'booking.edit.recur.holidayInclude': 'Ta med likevel',
  'booking.edit.recur.holidaySkip': 'Hopp over',
  'booking.edit.recur.holidaySet': 'Helligdagssett: {country} (endres i innstillinger).',
  'booking.edit.recur.collisions':
    'Bookinger som kolliderer hoppes over. Du får en oppsummering etterpå.',
  'booking.edit.cancelBooking': 'Kanseller booking',
  'booking.edit.save': 'Lagre',
  'booking.edit.create': 'Opprett',
  'booking.edit.checkAndCreate': 'Sjekk og opprett',
  'booking.edit.createActivity': 'Opprett aktivitet',
  'booking.edit.toPayment': 'Til betaling ({n} kr)',
  'booking.edit.invalid.duration': 'Ugyldig varighet (sluttid må være etter start).',
  'booking.edit.invalid.identity': 'Fyll inn navn eller tittel.',
  'booking.edit.invalid.courtTaken': 'Valgt bane/tid er ikke ledig.',
  'booking.edit.invalid.signupPassed': 'Påmeldingsfristen er allerede passert for denne bookingen.',
  'booking.edit.invalid.missing': 'Mangler nødvendig info.',
  // ── booking: the booking screen itself ──────────────────────────
  'booking.view.weekday.sun': 'Søn',
  'booking.view.weekday.mon': 'Man',
  'booking.view.weekday.tue': 'Tir',
  'booking.view.weekday.wed': 'Ons',
  'booking.view.weekday.thu': 'Tor',
  'booking.view.weekday.fri': 'Fre',
  'booking.view.weekday.sat': 'Lør',
  'booking.view.courtName': 'Bane {n}',
  'booking.view.courtStateError': 'Kunne ikke endre banestatus',
  'booking.view.alert.demoFull':
    'Ingen flere bookinger får plass — kalenderen er full eller hindret av buffer-regelen.',
  'booking.view.backToProgram': 'Tilbake til kampprogram',
  'booking.view.home': 'Hjem',
  'booking.view.serie.label': 'Reserverer bane for seriekamp',
  'booking.view.serie.discount': '{pct}% rabatt på banepris',
  'booking.view.serie.window':
    'Kan bookes inntil {days} dager frem. Velg en ledig tid under — prisen vises rabattert i betalingen.',
  'booking.view.eyebrow': 'Banebooking',
  'booking.view.title': 'Reservér bane',
  'booking.view.lead': 'Velg en ledig tid, eller opprett en ny booking.',
  'booking.view.adminTitle':
    'Admin-modus: book fritt og lag gjentakende bookinger uten å følge reglene.',
  'booking.view.adminOn': '🔓 Admin på',
  'booking.view.adminOff': '🔒 Admin av',
  // The matrix's closing-time marker, and what admin mode is overriding.
  'booking.view.closedFrom': 'Stengt fra {time}',
  'booking.view.overrideHours': '⚠ Utenfor åpningstiden ({open}–{close}).',
  'booking.view.overrideSmart': '⚠ Bryter smart booking — en annen bane passer bedre.',
  'booking.view.overrideLead': 'Admin-modus lar deg booke likevel:',
  'booking.view.newBooking': '+ Ny booking',
  'booking.view.zones': '💰 Prissoner ({n})',
  'booking.view.hoursNotSet.title': 'Booking er ikke åpnet ennå',
  'booking.view.hoursNotSet.body':
    'Lagre åpningstidene i bookinginnstillingene. Til da kan ingen booke – verken medlemmer eller dere her.',
  'booking.view.hoursNotSet.action': 'Legg inn åpningstider',
  'booking.view.settings': '⚙️ Innstillinger',
  'booking.view.help': '❓ Hjelp',
  'booking.view.demoTitle': 'Lag demobookinger for valgt dato',
  'booking.view.clearAllTitle': 'Tøm hele kalenderen (sletter ALLE bookinger)',
  'booking.view.jumpToDate': 'Hopp til en egendefinert dato',
  'booking.calendar.prevMonth': 'Forrige måned',
  'booking.calendar.nextMonth': 'Neste måned',
  'booking.view.custom': 'Egendefinert',
  'booking.view.past': 'forbi',
  'booking.view.dayTitleLocked': '{date} — kan bookes fra {days} dager før',
  'booking.view.dayTitleFree': '{date} — tidlig {early}, midt {mid}, kveld {evening} ledig',
  'booking.view.today': 'I dag',
  'booking.view.dotsTitle': 'Tidlig · midt på dagen · kveld',
  'booking.view.viewSlots': '🎯 Ledige tider',
  'booking.view.viewMatrix': '📋 Kalendervisning',
  'booking.view.courtHeader': 'Bane',
  'booking.view.outOfService': 'Ute av drift',
  'booking.view.reopenTitle': 'Sett {court} i drift igjen',
  'booking.view.closeTitle': 'Meld {court} ute av drift',
  'booking.view.open': 'Åpne',
  'booking.view.close': 'Steng',
  'booking.view.courtIsOut': '{court} er ute av drift',
  'booking.view.reasonPlaceholder': 'Hva er galt? (valgfritt)',
  'booking.view.noteStaffOnly': 'Notatet vises for ansatte, ikke for kunder.',
  'booking.view.updateNote': 'Oppdater notat',
  'booking.view.markOut': 'Meld ute av drift',
  'booking.view.setInService': 'Sett i drift',
  'booking.view.hoverSuggest': 'Ikke her — {court} passer ({dur}m)',
  'booking.view.hoverIndivisible': 'Ikke her — lager udelelig hull',
  'booking.view.hoverNone': '{time} · ingen ledig varighet',
  'booking.view.anonymous': 'Anonym',
  'booking.view.singleMatch': 'Enkeltmatch',
  'booking.view.passedSuffix': ' (passert)',
  'booking.view.singleMatchBadge': '⚡ Enkeltmatch',
  'booking.view.slotsTitle': 'Ledige tider',
  'booking.view.slotsLead': 'Trykk på et tidspunkt for å booke.',
  'booking.view.minutes': '{n} min',
  'booking.view.groupTitle': 'Del inn tidene i Morgen / Formiddag / Ettermiddag / Kveld',
  'booking.view.grouping': 'Inndeling',
  'booking.view.pickDuration': 'Velg minst én varighet over for å se ledige tider.',
  'booking.view.noSlots': 'Ingen ledige tider for {dur} denne dagen.',
  'booking.view.daypart.morning': 'Morgen',
  'booking.view.daypart.morningRange': 'før 12:00',
  'booking.view.daypart.lateMorning': 'Formiddag',
  'booking.view.daypart.lateMorningRange': '12:00 – 15:00',
  'booking.view.daypart.afternoon': 'Ettermiddag',
  'booking.view.daypart.afternoonRange': '15:00 – 18:00',
  'booking.view.daypart.evening': 'Kveld',
  'booking.view.daypart.eveningRange': '18:00 og senere',
  'booking.view.availability': 'Ledighet:',
  'booking.view.legendGood': 'God',
  'booking.view.legendHalf': 'Halvfullt',
  'booking.view.legendFilling': 'Fyller seg',
  'booking.view.legendAlmostFull': 'Nesten fullt',
  'booking.view.slotTitle': '{time} · {courts}{durations} · {price}',
  'booking.view.slotCourtsOne': '{n} av {total} bane ledig',
  'booking.view.slotCourtsMany': '{n} av {total} baner ledig',
  'booking.view.slotDurations': ' · {list} min',
  'booking.view.priceKr': '{n} kr',
  'booking.view.slotPriceDiscount': '{n} kr (-{pct}%)',
  'booking.view.freeLower': 'gratis',
  'booking.view.free': 'Gratis',
  'booking.view.priceNotSet': 'Pris ikke satt',
  'booking.view.allCourts': 'Alle baner',
  'booking.view.lastCourt': 'Siste bane!',
  'booking.view.someFree': '{n} av {total} ledig',
  'booking.view.footerCalendar':
    'Klikk på en ledig celle for å reservere bane på det tidspunktet. Klikk på en booking for å endre eller slette.',
  'booking.view.footerSlots':
    'Klikk på en tid for å reservere — første ledige bane velges automatisk.',
  // ── booking: equipment beside the courts (docs/domains/ASSET_BOOKING.md) ──
  'booking.view.assets': 'Utstyr',
  'booking.view.assetHeader': 'Utstyr',
  'booking.view.assetRowInactive': 'Ikke tilgjengelig',
  'booking.view.newAssetBooking': 'Book utstyr',
  'booking.view.assetLegend': 'Utstyr',
  'booking.view.alert.assetFailed': 'Banen ble booket, men utstyret kunne ikke bookes: {error}',
  'booking.view.alert.assetSaveFailed': 'Kunne ikke lagre utstyrsbookingen: {error}',
  'booking.view.assetTitle': '{asset} · {start}–{end} · {who}',
  'booking.view.assetLaneBook': 'Book {asset} — klikk på en ledig tid',
  'booking.view.slotAssetsFree': '{n} utstyr ledig',
  'booking.view.slotAssetsNone': 'utstyr opptatt',
  'booking.view.filter.label': 'Vis',
  'booking.view.filter.all': 'Alt',
  'booking.view.filter.courts': 'Bare baner',
  'booking.view.filter.assets': 'Bare utstyr',
  'booking.view.filter.kindAll': 'Alle typer',
  'booking.assets.title': 'Utstyr',
  'booking.assets.lead':
    'Ballmaskiner og annet utstyr som kan bookes ved siden av banene. Utstyret hører til dette senteret — en klubb kan eie utstyr som står her, og endrer det da fra sin egen side.',
  'booking.assets.empty': 'Ingen utstyr registrert ennå.',
  'booking.assets.add': 'Nytt utstyr',
  'booking.assets.name': 'Navn',
  'booking.assets.kind': 'Type',
  'booking.assets.kind.ball_machine': 'Ballmaskin',
  'booking.assets.kind.racket': 'Racket til utlån',
  'booking.assets.kind.camera': 'Kamera',
  'booking.assets.kind.other': 'Annet',
  'booking.assets.description': 'Beskrivelse',
  'booking.assets.active': 'Kan bookes',
  'booking.assets.inactive': 'Ikke tilgjengelig',
  'booking.assets.buffer': 'Minste hull mellom to bookinger',
  'booking.assets.bufferNone': 'Ingen regel',
  'booking.assets.bufferMin': '{n} min',
  'booking.assets.pricing': 'Pris',
  'booking.assets.defaultPrice': 'Standardpris per time (kr)',
  'booking.assets.zones': '{n} prissone(r)',
  'booking.assets.editZones': 'Rediger prissoner',
  'booking.assets.noZones': 'Ingen prissoner — standardprisen gjelder hele dagen.',
  'booking.assets.free': 'Gratis',
  'booking.assets.pricePerHour': '{n} kr/t',
  'booking.assets.ownedBy': 'Eies av {name}',
  'booking.assets.atVenue': 'Står hos {name}',
  'booking.assets.save': 'Lagre',
  'booking.assets.saving': 'Lagrer …',
  'booking.assets.delete': 'Slett',
  'booking.assets.deleteConfirm': 'Slette {name}?',
  'booking.assets.edit': 'Endre',
  'booking.assets.back': 'Tilbake til listen',
  'booking.assets.notOwner': 'Eies av {name} og endres der.',
  'booking.assets.error.asset_has_bookings':
    'Utstyret har bookinger og kan ikke slettes. Sett det som ikke tilgjengelig i stedet.',
  'booking.assets.error.forbidden_pricing': 'Du har ikke rettighet til å endre priser på utstyr.',
  'booking.assets.error.generic': 'Kunne ikke lagre: {error}',
  'booking.assetBooking.title.create': 'Book utstyr',
  'booking.assetBooking.title.edit': 'Endre utstyrsbooking',
  'booking.assetBooking.asset': 'Utstyr',
  'booking.assetBooking.date': 'Dato',
  'booking.assetBooking.start': 'Start',
  'booking.assetBooking.duration': 'Varighet',
  'booking.assetBooking.durationMin': '{n} min',
  'booking.assetBooking.bookedBy': 'Booket av',
  'booking.assetBooking.notes': 'Notat',
  'booking.assetBooking.attach': 'Koble til banebooking',
  'booking.assetBooking.attachNone': 'Ingen — utstyret bookes for seg selv',
  'booking.assetBooking.attachOption': '{court} · {start}–{end} · {who}',
  'booking.assetBooking.attachHint':
    'Utstyret må være innenfor banebookingens tid, og slettes sammen med den.',
  'booking.assetBooking.price': 'Pris',
  'booking.assetBooking.priceKr': '{n} kr',
  'booking.assetBooking.free': 'Gratis',
  'booking.assetBooking.paymentStatus': 'Betaling',
  'booking.assetBooking.status.free': 'Gratis',
  'booking.assetBooking.status.pending': 'Ikke betalt',
  'booking.assetBooking.status.paid': 'Betalt',
  'booking.assetBooking.save': 'Lagre',
  'booking.assetBooking.saving': 'Lagrer …',
  'booking.assetBooking.delete': 'Slett booking',
  'booking.assetBooking.unavailable': 'opptatt',
  'booking.assetBooking.noAssets':
    'Ingen utstyr er registrert. Legg til utstyr under «Utstyr» i admin-modus.',
  'booking.assetBooking.anonymous': 'uten navn',
  'booking.assetBooking.error.asset_taken': 'Utstyret er allerede booket på dette tidspunktet.',
  'booking.assetBooking.error.dead_gap':
    'Bookingen etterlater et for kort hull før eller etter en annen booking av utstyret.',
  'booking.assetBooking.error.outside_opening_hours':
    'Utenfor senterets åpningstid ({open}–{close}).',
  'booking.assetBooking.error.booking_hours_not_set':
    'Booking er ikke åpnet: lagre åpningstidene i bookinginnstillingene først.',
  'booking.assetBooking.error.off_grid': 'Start og slutt må være på hel eller halv time.',
  'booking.assetBooking.error.end_before_start': 'Sluttid må være etter starttid.',
  'booking.assetBooking.error.missing_fields': 'Velg utstyr, dato, start og varighet.',
  'booking.assetBooking.error.asset_inactive': 'Utstyret er ikke tilgjengelig for booking.',
  'booking.assetBooking.error.asset_not_found': 'Utstyret finnes ikke lenger.',
  'booking.assetBooking.error.asset_outside_booking':
    'Utstyret må bookes innenfor banebookingens tid.',
  'booking.assetBooking.error.booking_not_found': 'Banebookingen finnes ikke lenger.',
  'booking.assetBooking.error.payment_status_not_operator_controlled':
    'Betalt-status settes bare av en verifisert betaling.',
  'booking.assetBooking.error.payment_status_contradicts_price':
    'Betalingsstatusen stemmer ikke med prisen.',
  'booking.assetBooking.error.asset_conflict':
    'Banebookingen kan ikke flyttes: {asset} er opptatt på det nye tidspunktet.',
  'booking.assetBooking.error.generic': 'Utstyrsbookingen ble avvist.',
  'booking.edit.assets': 'Utstyr til bookingen',
  'booking.edit.assetsHint': 'Velg utstyr som skal følge banebookingen. Prisen kommer i tillegg.',
  'booking.edit.assetTaken': 'opptatt',
  'booking.edit.assetPrice': '+ {n} kr',
  'booking.edit.assetFree': 'gratis',
  'booking.edit.assetsAttached': 'Utstyr på denne bookingen',
  'booking.edit.assetRemove': 'Fjern',
  'booking.view.overrideTitle': 'Bane {court} er ute av drift',
  'booking.view.overrideBody':
    'Du kan booke denne banen likevel. Gjør det bare hvis du vet at banen kan brukes — kunden får ingen beskjed om at den er merket ute av drift.',
  'booking.view.overrideConfirm': 'Book likevel',
  'booking.view.conflictBooked': 'Booket',
  'booking.view.conflictBusy': 'Opptatt',
  'booking.view.alert.collision': 'Kunne ikke opprette: kollisjon med eksisterende booking.',
  'booking.view.alert.createFailed': 'Kunne ikke opprette: {error}',
  'booking.view.clearAll.title': 'Tøm hele kalenderen?',
  'booking.view.clearAll.before': 'Dette sletter',
  'booking.view.clearAll.strong': 'alle {count} bookinger',
  'booking.view.clearAll.after':
    'i hele systemet — ikke bare demo, og ikke bare valgt dag. Handlingen kan ikke angres.',
  'booking.view.clearAll.confirm': 'Ja, slett alt',
  'booking.view.serieMatch.title': 'Seriekamp-reservasjon',
  'booking.view.serieMatch.sub': 'Bane reservert for en seriekamp',
  'booking.view.serieMatch.goProgram': 'Gå til kampprogrammet',
  'booking.view.serieMatch.openMatch': 'Åpne kampen i seriespillet',
  'booking.view.serieMatch.edit': 'Endre eller kanseller bookingen',
  'booking.view.serieMatch.editSub': 'Gjelder kun denne reservasjonen ({date})',
  'booking.view.series.title': 'Gjentakende booking',
  'booking.view.series.lead':
    'Denne bookingen er del av en serie på {count} bookinger. Hva vil du endre?',
  'booking.view.series.goTournament': 'Gå til turneringen',
  'booking.view.series.openTournament': 'Åpne turneringen denne reservasjonen tilhører',
  'booking.view.series.onlyThis': 'Bare denne forekomsten',
  'booking.view.series.onlyThisSub': 'Endre/kanseller kun {date}',
  'booking.view.series.whole': 'Hele serien ({count})',
  'booking.view.series.wholeSub': 'Endring/kansellering gjelder alle i serien',
  'booking.view.alert.seriesRebuilt': 'Oppdaterte serien: {created} bookinger{failed}.',
  'booking.view.alert.seriesFailedPart': ', {n} feilet (kollisjon)',
  'booking.view.alert.seriesUpdated': 'Oppdaterte {created} av {total} i serien.{failed}',
  'booking.view.alert.seriesFailedSentence': ' {n} feilet (kollisjon).',
  'booking.view.alert.seriesUpdateFailed': 'Kunne ikke oppdatere serien.',
  'booking.view.alert.updateFailed': 'Kunne ikke oppdatere: {error}',
  'booking.view.alert.seriesCancelled': 'Kansellerte {n} bookinger i serien.',
  'booking.view.alert.seriesCancelFailed': 'Kunne ikke kansellere serien.',
  'booking.view.alert.deleteFailed': 'Kunne ikke slette: {error}',
  // Slett av en betalt booking avvises (`booking_has_payment`, #284): pek på
  // «Avbestill og refunder», som er døren for den.
  'booking.view.alert.hasPayment':
    'Bookingen er betalt og kan ikke slettes. Åpne den og bruk «Avbestill og refunder», så får kunden pengene tilbake.',
  'booking.view.alert.hasPaymentNoRight':
    'Bookingen er betalt og kan ikke slettes. Den må avbestilles med «Avbestill og refunder», som krever tilgang til å refundere – be noen som har det.',
  'booking.view.alert.createdOne': 'Opprettet {n} booking.',
  'booking.view.alert.createdMany': 'Opprettet {n} bookinger.',
  'booking.view.alert.cancelledExisting': ' {n} eksisterende ble kansellert (overstyrt).',
  'booking.view.alert.failedCount': ' {n} feilet.',
  'booking.view.alert.saveFailed': 'Kunne ikke lagre: {error}',
  'booking.view.alert.paidButNotSaved': 'Betaling OK, men booking kunne ikke lagres: {error}',
  'booking.view.confirm.title': 'Bekreft bookinger',
  'booking.view.confirm.counts':
    '{total} bookinger totalt · {free} ledige · {conflicts} kolliderer',
  'booking.view.confirm.conflictsAfter': 'av {total} kolliderer med eksisterende bookinger:',
  'booking.view.confirm.noConflicts': 'Ingen kollisjoner — alle {n} bookingene er ledige.',
  'booking.view.confirm.skip': 'Hopp over {skip} — book {book}',
  'booking.view.confirm.override': 'Overstyr — kanseller {n} og book alle',
  'booking.view.confirm.createAll': 'Opprett {n} bookinger',
  'booking.view.paymentSummary': 'Bane {court}, {date}, {start}–{end}',
  // ═══ end of the booking surface block ═══

  // ── ERROR CODES, mapped here rather than sent translated ──────────
  //
  // Reached DYNAMICALLY: `errorMessage` builds `errors.${code}` from the
  // server's machine code, so NO LITERAL GREP FINDS THESE. A sweep for
  // «unused password strings» deleted four live ones once for that reason,
  // and the way to decide whether a key is dead is to list its PRODUCERS in
  // `apps/api/src` — which is how the #754 stage 3 pass below was done.
  //
  // ⚠️ AND THE SENTENCE HAS TO BE TRUE FOR EVERY PRODUCER. These were written
  // when the only door was a password, and several outlived it. An accurate
  // key with a false sentence is worse than a missing one, because the lookup
  // succeeds and nothing fails (#844 review).
  //
  // `not_configured` is the sharpest case: thirteen routes raise it — the OTP
  // doors, the passkey routes, step-up, the mail invitation and the invitation
  // accept — so a sentence naming ANY one context is false for the others. It
  // said «password reset is not set up», then briefly «sign-in is not set up»,
  // which was the same mistake one word over. It names no context now.
  'errors.not_configured': 'Denne funksjonen er ikke satt opp i dette miljøet. Kontakt StarPoint.',
  'errors.throttled': 'For mange forsøk. Vent litt og prøv igjen.',
  'errors.unsupported_auth_method': 'Denne kontoen støtter ikke denne innloggingsmåten.',
  // Raised by the dev staff code door, for a wrong code or an unknown address
  // alike — it deliberately does not distinguish them.
  'errors.invalid_credentials': 'Innlogging avvist. Sjekk e-postadressen og koden.',
  'errors.account_disabled': 'Kontoen er deaktivert. Ta kontakt med klubben din.',
  // A CLOSED account (#1062): the person closed it, or support did on their
  // behalf. Named only after the code or passkey proved who is asking.
  'errors.account_closed':
    'Denne kontoen er avsluttet. Ta kontakt med StarPoint hvis du vil vite mer.',
  'errors.rate_limited': 'For mange forsøk. Vent litt og prøv igjen.',
  'errors.invalid_token': 'Lenken er ugyldig eller utløpt. Be om en ny.',
  'errors.network': 'Fikk ikke kontakt med serveren. Prøv igjen.',
  'errors.unknown': 'Noe gikk galt. Prøv igjen.',
  // `POST /members/import` — the member-list upload.
  'errors.too_many_rows': 'Listen har for mange personer om gangen. Del den opp.',
  'errors.payload_too_large': 'Listen er for stor til å sendes i ett stykke. Del den opp.',
  'errors.body_too_large':
    'Det du prøver å lagre er for stort til å sendes. Ta kontakt med oss hvis det skjer igjen.',
  'errors.rows_required': 'Fant ingen personer å legge til.',
  'errors.name_too_short': 'Skriv hele navnet, minst tre tegn.',
  'errors.reason_required': 'Skriv en begrunnelse, minst tre tegn.',
  'errors.reason_too_long': 'Begrunnelsen er for lang.',
  'errors.name_required': 'Skriv inn et navn.',
  'errors.invalid_email': 'E-postadressen ser ikke riktig ut.',
  'errors.invalid_gender': 'Velg mann, kvinne eller ikke oppgitt.',
  'errors.field_too_long': 'Ett av feltene er for langt.',
  'errors.player_self_managed':
    'Spilleren har egen konto og endrer dette selv. Kontakt StarPoint support hvis det må rettes.',
  'errors.email_required': 'Skriv inn en e-postadresse.',
  'errors.member_invite_retired':
    'Invitasjon med kode er avviklet. Last siden på nytt og inviter med e-post.',
  'errors.not_found': 'Fant ikke det du lette etter.',
  // ── Card payment for a court booking (#284) ──────────────────────
  // The booking exists before any of this is shown, so «avbryt» leaves a pending
  // booking whose court is still held — not a lost slot.
  'booking.pay.title': 'Betal med kort',
  'booking.pay.total': 'Å betale',
  'booking.pay.amount': '{amount} {currency}',
  'booking.pay.opening': 'Klargjør betaling …',
  'booking.pay.payNow': 'Betal nå',
  // Between the wallet buttons and the card form.
  'booking.pay.orCard': 'eller betal med kort',
  'booking.pay.confirming': 'Betaler …',
  'booking.pay.cancel': 'Avbryt',
  'booking.pay.close': 'Lukk',
  // ── THE WAY OUT OF AN ATTEMPT THAT CANNOT BE SENT (#607) ────────
  // Shown only for `attempt_terms_changed`: the open payment is abandoned at the
  // provider and a new one is opened at today's price. The COURT IS NOT LOST —
  // only the attempt is — and the wording has to make that unmistakable, because
  // «avbryt» next to a booking otherwise reads as «cancel my booking».
  'booking.pay.restart': 'Avbryt og start betalingen på nytt',
  'booking.pay.declined': 'Betalingen ble avvist. Prøv et annet kort.',
  // NOT «betalt». The webhook decides that, and until it lands the booking is
  // still pending — see StripePaymentSheet.
  'booking.pay.submitted': 'Betalingen er mottatt og bekreftes nå. Banen er din.',
  // Beside a booking in a list, where the full sentence would not fit.
  'booking.pay.submittedShort': 'Betaling bekreftes …',
  // ── WHEN THE WEBHOOK HAS LANDED, AND WHEN IT HAS NOT ────────────
  // The confirmation the member waited for. Said only on the SERVER's word —
  // `paymentStatus: 'paid'`, which only the signed webhook writes.
  'booking.pay.paid': 'Betalt',
  // The honest end of a wait that did not resolve. Not a failure: the card went
  // through, and a confirmation that is merely late must not read as money lost.
  'booking.pay.stillConfirming':
    'Betalingen er mottatt. Bekreftelsen tar litt lengre tid enn vanlig — banen er din, og betalingen vises her så snart den er bekreftet.',
  'booking.pay.heldNote': 'Banen er holdt av til deg mens du betaler.',
  // ── THE FIFTEEN MINUTES, SAID OUT LOUD (#607) ───────────────────
  // «When they hit book the court is held for 15 minutes. During that time they
  // have to complete payment or they lose the booking.» A countdown the member
  // cannot see is a rule they cannot follow, so the time is in the text — and
  // when it runs out, somebody has to say so rather than let the row vanish.
  'booking.pay.heldFor': 'Holdt av {time}',
  'booking.pay.heldNoteTimed': 'Banen er holdt av til deg i {time} mens du betaler.',
  'booking.pay.holdLost': 'Reservasjonen gikk ut – banen er frigitt',
  // ── Back from Vipps (#508) ───────────────────────────────────────
  // The member left the page to approve in Vipps and has just come back. What
  // they read is the SERVER's word where it has one: «paid» only once the
  // webhook has landed, and otherwise what Stripe told the browser.
  'booking.pay.return.checking': 'Sjekker betalingen …',
  'booking.pay.return.paid': 'Betalingen er registrert. Banen er din.',
  'booking.pay.return.confirming': 'Betalingen er mottatt og bekreftes …',
  'booking.pay.return.failed':
    'Betalingen ble ikke fullført. Banen er fortsatt holdt av til deg — prøv igjen under Reservasjoner.',
  'booking.pay.return.dismiss': 'Lukk',
  'booking.pay.notConfigured': 'Kortbetaling er ikke satt opp her ennå.',
  'booking.pay.unreachable':
    'Fikk ikke kontakt med betalingsløsningen. Banen er fortsatt reservert — prøv igjen.',
  // ── The server's refusal codes, as sentences (#310a) ─────────────
  // Reached through `errorMessage`, which is why they live under `errors.`: the
  // CODE crosses the wire and the browser decides the words.
  'errors.already_paid': 'Denne banetimen er allerede betalt.',
  'errors.nothing_to_pay': 'Denne banetimen koster ingenting.',
  'errors.unpriceable': 'Klubben har ikke satt pris på denne tiden. Ta kontakt med klubben.',
  'errors.organization_not_connected':
    'Klubben kan ikke ta imot betaling ennå. Ta kontakt med klubben.',
  'errors.not_your_booking': 'Denne banetimen er ikke din.',
  'errors.booking_not_identity_linked':
    'Denne banetimen kan ikke betales i appen. Ta kontakt med klubben.',
  'errors.reservation_expired': 'Reservasjonen er utløpt. Book tiden på nytt.',
  'errors.reservation_not_payable': 'Denne banetimen kan ikke betales nå.',
  'errors.awaiting_verification':
    'En tidligere betaling er ikke ferdig avklart. Vent litt og prøv igjen.',
  'errors.attempt_terms_changed':
    'Prisen er endret siden du startet betalingen. Avbryt den gamle betalingen og start på nytt.',
  'errors.payment_refused': 'Betalingen ble avvist. Prøv et annet kort.',
  'errors.provider_unknown':
    'Vi vet ikke om betalingen gikk gjennom. Vent litt før du prøver igjen — banen er fortsatt reservert.',
  'errors.asset_paid_with_booking': 'Utstyret betales sammen med banebookingen.',

  // ── players — database, profiles, merits and the swap/replace modals (#310a) ──
  //
  // RATINGS AND YEARS ARE INTERPOLATED AS STRINGS, not numbers. A numeric var
  // goes through `Intl.NumberFormat`, which turns 1500 into «1 500» and 2025
  // into «2 025». An ELO rating and a year are identifiers, not quantities.
  // Genuine counts (matches, wins, days) stay numbers and are formatted.
  //
  // NOT HERE, deliberately: format names (Padel24, Americano, Klubbmesterskap,
  // Seriespill) and anything `ELO_LEVELS` / `getTournamentType` produces. The
  // first are protocol vocabulary that `docs/architecture/DOMAIN_RULES.md`
  // keeps as it is; the second is engine output, which this slice must not move.
  'players.common.cancel': 'Avbryt',
  'players.common.save': 'Lagre',
  'players.common.name': 'Navn',
  'players.common.gender': 'Kjønn',
  'players.common.startElo': 'Start-ELO',
  'players.common.namePlaceholder': 'Fornavn Etternavn',
  'players.common.searchName': 'Søk navn…',
  'players.common.searchLabel': 'Søk etter spiller',
  'players.common.clearSearch': 'Tøm søket',
  'players.common.male': 'Mann',
  'players.common.female': 'Kvinne',
  'players.common.genderShort.male': 'M',
  'players.common.genderShort.female': 'K',
  'players.common.level': 'Nivå {level}',
  'players.common.levelTitle': 'Nivå {level} — {name}',
  'players.common.lastPlayed': 'sist for {days}d',
  'players.common.win': 'Seier',
  'players.common.loss': 'Tap',
  'players.common.noAvailablePlayers': 'Ingen tilgjengelige spillere',
  'players.common.dupe.before': 'Det finnes allerede en spiller som heter',
  'players.common.dupe.ghost': ' (ghost)',
  'players.common.dupe.after': '. For å unngå duplikat, bruk den eksisterende.',
  'players.common.useExisting': 'Bruk eksisterende spiller',
  'players.common.boostTitle':
    'Ny spiller-boost {boost} — avtar lineært over kamper, blir 1x når spilleren har spilt {count} kamper.',
  'players.common.statusNew': 'NY SPILLER',
  'players.common.statusComeback': 'TILBAKE',
  'players.common.matchesLeft.one': '{count} kamp igjen',
  'players.common.matchesLeft.other': '{count} kamper igjen',
  'players.common.nearlyEstablished': 'Snart etablert',
  'players.common.comebackTitle':
    'Etter mer enn {days} dagers inaktivitet får {name} forhøyet ELO-effekt som avtar etterhvert som de spiller flere kamper.',

  'players.swap.title': 'Bytt ut spiller',
  'players.swap.toNewPlayer': '→ ny spiller',
  'players.swap.availableHint': 'Tilgjengelige spillere (topp 10 etter ELO)',
  'players.swap.noMatches': 'Ingen treff for "{query}"',

  'players.replacementBadge.unknownTime': 'ukjent tidspunkt',
  'players.replacementBadge.buttonTitle': 'Innbytter — klikk for detaljer',
  'players.replacementBadge.kicker': 'Innbytter',
  'players.replacementBadge.title': 'Spillerbytte underveis',
  'players.replacementBadge.swappedOut': 'Byttet ut',
  'players.replacementBadge.replacedBy': 'Erstattet av',
  'players.replacementBadge.time': 'Tidspunkt',
  'players.replacementBadge.phase': 'Turneringsfase',
  'players.replacementBadge.finals': 'Finaler',
  'players.replacementBadge.phaseN': 'Fase {phase}',
  'players.replacementBadge.roundSuffix': ', Runde {round}',
  'players.replacementBadge.matchesPlayedLabel': 'Kamper spilt da byttet skjedde',
  'players.replacementBadge.matchesProgress': '{done} av {total} kamper ferdig',
  'players.replacementBadge.firstMatchFor': 'Første kamp for {name}',
  'players.replacementBadge.theSubstitute': 'innbytteren',
  'players.replacementBadge.matchPrefix': 'Kamp {number} · ',
  'players.replacementBadge.final': 'Finale',
  'players.replacementBadge.roundShort': ' · R{round}',
  'players.replacementBadge.court': 'Bane {court}',
  'players.replacementBadge.missingInfo': 'Manglende informasjon om byttet',
  'players.replacementBadge.close': 'Lukk',

  'players.newPlayer.title': 'Ny spiller',
  'players.newPlayer.lockedGenderNote': '(kreves av klassen)',
  'players.newPlayer.emailLabel': 'E-post (valgfritt)',
  'players.newPlayer.emailPlaceholder': 'valgfritt — for innlogging senere',
  'players.newPlayer.emailSet': 'Spilleren kan logge inn med denne e-posten.',
  'players.newPlayer.emailEmpty':
    'Uten e-post opprettes spilleren som «ghost». Du kan legge til e-post og innlogging senere.',
  'players.newPlayer.startEloHint': 'Nivå: {level} · Justeres automatisk etter første kamper',
  'players.newPlayer.submit': 'Opprett og legg til',

  'players.matchRow.baseChangeTitle': 'Basis-endring (uten ny spiller-boost)',
  'players.matchRow.round.semi': 'Semi',
  'players.matchRow.round.quarter': 'Kvart',
  'players.matchRow.round.r16': '8-del',
  'players.matchRow.round.r32': '16-del',
  'players.matchRow.final': 'Finale',
  'players.matchRow.finalN': 'Finale {number}',
  'players.matchRow.league': 'Serie',
  'players.matchRow.group': 'Grp',
  'players.matchRow.knockout': 'Sluttsp',
  'players.matchRow.eloBefore': 'ELO før kampen',
  'players.matchRow.eloCurrent': 'Nåværende ELO',
  'players.matchRow.teamAvgTitle': 'Lagets snitt-ELO før kampen{strongest}',
  'players.matchRow.strongestTeam': ' (sterkeste lag)',
  'players.matchRow.eloChangeTitle': 'ELO-endring etter kampen',
  'players.matchRow.notPlayed': 'Ikke spilt',
  'players.matchRow.draw': 'Uavgjort',

  'players.allMatches.genderMen': 'Herrer',
  'players.allMatches.genderWomen': 'Damer',
  'players.allMatches.genderMix': 'Mix',
  'players.allMatches.tournamentFallback': 'Turnering',
  'players.allMatches.singleMatch': 'Enkeltkamp',
  'players.allMatches.adjustment': 'Justering',
  'players.allMatches.empty': 'Ingen kamper spilt ennå',
  'players.allMatches.format': 'Format',
  'players.allMatches.all': 'Alle',
  'players.allMatches.count': '{shown} av {total} kamper — nyeste øverst',
  'players.allMatches.noMatchesForFilter': 'Ingen kamper matcher filteret',
  'players.allMatches.manualAdjustment': 'Manuell ELO-justering',

  'players.edit.fillIn': 'Fyll inn spiller',
  'players.edit.slotNumber': 'Plass #{number}',
  'players.edit.override': 'Overstyr spillerens detaljer',
  'players.edit.tabSearch': 'Søk i database',
  'players.edit.tabNew': 'Opprett ny',
  'players.edit.searchHint': 'Skriv navnet for å søke i klubbens spillerdatabase',
  'players.edit.noMatches': 'Ingen treff for "{query}".',
  'players.edit.createAs': 'Opprett "{query}" som ny',
  'players.edit.newLevelHint': 'Nivå: {level}. Justeres automatisk etter første kamper.',
  'players.edit.levelShort': 'nivå {level}',
  'players.edit.ratingFollowsResults':
    '💡 Ratingen følger av registrerte resultater og motorens beregning. Den er felles for hele plattformen, så en manuell korrigering gjøres av Starpoint.',
  'players.edit.createAndFill': 'Opprett og fyll inn',

  'players.playerEdit.createTitle': 'Opprett ny spiller',
  'players.playerEdit.editTitle': 'Rediger spiller',
  'players.playerEdit.confirmDelete.before': 'Er du sikker på at du vil slette',
  'players.playerEdit.confirmDelete.after':
    'fra databasen? Dette kan ikke angres. Spilleren vil fortsatt vises i tidligere arkiverte turneringer.',
  'players.playerEdit.deleteConfirm': 'Ja, slett spiller',
  'players.playerEdit.firstName': 'Fornavn',
  'players.playerEdit.lastName': 'Etternavn',
  'players.playerEdit.firstNamePlaceholder': 'Anna',
  'players.playerEdit.lastNamePlaceholder': 'Berg',
  'players.playerEdit.email': 'E-postadresse',
  'players.playerEdit.emailHint': '— for innlogging på sikt',
  'players.playerEdit.emailPlaceholder': 'anna@eksempel.no',
  'players.playerEdit.emailInvalid': 'Ugyldig e-postadresse',
  'players.playerEdit.emailSet': 'Spilleren kan knytte innlogging til denne e-posten senere.',
  'players.playerEdit.emailEmpty':
    'Uten e-post opprettes en «ghost» — spilleren finnes i systemet, men ingen kan logge inn før den faktiske personen registrerer seg som eier.',
  'players.playerEdit.maleOption': 'Mann ♂',
  'players.playerEdit.femaleOption': 'Kvinne ♀',
  'players.playerEdit.genderLocked': 'Kjønn er låst av klassens påmeldingsregel.',
  'players.playerEdit.eloRating': 'ELO-rating',
  'players.playerEdit.ratingFollowsResults':
    'Ratingen endres av registrerte resultater. Den er felles for hele plattformen, så en manuell korrigering gjøres av Starpoint.',
  'players.playerEdit.levelBadge': 'NIVÅ {level}',
  'players.playerEdit.selfManaged':
    'Spilleren har egen konto og endrer navn, e-post, telefon og kjønn selv. Kontakt StarPoint support hvis det må rettes.',
  'players.playerEdit.missingFirstName': 'Fornavn mangler',
  'players.playerEdit.missingLastName': 'Etternavn mangler',
  'players.playerEdit.emailInvalidOrEmpty': 'E-post er ugyldig (eller la den stå tom)',
  'players.playerEdit.eloRange': 'ELO må være mellom 100 og 4000',
  'players.playerEdit.cannotSave': 'Kan ikke lagre',
  'players.playerEdit.delete': 'Slett',
  'players.playerEdit.create': 'Opprett',

  'players.detail.notFound': 'Fant ikke spiller',
  // The first point of the rating chart, and the games-difference tile (#1063).
  'players.detail.eloStart': 'Start',
  'players.detail.gamesDiff': 'Diff: {diff}',
  'players.detail.group': 'Gruppe {letter}',
  'players.detail.profile': 'Spillerprofil',
  'players.detail.swappedOutTitle': 'Byttet ut med {name}',
  'players.detail.swappedOutBadge': '⊘ BYTTET UT',
  'players.detail.replacementTitle': 'Erstatter {name}',
  'players.detail.replacementBadge': '↻ INNBYTTER',
  'players.detail.eloChangeInTournament': '({change} i denne turneringen)',
  'players.detail.startEloTitle': 'Startet turneringen på {elo}',
  'players.detail.newPlayerTitle':
    'Som ny spiller får {name} forhøyet ELO-effekt som avtar lineært fra {boost}x (kamp 1) til 1x når de har spilt {count} kamper.',
  'players.detail.replacedOutHeading': 'Spilleren er byttet ut',
  'players.detail.replacedOutBefore': 'Erstattet av',
  'players.detail.replacedOutAfter':
    '. Kun kamper FØR byttet teller for {name} sin ELO. Resultater i kamper etter byttet tilfaller innbytter.',
  'players.detail.replacementHeading': 'Innbytter for {name}',
  'players.detail.replacementBody':
    'Tatt inn som erstatter underveis. Bare kamper FRA byttet og fremover teller for {name} sin ELO.',
  'players.detail.placeholderHeading': 'Reservert plass',
  'players.detail.placeholderBody':
    'Denne plassen er enda ikke fylt med en navngitt spiller. Klikk knappen under for å fylle inn — alle kommende kamper på plassen overføres til den nye spilleren.',
  'players.detail.fillNameTitle': 'Fyll inn navnet på denne reserverte plassen',
  'players.detail.replaceTitle': 'Bytt ut denne spilleren med en innbytter',
  'players.detail.fillName': 'Fyll inn navn',
  'players.detail.replacePlayer': 'Bytt ut spilleren',
  'players.detail.fullHistoryTitle':
    'Se spillerens fulle historikk på tvers av alle turneringer (spillerdatabase-profil)',
  'players.detail.fullHistory': 'Full historikk',
  'players.detail.statsWinLossDraw': 'Seire-tap-uavgjort',
  'players.detail.statsWinLoss': 'Seire-tap',
  'players.detail.statsMatchesPlayed': 'Kamper spilt',
  'players.detail.statsHomeAway': 'Hjemme–Borte',
  'players.detail.eloDevelopment': 'ELO-utvikling denne turneringen',
  'players.detail.afterEachMatch': 'Etter hver av {count} fullførte kamper',
  'players.detail.upcoming': 'Kommende kamper ({count})',
  'players.detail.completed': 'Ferdigspilt ({count})',
  'players.detail.newestFirst': 'Siste først',
  'players.detail.noMatches': 'Ingen kamper funnet for denne spilleren ennå',

  'players.history.back': 'Tilbake',
  'players.history.kicker': 'Spillerprofil · Full historikk',
  'players.history.kickerFemale': ' · Kvinne',
  'players.history.kickerMale': ' · Mann',
  'players.history.newBadge': 'NY',
  'players.history.eloAtYearEndTitle': 'ELO ved utgangen av {year}',
  'players.history.eloYear': 'ELO {year}:',
  'players.history.current': 'Nåværende:',
  'players.history.changeYearTitle': 'Endring i {year}',
  'players.history.changeYear': 'Endring {year}:',
  'players.history.newPlayerTitle':
    'Som ny spiller får {name} forhøyet ELO-effekt som avtar lineært fra {boost}x (kamp 1) til 1x når de har spilt {count} kamper. I americano (50% ELO-vekt) avtar boosten over {doubled} kamper.',
  'players.history.close': 'Lukk',
  'players.history.tournaments': 'Turneringer',
  'players.history.totalMatches': 'Kamper totalt',
  'players.history.wins': 'Seire',
  'players.history.losses': 'Tap',
  'players.history.eloDevelopmentYear': 'ELO-utvikling i {year}',
  'players.history.tabMatches': 'Alle kamper ({count})',
  'players.history.tabTournaments': 'Turneringer ({count})',
  'players.history.tabMerits': 'Meritter ({count})',
  'players.history.tabMeritsTitle': 'Plasseringer og medaljer fra turneringer',
  'players.history.noTournaments': '{name} har ikke deltatt i noen turneringer ennå',
  'players.history.unknownDate': 'Ukjent dato',
  'players.history.placement': '{position}. plass',
  'players.history.tournamentFallback': 'Turnering',
  'players.history.matchSummary': '{matches} kamper · {wins} seire · {losses} tap',
  'players.history.drawsSuffix': ' · {draws} uavgjort',
  'players.history.eloInTournament': 'ELO i turneringen',
  'players.history.goToTournament': 'Gå til turneringen',
  'players.history.showMatches': 'Kampene i {name}',
  'players.history.noMatches': 'Ingen kamper',

  'players.merits.empty':
    'Ingen meritter ennå — gode plasseringer i ferdige turneringer (og seriespill-sesonger) dukker opp her.',
  'players.merits.trophies': 'Pokaler',
  'players.merits.tournamentFallback': 'Turnering',
  'players.merits.placementDate': '{position}. plass · {date}',

  'players.view.home': 'Hjem',
  'players.view.title': 'Spillerdatabase',
  'players.view.lead':
    'Alle spillere registrert i klubben. ELO-rangeringen oppdateres etter hver fullførte turnering.',
  'players.view.create': 'Opprett spiller',
  'players.view.totalPlayers': 'Totalt spillere',
  'players.view.active': 'Aktive',
  'players.view.activeHint': 'har spilt minst én turnering',
  'players.view.avgElo': 'Snitt-ELO',
  'players.view.tournaments': 'Turneringer',
  'players.view.tournamentsHint': 'i historikken',
  // ── The two pages of the player database (2026-10-20) ─────────────
  //
  // «Alle spillere» is every player the club works with (its roster, its
  // events' players, guests); «Medlemmer» is the roster alone. Two questions,
  // two tabs.
  'players.view.tabsLabel': 'Spillere og medlemmer',
  'players.view.tabPlayers': 'Alle spillere',
  'players.view.tabMembers': 'Medlemmer',
  'players.view.searchPlaceholder': 'Søk etter spiller...',
  'players.view.filterAll': 'Alle',
  'players.view.filterWomen': 'Kvinner',
  'players.view.filterMen': 'Menn',
  'players.view.levelFilterTitle': 'Filtrer på spillernivå',
  'players.view.allLevels': 'Alle nivåer ({count})',
  'players.view.levelOption': 'Nivå {level} — {name} ({count})',
  'players.view.activeOnlyTitle': 'Vis kun spillere som har spilt en kamp de siste 30 dagene',
  'players.view.activeOnly': 'Kun aktive',
  'players.view.colPlayer': 'Spiller',
  'players.view.colLevel': 'Nivå',
  'players.view.colGender': 'Kjønn',
  'players.view.colEloYear': 'ELO {year}',
  'players.view.colEloYearTitle': 'ELO ved utgangen av {year}',
  'players.view.colNow': 'Nå',
  'players.view.trendTitle': 'Trend siste 30 dager',
  'players.view.colTrend': 'Trend',
  'players.view.colTournaments': 'Turneringer',
  'players.view.rankTitle': 'Plass på ELO-rangeringen',
  'players.view.customPlayer': 'Egendefinert spiller',
  'players.view.newBadge': 'NY',
  'players.view.levelPillTitle': 'Nivå {level}: {name} — {short}',
  'players.view.genderWoman': 'Dame',
  'players.view.genderMan': 'Mann',
  'players.view.noTrend': 'Ingen kamper siste 30 dager',
  'players.view.trendUp': '+{change} ELO siste 30 dager',
  'players.view.trendDown': '{change} ELO siste 30 dager',
  'players.view.trendFlat': '{change} ELO siste 30 dager — stabil',
  'players.view.noTournamentsCell': 'ingen',
  // ── INVITING A PLAYER TO THE CLUB, BY E-MAIL (#185, #1061) ───────
  'players.view.invitePlayer': 'Inviter til spiller-appen',
  'players.invite.title': 'Inviter {name}',
  'players.invite.explainConsent':
    'Vi sender en invitasjon til e-postadressen. Spilleren blir medlem først når de godtar den fra sin egen e-post.',
  'players.invite.emailLabel': 'E-postadresse',
  'players.invite.emailRequired': 'Skriv inn spillerens e-postadresse.',
  'players.invite.send': 'Send invitasjon',
  'players.invite.cancel': 'Avbryt',
  'players.invite.working': 'Sender …',
  'players.invite.sent': 'Invitasjonen er sendt til {email}. Lenken virker til {date}.',
  'players.invite.notYetMember':
    'Spilleren er ikke medlem av {club} ennå. Det blir de når de godtar invitasjonen.',
  'players.invite.claimNote':
    'Når spilleren godtar, knyttes kontoen deres til spilleren i klubbens liste.',
  'players.invite.notSent':
    'Invitasjonen kunne ikke sendes. En eldre invitasjon virker fortsatt. Prøv igjen.',
  'players.invite.close': 'Lukk',
  'players.invite.unreachable': 'Fikk ikke kontakt med serveren. Prøv igjen.',
  // ── FINDING A PLAYER FROM ANOTHER CLUB (#1060) ──────────────────
  'players.lookup.title': 'Finn en spiller fra en annen klubb',
  'players.lookup.explain':
    'Listen viser klubbens medlemmer og spillerne i klubbens egne arrangementer. Skriv hele navnet for å finne en annen spiller. Du ser navn og ELO, ikke kontaktinformasjon. Spilleren blir værende i listen når de er med i et av klubbens arrangementer.',
  'players.lookup.nameLabel': 'Fullt navn',
  'players.lookup.search': 'Søk',
  'players.lookup.working': 'Søker …',
  'players.lookup.none': 'Ingen spiller har akkurat det navnet.',
  'players.lookup.tooShort': 'Skriv hele navnet, minst tre tegn.',
  'players.lookup.unreachable': 'Fikk ikke kontakt med serveren. Prøv igjen.',
  'players.lookup.elo': 'ELO-poeng: {elo}',
  'players.lookup.adopt': 'Ta med i listen',
  'players.lookup.alreadyListed': 'Står allerede i listen',
  // ── «MIN PROFIL» — THE PLAYER EDITS THEIR OWN IDENTITY (#796) ─────
  'account.myProfile.title': 'Min profil',
  'account.myProfile.loading': 'Henter profilen …',
  'account.myProfile.name': 'Navn',
  'account.myProfile.email': 'E-post på profilen',
  'account.myProfile.emailNote':
    'Dette er adressen klubbene dine ser. Den endrer ikke e-postadressen du logger inn med.',
  'account.myProfile.phone': 'Telefon',
  'account.myProfile.gender': 'Kjønn',
  'account.myProfile.genderUnset': 'Ikke oppgitt',
  'account.myProfile.genderMale': 'Mann',
  'account.myProfile.genderFemale': 'Kvinne',
  'account.myProfile.save': 'Lagre profilen',
  'account.myProfile.saving': 'Lagrer …',
  'account.myProfile.saved': 'Profilen er lagret.',
  'account.myProfile.nameRequired': 'Skriv inn navnet ditt.',
  'account.myProfile.unreachable': 'Fikk ikke kontakt med serveren. Prøv igjen.',
  // ── «AVSLUTT KONTOEN» — ACCOUNT CLOSURE (#1062) ───────────────────
  'account.close.title': 'Avslutt kontoen',
  'account.close.lead':
    'Når kontoen er avsluttet, kan du ikke logge inn igjen. Alle påloggingene dine avsluttes og passkeyene dine fjernes.',
  'account.close.kept':
    'Resultatene, ELO-poengene, bookingene og medlemskapene dine slettes ikke. Det er en egen sak å be om å få slettet personopplysninger.',
  'account.close.start': 'Avslutt kontoen …',
  'account.close.confirmLead':
    'Er du sikker? Du blir logget ut med én gang, og kontoen kan ikke brukes igjen.',
  'account.close.confirm': 'Ja, avslutt kontoen',
  'account.close.cancel': 'Avbryt',
  'account.close.working': 'Avslutter …',
  'account.close.done': 'Kontoen er avsluttet. Du er logget ut.',
  'account.close.continue': 'Ferdig',
  'account.close.reauth':
    'Du må bekrefte at det er deg før kontoen kan avsluttes. Bekreft og trykk på knappen igjen.',
  'account.close.lastRoleManager':
    'Kontoen kan ikke avsluttes fordi den er den siste som kan dele ut StarPoint-roller.',
  'account.close.failed': 'Kontoen ble ikke avsluttet. Prøv igjen.',
  'account.close.unreachable': 'Fikk ikke kontakt med serveren. Kontoen ble ikke avsluttet.',
  // ── «LAST NED MINE DATA» — THE PERSON'S OWN EXPORT (#1062) ─────────
  'account.export.title': 'Last ned mine data',
  'account.export.lead':
    'Last ned en fil med det StarPoint har lagret om deg: kontoen, innloggingsmåtene, spillerprofilen, ELO-justeringer, enkeltkamper, feilrapporter og loggen over hva som er gjort med kontoen.',
  'account.export.notIncluded':
    'Klubbenes egne opplysninger om deg – bookinger, betalinger, medlemskap – er ikke med. Filen sier hvilke klubber som har dem; spør dem direkte.',
  'account.export.start': 'Last ned mine data',
  'account.export.working': 'Henter …',
  'account.export.done':
    'Filen er lastet ned. Den inneholder personopplysninger – oppbevar den trygt.',
  'account.export.reauth':
    'Du må bekrefte at det er deg før dataene kan lastes ned. Bekreft og trykk på knappen igjen.',
  'account.export.unreachable': 'Fikk ikke kontakt med serveren. Prøv igjen.',
  'account.export.failed': 'Dataene kunne ikke lastes ned. Prøv igjen.',
  // ── «SLETT KONTOEN MIN OG DATAENE MINE» — ERASURE (#1062) ──────────
  'account.erase.title': 'Slett kontoen min og dataene mine',
  'account.erase.lead':
    'Kontoen avsluttes, og det StarPoint har lagret om deg slettes. Dette kan ikke angres.',
  'account.erase.start': 'Slett kontoen min og dataene mine …',
  'account.erase.dialogTitle': 'Slette kontoen og dataene dine?',
  'account.erase.deletedHeading': 'Dette slettes',
  'account.erase.deleted1': 'Kontoen din, e-postadressen og alle innloggingsmåter og passkeys.',
  'account.erase.deleted2':
    'Navnet og kontaktopplysningene på spillerprofilen din. Profilen kobles fra deg for godt.',
  'account.erase.deleted3': 'Navnet ditt i feilrapporter du har sendt.',
  'account.erase.keptHeading': 'Dette beholdes',
  'account.erase.kept1':
    'Resultatene og ELO-historikken, uten navn – andres rangering er regnet ut fra dem.',
  'account.erase.kept2':
    'Klubbenes regnskapsbilag og egne opplysninger, som bookinger og betalinger. De er klubbens og slettes ikke herfra – spør klubben om dem.',
  'account.erase.kept3': 'En logg over at kontoen ble slettet, med tall og uten navn.',
  'account.erase.newAccount':
    'Logger du inn igjen senere med samme e-post, får du en ny, tom konto som ikke kobles til den gamle historikken.',
  'account.erase.exportFirst': 'Vil du ha en kopi først? Last ned dataene dine før du sletter.',
  'account.erase.typeLabel': 'Skriv {word} for å bekrefte',
  'account.erase.word': 'SLETT',
  'account.erase.confirm': 'Slett for godt',
  'account.erase.cancel': 'Avbryt',
  'account.erase.working': 'Sletter …',
  'account.erase.done': 'Kontoen og dataene dine er slettet. Du er logget ut.',
  'account.erase.continue': 'Ferdig',
  'account.erase.reauth':
    'Du må bekrefte at det er deg før kontoen kan slettes. Bekreft og trykk «Slett for godt» igjen.',
  'account.erase.lastRoleManager':
    'Kontoen kan ikke slettes fordi den er den siste som kan dele ut StarPoint-roller.',
  'account.erase.unreachable':
    'Fikk ikke kontakt med serveren. Vi vet ikke om kontoen ble slettet – last inn siden på nytt.',
  'account.erase.failed': 'Kontoen ble ikke slettet. Ingenting er endret – prøv igjen.',
  // En spiller hvis person har slettet dataene sine (#1062): navnet er tomt i
  // databasen, og skjermen viser denne nøytrale merkelappen i stedet.
  'players.erased': 'Slettet spiller',
  'players.view.editPlayer': 'Rediger spiller',
  'players.view.noSearchMatches': 'Ingen spillere matcher søket',

  'players.replace.fillSlotKicker': 'Fyll inn reservert plass',
  'players.replace.swapKicker': 'Bytt ut spiller',
  'players.replace.fillTitle': 'Velg spiller til {name}',
  'players.replace.swapTitle': 'Velg innbytter for {name}',
  'players.replace.bandHint': 'forslagene matcher dette nivået',
  'players.replace.fillLead':
    'Alle kommende kamper på denne plassen overføres til den nye spilleren. Ingen replacement-historikk lagres — det er som om spilleren alltid har vært på plassen.',
  'players.replace.swapLead':
    'Innbytteren tar over spillerens plass i alle gjenværende kamper. ELO-endringer fra ferdigspilte kamper påvirker den opprinnelige spilleren, kun nye kamper påvirker innbytteren.',
  'players.replace.searchPlaceholder': 'Søk navn...',
  'players.replace.standbyHeading': 'Beredskapsreserver',
  'players.replace.standbyFirst': 'foreslås først',
  'players.replace.standbyExplainBefore':
    'Forslaget rangerer etter hvor nær reserven er i ELO til den som må erstattes: først innenfor ±100 (er det flere der, går den som meldte seg',
  'players.replace.standbyExplainStrong': 'først',
  'players.replace.standbyExplainAfter':
    '), så ±200, deretter ±300 osv. En reserve langt nede på lista kan altså havne øverst hvis hen er nærmest nivået — og bli kalt inn selv om mange meldte seg før.',
  'players.replace.order': 'nr. {order}',
  'players.replace.fromOriginal': '{diff} fra original',
  'players.replace.notToday': ' · kan ikke i dag',
  'players.replace.canToday': 'kan i dag',
  'players.replace.otherDay': 'annen dag',
  'players.replace.otherPlayers': 'Andre spillere',
  'players.replace.noSearchMatches': 'Ingen spillere matcher søket',
  'players.replace.confirmFill': 'Fyll inn plassen',
  'players.replace.confirmSwap': 'Bekreft bytte',

  // ══ the member app (#310) ═════════════════════════════════════════
  //
  // The phone-shaped surface members actually use — and, through the Expo
  // WebView shell in apps/mobile, the native iOS/Android app as well. It is
  // the surface where a non-native speaker is most likely to turn up: a
  // club's staff are Norwegian, its MEMBERS need not be.
  //
  // Keys are namespaced by screen (`mobile.home.*`), with `mobile.common.*`
  // for the handful of words that genuinely recur across screens. The
  // Norwegian here is verbatim what the markup said before the move — the
  // rendered Norwegian does not change.
  // ── shared across the mobile screens ──────────────────────────────
  'mobile.common.player': 'Spiller',
  'mobile.common.openSlot': 'ledig',
  'mobile.common.close': 'Lukk',
  'mobile.common.back': 'Tilbake',
  'mobile.common.cancel': 'Avbryt',
  'mobile.common.save': 'Lagre',
  'mobile.common.saving': 'Lagrer …',
  'mobile.common.noMatches': 'Ingen treff',
  'mobile.common.centres': 'Padelsentre',
  'mobile.common.matches': 'Kamper',
  'mobile.common.activity': 'Aktivitet',
  'mobile.common.tournament': 'Turnering',
  'mobile.common.waitlist': 'Venteliste',
  'mobile.common.signedUp': 'Påmeldt',
  'mobile.common.round': 'Runde {round}',

  // ── the bottom tab bar ────────────────────────────────────────────
  'mobile.tab.home': 'Hjem',
  'mobile.tab.play': 'Spill',
  'mobile.tab.reservations': 'Reservasjoner',
  'mobile.tab.matches': 'Kamper',
  'mobile.tab.messages': 'Meldinger',
  'mobile.tab.profile': 'Profil',
  'mobile.tab.navLabel': 'Hovedmeny',
  'mobile.tab.unreadAria': '{label} ({count} uleste)',

  // ── the app shell ─────────────────────────────────────────────────
  'mobile.app.pickUser': 'Velg bruker',
  'mobile.app.profileAria': 'Profil · {name} ({initials})',
  'mobile.phoneFrame.close': 'Lukk forhåndsvisning',
  'mobile.emoji.pick': 'Velg emoji',
  // The picker's own copy (#1054): the group headings and the per-emoji
  // accessible name. Group headings are interface text, not data — the
  // picked emoji is what reaches the draft.
  'mobile.emoji.insert': 'Sett inn {emoji}',
  'mobile.emoji.group.padel': 'Padel',
  'mobile.emoji.group.smileys': 'Smil',
  'mobile.emoji.group.handsHearts': 'Hender & hjerter',

  // ── «hvem er du» / bytt bruker ────────────────────────────────────
  'mobile.memberPicker.title': 'Hvem er du?',
  'mobile.memberPicker.switch': 'Bytt bruker',
  'mobile.memberPicker.hint': 'Velg spilleren du vil bruke appen som.',
  'mobile.memberPicker.hintFirst':
    'Velg spilleren du vil bruke appen som. Påmeldinger og bookinger gjøres som denne spilleren.',
  'mobile.memberPicker.hintSwitch': 'Alt i appen gjøres som den valgte spilleren.',
  'mobile.memberPicker.search': 'Søk etter navn …',
  'mobile.memberPicker.searchLabel': 'Søk etter navn',

  // ── Hjem ──────────────────────────────────────────────────────────
  'mobile.home.greetMorning': 'God morgen',
  'mobile.home.greetDay': 'Hei',
  'mobile.home.greetEvening': 'God kveld',
  'mobile.home.eloCardAria': 'Din ELO og trend',
  'mobile.home.yourLevel': 'Ditt nivå',
  'mobile.home.topLevel': 'Toppnivå i klubben',
  'mobile.home.eloAndTrend': 'ELO & trend ›',
  'mobile.home.inProgress': 'Pågår nå',
  'mobile.home.startingSoon': 'Starter snart',
  'mobile.home.matchLeft': '{count} kamp igjen for deg',
  'mobile.home.matchesLeft': '{count} kamper igjen for deg',
  'mobile.home.allPlayed': 'Alle kampene dine er spilt',
  'mobile.home.nextForYou': 'Neste for deg',
  'mobile.home.nextMatch': 'Neste kamp · {name}',
  'mobile.home.emptyTitle': 'Ingenting planlagt ennå',
  'mobile.home.emptyHint': 'Book en bane eller meld deg på en aktivitet under.',
  'mobile.home.bookCourt': 'Book bane',
  'mobile.home.bookCourtSub': 'Ledige tider i dag',
  'mobile.home.signUp': 'Meld deg på',
  'mobile.home.signUpSub': 'Americano, kurs …',
  'mobile.home.seeAll': 'Alle',
  'mobile.home.onWaitlist': 'Du står på venteliste',
  'mobile.home.youAreSignedUp': 'Du er påmeldt',

  // ── Reservasjoner ─────────────────────────────────────────────────
  'mobile.reservations.title': 'Reservasjoner',
  'mobile.reservations.upcoming': 'Kommende',
  'mobile.reservations.past': 'Tidligere',
  'mobile.reservations.emptyUpcoming': 'Ingen reservasjoner ennå',
  'mobile.reservations.emptyPast': 'Ingenting tidligere',
  'mobile.reservations.emptyUpcomingHint': 'Book en bane eller meld deg på under Spill.',
  'mobile.reservations.emptyPastHint': 'Bookinger og påmeldinger du har hatt havner her.',
  'mobile.reservations.singleMatch': 'Enkeltkamp',
  'mobile.reservations.courtBooking': 'Banebooking',
  // The court is held; the payment is not done. Both halves matter (#284).
  'mobile.reservations.unpaid': 'Ikke betalt',
  // Said only on the server's word — see PayForBooking's `onPaid`.
  'mobile.reservations.paid': 'Betalt',
  // Avbestilling (#1135): medlemmet avbestiller selv før klubbens frist, betalt
  // inkludert, med full refusjon. Teksten kommer fra serverens svar.
  'mobile.reservations.cancel.open': 'Avbestill',
  'mobile.reservations.cancel.title': 'Avbestille bookingen?',
  'mobile.reservations.cancel.refund': 'Du får tilbake {amount} kr.',
  'mobile.reservations.cancel.free': 'Bookingen er ikke betalt, så ingenting blir trukket.',
  'mobile.reservations.cancel.until': 'Du kan avbestille fram til {when}.',
  'mobile.reservations.cancel.confirm': 'Ja, avbestill',
  'mobile.reservations.cancel.keep': 'Behold bookingen',
  'mobile.reservations.cancel.working': 'Avbestiller …',
  'mobile.reservations.cancel.done': 'Avbestilt',
  'mobile.reservations.cancel.doneRefund': 'Avbestilt – {amount} kr kommer tilbake til deg.',
  'mobile.reservations.cancel.tooLate': 'For sent å avbestille – kontakt klubben.',
  'mobile.reservations.cancel.unconfirmed':
    'Refusjonen er ikke bekreftet ennå. Banen er fortsatt din til klubben har sjekket betalingen.',
  'mobile.reservations.cancel.failed':
    'Vi fikk ikke refundert betalingen, så bookingen er beholdt. Prøv igjen eller kontakt klubben.',
  'mobile.reservations.cancel.error': 'Noe gikk galt. Bookingen er ikke avbestilt – prøv igjen.',
  'mobile.reservations.cancel.unavailable':
    'Avbestilling er ikke tilgjengelig nå – kontakt klubben.',
  'mobile.reservations.openSlot': '{count} ledig plass',
  'mobile.reservations.openSlots': '{count} ledige plasser',
  // Utstyr (#911 slice 2): en maskin booket alene er sin egen rad; en som
  // følger en banebooking nevnes på bookingens rad.
  'mobile.reservations.equipmentBooking': 'Utstyr',
  'mobile.reservations.withEquipment': 'Med {names}',

  // ── Alle klubber — egen historikk på tvers av klubber (#760) ─────
  'mobile.history.allClubs': 'Alle klubber',
  'mobile.history.noClubLead': 'Ingen klubb valgt. Her er det du har gjort i alle klubber.',
  'mobile.history.chooseClub': 'Velg klubb',
  'mobile.history.lead': 'Dine egne bookinger og påmeldinger, i alle klubbene du har spilt i.',
  'mobile.history.matchesLead': 'Dine enkeltkamper, i alle klubbene du har spilt i.',
  'mobile.history.loading': 'Henter historikken din …',
  'mobile.history.failed': 'Kunne ikke hente historikken din.',
  'mobile.history.retry': 'Prøv igjen',
  'mobile.history.empty': 'Ingenting ennå',
  'mobile.history.emptyHint': 'Bookinger og påmeldinger du gjør i en klubb havner her.',
  'mobile.history.emptyMatches': 'Ingen enkeltkamper ennå',
  'mobile.history.emptyMatchesHint':
    'Enkeltkamper du spiller havner her. Turneringer finner du under hver klubb.',
  'mobile.history.asPartner': 'Påmeldt som makker',
  'mobile.history.days': '{count} dager',
  'mobile.history.you': 'Du',
  'mobile.history.youAnd': 'Du og {name}',
  'mobile.history.versus': 'mot {names}',

  // ── Kamper ────────────────────────────────────────────────────────
  'mobile.matches.played': 'Spilt',
  'mobile.matches.emptyUpcoming': 'Ingen kommende kamper',
  'mobile.matches.emptyUpcomingHint':
    'Book en bane og registrer en kamp, eller meld deg på en turnering.',
  'mobile.matches.emptyPlayed': 'Ingen spilte kamper ennå',
  'mobile.matches.emptyPlayedHint': 'Enkeltkamper og turneringer du har spilt havner her.',
  'mobile.matches.singleMatches': 'Enkeltkamper',
  'mobile.matches.addPlayer': 'Legg til spiller',
  'mobile.matches.addPlayerHint': 'Velg hvem som skal ha plassen i kampen.',
  'mobile.matches.add': 'Legg til',
  'mobile.matches.removePlayer': 'Fjern {name}',
  'mobile.matches.openTag': '{count} LEDIG',
  'mobile.matches.openTagPlural': '{count} LEDIGE',
  'mobile.matches.fillSlotsHint': 'Legg til medspillere, eller la plassene stå åpne under Matcher.',
  'mobile.matches.enterResult': 'Legg inn resultat',
  'mobile.matches.needOneSet': 'Legg inn minst ett sett med en vinner.',
  'mobile.matches.saveFailed': 'Kunne ikke lagre resultatet.',
  'mobile.matches.saveResult': 'Lagre resultat',
  'mobile.matches.setNo': 'Sett {n}',
  'mobile.matches.setTeamAria': 'Sett {n}, lag {team}',
  'mobile.matches.setYouAria': 'Sett {n}, deg',
  'mobile.matches.setThemAria': 'Sett {n}, dem',
  'mobile.matches.setsYouThem': 'Sett (deg – dem)',
  'mobile.matches.superTieBreakNote': 'super-tiebreak teller som sett',
  'mobile.matches.playedTag': 'SPILT',
  'mobile.matches.wonTag': 'SEIER',
  'mobile.matches.lostTag': 'TAP',
  'mobile.matches.walkoverTag': 'WO',
  'mobile.matches.notPlayedTag': 'IKKE SPILT',
  'mobile.matches.nextMatch': 'Neste kamp',
  'mobile.matches.ofTotal': 'av {total}',
  'mobile.matches.inProgress': ' · pågår',
  'mobile.matches.notStarted': ' · ikke startet',
  'mobile.matches.leftForYou': ' · {count} igjen for deg',
  'mobile.matches.allPlayedSuffix': ' · alle spilt',
  'mobile.matches.noneScheduled': 'Ingen kamper satt opp for deg ennå',
  'mobile.matches.organiserRecords': 'Resultatet registreres av arrangøren.',
  'mobile.matches.you': 'Deg',
  'mobile.matches.them': 'Dem',
  'mobile.matches.pointsYou': 'Poeng, deg',
  'mobile.matches.pointsThem': 'Poeng, dem',

  // ── Seriespill ────────────────────────────────────────────────────
  'mobile.league.title': 'Seriespill',
  'mobile.league.ongoing': 'Pågående sesong',
  'mobile.league.pastSeasons': 'Tidligere sesonger',
  'mobile.league.emptyTitle': 'Du er ikke med i noe seriespill nå',
  'mobile.league.emptyHint':
    'Meld deg på en sesong under Spill → Aktiviteter når påmeldingen åpner.',
  'mobile.league.matchCount': '{count} kamper',
  'mobile.league.ofTotal': ' · av {total}',

  // ── ELO ───────────────────────────────────────────────────────────
  'mobile.elo.title': 'Din ELO',
  'mobile.elo.noPlayer': 'Ingen spiller valgt.',
  'mobile.elo.level': 'Nivå',
  'mobile.elo.topLevelShort': 'Øverste nivå',
  'mobile.elo.topLevel': 'Du er på toppnivået i klubben',
  'mobile.elo.pointsToNext': '{points} poeng til {level} · {name}',
  'mobile.elo.trend': 'Trend',
  'mobile.elo.days30': '30 dager',
  'mobile.elo.days90': '90 dager',
  'mobile.elo.noTrendYet': 'Spill noen kamper, så tegnes utviklingen her.',
  'mobile.elo.recentChanges': 'Siste endringer',
  'mobile.elo.emptyTitle': 'Ingen ELO-endringer ennå',
  'mobile.elo.emptyHint': 'Resultater fra kamper og turneringer havner her.',
  'mobile.elo.versus': ' · mot {names}',
  'mobile.elo.levels': 'Nivåene',
  'mobile.elo.explainer':
    'ELO endres etter hver kamp som teller: du vinner mer mot sterkere motstand og taper mer mot svakere. Treningskamper og walkover teller ikke.',
  'mobile.elo.chartAria': 'ELO-utvikling',

  // ── Profil ────────────────────────────────────────────────────────
  'mobile.profile.noPlayers': 'Ingen spillere i databasen ennå.',
  'mobile.profile.statElo': 'ELO',
  'mobile.profile.statWins': 'Seire',
  'mobile.profile.statTournaments': 'Turneringer',
  'mobile.profile.levelLine': 'Nivå {level} · {name}',
  'mobile.profile.last5': '· siste 5:',
  'mobile.profile.pickFavourite': 'Velg favorittsenter',
  'mobile.profile.centresHint': 'Søk, filtrer og velg favoritt',
  'mobile.profile.levelTraits': 'Kjennetegn på nivået',
  'mobile.profile.notifications': 'Varslinger',
  'mobile.profile.signedInAs': 'Innlogget som',
  'mobile.profile.switchProfile': 'Bytt profil',
  'mobile.profile.signOut': 'Logg ut',
  'mobile.identity.failedTitle': 'Vi fikk ikke hentet profilen din',
  'mobile.identity.failedBody':
    'Det er ikke noe galt med kontoen din — vi fikk bare ikke svar fra serveren.',
  'mobile.identity.retry': 'Prøv igjen',
  'mobile.identity.noneTitle': 'Du har ingen spillerprofil ennå',
  'mobile.identity.noneBody': 'Velg navnet du vil spille under, så er du i gang.',
  'mobile.identity.noneMemberBody': 'Ta kontakt med klubben din for å komme i gang.',
  'mobile.identity.nameLabel': 'Navn',
  'mobile.identity.create': 'Opprett spillerprofil',
  'mobile.identity.creating': 'Oppretter …',
  'mobile.identity.nameRequired': 'Skriv inn navnet ditt.',
  'mobile.identity.createFailed': 'Det gikk ikke. Prøv igjen.',
  'mobile.club.loading': 'Henter klubben …',
  'mobile.club.failedTitle': 'Vi fikk ikke hentet klubben',
  'mobile.club.failedBody':
    'Bookingene og kampene dine er der — vi fikk bare ikke svar fra serveren.',

  // ── the active club context — the 📍 selector (owner, 2026-10-16) ──
  'mobile.clubContext.title': 'Klubb',
  'mobile.clubContext.lead': 'Velg hvilken klubb appen viser baner, aktiviteter og kamper fra.',
  'mobile.clubContext.choose': 'Velg klubb',
  'mobile.clubContext.chooseOther': 'Velg en annen klubb',
  'mobile.clubContext.selectorAria': 'Klubb: {name}. Bytt klubb',
  'mobile.clubContext.none': 'Ingen klubb',
  'mobile.clubContext.noneHint': 'Bare det som er ditt eget',
  'mobile.clubContext.yours': 'Klubbene dine',
  'mobile.clubContext.loading': 'Henter klubbene dine …',
  'mobile.clubContext.empty': 'Du er ikke med i noen klubb ennå.',
  'mobile.clubContext.failed': 'Vi fikk ikke hentet klubbene dine.',
  'mobile.clubContext.findClub': 'Finn en klubb',
  'mobile.clubContext.findClubHint': 'Søk i klubber og sentre, og be om medlemskap',
  'mobile.clubContext.adminTitle': 'Administrer organisasjon',
  'mobile.clubContext.adminEnter': 'Gå til',
  'mobile.admin.title': 'Administrasjon',
  'mobile.admin.lead': 'Organisasjonene du kan administrere',
  'mobile.clubContext.neededTitle': 'Velg en klubb',
  'mobile.clubContext.neededLead':
    'Baner, aktiviteter og kamper hører til en klubb. Velg hvilken du vil se.',
  'mobile.clubContext.homeNone': 'Ingen klubb valgt — bookinger og aktiviteter vises per klubb.',
  'mobile.profile.demoNote': 'Demo — ekte innlogging kommer når brukerkontoer er på plass.',

  // ── push notifications (the native shell) ─────────────────────────
  'mobile.push.granted': 'Varslinger er på. Du får beskjed om kamper, bookinger og påmeldinger.',
  'mobile.push.denied':
    'Varslinger er avslått. Slå dem på under Innstillinger → Starpoint → Varslinger.',
  'mobile.push.unavailable': 'Ikke tilgjengelig på denne enheten (simulator).',
  'mobile.push.undetermined': 'Få beskjed når det skjer noe: kamper, bookinger og påmeldinger.',
  'mobile.push.unsupported': 'Varslinger krever Starpoint-appen.',
  'mobile.push.checking': 'Sjekker …',
  'mobile.push.enable': 'Slå på varslinger',

  // ── Padelsentre (the list) ────────────────────────────────────────
  'mobile.centres.title': 'Padelsentre',
  'mobile.centres.lead':
    'Velg favorittsenter og hvilke sentre du vil se aktiviteter fra. Flere sentre dukker opp etter hvert som de kobler seg på.',
  'mobile.centres.search': 'Søk etter senter …',
  'mobile.centres.searchLabel': 'Søk etter senter',
  'mobile.centres.all': 'Alle sentre',
  'mobile.centres.favouriteOnly': 'Bare favoritt',
  'mobile.centres.open': 'Åpne {name}',
  'mobile.centres.yourClub': 'Din klubb · ',
  'mobile.centres.openActivity': '{count} åpen aktivitet',
  'mobile.centres.openActivities': '{count} åpne aktiviteter',
  'mobile.centres.hiddenSuffix': ' · skjult',
  'mobile.centres.favourite': 'Sett som favoritt',
  'mobile.centres.unfavourite': 'Fjern som favoritt',
  'mobile.centres.noMatches': 'Ingen sentre matcher søket',

  // ── one centre's own page ─────────────────────────────────────────
  'mobile.centre.tabAbout': 'Om',
  'mobile.centre.tabBook': 'Book',
  'mobile.centre.tabActivities': 'Aktiviteter',
  'mobile.centre.tabMatches': 'Matcher',
  'mobile.centre.switch': 'Bytt senter',
  'mobile.centre.openingHours': 'Åpningstider',
  'mobile.centre.today': 'I dag',
  'mobile.centre.contact': 'Kontakt',
  'mobile.centre.courts': 'Baner',
  'mobile.centre.noCourts': 'Ingen baner registrert',
  'mobile.centre.courtKind': 'Padel · dobbel · innendørs',
  'mobile.centre.openActivityHere': '{count} åpen aktivitet her nå',
  'mobile.centre.openActivitiesHere': '{count} åpne aktiviteter her nå',
  'mobile.centre.bookingComingTitle': 'Booking hos dette senteret kommer',
  'mobile.centre.bookingComingHint': 'Baner kan foreløpig bare bookes hos klubben din.',
  'mobile.centre.matchesLead':
    'Enkeltkamper som medlemmer har lagt ut på en booking her. Ledige plasser kan du melde deg på — du legger ut din egen ved å booke en bane og krysse av for «registrer en kamp».',
  'mobile.centre.noMatchesTitle': 'Ingen matcher lagt ut',
  'mobile.centre.noMatchesHint': 'Book en bane under «Book» og kryss av for «registrer en kamp».',
  'mobile.centre.levelGate': 'Nivå {min}–{max} ·',
  'mobile.centre.friendlyMatch': 'Treningskamp · teller ikke på ELO',
  'mobile.centre.countsTowardsElo': 'Teller på ELO',
  'mobile.centre.countsTowardsEloPct': 'Teller på ELO ({pct}%)',
  'mobile.centre.outsideLevel': 'Utenfor nivåkravet (du er N{level}).',
  'mobile.centre.joinMatch': 'Meld meg på',
  'mobile.centre.leaveMatch': 'Meld meg av',
  // ── THE VENUE PAGE, SEEN FROM OUTSIDE (#833) ──────────────────────
  //
  // What a place has entered — courts and opening hours — for a player who is
  // not a member there. NO PRICE AND NO FREE TIMES: booking for non-members is
  // a later slice, and the page says so instead of pretending. And no claim
  // about what membership gives beyond booking — that is not decided (#833,
  // owner 2026-10-16).
  'mobile.venue.loading': 'Henter …',
  'mobile.venue.failed': 'Fikk ikke hentet informasjonen. Prøv igjen.',
  'mobile.venue.retry': 'Prøv igjen',
  'mobile.venue.notFound': 'Fant ikke dette stedet.',
  'mobile.venue.everyDay': 'Alle dager',
  'mobile.venue.noHours': 'Ikke lagt inn ennå',
  'mobile.venue.courtsOne': '1 padelbane',
  'mobile.venue.courtsMany': '{count} padelbaner',
  'mobile.venue.courtNumber': 'Bane {number}',
  'mobile.venue.courtDouble': 'Padel · dobbel',
  'mobile.venue.courtSingle': 'Padel · single',
  'mobile.venue.booking': 'Booking',
  'mobile.venue.memberHere': 'Du er medlem her.',
  'mobile.venue.book': 'Book bane',
  'mobile.venue.select': 'Gå til klubben',
  'mobile.venue.bookingSoonTitle': 'Booking uten medlemskap kommer',
  'mobile.venue.bookingSoonHint': 'Foreløpig kan bare medlemmer booke baner her i appen.',
  'mobile.venue.membership': 'Medlemskap',
  // What is free at a venue (#833 slice 2) — occupancy only, never a price.
  'mobile.venue.freeTimes': 'Ledige tider',
  'mobile.venue.freeTimesFor': 'Ledige starttider, {minutes} min',
  'mobile.venue.chooseDay': 'Velg dag',
  'mobile.venue.today': 'I dag',
  'mobile.venue.noFreeTimes': 'Ingen ledige tider denne dagen.',
  'mobile.venue.availabilityFailed': 'Fikk ikke hentet ledige tider. Prøv igjen.',
  'mobile.venue.freeCourts': 'Ledig: {courts}',

  // ── Aktiviteter ───────────────────────────────────────────────────
  'mobile.activities.title': 'Aktiviteter',
  'mobile.activities.saveFailed': 'Noe gikk galt. Prøv igjen.',
  'mobile.activities.paymentNotReady':
    'Betaling er ikke klar ennå. Ta kontakt med klubben for å melde deg på denne aktiviteten.',
  'mobile.activities.pickCentre': 'Velg padelsenter',
  'mobile.activities.myLevelFilter': 'Passer mitt nivå (N{level})',
  'mobile.activities.hiddenCount': '{count} skjult',
  'mobile.activities.outsideLevelCount': '{count} utenfor nivå',
  'mobile.activities.empty': 'Ingen åpne aktiviteter',
  'mobile.activities.emptyHint': 'Kom tilbake senere — nye legges ut jevnlig.',
  'mobile.activities.emptyAtCentre': 'Ingen åpne aktiviteter ved dette senteret',
  'mobile.activities.emptyAtCentreHint': 'Velg «Alle sentre» for å se resten.',
  'mobile.activities.openForSignup': 'Åpne for påmelding',
  'mobile.activities.levelRange': 'Nivå {min}–{max}',
  'mobile.activities.allLevels': 'Alle nivåer',
  'mobile.activities.free': 'Gratis',
  'mobile.activities.perPlayer': 'per spiller',
  'mobile.activities.onWaitlistCount': ' · {count} på venteliste',
  'mobile.activities.onWaitlist': 'På venteliste',
  'mobile.activities.signOff': 'Meld av',
  'mobile.activities.partnerSignup': 'Påmelding med makker/klasse — bruk aktivitetssiden.',
  'mobile.activities.signingUp': 'Melder på …',
  'mobile.activities.joinWaitlist': 'Sett meg på venteliste',
  'mobile.activities.signUpPriced': 'Meld på · {price} kr',

  // ── Book bane ─────────────────────────────────────────────────────
  'mobile.book.anyLength': 'Alle',
  'mobile.book.freeTimes': 'Ledige tider · {day}',
  'mobile.book.noTimes': 'Ingen ledige tider',
  'mobile.book.noTimesHint': 'Prøv en annen dag eller kortere varighet.',
  'mobile.book.notOpen': 'Booking er ikke åpnet ennå',
  'mobile.book.notOpenHint':
    'Klubben har ikke lagt inn åpningstidene sine. Når de er lagret, kan du booke her.',
  'mobile.book.priceNotSet': 'Pris ikke satt',
  'mobile.book.priceNotSetHint':
    'Klubben har ikke satt prisene sine ennå. Når de er satt, kan du booke banen her.',
  'mobile.book.courtFree': '{count} bane ledig',
  'mobile.book.courtsFree': '{count} baner ledig',
  'mobile.book.pickCourtOpen': 'Velg en ledig bane under for kl. {time} — så velger du hvor lenge.',
  'mobile.book.pickCourtFixed': 'Velg en ledig bane under for {from}–{to}.',
  'mobile.book.pickTimeFirst':
    'Velg en tid, så viser banelisten hva som er ledig. Gul kant = siste ledige bane.',
  'mobile.book.freeCourtsAt': 'Ledige baner · kl. {time}',
  'mobile.book.freeCourtsBetween': 'Ledige baner · {from}–{to}',
  'mobile.book.noCourts': 'Ingen ledige baner',
  'mobile.book.noCourtsHint': 'Prøv en annen tid eller varighet.',
  'mobile.book.chosen': 'Valgt',
  'mobile.book.pickLength': 'Velg lengde',
  'mobile.book.lengthCount': '{count} lengde',
  'mobile.book.lengthCountPlural': '{count} lengder',
  'mobile.book.freeBookIt': 'Ledig · book',
  'mobile.book.taken': 'Opptatt',
  'mobile.book.timeCount': '{count} tid',
  'mobile.book.timeCountPlural': '{count} tider',
  'mobile.book.confirmedAria': 'Booking bekreftet',
  'mobile.book.expand': 'Utvid bookingen',
  'mobile.book.collapse': 'Minimer bookingen',
  'mobile.book.courtIsYours': 'Banen er din!',
  // NOT «banen er din» while the booking still owes money — saying only that
  // sends the member away from the one screen that can take the payment (#284).
  'mobile.book.courtHeldUnpaid': 'Banen er reservert. Betal for å fullføre.',
  'mobile.book.withMatchSuffix': ' · med kamp',
  'mobile.book.bookShort': 'Book',
  'mobile.book.bookedFor': '{court} · bookes på {name}',
  'mobile.book.you': 'deg',
  'mobile.book.registerMatch': 'Registrer en kamp på denne bookingen',
  'mobile.book.registerMatchHint':
    'Du settes opp som spiller, og de tre andre plassene legges ut under Matcher så andre kan melde seg på.',
  'mobile.book.levelGateHeading':
    'Nivåkrav for de som melder seg på · anbefalt N{min}–N{max} (ditt nivå ±1)',
  'mobile.book.levelMin': 'Min',
  'mobile.book.levelMax': 'Maks',
  'mobile.book.levelMinAria': 'Laveste nivå',
  'mobile.book.levelMaxAria': 'Høyeste nivå',
  'mobile.book.levelRecommended': 'N{min}–N{max} · anbefalt',
  'mobile.book.useRecommended': 'Bruk anbefalt',
  'mobile.book.booking': 'Booker …',
  'mobile.book.payAndBook': 'Betal {price} kr og book',
  'mobile.book.bookIt': 'Book banen',
  'mobile.book.failed': 'Kunne ikke booke — prøv en annen tid.',
  'mobile.book.matchPosted': 'Kampen er lagt ut under Matcher — 3 ledige plasser.',
  'mobile.book.matchNotRegistered': 'Bookingen er i orden, men kampen ble ikke registrert.',
  'mobile.book.fromTime': 'fra {time}',
  'mobile.book.tapLength': 'trykk på lengden du vil ha',
  'mobile.book.minutesUntil': '{minutes} minutter til {end}',
  'mobile.book.minutesUnavailable': '{minutes} minutter ikke ledig',
  'mobile.book.freeLower': 'gratis',
  // Equipment beside a court, in the member app (#911 slice 2).
  'mobile.book.equipment.heading': 'Legg til utstyr',
  'mobile.book.equipment.hint': 'Utstyret bookes for hele banetiden og betales sammen med banen.',
  'mobile.book.equipment.noneFree': 'Ikke noe utstyr er ledig på denne tiden.',
  'mobile.book.equipment.added': 'Med {names}',
  'mobile.book.equipment.error.taken':
    'Utstyret du valgte er ikke ledig lenger. Velg på nytt — banen ble ikke booket.',
  'mobile.book.equipment.error.unavailable':
    'Utstyret du valgte kan ikke bookes nå. Velg på nytt — banen ble ikke booket.',
  'mobile.book.equipment.error.generic':
    'Utstyret kunne ikke bookes sammen med banen. Banen ble ikke booket.',
  // Utstyr på egen hånd — «Utstyr»-modusen (#911 slice 2, ASSET_BOOKING.md § 6b).
  'mobile.book.equipment.alone.modeAria': 'Hva vil du booke?',
  'mobile.book.equipment.alone.modeCourt': 'Bane',
  'mobile.book.equipment.alone.modeEquipment': 'Utstyr',
  'mobile.book.equipment.alone.freeTimes': 'Ledig utstyr · {day}',
  'mobile.book.equipment.alone.freeAt': 'Ledig utstyr · {from}–{to}',
  'mobile.book.equipment.alone.none': 'Ikke noe ledig utstyr',
  'mobile.book.equipment.alone.noneHint': 'Prøv en annen dag eller en kortere varighet.',
  'mobile.book.equipment.alone.pick': 'Velg utstyr under for {from}–{to}.',
  'mobile.book.equipment.alone.bookedFor': '{asset} · bookes på {name}',
  'mobile.book.equipment.alone.book': 'Book utstyret',
  'mobile.book.equipment.alone.sheetAria': 'Book utstyr',
  'mobile.book.equipment.alone.confirmedAria': 'Utstyrsbooking bekreftet',
  'mobile.book.equipment.alone.yours': 'Utstyret er ditt!',
  'mobile.book.equipment.alone.heldUnpaid': 'Utstyret er reservert. Betal for å fullføre.',
  'mobile.book.equipment.alone.error.taken':
    'Utstyret er ikke ledig lenger. Velg et annet tidspunkt.',
  'mobile.book.equipment.alone.error.unavailable': 'Utstyret kan ikke bookes nå.',
  'mobile.book.equipment.alone.error.generic': 'Utstyret kunne ikke bookes.',

  // ── Meldinger (inbox) ─────────────────────────────────────────────
  'mobile.messaging.clubFallback': 'Klubben',
  'mobile.messaging.courtPad': 'Bane {court} · banepad',
  'mobile.messages.unavailableTitle': 'Meldinger er ikke tilgjengelig ennå',
  'mobile.messages.unavailableLead':
    'Vi jobber med meldinger for medlemmer. Ta kontakt med klubben din i mellomtiden.',
  'mobile.messages.pickWhoFirst': 'Velg hvem du er først',
  'mobile.messages.pickWhoFirstHint': 'Meldingene dine følger profilen din.',
  'mobile.messages.offTitle': 'Meldinger er slått av for deg',
  'mobile.messages.offLead':
    'Du sender og mottar ingen meldinger, du vises ikke når andre søker, og du er tatt ut av gruppene. Slå på igjen når du vil.',
  'mobile.messages.turnOn': 'Slå på meldinger',
  'mobile.messages.newShort': 'Ny',
  'mobile.messages.search': 'Søk i samtaler',
  'mobile.messages.filterAll': 'Alle',
  'mobile.messages.filterUnread': 'Uleste',
  'mobile.messages.filterGroups': 'Grupper',
  'mobile.messages.leftGroups': 'Du har meldt deg ut av de automatiske gruppene i {clubs}.',
  'mobile.messages.rejoin': 'Bli med igjen',
  'mobile.messages.emptySearch': 'Ingen samtaler matcher',
  'mobile.messages.emptySearchHint': 'Prøv et annet navn.',
  'mobile.messages.emptyUnread': 'Ingenting ulest',
  'mobile.messages.emptyUnreadHint': 'Du er à jour.',
  'mobile.messages.emptyGroups': 'Ingen grupper ennå',
  'mobile.messages.emptyAll': 'Ingen meldinger ennå',
  'mobile.messages.emptyAllHint':
    'Send en melding til en medspiller, lag en gruppe, eller skriv til klubben.',
  'mobile.messages.muted': 'Dempet',
  'mobile.messages.autoGroupPreview': '{n} medlemmer · automatisk gruppe',
  'mobile.messages.unreadAria': '{count} uleste',
  'mobile.messages.turnOffForMe': 'Slå av meldinger for meg',
  'mobile.messages.turnOff': 'Slå av meldinger',
  'mobile.messages.turnOffConfirmTitle': 'Slå av meldinger?',
  'mobile.messages.turnOffConfirmLead':
    'Du vil ikke sende eller motta meldinger, du vises ikke når andre søker etter medlemmer, og du tas ut av gruppene dine. Du kan slå på igjen fra samme sted.',

  // ── «Ny melding» (the recipient picker) ───────────────────────────
  'mobile.newMessage.title': 'Ny melding',
  'mobile.newMessage.lead': 'Velg én for en samtale, flere for en gruppe.',
  'mobile.newMessage.toClub': 'Skriv til klubben',
  'mobile.newMessage.search': 'Søk medlem',
  'mobile.newMessage.noMembers': 'Ingen medlemmer matcher.',
  'mobile.newMessage.groupNameOptional': 'Gruppenavn (valgfritt)',
  'mobile.newMessage.groupName': 'Gruppenavn',
  'mobile.newMessage.writeTo': 'Skriv til {name}',
  'mobile.newMessage.theMember': 'medlemmet',
  'mobile.newMessage.startGroup': 'Start gruppe med {count} medlemmer',

  // ── one conversation ──────────────────────────────────────────────
  'mobile.chat.yourClub': 'Klubben din',
  'mobile.chat.memberCount': '{n} medlem',
  'mobile.chat.memberCountMany': '{n} medlemmer',
  'mobile.chat.autoGroupLevel': 'Automatisk gruppe · {members} på nivå {level}',
  'mobile.chat.autoGroupAll': 'Automatisk gruppe · alle {members}',
  'mobile.chat.groupSubtitle': '{n} medlemmer · {names}',
  'mobile.chat.unknown': 'Ukjent',
  'mobile.chat.groupSettings': 'Gruppeinnstillinger',
  'mobile.chat.autoGroupNote':
    'Automatisk gruppe — medlemmene holdes oppdatert etter faktisk nivå.',
  'mobile.chat.mute': 'Demp gruppen',
  'mobile.chat.unmute': 'Slå på varsler',
  'mobile.chat.muteHint': 'Gruppen blir liggende, men teller ikke som ulest.',
  'mobile.chat.unmuteHint': 'Uleste meldinger teller igjen i merket på fanen.',
  'mobile.chat.leaveAuto': 'Meld deg ut av de automatiske gruppene',
  'mobile.chat.leaveAutoHint':
    'Gjelder både nivågruppen og «Alle» i {club}. Du kan bli med igjen fra innboksen.',
  'mobile.chat.theClub': 'klubben',
  'mobile.chat.emptyClub': 'Her kommer meldinger fra klubben. Du kan også skrive til dem.',
  'mobile.chat.emptyDm': 'Ingen meldinger ennå — si hei!',
  'mobile.chat.toEveryone': ' · til alle',
  'mobile.chat.blocked': '{name} har slått av meldinger. Samtalen er lukket til det slås på igjen.',
  'mobile.chat.theMember': 'Medlemmet',
  'mobile.chat.emoji': 'Emoji',
  'mobile.chat.composerPlaceholder': 'Skriv en melding …',
  'mobile.chat.composerAria': 'Skriv en melding',
  'mobile.chat.send': 'Send',

  // ══ the home surface (#310a) ══════════════════════════════════════
  //
  // The operator's landing screen and everything reached straight from it:
  // the saved-setup drafts, the running and paused tournament cards, the
  // mini-tournament picker and the activities module. Keys are namespaced by
  // screen (`home.drafts.*`), with `home.common.*` for the handful of words
  // that genuinely recur. Activities are big enough to own a top-level
  // namespace (`activities.*`) rather than hide inside `home.*`.
  //
  // The Norwegian is verbatim what the markup said before the move — the
  // rendered Norwegian does not change.
  // ── shared across the home screens ────────────────────────────────
  'home.common.cancel': 'Avbryt',
  'home.common.delete': 'Slett',
  // «hvor lenge siden», written the same way by DraftsCard and
  // PausedTournamentsCard — one ladder, one set of keys.
  'home.common.ago.justNow': 'nettopp',
  'home.common.ago.minutes': '{min} min siden',
  'home.common.ago.hour': '{count} time siden',
  'home.common.ago.hours': '{count} timer siden',
  'home.common.ago.day': '{count} dag siden',
  'home.common.ago.days': '{count} dager siden',
  // The short weekday and month names the home screen builds its own date
  // strings from. DISPLAY ONLY — nothing here is ever stored or compared
  // (DOMAIN_RULES §4); the ISO keys beside them stay computed from local
  // date parts.
  'home.weekday.short.sun': 'søn',
  'home.weekday.short.mon': 'man',
  'home.weekday.short.tue': 'tir',
  'home.weekday.short.wed': 'ons',
  'home.weekday.short.thu': 'tor',
  'home.weekday.short.fri': 'fre',
  'home.weekday.short.sat': 'lør',
  'home.month.short.jan': 'jan',
  'home.month.short.feb': 'feb',
  'home.month.short.mar': 'mar',
  'home.month.short.apr': 'apr',
  'home.month.short.may': 'mai',
  'home.month.short.jun': 'jun',
  'home.month.short.jul': 'jul',
  'home.month.short.aug': 'aug',
  'home.month.short.sep': 'sep',
  'home.month.short.oct': 'okt',
  'home.month.short.nov': 'nov',
  'home.month.short.dec': 'des',
  'home.weekday.long.sun': 'søndag',
  'home.weekday.long.mon': 'mandag',
  'home.weekday.long.tue': 'tirsdag',
  'home.weekday.long.wed': 'onsdag',
  'home.weekday.long.thu': 'torsdag',
  'home.weekday.long.fri': 'fredag',
  'home.weekday.long.sat': 'lørdag',
  'home.month.long.jan': 'januar',
  'home.month.long.feb': 'februar',
  'home.month.long.mar': 'mars',
  'home.month.long.apr': 'april',
  'home.month.long.may': 'mai',
  'home.month.long.jun': 'juni',
  'home.month.long.jul': 'juli',
  'home.month.long.aug': 'august',
  'home.month.long.sep': 'september',
  'home.month.long.oct': 'oktober',
  'home.month.long.nov': 'november',
  'home.month.long.dec': 'desember',

  // ── saved setups (DraftsCard) ─────────────────────────────────────
  'home.drafts.headingOne': 'Lagret oppsett',
  'home.drafts.headingMany': '{count} lagrede oppsett',
  'home.drafts.hint': 'Klikk for å starte ny turnering basert på et lagret oppsett',
  'home.drafts.deleteTitle': 'Slette "{name}"?',
  'home.drafts.deleteLead': 'Dette lagrede oppsettet vil bli slettet. Dette kan ikke angres.',
  'home.drafts.deleteAria': 'Slett dette lagrede oppsettet',
  'home.drafts.playerOne': ' · {count} spiller',
  'home.drafts.playerMany': ' · {count} spillere',
  'home.drafts.groupsReady': ' · grupper klar',

  // ── running and paused tournaments (PausedTournamentsCard) ────────
  'home.paused.untitled': 'Turnering',
  'home.paused.players': '{count} spillere',
  'home.paused.matchesPlayed': '{done}/{total} kamper spilt',
  'home.paused.notStarted': 'ikke startet',
  'home.paused.groups': '{count} grupper',
  'home.paused.round': 'Runde {round}',
  'home.paused.placementMatches': 'Plasseringskamper',
  'home.paused.inProgress': 'Pågår',
  'home.paused.pausedSince': 'Pauset {since}',
  'home.paused.reopenTitle': 'Åpne denne turneringen igjen',
  'home.paused.resumeTitle': 'Gjenoppta denne turneringen',
  'home.paused.open': 'Åpne',
  'home.paused.resume': 'Gjenoppta',
  'home.paused.pause': 'Pause',
  'home.paused.pauseTitle':
    'Sett denne turneringen på pause — flyttes til «Pausede» og kan gjenopptas senere',
  'home.paused.archiveTitleAttr':
    'Lagre turneringen i arkivet og lukk den. Kan gjenåpnes fra arkivet med «Fortsett turneringen».',
  'home.paused.saveAndClose': 'Lagre og avslutt',
  'home.paused.endAndDeleteTitle': 'Avslutt og slett denne turneringen',
  'home.paused.deletePausedTitle': 'Slett pauset turnering',
  'home.paused.runningOne': 'Pågående turnering',
  'home.paused.runningMany': '{count} pågående turneringer',
  'home.paused.runningHint': 'Klikk «Åpne» for å fortsette — flere kan være i gang samtidig',
  'home.paused.openNow': 'Åpen nå',
  // The status BADGE. The stored status stays a code — `'done'`, never
  // «Ferdig» (DOMAIN_RULES §1); only the label moves.
  'home.paused.status.incomplete': 'Ufullstendig',
  'home.paused.status.ready': 'Klar til start',
  'home.paused.status.running': 'Pågår',
  'home.paused.status.paused': 'Pauset',
  'home.paused.status.finals': 'Finaler',
  'home.paused.status.done': 'Ferdig',
  'home.paused.status.active': 'Aktiv',
  'home.paused.collapseTitle': 'Skjul pausede turneringer',
  'home.paused.expandTitle': 'Vis pausede turneringer',
  'home.paused.pausedOne': 'Pauset turnering',
  'home.paused.pausedMany': '{count} pausede turneringer',
  'home.paused.resumeAnytime': 'Kan gjenopptas når som helst',
  'home.paused.tapToShow': 'Trykk for å vise',
  'home.paused.deleteAllTitleAttr': 'Slett alle parkerte turneringer',
  'home.paused.deleteAll': 'Slett alle',
  'home.paused.deleteConfirmTitle': 'Slett pauset turnering?',
  // Split at the <strong>, so the tournament name stays bold.
  'home.paused.deleteLeadPre': 'Vil du slette',
  'home.paused.deleteLeadPost': '? All data går tapt.',
  'home.paused.theTournament': 'turneringen',
  'home.paused.aTournament': 'turnering',
  'home.paused.archiveConfirmTitle': 'Lagre og avslutt «{name}»?',
  'home.paused.archiveLeadPre': 'Denne turneringen er',
  'home.paused.archiveUnfinished': 'ikke ferdigspilt',
  'home.paused.archiveLeadMid': '. Den blir lagret og flyttet til',
  'home.paused.archiveWord': 'arkivet',
  'home.paused.archiveLeadPost': ', og forsvinner fra forsiden.',
  'home.paused.archiveNote':
    'Ingen data går tapt. Enkelte turneringstyper kan gjenopptas fra arkivet ved en senere anledning.',
  'home.paused.archiveConfirm': 'Lagre og arkiver',
  'home.paused.deleteAllConfirmTitle': 'Slett alle pausede turneringer?',
  'home.paused.deleteAllLeadPre': 'Dette sletter',
  'home.paused.deleteAllCount': 'alle {count} pausede turneringer',
  'home.paused.deleteAllLeadPost': 'permanent. All data går tapt og kan ikke gjenopprettes.',
  'home.paused.deleteAllWarning':
    'Dette er en uopprettelig handling. Trykk «Slett alle» for å bekrefte.',
  'home.paused.deleteAllCta': 'Slett alle {count}',

  // ── the landing screen itself (HomeView) ──────────────────────────
  'home.view.leadWithClub':
    'Du er logget inn hos {club}. Her styrer du booking og turneringer fra ett sted.',
  'home.view.leadNoClub':
    'Velkommen til {brand} — plattformen for baner, booking og turneringer. Sett opp klubbinformasjon ved å klikke på klubb-knappen øverst til høyre, eller start en turnering med en gang.',
  'home.view.deleteUnsavedTitle': 'Slette uten å lagre?',
  'home.view.deleteUnsavedPre': 'Alle data for',
  'home.view.deleteUnsavedPost': 'vil bli slettet permanent. Dette kan ikke angres.',
  'home.view.theTournament': 'turneringen',
  'home.view.deletePermanently': 'Slett permanent',
  'home.view.activeSeries': 'Aktiv serie',
  'home.view.seriesFallback': 'Seriespill',
  'home.view.seriesClassDiv': '{name} · {count} div',
  'home.view.seriesMatches': '{done}/{total} kamper',
  // The deadline is a STORED date, passed straight through — not formatted.
  'home.view.seriesDeadline': 'Frist {date}',
  'home.view.tournamentsEyebrow': 'Turneringer',
  'home.view.createTournament': 'Opprett turnering',
  'home.view.tournamentsLead':
    'Trykk hvor som helst for å se alle — eller velg et format ved siden av.',
  'home.view.seeAllFormats': 'Se alle formater',
  'home.view.pickFormat': 'Velg format',
  'home.view.miniTournaments': 'Miniturneringer',
  'home.view.bookingEyebrow': 'Banebooking',
  'home.view.reserveCourt': 'Reservér bane',
  'home.view.bookingLead': 'Trykk hvor som helst for å åpne — eller velg en dag ved siden av.',
  // The leading space is deliberate: this is appended to the sentence above.
  'home.view.bookingsActiveOne': ' {count} aktive booking.',
  'home.view.bookingsActiveMany': ' {count} aktive bookinger.',
  'home.view.open': 'Åpne',
  'home.view.availability7': 'Ledighet 7 dager',
  'home.view.dayDotsTitle': '{day} — tidlig · midt på dagen · kveld',
  'home.view.today': 'I dag',
  'home.view.activitiesEyebrow': 'Aktiviteter',
  'home.view.activitiesOpenCount': '{count} åpen',
  'home.view.signUp': 'Meld deg på',
  'home.view.activitiesLead':
    'Trykk hvor som helst for å åpne — eller velg en aktivitet ved siden av.',
  'home.view.nearestActivities': 'Nærmeste aktiviteter',
  'home.view.noUpcoming': 'Ingen kommende ennå',
  'home.view.activityFallback': 'Aktivitet',
  'home.view.dateNotSet': 'Dato ikke satt',
  'home.view.whenDate': '{wd} {day}. {mo}',
  'home.view.whenTime': ' kl {hh}:{mm}',
  'home.view.banepadEyebrow': 'Banepad',
  'home.view.screensAtCourts': 'Skjermer ved banene',
  'home.view.banepadLead': 'Se hva som skjer på alle baner live — eller åpne paden for én bane.',
  'home.view.openCentral': 'Åpne sentral',
  'home.view.courtsNow': 'Baner nå',
  'home.view.court': 'Bane {court}',
  'home.view.live': 'Live',
  'home.view.next': 'Neste',
  'home.view.booked': 'Booket',
  'home.view.courtAvailable': 'Ledig',
  'home.view.databaseEyebrow': 'Database',
  'home.view.playerDatabase': 'Spillerdatabase',
  'home.view.playersCount': '{count} spillere',
  'home.view.playersFallback': 'Se og rediger spillere',
  'home.view.archiveEyebrow': 'Arkiv',
  'home.view.tournamentArchive': 'Turneringsarkiv',
  'home.view.archiveSummaryOne': '{count} turnering · {matches} kamper',
  'home.view.archiveSummaryMany': '{count} turneringer · {matches} kamper',
  'home.view.loading': 'Laster…',
  'home.view.howItWorks': 'Slik fungerer det',
  'home.view.step1': 'Sett opp turnering (navn, datoer, tider, baner)',
  'home.view.step2': 'Velg spillere fra databasen',
  'home.view.step3': 'Systemet foreslår grupper basert på ELO',
  'home.view.step4': 'Generer kampoppsett automatisk',
  'home.view.step5': 'Start og kjør turneringen',
  'home.view.features': 'Funksjoner',
  'home.view.feature1': 'Auto-genererte kamper og baneoppsett',
  'home.view.feature2': 'Live registrering av resultater',
  'home.view.feature3': 'Opprykk/nedrykk etter hver fase',
  'home.view.feature4': 'Finaler og plasseringskamper for alle',
  'home.view.feature5': 'Sluttrangering med ELO-oppdatering',
  'home.view.whatIsElo': 'Hva er ELO?',
  'home.view.eloExplainerPre':
    'ELO er et rangeringssystem som tallfester spillernivå. Vinner du mot en bedre spiller, får du flere poeng — taper du mot en svakere, mister du flere. Etter hver kamp justeres begge spilleres ELO automatisk. Når to spillere danner et lag, brukes',
  'home.view.eloAverage': 'snittet av deres ELO',
  'home.view.eloExplainerPost':
    'som lagets styrkemål — slik at to middels spillere kan møte en sterk + en svak.',
  'home.view.eloLevel': 'NIVÅ {level}',
  'home.view.eloScale':
    'Padel24 bruker en 9-nivå skala fra 500 (helt nybegynner) til 3000+ (toppnivå i klubben). ELO oppdateres automatisk etter hver kamp og brukes til gruppeinndeling og lagsammensetning.',
  'home.view.deleteTournamentTitle': 'Slette turnering?',
  'home.view.deleteArchivedPre': 'Er du sikker på at du vil slette',
  'home.view.deleteArchivedPost':
    '? Alle data vil bli slettet permanent og kan ikke gjenopprettes.',
  'home.view.thisTournament': 'denne turneringen',
  'home.view.yesDeletePermanently': 'Ja, slett permanent',
  'home.view.recentTournaments': 'Siste turneringer',
  'home.view.seeAll': 'Se alle ({count})',
  'home.view.unknownDate': 'Ukjent dato',
  'home.view.done': 'Ferdig',
  'home.view.tournamentFallback': 'Turnering',
  'home.view.matchesRegistered': '{count} kamper registrert',
  'home.view.playersAndMatches': '{players} spillere · {matches} kamper',
  'home.view.deleteTournamentAria': 'Slett turnering',

  // ── the mini-tournament format picker (MiniTournamentsView) ───────
  //
  // FORMAT NAMES ARE NOT KEYED. «Americano», «Mexicano», «Club Team
  // Americano» and the rest are product names (I18N_GLOSSARY.md) and stay
  // literal in the component — only what DESCRIBES them lives here.
  'home.mini.back': 'Tilbake',
  'home.mini.title': 'Miniturneringer',
  'home.mini.lead':
    'Korte rotasjons-formater spilt på én økt. Felles for alle: minimal admin, 1-3 timer per gjennomføring.',
  'home.mini.eloWeightPre': 'ELO-vekt er',
  'home.mini.eloWeightValue': '50 %',
  'home.mini.eloWeightPost':
    'av Padel24 og enkeltkamper, siden disse kampene typisk er kortere og dermed mindre representative.',
  'home.mini.available': 'Tilgjengelig',
  'home.mini.comingSoon': 'Kommer snart',
  'home.mini.hideDetails': 'Skjul detaljer',
  'home.mini.showDetails': 'Vis detaljer',
  'home.mini.less': 'Mindre',
  'home.mini.moreInfo': 'Mer info',
  'home.mini.filterByProperty': 'Filtrer på egenskap',
  'home.mini.hideAllInfoTitle': 'Skjul info på alle',
  'home.mini.showAllInfoTitle': 'Vis info på alle',
  'home.mini.hideAllInfo': 'Skjul all info',
  'home.mini.showAllInfo': 'Vis all info',
  'home.mini.reset': 'Nullstill ({count})',
  'home.mini.noMatchCombo': 'Ingen formater matcher den kombinasjonen. Prøv å fjerne et filter.',
  'home.mini.showingCount': 'Viser {shown} av {total} formater',
  'home.mini.noMatchFilters': 'Ingen formater matcher de aktive filtrene',
  'home.mini.otherFormats': 'Andre formater',
  // The icon legend — one entry, read by both the card chip and the filter
  // chip, which carried the same sentence twice in the markup.
  'home.mini.icon.team': 'Fast makker (par bevares hele turneringen)',
  'home.mini.icon.random': 'Balansert rotasjon — alle møter alle over tid',
  'home.mini.icon.skill': 'Nivåbestemt rotasjon — du møter spillere rundt din egen plassering',
  'home.mini.icon.mix': 'Kjønnsblanding (dame + mann i hvert lag)',
  'home.mini.icon.club': 'Mellom to padel-klubber',
  'home.mini.filter.random': 'Balansert rotasjon',
  'home.mini.filter.skill': 'Nivåbestemt',
  'home.mini.filter.team': 'Fast makker',
  'home.mini.filter.mix': 'Kjønnsblanding',
  'home.mini.filter.club': 'Klubb mot klubb',
  // Feature lines several formats share verbatim.
  'home.mini.shared.oneWomanOneMan': 'Hvert lag = 1 dame + 1 mann',
  'home.mini.shared.genderOverride': 'Midlertidig kjønns-override ved ujevn balanse',
  'home.mini.shared.fixedPairs': 'Faste makkerpar gjennom hele turneringen',
  'home.mini.shared.twoVsTwo': '2 fra klubb A vs 2 fra klubb B på hver bane',
  'home.mini.shared.clubLogoName': 'Klubblogo + navn på begge klubber',
  'home.mini.shared.clubScoreIndividual':
    'Samlet klubb-score øverst, individuell stilling som vanlig',
  'home.mini.americano.tagline': 'Alle mot alle, roterende makkere',
  'home.mini.americano.desc':
    'Klassisk americano: spillerne deles inn på baner og kombineres slik at alle spiller med alle minst én gang. Etter hver runde rulleres lag og motstandere. Poengsum eller seire teller for sluttranking.',
  'home.mini.americano.f1': '4 spillere per bane, roterende makkere',
  'home.mini.americano.f2': 'Velg mellom Totalsum, Race, Bingo eller Tid',
  'home.mini.americano.f3': 'Konfigurerbar finale (Balansert eller Topp mot bunn)',
  'home.mini.americano.f4': 'Valgfri sluttspillranking eller totalstanding',
  'home.mini.mixAmericano.tagline': 'Som Americano, krav om kjønnsblanding',
  'home.mini.mixAmericano.desc':
    'Samme rotasjon som Americano, men hvert lag består av én dame og én mann. Krever balansert kjønnsfordeling — algoritmen håndterer ubalanse via midlertidig kjønns-override.',
  'home.mini.mixAmericano.f2': 'Optimal anti-rep matchmaking (samme som Americano)',
  'home.mini.mixAmericano.f4': 'Spill på poeng, games, tid eller sett',
  'home.mini.mexicano.tagline': 'Jevnere kamper basert på stilling',
  'home.mini.mexicano.desc':
    'Som Americano, men neste runde genereres ut fra stillingen — du møter spillere rundt din egen plassering. Gir tettere kamper utover i turneringen. Random-runde kan brytes inn for å unngå spiller-loop.',
  'home.mini.mexicano.f1': '4 spillere per bane, makkere bestemt av stilling',
  'home.mini.mexicano.f2': 'Pool-størrelse 4/6/8 for kontroll over variasjon',
  'home.mini.mexicano.f3': 'Tilfeldige første runder og hver Nte runde (valgfritt)',
  'home.mini.mexicano.f4': '"Tilfeldig runde nå"-knapp for å bryte spiller-loop',
  'home.mini.mixicano.tagline': 'Mexicano med kjønnsblanding',
  'home.mini.mixicano.desc':
    'Som Mexicano, men hvert lag har én dame og én mann. Krever balansert kjønnsfordeling — algoritmen håndterer ubalanse via midlertidig kjønns-override.',
  'home.mini.mixicano.f2': 'Pool-størrelse 4/6/8 som Mexicano',
  'home.mini.mixicano.f4': '"Tilfeldig runde nå" + samme rangering som Mexicano',
  'home.mini.teamAmericano.tagline': 'Faste lag, alle lag møter alle',
  'home.mini.teamAmericano.desc':
    'Spillerne danner faste makkerpar. Hvert lag møter alle andre lag minst én gang. Resultat per lag summeres.',
  'home.mini.teamAmericano.f2': 'Hvert lag møter alle andre lag',
  'home.mini.teamAmericano.f3': 'Standings beregnes per lag (ikke per spiller)',
  'home.mini.teamAmericano.f4': 'Finale-oppgjør: 1.lag vs 2.lag (gull), 3. vs 4. (bronse), osv.',
  'home.mini.teamMexicano.tagline': 'Faste lag, jevnere kamper',
  'home.mini.teamMexicano.desc':
    'Faste makkerpar som i Team Americano, men neste runde genereres ut fra lag-rangering — sterke lag møter sterke lag. Gir jevnere kamper jo lengre i turneringen man kommer.',
  'home.mini.teamMexicano.f2': 'Lag-rangering driver matchmaking (sterke lag møter sterke)',
  'home.mini.teamMexicano.f3': 'Pool 4/6/8 — samme som Mexicano, men på lag-nivå',
  'home.mini.teamMexicano.f4': 'Spill på poeng, games eller tid',
  'home.mini.clubAmericano.tagline': 'Klubb mot klubb, roterende makkere',
  'home.mini.clubAmericano.desc':
    'Halvparten av spillerne representerer klubb A, andre halvparten klubb B. Hver bane er alltid 2 fra klubb A mot 2 fra klubb B. Makkere innen klubben rulleres, og klubbene konkurrerer om samlet score.',
  'home.mini.clubAmericano.f4': 'Drag-drop for å flytte spillere mellom klubber',
  'home.mini.clubTeamAmericano.tagline': 'Klubb mot klubb, faste makkere',
  'home.mini.clubTeamAmericano.desc':
    'Lag fra to klubber møter hverandre. Hvert lag består av faste makkere. Round-robin: hver A-lag møter alle B-lag minst én gang. Klubbene konkurrerer om samlet score.',
  'home.mini.clubTeamAmericano.f1': 'Faste makkerpar fra hver klubb',
  'home.mini.clubTeamAmericano.f2': 'Round-robin matching: hver A-lag møter alle B-lag',
  'home.mini.clubTeamAmericano.f4': 'Samlet klubb-score øverst, lag-stilling som lederboard',
  'home.mini.clubMexicano.tagline': 'To klubber, jevnere kamper',
  'home.mini.clubMexicano.desc':
    'Som Club Americano, men matchingen følger Mexicano-regler: best fra klubb A møter best fra klubb B, nest best møter nest best, osv. Gir mer kompetitive kamper utover i turneringen.',
  'home.mini.clubMexicano.f2': 'Best fra klubb A møter best fra klubb B',
  'home.mini.clubMexicano.f3': 'Pool-størrelse styrer makker-variasjon innen klubben',

  // ══ Aktiviteter (ActivitiesView) ══════════════════════════════════
  //
  // The club's sign-up hub — the list, the create pickers and the whole
  // per-activity detail/sign-up flow for every format. It is its own
  // top-level namespace rather than `home.activities.*` because it is a
  // surface in its own right, reached from several places.
  //
  // VOCABULARY FOLLOWS THE MEMBER APP (`mobile.activities.*`): påmelding is
  // a SIGN-UP, kamp is a MATCH, nivå is a LEVEL, a free court is AVAILABLE
  // and `gratis` is free. Where the same thing is said on both surfaces, it
  // is said the same way.
  //
  // STATUS CODES AND DATES ARE NOT COPY. `'confirmed'`, `'waitlist'`,
  // `'standby'`, `'done'` and every `YYYY-MM-DD` stay exactly as they were:
  // only the labels drawn from them live here (DOMAIN_RULES §1 and §4).
  'activities.when.notSet': 'Tid ikke satt',
  'activities.when.days': '{count} dager · fra {from}',
  'activities.unknownPlayer': 'Ukjent spiller',
  'activities.level.exact': 'Nivå {level} – {name}',
  'activities.level.range': 'Nivå {min}–{max}',
  'activities.levelBadge': 'Nivå {level} — {name}',
  'activities.km.rule.women': 'to damer',
  'activities.km.rule.mix': 'én dame + én herre',
  'activities.km.rule.men': 'åpen — damer kan spille opp',
  'activities.swapThisPlayer': 'Bytt ut denne spilleren',
  'activities.woman': 'Dame',
  'activities.man': 'Herre',
  'activities.overQuotaTitle': 'Påmeldt utover kvoten',
  'activities.extra': 'ekstra',
  'activities.paid': 'Betalt',
  'activities.refunded': 'Refundert',
  'activities.manual': 'Manuell',
  'activities.pendingPayment': 'Venter',
  'activities.coveredByBooker': 'Dekket — gjør opp med {booker}',
  'activities.bookerFallback': 'booker',
  'activities.remove': 'Fjern',
  'activities.day': 'Dag',
  'activities.searchName': 'Søk på navn…',
  'activities.noMatches': 'Ingen treff.',
  'activities.noAvailablePlayers': 'Ingen tilgjengelige spillere.',
  'activities.createPlayer': 'Opprett «{name}»',
  'activities.clubPlayersCount': '{confirmed}/{max} spillere',
  // Dates the screen assembles itself. Display only — see the note above.
  'activities.date.dayMonth': '{wd} {day}. {mo}',
  'activities.date.dayMonthYear': '{wd} {day}. {mo} {year}',
  'activities.date.dayNumMonth': '{wd} {day}.{month}',
  'activities.date.closeAt': '{wd} {day}.{month} kl {hh}:{mm}',
  'activities.date.dayMonthAt': '{wd} {day}. {mo} kl {hh}:{mm}',

  // ── the shell around a single activity ────────────────────────────
  'activities.allActivities': 'Alle aktiviteter',
  'activities.adminOn': '🔓 Admin på',
  'activities.adminOff': '🔒 Admin av',
  'activities.toggleAdminTitle': 'Bytt mellom brukervisning og admin',
  'activities.full': 'FULLT',
  'activities.freeSlots': 'LEDIGE PLASSER',
  'activities.venueFallback': 'Padelsenteret',
  'activities.courtsReserved': 'Baner reserveres for denne aktiviteten',
  'activities.reserveCourts': 'Reserver baner',
  'activities.signedUpCount': 'Påmeldte ({count}{max})',
  'activities.waitlistCount': 'Venteliste ({count}{max})',
  'activities.noSignupsYet': 'Ingen påmeldte ennå.',
  'activities.signMeOff': 'Meld meg av',
  'activities.signMeUp': 'Meld meg på',
  'activities.joinWaitlist': 'Sett på venteliste',
  'activities.addParticipant': 'Legg til deltaker',
  'activities.searchPlayer': 'Søk spiller …',
  'activities.previewNeeds4': 'Minst 4 spillere for å forhåndsvise turneringen',
  'activities.previewTournament': 'Forhåndsvis turneringen',
  'activities.preview': 'Forhåndsvis',
  'activities.starting': 'Starter…',

  // ── admin: which courts to release when locking ───────────────────
  'activities.release.pick':
    'Velg hvilke baner som frigjøres ({chosen} valgt — du trenger {needed} til {players} spillere):',
  'activities.release.court': 'Bane {court} · {state}',
  'activities.release.released': '✕ frigjøres',
  'activities.release.kept': '✓ beholdes',
  'activities.release.keepMinOne':
    'Du må beholde minst {needed} bane til de {players} påmeldte. Velg færre å frigjøre.',
  'activities.release.keepMinMany':
    'Du må beholde minst {needed} baner til de {players} påmeldte. Velg færre å frigjøre.',
  'activities.release.keepingMore': 'Du beholder {kept} baner — flere enn de {needed} som trengs.',

  // ── seriespill season sign-up ─────────────────────────────────────
  'activities.signupOpenBadge': 'PÅMELDING ÅPEN',
  'activities.seasonFallbackName': 'Seriespill — sesong',
  'activities.seasonStart': 'Sesongstart',
  'activities.signupDeadlineSuffix': ' · påmeldingsfrist {date}',
  'activities.noSeasonFee': 'Gratis — ingen sesongavgift',
  'activities.seasonFee': 'Sesongavgift: {amount} kr per lag',
  'activities.seasonLeadPre': 'Påmelding til seriespillets neste sesong. Påmelding skjer',
  'activities.seasonLeadBold': 'lagvis',
  'activities.seasonLeadPost': '(par), og sesongavgiften betales per lag før sesongen starter.',
  'activities.signedInAs': 'Innlogget som',
  'activities.noProfile': 'ingen profil',
  'activities.youAreSignedUpWith': 'Du er påmeldt med',
  'activities.seasonFeePaid': 'Sesongavgift betalt',
  'activities.notPaid': 'Ikke betalt',
  'activities.payAmount': 'Betal {amount} kr',
  'activities.withdrawTeam': 'Meld laget av',
  'activities.pickPartner': 'Velg makker',
  'activities.signUpYourTeam': 'Meld på laget ditt',
  'activities.priceSuffix': ' · {amount} kr',
  'activities.addTeamAdmin': 'Legg til lag (admin)',
  'activities.partnerFor': 'Makker for',
  'activities.pickP1ThenPartner':
    'Velg spiller 1, deretter makker. Laget legges til som påmeldt (ikke betalt).',
  'activities.signedUpTeams': 'Påmeldte lag ({count})',
  'activities.noTeamsYet': 'Ingen lag påmeldt ennå.',
  'activities.undo': 'angre',
  'activities.markPaid': 'marker betalt',
  'activities.removeLower': 'fjern',
  'activities.newTeamsNote':
    'Nye lag som melder seg på her må legges inn i serien av admin (i seriespillet) før neste sesong. Eksisterende lag forvaltes i seriespillets «Sesongpåmelding»-fane.',
  'activities.paidSeasonFee': 'Har betalt sesongavgiften ({count})',
  'activities.nonePaidForSeason': 'Ingen lag har betalt for sesong {season} ennå.',
  'activities.activeUnpaid': 'Aktive lag som ikke har betalt ({count})',
  'activities.activeUnpaidLead':
    'Lag som spiller inneværende sesong, men ikke har betalt sesongavgiften for sesong {season} ennå.',
  'activities.allActiveTeamsPaid': 'Alle aktive lag har betalt.',

  // ── the activity detail page ──────────────────────────────────────
  'activities.activityFallback': 'Aktivitet',
  'activities.allLevels': 'Alle nivåer',
  'activities.free': 'Gratis',
  'activities.badge.cancelled': 'KANSELLERT',
  'activities.badge.locked': 'LÅST',
  'activities.badge.closed': 'STENGT',
  'activities.badge.closedAuto': 'STENGT (AUTO)',
  'activities.badge.waitlistOpen': 'VENTELISTE ÅPEN',
  'activities.badge.open': 'ÅPEN',
  'activities.partOfSeries': 'Del av serien',
  'activities.seriesNameSuffix': ' «{name}»',
  'activities.occurrenceOf': ' — gang {index} av {total}',
  'activities.occurrenceRolling': ' — gang {index} (fortløpende)',
  'activities.when.notSetYet': 'Tid ikke satt ennå',
  'activities.courtSuffix': ' · bane {courts}',
  'activities.pricePerTeam': '{amount} kr/lag',
  'activities.pricePerTeamRange': '{min}–{max} kr/lag',
  'activities.pricePerPlayer': '{amount} kr/spiller',
  'activities.deregisterCutoff': '· avmelding senest {hours} t før',
  'activities.signupAutoCloses': 'Påmelding stenger automatisk {at} ({when}).',
  'activities.hoursBeforeStart': '{hours} t før start',
  'activities.atStart': 'ved start',
  'activities.signupClosesAt': 'Påmelding stenger {at}.',
  'activities.signupClosedDeadlinePassed': 'Påmelding er stengt (frist passert{at}).',
  'activities.signupClosedHow': 'Påmelding er stengt{how}.',
  'activities.closedAutoHours': ' (automatisk, {hours} t før start)',
  'activities.closedAutoAtStart': ' (automatisk, ved start)',
  'activities.closedManually': ' manuelt',
  'activities.mixCounts': '{men}/{menMax} menn · {women}/{womenMax} damer',
  'activities.signedUpLower': 'påmeldt',
  'activities.waitlistSuffix': ' · {count} venteliste',
  'activities.aboutTournament': 'Om turneringen',

  // ── deadline, minimum and the go/no-go decision ───────────────────
  'activities.cancelledTooFew': '✕ Kansellert — for få spillere',
  'activities.cancelledLead':
    'Det ble {count} påmeldt{min}. Banene er frigjort, og betalte spillere er markert refundert.',
  'activities.butMinRequired': ', men minst {min} kreves',
  'activities.reopenSignup': 'Gjenåpne påmelding',
  'activities.confirmedGoesAhead': '✓ Bekreftet — turneringen blir noe av',
  'activities.confirmedLead': '{count} påmeldte. Påmeldingen er stengt.',
  'activities.adjustCourtsHint': ' Baner justerer du under «Baner & plasser».',
  'activities.adjustCourtsAnytimeHint': ' Baner justerer du når som helst under «Baner & plasser».',
  'activities.deadlineAndMinimum': 'Påmeldingsfrist & minimum',
  'activities.minimumMarker': 'Minimum {count}',
  'activities.deadlinePassed': 'FRIST PASSERT',
  'activities.tier.full': '✓ fullt',
  'activities.tier.enough': '✓ nok spillere',
  'activities.tier.enoughCanFill': '✓ nok — kan fylles til {max}',
  'activities.tier.missing': 'mangler {count} til minimum',
  'activities.progressCount': '{count}{of} påmeldte{min}',
  'activities.ofMax': ' av {max}',
  'activities.minSuffix': ' · min {min}',
  'activities.closedEnough':
    'Påmeldingen er stengt med nok påmeldte ({count}{min}). Turneringen blir noe av.',
  'activities.closedTooFew':
    'Påmeldingen er stengt med for få påmeldte ({count}{min}). Aktiviteten kan kanselleres og alle banene frigjøres.',
  'activities.confirmGoesAhead': 'Bekreft — turneringen blir noe av',
  'activities.cancelAndRelease': 'Kanseller & frigjør alle baner',
  'activities.awaitingClubDecision': 'Venter på at klubben bekrefter utfallet.',
  'activities.atTheDeadline': 'Ved fristen',
  'activities.whenSignupCloses': 'Når påmeldingen stenger',
  'activities.autoDecidePre': '{when} ({deadline}) avgjøres det automatisk: minst',
  'activities.autoDecidePost':
    'påmeldte → turneringen blir noe av; færre → den avlyses, alle banene frigjøres og betalte refunderes.',

  // ── courts & slots (admin) ────────────────────────────────────────
  'activities.courtsAndSlots': 'Baner & plasser',
  'activities.courtsBookedOne': '{count} bane booket',
  'activities.courtsBookedMany': '{count} baner booket',
  'activities.courtListSuffix': ' (bane {courts})',
  'activities.slotsCount': '{count} plasser',
  'activities.capacityFollowsCourts': 'kapasitet følger banene',
  'activities.releaseOneCourt': '− Frigjør en bane',
  'activities.openOneMoreCourt': '+ Åpne en bane til',
  'activities.courtButtonsHelp':
    '«Åpne en bane til» reserverer en ledig bane på aktivitetens tid, gir 4 nye plasser og åpner påmeldingen igjen — også om aktiviteten er låst. «Frigjør en bane» fjerner den øverste banen og senker kapasiteten.',
  'activities.waitlistEnoughForOneCourt':
    '{count} står på venteliste — nok til {courts} ekstra bane. «Åpne en bane til» slipper inn 4 om gangen.',
  'activities.waitlistEnoughForCourts':
    '{count} står på venteliste — nok til {courts} ekstra baner. «Åpne en bane til» slipper inn 4 om gangen.',

  // ── the demo profile banner + the member-side sign-up ─────────────
  'activities.switchProfile': 'Bytt profil',
  'activities.pickDemoProfile': 'Velg hvilken profil du vil simulere (kun for demo):',
  'activities.searchProfile': 'Søk profil…',
  'activities.signupClosedForThis': 'Påmeldingen er stengt for denne aktiviteten.',
  'activities.swapPartner': 'Bytt makker',
  'activities.youAre': 'Du er',
  'activities.standbyFree': 'beredskapsreserve (gratis)',
  'activities.standby': 'beredskapsreserve',
  'activities.queueNumberSuffix': ' · nr. {n}',
  'activities.queueNumberParen': ' (nr. {n})',
  'activities.onWaitlistReserve': 'på venteliste (reserve)',
  'activities.partnerLabel': 'makker:',
  'activities.putMeOnWaitlist': 'Sett meg på venteliste',
  'activities.signYourselfOff': 'Meld deg av',
  'activities.alsoStandby': 'Jeg vil i tillegg være beredskapsreserve',
  'activities.youAreNumberInQueue': ' — du er nr. {n} i køen',
  'activities.standbyExplain1': 'Du står som vanlig reserve og betaler for reserveplassen',
  'activities.amountParen': ' ({amount} kr)',
  'activities.standbyExplain2':
    '. Blir du benyttet som reserve spiller du turneringen som vanlig. Blir du',
  'activities.not': 'ikke',
  'activities.standbyExplain3':
    'benyttet som reserve, trer beredskapsreserve-rollen inn: du kan bli kontaktet for å steppe inn hvis noen skader seg midt i turneringen — og du får',
  'activities.refundedWord': 'refundert',
  'activities.standbyExplain4': 'reserve-beløpet. Beredskapsreserver må være',
  'activities.standbyNearby': 'i nærheten av anlegget og kunne stille på kort varsel',
  'activities.standbyExplain5': '. Beredskapsreservene nummereres i rekkefølgen de melder seg.',
  'activities.standbyListFull': 'Beredskapslisten er full ({count}/{max}).',
  'activities.whichDays': 'Hvilke dager kan du stille?',
  'activities.standbyDaysNote':
    'Du vises kun som beredskapsreserve i turneringen på dagene du har krysset av.',
  'activities.saveChoice': 'Lagre valg',
  'activities.undoCapital': 'Angre',
  'activities.unsavedChanges': 'Ulagrede endringer',
  'activities.levelRequirement': 'Nivåkrav: {label}',
  'activities.yourLevelIs':
    'Du har nivå {level}{name} og er {relation} nivåkravet, så du kan ikke melde deg på selv. Ta kontakt med klubben hvis du mener dette er feil.',
  'activities.nameParen': ' ({name})',
  'activities.relationUnder': 'under',
  'activities.relationOver': 'over',
  'activities.relationOutside': 'utenfor',
  'activities.genderSlotsFull':
    '{slots} er fulle — du settes på venteliste og rykker opp hvis en {gender} melder seg av.',
  'activities.womensPlaces': 'Dameplassene',
  'activities.mensPlaces': 'Herreplassene',
  'activities.aWoman': 'dame',
  'activities.aMan': 'herre',
  'activities.readyMix':
    'Klar for påmelding. {men} ledige herreplasser · {women} ledige dameplasser.',
  'activities.fullGoesToWaitlist':
    'Aktiviteten er full — du settes på venteliste og rykker opp hvis en plass blir ledig.{waitlist}',
  'activities.waitlistNowSuffix': ' {count} står på venteliste nå.',
  'activities.readyForSignup': 'Klar for påmelding.{free}',
  'activities.freePlacesSuffix': ' {count} ledige plasser.',
  'activities.priceSentence': ' Pris: {amount} kr.',
  'activities.waitlistTip':
    'Tips: for hver 4. spiller på venteliste er det nok til en hel ekstra bane — da kan klubben åpne en bane til og slippe inn flere.',
  'activities.waitlistTipCountOne':
    ' Det står {count} på venteliste nå ({courts} ekstra bane mulig).',
  'activities.waitlistTipCountMany':
    ' Det står {count} på venteliste nå ({courts} ekstra baner mulig).',
  'activities.yourSignups': 'Dine påmeldinger ({count})',
  'activities.classFallback': 'Klasse',
  'activities.waitlistDotSuffix': ' · venteliste',

  // ── klubbmesterskap: classes, the pair rule and playing up ────────
  'activities.km.levelExact': 'nivå {level}',
  'activities.km.levelRange': 'nivå {min}–{max}',
  'activities.km.alreadyInCategory': 'Du er allerede påmeldt i denne kategorien',
  'activities.km.classFull': 'Klassen er full',
  'activities.km.requiresLevel': 'Krever {label} — ditt nivå er utenfor',
  'activities.km.womenOnly': 'Kun damer kan melde seg på i denne klassen',
  'activities.km.teamPriceSuffix': ' · {amount} kr/lag',
  'activities.km.signedUpSuffix': ' · påmeldt',
  'activities.km.fullSuffix': ' · FULL',
  'activities.km.playUpTipPre': 'Herreklassen er',
  'activities.km.openToAll': 'åpen for alle',
  'activities.km.playUpTipMid':
    '. Som dame kan du «spille opp» her — det er fullt lov, men husk at dette er en herreklasse, og du får en advarsel når du melder deg på. For en egen dameklasse, velg',
  'activities.km.womenClass': 'Damer',
  'activities.km.pairsNote':
    'Du melder deg på som par. Damer kan også melde seg på i herreklassen.',
  'activities.km.pickPartnerFor': 'Velg makker — {label}',
  'activities.km.switchClass': 'Bytt klasse',
  'activities.km.womanUpWarnPre': '⚠ Du melder deg på i',
  'activities.km.mensClass': 'herreklassen',
  'activities.km.womanUpWarnPost':
    ' som dame. Det er tillatt, men merk at dette er en herreklasse.',
  'activities.km.pairRule': '{label} — par krever {rule}.',
  'activities.km.pickPartnerForPlayer': 'Velg makker for {name}.',
  'activities.km.pickPlayer1': 'Velg spiller 1.',
  'activities.km.addingWomanWarn':
    '⚠ Du legger til en dame i herreklassen. Tillatt, men markeres som «spiller opp».',
  'activities.km.noPlayersForClass': 'Ingen tilgjengelige spillere for denne klassen.',

  // ── club and club-team sign-up ────────────────────────────────────
  'activities.clubTeam.pickYourClub': 'Velg klubben du representerer',
  'activities.clubTeam.availableCount': '{count} ledige',
  'activities.clubTeam.signupNote':
    'Du melder deg på som lag (eller alene → pares med en fra samme klubb). Singler pares aldri på tvers av klubber.',
  'activities.clubTeam.pickPartnerIn': 'Velg makker — {club}',
  'activities.clubTeam.youRepresent': 'Du representerer',
  'activities.clubTeam.switchClub': 'Bytt klubb',
  'activities.clubTeam.aloneNote':
    'Melder du deg på alene, pares du med en annen single fra {club} ved start.',
  'activities.club.evenNote':
    'Like mange fra hver klubb spiller. Er din klubb full, havner du på venteliste.',
  'activities.signUpWithPartner': 'Meld på med makker',
  'activities.signUpAlone': 'Meld på alene',
  'activities.pickYourPartner': 'Velg makkeren din',
  'activities.signUpWithPartnerSelf': 'Meld deg på med makker',
  'activities.signUpAloneSelf': 'Meld deg på alene',
  'activities.waitlistFull': 'Venteliste full',
  'activities.waitlistWithPartner': 'Venteliste med makker',
  'activities.waitlistAlone': 'Venteliste (alene)',
  'activities.signupFull': 'Påmelding full',
  'activities.bothFull': 'Påmeldingen er full og ventelista er full — du kan ikke melde deg på nå.',
  'activities.fullWaitlisted': 'Aktiviteten er full — du settes på venteliste.',
  'activities.aloneNote':
    'Melder du deg på alene, blir du satt sammen med en annen single ved start. Par prioriteres for de siste plassene.',
  'activities.waitlistParen': ' (venteliste)',
  'activities.availableSuffixOne': ' · {count} ledig',
  'activities.availableSuffixMany': ' · {count} ledige',
  'activities.waitlistAndPay': 'Sett meg på venteliste · betal {amount} kr',
  'activities.signUpAndPay': 'Meld meg på · betal {amount} kr',

  // ── the Padel24 standby-reserve prompt ────────────────────────────
  'activities.standbyListIsFull': 'Beredskapslisten er full',
  'activities.standbyOnlyTitle': 'Bli kun beredskapsreserve — gratis, uten å spille',
  'activities.standbyOnlyButton': 'Beredskapsreserve – gratis',
  'activities.becomeStandbyFree': 'Bli beredskapsreserve (gratis)',
  'activities.youGoOnWaitlist': 'Du havner på venteliste',
  'activities.standbyOnlyExplain':
    'Du settes ikke på venteliste og betaler ingenting. Du står kun som beredskapsreserve og kan bli kontaktet for å steppe inn hvis noen skader seg midt i turneringen.',
  'activities.fullSoReserve': 'Aktiviteten er full, så du settes på venteliste som reserve.',
  'activities.reservePaymentNote':
    ' Du betaler {amount} kr for reserveplassen, men får refusjon dersom du ikke blir benyttet.',
  'activities.standbyReservesWord': 'Beredskapsreserver',
  'activities.standbyPromptExplain':
    'Blir du ikke benyttet som reserve, kan du steppe inn hvis noen skader seg midt i turneringen — og får da reserve-beløpet refundert. Beredskapsreserver',
  'activities.mustBe': 'må være',
  'activities.standbyCallOrderPre': 'Når noen må erstattes, kalles den beredskapsreserven som er',
  'activities.closestInLevel': 'nærmest i nivå (ELO)',
  'activities.standbyCallOrderPost':
    'til spilleren inn — først innenfor ±100, så ±200, ±300 osv. Du kan derfor bli kalt inn selv om du står langt nede på lista, dersom du er den nærmeste i nivå akkurat da.',
  'activities.signMeUpAsStandby': 'Meld meg som beredskapsreserve →',
  'activities.continueToPayment': 'Fortsett til betaling →',
  'activities.putMeOnWaitlistArrow': 'Sett meg på venteliste →',
  'activities.paymentSummary': '{name} — {label}{reserve}',
  'activities.reservePlaceSuffix': ' · reserveplass (refunderes hvis du ikke blir benyttet)',
  'activities.recordResult': 'Registrer resultat',
  // The API/domain error CODE is not translated — only the sentence around it.
  'activities.errorPrefix': 'Feil: {message}',
  'activities.unknownError': 'ukjent feil',

  // ── admin: setup, court time and the manual add list ──────────────
  'activities.setup': 'Oppsett',
  'activities.tuneSetup': 'Tune oppsett',
  'activities.save': 'Lagre',
  'activities.nameLabel': 'Navn',
  'activities.maxPlayersLabel': 'Maks spillere',
  'activities.pricePerPlayerLabel': 'Pris/spiller (kr)',
  'activities.dateLabel': 'Dato',
  'activities.fromLabel': 'Fra',
  'activities.toLabel': 'Til',
  'activities.padel24SetupNote':
    'Dato, banetid og baner styres i Padel24-oppsettet — trykk «Tune oppsett».',
  'activities.deregisterCutoffLabel': 'Avmeldingsfrist (timer før start)',
  'activities.signupCloseHoursLabel': 'Steng påmelding automatisk (timer før start)',
  'activities.signupCloseHoursHelp':
    'Påmelding stenges automatisk når det er så mange timer igjen til reservert starttid. Du kan også stenge/åpne manuelt.',
  'activities.courtTime': 'Banetid',
  'activities.courtsReservedInCalendarOne':
    '{count} bane reservert i Padelsenter-kalenderen · {when}',
  'activities.courtsReservedInCalendarMany':
    '{count} baner reservert i Padelsenter-kalenderen · {when}',
  'activities.editInCalendar': 'Endre i kalender',
  'activities.release': 'Frigjør',
  'activities.noCourtsUsed': 'Denne aktiviteten bruker ingen baner.',
  'activities.pickCourtsVisually':
    'Velg tid og baner visuelt i den ekte booking-kalenderen. Reserverte baner tagges til denne aktiviteten.',
  'activities.reserveCourtsInCalendar': 'Reserver baner i kalenderen',
  'activities.addSingle': 'Legg til single',
  'activities.addPair': 'Legg til par',
  'activities.add': 'Legg til',
  'activities.addPlayerManually': 'Legg til spiller manuelt',
  'activities.searchNameOrBrowse': 'Søk på navn — eller bla i lista under…',
  'activities.nounPairs': 'par',
  'activities.nounSingles': 'singler',
  'activities.nounPlayers': 'spillere',
  'activities.fillWithRandom': 'Fyll med tilfeldige {noun}',
  'activities.withinLevelSuffix': ' innenfor nivået',
  'activities.pairsCount': '{count} par',
  'activities.available': 'tilgjengelig',
  'activities.inLevelSuffix': ' i nivå',
  'activities.levelEloParen': ' (nivå {level}, {elo} ELO)',
  'activities.unknownLevelParen': ' (ukjent nivå)',
  'activities.outsideLevelAsk': 'er {relation} nivåkravet{label}. Legge til likevel?',
  'activities.addAnyway': 'Legg til likevel',
  'activities.quotaFullPre': 'er fullt. Legge til som',
  'activities.quotaFullPost': '(overstyr kvoten)?',
  'activities.addAsExtra': 'Legg til som ekstra',
  'activities.addAsColon': 'legg til som:',
  'activities.single': 'Single',
  'activities.pickPlayer2Suffix': '? Velg spiller 2 i lista under.',
  'activities.pickInListSuffix': '? Velg i lista.',
  'activities.underLevel': 'under nivå',
  'activities.overLevel': 'over nivå',
  'activities.mixColMen': 'Herrer',
  'activities.mixColWomen': 'Damer',
  'activities.searchIn': 'Søk {what}…',
  'activities.searchTo': 'Søk til {club}…',
  'activities.searchInClub': 'Søk i {club}…',
  'activities.none': 'Ingen.',

  // ── the confirmed / waitlist roster ───────────────────────────────
  'activities.km.womanInMensClass': '⚠ Dame spiller i herreklassen',
  'activities.noPairsYet': 'Ingen par ennå.',
  'activities.noneYet': 'Ingen ennå.',
  'activities.singlesPairedAtStart': 'Enkeltpåmeldte · pares ved start ({count})',
  'activities.oneSingleMissingPartnerInClub': 'Én single mangler makker i {club}.',
  'activities.oneSingleMissingPartner':
    'Én single mangler makker — trenger én til for å bli et lag.',
  'activities.waitlistHeading': 'Venteliste ({what})',
  'activities.playersCount': '{count} spillere',
  'activities.swapPlayer': 'Bytt ut spiller',
  'activities.swappingOut': 'Bytter ut',
  'activities.isKeptPickNew': 'beholdes. Velg ny makker:',

  // ── the admin action row ──────────────────────────────────────────
  'activities.moreOptions': '· flere valg',
  'activities.openSignup': 'Åpne påmelding',
  'activities.closeSignup': 'Steng påmelding',
  'activities.sessionCompleted': 'Økta er gjennomført — ligger nå under «Tidligere».',
  'activities.sessionRunning': '● Økta er i gang',
  'activities.endSession': 'Avslutt økta',
  'activities.noSignupsCanStart': 'Ingen påmeldte ennå — du kan likevel starte økta.',
  'activities.startSession': 'Start økt',
  'activities.startTournament': 'Start turneringen',
  'activities.blocked.km': 'Minst 2 par i én klasse for å forhåndsvise klubbmesterskapet',
  'activities.blocked.clubTeam': 'Trenger like mange lag i hver klubb (minst 1 hver) — nå {now}',
  'activities.blocked.club': 'Trenger like mange fra hver klubb (minst 2 hver) — nå {now}',
  'activities.blocked.mix': 'Minst 2 herrer og 2 damer for å starte',
  'activities.blocked.min4': 'Minst 4 spillere for å starte',
  'activities.danglingSingleInClub':
    'Én single står uten makker i en klubb — pares ikke ved start.',

  // ── deleting one activity, or a recurring series ──────────────────
  'activities.deleteSeriesActivity': 'Slett serie-aktivitet',
  'activities.partOfSeriesShort': 'Del av serie',
  'activities.whatToDelete': '. Hva vil du slette?',
  'activities.deleteScope.thisOnly': 'Bare denne',
  'activities.deleteScope.oneActivityBookingOne': '· 1 aktivitet, {bookings} booking',
  'activities.deleteScope.oneActivityBookingMany': '· 1 aktivitet, {bookings} bookinger',
  'activities.deleteScope.thisAndFuture': 'Denne og fremtidige',
  'activities.deleteScope.wholeSeries': 'Hele serien',
  'activities.deleteScope.activitiesOne': '· {count} aktivitet,',
  'activities.deleteScope.activitiesMany': '· {count} aktiviteter,',
  'activities.deleteScope.bookingsOne': ' {count} booking',
  'activities.deleteScope.bookingsMany': ' {count} bookinger',
  'activities.scope.thisActivity': 'denne aktiviteten',
  'activities.scope.thisAndFuture': 'denne og alle fremtidige i serien',
  'activities.scope.wholeSeries': 'hele serien',
  'activities.confirmDeletion': 'Bekreft sletting',
  'activities.aboutToDelete': 'Du er i ferd med å slette {scope}',
  'activities.deleteCountsOne': ' — {count} aktivitet og {bookings} {bookingWord}',
  'activities.deleteCountsMany': ' — {count} aktiviteter og {bookings} {bookingWord}',
  'activities.bookingWordOne': 'banereservasjon',
  'activities.bookingWordMany': 'banereservasjoner',
  'activities.cannotBeUndone': 'Dette kan ikke angres.',
  'activities.alsoRemoveFutureBookings':
    'Fjern også fremtidige banereservasjoner (historiske beholdes).',
  'activities.yesDelete': 'Ja, slett',

  // ── the activity list ─────────────────────────────────────────────
  'activities.badge.completed': 'GJENNOMFØRT',
  'activities.badge.inProgress': 'PÅGÅR',
  'activities.openToAllLevels': 'Åpen for alle nivåer',
  'activities.levelOnly': 'Nivå {level}',
  'activities.eloLevelTitle': '{label} ELO-nivå',
  'activities.seg.thisWeek': 'Denne uken',
  'activities.seg.nextWeek': 'Neste uke',
  'activities.seg.later': 'Senere',
  'activities.seg.noFixedTime': 'Uten fast tidspunkt',
  'activities.seg.past': 'Tidligere',
  'activities.create.coaching': 'Coaching',
  'activities.create.arrangement': 'Arrangement',
  'activities.create.newActivity': 'Ny aktivitet',
  'activities.toHome': 'Til forsiden',
  'activities.eyebrow.activities': 'Aktiviteter',
  'activities.eyebrow.tournaments': 'Turneringer',
  'activities.eyebrow.signups': 'Påmeldinger',
  'activities.title.activities': 'Aktiviteter',
  'activities.title.tournaments': 'Påmelding til turneringer',
  'activities.title.signups': 'Påmeldinger og aktiviteter',
  'activities.theCentre': 'senteret',
  'activities.lead.activities':
    'Coaching, arrangementer og egendefinerte aktiviteter hos {club}. Disse kan startes uavhengig av antall påmeldte.',
  'activities.lead.tournaments':
    'Turneringer som er åpne for påmelding hos {club}. Meld deg på en ledig plass, eller still deg på venteliste hvis det er fullt.',
  'activities.lead.signups':
    'Alle påmeldinger og aktiviteter hos {club} — turneringer og egendefinerte aktiviteter samlet.',
  'activities.createActivity': 'Opprett aktivitet',
  'activities.tab.all': 'Alle',
  'activities.tab.tournaments': 'Turneringer',
  'activities.tab.activities': 'Aktiviteter',
  'activities.createPickerLead':
    'Velg type. Egendefinerte aktiviteter settes opp her; turneringsformater åpner sitt eget oppsett (med påmelding via Aktiviteter forhåndsvalgt).',
  'activities.customSection': 'Egendefinerte',
  'activities.create.coachingHint': 'Trening med påmelding, baner + pris',
  'activities.create.arrangementHint': 'Sosialt arrangement, fest e.l.',
  'activities.create.customHint': 'Helt fritt oppsett',
  'activities.tournamentFormatsSection': 'Turneringsformater',
  'activities.leagueNotFromHere':
    'Seriespill opprettes ikke herfra — det startes fra sin egen modul.',

  // ── the custom-activity setup form ────────────────────────────────
  'activities.newOfType': 'Ny {type}',
  'activities.namePlaceholder': 'f.eks. Nybegynnerkurs torsdag',
  'activities.placeOptional': 'Sted (valgfri)',
  'activities.maxParticipantsLabel': 'Maks deltakere (tomt = ingen grense)',
  'activities.egEight': 'f.eks. 8',
  'activities.maxWaitlistLabel': 'Maks på venteliste (tomt = uendelig)',
  'activities.egFour': 'f.eks. 4',
  'activities.allowWaitlist': 'Tillat venteliste når det er fullt',
  'activities.reserveCourtsForThis': 'Reserver baner for denne aktiviteten',
  'activities.reserveCourtsLater':
    'Baner kan reserveres fra aktivitetens detaljside etter oppretting.',
  'activities.descriptionOptional': 'Beskrivelse (valgfri)',
  'activities.descriptionPlaceholder': 'Info til deltakerne …',
  'activities.backArrow': '← Tilbake',

  // ── the empty state, the filters and the list cards ───────────────
  'activities.emptyTitle': 'Ingen aktiviteter ennå',
  'activities.emptyLead':
    'Når en turnering settes til «Vent på påmelding» dukker den opp her med ledige plasser og venteliste. Det settes opp i turneringens oppsett (vi starter med Americano).',
  'activities.filter.sort': 'Sorter',
  'activities.filter.status': 'Status',
  'activities.filter.type': 'Type',
  'activities.filter.venue': 'Padelsenter',
  'activities.filter.all': 'Alle',
  'activities.sort.newest': 'Nyeste først',
  'activities.sort.oldest': 'Eldste først',
  'activities.sort.courtTime': 'Banetid (først)',
  'activities.sort.name': 'Navn (A–Å)',
  'activities.status.openSignup': 'Åpen påmelding',
  'activities.status.waitlistOpen': 'Venteliste åpen',
  'activities.status.closed': 'Stengt',
  'activities.status.cancelled': 'Kansellert',
  'activities.resetFilters': 'Nullstill filtre',
  'activities.visibleOfTotal': '{shown} av {total}',
  'activities.deleteAll': 'Slett alle',
  'activities.wipeTitle': 'Slett ALLE aktiviteter?',
  'activities.wipeLeadPre':
    'Dette sletter alle {count} aktivitetene (inkl. serier og påmeldinger) og',
  'activities.wipeLeadBold': 'frigir alle banereservasjonene',
  'activities.wipeLeadPost': 'som hører til dem.',
  'activities.deleting': 'Sletter…',
  'activities.yesDeleteAll': 'Ja, slett alle',
  'activities.noMatchFilter': 'Ingen aktiviteter matcher filteret.',
  'activities.clickToShow': 'klikk for å vise',
  'activities.seasonStartsOn': 'Sesongstart {date}',
  'activities.seasonPeriodSuffix': '· sesong (periode)',
  'activities.priceKr': '{amount} kr',
  'activities.partOfSeriesTitle': 'Del av serie{name}',
  'activities.seriesTag': '↻ Serie',
  'activities.occurrenceShort': ' · gang {index}',
  'activities.fullWord': 'Fullt',
  'activities.waitlistOpenSuffix': ' · venteliste åpen',
  'activities.freeCountOne': '{count} ledig',
  'activities.freeCountMany': '{count} ledige',
  'activities.onWaitlistSuffix': ' · {count}{max} på venteliste',
  'activities.km.signUpInAClass': 'Meld deg på i en klasse',
  'activities.signedUpHeading': 'Påmeldte',
  'activities.pairsParen': '({count} par)',
  'activities.mixCountsParen': '({men}/{menMax} menn · {women}/{womenMax} damer)',
  'activities.playersWordSuffix': ' spillere',
  // ══ seriespill — league play (#310a) ══════════════════════════════
  //
  // The club-side league surface: the season's classes and divisions, the
  // sign-up, the schedule and standings, the court booking a round's matches
  // hang off, and the season transition that promotes and relegates pairs.
  //
  // `seriespill` is the surface, so it is the namespace; the ENGLISH word is
  // LEAGUE (docs/architecture/I18N_GLOSSARY.md) — «series» means something
  // else in sport. The member app already says league in `mobile.league.*`,
  // and these keys use the same vocabulary: kamp is a MATCH, pulje a GROUP,
  // påmelding a SIGN-UP. Keys are sub-grouped by screen, with
  // `seriespill.common.*` and `seriespill.table.*` for what genuinely recurs.
  //
  // The Norwegian is verbatim what the markup said before the move — the
  // rendered Norwegian does not change.
  // ── the standings banner pinned inside a Banepad court pad ──────────
  'seriespill.pad.division': 'Divisjon',
  'seriespill.pad.class': 'Klasse',
  'seriespill.pad.table': 'Tabell',
  'seriespill.pad.playedShort': '{count}k',
  'seriespill.pad.noResults': 'Ingen resultater ennå',
  // ── editing one round/season deadline ───────────────────────────────
  'seriespill.deadline.seasonTitle': 'Sesongfrist',
  'seriespill.deadline.startTitle': 'Start for runde {round}',
  'seriespill.deadline.roundTitle': 'Frist for runde {round}',
  'seriespill.deadline.close': 'Lukk',
  'seriespill.deadline.roundStartLabel': 'Rundestart (dato)',
  'seriespill.deadline.seasonLabel': 'Sesongfrist (dato)',
  'seriespill.deadline.deadlineLabel': 'Frist (dato)',
  'seriespill.deadline.othersQuestion': 'De andre fristene?',
  'seriespill.deadline.keepOthers': 'La øvrige stå',
  'seriespill.deadline.keepOthersHint': 'Endrer kun denne.',
  'seriespill.deadline.shiftLater': 'Flytt alle senere tilsvarende',
  'seriespill.deadline.shiftLaterHint':
    'Forskyver alle påfølgende frister like mye fremover/bakover.',
  'seriespill.deadline.cancel': 'Avbryt',
  'seriespill.deadline.save': 'Lagre frist',
  // ── the markdown rule-note editor ───────────────────────────────────
  'seriespill.note.defaultTitle': 'Rediger regelnotat',
  'seriespill.note.close': 'Lukk',
  'seriespill.note.h1': 'Stor overskrift',
  'seriespill.note.h2': 'Overskrift',
  'seriespill.note.bold': 'Fet',
  'seriespill.note.italic': 'Kursiv',
  'seriespill.note.bulletList': 'Punktliste',
  'seriespill.note.bulletListLabel': '• Liste',
  'seriespill.note.numberedList': 'Nummerert liste',
  'seriespill.note.numberedListLabel': '1. Liste',
  'seriespill.note.quote': 'Sitat',
  'seriespill.note.quoteLabel': '❝ Sitat',
  'seriespill.note.insertLink': 'Sett inn lenke',
  'seriespill.note.linkLabel': '🔗 Lenke',
  'seriespill.note.insertImage': 'Sett inn bilde (URL)',
  'seriespill.note.imageLabel': '🖼 Bilde',
  'seriespill.note.image': 'Bilde',
  'seriespill.note.link': 'Lenke',
  'seriespill.note.altPlaceholder': 'Bildetekst (alt)',
  'seriespill.note.linkTextPlaceholder': 'Lenketekst',
  'seriespill.note.urlPlaceholder': 'https://…',
  'seriespill.note.urlLabel': 'Adresse (URL)',
  'seriespill.note.insert': 'Sett inn',
  'seriespill.note.cancel': 'Avbryt',
  'seriespill.note.save': 'Lagre notat',
  'seriespill.note.editorPlaceholder':
    'Skriv regelnotat. Markdown: **fet**, *kursiv*, # overskrift, - punkt, 1. nummerert, > sitat, [tekst](url), ![alt](bilde-url).',
  // The toolbar writes these into the note itself when nothing is selected,
  // so they are scaffolding the operator overwrites — not stored business data.
  'seriespill.note.phText': 'tekst',
  'seriespill.note.phBold': 'fet tekst',
  'seriespill.note.phItalic': 'kursiv',
  'seriespill.note.phImage': 'bilde',
  'seriespill.note.phLink': 'lenke',
  // ── the mid-season substitution badge (mirrors players.replacementBadge.*) ───
  'seriespill.replacement.playerFallback': 'Spiller',
  'seriespill.replacement.kicker': 'Innbytter',
  'seriespill.replacement.buttonTitle': 'Innbytter — klikk for detaljer',
  'seriespill.replacement.title': 'Spillerbytte underveis',
  'seriespill.replacement.unknownTime': 'ukjent tidspunkt',
  'seriespill.replacement.swappedOut': 'Byttet ut',
  'seriespill.replacement.replacedBy': 'Erstattet av',
  'seriespill.replacement.time': 'Tidspunkt',
  'seriespill.replacement.matchesPlayedLabel': 'Kamper spilt da byttet skjedde',
  'seriespill.replacement.matchesProgress': '{done} av {total} kamper ferdig',
  'seriespill.replacement.firstMatchFor': 'Første kamp for {name}',
  'seriespill.replacement.theSubstitute': 'innbytteren',
  'seriespill.replacement.round': 'Runde {round}',
  'seriespill.replacement.upcoming': 'Kommende',
  'seriespill.replacement.close': 'Lukk',
  // ── shared across the league screens ────────────────────────────────
  'seriespill.common.genderMen': 'Herrer',
  'seriespill.common.genderWomen': 'Damer',
  'seriespill.common.genderMix': 'Mix',
  // The standings-table column heads. Norwegian abbreviates the words
  // (S/V/U/T/P); English uses the football-table convention, where P is
  // PLAYED and points are Pts — so they cannot both be «P».
  'seriespill.table.pair': 'Par',
  'seriespill.table.played': 'S',
  'seriespill.table.wins': 'V',
  'seriespill.table.draws': 'U',
  'seriespill.table.losses': 'T',
  'seriespill.table.points': 'P',
  // ── the archived-seasons browser ────────────────────────────────────
  'seriespill.history.title': '{name} — Sesong {season}',
  'seriespill.history.allTitle': 'Tidligere sesonger',
  'seriespill.history.empty': 'Ingen tidligere sesonger ennå.',
  'seriespill.history.season': 'Sesong {season}',
  'seriespill.history.divisionCountOne': '{count} divisjon',
  'seriespill.history.divisionCountMany': '{count} divisjoner',
  'seriespill.history.tabTable': '🏆 Tabell',
  'seriespill.history.tabMatches': '🎾 Kamper',
  'seriespill.history.noData': 'Ingen data.',
  'seriespill.history.round': 'Runde {round}',
  'seriespill.history.drawShort': 'U',
  'seriespill.history.noMatches': 'Ingen kamper spilt.',
  'seriespill.history.back': 'Tilbake til alle sesonger',
  // ── one player's league profile ─────────────────────────────────────
  'seriespill.player.nameFallback': 'Spiller',
  'seriespill.player.kicker': 'Spillerprofil · seriespill',
  'seriespill.player.swappedOutTag': '⊘ BYTTET UT',
  'seriespill.player.substituteTag': '↻ INNBYTTER',
  'seriespill.player.substituteTitle': 'Innbytter — byttet inn denne sesongen',
  'seriespill.player.eloInSeason': '({delta} i serien)',
  'seriespill.player.pairChip': '{cls} · {div} · med {partner}',
  'seriespill.player.swapAction': 'Bytt ut spilleren',
  'seriespill.player.fullProfile': 'Full profil',
  'seriespill.player.notInSeries': 'Spilleren er ikke med i dette seriespillet.',
  'seriespill.player.statWinLoss': 'Seire-tap',
  'seriespill.player.statSets': 'Sett',
  'seriespill.player.statPlayed': 'Kamper spilt',
  'seriespill.player.statElo': 'ELO i serien',
  'seriespill.player.eloChartTitle': 'ELO-utvikling i serien',
  'seriespill.player.eloChartCaption': 'Etter hver av {count} fullførte kamper',
  // The ELO chart axis labels. «K» is kamp — the English one cannot be a K.
  'seriespill.player.eloStart': 'Start',
  'seriespill.player.eloAfterMatch': 'K{n}',
  'seriespill.player.upcomingHeader': 'Kommende kamper ({count})',
  'seriespill.player.completedHeader': 'Ferdigspilt ({count})',
  'seriespill.player.latestFirst': 'Siste først',
  'seriespill.player.noMatches': 'Ingen kamper funnet for denne spilleren ennå.',
  'seriespill.player.voided': 'Annullert',
  'seriespill.player.notPlayed': 'Ikke spilt',
  'seriespill.player.lossWo': 'Tap (WO)',
  'seriespill.player.draw': 'Uavgjort',
  'seriespill.player.win': 'Seier',
  'seriespill.player.loss': 'Tap',
  // The countdown under an upcoming match deadline.
  'seriespill.player.overdue': 'Forfalt',
  'seriespill.player.today': 'I dag!',
  'seriespill.player.daysOne': '{days} dag',
  'seriespill.player.daysMany': '{days} dager',
  'seriespill.player.inMonths': 'om {months} mnd',
  // Weekday and month names the league screens spell out by hand, plus the
  // date shape they build from them. DISPLAY ONLY — never stored or compared
  // (docs/architecture/DOMAIN_RULES.md §4). See components/seriespill/dateNames.ts.
  'seriespill.date.weekdayLong0': 'søndag',
  'seriespill.date.weekdayLong1': 'mandag',
  'seriespill.date.weekdayLong2': 'tirsdag',
  'seriespill.date.weekdayLong3': 'onsdag',
  'seriespill.date.weekdayLong4': 'torsdag',
  'seriespill.date.weekdayLong5': 'fredag',
  'seriespill.date.weekdayLong6': 'lørdag',
  'seriespill.date.weekdayShort0': 'Søn',
  'seriespill.date.weekdayShort1': 'Man',
  'seriespill.date.weekdayShort2': 'Tir',
  'seriespill.date.weekdayShort3': 'Ons',
  'seriespill.date.weekdayShort4': 'Tor',
  'seriespill.date.weekdayShort5': 'Fre',
  'seriespill.date.weekdayShort6': 'Lør',
  'seriespill.date.monthShort0': 'jan',
  'seriespill.date.monthShort1': 'feb',
  'seriespill.date.monthShort2': 'mar',
  'seriespill.date.monthShort3': 'apr',
  'seriespill.date.monthShort4': 'mai',
  'seriespill.date.monthShort5': 'jun',
  'seriespill.date.monthShort6': 'jul',
  'seriespill.date.monthShort7': 'aug',
  'seriespill.date.monthShort8': 'sep',
  'seriespill.date.monthShort9': 'okt',
  'seriespill.date.monthShort10': 'nov',
  'seriespill.date.monthShort11': 'des',
  'seriespill.date.long': '{wd} {day}. {mon} {year}',
  // ── next season's sign-up and payment tracking ──────────────────────
  'seriespill.signup.intro':
    'Hver klasse har sin egen sesong, pris og påmelding. Sett pris per lag, publiser neste sesong til Aktiviteter med startdato, og følg betalingene — alt per klasse.',
  'seriespill.signup.noClasses': 'Ingen klasser ennå.',
  'seriespill.signup.classHeading': '{name} — sesong {season}',
  'seriespill.signup.classSub': 'Påmelding til neste sesong. Inneværende sesong ({season}) pågår.',
  'seriespill.signup.teamsPaid': '{paid}/{total} lag betalt',
  'seriespill.signup.incomeSuffix': ' · {amount} kr inn',
  'seriespill.signup.pricePerTeam': 'Pris per lag (kr)',
  'seriespill.signup.pricePlaceholder': 'f.eks. 1500',
  'seriespill.signup.savePrice': 'Lagre pris',
  'seriespill.signup.feeSaved': 'Sesongavgift lagret',
  'seriespill.signup.seasonFeeWord': 'sesongavgift',
  'seriespill.signup.free': 'Gratis',
  'seriespill.signup.feeKr': '{fee} kr',
  'seriespill.signup.inActivities': 'Påmelding i Aktiviteter',
  'seriespill.signup.published': '✓ Publisert',
  'seriespill.signup.seasonStart': 'Sesongstart',
  'seriespill.signup.signupDeadline': 'Påmeldingsfrist (valgfri)',
  'seriespill.signup.updateInActivities': 'Oppdater i Aktiviteter',
  'seriespill.signup.publishToActivities': 'Publiser til Aktiviteter →',
  'seriespill.signup.seeInActivities': 'Se i Aktiviteter →',
  'seriespill.signup.remove': 'Fjern',
  'seriespill.signup.publishedStart': 'Ute med start {date}',
  'seriespill.signup.publishedDeadline': ' · frist {date}',
  'seriespill.signup.publishedNewTeams': ' {count} nye lag påmeldt via Aktiviteter.',
  'seriespill.signup.noTeams': 'Ingen lag i denne klassen ennå.',
  'seriespill.signup.withdrawnSuffix': ' · meldt av',
  'seriespill.signup.paidSuffix': ' · betalt {date}',
  'seriespill.signup.paidBySelf': ' (selvbetjent)',
  'seriespill.signup.paidByAdmin': ' (admin)',
  'seriespill.signup.withdrawnTag': 'Meldt av',
  'seriespill.signup.rejoin': 'Meld på igjen',
  'seriespill.signup.cancel': 'Avbryt',
  'seriespill.signup.undo': 'angre',
  'seriespill.signup.undoAction': 'Angre',
  'seriespill.signup.undoWithdrawTitle': 'Angre avmelding',
  'seriespill.signup.undoPaidTitle': 'Admin: angre',
  'seriespill.signup.signedUpTag': 'Påmeldt',
  'seriespill.signup.paidTag': 'Betalt',
  'seriespill.signup.unpaidTag': 'Ikke betalt',
  'seriespill.signup.selfPayTitle': 'Selvbetjent betaling (DEMO)',
  'seriespill.signup.confirmFree': 'Bekreft (gratis)',
  'seriespill.signup.pay': 'Betal {fee}',
  'seriespill.signup.withdraw': 'Meld av',
  'seriespill.signup.withdrawTitle': 'Laget trekker seg fra neste sesong',
  'seriespill.signup.markPaid': 'marker betalt',
  'seriespill.signup.closeMore': 'Lukk',
  'seriespill.signup.markPaidTitle': 'Admin: marker som betalt uten betalingsflyt',
  'seriespill.signup.more': 'mer ▾',
  'seriespill.signup.moreTitle': 'Flere valg',
  // ── starting the next season: promotion, relegation and placement ───
  'seriespill.transition.title': '{name} — start sesong {season}',
  'seriespill.transition.lead':
    'Sesong {season} for {name} er ferdig — bekreft plasseringen, juster ved behov, og start neste sesong.',
  'seriespill.transition.close': 'Lukk',
  // The short level badge stays «N{level}» in English too — see mobile.activities.myLevelFilter.
  'seriespill.transition.levelTag': 'N{level}',
  'seriespill.transition.levelTagTitle': '{name}: Nivå {level} — {levelName}',
  'seriespill.transition.avgElo': 'snitt {avg}',
  'seriespill.transition.avgEloTitle': 'Lagets snitt-ELO',
  'seriespill.transition.divAvg': 'Snitt {avg}',
  'seriespill.transition.divAvgTitle': 'Divisjonens snitt-rating (foreslått neste sesong)',
  'seriespill.transition.pairsNextSeason': '{count} par til neste sesong',
  'seriespill.transition.newTag': 'nytt',
  'seriespill.transition.promotedTag': '↑ opprykk',
  'seriespill.transition.relegatedTag': '↓ nedrykk',
  'seriespill.transition.manualUpTag': '✋ Manuelt opp ↑',
  'seriespill.transition.manualUpTitle': 'Manuelt flyttet opp fra {div}',
  'seriespill.transition.manualDownTag': '✋ Manuelt ned ↓',
  'seriespill.transition.manualDownTitle': 'Manuelt flyttet ned fra {div}',
  'seriespill.transition.suggestedDivFallback': 'foreslått divisjon',
  'seriespill.transition.moveTitle': 'Flytt par til en annen divisjon',
  'seriespill.transition.remove': 'Fjern',
  'seriespill.transition.removeTitle': 'Fjern par (frafall)',
  'seriespill.transition.emptyDivision':
    'Ingen par i denne divisjonen — sesongen vil ikke ha kamper her.',
  'seriespill.transition.falloutHeading': 'Frafalt fra sesong {season}',
  'seriespill.transition.withdrawnTag': 'meldt av',
  'seriespill.transition.unpaidTag': 'ikke betalt',
  'seriespill.transition.putBackIn': 'Legg tilbake i…',
  'seriespill.transition.moveLabel': 'Flytt {pair} til divisjon',
  'seriespill.transition.putBackInLabel': 'Legg {pair} tilbake i divisjon',
  'seriespill.transition.placeInLabel': 'Plasser {pair} i divisjon',
  'seriespill.transition.player1Label': 'Spiller 1',
  'seriespill.transition.player2Label': 'Spiller 2',
  'seriespill.transition.divisionLabel': 'Divisjon',
  'seriespill.transition.falloutNote':
    'Frafalte par utelates fra neste sesong med mindre du legger dem tilbake. Plassene blir tomme — andre par flyttes ikke automatisk.',
  'seriespill.transition.falloutNoteUnpaid':
    ' Lag merket «ikke betalt» er utelatt fordi sesongavgiften ikke er betalt — de regnes som frafalt, men kan legges tilbake manuelt om nødvendig.',
  'seriespill.transition.unplacedHeading': 'Ikke plassert ennå — nye lag fra påmeldingen ({count})',
  'seriespill.transition.placeIn': 'Plasser i…',
  'seriespill.transition.unplacedNote':
    'Plasser hvert nytt lag i riktig divisjon ut fra snitt-ELO og spillernes nivå — så de beste ikke havner i nederste divisjon.',
  'seriespill.transition.addPairHeading': 'Legg til nytt par',
  'seriespill.transition.noFreePlayers':
    'Ingen ledige spillere å danne et nytt par av (alle er allerede plassert).',
  'seriespill.transition.player1': 'Spiller 1…',
  'seriespill.transition.player2': 'Spiller 2…',
  'seriespill.transition.add': 'Legg til',
  'seriespill.transition.pairAvgLabel': 'Parets snitt: ',
  'seriespill.transition.suggestedDivLabel': ' · foreslått divisjon: ',
  'seriespill.transition.suggestedDivTail':
    ' ★ (snitt nærmest) — kun et forslag, du kan velge fritt.',
  // «nytte» is what the screen says today (`nytt` + `e`) and the migration keeps
  // it — see issue #513. Fixing the wording here would change the rendered
  // Norwegian, which is not what this PR is for.
  'seriespill.transition.unplacedWarnOne':
    '{count} nytt lag er ikke plassert ennå. Plasser alle (eller la dem stå udelt) før du starter sesongen.',
  'seriespill.transition.unplacedWarnMany':
    '{count} nye lag er ikke plassert ennå. Plasser alle (eller la dem stå udelt) før du starter sesongen.',
  'seriespill.transition.reset': 'Tilbakestill',
  'seriespill.transition.resetTitle': 'Tilbakestill til motorens opp/nedrykk-forslag',
  'seriespill.transition.cancel': 'Avbryt',
  'seriespill.transition.start': 'Start sesong {season} →',
  'seriespill.transition.startDisabledTitle': 'Plasser alle nye lag først',
  // ── the generated rule summary ──────────────────────────────────────
  'seriespill.rules.title': 'Regler for serien',
  'seriespill.rules.subtitle': 'Generert fra oppsettet',
  'seriespill.rules.noClasses': 'Ingen klasser.',
  'seriespill.rules.sharedRules': 'Felles regler',
  'seriespill.rules.sharedRulesHint': 'gjelder alle klasser',
  'seriespill.rules.perClass': 'Spesielt per klasse',
  'seriespill.rules.overriddenCount': '{count} overstyrt',
  'seriespill.rules.divisionFallback': 'divisjon {level}',
  // A per-division line: the division, then the rule that applies to it.
  'seriespill.rules.divLine': '{div}: {text}',
  'seriespill.rules.divLineDot': '{div}: {text}.',
  // Section headings. They are also the KEYS of the icon/colour map in
  // SeriespillRulesModal, which is built from these same entries.
  'seriespill.rules.secFormat': 'Format',
  'seriespill.rules.secMatchFormat': 'Kampformat',
  'seriespill.rules.secDeuce': 'Avgjøring ved 40–40',
  'seriespill.rules.secPoints': 'Poeng på tabellen',
  'seriespill.rules.secReveal': 'Annonsering av runder',
  'seriespill.rules.secDeadlines': 'Frister',
  'seriespill.rules.secUnplayed': 'Uspilte kamper',
  'seriespill.rules.secCourt': 'Bane og tidspunkt',
  'seriespill.rules.secMixServe': 'Serve i Mix',
  'seriespill.rules.secUpDown': 'Opp- og nedrykk',
  'seriespill.rules.secRating': 'Rating (ELO)',
  'seriespill.rules.secInOut': 'Uenighet om ball inn/ut',
  'seriespill.rules.secOwnRules': 'Egne regler',
  // The match formats. «super-tiebreak» is a super TIE-BREAK in English
  // (ITF style, hyphenated) and a game is a game inside a set — never a match.
  'seriespill.rules.fmtBo3Super':
    'Best of 3 sett. Står det 1–1 i sett, avgjøres kampen med et super-tiebreak til 10 poeng (vinn med 2).',
  'seriespill.rules.fmtBo3Full':
    'Best of 3 sett. Et avgjørende tredje sett spilles som et fullt sett til 6 game.',
  'seriespill.rules.fmtBo1Set6': 'Ett sett til 6 game (vinn med 2; tiebreak ved 6–6).',
  'seriespill.rules.fmtBo1Short': 'Ett kort sett til 4 game (vinn med 2).',
  'seriespill.rules.formatRoundRobin':
    'Faste par spiller serie (alle mot alle) innen sin divisjon.',
  'seriespill.rules.formatDouble': 'Dobbel serie: alle par møtes to ganger (hjemme og borte).',
  'seriespill.rules.formatSingle': 'Enkel serie: alle par møtes én gang.',
  'seriespill.rules.pointsWinLoss': 'Seier gir {win} poeng, tap gir {loss} poeng.',
  'seriespill.rules.pointsWalkover': 'Walkover (WO) gir vinneren {points} poeng.',
  'seriespill.rules.pointsRanking': 'Tabellen rangeres på poeng, deretter settdifferanse og seire.',
  'seriespill.rules.revealRollingOne':
    'Kampoppsettet annonseres fortløpende: inntil {count} runde frem vises av gangen.',
  'seriespill.rules.revealRollingMany':
    'Kampoppsettet annonseres fortløpende: inntil {count} runder frem vises av gangen.',
  'seriespill.rules.revealLead': ' En runde åpnes {value} {unit} før den starter.',
  'seriespill.rules.revealPlayedAlways':
    'Spilte kamper vises alltid, også om de ligger lenger frem.',
  'seriespill.rules.revealAll': 'Alle runder og kampoppsett er synlige fra start.',
  // Time units, spelled out because the sentences above build them by hand.
  'seriespill.rules.unitDay': 'dag',
  'seriespill.rules.unitDays': 'dager',
  'seriespill.rules.unitWeek': 'uke',
  'seriespill.rules.unitWeeks': 'uker',
  'seriespill.rules.unitMonth': 'måned',
  'seriespill.rules.unitMonths': 'måneder',
  'seriespill.rules.seasonStart': 'Sesongstart: {date}.',
  'seriespill.rules.seasonDeadline': 'Hele sesongen skal være ferdigspilt innen {date}.',
  'seriespill.rules.roundInterval': 'Ny runde åpner hver {value} {unit}.',
  'seriespill.rules.noDeadlines':
    'Ingen faste runde-frister — spill kampene når det passer innen sesongen.',
  'seriespill.rules.unplayedVoid':
    'Kamper som ikke er spilt når en runde avsluttes, annulleres (teller ikke).',
  'seriespill.rules.unplayedWalkover':
    'Kamper som ikke er spilt når en runde avsluttes, regnes som tap 0–2 for begge par (dobbel walkover).',
  'seriespill.rules.forfeitAuto': 'Runden avsluttes automatisk når rundefristen passerer.',
  'seriespill.rules.forfeitManual': 'Runden avsluttes manuelt av administrator.',
  'seriespill.rules.courtAllocated': 'Baner tildeles på faste spilledager.',
  'seriespill.rules.courtSelfBooked': 'Parene avtaler selv tidspunkt og booker bane for hver kamp.',
  'seriespill.rules.mixServe': 'Dame server mot dame og herre mot herre.',
  'seriespill.rules.upDownSingleDivision': 'Kun én divisjon — ingen opp-/nedrykk.',
  'seriespill.rules.upDownAuto':
    'Etter sesongen rykker de beste lagene opp en divisjon og de svakeste ned.',
  'seriespill.rules.upDownAutoCount':
    'Antallet beregnes automatisk ut fra antall lag i divisjonen (færre lag = færre opp/ned, flere lag = flere).',
  'seriespill.rules.upDownAutoDiv': '{div}: opp/ned beregnes automatisk ut fra antall lag.',
  'seriespill.rules.upDownFixedDiv': '{div}: {up} opp / {down} ned.',
  'seriespill.rules.ratingWeight':
    'Resultatene teller på spillernes ELO-rating. Seriespill vektes tungt ({percent} %).',
  'seriespill.rules.inOut': 'Er dere uenige om ballen var inne eller ute, spilles poenget på nytt.',
  'seriespill.rules.noteHeading': 'Regelnotat',
  'seriespill.rules.noteShared': 'Felles regelnotat',
  'seriespill.rules.noteAppend': 'Tillegg · {name}',
  'seriespill.rules.noteOverride': 'Regelnotat · {name}',
  'seriespill.rules.noteOverrideSuffix': ' (erstatter felles)',
  // ── per-class settings: overrides, divisions and pairs ──────────────
  'seriespill.classSettings.title': 'Innstillinger — {name}',
  'seriespill.classSettings.lead':
    'Som default følger klassen serie-innstillingene. Slå på «Overstyr» per felt for å avvike.',
  'seriespill.classSettings.close': 'Lukk',
  'seriespill.classSettings.override': 'Overstyr',
  'seriespill.classSettings.overridden': '✓ Overstyrt',
  'seriespill.classSettings.overrideOnTitle': 'Overstyr serie-default for denne klassen',
  'seriespill.classSettings.overrideResetTitle': 'Klikk for å tilbakestille til serie-default',
  'seriespill.classSettings.followsMain': 'Følger hovedinnstillinger.',
  'seriespill.classSettings.confirmDeleteDivision': 'Slett denne divisjonen og alle par i den?',
  'seriespill.classSettings.tabSettings': 'Innstillinger',
  'seriespill.classSettings.tabBooking': 'Banebooking',
  'seriespill.classSettings.tabDivisions': 'Divisjoner & spillere',
  'seriespill.classSettings.secCourtMode': 'Banemodus',
  'seriespill.classSettings.courtFree': 'Fri booking',
  'seriespill.classSettings.courtFreeHint': 'Parene avtaler og booker bane selv.',
  'seriespill.classSettings.courtAllocated': 'Allokerte baner',
  'seriespill.classSettings.courtAllocatedHint': 'Baner settes av på faste dager (kalender).',
  'seriespill.classSettings.allocNoteA': 'Spilledager og baner settes opp',
  'seriespill.classSettings.allocNoteSlot': 'per tidsrom',
  'seriespill.classSettings.allocNoteB': ' under',
  'seriespill.classSettings.allocNotePath': 'Hovedinnstillinger → Banebooking',
  'seriespill.classSettings.allocNoteC': '— der velger du hvilke klasser hvert tidsrom dekker.',
  'seriespill.classSettings.inSlots': 'Denne klassen er med i: {list}.',
  'seriespill.classSettings.noSlots':
    '⚠ Klassen er ikke med i noe tidsrom ennå — den får ingen slots før du legger den til et tidsrom under Banebooking.',
  'seriespill.classSettings.saveAndOpenBooking': 'Lagre og åpne Banebooking →',
  'seriespill.classSettings.secDates': 'Datoer & rundefrist',
  'seriespill.classSettings.rollingDeadlines': 'Rullerende rundefrister',
  'seriespill.classSettings.fixedDeadline': 'Fast sesongfrist',
  'seriespill.classSettings.seasonStart': 'Sesongstart',
  'seriespill.classSettings.seasonDeadline': 'Sesongfrist',
  'seriespill.classSettings.estimatedEnd': 'Estimert sesongslutt',
  'seriespill.classSettings.estimatedEndNA': 'Beregnes når par/runder finnes',
  'seriespill.classSettings.roundDeadlineRolling': 'Rundefrist (rullerende)',
  'seriespill.classSettings.every': 'Hver',
  'seriespill.classSettings.unitDays': 'dag(er)',
  'seriespill.classSettings.unitWeeks': 'uke(r)',
  'seriespill.classSettings.unitMonths': 'måned(er)',
  'seriespill.classSettings.autoDeadlineNote':
    'Sesongfristen settes automatisk til estimert sesongslutt',
  'seriespill.classSettings.autoDeadlineDate': ' ({date})',
  'seriespill.classSettings.secUnplayed': 'Uspilte kamper & avslutning',
  'seriespill.classSettings.unplayedLabel': 'Uspilte kamper ved avsluttet runde',
  'seriespill.classSettings.unplayedWo': 'Begge taper 0–2 (WO)',
  'seriespill.classSettings.unplayedWoHint': 'Begge lag får tap. Påvirker ikke ELO.',
  'seriespill.classSettings.unplayedDraw': 'Uavgjort',
  'seriespill.classSettings.unplayedDrawHint':
    'Registreres som uavgjort. 0 poeng til hver, teller som spilt. Påvirker ikke ELO.',
  'seriespill.classSettings.roundEndLabel': 'Avslutning av runde',
  'seriespill.classSettings.roundEndManual': 'Manuelt',
  'seriespill.classSettings.roundEndManualHint':
    'Du avslutter runden selv med «Avslutt runde»-knappen i kampprogrammet.',
  'seriespill.classSettings.roundEndAuto': 'Automatisk ved frist',
  'seriespill.classSettings.roundEndAutoHint':
    'Runden avsluttes automatisk når rundefristen passerer. Uspilte kamper får resultatet valgt over.',
  'seriespill.classSettings.secReveal': 'Annonsering av runder',
  'seriespill.classSettings.revealAll': 'Alle runder',
  'seriespill.classSettings.revealAllHint': 'Hele kampprogrammet er synlig fra start.',
  'seriespill.classSettings.revealRolling': 'Før hver runde',
  'seriespill.classSettings.revealRollingHint': 'Fremtidige runder annonseres rett før de starter.',
  'seriespill.classSettings.announce': 'Annonser',
  'seriespill.classSettings.beforeRoundStarts': 'før runden starter',
  'seriespill.classSettings.show': 'Vis',
  'seriespill.classSettings.roundsAhead': 'runde(r) i forkant',
  'seriespill.classSettings.secRoundRobin': 'Round-robin-format',
  'seriespill.classSettings.singleRound': 'Enkel',
  'seriespill.classSettings.doubleRound': 'Dobbel',
  'seriespill.classSettings.homeAway': '(h/b)',
  'seriespill.classSettings.everyPairMeets': 'Alle par møter alle ',
  'seriespill.classSettings.onceStrong': 'én gang',
  'seriespill.classSettings.twiceStrong': 'to ganger',
  'seriespill.classSettings.secMatchFormat': 'Kampformat',
  'seriespill.classSettings.fmtSuperTiebreak': 'Super-tiebreak',
  'seriespill.classSettings.fmtSuperTiebreakHint':
    'Best av 3, super-tiebreak til 10 ved 1–1 (FIP).',
  'seriespill.classSettings.fmtFullThird': 'Fullt 3. sett',
  'seriespill.classSettings.fmtFullThirdHint': 'Best av 3, fullt tredje sett til 6.',
  'seriespill.classSettings.secDeuce': 'Tellemåte ved 40–40',
  'seriespill.classSettings.deuceAdvantage': 'Fordel',
  'seriespill.classSettings.deuceGolden': 'Gullpoeng',
  // Starpoint is the product, not a word — it is never translated.
  'seriespill.classSettings.deuceStarpoint': 'Starpoint',
  'seriespill.classSettings.secMixServe': 'Mix: serveregel',
  'seriespill.classSettings.mixServeFree': 'Fri',
  'seriespill.classSettings.mixServeSame': 'Dame↔dame',
  'seriespill.classSettings.mixServeHint': 'Dame server mot dame, herre mot herre.',
  'seriespill.classSettings.ruleNote': 'Regel-notat',
  'seriespill.classSettings.noteInherit': 'Følg felles',
  'seriespill.classSettings.noteAppend': 'Legg til',
  'seriespill.classSettings.noteOverride': 'Overstyr',
  'seriespill.classSettings.noteInheritHint': 'Klassen bruker det felles regelnotatet.',
  'seriespill.classSettings.noteAppendHint':
    'Vises i tillegg til det felles regelnotatet, merket for denne klassen.',
  'seriespill.classSettings.noteOverrideHint':
    'Erstatter det felles regelnotatet for denne klassen.',
  'seriespill.classSettings.fullscreenEditor': '✎ Fullskjerm-editor',
  'seriespill.classSettings.notePlaceholder':
    'Skriv fritt, eller bruk fullskjerm-editoren. Markdown: **fet**, *kursiv*, # overskrift, - punkt, [tekst](url), ![alt](bilde-url).',
  'seriespill.classSettings.noteEditorTitle': 'Regelnotat — {name}',
  'seriespill.classSettings.classFallback': 'klasse',
  'seriespill.classSettings.secEloWeight': 'ELO-vekt',
  'seriespill.classSettings.eloWeightHint':
    'Bestemmer hvor mye kampene i denne klassen påvirker spillernes ELO. Standard for seriespill er 200 %.',
  'seriespill.classSettings.secAutoMode': 'Auto-modus (sesongovergang)',
  'seriespill.classSettings.autoMode': '⚡ Auto-modus',
  'seriespill.classSettings.autoModeHint':
    'Når klassen låses (alle har spilt eller deadline passerer), starter neste sesong automatisk uten admin-bekreftelse.',
  'seriespill.classSettings.divisionsHeading': 'Divisjoner & påmelding',
  'seriespill.classSettings.demo': 'Demo',
  'seriespill.classSettings.demoTargetTitle':
    'Hvor demo-parene legges: i én valgt divisjon, eller N par i HVER divisjon',
  'seriespill.classSettings.demoAllDivisions': 'i alle divisjoner',
  'seriespill.classSettings.demoInDivision': 'i {name}',
  'seriespill.classSettings.demoAddPairs': '+ {count} par',
  'seriespill.classSettings.demoPairsAllTitle': '{count} par fra ledige spillere i HVER divisjon',
  'seriespill.classSettings.demoPairsOneTitle': '{count} par fra ledige spillere i valgt divisjon',
  'seriespill.classSettings.addDivision': '+ Legg til divisjon',
  'seriespill.classSettings.addPairHeading': 'Legg til par',
  'seriespill.classSettings.playerSlot': 'Spiller {slot}',
  'seriespill.classSettings.pickBelow': '(velg nedenfor)',
  'seriespill.classSettings.newSuffix': ' · Ny',
  'seriespill.classSettings.clearPlayer': 'Fjern valgt spiller',
  'seriespill.classSettings.searchBelow': 'Søk under for å velge…',
  'seriespill.classSettings.putIn': 'Legg i',
  'seriespill.classSettings.movePairLabel': 'Flytt {pair} til divisjon',
  'seriespill.classSettings.divisionNameLabel': 'Divisjonsnavn',
  'seriespill.classSettings.intervalCountLabel': 'Antall',
  'seriespill.classSettings.intervalUnitLabel': 'Enhet',
  'seriespill.classSettings.searchPlayer': 'Søk etter spiller {slot}…',
  'seriespill.classSettings.newPlayer': '+ Ny spiller',
  'seriespill.classSettings.includeWomen': 'Søk også blant damer (damer kan delta i herreklassen)',
  'seriespill.classSettings.noMatchesA': 'Ingen treff på "',
  'seriespill.classSettings.noMatchesB': '".',
  'seriespill.classSettings.createPlayer': 'Opprett "{name}" som ny spiller',
  'seriespill.classSettings.noFreePlayers': 'Ingen flere ledige spillere — alle er allerede valgt.',
  'seriespill.classSettings.genderMale': '♂ Mann',
  'seriespill.classSettings.genderFemale': '♀ Kvinne',
  'seriespill.classSettings.genderMix': '⚥ Mix',
  'seriespill.classSettings.newTag': 'NY',
  'seriespill.classSettings.pick': 'Velg →',
  'seriespill.classSettings.addPair': '🤝 Legg til par',
  'seriespill.classSettings.pairCount': '{count} par',
  'seriespill.classSettings.delete': 'Slett',
  'seriespill.classSettings.upDown': 'Opp-/nedrykk',
  'seriespill.classSettings.upDownAutoTitle':
    'Auto: regnes ut fra antall lag i divisjonen. Slå av for å sette egne tall.',
  'seriespill.classSettings.upDownAuto': '⚙ Auto',
  'seriespill.classSettings.upDownManual': 'Manuell',
  'seriespill.classSettings.autoUpCount': '{count} opp',
  'seriespill.classSettings.autoDownCount': '{count} ned',
  'seriespill.classSettings.teamCountSuffix': ' · {count} lag',
  'seriespill.classSettings.promote': 'Opprykk',
  'seriespill.classSettings.relegate': 'Nedrykk',
  'seriespill.classSettings.divDeuce': 'Tellemåte (40–40)',
  'seriespill.classSettings.inherit': 'Arv',
  'seriespill.classSettings.deuceGoldenShort': 'Gull',
  'seriespill.classSettings.deuceStarpointShort': 'Star',
  'seriespill.classSettings.fmtSuperTieShort': 'Super-tie',
  'seriespill.classSettings.fmtFullThirdShort': 'Fullt 3.',
  'seriespill.classSettings.divMixServe': 'Mix-serve',
  'seriespill.classSettings.on': 'På',
  'seriespill.classSettings.off': 'Av',
  'seriespill.classSettings.divRuleNote': 'Regel-notat (divisjon)',
  'seriespill.classSettings.divRuleNotePlaceholder': 'Valgfritt — vises i divisjonens regler',
  'seriespill.classSettings.noPairs': 'Ingen par. Bruk «Legg til par» over.',
  'seriespill.classSettings.removePair': 'Fjern',
  'seriespill.classSettings.cancel': 'Avbryt',
  'seriespill.classSettings.save': 'Lagre endringer',
  // ── the league-wide (shared) settings ───────────────────────────────
  'seriespill.mainSettings.title': 'Hovedinnstillinger',
  'seriespill.mainSettings.lead':
    'Gjelder hele serien. Klassene arver fra disse hvis de ikke har egne overstyringer.',
  'seriespill.mainSettings.close': 'Lukk',
  'seriespill.mainSettings.tabSettings': '⚙ Innstillinger',
  'seriespill.mainSettings.tabBooking': '🎾 Banebooking',
  'seriespill.mainSettings.name': 'Navn på serien',
  // The warning shown when a main setting moves under a class override.
  'seriespill.mainSettings.ovrMatchFormat': 'Kampformat',
  'seriespill.mainSettings.ovrDeuce': 'Tellemåte ved 40–40',
  'seriespill.mainSettings.ovrMixServe': 'Mix-serveregel',
  'seriespill.mainSettings.ovrRoundRobin': 'Round-robin',
  'seriespill.mainSettings.ovrEloMultiplier': 'ELO-multiplikator',
  'seriespill.mainSettings.ovrUnplayed': 'Uspilte kamper',
  'seriespill.mainSettings.ovrReveal': 'Annonsering',
  'seriespill.mainSettings.ovrRuleNote': 'Regelnotat',
  'seriespill.mainSettings.ovrDialogTitle': 'Klasser har egne overstyringer',
  'seriespill.mainSettings.ovrDialogLead':
    'Du endret innstillinger som noen klasser har overstyrt. Velg per klasse om den skal beholde sin egen verdi eller følge den nye hovedinnstillingen.',
  'seriespill.mainSettings.ovrClassValue': 'Klassens verdi: ',
  'seriespill.mainSettings.ovrMainValue': ' · Ny hovedinnstilling:',
  'seriespill.mainSettings.ovrKeep': 'Behold klassens',
  'seriespill.mainSettings.ovrFollow': 'Følg hovedinnstilling',
  'seriespill.mainSettings.ovrConfirm': 'Bekreft og lagre',
  'seriespill.mainSettings.fmtFullSet': 'Fullt sett',
  'seriespill.mainSettings.fmtOneSet': 'Ett sett',
  'seriespill.mainSettings.fmtShortSet': 'Kort sett',
  'seriespill.mainSettings.courtModeHeading': 'Banemodus',
  'seriespill.mainSettings.courtFree': 'Fri booking',
  'seriespill.mainSettings.courtFreeHint': 'Parene avtaler og booker bane selv.',
  'seriespill.mainSettings.courtAllocated': 'Allokerte baner',
  'seriespill.mainSettings.courtAllocatedHint': 'Baner settes av på faste dager (kalender).',
  'seriespill.mainSettings.perksHeading': 'Fri booking — fordeler for seriespill-lag',
  'seriespill.mainSettings.perksLead':
    'Når lag booker bane til en seriekamp fra kampprogrammet, kan de få rabatt på ordinær banepris og booke lengre frem i tid enn vanlige medlemmer. Gjelder alle klasser med fri booking.',
  'seriespill.mainSettings.discount': 'Rabatt (%)',
  'seriespill.mainSettings.discountPlaceholder': 'f.eks. 10',
  'seriespill.mainSettings.window': 'Bookingvindu (dager frem)',
  'seriespill.mainSettings.windowPlaceholder': 'f.eks. 14',
  'seriespill.mainSettings.maxActive': 'Maks aktive reservasjoner per spiller',
  'seriespill.mainSettings.maxActivePlaceholder': 'f.eks. 2',
  'seriespill.mainSettings.maxActiveLabel': 'Maks aktive reservasjoner:',
  'seriespill.mainSettings.maxActiveA': ' Begrenser hvor mange',
  'seriespill.mainSettings.maxActiveEm': 'kommende (uspilte)',
  'seriespill.mainSettings.maxActiveB':
    ' baner et lag kan ha reservert om gangen. Grensen gjelder ',
  'seriespill.mainSettings.maxActiveStrong': 'hver av de fire spillerne',
  'seriespill.mainSettings.maxActiveC':
    ' i kampen — har én av dem allerede dette antallet kommende reservasjoner, må de spille av en av dem før de kan reservere bane til neste kamp. Tomt = ingen grense.',
  'seriespill.mainSettings.emptyFieldsNote':
    'Tomt felt = ingen rabatt / standard bookingvindu (7 dager) / ingen grense. Gjelder kun fri booking.',
  'seriespill.mainSettings.perClassHeading': 'Overstyr per klasse (valgfritt)',
  'seriespill.mainSettings.perClassNote':
    'Tomt felt = bruker serie-standarden over ({disc}% rabatt / {win} dager).',
  'seriespill.mainSettings.discountShort': 'Rabatt %',
  'seriespill.mainSettings.windowShort': 'Dager frem',
  'seriespill.mainSettings.matchDuration': 'Varighet per kamp (felles)',
  'seriespill.mainSettings.minutes': '{min} min',
  'seriespill.mainSettings.bookingLinkLead': 'Spilledager og baner settes opp under',
  'seriespill.mainSettings.bookingLinkLabel': 'Banebooking',
  'seriespill.mainSettings.slotRoomCount': '. {count} tidsrom satt opp.',
  'seriespill.mainSettings.seasonDefaults': 'Default sesong-innstillinger',
  'seriespill.mainSettings.seasonDefaultsHint':
    'Klassene bruker disse hvis de ikke har egne overstyringer.',
  'seriespill.mainSettings.seasonStart': 'Sesongstart',
  'seriespill.mainSettings.fixedCalendarNote':
    '📆 Fast kalender: kampene får faste datoer, klokkeslett og baner fra booking-kalenderen ved reservering. Ingen rundefrist.',
  'seriespill.mainSettings.rollingDeadlines': 'Rullerende rundefrister',
  'seriespill.mainSettings.fixedDeadline': 'Fast sesongfrist',
  'seriespill.mainSettings.seasonDeadline': 'Sesongfrist',
  'seriespill.mainSettings.estimatedEnd': 'Estimert sesongslutt',
  'seriespill.mainSettings.estimatedEndNA': 'Beregnes når par/runder finnes',
  'seriespill.mainSettings.roundDeadlineRolling': 'Rundefrist (rullerende)',
  'seriespill.mainSettings.every': 'Hver',
  'seriespill.mainSettings.unitDays': 'dag(er)',
  'seriespill.mainSettings.unitWeeks': 'uke(r)',
  'seriespill.mainSettings.unitMonths': 'måned(er)',
  'seriespill.mainSettings.autoDeadlineNote':
    'Sesongfristen settes automatisk til estimert sesongslutt',
  'seriespill.mainSettings.autoDeadlineDate': ' ({date})',
  'seriespill.mainSettings.unplayedLabel': 'Uspilte kamper ved avsluttet runde',
  'seriespill.mainSettings.unplayedWo': 'Begge taper 0–2 (WO)',
  'seriespill.mainSettings.unplayedWoHint': 'Begge lag får tap. Påvirker ikke ELO.',
  'seriespill.mainSettings.unplayedDraw': 'Uavgjort',
  'seriespill.mainSettings.unplayedDrawHint':
    'Registreres som uavgjort. 0 poeng til hver, teller som spilt. Påvirker ikke ELO.',
  'seriespill.mainSettings.roundEndLabel': 'Avslutning av runde',
  'seriespill.mainSettings.roundEndManual': 'Manuelt',
  'seriespill.mainSettings.roundEndManualHint':
    'Du avslutter runden selv med «Avslutt runde»-knappen i kampprogrammet.',
  'seriespill.mainSettings.roundEndAuto': 'Automatisk ved frist',
  'seriespill.mainSettings.roundEndAutoHint':
    'Runden avsluttes automatisk når rundefristen passerer. Uspilte kamper får resultatet valgt over.',
  'seriespill.mainSettings.revealLabel': 'Annonsering av runder',
  'seriespill.mainSettings.revealAll': 'Alle runder',
  'seriespill.mainSettings.revealAllHint': 'Hele kampprogrammet er synlig fra start.',
  'seriespill.mainSettings.revealRolling': 'Før hver runde',
  'seriespill.mainSettings.revealRollingHint': 'Fremtidige runder annonseres rett før de starter.',
  'seriespill.mainSettings.announce': 'Annonser',
  'seriespill.mainSettings.beforeRoundStarts': 'før runden starter',
  'seriespill.mainSettings.show': 'Vis',
  'seriespill.mainSettings.roundsAhead': 'runde(r) i forkant',
  'seriespill.mainSettings.roundRobinLabel': 'Round-robin-format',
  'seriespill.mainSettings.singleRound': 'Enkel',
  'seriespill.mainSettings.doubleRound': 'Dobbel',
  'seriespill.mainSettings.homeAway': '(h/b)',
  'seriespill.mainSettings.everyPairMeets': 'Alle par møter alle ',
  'seriespill.mainSettings.onceStrong': 'én gang',
  'seriespill.mainSettings.twiceStrong': 'to ganger',
  'seriespill.mainSettings.rulesHeading': 'Spilleregler (serie-standard)',
  'seriespill.mainSettings.rulesHint':
    'Klasser og divisjoner arver disse hvis de ikke har egne avvik.',
  'seriespill.mainSettings.matchFormatLabel': 'Kampformat',
  'seriespill.mainSettings.fmtSuperTiebreak': 'Super-tiebreak',
  'seriespill.mainSettings.fmtSuperTiebreakHint': 'Best av 3, super-tiebreak til 10 ved 1–1 (FIP).',
  'seriespill.mainSettings.fmtFullThird': 'Fullt 3. sett',
  'seriespill.mainSettings.fmtFullThirdHint': 'Best av 3, fullt tredje sett til 6.',
  'seriespill.mainSettings.deuceLabel': 'Tellemåte ved 40–40',
  'seriespill.mainSettings.deuceAdvantage': 'Fordel',
  'seriespill.mainSettings.deuceGolden': 'Gullpoeng',
  'seriespill.mainSettings.deuceStarpoint': 'Starpoint',
  'seriespill.mainSettings.mixServeLabel': 'Mix: serveregel',
  'seriespill.mainSettings.mixServeFree': 'Fri',
  'seriespill.mainSettings.mixServeSame': 'Dame↔dame',
  'seriespill.mainSettings.mixServeHint': 'Dame server mot dame, herre mot herre.',
  'seriespill.mainSettings.on': 'På',
  'seriespill.mainSettings.off': 'Av',
  'seriespill.mainSettings.sharedNoteLabel': 'Felles regelnotat (markdown)',
  'seriespill.mainSettings.fullscreenEditor': '✎ Fullskjerm-editor',
  'seriespill.mainSettings.notePlaceholder':
    'Skriv fritt, eller bruk fullskjerm-editoren. Markdown: **fet**, *kursiv*, # overskrift, - punkt, [tekst](url), ![alt](bilde-url).',
  'seriespill.mainSettings.noteEditorTitle': 'Felles regelnotat',
  'seriespill.mainSettings.eloWeightHeading': 'ELO-vekt for serien',
  'seriespill.mainSettings.eloWeightHint':
    'Bestemmer hvor mye kampene i denne serien påvirker spillernes ELO. Endring i etterkant gjelder kun nye kamper. Standard for seriespill er 200 % (full sesong, vektes tyngst).',
  'seriespill.mainSettings.autoMode': '⚡ Auto-modus',
  'seriespill.mainSettings.autoModeHint':
    'Når en klasse låses (alle har spilt eller deadline passerer), starter neste sesong automatisk uten admin-bekreftelse. Motoren bruker standard opp/nedrykk.',
  'seriespill.mainSettings.pauseHeading': 'Pause serie',
  'seriespill.mainSettings.pauseLead':
    'Skjul serien fra hjemskjermen midlertidig. Kamper, par og standings bevares — du kan gjenoppta serien senere uten å miste data. Ingen ELO commitres.',
  'seriespill.mainSettings.pauseAction': '⏸ Sett serie på pause',
  'seriespill.mainSettings.retireHeading': 'Pensjonér serie',
  'seriespill.mainSettings.retireLead':
    'Avslutter serien permanent. ELO commitres til spiller-DB-en og serien arkiveres. Ingen flere sesonger.',
  'seriespill.mainSettings.retireConfirm': 'Bekreft pensjonering',
  'seriespill.mainSettings.retireAction': 'Pensjonér serie',
  'seriespill.mainSettings.cancel': 'Avbryt',
  'seriespill.mainSettings.save': 'Lagre endringer',
  // ── the new-league setup wizard ─────────────────────────────────────
  'seriespill.setup.back': 'Tilbake',
  'seriespill.setup.title': 'Nytt seriespill',
  'seriespill.setup.lead':
    'Faste par i divisjoner, round-robin (alle møter alle). Opp-/nedrykk ved sesongslutt — serien lever videre.',
  'seriespill.setup.step1': 'Grunninfo',
  'seriespill.setup.step2': 'Påmelding',
  'seriespill.setup.step3': 'Forhåndsvisning',
  // Errors the wizard puts under the step navigation.
  'seriespill.setup.errPickTwo': 'Velg to spillere for paret.',
  'seriespill.setup.errSamePlayer': 'Velg to ulike spillere.',
  'seriespill.setup.thePlayer': 'Spilleren',
  'seriespill.setup.errAlreadyInClass':
    '{name} er allerede påmeldt i denne klassen. En spiller kan kun delta i én divisjon per klasse.',
  'seriespill.setup.errPickClassTab': 'Velg klasse-fane først.',
  'seriespill.setup.errNoFreePlayers': 'Ingen ledige spillere igjen i databasen.',
  'seriespill.setup.errDivisionsFull': 'Alle divisjoner er allerede fulle ({perDiv} par hver).',
  'seriespill.setup.errNotEnoughPlayers':
    'Ikke nok ledige spillere til å lage par i denne klassen.',
  'seriespill.setup.errPartialAdd':
    'La til {added} av {wanted} par — ikke flere ledige spillere med riktig kjønn.',
  'seriespill.setup.errPickClass': 'Velg minst én klasse.',
  'seriespill.setup.errNameAndClass': 'Velg navn og minst én klasse.',
  // Per-division warnings on the sign-up step.
  'seriespill.setup.issueEmpty': 'Tom — påmelding kan legges til senere',
  'seriespill.setup.issueOnePair': 'Bare 1 par i {level}. divisjon — trenger minst 2 for å spille',
  'seriespill.setup.issueTooFew': '{count} par i {level}. divisjon — anbefalt minst 4',
  'seriespill.setup.issueTooMany':
    '{count} par i {level}. divisjon — anbefalt maks 10 for håndterlig sesonglengde',
  'seriespill.setup.nameLabel': 'Navn',
  'seriespill.setup.namePlaceholder': 'Seriespill 2026',
  'seriespill.setup.classesLabel': 'Klasser',
  'seriespill.setup.classesHint':
    'Velg minst én klasse å spille i — trykk på et kort for å aktivere det.',
  'seriespill.setup.selected': 'Valgt',
  'seriespill.setup.notSelected': 'Ikke valgt',
  'seriespill.setup.divisionCount': 'Antall divisjoner:',
  'seriespill.setup.courtsLabel': 'Baner',
  'seriespill.setup.courtFree': 'Fri booking',
  'seriespill.setup.courtFreeHint': 'Parene avtaler og booker bane selv.',
  'seriespill.setup.courtAllocated': 'Allokerte baner',
  'seriespill.setup.courtAllocatedHint': 'Baner settes av på faste dager (kalender).',
  'seriespill.setup.playingDays': 'Faste spilledager (gjentas hver uke)',
  'seriespill.setup.timeWindow': 'Tidsvindu',
  'seriespill.setup.matchDuration': 'Varighet per kamp',
  'seriespill.setup.minutes': '{min} min',
  'seriespill.setup.courtsReserved': 'Antall baner avsatt',
  'seriespill.setup.pickADay': 'Velg minst én spilledag. 1 slot = 1 kamp.',
  'seriespill.setup.slotSummaryOne':
    '{slots} slot per bane × {courts} baner = {matches} kamp per spilledag. Kampene plasseres automatisk når serien starter.',
  'seriespill.setup.slotSummaryMany':
    '{slots} slot per bane × {courts} baner = {matches} kamper per spilledag. Kampene plasseres automatisk når serien starter.',
  'seriespill.setup.seasonStart': 'Sesongstart',
  'seriespill.setup.deadlineLabel': 'Frist',
  'seriespill.setup.rollingDeadline': 'Rullerende rundefrist',
  'seriespill.setup.fixedDeadline': 'Fast sesongfrist',
  'seriespill.setup.newRoundEvery': 'Ny runde hver',
  'seriespill.setup.unitDays': 'dag(er)',
  'seriespill.setup.unitWeeks': 'uke(r)',
  'seriespill.setup.unitMonths': 'måned(er)',
  'seriespill.setup.estEndLabel': 'Estimert sesongslutt:',
  'seriespill.setup.estEndRounds': ' · største divisjon trenger {rounds} runder.',
  'seriespill.setup.estEndUnknown':
    'Estimert sesongslutt beregnes når sesongstart og minst én divisjon med ≥2 par er satt.',
  'seriespill.setup.fixedDeadlineHint':
    'Fast dato for hele sesongen. Ingen rullerende rundefrister.',
  'seriespill.setup.revealLabel': 'Annonsering av runder',
  'seriespill.setup.revealRolling': 'Før hver runde',
  'seriespill.setup.revealRollingHint': 'Fremtidige runder annonseres rett før de starter.',
  'seriespill.setup.revealAll': 'Alle runder',
  'seriespill.setup.revealAllHint': 'Hele kampprogrammet er synlig fra start.',
  'seriespill.setup.revealAllNote': 'Hele kampprogrammet vises fra start.',
  'seriespill.setup.announce': 'Annonser',
  'seriespill.setup.beforeRoundStarts': 'før runden starter',
  'seriespill.setup.show': 'Vis',
  'seriespill.setup.roundsAhead': 'runde(r) i forkant',
  'seriespill.setup.roundRobinLabel': 'Round-robin-format',
  'seriespill.setup.singleRound': 'Enkel',
  'seriespill.setup.doubleRound': 'Dobbel',
  'seriespill.setup.homeAway': '(hjemme/borte)',
  'seriespill.setup.everyPairMeets': 'Hvert par møter hvert annet par ',
  'seriespill.setup.onceStrong': 'én gang',
  'seriespill.setup.twiceStrong': 'to ganger',
  'seriespill.setup.singleRoundHint': '. Kortere sesong, færre kamper.',
  'seriespill.setup.doubleRoundHint': '. Som fotball-liga — dobler antall kamper.',
  'seriespill.setup.matchFormatLabel': 'Kampformat',
  'seriespill.setup.fmtSuperTiebreak': 'Super-tiebreak',
  'seriespill.setup.fmtSuperTiebreakA': 'Best av 3. Står det ',
  'seriespill.setup.fmtSuperTiebreakStrong': '1–1 i sett',
  'seriespill.setup.fmtSuperTiebreakB': ', avgjøres kampen med super-tiebreak til 10 (FIP).',
  'seriespill.setup.fmtFullSet': 'Fullt sett',
  'seriespill.setup.fmtFullSetA': 'Best av 3. Et avgjørende tredje sett spilles som et',
  'seriespill.setup.fmtFullSetStrong': 'fullt sett til 6 game',
  'seriespill.setup.deuceLabel': 'Tellemåte ved 40–40',
  'seriespill.setup.deuceAdvantage': 'Fordel',
  'seriespill.setup.deuceAdvantageTag': '(vanlig)',
  'seriespill.setup.deuceAdvantageA': 'Vinn ',
  'seriespill.setup.deuceAdvantageStrong': 'to poeng på rad',
  'seriespill.setup.deuceAdvantageB': ' ved 40–40.',
  'seriespill.setup.deuceGolden': 'Gullpoeng',
  'seriespill.setup.deuceGoldenStrong': 'Ett',
  'seriespill.setup.deuceGoldenHint': ' avgjørende poeng ved første 40–40.',
  'seriespill.setup.deuceStarpoint': 'Starpoint',
  'seriespill.setup.deuceStarpointA': 'Fordel de to første gangene, ',
  'seriespill.setup.deuceStarpointStrong': 'gullpoeng ved tredje',
  'seriespill.setup.deuceStarpointB': ' 40–40.',
  'seriespill.setup.unplayedLabel': 'Uspilte kamper ved avsluttet runde',
  'seriespill.setup.unplayedWo': 'Begge taper 0–2 (WO)',
  'seriespill.setup.unplayedWoHint': 'Begge lag får tap. Påvirker ikke ELO.',
  'seriespill.setup.unplayedDraw': 'Uavgjort',
  'seriespill.setup.unplayedDrawHint':
    'Registreres som uavgjort. 0 poeng til hver, teller som spilt. Påvirker ikke ELO.',
  'seriespill.setup.roundEndLabel': 'Avslutning av runde',
  'seriespill.setup.roundEndManual': 'Manuelt',
  'seriespill.setup.roundEndManualHint':
    'Du avslutter runden selv med «Avslutt runde»-knappen i kampprogrammet.',
  'seriespill.setup.roundEndAuto': 'Automatisk ved frist',
  'seriespill.setup.roundEndAutoHint':
    'Runden avsluttes automatisk når rundefristen passerer. Uspilte kamper får resultatet valgt over.',
  'seriespill.setup.pairCount': '{count} par',
  'seriespill.setup.pairCountOk': '· OK',
  'seriespill.setup.addPairIn': 'Legg til par i {name}',
  'seriespill.setup.demo': 'Demo',
  'seriespill.setup.demoAdd': '+ {count} par',
  'seriespill.setup.demoAddTitle': 'Legg til {count} par fra ledige spillere i databasen',
  'seriespill.setup.playerSlot': 'Spiller {slot}',
  'seriespill.setup.pickBelow': '(velg nedenfor)',
  'seriespill.setup.newSuffix': ' · Ny',
  'seriespill.setup.clearPlayer': 'Fjern valgt spiller',
  'seriespill.setup.searchBelow': 'Søk under for å velge…',
  'seriespill.setup.putIn': 'Legg i',
  'seriespill.setup.divisionName': '{level}. divisjon',
  'seriespill.setup.divisionShort': '{level}. div',
  'seriespill.setup.searchPlayer': 'Søk etter spiller {slot}…',
  'seriespill.setup.newPlayer': '+ Ny spiller',
  'seriespill.setup.includeWomen': 'Søk også blant damer (damer kan delta i herreklassen)',
  'seriespill.setup.noMatchesA': 'Ingen treff på "',
  'seriespill.setup.noMatchesB': '".',
  'seriespill.setup.createPlayer': 'Opprett "{name}" som ny spiller',
  'seriespill.setup.noFreePlayers': 'Ingen flere ledige spillere — alle er allerede valgt.',
  'seriespill.setup.genderMale': '♂ Mann',
  'seriespill.setup.genderFemale': '♀ Kvinne',
  'seriespill.setup.genderMix': '⚥ Mix',
  'seriespill.setup.newTag': 'NY',
  'seriespill.setup.pick': 'Velg →',
  'seriespill.setup.addPair': '🤝 Legg til par',
  'seriespill.setup.pickHint': 'Søk og klikk «Velg →» for å fylle spiller 1 og 2',
  'seriespill.setup.pickHintDivisions':
    '. Standard divisjon velges over — du kan flytte par mellom divisjoner i listen under',
  'seriespill.setup.noPairsHere': 'Ingen par her ennå. Bruk «Legg til par» over.',
  'seriespill.setup.removePair': 'Fjern',
  'seriespill.setup.readyTitle': 'Klar til å starte',
  'seriespill.setup.sumName': 'Navn',
  'seriespill.setup.sumClasses': 'Klasser',
  'seriespill.setup.sumDivisions': 'Totalt divisjoner',
  'seriespill.setup.sumPairs': 'Totalt par',
  'seriespill.setup.sumMatches': 'Kamper denne sesongen',
  'seriespill.setup.sumCourtMode': 'Banemodus',
  'seriespill.setup.sumPlayingDays': 'Spilledager',
  'seriespill.setup.sumTimeWindow': '{start}–{end} · {minutes} min · {courts} baner',
  'seriespill.setup.sumFormat': 'Format',
  'seriespill.setup.sumFormatDouble': 'Dobbel (hjemme/borte)',
  'seriespill.setup.sumFormatSingle': 'Enkel (alle møter alle)',
  'seriespill.setup.sumDeuce': 'Ved 40–40',
  'seriespill.setup.sumRoundDeadline': 'Rundefrist',
  'seriespill.setup.sumEvery': 'Hver {value} {unit}',
  'seriespill.setup.sumEstimatedEnd': 'Sesongslutt (estimert)',
  'seriespill.setup.sumSeasonDeadline': 'Sesongfrist',
  'seriespill.setup.divPairSummary': '{level}. div: {count} par',
  'seriespill.setup.recommendations': 'Anbefalinger ({count})',
  'seriespill.setup.recommendationLine': '• {name}: {msg}',
  'seriespill.setup.recommendationNote':
    'Anbefalt 4–10 par per divisjon. Du kan starte uansett — det er bare en pekepinn.',
  'seriespill.setup.status': 'Status',
  'seriespill.setup.statDivisions': 'Divisjoner',
  'seriespill.setup.statPairs': 'Par',
  'seriespill.setup.statMatches': 'Kamper',
  'seriespill.setup.hardErrorsNote': '⚠ {count} divisjon(er) trenger flere par',
  'seriespill.setup.softWarningsNote': '! {count} anbefaling(er) — se steg 3',
  'seriespill.setup.guidance': 'Pekepinn',
  'seriespill.setup.guidanceNote':
    'Anbefalt 4–10 par per divisjon. Færre gir få kamper, flere gir lange sesonger. Du kan justere etterpå.',
  'seriespill.setup.cancel': '← Avbryt',
  'seriespill.setup.previous': '← Forrige',
  'seriespill.setup.next': 'Neste →',
  'seriespill.setup.create': 'Opprett serie',
  'seriespill.setup.pausedTitle': 'Pausede serier ({count})',
  'seriespill.setup.pausedLead':
    'Du har serier som er satt på pause. Du kan gjenoppta én av dem i stedet for å opprette en ny.',
  'seriespill.setup.pausedClassOne': '{count} klasse',
  'seriespill.setup.pausedClassMany': '{count} klasser',
  'seriespill.setup.pausedPairs': ' · {count} par ·',
  'seriespill.setup.pausedAt': ' pauset {date}',
  'seriespill.setup.resume': 'Gjenoppta →',
  'seriespill.setup.deletePaused': 'Slett',
  'seriespill.setup.deletePausedTitle': 'Slett pauset serie permanent',
  'seriespill.setup.confirmDeletePaused': 'Slette denne pausede serien permanent?',
  // Month names in full, for the single-date summary in the booking panel.
  'seriespill.date.monthLong0': 'januar',
  'seriespill.date.monthLong1': 'februar',
  'seriespill.date.monthLong2': 'mars',
  'seriespill.date.monthLong3': 'april',
  'seriespill.date.monthLong4': 'mai',
  'seriespill.date.monthLong5': 'juni',
  'seriespill.date.monthLong6': 'juli',
  'seriespill.date.monthLong7': 'august',
  'seriespill.date.monthLong8': 'september',
  'seriespill.date.monthLong9': 'oktober',
  'seriespill.date.monthLong10': 'november',
  'seriespill.date.monthLong11': 'desember',
  // ── the court-booking panel (time slots, reserving, placing matches) ───
  'seriespill.booking.heading': 'Banebooking · tidsrom',
  'seriespill.booking.courtN': 'Bane {num}',
  'seriespill.booking.minutes': '{min} min',
  'seriespill.booking.modeIntegrated': 'Bruk Banebooking',
  'seriespill.booking.modeIntegratedHint':
    'Reserverer i Padelsenter-kalenderen. Sjekker at banene er ledige.',
  'seriespill.booking.modeStandalone': 'Frittstående',
  'seriespill.booking.modeStandaloneHint':
    'Reserverer ingenting i kalenderen og sjekker ikke ledighet. Du angir selv tid, antall baner og sted — f.eks. på et annet senter.',
  'seriespill.booking.startsFrom': 'Bookinger starter fra',
  'seriespill.booking.usesSeasonStart': '(bruker sesongstart)',
  'seriespill.booking.roomsLeadSeries':
    'Hvert tidsrom er et sett faste spilledager. Et tidsrom kan dekke én eller flere klasser. ',
  'seriespill.booking.roomsLeadSingle': 'Velg dato, starttid og baner. ',
  'seriespill.booking.sharedDuration': 'Felles varighet: ',
  'seriespill.booking.sharedDurationTail': ' (endres under Innstillinger). 1 slot = 1 kamp.',
  'seriespill.booking.courtNumbersMustMatch': ' Banenumrene må matche klubbens baner.',
  'seriespill.booking.uncoveredClasses': '⚠ Uten tidsrom: {classes} — disse får ingen slots.',
  // The needs panel: how many slots the setup books against how many matches there are.
  'seriespill.booking.playsLead': 'Hver spiller spiller',
  'seriespill.booking.playsPerMonthOne': ' kamp/mnd med dagens oppsett',
  'seriespill.booking.playsPerMonthMany': ' kamper/mnd med dagens oppsett',
  'seriespill.booking.playsDetail': '({players} spillere · ≈ {slots} slots/mnd)',
  'seriespill.booking.seasonMatchesLead': '{count} kamper denne sesongen',
  'seriespill.booking.seasonMatchesClasses': ' ({classes})',
  'seriespill.booking.seasonMatchesNeed': ' → trenger {count} slots. Du booker',
  'seriespill.booking.slotsSurplus':
    '⚠ {count} flere enn nødvendig — du kan redusere antall slotter/baner/dager.',
  'seriespill.booking.slotsShort': '⚠ {count} for få til å spille alle kamper i sesongen.',
  'seriespill.booking.slotsExact': '✓ Akkurat nok.',
  // Errors from reserving and placing.
  'seriespill.booking.errNoDates': 'Mangler startdato eller sesongfrist — sett disse først.',
  'seriespill.booking.errReadCalendar': 'Kunne ikke lese kalenderen.',
  'seriespill.booking.errNoSlots': 'Ingen slots — sjekk tidsrom, dager og datoer.',
  'seriespill.booking.errNoReserved': 'Ingen reserverte slots — reserver først.',
  'seriespill.booking.errPlace': 'Kunne ikke plassere kampene.',
  'seriespill.booking.errClearPlacement': 'Kunne ikke nullstille plasseringen.',
  'seriespill.booking.errClearBookings': 'Kunne ikke fjerne bookingene.',
  'seriespill.booking.errReserveAndPlace': 'Kunne ikke reservere og plassere.',
  'seriespill.booking.errRemoveSurplus': 'Kunne ikke fjerne overskytende bookinger.',
  'seriespill.booking.reservationFallback': 'Reservasjon',
  // Holidays.
  'seriespill.booking.holidaysSkipped': 'Helligdager som hoppes over ({country})',
  'seriespill.booking.noHolidays': 'Ingen helligdager i sesongperioden.',
  'seriespill.booking.dayMonth': '{day}. {month}',
  'seriespill.booking.holidayNameSuffix': ' · {name}',
  'seriespill.booking.holidaySetNote':
    'Samme helligdagssett som bookingene. Legg til / fjern dager under booking-innstillinger → Helligdager.',
  'seriespill.booking.holidayLanding': 'Når en dato lander på en helligdag',
  'seriespill.booking.holidayInclude': 'Ta med likevel',
  'seriespill.booking.holidaySkip': 'Hopp over',
  'seriespill.booking.holidaySet': 'Helligdagssett: {country} (endres i innstillinger).',
  // One time slot: its summary line and its editor.
  'seriespill.booking.noDate': 'ingen dato',
  'seriespill.booking.noDays': 'ingen dager',
  'seriespill.booking.noClasses': 'ingen klasser',
  'seriespill.booking.everyNthWeek': ' (hver {n}. uke)',
  'seriespill.booking.freqDaily': 'Daglig',
  'seriespill.booking.freqWeekly': 'Ukentlig',
  'seriespill.booking.freqMonthly': 'Månedlig',
  'seriespill.booking.freqYearly': 'Årlig',
  'seriespill.booking.roomFallback': 'Tidsrom {n}',
  'seriespill.booking.standardRoom': 'Standard tidsrom',
  'seriespill.booking.slotsByCourts': '{count} slot × {courts} bane',
  'seriespill.booking.courtsOnly': '{courts} bane',
  'seriespill.booking.plannedSlots': '{count} slots',
  'seriespill.booking.bookedCount': '{count} booket',
  'seriespill.booking.freeCountOne': '· {count} ledig',
  'seriespill.booking.freeCountMany': '· {count} ledige',
  'seriespill.booking.collapse': '▲ skjul',
  'seriespill.booking.expand': '▼ tune',
  'seriespill.booking.removeRoom': 'Fjern',
  'seriespill.booking.recurring': 'Gjentakende booking',
  'seriespill.booking.repeats': 'Gjentas',
  'seriespill.booking.every': 'Hver',
  'seriespill.booking.unitDays': 'dag(er)',
  'seriespill.booking.unitWeeks': 'uke(r)',
  'seriespill.booking.unitMonths': 'måned(er)',
  'seriespill.booking.unitYears': 'år',
  'seriespill.booking.onDays': 'På dager',
  'seriespill.booking.endsLabel': 'Slutter',
  'seriespill.booking.endAfterCount': 'Etter antall',
  'seriespill.booking.endOnDate': 'På dato',
  'seriespill.booking.endNever': 'Aldri',
  'seriespill.booking.occurrences': 'forekomster',
  'seriespill.booking.repeatsUntilDeadline': 'Gjentas til sesongfristen ({date}).',
  'seriespill.booking.date': 'Dato',
  'seriespill.booking.from': 'Fra',
  'seriespill.booking.to': 'til',
  'seriespill.booking.pickADate': 'Velg en dato',
  'seriespill.booking.pickADateDot': 'Velg en dato.',
  'seriespill.booking.pickADay': 'Velg minst én spilledag.',
  'seriespill.booking.niceDate': '{wd} {day}. {month} {year}',
  'seriespill.booking.durHours': '{h}t',
  'seriespill.booking.durMinutes': ' {m}min',
  'seriespill.booking.endBeforeStart': '⚠ Sluttid må være etter start',
  'seriespill.booking.atTime': 'kl. {start}–{end} · {dur}',
  'seriespill.booking.startTime': 'Starttid',
  'seriespill.booking.slotCount': 'Antall slotter',
  'seriespill.booking.slotCountDecrease': 'Færre slotter',
  'seriespill.booking.slotCountIncrease': 'Flere slotter',
  'seriespill.booking.removeCourt': 'Fjern bane {num}',
  'seriespill.booking.end': 'Slutt',
  'seriespill.booking.slotDurationNote': '({min} min/slot — anbefalt, kan justeres)',
  // The day timeline for placing a single-date booking.
  'seriespill.booking.placeInCalendar': 'Plasser i kalenderen',
  'seriespill.booking.prevDay': 'Forrige dag',
  'seriespill.booking.nextDay': 'Neste dag',
  'seriespill.booking.backToToday': 'Tilbake til i dag',
  'seriespill.booking.today': 'I dag',
  'seriespill.booking.showAllCourts': 'Vis alle baner',
  'seriespill.booking.notSelectedSuffix': ' (ikke valgt)',
  'seriespill.booking.busy': 'opptatt',
  'seriespill.booking.dragHint': 'Dra for å flytte · dra kantene for å endre varighet',
  'seriespill.booking.findNextFree': '⏭ Finn neste ledige dag',
  'seriespill.booking.findAnyDay': 'Alle dager',
  'seriespill.booking.findWeekday': 'Kun hverdag',
  'seriespill.booking.findWeekend': 'Kun helg',
  'seriespill.booking.noFreeDay': 'Fant ingen ledig dag innen ett år.',
  'seriespill.booking.searchFailed': 'Kunne ikke søke.',
  'seriespill.booking.freeStartTimes': 'Ledige starttider (klikk for å plassere):',
  'seriespill.booking.noFreeSlotsOne':
    'Ingen ledige slotter for {count} bane i denne varigheten denne dagen.',
  'seriespill.booking.noFreeSlotsMany':
    'Ingen ledige slotter for {count} baner i denne varigheten denne dagen.',
  // Standalone venue fields.
  'seriespill.booking.courtCount': 'Antall baner',
  'seriespill.booking.courtCountOne': '{count} bane',
  'seriespill.booking.courtCountMany': '{count} baner',
  'seriespill.booking.courtCountHint':
    'Hvor mange baner dere har til rådighet (4 spillere per bane). Frittstående sjekker ikke ledighet.',
  'seriespill.booking.whereLabel': 'Hvor spilles det?',
  'seriespill.booking.venuePlaceholder': 'Sted / senter (f.eks. Smaalenene Padelklubb)',
  'seriespill.booking.addressPlaceholder': 'Gateadresse — søk og velg',
  'seriespill.booking.searching': 'søker…',
  'seriespill.booking.addressHint':
    'Søk på gateadresse og velg fra lista — postnummer og poststed fylles inn automatisk (Kartverket/Geonorge, gratis). Krever nett; virker på den publiserte siden. Adressen kan også skrives fritt.',
  // Court selection and the availability legend.
  'seriespill.booking.courtsLabel': 'Baner',
  'seriespill.booking.courtBusyTitle': 'Ikke ledig (allerede booket)',
  'seriespill.booking.courtFreeTitle': 'Ledig',
  'seriespill.booking.courtWasteTitle': 'Skaper ubrukt tid — tillatt som admin',
  'seriespill.booking.gapTitle': '{mins} min hull {from}–{to}',
  'seriespill.booking.busySuffix': '· opptatt',
  'seriespill.booking.pickClubCourts':
    'Velg hvilke av klubbens baner som brukes. Banenavn endres i booking-innstillinger.',
  'seriespill.booking.legendGreen': '● Grønn = ledig',
  'seriespill.booking.legendYellow': '● Gul = bryter smartbooking (tillatt som admin)',
  'seriespill.booking.legendRed': '● Rød = ikke ledig',
  'seriespill.booking.smartBooking': 'Smartbooking:',
  'seriespill.booking.smartOn': 'på',
  'seriespill.booking.smartOff': 'av',
  'seriespill.booking.smartDetail': ' · buffer {buffer} min · varigheter {durations} min',
  'seriespill.booking.smartOffDetail': ' — kun harde kollisjoner sjekkes',
  'seriespill.booking.redSelected':
    '⚠ {courts} er valgt men ikke ledig — disse hoppes over ved reservering. Fjern dem eller velg et annet tidsrom.',
  'seriespill.booking.forcedCourts':
    'ℹ Du trenger flere baner enn det finnes «perfekte» ledige, så {courts} er godkjent selv om de etterlater litt ubrukt tid rundt blokken — uunngåelig når alle banene skal brukes.',
  'seriespill.booking.yellowConsequence':
    '⚠ Konsekvens av valgte gule baner (du booker som admin):',
  'seriespill.booking.wastedGap': '{mins} min ubrukt tid {from}–{to}',
  'seriespill.booking.wastedGapDead': ' (for kort til at noen andre rekker å booke)',
  'seriespill.booking.resultLeaves': 'Resultat — etterlater {gaps}.',
  'seriespill.booking.resultIsolated':
    'Resultat — gir mer oppstykket kalender; banen står isolert fra de andre bookingene (mer ubrukt tid totalt).',
  'seriespill.booking.adminAllowed': 'Det er lov for admin — banen reserveres likevel.',
  'seriespill.booking.courtNamePlaceholder': 'Bane {num} (valgfritt navn)',
  'seriespill.booking.addCourt': '+ Legg til bane',
  // Which classes a time slot covers.
  'seriespill.booking.appliesToClasses': 'Gjelder klasser',
  'seriespill.booking.noAllocatedClasses': 'Ingen klasser er satt til allokerte baner ennå.',
  'seriespill.booking.notAllocatable':
    '{classes} er på fri booking og kan ikke legges til. Endre banemodus i klasse-innstillingene for å gjøre dem tilgjengelige.',
  'seriespill.booking.roomSummaryOne':
    '{slots} slot/bane × {courts} baner = {matches} kamp/dag. {start}–{end}. Starttider: {times}.',
  'seriespill.booking.roomSummaryMany':
    '{slots} slot/bane × {courts} baner = {matches} kamper/dag. {start}–{end}. Starttider: {times}.',
  'seriespill.booking.pastMidnight':
    '⚠ går forbi midnatt — reduser antall slotter eller flytt starttid.',
  'seriespill.booking.oneRoundFor':
    'Én runde for {classes} = {matches} kamper · {slots} slots/uke →',
  'seriespill.booking.weeksBetweenOne': '~{weeks} uke mellom hver runde',
  'seriespill.booking.weeksBetweenMany': '~{weeks} uker mellom hver runde',
  'seriespill.booking.noPairsYet':
    'Ingen par i de valgte klassene ennå — uke-estimat vises når par er lagt til.',
  'seriespill.booking.addRoom': '+ Legg til tidsrom',
  // Reserving and placing.
  'seriespill.booking.checkingCalendar': 'Sjekker kalenderen…',
  'seriespill.booking.reservedOne': '✓ {count} bane reservert',
  'seriespill.booking.reservedMany': '✓ {count} baner reservert',
  'seriespill.booking.updateReservation': 'Oppdater reservasjon',
  'seriespill.booking.reserveCourts': 'Reserver baner',
  'seriespill.booking.placing': 'Plasserer…',
  'seriespill.booking.placeMatches': 'Plasser kampene i slotene',
  'seriespill.booking.clearPlacement': 'Nullstill plassering',
  'seriespill.booking.clearBookings': 'Fjern bookingene',
  'seriespill.booking.reserveHelpWithPlace':
    'Reserver banene først (holder av tidene i Padelsenter-kalenderen). Plasser kampene i et eget steg etterpå (typisk når påmelding er klar). Plassering fyller bare kamper uten dato.',
  'seriespill.booking.reserveHelp': 'Reserver de valgte banene for turneringen i kalenderen.',
  'seriespill.booking.clearedPlacement': '✓ Nullstilte plassering for {count} kamper.',
  'seriespill.booking.clearedBookings': '✓ Fjernet {count} bookinger fra denne serien.',
  'seriespill.booking.placedResult': '✓ {count} kamper fikk dato/tid/bane',
  'seriespill.booking.placedMissing': ' · ⚠ {count} mangler slot',
  'seriespill.booking.standaloneHelp':
    'Frittstående: kampene plasseres på banene/dagene du angir, uten kalender-reservasjon. «Plasser» fyller bare kamper uten dato.',
  // The confirmation before clearing a placement or the bookings.
  'seriespill.booking.confirmTitle': 'Er du sikker?',
  'seriespill.booking.confirmClearA': 'Dette nullstiller dato, tid og bane for',
  'seriespill.booking.confirmClearBOne': ' plassert kamp i serien.',
  'seriespill.booking.confirmClearBMany': ' plasserte kamper i serien.',
  'seriespill.booking.confirmClearNote':
    'Selve banebookingene i kalenderen beholdes — du kan plassere kampene på nytt etterpå. Ferdigspilte kamper berøres ikke.',
  'seriespill.booking.confirmRemoveA': 'Dette fjerner ',
  'seriespill.booking.confirmRemoveBOne':
    'reservert booking for serien fra Padelsenter-kalenderen.',
  'seriespill.booking.confirmRemoveBMany':
    'reserverte bookinger for serien fra Padelsenter-kalenderen.',
  'seriespill.booking.period': 'Periode: {span}',
  'seriespill.booking.confirmRemoveNote':
    'Handlingen kan ikke angres. Plasserte kamper beholder dato/tid/bane til du eventuelt nullstiller plasseringen.',
  'seriespill.booking.cancel': 'Avbryt',
  // The calendar booking list.
  'seriespill.booking.calendarBookings': 'Bookinger i kalenderen',
  'seriespill.booking.showBookings': 'Vis bookinger',
  'seriespill.booking.refresh': 'Oppdater',
  'seriespill.booking.showBookingsHint':
    'Klikk «Vis bookinger» for å se alle reserverte tider i senterets kalender.',
  'seriespill.booking.noBookings': 'Ingen bookinger i kalenderen ennå.',
  'seriespill.booking.pickDateForBookings':
    'Velg dato og tidsrom over for å se relevante bookinger.',
  'seriespill.booking.noBookingsInWindow': 'Ingen bookinger i turneringens tidsrom ennå.',
  'seriespill.booking.relevantOne': '{count} relevant booking ·',
  'seriespill.booking.relevantMany': '{count} relevante bookinger ·',
  'seriespill.booking.fromThisTournament': 'fra denne turneringen.',
  'seriespill.booking.weekdayDayMonth': '{wd} {day}. {month}',
  'seriespill.booking.bookingCountOne': '{count} booking',
  'seriespill.booking.bookingCountMany': '{count} bookinger',
  'seriespill.booking.ofWhichSeries': ' · {count} serie',
  'seriespill.booking.timeAndCourt': '{start}–{end} · bane {court}',
  'seriespill.booking.seriesLabel': 'Serie',
  // The reserve-preview modal.
  'seriespill.booking.reserveModalTitle': 'Reserver i kalenderen',
  'seriespill.booking.willReserveOne': ' bane reserveres i kalenderen',
  'seriespill.booking.willReserveMany': ' baner reserveres i kalenderen',
  'seriespill.booking.andPlaceMatches': ', og kampene plasseres i dem',
  'seriespill.booking.reservationName': 'Reservasjonen får navnet:',
  'seriespill.booking.tournamentId': 'Turnerings-ID: {id}',
  'seriespill.booking.thisIsReserved': 'Dette reserveres:',
  'seriespill.booking.dateTimeCourts': '{date} · kl. {time} · bane {courts}',
  'seriespill.booking.andMoreDays': '… og {count} dager til',
  'seriespill.booking.andMore': '… og {count} til',
  'seriespill.booking.conflictsHeading': '⚠ {count} slots er allerede opptatt og hoppes over:',
  'seriespill.booking.conflictLine': '{date} · bane {court} · {start}–{end}',
  'seriespill.booking.noConflicts': 'Ingen kollisjoner — alle slotene er ledige.',
  'seriespill.booking.working': 'Jobber…',
  'seriespill.booking.reserveCourtsCount': 'Reserver baner ({count})',
  // The status modal after reserving and/or placing.
  'seriespill.booking.statusPlaced': 'Kampene er plassert',
  'seriespill.booking.statusReservedAndPlaced': 'Reservert og plassert',
  'seriespill.booking.statusReserved': 'Baner reservert',
  'seriespill.booking.statSlotsReserved': 'Slots reservert',
  'seriespill.booking.statSlotsAvailable': 'Tilgjengelige slots',
  'seriespill.booking.statSkipped': 'Hoppet over (opptatt)',
  'seriespill.booking.statPlaced': 'Kamper plassert',
  'seriespill.booking.statUnplaced': 'Kamper uten ledig slot',
  'seriespill.booking.statEmpty': 'Tomme slots (overskytende)',
  'seriespill.booking.reservedInCalendar': 'Reservert i kalenderen:',
  'seriespill.booking.freedOne': '✓ Frigjorde {count} overskytende booking i kalenderen.',
  'seriespill.booking.freedMany': '✓ Frigjorde {count} overskytende bookinger i kalenderen.',
  'seriespill.booking.surplusQuestion':
    'Du reserverte flere baner enn det er kamper. Vil du frigjøre de tomme tidene i kalenderen?',
  'seriespill.booking.removing': 'Fjerner…',
  'seriespill.booking.removeSurplus': 'Fjern {count} overskytende',
  'seriespill.booking.done': 'Ferdig',
  // ── the live league screen: standings, schedule, calendar ───────────
  'seriespill.view.kicker': 'Seriespill',
  'seriespill.view.home': 'Hjem',
  'seriespill.view.back': 'Tilbake',
  'seriespill.view.noDivisions': 'Ingen divisjoner i dette seriespillet.',
  'seriespill.view.showPlayer': 'Vis spiller',
  'seriespill.view.matchAction': 'Resultat: {home} mot {away}',
  'seriespill.view.cancel': 'Avbryt',
  'seriespill.view.all': 'Alle',
  'seriespill.view.courtN': 'Bane {court}',
  'seriespill.view.courtsCount': '{count} baner',
  'seriespill.view.courtSuffix': ' · Bane {court}',
  'seriespill.view.weekdayDayMonth': '{wd} {day}. {month}',
  'seriespill.view.season': 'Sesong {season}',
  'seriespill.view.round': 'Runde {round}',
  'seriespill.view.ofRounds': 'av {total}',
  'seriespill.view.divisionName': '{level}. divisjon',
  'seriespill.view.classDivision': '{gender} Divisjon {level}',
  'seriespill.view.today': 'i dag',
  'seriespill.view.inDaysOne': 'om {days} dag',
  'seriespill.view.inDaysMany': 'om {days} dager',
  'seriespill.view.agoDaysOne': '{days} dag siden',
  'seriespill.view.agoDaysMany': '{days} dager siden',
  'seriespill.view.inDaysShort': 'om {days} d',
  'seriespill.view.agoDaysShort': '{days} d siden',
  // The standings table.
  'seriespill.view.tableLabel': 'Tabell',
  'seriespill.view.divAvg': '· Snitt {avg}',
  'seriespill.view.divAvgTitle': 'Divisjonens snitt-rating',
  'seriespill.view.matchesPlayed': ' · {played}/{total} kamper spilt',
  'seriespill.view.noPairsInDivision':
    'Ingen par i denne divisjonen ennå. Bruk «Innstillinger for {name}» øverst for å legge til.',
  'seriespill.view.colSets': 'Sett',
  'seriespill.view.colForm': 'Form',
  'seriespill.view.formWin': 'Seier',
  'seriespill.view.formLoss': 'Tap',
  'seriespill.view.formDraw': 'Uavgjort',
  'seriespill.view.formWinShort': 'V',
  'seriespill.view.formLossShort': 'T',
  'seriespill.view.formDrawShort': 'U',
  // One round card in the schedule.
  'seriespill.view.editRoundStartTitle': 'Klikk for å endre rundestart',
  'seriespill.view.announcedOn': 'Annonseres {date} ✎',
  'seriespill.view.beforeRoundStarts': 'før runden starter',
  'seriespill.view.announceNow': 'Annonser runden nå',
  'seriespill.view.startsOn': 'Starter {date} ✎',
  'seriespill.view.roundDone': 'Fullført',
  'seriespill.view.roundLeft': '{left} av {total} igjen',
  'seriespill.view.activeNow': 'Aktiv nå',
  'seriespill.view.partialHidden':
    'Viser kun spilte kamper — kommende kamper i denne runden annonseres senere.',
  'seriespill.view.avgElo': 'Snitt ELO {elo}',
  'seriespill.view.voided': 'Annullert',
  'seriespill.view.draw': 'Uavgjort',
  'seriespill.view.result': 'Resultat',
  'seriespill.view.courtReserved': 'Bane reservert',
  'seriespill.view.change': 'endre',
  'seriespill.view.maxActiveReached': 'Maks aktive nådd',
  'seriespill.view.maxActiveTitle':
    'Maks aktive reservasjoner nådd for: {names}. Spill av en kommende reservasjon først.',
  'seriespill.view.playerCount': '{count} spillere',
  'seriespill.view.reserveCourt': 'Reserver bane',
  'seriespill.view.endRoundLeadOne': '{count} uspilt kamp får',
  'seriespill.view.endRoundLeadMany': '{count} uspilte kamper får',
  'seriespill.view.unplayedVoided': 'status «annullert» (teller ikke)',
  'seriespill.view.unplayedWo': 'tap 0–2 (WO) til begge lag',
  'seriespill.view.endRoundQuestion': '. Avslutte runde {round}?',
  'seriespill.view.endRoundYes': 'Ja, avslutt runde',
  'seriespill.view.endRoundNow': 'Avslutt runde {round} nå',
  'seriespill.view.editDeadlineTitle': 'Klikk for å endre fristen',
  'seriespill.view.deadlineOn': 'Frist {date}',
  'seriespill.view.autoSuffix': '· auto',
  'seriespill.view.editRoundStart': 'Endre rundestart',
  'seriespill.view.startOn': 'Start {date} ✎',
  'seriespill.view.editDeadline': 'Endre frist',
  'seriespill.view.deadlineWithLeft': 'Frist {date} · {left} ✎',
  'seriespill.view.hideRoundAgain': 'Skjul runden igjen',
  'seriespill.view.unannounce': '↩ Av-annonser',
  'seriespill.view.playedRange': 'Spilles {from}–{to}',
  'seriespill.view.playedOn': 'Spilles {date}',
  'seriespill.view.roundEndsNote': '· rundeslutt når siste kamp er spilt',
  // The schedule, per division and round-major.
  'seriespill.view.scheduleFor': 'Kampprogram — {name}',
  'seriespill.view.scheduleAllDivisions': 'Kampprogram — alle divisjoner',
  'seriespill.view.noPairsHere': 'Ingen par i denne divisjonen.',
  'seriespill.view.noPairsHint':
    'Bruk «Innstillinger for {name}» øverst for å legge til par. Når det er minst 2 par, genereres kampprogrammet automatisk.',
  'seriespill.view.noMatchesYet': 'Ingen kamper ennå.',
  'seriespill.view.hideDoneRounds': 'Skjul fullførte runder',
  'seriespill.view.showDoneRoundsOne': 'Vis {count} fullført runde',
  'seriespill.view.showDoneRoundsMany': 'Vis {count} fullførte runder',
  'seriespill.view.hideFutureRounds': 'Skjul kommende runder',
  'seriespill.view.showFutureRoundsOne': 'Vis {count} kommende runde',
  'seriespill.view.showFutureRoundsMany': 'Vis {count} kommende runder',
  // The team search.
  'seriespill.view.searchTeam': 'Søk lag…',
  'seriespill.view.allTeams': 'Alle lag',
  'seriespill.view.teamsMatch': '{count} lag matcher',
  'seriespill.view.clearTeamFilter': 'Fjern lag-filter',
  // The temporary development tools.
  'seriespill.view.devBanner': '⚠ Midlertidig (under utvikling)',
  'seriespill.view.repairReport':
    '✓ Reparert: {ids} id-er fornyet, {dupes} duplikat fjernet, {relinked} kamper re-linket ({before}→{after} par)',
  'seriespill.view.repairTitle':
    'Gir alle par unike id-er og re-linker kamper (fikser dupliserte par fra eldre data)',
  'seriespill.view.repairAction': '🔧 Reparer par-id-er',
  'seriespill.view.deleteSeries': '🗑 Slett hele seriespillet',
  'seriespill.view.deleteConfirm': 'Slette ALT og starte på nytt?',
  'seriespill.view.deleteYes': 'Ja, slett',
  // The hero header.
  'seriespill.view.allClasses': 'Alle klasser',
  'seriespill.view.classAndSeason': '{name} · Sesong {season}',
  'seriespill.view.lockReasonCompleted': 'Alle har spilt',
  'seriespill.view.lockReasonDeadline': 'Fristen er passert',
  'seriespill.view.seasonLocked': 'Sesong låst',
  'seriespill.view.seasonActive': 'Sesong aktiv',
  'seriespill.view.freeBooking': 'Fri booking',
  'seriespill.view.allocatedStandalone': 'Allokert · frittstående',
  'seriespill.view.allocatedBooking': 'Allokert · Banebooking',
  'seriespill.view.doubleRound': 'Dobbel serie',
  'seriespill.view.singleRound': 'Enkel serie',
  'seriespill.view.editSeasonDeadlineTitle': 'Klikk for å endre sesongfristen',
  'seriespill.view.seasonDeadlineOn': 'Frist {date}',
  'seriespill.view.setSeasonDeadline': 'Sett frist',
  'seriespill.view.rules': '📋 Regler',
  'seriespill.view.rulesTitle': 'Se reglene for serien',
  'seriespill.view.pastSeasonsOne': '🗓 {count} tidligere sesong →',
  'seriespill.view.pastSeasonsMany': '🗓 {count} tidligere sesonger →',
  'seriespill.view.historyTitle': 'Se tidligere sesonger — tabeller og kamper',
  'seriespill.view.mainSettings': 'Hovedinnstillinger',
  'seriespill.view.mainSettingsTitle':
    'Hovedinnstillinger for hele serien (navn, datoer, auto-modus, pensjonering)',
  'seriespill.view.classSettings': 'Innstillinger {name}',
  'seriespill.view.classSettingsTitle': 'Innstillinger for {name}: divisjoner, par, datoer',
  'seriespill.view.theClass': 'klassen',
  'seriespill.view.startSeason': '⚡ Start sesong {season} →',
  'seriespill.view.startSeasonTitle':
    'Sesongen for {name} er låst. Bekreft for å starte neste sesong med opp/nedrykk.',
  'seriespill.view.seasonRunning': 'Sesong {season} pågår',
  'seriespill.view.seasonRunningTitle':
    'Sesong {season} pågår — knappen aktiveres når alle kamper er spilt eller fristen er nådd.',
  'seriespill.view.autoModeOn': '⚡ Auto-modus på',
  'seriespill.view.demoRound': '🎲 Random (runden)',
  'seriespill.view.demoRoundTitle':
    'Demo: fyll neste runde med uspilte kamper i de viste divisjonene — alle spiller én kamp',
  'seriespill.view.demoAll': '🎲 Random (alt)',
  'seriespill.view.demoAllTitle':
    'Demo: fyll tilfeldige resultater for alle uspilte kamper i de viste divisjonene/klassene',
  'seriespill.view.divisionCountOne': '{count} divisjon',
  'seriespill.view.divisionCountMany': '{count} divisjoner',
  'seriespill.view.addClassLabel': 'Legg til klasse:',
  'seriespill.view.addClassTitle':
    'Legg til {name}-klasse i serien (starter tom — fyll par via Innstillinger)',
  'seriespill.view.readyToStart': '⚡ Klar til å starte sesongen',
  'seriespill.view.readyToStartHint':
    'Par er registrert, men kampprogrammet er ikke generert ennå. Generer kampene for alle divisjoner med minst 2 par.',
  'seriespill.view.startSeasonAction': 'Start sesong →',
  'seriespill.view.divisionChevron': 'Divisjon ›',
  // The tab bar.
  'seriespill.view.tabTable': '📊 Tabell',
  'seriespill.view.tabSchedule': '🎾 Kampprogram',
  'seriespill.view.tabCalendar': '📆 Kalender',
  'seriespill.view.tabBanepad': '📲 Banepad',
  'seriespill.view.tabSignup': '💳 Sesongpåmelding',
  'seriespill.view.progress': '{played}/{total} kamper · {pct}%',
  'seriespill.view.noPadMatches':
    'Ingen kamper er plassert på baner ennå. Plasser kamper på en bane for å bruke banepaden.',
  // The prompt after a round is closed by hand.
  'seriespill.view.roundEnded': 'Runde {round} avsluttet',
  'seriespill.view.nextRoundPlanned':
    'Neste runde (runde {round}) er planlagt å starte {date}. Vil du beholde fristen, eller flytte den slik at neste runde starter nå?',
  'seriespill.view.afterScheduledDeadline': 'etter oppsatt frist',
  'seriespill.view.keepDeadline': 'Behold frist',
  'seriespill.view.startRoundNow': 'Start runde {round} nå',
  // The calendar tab.
  'seriespill.view.noRoomsLead': 'Ingen tidsrom satt opp ennå. Gå til',
  'seriespill.view.mainSettingsBookingLink': 'Hovedinnstillinger → Banebooking',
  'seriespill.view.noRoomsTail': 'for å sette opp spilledager og baner.',
  'seriespill.view.noReservedSlots': 'Ingen reserverte slots ennå. Reserver banene under',
  'seriespill.view.noMatchesInFilter': 'Ingen kamper i dette filteret.',
  'seriespill.view.releaseUnusedOne': 'Slipp {count} ubrukt bane tilbake',
  'seriespill.view.releaseUnusedMany': 'Slipp {count} ubrukte baner tilbake',
  'seriespill.view.freeSlot': 'ledig',
  'seriespill.view.close': 'Lukk',
  'seriespill.view.swap': 'Bytt',
  'seriespill.view.drawSuffix': ' · uavgjort',
  'seriespill.view.playedSuffix': ' · spilt',
  'seriespill.view.reservedTimes': 'Reserverte tider · hvem spiller',
  'seriespill.view.filledOfTotal': '{filled}/{total} fylt',
  'seriespill.view.noUpcoming': 'Ingen kommende kamper',
  'seriespill.view.noUpcomingAllPlayed': ' — alt er spilt. Se historikk under.',
  'seriespill.view.noUpcomingDot': '.',
  'seriespill.view.calendarHistory': '📦 Historikk · spilte / passerte ({count})',
  'seriespill.view.reservedTimesNote':
    'Viser kun spilledager og tider som er reservert, og følger gjeldende klasse-/divisjonsfilter. Grønne rader er fylt med kamp; «ledig» = reservert slot uten kamp ennå.',
  'seriespill.view.unusedCourtsOne': '{count} reservert bane uten kamp.',
  'seriespill.view.unusedCourtsMany': '{count} reserverte baner uten kamp.',
  'seriespill.view.releaseBackOne': 'Slipp {count} ubrukt tilbake',
  'seriespill.view.releaseBackMany': 'Slipp {count} ubrukte tilbake',
  'seriespill.view.weeklyTemplate': 'Ukesmal · reserverte baner',
  'seriespill.view.weeklyTemplateNote':
    'Faste slots som gjentas hver uke. Felles varighet {min} min · 1 slot = 1 kamp. Kampene plasseres i disse slotene når generatoren kjøres.',
  'seriespill.view.timeBetweenRounds': 'Tid mellom runder',
  'seriespill.view.matchesPerRound': '{matches} kamper/runde · {slots} slots/uke',
  'seriespill.view.noPairsYet': 'ingen par ennå',
  'seriespill.view.weeksOne': '~{weeks} uke',
  'seriespill.view.weeksMany': '~{weeks} uker',
  'seriespill.view.roundDeadlineIsOne': '⚠ rundefrist er {weeks} uke',
  'seriespill.view.roundDeadlineIsMany': '⚠ rundefrist er {weeks} uker',
  'seriespill.view.cadenceNote': 'Beregnet av kamper i én runde delt på ledige slots per uke.',
  'seriespill.view.configuredDeadlineOne': 'Konfigurert rundefrist: {weeks} uke.',
  'seriespill.view.configuredDeadlineMany': 'Konfigurert rundefrist: {weeks} uker.',
  'seriespill.view.colTime': 'Tid',
  'seriespill.view.roomsWithoutClasses':
    '⚠ Ett eller flere rom mangler tilknyttede klasser — disse vil ikke få kamper plassert.',
  // The move-match dialog.
  'seriespill.view.moveMatch': 'Flytt kamp',
  'seriespill.view.currentSlot': 'Nåværende: {date} {time} · Bane {court} ·',
  'seriespill.view.swapSafe': 'Trygg',
  'seriespill.view.swapClash': 'Kollisjon (samtidig / rett etter)',
  'seriespill.view.clashSameTime': 'samtidig',
  'seriespill.view.clashBackToBack': 'rett etter',
  'seriespill.view.noOtherSlots': 'Ingen andre reserverte slots å flytte til.',
  'seriespill.view.safeOfTotal': '{safe} trygge / {total}',
  'seriespill.view.timeAndCourt': '{time} · Bane {court}',
  'seriespill.view.swapWith': '↔ {name}',
  // Results and substitutions.
  'seriespill.view.errRecordResult': 'Kunne ikke registrere resultatet',
  'seriespill.view.errSubstituteNotFound': 'Fant ikke innbytteren i spillerlisten.',
  'seriespill.view.errSwapFailed': 'Byttet kunne ikke utføres.',
  'seriespill.view.playerFallback': 'Spiller',
  'seriespill.view.theOldPlayer': 'den gamle spilleren',
  'seriespill.view.swapDone':
    '{original} byttet ut med {replacement}. Spilte runder beholder {originalOld}; {replacement} overtar fra neste uspilte runde og vises i tabellen med bytte-ikon.',
  // ── tournament: scheduled breaks (BreakRow) ──────────────────────
  'tournament.break.namePlaceholder': 'Navn på pause',
  'tournament.break.afterPhase': 'Etter fase',
  'tournament.break.afterPhaseTitle':
    'Pause starter rett etter spesifisert fase er ferdigspilt (ingen dødtid)',
  'tournament.break.fixedTime': 'Fast tid',
  'tournament.break.fixedTimeTitle':
    'Pause starter på spesifisert klokkeslett (kan gi dødtid hvis kamper slutter tidligere)',
  'tournament.break.phaseSelectTitle': 'Hvilken fase skal pausen komme etter',
  'tournament.break.nameLabel': 'Navn på pausen',
  'tournament.break.startLabel': 'Når pausen starter',
  'tournament.break.durationLabel': 'Pausens lengde i minutter',
  'tournament.break.remove': 'Fjern pausen',
  'tournament.break.phaseOption': 'Fase {phase}',
  'tournament.break.afterPhaseOption': 'Etter Fase {phase}',
  'tournament.break.afterPhaseHint':
    'Pausen starter rett etter at Fase {phase} er ferdigspilt — ingen dødtid før pausen.',
  'tournament.break.timeHint':
    'Pausen starter eksakt kl {time}. Hvis kamper slutter før dette, blir det dødtid.',

  // ── tournament: the half-built tournament (BrokenTournamentView) ──
  'tournament.broken.back': 'Tilbake til hjem',
  'tournament.broken.title': 'Turneringen er ikke ferdig satt opp',
  'tournament.broken.lead':
    'Denne turneringen mangler kampoppsett — sannsynligvis ble den ikke fullført i wizarden, eller dataen er korrupt. Du kan slette den og starte en ny.',
  'tournament.broken.name': 'Navn:',
  'tournament.broken.players': 'Spillere:',
  'tournament.broken.groups': 'Grupper:',
  'tournament.broken.matches': 'Kamper:',
  'tournament.broken.missing': 'mangler',
  'tournament.broken.confirmDelete':
    'Slett denne turneringen permanent? Den kan ikke gjenopprettes.',
  'tournament.broken.delete': 'Slett turneringen',
  'tournament.broken.afterDelete':
    'Når den er slettet kan du starte en ny turnering fra hjemskjermen.',

  // ── tournament: the club-vs-club banner (ClubPadBanner) ──────────
  'tournament.clubPad.record': '{wins}V · {losses}T · {ties}U',
  'tournament.clubPad.clubA': 'Klubb A',
  'tournament.clubPad.clubB': 'Klubb B',
  'tournament.clubPad.matchesPlayed': '{n} kamper',

  // ── tournament: confirming a result (ConfirmResultModal) ─────────
  'tournament.confirm.headerFinals': 'Bane {court} · Finale',
  'tournament.confirm.headerPhase': 'Bane {court} · Fase {phase}',
  'tournament.confirm.headerPhaseRound': 'Bane {court} · Fase {phase} · R{round}',
  'tournament.confirm.title': 'Bekreft resultat',
  'tournament.confirm.draw': 'Uavgjort: {s1} – {s2}',
  'tournament.confirm.winnerLabel': 'Vinner:',
  'tournament.confirm.explainer':
    'Når du bekrefter blir ELO-rating oppdatert for begge lag og resultatet legges til i kampplanen. Du kan endre resultatet senere ved å trykke på "Endre" på den forrige kampen.',
  'tournament.confirm.cancel': 'Avbryt',
  'tournament.confirm.submit': 'Bekreft og send',

  // ── tournament: one player in a match row (PlayerCell) ───────────
  'tournament.playerCell.groupTitle': 'Gruppe {group}',
  'tournament.playerCell.showProfile': 'Vis profil for {name}',
  'tournament.playerCell.eloTitle': 'ELO: {elo}',
  'tournament.playerCell.luckyUp':
    'Lucky Looser — vant gruppe {group} og rykket opp hit til bronsefinalen',
  'tournament.playerCell.luckyDown': 'Rykket ned fra gruppe {group} (sistemann)',
  'tournament.playerCell.newbieBoost':
    'Ny spiller-boost {boost} — avtar lineært over kamper, blir 1x når spilleren har spilt {threshold} kamper (justert for turneringens ELO-vekt).',
  'tournament.playerCell.tba': 'TBA',
  'tournament.playerCell.position': '{position}. plass',
  'tournament.playerCell.luckyHintUp':
    'Lucky Looser — vinneren av gruppe {group} rykker opp og spiller her',
  'tournament.playerCell.luckyHintDown': 'Sistemann fra gruppe {group} rykker ned hit',

  // ── tournament: the saved-setup dropdown (SetupDropdown) ─────────
  'tournament.setupDropdown.load': 'Last oppsett…',
  'tournament.setupDropdown.loadTitle': 'Last et lagret oppsett',
  'tournament.setupDropdown.confirmDelete': 'Slette "{name}"?',
  'tournament.setupDropdown.delete': 'Slett',
  'tournament.setupDropdown.cancel': 'Avbryt',
  'tournament.setupDropdown.metaPlayersCourts': '{players} sp · {courts} baner',
  'tournament.setupDropdown.metaCourts': '{courts} baner',
  'tournament.setupDropdown.metaFallback': 'Oppsett',
  'tournament.setupDropdown.deleteTitle': 'Slett oppsett',

  // ── tournament: the seeding cards (SeedGroupCard) ────────────────
  'tournament.seedGroup.title': 'Gruppe {letter}',
  'tournament.seedGroup.meta': '{players} spillere · ELO ⌀ {elo}',
  'tournament.seedGroup.top': 'Topp',
  'tournament.seedGroup.bottom': 'Bunn',
  'tournament.seedGroup.suggested': 'Foreslått: Gruppe {group}',
  'tournament.seedGroup.unlockTitle': 'Låst — klikk for å låse opp',
  'tournament.seedGroup.lockTitle': 'Lås til denne gruppen',
  'tournament.seedGroup.dropHere': 'Slipp spillere her',

  // ── tournament: the match-list header (UniversalMatchListHeader) ──
  'tournament.matchList.colCourt': 'Bane',
  'tournament.matchList.home': 'Hjemmelag',
  'tournament.matchList.away': 'Bortelag',
  'tournament.matchList.serves': 'Starter å serve i denne runden',
  'tournament.matchList.player1': 'Spiller 1',
  'tournament.matchList.player2': 'Spiller 2',
  'tournament.matchList.teamElo': 'Lag-ELO',
  'tournament.matchList.score': 'Score',
  'tournament.matchList.vs': 'vs',

  // ── tournament: the tiebreak-order editor (TiebreakOrderEditor) ──
  'tournament.tiebreak.defaultTitle': 'Rangering innen gruppe',
  'tournament.tiebreak.activeCount': '{count} aktive',
  'tournament.tiebreak.dragTitle': 'Dra for å endre rekkefølge',
  'tournament.tiebreak.offShort': 'av',
  'tournament.tiebreak.disableTitle': 'Klikk for å deaktivere',
  'tournament.tiebreak.enableTitle': 'Klikk for å aktivere',
  'tournament.tiebreak.on': 'På',
  'tournament.tiebreak.off': 'Av',
  'tournament.tiebreak.reset': 'Tilbakestill til standard',

  // ── americano: the ranking-order note (AmericanoSetupView, AmericanoSettingsModal) ──
  // The editor lets head to head be dragged anywhere; the engine applies it
  // last, inside a shared place, whatever its position (#648/#720, #859).
  'americano.ranking.h2hNote':
    'Innbyrdes oppgjør brukes alltid til slutt, uansett hvor det står i lista: det avgjør aldri plassen, bare rekkefølgen mellom spillere som deler plass.',

  // ── americano: the «Lagre som mal» dialog's name field (AmericanoSetupView, #900) ──
  'americano.setup.templateName': 'Navn på malen',

  // ── help: Americano points & ranking (AmericanoScoringSection) ──
  'help.americanoScoring.title': 'Poeng og ranking',
  'help.americanoScoring.intro.before':
    'Resultatet av hver kamp summeres opp i en lederboard. Spillerne rangeres etter valgt tiebreak-rekkefølge — som er',
  'help.americanoScoring.intro.strong': 'fullt justerbar',
  'help.americanoScoring.intro.after': 'i Americano (i motsetning til Padel24).',
  'help.americanoScoring.primary.title': 'Primær ranking',
  'help.americanoScoring.primary.headMode': 'Modus',
  'help.americanoScoring.primary.headCounts': 'Hva som teller',
  'help.americanoScoring.primary.winsFirst': 'Seire først (default)',
  'help.americanoScoring.primary.winsFirstOrder': '1) Seire, 2) poeng-diff, 3) totale poeng',
  'help.americanoScoring.primary.pointsFirst': 'Poeng først',
  'help.americanoScoring.primary.pointsFirstOrder': '1) Totale poeng, 2) seire, 3) poeng-diff',
  'help.americanoScoring.primary.draws':
    'Uavgjort teller som en halv seier i rangeringen — én seier og én uavgjort går foran én seier og ett tap. Lederboardet viser uavgjort i egen kolonne (U). Det samme gjelder for sittover med «Uavgjort + snitt».',
  'help.americanoScoring.h2h.title': 'Innbyrdes oppgjør',
  'help.americanoScoring.h2h.intro':
    'Innbyrdes oppgjør avgjør aldri plassen. Spillere som er like på alle de andre kriteriene, deler plassen. Er innbyrdes oppgjør slått på, bestemmer det bare rekkefølgen de vises i innenfor den delte plassen — alltid til slutt, uansett hvor det står i rangeringslista.',
  'help.americanoScoring.h2h.headSetting': 'Innstilling',
  'help.americanoScoring.h2h.headEffect': 'Effekt',
  'help.americanoScoring.h2h.on': 'På (standard)',
  'help.americanoScoring.h2h.onEffect':
    'Spillere som deler plass, vises i rekkefølge etter innbyrdes resultat',
  'help.americanoScoring.h2h.off': 'Av',
  'help.americanoScoring.h2h.offEffect':
    'Spillere som deler plass, vises i en teknisk rekkefølge — ikke en rangering',
  'help.americanoScoring.h2h.cardTitle': 'Innbyrdes oppgjør i Americano',
  'help.americanoScoring.h2h.cardBody':
    'Beregnes ut fra alle kamper der spillerne var motstandere på motsatt lag; uavgjort teller ikke. Hver spiller som deler plassen, får sine seire mot de andre i den delte plassen minus tapene mot dem, og høyest vises først. Har A slått B to ganger og B slått A én gang, vises A over B — men de deler fortsatt plassen. Går det i ring (A slo B, B slo C, C slo A), står alle likt.',

  // ── tournament: groups & seeding, wizard step 3 (Step3Groups) ────
  'tournament.groups.generating': 'Genererer grupper…',
  'tournament.groups.title': 'Grupper & seeding',
  'tournament.groups.lead':
    '{count} grupper foreslått basert på ELO. Dra-og-slipp for å flytte. Lås spillere som ikke skal endres.',
  'tournament.groups.regenerate': 'Foreslå på nytt',
  'tournament.groups.lockedCount': '({count} låst)',
  'tournament.groups.hintDrag': 'Dra spillerkort til annen gruppe',
  'tournament.groups.hintLock': 'Klikk lås for å beholde i gruppe ved regenerering',
  'tournament.groups.hintArrow': 'Oransje pil = avviker fra foreslått gruppe',

  // ── tournament: the final ranking (FinalRankingTab) ──────────────
  'tournament.ranking.finalsPending': 'Finaler genereres etter siste gruppefase.',
  'tournament.ranking.position': '{position}. plass',
  'tournament.ranking.positionRange': '{from}.–{to}. plass',
  'tournament.ranking.title': 'Turneringsresultatet',
  'tournament.ranking.lead':
    'Rangering basert på finalene. Vinnerne av Finale 1 er på 1. plass, taperne på 2. plass. Finale 2 spiller om 3. og 4. plass, osv.',
  'tournament.ranking.pendingFinals':
    '{done} av {total} finaler ferdig — uavklart resultat for {left} finaler',
  'tournament.ranking.winnersLabel': '🏆 Turneringsvinnere',
  'tournament.ranking.wonGoldFinal': 'Vant gull-finalen {score}',
  'tournament.ranking.colPlace': 'Plass',
  'tournament.ranking.colPlayers': 'Spillere',
  'tournament.ranking.colFinal': 'Finale',
  'tournament.ranking.colResult': 'Resultat',
  'tournament.ranking.waiting': '(venter)',
  'tournament.ranking.notPlayed': 'Ikke spilt',

  // ── tournament: the compact match card (MatchCard) ───────────────
  'tournament.matchCard.courtShort': 'B{court}',
  'tournament.matchCard.final': 'Finale',
  'tournament.matchCard.phaseRound': 'F{phase}·R{round}',
  'tournament.matchCard.cross': 'Kryss {group}',
  'tournament.matchCard.group': 'Grp {group}',
  'tournament.matchCard.luckyLooserTitle':
    'Bronsefinale med Lucky Looser — gruppevinneren fra gruppen under spiller her',
  'tournament.matchCard.luckyLooser': 'LUCKY LOOSER',
  'tournament.matchCard.done': '✓ Ferdig',
  'tournament.matchCard.vs': 'vs',
  'tournament.matchCard.cancel': 'Avbryt',
  'tournament.matchCard.save': 'Lagre',
  'tournament.matchCard.edit': 'Endre',
  'tournament.matchCard.enterScore': 'Registrer skår',
  'tournament.matchCard.playersLater': 'Spillere fastsettes etter at tidligere faser er ferdige',

  // ── tournament: a player's own match row (MatchRow) ──────────────
  'tournament.matchRow.final': 'Finale',
  'tournament.matchRow.phase': 'Fase {phase}',
  'tournament.matchRow.round': 'Runde {round}',
  'tournament.matchRow.roundShort': 'R{round}',
  'tournament.matchRow.thisPlayer': 'Denne spilleren',
  'tournament.matchRow.groupTitle': 'Gruppe {group}',
  'tournament.matchRow.newbieBoost':
    'Ny spiller-boost {boost} — avtar lineært fra {from}x (kamp 1) til 1x når spilleren har spilt {threshold} kamper (justert for turneringens ELO-vekt).',
  'tournament.matchRow.teamEloTitle': 'Lagets snitt-ELO før kampen',
  'tournament.matchRow.teamEloTitleStrongest': 'Lagets snitt-ELO før kampen (sterkeste lag)',
  'tournament.matchRow.eloChangeTitle': 'ELO-endring etter kampen',
  'tournament.matchRow.court': 'Bane {court}',
  'tournament.matchRow.vs': 'vs',
  'tournament.matchRow.notPlayed': 'Ikke spilt',
  'tournament.matchRow.draw': 'Uavgjort',
  'tournament.matchRow.win': 'Seier',
  'tournament.matchRow.loss': 'Tap',

  // ── tournament: the full match table (MatchTable) ────────────────
  'tournament.matchTable.empty': 'Ingen kamper å vise med dette filteret.',
  'tournament.matchTable.home': 'Hjemmelag',
  'tournament.matchTable.homeServes': 'Hjemmelaget starter å serve',
  'tournament.matchTable.away': 'Bortelag',
  'tournament.matchTable.colTime': 'Tid',
  'tournament.matchTable.colMatch': 'Kamp',
  'tournament.matchTable.colCourt': 'Bane',
  'tournament.matchTable.colPlayer1': 'Spiller 1',
  'tournament.matchTable.colPlayer2': 'Spiller 2',
  'tournament.matchTable.colTeamElo': 'Lag-ELO',
  'tournament.matchTable.colScore': 'Score',
  'tournament.matchTable.colVs': 'vs',
  'tournament.matchTable.breakLabel': 'Pause',
  'tournament.matchTable.rotation': 'Kamprotasjon {n}',

  // ── tournament: one match-table row (MatchTableRow) ──────────────
  'tournament.matchTableRow.final': 'Finale',
  'tournament.matchTableRow.finalNumbered': 'Finale {number}',
  'tournament.matchTableRow.phase': 'Fase {phase}',
  'tournament.matchTableRow.roundShort': 'R{round}',
  'tournament.matchTableRow.unscheduled': 'Ikke planlagt',
  'tournament.matchTableRow.vs': 'vs',

  // ── tournament: the ELO standings tab (StandingsTab) ─────────────
  'tournament.standings.title': 'Turneringsprestasjon',
  'tournament.standings.lead':
    'Spillere rangert etter ELO-endring i løpet av turneringen — størst økning øverst.',
  'tournament.standings.biggestGain': 'Største økning',
  'tournament.standings.biggestLoss': 'Største nedgang',
  'tournament.standings.playersUp': 'Spillere i pluss',
  'tournament.standings.playersDown': 'Spillere i minus',
  'tournament.standings.ofTotal': 'av {total}',
  'tournament.standings.colPlayer': 'Spiller',
  'tournament.standings.colGroup': 'Grp',
  'tournament.standings.colPlayed': 'Sp',
  'tournament.standings.colPlayedTitle': 'Antall spilte kamper',
  'tournament.standings.colWins': 'V',
  'tournament.standings.colWinsTitle': 'Seire',
  'tournament.standings.colLosses': 'T',
  'tournament.standings.colLossesTitle': 'Tap',
  'tournament.standings.colDraws': 'U',
  'tournament.standings.colDrawsTitle': 'Uavgjort',
  'tournament.standings.colStart': 'Start',
  'tournament.standings.colNow': 'Nå',
  'tournament.standings.colChange': 'Endring',

  // ── tournament: the phase-1 group editor (Padel24GroupEditor) ────
  'tournament.groupEditor.title': 'Grupper · Forhåndsvisning',
  'tournament.groupEditor.redraw': 'Trekk på nytt',
  'tournament.groupEditor.redrawTitle': 'Trekk fase 1-kampene på nytt fra de oppdaterte gruppene',
  'tournament.groupEditor.drawn': '✓ Kampene er trukket',
  'tournament.groupEditor.dirtyBefore':
    'Du har endret gruppene, så kampplanen passer ikke lenger. Trykk',
  'tournament.groupEditor.dirtyButton': '«Trekk på nytt»',
  'tournament.groupEditor.dirtyAfter': 'når du er ferdig med å flytte spillere.',
  'tournament.groupEditor.hint':
    'Dra en spiller (eller bruk pilene) for å flytte hen. Alle andre forskyves ett hakk, og gruppene holder alltid likt antall — også på tvers av gruppegrenser.',
  'tournament.groupEditor.groupTitle': 'Gruppe {letter}1',
  'tournament.groupEditor.playerCount': '{count} spillere',
  'tournament.groupEditor.levelShort': 'N{level}',
  'tournament.groupEditor.levelTitle': 'Nivå {level} · {name}',
  'tournament.groupEditor.eloTitle': 'ELO',
  'tournament.groupEditor.moveUp': 'Flytt opp',
  'tournament.groupEditor.moveDown': 'Flytt ned',

  // ── tournament: the full schedule tab (FullPlanTab) ──────────────
  'tournament.fullPlan.title': 'Full kampplan',
  'tournament.fullPlan.subtitle': '{matches} kamper · {done} ferdig',
  'tournament.fullPlan.statMatches': 'Totalt kamper',
  'tournament.fullPlan.statPlayers': 'Spillere',
  'tournament.fullPlan.statRounds': 'Runder',
  'tournament.fullPlan.statDone': 'Ferdig',
  'tournament.fullPlan.tabAll': 'Full oversikt',
  'tournament.fullPlan.tabPhase': 'Fase {phase}',
  'tournament.fullPlan.tabFinals': 'Finaler',
  'tournament.fullPlan.searchPlaceholder': 'Søk spiller…',
  'tournament.fullPlan.clearSearch': 'Tøm søk',
  'tournament.fullPlan.showLabel': 'Vis:',
  'tournament.fullPlan.allDays': 'Alle dager',
  'tournament.fullPlan.hideCompleted': 'Skjul ferdige',
  'tournament.fullPlan.hideUndrawn': 'Skjul utrukkede',
  'tournament.fullPlan.showElo': 'Vis ELO',
  'tournament.fullPlan.lockTitle': 'Noen finaler vises tidlig — men kan ikke registreres ennå',
  'tournament.fullPlan.lockBefore':
    'Spillerne kan se på forhånd hvilke finaler de skal spille, men resultater kan først registreres når',
  'tournament.fullPlan.lockStrong': 'alle grupper er ferdig med siste fase',

  // ── tournament: the groups tab (GroupsTab) ───────────────────────
  'tournament.groupsTab.headerFinal': 'Grupper · Sluttoppstilling',
  'tournament.groupsTab.headerPhase': 'Grupper · Fase {phase}',
  'tournament.groupsTab.previewTag': '(forhåndsvisning)',
  'tournament.groupsTab.previewTitle':
    'Forhåndsvisning — {drawn} av {total} grupper trukket så langt',
  'tournament.groupsTab.showPhaseTitle': 'Vis gruppene i fase {phase}',
  'tournament.groupsTab.phaseButton': 'Fase {phase}',
  'tournament.groupsTab.historical': '📜 Historisk visning — viser slutt-status for fase {phase}',
  'tournament.groupsTab.previewLead':
    '✨ Forhåndsvisning av Fase {phase} — gruppene populeres etterhvert som de trekkes. Stilling starter på null når fasen offisielt begynner.',
  'tournament.groupsTab.promotionLead':
    'Topp {count} rykker opp · bunn {count} rykker ned etter fasen',
  'tournament.groupsTab.groupTitle': 'Gruppe {label}',
  'tournament.groupsTab.metaDrawn': '{players} spillere · trukket',
  'tournament.groupsTab.metaWaiting': 'Venter på trekning',
  'tournament.groupsTab.metaPhase': '{players} spillere · Fase {phase}',
  'tournament.groupsTab.drawnTag': 'Trukket',
  'tournament.groupsTab.notDrawnTag': 'Ikke trukket',
  'tournament.groupsTab.topTag': 'Topp',
  'tournament.groupsTab.drawnLater': 'Trekkes når nabogruppene er ferdigspilt.',
  'tournament.groupsTab.colPlayer': 'Spiller',
  'tournament.groupsTab.colElo': 'ELO',
  'tournament.groupsTab.colWins': 'S/Sp',
  'tournament.groupsTab.colWinsTitle': 'Seire / spilte',
  'tournament.groupsTab.colDiff': 'Diff',
  'tournament.groupsTab.colDiffTitle': 'Games-differanse',
  'tournament.groupsTab.aheadBy': 'Foran {next} på: {reason}',
  'tournament.groupsTab.why.wins': 'flere seire',
  'tournament.groupsTab.why.gamesDiff': 'bedre games-differanse',
  'tournament.groupsTab.why.h2h': 'vant det innbyrdes oppgjøret',
  'tournament.groupsTab.why.gamesWon': 'flere vunne games',
  'tournament.groupsTab.why.oppStrength': 'tøffere motstand i fasen',
  'tournament.groupsTab.why.draw': 'loddtrekning — alt annet er likt',
  'tournament.groupsTab.colGames': 'Games',
  'tournament.groupsTab.colGamesTitle': 'Games vunnet',
  'tournament.groupsTab.newPlayerTag': 'NY',
  'tournament.groupsTab.newPlayerTitle':
    'Ny spiller i denne gruppen — har rykket opp eller ned fra annen gruppe',

  // ── tournament: the group filter (GroupFilterDropdown) ───────────
  'tournament.groupFilter.label': 'Gruppe:',
  'tournament.groupFilter.all': 'Alle grupper',
  'tournament.groupFilter.group': 'Gruppe {letter}',

  // ── tournament: the event log (LogTab) ───────────────────────────
  'tournament.log.title': 'Hendelseslogg ({count})',
  // ── Tournament event log (#519) ──────────────────────────────────
  // PERSISTED AS CODES, rendered here. The log is read back by everyone who
  // opens the tournament, so the sentence is decided at render time rather than
  // stored in whichever language the creator happened to be using. The Norwegian
  // below is the wording these entries have always had — deliberately unchanged,
  // so an existing log and a new one read the same to a Norwegian club.
  // Two events have a singular and a plural key: the catalog interpolates
  // `{name}` and nothing else, so choosing between them is the call site's job.
  'tournament.log.event.started': 'Turnering startet — fase {phase}, runde {round}',
  'tournament.log.event.created':
    'Turnering opprettet — {matches} kamper planlagt. Forhåndsvisning: juster fritt, trykk «Start turnering» når klar.',
  'tournament.log.event.phase1Redrawn': 'Fase 1 trukket på nytt etter gruppe-endring',
  'tournament.log.event.paused': 'Pauset',
  'tournament.log.event.resumed': 'Fortsatt',
  'tournament.log.event.result': 'Resultat: {team1} {score1}–{score2} {team2}{elo}',
  'tournament.log.event.finalResult': 'Finale: {team1} {score1}–{score2} {team2}{elo}',
  'tournament.log.event.resultBanepad':
    '[Banepad] Resultat: {team1} {score1}–{score2} {team2}{elo}',
  'tournament.log.event.resultBanepadEdit':
    '[Banepad endre] Resultat: {team1} {score1}–{score2} {team2}{elo}',
  'tournament.log.event.groupDoneFinalsReady':
    'Gruppe {group} ferdig med fase {phase} — finaler for gruppen er klare',
  'tournament.log.event.allGroupsDoneBonus':
    'Alle grupper ferdig — bonuspoeng tildelt topp 3 i hver gruppe (+3/+2/+1 til sluttrangering)',
  'tournament.log.event.allGroupsDoneFinalsDrawn': 'Alle grupper ferdig — finaler trukket',
  'tournament.log.event.allFinalsDone': 'Alle finaler ferdig — turnering fullført',
  'tournament.log.event.completed': 'Turnering fullført!',
  'tournament.log.event.finishedRanked': 'Turnering ferdig — sluttrangering generert',
  'tournament.log.event.phaseDoneFinalsReady': 'Fase {phase} ferdig — finaler klare',
  'tournament.log.event.phaseStartedHistory':
    '→ Fase {phase} starter — kamper trukket basert på makker/motstander-historikk',
  'tournament.log.event.phaseRound': '→ Fase {phase}, runde {round}',
  'tournament.log.event.phaseDoneBonusPerGroup':
    'Fase {phase} ferdig — bonuspoeng tildelt topp 3 i hver gruppe (+3/+2/+1 til sluttrangering)',
  'tournament.log.event.phaseDoneBonusPromotion':
    'Fase {phase} ferdig — bonuspoeng tildelt (+3/+2/+1 for topp 3) og opprykk/nedrykk anvendt',
  'tournament.log.event.finalsNamed': 'Finaler/plasseringskamper er klare med navn',
  'tournament.log.event.groupPhaseDrawn':
    '🎯 Gruppe {group}: Fase {phase} trukket — andre grupper påvirkes ikke',
  'tournament.log.event.groupsDrawnOne': '🎯 Trekt 1 gruppe for Fase {phase}: {groups}',
  'tournament.log.event.groupsDrawnMany': '🎯 Trekt {count} grupper for Fase {phase}: {groups}',
  'tournament.log.event.groupFinalsDrawn':
    '🏆 Gruppe {group}: finaler trukket basert på sluttrangering i Fase {phase}',
  'tournament.log.event.groupFinalsDrawnOne': '🏆 Trekt finaler for 1 gruppe: {groups}',
  'tournament.log.event.groupFinalsDrawnMany': '🏆 Trekt finaler for {count} grupper: {groups}',
  // The rest of #519: the engine's own entries (written in packages/engine, by
  // code string, so apps/web/tests/tournament-log-engine-codes.test.ts checks
  // each has a message here) and the lifecycle / americano / preview writers.
  // The Norwegian is, again, exactly the sentence each one used to store.
  'tournament.log.event.substitution':
    '⇄ Spillerbytte: {original} → {replacement} (ELO {eloFrom} → {eloTo}) · Fase {phase}, Runde {round} · Etter {completed}/{total} kamper',
  'tournament.log.event.americanoSubstitutionOne':
    '⇄ Spillerbytte: {original} → {replacement} (ELO {eloFrom} → {eloTo}) · Runde {round} · Original hadde spilt {count} kamp',
  'tournament.log.event.americanoSubstitutionMany':
    '⇄ Spillerbytte: {original} → {replacement} (ELO {eloFrom} → {eloTo}) · Runde {round} · Original hadde spilt {count} kamper',
  'tournament.log.event.placeholderFilled':
    '✓ Reservert plass fylt inn: {placeholder} → {player} (ELO {elo})',
  'tournament.log.event.parkedReadyNewTournament':
    'Lagret (klar til start) — parkert ved start av ny turnering',
  'tournament.log.event.setAsideRunningNewTournament': 'Satt til side (fortsatt i gang)',
  'tournament.log.event.autoParkedNewTournament': 'Automatisk parkert (ny turnering startet)',
  'tournament.log.event.parkedReady': 'Lagret (klar til start) og parkert',
  'tournament.log.event.pausedParked': 'Pauset og parkert',
  'tournament.log.event.setAsideRunningSwitched':
    'Satt til side (fortsatt i gang) — byttet til annen turnering',
  'tournament.log.event.autoParkedSwitched': 'Automatisk parkert (byttet til annen turnering)',
  'tournament.log.event.resumedFromParked': 'Gjenopptatt',
  'tournament.log.event.pausedFromHome': 'Satt på pause fra hovedsiden',
  'tournament.log.event.restoredFromArchive': 'Hentet fra arkivet og fortsatt',
  'tournament.log.event.resetToReady': 'Satt tilbake til klar til start',
  'tournament.log.event.previewBuilt': 'Forhåndsvisning bygget — {matches} kamper planlagt',
  'tournament.log.event.markedInjured': '{player} markert som skadet — utelates fra nye runder',
  'tournament.log.event.returnedFromInjury': '{player} tilbake fra skade',
  'tournament.log.event.roundDeleted':
    'Runde {round} slettet — {matches} kamper fjernet, ELO rullet tilbake, senere runder renummerert',

  // ── tournament: the matchup header (MatchupLines) ────────────────
  'tournament.matchup.home': 'Hjemme',
  'tournament.matchup.away': 'Borte',
  'tournament.matchup.avgElo': 'snitt ELO',
  'tournament.matchup.vs': 'VS',
  'tournament.matchup.tba': 'TBA',
  'tournament.matchup.placeholder': '{position}. plass G{group}',

  // ── tournament: the format picker (CreateTournamentView) ─────────
  'tournament.create.back': 'Tilbake',
  'tournament.create.title': 'Opprett turnering',
  'tournament.create.lead': 'Velg turneringstype. Alle formater påvirker spillernes ELO.',
  'tournament.create.comingSoon': 'Kommer snart',
  'tournament.create.available': 'Tilgjengelig',
  'tournament.create.eloWeightTitle': 'ELO-vekt: {weight} — hvor mye formatet påvirker ELO',
  'tournament.create.eloWeightPill': 'ELO {weight}',
  'tournament.create.padel24.name': 'Padel24',
  'tournament.create.padel24.tagline': 'Klubbens hovedformat',
  'tournament.create.padel24.desc':
    'Padel24 er klubbens egen turneringsstruktur med flere grupper på ulikt nivå. Hver fase trekkes nye makkere innen gruppen, og etter fasen rykker de beste opp en gruppe og de svakeste ned. Kan kjøres på én økt eller spres over flere dager. Best for 20-80 spillere.',
  'tournament.create.padel24.f1': '10 spillere per gruppe (justerbart 6-20)',
  'tournament.create.padel24.f2': 'Flere faser — nye makkere innen gruppen hver gang',
  'tournament.create.padel24.f3': 'Opp- og nedrykk mellom grupper etter hver fase',
  'tournament.create.padel24.f4': 'Banepad med auto-fløyte og pause-håndtering',
  'tournament.create.km.name': 'Klubbmesterskap',
  'tournament.create.km.tagline': 'Cup-format med utslagsrunder',
  'tournament.create.km.desc':
    'Tradisjonell turnering med gruppespill og utslagsrunder. Spilles over en helg eller noen uker. Krones klubbmester per klasse. Støtter flere klasser (Herrer, Damer, Mix) med divisjoner (A/B/C) i samme turnering.',
  'tournament.create.km.f1': 'Snake-seeding av grupper basert på ELO eller manuell rangering',
  'tournament.create.km.f2': 'FIP-stil sluttspill (gruppe-vinnere møtes tidligst i finale)',
  'tournament.create.km.f3': 'Konfigurerbar match-format og tid per fase',
  'tournament.create.km.f4': 'Best of 3 med super-tiebreak (FIP-standard)',
  'tournament.create.league.name': 'Seriespill',
  'tournament.create.league.tagline': 'Liga over en hel sesong',
  'tournament.create.league.desc':
    'Klubbens egen liga med faste par som spilles over en lengre periode. Klasser (Herrer, Damer, Mix) med flere divisjoner — round-robin der alle par møter alle. Tabellbasert sammenlagt-stilling med opp- og nedrykk mellom divisjonene ved sesongslutt. Baner kan allokeres til faste dager, eller parene booker og spiller selv.',
  'tournament.create.league.f1': 'Faste par — lagstyrke etter snitt-ELO',
  'tournament.create.league.f2': 'Klasser med flere divisjoner (1., 2., 3. div …)',
  'tournament.create.league.f3': 'Round-robin: alle møter alle, runde for runde',
  'tournament.create.league.f4': 'Opp- og nedrykk mellom divisjoner (justerbart)',
  'tournament.create.league.f5': 'Sesong-frist, valgfri starpoint (gullpoeng)',
  'tournament.create.league.f6': 'Baner på faste dager — eller fri booking',
  'tournament.create.mini.name': 'Miniturneringer',
  'tournament.create.mini.tagline': 'Korte rotasjons-formater',
  'tournament.create.mini.desc':
    'Korte turneringsformater spilt på én økt — Americano, Mexicano, Mix Mexicano og varianter. Felles for alle: minimal admin, gjennomføring på 1-3 timer, roterende makkere eller faste lag.',
  'tournament.create.mini.f1': 'Americano — alle spiller med alle 1 gang',
  'tournament.create.mini.f2': 'Mexicano — jevnere kamper basert på stilling',
  'tournament.create.mini.f3': 'Team-formater med fast makker',
  'tournament.create.mini.f4': 'Bingo, Tids-modus og poeng-modus',
  'tournament.create.single.name': 'Enkeltkamp',
  'tournament.create.single.tagline': 'Logg én spilt kamp',
  'tournament.create.single.desc':
    'Registrér en padel-kamp spilt utenfor turnering. Velg fritt hvor mye kampen skal telle på ELO (0–200%) — fra treningskamp (0%) til full best av 3 (200%). ELO oppdateres umiddelbart for de fire spillerne.',
  'tournament.create.single.f1': 'Velg ELO-vekt per kamp (0–200%)',
  'tournament.create.single.f2': 'Skriv inn games per sett',
  'tournament.create.single.f3': 'Vinneren tar 2 sett',
  'tournament.create.single.f4': 'ELO oppdateres umiddelbart',

  // ── tournament: the wizard summary, step 5 (Step5Start) ──────────
  'tournament.start.title': 'Oppsummering',
  'tournament.start.lead':
    'Sjekk at alt stemmer, og trykk «Opprett turnering». Du kommer til en forhåndsvisning der alt kan justeres før du starter.',
  'tournament.start.drawMirror': 'Speilrotasjon (R1-makkere blir R2-motstandere)',
  'tournament.start.drawVaried': 'Variert (standard regler)',
  'tournament.start.pacingEven': 'Jevnt fordelt',
  'tournament.start.pacingBackToBack': 'Rett etter hverandre',
  'tournament.start.pacingOneGap': 'Én kamp gap',
  'tournament.start.pacingPacked': 'Pakket',
  'tournament.start.orderBestFirst': 'Beste gruppe først',
  'tournament.start.orderBestLast': 'Beste gruppe sist (klimaks)',
  'tournament.start.finalsPairing12': '1 + 2 vs 3 + 4 (best mot dårligst)',
  'tournament.start.finalsPairing13': '1 + 3 vs 2 + 4 (jevnest)',
  'tournament.start.statPlayers': 'Spillere',
  'tournament.start.statGroups': 'Grupper',
  'tournament.start.statPhases': 'Faser',
  'tournament.start.statMatchesPerPlayer': 'Kamper/spiller',
  'tournament.start.dayValue':
    '{date} · {start}–{end} · {courts} baner · {duration}m/kamp · {phases}{final}{breaks}',
  'tournament.start.dayPhase': '{count} fase',
  'tournament.start.dayPhases': '{count} faser',
  'tournament.start.dayFinal': ' + finale',
  'tournament.start.dayBreaks': ' · {count} pause(r)',
  'tournament.start.betweenMatches': 'Tid mellom kamper',
  'tournament.start.minutes': '{minutes} min',
  'tournament.start.totalMatches': 'Totalt kamper',
  'tournament.start.settingsTitle': 'Valgte innstillinger',
  'tournament.start.playersPerGroup': 'Spillere per gruppe',
  'tournament.start.promotion': 'Opp/ned mellom faser',
  'tournament.start.promotionValue': '{count} spillere',
  'tournament.start.drawMode': 'Trekningsmodus',
  'tournament.start.pacing': 'Tidsfordeling i fase (pacing)',
  'tournament.start.groupOrder': 'Rekkefølge på grupper',
  'tournament.start.finalsPairing': 'Makker-mønster i finaler',
  'tournament.start.luckyLooser': 'Lucky Looser',
  'tournament.start.on': 'Aktiv',
  'tournament.start.off': 'Av',
  'tournament.start.eloWeight': 'ELO-vekt',
  'tournament.start.maxCourts': 'Maks baner',
  'tournament.start.drawRules': 'Trekningsregler',
  'tournament.start.ruleNoSamePartner': 'unngå lik makker',
  'tournament.start.ruleNoSameOpponent': 'unngå lik motstander',
  'tournament.start.ruleBalanceHomeAway': 'balansér hjemme/borte',
  'tournament.start.ruleBalanceElo': 'balansér ELO',
  'tournament.start.ruleNone': 'ingen',
  'tournament.start.plannedNote':
    'Alle kamper er allerede planlagt med tider og baner. Fase 1 (krysskobling A+B, C+D) har konkrete spillere. Senere faser har plassholdere som fylles inn automatisk når resultater registreres.',

  // ── tournament: one configurable day (DayBlock) ──────────────────
  'tournament.day.slots': '· {total} slots ({perCourt}/bane × {courts})',
  'tournament.day.removeDay': 'Fjern dag',
  'tournament.day.nameLabel': 'Navn på dagen',
  'tournament.day.date': 'Dato',
  'tournament.day.start': 'Start',
  'tournament.day.end': 'Slutt',
  'tournament.day.courts': 'Baner',
  'tournament.day.courtsLockedHint': 'Styres av banevalg i «Velg oppsett»',
  'tournament.day.phases': 'Antall faser',
  'tournament.day.matchesPerPlayer': '{count} kamp/spiller',
  'tournament.day.matchesPerPlayerFinal': ' ({count}+1 finale)',
  'tournament.day.matchDuration': 'Min/kamp denne dagen',
  'tournament.day.slotMath': 'slot: {slot}m ({match}+{between})',
  'tournament.day.finalsDay': 'Finaledagen',
  'tournament.day.finalsDayNote': '— plasseringskamper spilles etter siste fase (+1 kamp/spiller)',
  'tournament.day.timeUse': 'Tidsbruk',
  'tournament.day.exceeds': '(overskrider {time})',
  'tournament.day.matchesCount': '{count} kamper/spiller',
  'tournament.day.matchesCountFinal': ' (inkl. finale)',
  'tournament.day.matchesCountOverMax': ' (over maks {max}!)',
  'tournament.day.unusedTime': 'Ubrukt tid:',
  'tournament.day.unusedHours': '{hours}t ',
  'tournament.day.unusedMinutes': '{minutes}m',
  'tournament.day.moveStart': '⤴ Flytt start til {time}',
  'tournament.day.moveEnd': '⤵ Flytt slutt til {time}',
  'tournament.day.notEnoughTime': 'Ikke nok tid for {phases} faser.',
  'tournament.day.notEnoughTimeFinal': 'Ikke nok tid for {phases} faser + finale.',
  'tournament.day.extendEnd': '⤢ Utvid slutt til {time}',
  'tournament.day.breaks': 'Pauser ({count})',
  'tournament.day.addBreak': 'Legg til pause',
  'tournament.day.noBreaks': 'Ingen pauser',
  'tournament.day.breakAdviceTitle': 'Anbefalt: pause mellom fasene',
  'tournament.day.breakAdvice':
    'Med dette banevalget blir hver fase kort ({rotations} kamprotasjoner). Denne dagen har flere faser men ingen pause imellom — da rekker ikke spillerne å se sine neste kamper i tide.',
  'tournament.day.addBreakShort': '+ Legg til pause',
  'tournament.day.undoRemove': '↩ Angre fjerning',

  // ── tournament: weekday and month names, DISPLAY only (Step2Players)
  'tournament.weekday.sun': 'søndag',
  'tournament.weekday.mon': 'mandag',
  'tournament.weekday.tue': 'tirsdag',
  'tournament.weekday.wed': 'onsdag',
  'tournament.weekday.thu': 'torsdag',
  'tournament.weekday.fri': 'fredag',
  'tournament.weekday.sat': 'lørdag',
  'tournament.month.jan': 'jan',
  'tournament.month.feb': 'feb',
  'tournament.month.mar': 'mar',
  'tournament.month.apr': 'apr',
  'tournament.month.may': 'mai',
  'tournament.month.jun': 'jun',
  'tournament.month.jul': 'jul',
  'tournament.month.aug': 'aug',
  'tournament.month.sep': 'sep',
  'tournament.month.oct': 'okt',
  'tournament.month.nov': 'nov',
  'tournament.month.dec': 'des',

  // ── tournament: sign-up and publishing, step 2 (Step2Players) ────
  'tournament.signup.howTitle': 'Hvordan meldes spillerne på?',
  'tournament.signup.modeManual': 'Kjør utenfor',
  'tournament.signup.modeManualDesc':
    'Du legger til spillerne selv og starter turneringen med en gang. Passer når deltakerne allerede er klare.',
  'tournament.signup.modeActivity': 'Via Aktiviteter',
  'tournament.signup.modeActivityDesc':
    'Publiser med «Vent på påmelding». Spillerne melder seg på selv, med venteliste.',
  'tournament.signup.dateFormat': '{weekday} {day}. {month} {year}',
  'tournament.signup.publishedTitle': 'Påmelding (publisert)',
  'tournament.signup.publishTitle': 'Publiser for selvbetjent påmelding',
  'tournament.signup.publishedLead':
    'Turneringen er publisert. Spillerne melder seg på selv (med venteliste når den er full). Du kan justere pris/frist her, eller gå til påmeldingssiden.',
  'tournament.signup.publishLead':
    'Spillerne melder seg på selv, én og én. Når påmeldingen er klar starter du turneringen fra aktiviteten — da fylles oppsettet med de påmeldte spillerne.',
  'tournament.signup.price': 'Pris per spiller (kr)',
  'tournament.signup.pricePlaceholder': 'f.eks. 200',
  'tournament.signup.maxPlayers': 'Maks spillere',
  'tournament.signup.playerCount': '{count} spillere',
  'tournament.signup.maxPlayersNote':
    'Maks antall følger valgt preset ({count} spillere) fra Grunnoppsett. Påmeldte utover dette havner på venteliste.',
  'tournament.signup.levelTitle': 'Nivå — hvem kan melde seg på',
  'tournament.signup.deadlineTitle': 'Påmeldingsstopp',
  'tournament.signup.deadlineDays': 'Dager før start',
  'tournament.signup.deadlineDate': 'Bestemt dato',
  'tournament.signup.deadlineNone': 'Ingen frist',
  'tournament.signup.daysBeforeStart': 'dager før start',
  'tournament.signup.noStartDate':
    'Sett en dato under Grunnoppsett/Banetilgang først, så regnes fristen ut fra start.',
  'tournament.signup.closesOn': 'Påmelding stenger',
  'tournament.signup.closesStart': ' (start {date})',
  'tournament.signup.reservesTitle': 'Reserver (venteliste)',
  'tournament.signup.allowReserves': 'Tillat reserver',
  'tournament.signup.maxReserves': 'Maks antall reserver',
  'tournament.signup.maxStandby': 'Maks antall beredskapsreserver',
  'tournament.signup.reservesP1':
    'Når aktiviteten er full havner påmeldte på venteliste (reserver) opp til dette antallet. Reserver betaler for reserveplassen, men får refusjon dersom de ikke blir benyttet. Reserver kan',
  'tournament.signup.reservesStrong1': 'i tillegg',
  'tournament.signup.reservesP2': 'krysse av for',
  'tournament.signup.reservesStrong2': 'beredskapsreserve',
  'tournament.signup.reservesP3':
    '— blir de ikke benyttet som reserve, kan de steppe inn midt i turneringen hvis noen skader seg (og får da reserve-beløpet refundert). Beredskapsreserver må være i nærheten av anlegget og kunne stille på kort varsel, og kan velge hvilke dager de kan stille. Beredskapsreservene nummereres i den rekkefølgen de melder seg.',
  'tournament.signup.noReserves': 'Ingen venteliste — når aktiviteten er full stenges påmeldingen.',
  'tournament.signup.publishedTag': '✓ Publisert',
  'tournament.signup.saveChanges': 'Lagre endringer',
  'tournament.signup.viewSignup': 'Se påmelding →',
  'tournament.signup.publishCta': 'Publiser for påmelding →',
  'tournament.signup.calendarNote':
    'Banetid og kalender styres av oppsettet (Grunnoppsett/Banetilgang). Påmeldingen samler kun spillerne.',

  // ── tournament: the match setup, wizard step 4 (Step4Matches) ────
  'tournament.matches.generating': 'Genererer kampplan…',
  'tournament.matches.title': 'Kampoppsett',
  'tournament.matches.lead':
    'Full oversikt over alle kamper. Plassholdere fylles automatisk inn etter hvert som resultater registreres.',
  'tournament.matches.statMatches': 'Totalt kamper',
  'tournament.matches.statPlayers': 'Spillere',
  'tournament.matches.statRounds': 'Runder',
  'tournament.matches.statEnd': 'Estimert slutt',
  'tournament.matches.phase1Drawn': 'Fase 1 trukket — {count} kamper',
  'tournament.matches.phase1Pending': 'Fase 1 venter på trekning — {count} kamper',
  'tournament.matches.phase1DrawnLead':
    'Du kan trekke på nytt for å få nye randomiserte kombinasjoner.',
  'tournament.matches.phase1PendingLead':
    'Kampene trekkes basert på regler du valgte i grunnoppsettet.',
  'tournament.matches.activeRules': 'Aktive regler:',
  'tournament.matches.noRules': 'Ingen regler aktive — fullt random',
  'tournament.matches.closeRules': 'Lukk',
  'tournament.matches.editRules': 'Rediger regler',
  'tournament.matches.redraw': 'Trekk på nytt',
  'tournament.matches.drawPhase1': 'Trekk fase 1',
  'tournament.matches.ruleMirror': 'Speilrotasjon i R2',
  'tournament.matches.ruleNoSamePartner': 'Unngå samme makker',
  'tournament.matches.ruleNoSameOpponent': 'Unngå samme motstander',
  'tournament.matches.ruleBalanceHomeAway': 'Balanser hjemme/borte',
  'tournament.matches.ruleBalanceElo': 'Unngå skjeve ELO-kamper',
  'tournament.matches.modeVaried': 'Variert',
  'tournament.matches.modeVariedDesc': 'Spre makkere og motstandere bredt',
  'tournament.matches.modeMirror': 'Speilrotasjon',
  'tournament.matches.modeMirrorDesc': 'Gamle makkere blir motstandere',
  'tournament.matches.rulesNote':
    'Endringer påvirker neste trekning — trykk "Trekk på nytt" for å se ny kampoppsett.',
  'tournament.matches.tabAll': 'Full oversikt',
  'tournament.matches.tabPhase': 'Fase {phase}',
  'tournament.matches.tabFinals': 'Finaler',
  'tournament.matches.showLabel': 'Vis:',
  'tournament.matches.allDays': 'Alle dager',
  'tournament.matches.group': 'Gruppe {letter}',
  'tournament.matches.showElo': 'Vis ELO',
  'tournament.matches.explainTitle': 'Forklaring',
  'tournament.matches.explain1Label': 'Fase 1 (Runde 1-2)',
  'tournament.matches.explain1Rest': ': Krysskobling mellom grupper (A med B, C med D)',
  'tournament.matches.explain2Label': 'Fase 2-{phases} (Runde 3-{rounds})',
  'tournament.matches.explain2Rest': ': Innad i grupper',
  'tournament.matches.explain3':
    'Etter hver fase: Topp {count} rykker opp, bunn {count} rykker ned',
  'tournament.matches.explain4': 'Finaler spilles etter siste runde (plasseringskamper per gruppe)',
  'tournament.matches.perPlayerTitle': 'Antall kamper per spiller',
  'tournament.matches.allEqual': 'Alle spillere får {count} kamper',
  'tournament.matches.crossMatches': '{count} krysskamper + ',
  'tournament.matches.inGroupMatches': '{count} innad i gruppe',
  'tournament.matches.plusFinal': ' + finale',
  'tournament.matches.variation': 'Variasjon: {min}–{max} kamper i fase 1',

  // ── tournament: a player's tournament profile (TournamentPlayerModal)
  'tournament.playerModal.chartStart': 'Start',
  'tournament.playerModal.chartMatch': 'K{n}',
  'tournament.playerModal.title': 'Turneringsprofil',
  'tournament.playerModal.elo': 'ELO {elo}',
  'tournament.playerModal.reservedDot': '· Reservert plass',
  'tournament.playerModal.close': 'Lukk',
  'tournament.playerModal.replacedOut': 'Byttet ut',
  'tournament.playerModal.replacedOutLead':
    'Spilleren ble byttet ut underveis. Kun kamper FØR byttet teller for denne spilleren.',
  'tournament.playerModal.substitute': 'Innbytter',
  'tournament.playerModal.substituteLead':
    'Kom inn som erstatter. Kun kamper FRA byttet og fremover teller for denne spilleren.',
  'tournament.playerModal.reserved': 'Reservert plass',
  'tournament.playerModal.reservedLead':
    'Denne plassen er ikke fylt med en navngitt spiller. Klikk på navnet i kampene for å fylle inn.',
  'tournament.playerModal.winsLossesDraws': 'Seire-tap-uavgjort',
  'tournament.playerModal.winsLosses': 'Seire-tap',
  'tournament.playerModal.diff': 'Diff: {diff}',
  'tournament.playerModal.matchesPlayed': 'Kamper spilt',
  'tournament.playerModal.homeAway': 'Hjemme–Borte',
  'tournament.playerModal.eloProgress': 'ELO-utvikling',
  'tournament.playerModal.eloProgressNote': 'Etter hver av {count} fullførte kamper',
  'tournament.playerModal.injured': 'Skadet',
  'tournament.playerModal.injuredLead': '— utelatt fra nye runder. ELO og poeng er bevart.',
  'tournament.playerModal.fillNameTitle': 'Fyll inn navnet på denne reserverte plassen',
  'tournament.playerModal.replaceTitle': 'Bytt ut denne spilleren med en innbytter',
  'tournament.playerModal.fillName': 'Fyll inn navn',
  'tournament.playerModal.replace': 'Bytt ut spilleren',
  'tournament.playerModal.markBackTitle':
    'Marker spilleren som tilbake fra skade — vil delta i kommende runder',
  'tournament.playerModal.markInjuredTitle':
    'Marker som skadet — utelates fra nye runder, eksisterende poeng beholdes',
  'tournament.playerModal.markBack': 'Markér tilbake',
  'tournament.playerModal.markInjured': 'Markér skadet',
  'tournament.playerModal.fullHistoryTitle':
    'Se spillerens fulle historikk på tvers av alle turneringer',
  'tournament.playerModal.fullHistory': 'Full historikk',
  'tournament.playerModal.upcoming': 'Kommende ({count})',
  'tournament.playerModal.finished': 'Ferdigspilt ({count})',
  'tournament.playerModal.latestFirst': 'Siste først',
  'tournament.playerModal.noMatches': 'Ingen kamper funnet for denne spilleren ennå',
  'tournament.playerModal.confirmBackTitle': 'Markér {name} som tilbake?',
  'tournament.playerModal.confirmInjuredTitle': 'Markér {name} som skadet?',
  'tournament.playerModal.confirmBackLead':
    'Spilleren vil delta i kommende runder. Eksisterende poeng og statistikk er uendret.',
  'tournament.playerModal.confirmInjuredLead':
    'Spilleren utelates fra nye runder. Eksisterende ELO, poeng og kamphistorikk beholdes — vises nederst i Lederboard med "Skadet"-tag.',
  'tournament.playerModal.confirmTooFew':
    '⚠️ Færre enn 4 aktive spillere igjen etter dette — turneringen kan ikke generere nye runder.',
  'tournament.playerModal.cancel': 'Avbryt',

  // ── tournament: the Padel24 wizard shell (Padel24Wizard) ─────────
  'tournament.wizard.occupied': 'Opptatt',
  'tournament.wizard.consDemoteOne':
    '{count} spiller flyttes fra bekreftet plass til reserve (de sist påmeldte). De beholder rekkefølgen og rykker opp igjen hvis du øker antallet.',
  'tournament.wizard.consDemoteMany':
    '{count} spillere flyttes fra bekreftet plass til reserve (de sist påmeldte). De beholder rekkefølgen og rykker opp igjen hvis du øker antallet.',
  'tournament.wizard.consPromoteOne': '{count} reserve rykker opp til bekreftet plass.',
  'tournament.wizard.consPromoteMany': '{count} reserver rykker opp til bekreftet plass.',
  'tournament.wizard.consMaxChanged': 'Maks antall endres fra {from} til {to}.',
  'tournament.wizard.consOutsideOne':
    '{count} allerede påmeldt er utenfor det nye nivåkravet. De beholdes, men nye spillere utenfor nivå kan ikke melde seg på selv.',
  'tournament.wizard.consOutsideMany':
    '{count} allerede påmeldte er utenfor det nye nivåkravet. De beholdes, men nye spillere utenfor nivå kan ikke melde seg på selv.',
  'tournament.wizard.consNewLevel': 'Nytt nivåkrav gjelder for nye påmeldinger.',
  'tournament.wizard.consImmediateOne':
    'Endringene gjelder umiddelbart for påmeldingen ({count} påmeldt).',
  'tournament.wizard.consImmediateMany':
    'Endringene gjelder umiddelbart for påmeldingen ({count} påmeldte).',
  'tournament.wizard.cancel': 'Avbryt',
  'tournament.wizard.savedTitle': 'Lagret!',
  'tournament.wizard.unsavedTitle': 'Ulagrede endringer — klikk for å lagre som "{name}"',
  'tournament.wizard.nothingToSaveTitle': 'Ingen endringer å lagre',
  'tournament.wizard.saved': 'Lagret',
  'tournament.wizard.save': 'Lagre',
  'tournament.wizard.helpTitle': 'Åpne hjelp for Padel24-format',
  'tournament.wizard.help': 'Hjelp',
  'tournament.wizard.back': 'Tilbake',
  'tournament.wizard.next': 'Neste',
  'tournament.wizard.createTitle':
    'Opprett turneringen — du kommer til en forhåndsvisning der du kan justere grupper og trekning før du starter',
  'tournament.wizard.create': 'Opprett turnering →',
  'tournament.wizard.stepSetup': 'Grunnoppsett',
  'tournament.wizard.stepPlayers': 'Spillere',
  'tournament.wizard.fillAll': 'Fyll inn alle felter',
  'tournament.wizard.pickAtLeast8': 'Velg minst 8 ({count}/8)',
  'tournament.wizard.mustBeEven': 'Antall må være partall',
  'tournament.wizard.confirmTitle': 'Lagre endringer i påmeldingen?',
  'tournament.wizard.confirmLead':
    'Aktiviteten ligger ute til påmelding. Endringene får følgende konsekvenser:',
  'tournament.wizard.saveChanges': 'Lagre endringer',

  // ── tournament: the basic setup, wizard step 1 (Step1Setup) ──────
  'tournament.setup.title': 'Grunnoppsett',
  'tournament.setup.lead': 'Navn, dager, baner, kamptid og pauser.',
  'tournament.setup.savePublished': 'Lagre endringer',
  'tournament.setup.savePublishedTitle':
    'Lagre endringer i den publiserte påmeldingen (du får en bekreftelse med konsekvenser)',
  'tournament.setup.publishedBannerBefore':
    'Aktiviteten ligger ute til påmelding. Endrer du preset (baner/spillere) eller banevalg, trykk',
  'tournament.setup.publishedBannerAfter':
    '— du får en bekreftelse med konsekvensene. Husk å oppdatere banereservasjonene per dag så de stemmer med nytt antall baner.',
  'tournament.setup.mismatchDayOne': '{count} dag',
  'tournament.setup.mismatchDayMany': '{count} dager',
  'tournament.setup.mismatchLead':
    '({days}) har banereservasjoner som ikke stemmer med antall baner lenger. Rull ned til hver dag og trykk',
  'tournament.setup.mismatchButton': '«Oppdater reservasjon»',
  'tournament.setup.name': 'Turneringsnavn',
  'tournament.setup.organiser': 'Arrangør',
  'tournament.setup.auto': 'Auto',
  'tournament.setup.pickPreset': 'Velg oppsett',
  'tournament.setup.perGroupShort': '{count} sp/gruppe',
  'tournament.setup.pickPresetLead':
    'Padel24 fungerer best med faste spilleranatall. Velg hvor mange spillere du planlegger å arrangere for. Tidsoppsettet (Fre 16:00-23:00 + Lør 9:00-17:00) holdes uendret.',
  'tournament.setup.playersWord': 'spillere',
  'tournament.setup.groupsCount': '{count} grupper',
  'tournament.setup.suitOddTitle':
    'Oddetall grupper — siste gruppe spiller innad i fase 1 i stedet for å krysskobles med en nabogruppe. Fungerer helt fint; cross-kobling av alle grupper er bare marginalt bedre.',
  'tournament.setup.suitManyTitle':
    'Mange grupper — vanskeligere å rykke fra bunn til topp, og spillertallet kan ikke deles i færre, større grupper.',
  'tournament.setup.minimum': 'minimum',
  'tournament.setup.courtsWord': 'baner',
  'tournament.setup.smallGroupsTitle': 'Mindre grupper (12 per gruppe)',
  'tournament.setup.smallGroupsOn':
    'Aktiv: {groups} grupper à 12 spillere. 4 rykker opp/ned per fase.',
  'tournament.setup.smallGroupsOff':
    'Standard: {groups} grupper à {size}. Slå på for {small} mindre grupper à 12.',
  'tournament.setup.smallGroupsToggle': 'Veksle mindre grupper',
  'tournament.setup.smallGroupsStdLabel': 'Standard ({groups} grupper à {size}):',
  'tournament.setup.smallGroupsStdBody':
    'Færre grupper å klatre gjennom, og {promotion} spillere rykker opp/ned per fase — spillere kan rykke lengre opp gjennom turneringen.',
  'tournament.setup.smallGroupsSmallLabel': 'Mindre grupper (12):',
  'tournament.setup.smallGroupsSmallBody':
    'Snevrere ELO-spenn i hver gruppe gir jevnere kamper, men flere grupper å klatre gjennom og kun 4 opp/ned per fase.',
  'tournament.setup.suitHeading': '{label}: {players} spillere',
  'tournament.setup.suitBody':
    'Dette spillertallet kan bare deles i {groups} små grupper à 12 — det finnes ingen god måte å lage færre, større grupper på. Med {groups} grupper blir det {degree}vanskelig for spillere i de laveste gruppene å rykke helt til topps i løpet av turneringen. Vurder et nærliggende spillertall (f.eks. 96 eller 120) som kan deles i færre, større grupper.',
  'tournament.setup.suitDegreeVery': 'svært ',
  'tournament.setup.suitDegreeQuite': 'ganske ',
  'tournament.setup.nonStandard': 'Ikke-standard spillertall: {players}',
  'tournament.setup.nonStandardLead':
    'Vi anbefaler å velge ett av oppsettene over for optimal banebruk.',
  'tournament.setup.oddGroupNote':
    'Oddetalls grupper: siste gruppe spiller innad i fase 1 i stedet for å krysskobles med en partnergruppe. Fungerer helt fint.',
  'tournament.setup.optMinimum': 'Standard',
  'tournament.setup.optMinimumPros':
    'Lengst pauser mellom kampene, jevn flyt, krever færrest baner.',
  'tournament.setup.optMinimumCons': 'Tar hele dagen — sen slutt.',
  'tournament.setup.optComfortable': 'Romslig',
  'tournament.setup.optComfortablePros': 'Kortere dag, fortsatt gap mellom egne kamper.',
  'tournament.setup.optComfortableCons':
    'Krever ~50 % flere baner. Noen rotasjoner har ledig bane.',
  'tournament.setup.optShort': 'Effektiv',
  'tournament.setup.optShortPros': 'Kort dag, alle kamper tett pakket.',
  'tournament.setup.optShortCons':
    'Dobbelt så mange baner. Lite pause — R1 og R2 nær hverandre, pause mellom faser anbefales.',
  'tournament.setup.optFast': 'Komprimert',
  'tournament.setup.optFastPros': 'Svært kort dag.',
  'tournament.setup.optFastCons': 'Mange baner, nesten ingen pause for spillerne.',
  'tournament.setup.optAllAtOnce': 'Alle samtidig ⚡',
  'tournament.setup.optAllAtOncePros': 'Kortest mulig — hele fasen på to kamprotasjoner.',
  'tournament.setup.optAllAtOnceCons':
    'Krever én bane per fire spillere. Ingen gap; R1 og R2 rett etter hverandre.',
  'tournament.setup.courtOptions': 'Banealternativer',
  'tournament.setup.colCourts': 'Baner',
  'tournament.setup.colDescription': 'Beskrivelse',
  'tournament.setup.colRotations': 'Rot/fase',
  'tournament.setup.colDay1': 'Dag 1',
  'tournament.setup.colDay2': 'Dag 2',
  'tournament.setup.recommended': 'Anbefalt',
  'tournament.setup.freeCourtsOne': '· {free} ledig bane i {rotations} av {total} rot.',
  'tournament.setup.freeCourtsMany': '· {free} ledige baner i {rotations} av {total} rot.',
  'tournament.setup.rotationsValue': '{count} stk',
  'tournament.setup.utilisation': '· Baneutnyttelse {percent} %.',
  'tournament.setup.courtOptionsFooter':
    'Maks 4 kamper/dag (+1 finale siste dag). Rot/fase = antall kamprotasjoner per fase — færre er raskere. «Én kamp gap» krever minst 4.',
  'tournament.setup.tooFewCourts': 'Du har for få baner ({courts} av {needed} nødvendige)',
  'tournament.setup.tooFewCourtsLead':
    'Med {players} spillere trenger du minst {needed} baner for å få plass innenfor tidsrammen.',
  'tournament.setup.formatTitle': 'Turneringsformat',
  'tournament.setup.plusFinal': ' + finale',
  'tournament.setup.phasesSummary': '{phases} faser{final} = {matches} kamper/spiller',
  'tournament.setup.phasesWanted': 'Ønsket: {phases} faser{final} ({matches} kamper)',
  'tournament.setup.autoDistribute': 'Fordel automatisk',
  'tournament.setup.phasesMissing': 'Mangler {count} fase(r)',
  'tournament.setup.phasesTooMany': '{count} fase(r) for mye',
  'tournament.setup.capacity': 'Kapasitet',
  'tournament.setup.playerCount': 'Antall spillere',
  'tournament.setup.lockedByPreset': 'Styres av valgt oppsett — endre via «Velg oppsett»',
  'tournament.setup.totalPhases': 'Faser totalt',
  'tournament.setup.maxPerDay': 'Maks kamper/dag',
  'tournament.setup.capacityLine':
    '{players} spillere · {groupMatches} gruppekamper{final} · maks {max}/dag (finale +1)',
  'tournament.setup.oddPlayers': '⚠ Oddetall — padel krever partall!',
  'tournament.setup.includeFinal': 'Inkluder finale',
  'tournament.setup.finalYes': '✓ Ja (+1 kamp)',
  'tournament.setup.finalNo': 'Nei',
  'tournament.setup.finalNote': 'Plasseringskamper for alle.',
  'tournament.setup.groupsTitle': 'Grupper',
  'tournament.setup.autoTune': 'Autotilpass',
  'tournament.setup.perGroup': 'Per gruppe',
  'tournament.setup.promotionPerPhase': 'Opp/ned per fase',
  'tournament.setup.groupsLabel': '{count} grupper:',
  'tournament.setup.unevenGroup': 'Gruppe {letter} har bare {count} spillere',
  'tournament.setup.oddGroups':
    '{count} grupper er oddetall — gruppe {letter} kan ikke krysskobles og spiller innad i fase 1. Partall grupper anbefales for jevnere format.',
  'tournament.setup.promotionLine': '↑{up} opp · {stay} blir · ↓{up} ned',
  'tournament.setup.recommendedValue': '(anbefalt: {value})',
  'tournament.setup.drawRulesTitle': 'Trekningsregler',
  'tournament.setup.drawRulesLead':
    'Når en ny fase starter, trekkes kamper for de nye gruppene. Velg trekningsmodus og hvilke regler som skal gjelde.',
  'tournament.setup.modeVaried': 'Variert',
  'tournament.setup.modeVariedDesc': 'Spre makkere og motstandere bredt',
  'tournament.setup.modeMirror': 'Speilrotasjon',
  'tournament.setup.modeMirrorDesc': 'Gamle makkere blir motstandere i neste runde',
  'tournament.setup.mirrorHowTitle': 'Slik fungerer Speilrotasjon',
  'tournament.setup.mirrorHow1': 'Runde 1: tilfeldig parring som følger reglene under',
  'tournament.setup.mirrorHow2':
    'Runde 2: hver spiller møter sin runde 1-makker som motstander (ny makker)',
  'tournament.setup.activeRules': 'Aktive regler (prioritert)',
  'tournament.setup.ruleNoSamePartner': 'Unngå samme makker',
  'tournament.setup.ruleNoSamePartnerDesc': 'Spillere får nye makkere fremfor å gjenta partnerskap',
  'tournament.setup.ruleNoSameOpponent': 'Unngå samme motstander',
  'tournament.setup.ruleNoSameOpponentDesc':
    'Spillere møter nye motstandere fremfor å gjenta motstanderpar',
  'tournament.setup.ruleBalanceHomeAway': 'Balanser hjemme/borte',
  'tournament.setup.ruleBalanceHomeAwayDesc': 'Forsøk å gi alle like mange hjemme- og bortekamper',
  'tournament.setup.ruleBalanceElo': 'Unngå skjeve ELO-kamper',
  'tournament.setup.ruleBalanceEloDesc': 'Hindrer ekstrem ubalanse mellom lag (toleranse 100 ELO)',
  'tournament.setup.finalsPairingTitle': 'Makker-mønster i finaler',
  'tournament.setup.finalsPairingLead':
    'Etter siste fase spiller plass 1–4 finale 1, plass 5–8 finale 2, osv. Velg hvordan makkere parres opp:',
  'tournament.setup.pairing13': '1 + 3 vs 2 + 4',
  'tournament.setup.pairing13Desc': 'Best og dårligst på hvert lag (jevnest)',
  'tournament.setup.pairing12': '1 + 2 vs 3 + 4',
  'tournament.setup.pairing12Desc': 'Best mot dårligst (mest dramatisk)',
  'tournament.setup.luckyLooser': 'Lucky Looser',
  'tournament.setup.luckyOn': 'Aktiv',
  'tournament.setup.luckyOff': 'Av',
  'tournament.setup.luckyLead':
    'Hver gruppes vinner rykker opp og spiller bronsefinalen (8.-plass) i gruppen over. Gruppens egne spillere på plass 8 og nedover skyves da én plass ned (8→9, 9→10 …) — derfor rykker sistemann ut og ned til 1.-plass i gruppen under. Påvirker kun finaletrekningen.',
  'tournament.setup.luckyToggle': 'Veksle Lucky Looser',
  'tournament.setup.luckyNote':
    'En spiller fra gruppen under får en sjanse til å spille bronsefinalen i en høyere gruppe. Finalene trekkes samlet når alle grupper er ferdige (kan ikke trekkes per gruppe).',
  'tournament.setup.rankingTitle': 'Rangering innen gruppe (fase-til-fase)',
  'tournament.setup.rankingNote':
    'Etter hver fase rangeres spillere innen samme gruppe i denne rekkefølgen — vinneren av hver gruppe rykker opp, taperen rykker ned. Skadede spillere holdes utenfor rangeringen. Innbyrdes oppgjør brukes kun hvis de to spillerne har møtt hverandre som motstandere, og teller møter fra ALLE faser — ikke bare den fasen tabellen viser. Er alt likt helt ned, trekkes det lodd, og gruppetabellen merker raden med et ikon som viser hva som avgjorde.',
  'tournament.setup.eloWeightTitle': 'ELO-vekt for turneringen',
  'tournament.setup.eloWeightLead':
    'Bestemmer hvor mye kampene i denne turneringen påvirker spillernes ELO. Endring i etterkant gjelder kun nye kamper.',
  'tournament.setup.pacingTitle': 'Tidsplan for kampene',
  'tournament.setup.pacingAllAtOnce': 'Alle spiller samtidig',
  'tournament.setup.pacingPacked': 'Tett pakket',
  'tournament.setup.pacingAllAtOnceLead':
    'Med dette banevalget får hele fasen plass på én kamprotasjon — alle spiller samtidig. Det finnes ingen rekkefølge eller gap å velge.',
  'tournament.setup.pacingPackedLead':
    'Med {courts} baner spilles fasen på bare {rotations} kamprotasjoner. Kampene pakkes så tett og jevnt som mulig — det er ikke plass til gap mellom R1 og R2, og plassering er derfor ikke et valg her.',
  'tournament.setup.pacingOneGap': 'Én kamp gap (anbefalt)',
  'tournament.setup.pacingOneGapDesc': 'R1, vent én kamp, R2 på samme bane',
  'tournament.setup.pacingOneGapWait': '~40 min',
  'tournament.setup.pacingBackToBack': 'Back-to-back',
  'tournament.setup.pacingBackToBackDesc': 'R1 og R2 rett etter hverandre',
  'tournament.setup.pacingBackToBackWait': '~5 min',
  'tournament.setup.pacingLead': 'Hvordan plasseres de to kampene innen samme fase i tid.',
  'tournament.setup.waitLabel': 'Ventetid:',
  'tournament.setup.orderTitle': 'Rekkefølge i fasen',
  'tournament.setup.orderLead':
    'Hvilken gruppe spiller først? Gjelder også finalene — Finale 1 (Gull) plasseres motsatt: spilles sist når beste spiller sist, først når beste spiller først.',
  'tournament.setup.orderBestLast': 'Beste sist (klimaks)',
  'tournament.setup.orderBestLastDesc':
    'Svakeste gruppe åpner fasen, gruppe A avslutter. Finale 1 (Gull) spilles helt til slutt.',
  'tournament.setup.orderBestFirst': 'Beste først',
  'tournament.setup.orderBestFirstDesc':
    'Gruppe A åpner fasen, svakeste avslutter. Finale 1 (Gull) spilles først.',
  'tournament.setup.padFlowTitle': 'Banepad — roter etter',
  'tournament.setup.padFlowLead':
    'Hva styrer at en bane går videre til neste kamp? Kan også overstyres per bane underveis.',
  'tournament.setup.padFlowResult': 'Resultat',
  'tournament.setup.padFlowResultDesc':
    'Bane bytter straks resultatet er registrert. Banen ruller videre uten å vente på klokken.',
  'tournament.setup.padFlowResultHint': 'Anbefalt for Padel24 — rask flyt',
  'tournament.setup.padFlowTime': 'Tidspunkt',
  'tournament.setup.padFlowTimeDesc':
    'Bane bytter når neste kamps starttid kommer. Spillerne ser "neste kamp om X min" mellom kampene.',
  'tournament.setup.padFlowTimeHint': 'Brukbart hvis kampplan er stram',
  'tournament.setup.betweenTitle': 'Tid mellom kamper',
  'tournament.setup.minutes': 'Minutter',
  'tournament.setup.betweenNote': 'Bytte av lag + oppvarming. Kamptid settes per dag.',
  'tournament.setup.courtsTitle': 'Baner',
  'tournament.setup.perDay': 'Per dag',
  'tournament.setup.allDays': 'Alle dager',
  'tournament.setup.lockedByCourtChoice': 'Styres av banevalg i «Velg oppsett»',
  'tournament.setup.courtsFromTable': 'Antall baner styres av banevalg-tabellen i «Velg oppsett».',
  'tournament.setup.courtsClubNormal': 'Klubbens normale: {courts}. Kan overstyres per dag.',
  'tournament.setup.courtsFromClubSettings': 'Settes i klubbinnstillingene som default.',
  'tournament.setup.courtsPerDayNote': 'Ulikt per dag. Juster nedenfor, eller',
  'tournament.setup.courtsSameForAll': 'samme på alle',
  'tournament.setup.daysTitle': 'Dager',
  'tournament.setup.addDay': 'Legg til dag',
  'tournament.setup.summaryTitle': 'Sammendrag',
  'tournament.setup.sumPlayers': 'Spillere',
  'tournament.setup.sumPhases': 'Faser',
  'tournament.setup.sumPhasesValue': '{phases} ({matches} kamper)',
  'tournament.setup.sumMatchesPerPlayer': 'Kamper/spiller',
  'tournament.setup.sumPlusOneFinal': ' + 1 finale',
  'tournament.setup.sumGroupCount': 'Antall grupper',
  'tournament.setup.sumGroupCountValue': '{count} grupper',
  'tournament.setup.sumUneven': ' (ujevn)',
  'tournament.setup.sumPerGroup': 'Per gruppe',
  'tournament.setup.sumPlayersValue': '{count} spillere',
  'tournament.setup.sumPromotion': 'Opp/ned',
  'tournament.setup.sumMaxPerDay': 'Maks/dag',
  'tournament.setup.sumPlusFinal': ' (+1 finale)',
  'tournament.setup.sumPhaseMismatch': 'Dagene har {actual} faser, ønsket {wanted}.',
  'tournament.setup.sumBetween': 'Tid mellom kamper: {minutes} min. Kamptid varierer per dag.',
  'tournament.setup.sumCapacity': 'Maks kapasitet',
  'tournament.setup.sumCapacityNote': 'Maks mulig med nåværende dager, tider og baner.',
  'tournament.setup.sumOverCapacity': 'Planlagt {players} er over maks!',
  'tournament.setup.sumBreaks': 'Pauser',
  'tournament.setup.sumBreakAfterPhase': 'etter Fase {phase}',
  'tournament.setup.sumBreakMinutes': '{minutes}m',

  // ── tournament: picking the players (TournamentPlayerSelection) ──
  'tournament.pick.clubA': 'Klubb A',
  'tournament.pick.clubB': 'Klubb B',
  'tournament.pick.lastTournament': 'Siste turnering',
  'tournament.pick.title': 'Spillere',
  'tournament.pick.lead':
    'Velg konkrete spillere, reserver plasser, eller fyll opp med demo-spillere.',
  'tournament.pick.quickTools': 'Hurtigverktøy',
  'tournament.pick.filledCount': '{filled}/{total} fylt · {left} igjen',
  'tournament.pick.reserveTitle': 'Reserver plasser',
  'tournament.pick.reserveLead':
    'Tomme plasser med startnummer — fyll inn navn etter hvert som påmeldinger kommer inn.',
  'tournament.pick.count': 'Antall:',
  'tournament.pick.reserve': 'Reserver',
  'tournament.pick.reservedSoFarOne': '{count} reservert så langt',
  'tournament.pick.reservedSoFarMany': '{count} reserverte så langt',
  'tournament.pick.demoTitle': 'Demo-spillere',
  'tournament.pick.demoLead': 'Tilfeldig utvalg fra databasen — nyttig for testing av oppsettet.',
  'tournament.pick.fillRest': 'Fyll opp resten ({count})',
  'tournament.pick.orAdd': 'eller legg til',
  'tournament.pick.add': 'Legg til',
  'tournament.pick.searchPlaceholder': 'Søk navn… (legges til med Enter eller klikk)',
  'tournament.pick.clearSearch': 'Tøm søket',
  'tournament.match.enterScore': 'Registrer resultat',
  'tournament.pick.noHits': 'Ingen treff for "{search}".',
  'tournament.pick.createPlayer': 'Opprett ny spiller?',
  'tournament.pick.newPlayer': 'Ny spiller',
  'tournament.pick.clearAll': 'Tøm alle',
  'tournament.pick.addAllTitle': 'Legg til alle som ikke allerede er valgt',
  'tournament.pick.addAll': '+ Legg til alle',
  'tournament.pick.lastTournamentMeta': '{name} · {count} spillere',
  'tournament.pick.recentlyPlayed': 'Nylig spilt',
  'tournament.pick.teams': 'Lag',
  'tournament.pick.ok': '✓ OK',
  'tournament.pick.minPlayers': 'min {min}',
  'tournament.pick.notEven': 'ikke partall',
  'tournament.pick.playerCountOne': '{count} spiller',
  'tournament.pick.playerCountMany': '{count} spillere',
  'tournament.pick.avgElo': 'Snitt-ELO:',
  'tournament.pick.noTeams': 'Ingen lag dannet ennå.',
  'tournament.pick.noTeamsLead': 'Velg spillere så pares de automatisk:',
  'tournament.pick.noTeamsExample': '1+2 = Lag 1, 3+4 = Lag 2, …',
  'tournament.pick.colPlayer1': 'Spiller 1',
  'tournament.pick.colPlayer2': 'Spiller 2',
  'tournament.pick.colAvg': 'Snitt',
  'tournament.pick.sortedByAvgElo': 'Sortert på snitt-ELO (klikk for å fjerne)',
  'tournament.pick.sortByAvgElo': 'Klikk for å sortere etter snitt-ELO',
  'tournament.pick.clickToSwap': 'Klikk for å bytte ut',
  'tournament.pick.levelTitle': 'Nivå {level} — {name}',
  'tournament.pick.teamLabel': 'Lag {number}',
  'tournament.pick.waitingForPartner': 'venter på makker',
  'tournament.pick.teamAvgEloTitle': 'Snitt-ELO for laget',
  'tournament.pick.removeTeamTitle': 'Fjern hele Lag {number}',
  'tournament.pick.allTeamsPlay': 'Alle {teams} lag spiller hver runde',
  'tournament.pick.onCourtsOne': 'på {courts} bane.',
  'tournament.pick.onCourtsMany': 'på {courts} baner.',
  'tournament.pick.rotationTitle': 'Innbytter-rotasjon:',
  'tournament.pick.rotationPlaying': '{teams} lag spiller,',
  'tournament.pick.rotationSitting': '{teams} sitter over',
  'tournament.pick.rotationNote':
    'per runde. Appen veksler så alle får like mange sittover-runder.',
  'tournament.pick.timeHoursMinutes': '{hours} t {minutes} min',
  'tournament.pick.timeHours': '{hours} t',
  'tournament.pick.timeMinutes': '{minutes} min',
  'tournament.pick.roundRobinTitle': 'Full round-robin:',
  'tournament.pick.roundRobinRoundOne': 'runde',
  'tournament.pick.roundRobinRoundMany': 'runder',
  'tournament.pick.roundRobinMatchesOne': '({matches} kamp totalt) ·',
  'tournament.pick.roundRobinMatchesMany': '({matches} kamper totalt) ·',
  'tournament.pick.oddTeamsTitle': 'Oddetall lag ({count}):',
  'tournament.pick.oddTeamsLead':
    'ett lag må sitte over ekstra ofte. Hvis du kjører sluttspill får siste lag i rangeringen ingen finale — vurder "Totalpoeng" som vinner-kriterium istedet.',
  'tournament.pick.entrants': 'Påmeldte',
  'tournament.pick.balanced': '✓ Balansert',
  'tournament.pick.unevenBalance': 'Ujevn balanse',
  'tournament.pick.maleCount': '♂ {count}',
  'tournament.pick.femaleCount': '♀ {count}',
  'tournament.pick.noPlayers': 'Ingen spillere valgt ennå.',
  'tournament.pick.noPlayersLead': 'Bruk søket, hurtiglistene eller hurtigverktøyene.',
  'tournament.pick.menColumn': '♂ Menn ({count})',
  'tournament.pick.noMen': 'Ingen menn valgt',
  'tournament.pick.womenColumn': '♀ Kvinner ({count})',
  'tournament.pick.noWomen': 'Ingen kvinner valgt',
  'tournament.pick.clickToFill': 'Klikk for å fylle inn',
  'tournament.pick.clickToEdit': 'Klikk for å redigere',
  'tournament.pick.clickToFillName': 'Klikk for å fylle inn navn',
  'tournament.pick.remove': 'Fjern',
  'tournament.pick.unevenSplit': 'Ujevn fordeling',
  'tournament.pick.divisibleByFour': 'Antall må deles på 4',
  'tournament.pick.notDivisible': 'ikke {divisor}-delelig',
  'tournament.pick.clubCount': '{club}: {count}',
  'tournament.pick.autoBalanceTitle': 'Fordel ELO-balansert annenhver til A/B',
  'tournament.pick.autoBalance': '↔ Auto',
  'tournament.pick.namedCount': '{count} navngitt',
  'tournament.pick.emptyCount': '{count} tomme',
  'tournament.pick.newTag': 'Ny',
  'tournament.pick.notFilled': 'Ikke fylt inn — klikk for å velge',
  'tournament.pick.removeFromEntrants': 'Fjern fra påmeldte',
  'tournament.pick.group': 'Gruppe {letter}',
  'tournament.pick.dragToOtherClub': 'Dra for å flytte til motsatt klubb',
  'tournament.pick.moveToOtherClub': 'Flytt til motsatt klubb',
  'tournament.pick.dropPlayersHere': 'Drag spillere hit',
  'tournament.pick.missingOnePlayer': '· Mangler 1 spiller',
  'tournament.pick.moveTeamTitle': 'Flytt hele laget til motsatt klubb',
  'tournament.pick.moveClub': 'Flytt klubb',
  'tournament.pick.teamCount': '{count} lag',
  'tournament.pick.eloLevel': 'ELO {elo} · Nivå {level}',
  'tournament.pick.newSuffix': '· Ny',

  // ── tournament: the running tournament (TournamentViewActive) ────
  'tournament.active.toastStartFirst': 'Start turneringen før du registrerer resultater.',
  'tournament.active.toastRecordFailed': 'Kunne ikke registrere resultatet',
  'tournament.active.home': 'Hjem',
  'tournament.active.helpTitle': 'Åpne hjelp for Padel24-format',
  'tournament.active.help': 'Hjelp',
  'tournament.active.archivedTitle': 'Du ser på en arkivert turnering — endringer kan ikke gjøres',
  'tournament.active.archived': 'Arkivert · Kun lesing',
  'tournament.active.statusFinals': 'Plasseringskamper',
  'tournament.active.statusDone': 'Avsluttet',
  'tournament.active.statusRunning': 'Fase {phase}/{phasesTotal} · Runde {round}/{roundsTotal}',
  'tournament.active.resumeTitle':
    'Gjør turneringen aktiv igjen så du kan fortsette å spille. Den vises som «Pågår» på hovedsiden, og arkiv-kopien beholdes til du lagrer på nytt.',
  'tournament.active.resume': 'Fortsett turneringen',
  'tournament.active.randomRotationTitle':
    'Fyll inn tilfeldige resultater kun for den tidligste pågående kamprotasjonen (samme klokkeslett, alle baner)',
  'tournament.active.randomRotation': '🎲 Random (kamprotasjon)',
  'tournament.active.randomPhaseTitle':
    'Fyll inn tilfeldige resultater på alle åpne kamper i denne fasen. Når siste fase er ferdig: klikk igjen for å spille finalene.',
  'tournament.active.randomPhase': '🎲 Random (denne fase)',
  'tournament.active.randomAllTitle': 'Simulér hele turneringen til ferdig',
  'tournament.active.randomAll': '🎲 Random (hele turneringen)',
  'tournament.active.standbyTitle':
    'Vis beredskapsreserver — spillere som kan steppe inn ved skade',
  'tournament.active.standbyButton': 'Beredskapsreserver ({count})',
  'tournament.active.backToSetupTitle':
    'Gå tilbake til oppsettet og endre innstillinger (mulig så lenge ingen resultater er registrert)',
  'tournament.active.backToSetup': 'Tilbake til oppsett',
  'tournament.active.start': 'Start turneringen',
  'tournament.active.revertTitle':
    'Sett tilbake til «Klar til start» — gjenåpner påmelding hvis turneringen kom fra en aktivitet',
  'tournament.active.revert': 'Tilbake til klar til start',
  'tournament.active.pauseTitle':
    'Sett turneringen på pause — parker den så du kan gjenoppta en annen dag. (Å gå hjem pauser den IKKE — da fortsetter den å vise «Pågår».)',
  'tournament.active.pause': 'Pause',
  'tournament.active.continue': 'Fortsett',
  'tournament.active.complete': 'Fullfør turnering',
  'tournament.active.finishTitle': 'Avslutt turnering',
  'tournament.active.finish': 'Avslutt',
  'tournament.active.previewTitle': 'Forhåndsvisning',
  'tournament.active.previewLead':
    'Turneringen er opprettet, men ikke startet. Juster grupper, trekning og oppsett fritt — resultater kan ikke registreres ennå. Trykk «Start turnering» når du er klar; da låses oppsettet og banepaden åpnes.',
  'tournament.active.saveForLaterTitle':
    'Lagre turneringen under «Pausede turneringer» og gjenoppta senere — uten å starte den',
  'tournament.active.saveForLater': 'Lagre til senere',
  'tournament.active.previewSignupLead':
    'Bygget fra påmeldingen. Påmelding og reserve er fortsatt åpen, og statusen er ikke endret. Du kan justere oppsettet, gå tilbake til påmelding, eller trykke «Start turneringen» når du er klar — da stenges påmeldingen.',
  'tournament.active.backToSignup': 'Tilbake til påmelding',
  'tournament.active.doneTitle': 'Turneringen er ferdig! 🎉',
  'tournament.active.doneLead':
    'Alle kamper er spilt og sluttrangering er klar. Lagre turneringen for å oppdatere spillernes ELO og se den i arkivet — eller slett den uten å lagre.',
  'tournament.active.saveAndExit': 'Lagre og avslutt',
  'tournament.active.saveAndExitDesc':
    'Turneringen flyttes til turneringshistorikk på forsiden. Du kan åpne den igjen for å se resultater.',
  'tournament.active.deleteWithoutSaving': 'Slett uten å lagre',
  'tournament.active.deleteWithoutSavingDesc': 'Alle data går tapt. Kan ikke angres.',
  'tournament.active.earlyDrawTitle': 'Trekk neste fase tidlig — én gruppe om gangen',
  'tournament.active.earlyDrawLead':
    'En gruppe kan trekkes når den selv og nabogruppene direkte over og under også er ferdige (spillere rykker opp/ned mellom nabogrupper). Kampene fylles inn på sin opprinnelige plass i timeplanen.',
  'tournament.active.drawAllTitle': 'Trekk Fase {phase} for alle {groups} klare grupper på en gang',
  'tournament.active.drawAll': 'Trekk alle ({count})',
  'tournament.active.drawGroupTitle': 'Trekk Fase {phase} for gruppe {group}',
  'tournament.active.drawGroup': 'Trekk Fase {phase} for {group}',
  'tournament.active.waitingNeighbours': 'Venter på nabogrupper',
  'tournament.active.and': ' og ',
  'tournament.active.waitingNeighbourTitle':
    'Gruppe {group} er ferdig, men venter på at gruppe {missing} også skal bli ferdig',
  'tournament.active.waitingNeighbour': '{group} klar — venter på {missing}',
  'tournament.active.noGroupsReady':
    'Ingen grupper er klare for trekning enda — vent til nabogruppene også er ferdig.',
  'tournament.active.earlyFinalsTitle': 'Trekk finaler tidlig — én gruppe om gangen',
  'tournament.active.earlyFinalsLead':
    'Finalene er innen-gruppe (Finale 1 = topp i A, Finale 2 = neste i A osv.) og avhenger kun av gruppens egen sluttrangering. Så snart gruppen er ferdig med Fase {phase}, kan finalene trekkes — du trenger ikke vente på andre grupper.',
  'tournament.active.drawAllFinalsTitle':
    'Trekk finaler for alle {groups} klare grupper på en gang',
  'tournament.active.drawAllFinals': 'Trekk alle finaler ({count})',
  'tournament.active.drawFinalsTitle': 'Trekk finaler for gruppe {group}',
  'tournament.active.drawFinals': 'Trekk finaler for {group}',
  'tournament.active.waitingLastPhase': 'Venter på siste fase',
  'tournament.active.waitingGroupMatches':
    'Gruppe {group} — venter på at gruppens egne kamper er ferdige',
  'tournament.active.ruleMirror': 'Speilrotasjon i R2',
  'tournament.active.ruleNoSamePartner': 'Unngå samme makker',
  'tournament.active.ruleNoSameOpponent': 'Unngå samme motstander',
  'tournament.active.ruleBalanceHomeAway': 'Balanser hjemme/borte',
  'tournament.active.ruleBalanceElo': 'Unngå skjeve ELO-kamper',
  'tournament.active.readyForFinals': 'Klar for finaler',
  'tournament.active.phaseAwaitingDraw': 'Fase {phase} venter på trekning',
  'tournament.active.readyForFinalsLead':
    'Alle gruppespill er ferdig. Finalene plasseres ut basert på siste fases stilling.',
  'tournament.active.phaseAwaitingDrawLead':
    'Nye grupper og kamper trekkes basert på aktive regler.',
  'tournament.active.activeRules': 'Aktive regler:',
  'tournament.active.noRules': 'Ingen regler aktive — fullt random',
  'tournament.active.closeRules': 'Lukk',
  'tournament.active.editRules': 'Rediger regler',
  'tournament.active.toFinals': 'Til finaler',
  'tournament.active.generateNextPhase': 'Generer neste fase',
  'tournament.active.modeVaried': 'Variert',
  'tournament.active.modeVariedDesc': 'Spre makkere og motstandere bredt',
  'tournament.active.modeMirror': 'Speilrotasjon',
  'tournament.active.modeMirrorDesc': 'Gamle makkere blir motstandere',
  'tournament.active.tabPlan': 'Kampplan',
  'tournament.active.tabPlanDesc': 'Hele turneringen',
  'tournament.active.tabGroups': 'Grupper',
  'tournament.active.tabStandings': 'Turneringsprestasjon',
  'tournament.active.tabRanking': 'Turneringsresultatet',
  'tournament.active.tabBanepad': 'Banepad',
  'tournament.active.tabLog': 'Logg',
  'tournament.active.finishLead': 'Hva vil du gjøre med {name}?',
  'tournament.active.thisTournament': 'denne turneringen',
  'tournament.active.cancel': 'Avbryt',
  'tournament.active.deleteTitle': 'Slette permanent?',
  'tournament.active.deleteBefore': 'Alle kamper, resultater og statistikk for',
  'tournament.active.theTournament': 'turneringen',
  'tournament.active.deleteAfter': 'vil bli slettet permanent. Dette kan ikke angres.',
  'tournament.active.back': 'Tilbake',
  'tournament.active.deletePermanently': 'Slett permanent',
  'tournament.active.startConfirmTitle': 'Start turneringen?',
  'tournament.active.startActivity1': 'Dette',
  'tournament.active.startActivityStrong': 'stenger påmeldingen',
  'tournament.active.startActivity2': 'og fjerner',
  'tournament.active.startActivity3':
    'fra aktiviteter. Oppsettet låses og banepaden åpnes for resultater. Du kan sette den tilbake til «Klar til start» igjen så lenge ingen resultater er registrert.',
  'tournament.active.startPlain1': 'Oppsettet',
  'tournament.active.startPlainStrong': 'låses',
  'tournament.active.startPlain2':
    'og banepaden åpnes for resultater. Du kan sette turneringen tilbake til «Klar til start» igjen så lenge ingen resultater er registrert.',
  'tournament.active.revertConfirmTitle': 'Tilbake til klar til start?',
  'tournament.active.readyToStart': '«Klar til start»',
  'tournament.active.revertActivity1': 'Turneringen settes tilbake til',
  'tournament.active.revertActivity2': ', og',
  'tournament.active.theActivity': 'aktiviteten',
  'tournament.active.revertActivity3': 'legges tilbake i aktiviteter med påmelding åpen igjen.',
  'tournament.active.revertPlain1': 'Turneringen settes tilbake til',
  'tournament.active.revertPlain2':
    '(forhåndsvisning). Du kan justere grupper, trekning og oppsett på nytt.',
  'tournament.active.revertConfirm': 'Sett tilbake',
  'tournament.active.playerProfile': 'Spillerprofil',
  'tournament.active.close': 'Lukk',
  'tournament.active.weekday.sun': 'søn',
  'tournament.active.weekday.mon': 'man',
  'tournament.active.weekday.tue': 'tir',
  'tournament.active.weekday.wed': 'ons',
  'tournament.active.weekday.thu': 'tor',
  'tournament.active.weekday.fri': 'fre',
  'tournament.active.weekday.sat': 'lør',
  'tournament.active.day': 'Dag',
  'tournament.active.queueNumber': 'nr. {number}',
  'tournament.active.standbyOnly': '· kun beredskap',
  'tournament.active.levelPrefix': 'Nivå {level} · ',
  'tournament.active.eloValue': '{elo} ELO',
  'tournament.active.standbyDays': ' · {days}',
  'tournament.active.used': 'brukt',
  'tournament.active.availableToday': 'kan i dag',
  'tournament.active.anotherDay': 'annen dag',
  'tournament.active.standbyHeading': 'Beredskapsreserver',
  'tournament.active.standbySubheading': 'Kan steppe inn ved skade',
  'tournament.active.standbyExplainer':
    'Ved skade foreslår systemet den reserven som er nærmest i ELO til den som må erstattes — først innenfor ±100 (eldste påmelding først hvis flere), så ±200, ±300 osv. Derfor kan også en reserve langt nede på lista bli kalt inn, hvis hen er nærmest i nivå. Tallene viser køplass (nr.), nivå/ELO og telefon for rask kontakt.',
  'tournament.active.noStandby': 'Ingen beredskapsreserver.',
  'tournament.active.alreadyUsed': 'Allerede brukt ({count})',
  'tournament.active.standbyFooter':
    'Foreslås først ved spillerbytte (nivå-match + rekkefølge). «Kan i dag» betyr at reserven har sagt seg tilgjengelig for dagens dato.',
  // ── /diagnostics/passkeys — the WebAuthn probe (#676) ─────────────
  //
  // A MEASUREMENT page for the owner, not a member surface: it reports what
  // the native app's WebView can do with passkeys. Technical names
  // (PublicKeyCredential, get/create) are kept as they are in both languages,
  // because they are what the reader looks up.
  'probe.title': 'Passkey-diagnostikk',
  'probe.lead':
    'Viser hva denne nettleseren eller appen støtter av passkeys. Ingenting sendes til serveren, og ingen konto brukes. Kopier rapporten nederst inn i saken.',
  'probe.reading': 'Leser …',
  'probe.absent': 'finnes ikke',
  'probe.threw': 'feilet: {name}: {message}',
  'probe.unknown': 'ukjent',
  'probe.yes': 'ja',
  'probe.no': 'nei',
  'probe.env.title': 'Miljø',
  'probe.env.origin': 'Opprinnelse (origin)',
  'probe.env.shell': 'Starpoint-appen',
  'probe.env.noShell': 'ikke i appen (vanlig nettleser)',
  'probe.env.webView': 'Android WebView',
  'probe.env.chromium': 'WebView-/Chromium-versjon',
  'probe.env.android': 'Android-versjon',
  'probe.env.highEntropy': 'Detaljer fra nettleseren',
  'probe.env.userAgent': 'User agent',
  'probe.caps.title': 'Støtte',
  'probe.caps.publicKeyCredential': 'PublicKeyCredential',
  'probe.caps.credentials': 'navigator.credentials',
  'probe.caps.platformAuthenticator':
    'Innebygd autentisering (isUserVerifyingPlatformAuthenticatorAvailable)',
  'probe.caps.conditionalMediation': 'Autofyll (isConditionalMediationAvailable)',
  'probe.caps.clientCapabilities': 'getClientCapabilities',
  'probe.ceremony.title': 'Prøv en seremoni',
  'probe.ceremony.lead':
    'Trykk én om gangen og noter om telefonen viser et passkey-vindu. Feilnavnet og tiden under er selve målingen.',
  'probe.ceremony.get': 'Logg inn (get) for {rpId}',
  'probe.ceremony.create': 'Lag test-passkey (create) for {rpId}',
  'probe.ceremony.createWarning':
    '«Lag test-passkey» lagrer en ekte passkey på enheten, merket «Starpoint WebView probe». Slett den i passordbehandleren etterpå.',
  'probe.ceremony.running': 'Venter på enheten …',
  'probe.ceremony.completed': 'Fullført.',
  'probe.ceremony.unavailable': 'WebAuthn finnes ikke her.',
  'probe.hint.completed':
    'Seremonien kjørte helt gjennom. Opprinnelsen over er den plattformen signerte — den avgjør hva serveren må godta.',
  'probe.hint.notAllowed':
    'Avvist eller avbrutt. Kom det et passkey-vindu først, virker WebAuthn her. Kom det på et øyeblikk uten vindu, ble forespørselen avvist.',
  'probe.hint.notSupported': 'Denne konteksten kan ikke kjøre seremonien.',
  'probe.hint.security':
    'Avvist av sikkerhetsregler: RP-ID-en passer ikke med opprinnelsen, eller domenet går ikke god for appen (assetlinks.json).',
  'probe.hint.aborted': 'Avbrutt før den var ferdig.',
  'probe.hint.invalidState': 'Enheten har allerede en passkey som ble utelukket.',
  'probe.hint.unavailable': 'API-et finnes ikke i denne konteksten.',
  'probe.hint.other': 'Uventet feil — ta med navnet og meldingen i rapporten.',
  'probe.report.title': 'Rapport',
  'probe.report.lead': 'Alt over, som JSON. Kopier hele og lim inn i saken.',
  'probe.report.copy': 'Kopier rapporten',
  'probe.report.copied': 'Kopiert.',
  'probe.report.copyFailed': 'Fikk ikke kopiert. Trykk i feltet, merk alt og kopier selv.',
  'probe.reload': 'Last siden på nytt',
  'probe.back': 'Til medlemsappen',
} as const;

/** Every key the app may ask for. Defined by the SOURCE catalog. */
export type MessageKey = keyof typeof nb;

/**
 * English.
 *
 * Typed as a complete record of `MessageKey`, so a missing key is a COMPILE
 * error rather than a string that silently falls back in front of a user. The
 * runtime fallback still exists for catalogs loaded later or edited by hand —
 * belt and braces, because only one of the two survives a refactor.
 */
export const en: Record<MessageKey, string> = {
  // ── A banepad changed the tournament (#556) ───────────────────────
  'padRefresh.waiting': 'A result has been recorded on the court pad.',
  'padRefresh.holding': 'You have a score that has not been sent, so nothing here has changed yet.',
  'padRefresh.accept': 'Bring in the result',
  // ── /sa → Permissions: who a custom role is offered to (#535) ─────
  'saScope.title': 'Available to',
  'saScope.platform': 'The whole platform',
  'saScope.selected': 'Selected organizations',
  'saScope.search': 'Search organizations',
  'saScope.add': 'Add',
  'saScope.addCount': 'Add {count}',
  'saScope.staged': '{count} selected, not added yet',
  'saScope.allAdded': 'Every eligible organization has been added.',
  'saScope.noMatch': 'No matches.',
  'saScope.centre': 'centre',
  'saScope.club': 'club',
  'saScope.none': 'None selected — the role cannot be granted anywhere.',
  'saScope.remove': 'Remove {name}',
  'saScope.save': 'Save availability',
  'saScope.pendingPlatform': 'Will be available to every organization.',
  'saScope.pendingSelected': '{count} organization(s) after saving.',
  // ── /sa → Platform users: the Entra-first step guide, and who holds which role (#975) ──
  'saUsers.title': 'Platform users',
  'saUsers.lead':
    'Microsoft Entra ID decides who can sign in as StarPoint staff. Here you decide what they may do: a platform role is given to an account that has already signed in. The roles give access to this panel — not to the club portal. Customer accounts are under their club in “Organizations”; search here to find one.',
  'saUsers.guide.title': 'Give access to /sa',
  'saUsers.guide.lead':
    'Three steps, in this order. The first is done in Microsoft Entra, the last here.',
  'saUsers.guide.step1.title': 'Give the person access in Microsoft Entra ID',
  'saUsers.guide.step1.body':
    "Assign the person to StarPoint's staff app in Microsoft Entra: Enterprise applications → the app → Users and groups → Add. Entra verifies who the person is, with Microsoft sign-in and MFA. This step cannot be done from StarPoint.",
  'saUsers.guide.step1.link': 'Open the Microsoft Entra admin center ↗',
  'saUsers.guide.step2.title': 'The person signs in to /sa with Microsoft once',
  'saUsers.guide.step2.body':
    'The first sign-in creates the account here. These people have signed in with Microsoft but hold no platform role yet:',
  'saUsers.guide.step2.empty':
    'Nobody is waiting for a role. Once the person has signed in, they appear here.',
  'saUsers.guide.step2.refresh': 'Refresh',
  'saUsers.guide.step2.signedIn': 'signed in {date}',
  'saUsers.guide.step3.title': 'Give a platform role',
  'saUsers.guide.step3.body':
    'Choose the person and the role. The role can be changed or removed in the list below.',
  'saUsers.guide.step3.person': 'Person',
  'saUsers.guide.step3.choosePerson': 'Choose a person …',
  'saUsers.guide.step3.role': 'Role',
  'saUsers.guide.step3.chooseRole': 'Choose a role …',
  'saUsers.guide.step3.submit': 'Give the role',
  'saUsers.guide.step3.nobody': 'Nobody to choose yet — see step 2.',
  'saUsers.guide.step3.readOnly': 'You can see who is waiting, but not give roles.',
  'saUsers.directory.title': 'Who holds which platform role',
  'saUsers.staff.title': 'Signs in with Microsoft',
  'saUsers.staff.lead': 'StarPoint staff with a platform role. Press a role to give or remove it.',
  'saUsers.staff.empty': 'Nobody who signs in with Microsoft holds a platform role yet.',
  'saUsers.local.title': 'Accounts that do not sign in with Microsoft',
  'saUsers.local.lead':
    'These hold a platform role but have no Microsoft sign-in — for example old demo accounts and the developer door on dev. New staff are not added here. Remove the roles that should no longer be there.',
  'saUsers.local.empty': 'None — everybody with a platform role signs in with Microsoft.',
  'saUsers.local.noRole': 'No platform role.',
  'saUsers.local.revoke': 'Remove',
  'saUsers.local.revokeLabel': 'Remove {role} from {name}',
  'saUsers.granted': '{name} was given the role {role}.',
  'saUsers.noEntraIdentity':
    '{name} has no Microsoft account in Starpoint, so a platform role would never work. The person must sign in to /sa with Microsoft first.',
  'saUsers.revokedLast': '{name} no longer holds any platform role, and is now under step 2.',
  'saUsers.refused': 'The server refused the change.',
  'saUsers.roleFailed': 'Could not change the role.',
  'saUsers.readOnly':
    'You have read access. Giving and removing platform roles requires the right to manage roles.',
  'saUsers.status.disabled': 'The account is disabled. {count} sign-in(s) were ended.',
  'saUsers.status.enabled': 'The account is enabled again.',
  'saUsers.status.failed': "Could not change the account's status.",
  'saUsers.search.placeholder': 'Search by name or e-mail',
  'saUsers.search.busy': 'Searching…',
  'saUsers.search.submit': 'Search',
  'saUsers.search.clear': 'Back to the overview',
  'saUsers.search.scope':
    "The search also reaches customer accounts. “Back to the overview” shows StarPoint's own people only.",
  'saUsers.truncated': 'Showing the first matches — search to narrow it down.',
  'saUsers.state.noApi': 'The API is not connected in this version yet.',
  'saUsers.state.forbidden': 'You do not have access to platform users.',
  'saUsers.state.error': 'Could not load users.',
  'saUsers.state.errorStatus': 'Could not load users (HTTP {status}).',
  'saUsers.state.empty': 'No accounts matched.',
  'saUsers.customer': 'Customer account',
  'saUsers.noName': '(no name)',
  'saUsers.noSignIn': 'no sign-in',
  'saUsers.clubs': '{count} club(s)',
  'saUsers.provider.ENTRA': 'Microsoft',
  'saUsers.provider.LOCAL': 'E-mail',
  'saUsers.account.enable': 'Enable the account',
  'saUsers.account.disable': 'Disable the account',
  'saUsers.account.disableConfirm': 'Confirm: disable',
  'saUsers.account.cancel': 'Cancel',
  'saUsers.account.disableHint': 'Ends every sign-in of the account at once.',
  'saUsers.account.closed': 'The account is closed',
  'saUsers.account.close': 'Close the account …',
  'saUsers.account.closeReason': 'Reason',
  'saUsers.account.closeReason.owner_request': 'The person asked for it',
  'saUsers.account.closeReason.compromised': 'The account is compromised',
  'saUsers.account.closeReason.deceased': 'The person has died',
  'saUsers.account.closeReference': 'Ticket reference',
  'saUsers.account.closeReferenceHint':
    'Letters, digits and ._:#/- only — not a name or an e-mail address.',
  'saUsers.account.closeConfirm': 'Confirm: close the account',
  'saUsers.account.closeHint':
    'The account can no longer sign in. Everything else is kept. It cannot be undone here.',
  'saUsers.status.closedNow': 'The account is closed. {count} sign-in(s) were ended.',
  'saUsers.status.closeInvalid': 'Choose a reason and enter a valid ticket reference.',
  // ── /sa for a signed-out visitor with no dev door (#789) ──────────
  'saEntry.title': 'Sign in',
  'saEntry.checking': 'Checking sign-in …',
  'saEntry.redirecting': 'Taking you to Microsoft sign-in …',
  'saEntry.notCompleted': 'Sign-in was not completed.',
  'saEntry.signIn': 'Sign in with Microsoft',
  'saEntry.unavailable': 'Sign-in is not available here.',
  // ── /sa on a session the platform does not honour (#1030) ─────────
  'saEntraRequired.title': 'Sign in with Microsoft',
  'saEntraRequired.body':
    'You are signed in with e-mail or a passkey. Platform access in Starpoint requires signing in with your Microsoft account.',
  'saEntraRequired.signIn': 'Sign in with Microsoft',
  'saEntraRequired.unavailable': 'Microsoft sign-in is not available in this environment.',
  // ── /sa → Organizations → Invite a new user (#1009) ───────────────
  'saOrgInvite.lead':
    'The person gets an e-mail and signs in with their address and a one-time code.',
  'saOrgInvite.createdMailed':
    'Account created. The invitation has been sent — the person signs in with their address and a one-time code.',
  'saOrgInvite.createdNotMailed':
    'Account created. No e-mail is sent from this environment — let the person know yourself. They sign in with their address and a one-time code.',
  // ── /sa → Players → Rating correction (#793) ──────────────────────
  'saAreas.role': 'Role',
  'saAreas.none': 'None',
  'saAreas.custom': 'Custom ({held} of {total})',
  'saAreas.unsaved': 'Not saved',
  'saAreas.cellTitle': '{role}: {area}',
  'saAreas.area.organization': 'The organization',
  'saAreas.area.court': 'Courts and venue',
  'saAreas.area.booking': 'Booking',
  'saAreas.area.activity': 'Activities',
  'saAreas.area.tournament': 'Tournaments',
  'saAreas.area.member': 'Members',
  'saAreas.area.messaging': 'Communication',
  'saAreas.area.finance': 'Finance',
  'saAreas.area.other': 'Other',
  'saAreas.level.read': 'Read only',
  'saAreas.level.user': 'User',
  'saAreas.level.daily': 'Day-to-day',
  'saAreas.level.operate': 'Operations',
  'saAreas.level.rules': 'Rules and prices',
  'saAreas.level.results': 'Results',
  'saAreas.level.lead': 'Tournament director',
  'saAreas.level.responsible': 'Responsible',
  'saAreas.level.admin': 'Administrator',
  'saAreas.level.owner': 'Owner',
  'saAreas.panel.title': '{role} — {area}',
  'saAreas.panel.close': 'Close',
  'saAreas.panel.level': 'Level',
  'saAreas.panel.rights': 'Exactly what it grants',
  'saAreas.panel.save': 'Save',
  'saAreas.panel.saving': 'Saving …',
  'saAreas.panel.pending': '{added} added, {removed} removed — not saved yet',
  'saAreas.panel.readonly': 'You have read access. Only a global administrator can change roles.',
  'saAreas.view.toDetail': 'Show the old table with tabs',
  'saAreas.view.toOverview': '← Back to the overview',
  'saAreas.search': 'Search roles',
  'saAreas.search.none': 'No role matches the search.',
  'saAreas.legend': 'Level: {one} lowest · {two} middle · {three} highest · – none',
  'saAreas.group.both': 'For both centres and clubs',
  'saAreas.group.both.lead': 'Granted in either kind of organization.',
  'saAreas.group.centre': 'Centres only',
  'saAreas.group.centre.lead': 'Can only be granted at a centre.',
  'saAreas.group.club': 'Clubs only',
  'saAreas.group.club.lead': 'Can only be granted in a club.',
  'saAreas.group.empty': 'No roles here yet.',
  'saAreas.role.open': 'Show all of {role}’s rights',
  'saAreas.role.allRights': 'All of {role}’s rights',
  'saAreas.role.custom': 'Custom role',
  'saAreas.section.organizations': 'Roles in centres and clubs',
  'saAreas.section.organizations.lead':
    'Each role per area. Click a cell to change one area, or the role’s name for everything.',
  'saAreas.section.platform': 'Starpoint roles',
  'saAreas.section.platform.lead': 'Roles for Starpoint’s own people in this panel.',
  'saAreas.section.link': 'Co-operation',
  'saAreas.section.link.lead': 'What a link lets one organization do at the other.',
  'saAreas.section.empty': 'No roles here yet. Create one with «Ny rolle».',
  // ── /sa → Audit (#1041) ──
  'saAudit.menuGroup': 'Audit',
  'saAudit.menu': 'Audit trail',
  'saAudit.title': 'Audit trail',
  'saAudit.lead':
    'Who did what, when, and for which organization — across the whole platform. Read-only: nothing here can be changed.',
  'saAudit.logged': 'Every search is itself logged, with the filters you used.',
  'saAudit.filter.organization': 'Organization',
  'saAudit.filter.actor': 'Actor',
  'saAudit.filter.action': 'Action',
  'saAudit.filter.from': 'From',
  'saAudit.filter.to': 'To',
  'saAudit.filter.impersonation': 'Impersonation session',
  'saAudit.filter.subject': 'Subject (ID)',
  'saAudit.filter.subjectType': 'Subject (type)',
  'saAudit.hint.id': 'An ID, for example from a row below.',
  'saAudit.hint.actor': 'Account ID — or a player or device ID.',
  'saAudit.hint.action':
    'Exact, or everything starting with something: end with a dot or a star, like organization_role.*',
  'saAudit.hint.to': 'Up to, not including.',
  'saAudit.hint.subjectType': 'For example organization or user.',
  'saAudit.search': 'Search',
  'saAudit.searching': 'Searching …',
  'saAudit.clear': 'Clear the filters',
  'saAudit.idle': 'Choose filters and press Search. With no filters you get the newest events.',
  'saAudit.empty': 'No events match the filters.',
  'saAudit.count': '{count} events shown, newest first.',
  'saAudit.more': 'Show more',
  'saAudit.table.caption': 'Events in the audit trail, newest first',
  'saAudit.col.when': 'When',
  'saAudit.col.who': 'Who',
  'saAudit.col.what': 'What',
  'saAudit.col.organization': 'Organization',
  'saAudit.col.assurance': 'Assurance',
  'saAudit.col.impersonation': 'Impersonation',
  'saAudit.col.detail': 'Detail, as stored',
  'saAudit.subject': 'Subject {type}: {id}',
  'saAudit.platformWide': 'The platform',
  'saAudit.none': '—',
  'saAudit.showContext': 'Show the whole session',
  'saAudit.showContext.label': 'Show everything done in impersonation session {id}',
  'saAudit.actorType.MEMBER': 'Member',
  'saAudit.actorType.CLUB_USER': 'Club user',
  'saAudit.actorType.DEVICE': 'Court screen',
  'saAudit.actorType.PLATFORM': 'Starpoint',
  'saAudit.actorType.OPERATOR': 'Operator (script)',
  'saAudit.actorType.PROVIDER': 'Payment provider',
  'saAudit.actorType.SYSTEM': 'The system',
  'saAudit.error.invalidFilter': 'The filter is not valid. Correct it and search again.',
  'saAudit.error.forbidden': 'You do not have access to the audit trail.',
  'saAudit.error.entraRequired':
    'The audit trail requires signing in with Microsoft (Entra). Sign in again.',
  'saAudit.error.failed': 'Could not load the audit trail. Please try again.',
  'saAudit.error.noApi': 'The API is not connected in this version.',
  // ── /sa «Payment exceptions» (#1131) ──────────────────────────────
  'saPayEx.menuGroup': 'Payments',
  'saPayEx.menu': 'Payment exceptions',
  'saPayEx.title': 'Payment exceptions',
  'saPayEx.lead':
    'Payments the system could not settle on its own, and payment-provider events that matched no payment. Read-only: what to do with each row is in the payment exceptions runbook. Every view is logged.',
  'saPayEx.read': 'Load the list',
  'saPayEx.refresh': 'Refresh',
  'saPayEx.loading': 'Loading …',
  'saPayEx.payments.heading': 'Payments with an exception code ({count})',
  'saPayEx.payments.caption': 'Payments with an exception code, most urgent first',
  'saPayEx.payments.empty': 'No payment has an exception code.',
  'saPayEx.unmatched.heading': 'Events without a payment ({count})',
  'saPayEx.unmatched.caption': 'Payment-provider events that matched no payment',
  'saPayEx.unmatched.empty': 'No event is without a payment.',
  'saPayEx.truncated':
    'Showing the first {count}. There are more — the full list is in the runbook’s SQL.',
  'saPayEx.level.0': 'Resolved / info',
  'saPayEx.level.1': 'Waiting for the provider',
  'saPayEx.level.2': 'Waiting for a decision',
  'saPayEx.level.3': 'Needs action',
  'saPayEx.level.unknown': 'Unknown — needs action',
  'saPayEx.col.level': 'Triage',
  'saPayEx.col.organization': 'Organization',
  'saPayEx.col.reason': 'Exception code',
  'saPayEx.col.state': 'State',
  'saPayEx.col.amount': 'Amount',
  'saPayEx.col.booking': 'Booking',
  'saPayEx.col.updated': 'Last changed',
  'saPayEx.col.event': 'Event',
  'saPayEx.col.provider': 'Provider',
  'saPayEx.col.process': 'Process',
  'saPayEx.col.deliveries': 'Deliveries',
  'saPayEx.col.firstSeen': 'First seen',
  'saPayEx.col.lastSeen': 'Last seen',
  'saPayEx.booking.gone': 'The booking was deleted',
  'saPayEx.error.forbidden': 'You do not have access to the payment exceptions.',
  'saPayEx.error.entraRequired':
    'The payment exceptions require signing in with Microsoft (Entra). Sign in again.',
  'saPayEx.error.failed': 'Could not load the payment exceptions. Please try again.',
  'saPayEx.error.noApi': 'The API is not connected in this version.',
  'saReports.menuGroup': 'Reports',
  'saReports.menu': 'Platform figures',
  'saReports.title': 'Platform figures',
  'saReports.lead':
    'Totals for the whole platform per month or per organization: organizations, bookings, activities, sign-ins and card payments. Figures only, never people.',
  'saReports.logged': 'Every view is logged, with the period you chose.',
  'saReports.idle': 'Choose a period and press «Show report».',
  'saReports.period': 'Period',
  'saReports.mode.month': 'Month',
  'saReports.mode.quarter': 'Quarter',
  'saReports.mode.custom': 'Custom',
  'saReports.month': 'Month',
  'saReports.year': 'Year',
  'saReports.quarter': 'Quarter',
  'saReports.quarterN': 'Q{n}',
  'saReports.from': 'From',
  'saReports.to': 'To (inclusive)',
  'saReports.hint.to': 'At most 24 months.',
  'saReports.groupBy': 'Show per',
  'saReports.groupBy.month': 'Month',
  'saReports.groupBy.organization': 'Organization',
  'saReports.show': 'Show report',
  'saReports.loading': 'Loading …',
  'saReports.showing': 'Period: {from} – {to} (Norwegian time).',
  'saReports.csv': 'Download CSV',
  'saReports.caption.month': 'Platform figures per month, with the total for the period',
  'saReports.caption.organization':
    'Platform figures per organization, with the total for the period',
  'saReports.col.month': 'Month',
  'saReports.col.organization': 'Organization',
  'saReports.col.kind': 'Kind',
  'saReports.col.active': 'Active',
  'saReports.col.centres': 'Centres',
  'saReports.col.activeCentres': 'Active centres',
  'saReports.col.clubs': 'Clubs',
  'saReports.col.activeClubs': 'Active clubs',
  'saReports.col.bookings': 'Bookings',
  'saReports.col.courtHours': 'Court hours',
  'saReports.col.activities': 'Activities',
  'saReports.col.signups': 'Sign-ups',
  'saReports.col.signedIn': 'Accounts signed in',
  'saReports.col.newAccounts': 'New accounts',
  'saReports.col.gross': 'Card payments (NOK)',
  'saReports.col.refunded': 'Refunded (NOK)',
  'saReports.col.platformFees': 'Platform fees (NOK)',
  'saReports.kind.CENTRE': 'Centre',
  'saReports.kind.CLUB': 'Club',
  'saReports.yes': 'Yes',
  'saReports.no': 'No',
  'saReports.total': 'Total',
  'saReports.totalOrganizations': 'Total ({count} organizations)',
  'saReports.activeCount': '{count} active',
  'saReports.partialMonth': '{month} (days {from}–{to})',
  'saReports.note.accountsPlatformWide':
    'Accounts belong to no single organization, so they are shown for the whole platform: {signedIn} accounts signed in and {created} new accounts in the period.',
  'saReports.note.distinct':
    'Accounts signed in and active organizations are counted once per row, so the total for the period is not the sum of the months.',
  'saReports.note.money':
    'Card payments come from the payment ledger alone. The price and payment status on older bookings are not included.',
  'saReports.note.fees':
    'Platform fees appear once the payment settlement writes them. Until then the figure is 0.',
  'saReports.note.otherCurrency':
    '{count} payment entries in a currency other than NOK are not included in the amounts.',
  'saReports.error.invalidPeriod': 'The period is not valid. Choose a from date and a to date.',
  'saReports.error.tooLong': 'The period can be at most 24 months.',
  'saReports.error.forbidden': 'You do not have access to the platform figures.',
  'saReports.error.entraRequired':
    'The platform figures require signing in with Microsoft (Entra). Sign in again.',
  'saReports.error.failed': 'Could not load the report. Please try again.',
  'saReports.error.noApi': 'The API is not connected in this version.',
  'saRating.menuGroup': 'Players',
  'saPlayerProfile.menu': 'Player profile',
  'saPlayerProfile.title': "Correct a player's profile",
  'saPlayerProfile.intro':
    'A player with an account changes their own name, e-mail, phone and gender, and clubs cannot. Here support corrects it when the player cannot. The reason and which fields changed are logged.',
  'saPlayerProfile.loading': 'Loading players …',
  'saPlayerProfile.noApi': 'This page needs the API.',
  'saPlayerProfile.loadFailed': 'Could not load the players. Reload the page.',
  'saPlayerProfile.search': 'Search by name',
  'saPlayerProfile.name': 'Name',
  'saPlayerProfile.email': 'E-mail on the profile',
  'saPlayerProfile.phone': 'Phone',
  'saPlayerProfile.gender': 'Gender',
  'saPlayerProfile.genderUnset': 'Not given',
  'saPlayerProfile.genderMale': 'Man',
  'saPlayerProfile.genderFemale': 'Woman',
  'saPlayerProfile.reason': 'Reason (required)',
  'saPlayerProfile.save': 'Save the correction',
  'saPlayerProfile.saving': 'Saving …',
  'saPlayerProfile.saved': 'Saved. {count} field(s) changed.',
  'saPlayerProfile.noChange': 'Nothing to change: the profile already has these values.',
  'saPlayerProfile.uncertain':
    'No certain answer came back. Reload the list and check the profile before trying again.',
  'saRating.menu': 'Rating correction',
  'saRating.title': "Correct a player's rating",
  'saRating.lead':
    'The rating is shared across the whole platform. A correction applies in every club, and is logged with its reason and who made it.',
  'saRating.load.loading': 'Loading players and the correction log …',
  'saRating.load.noApi': 'The API is not connected in this build.',
  'saRating.load.refused': 'The server did not grant access to the player database.',
  'saRating.load.failed': 'Could not load the players. Please try again.',
  'saRating.load.retry': 'Try again',
  'saRating.load.reload': 'Reload the list',
  'saRating.search.label': 'Find player',
  'saRating.search.hint': 'Name or player ID, at least two characters.',
  'saRating.search.noMatch': 'No players match the search.',
  'saRating.search.results': 'Results',
  'saRating.search.resultMeta': 'Player ID {id} · rating {elo}',
  'saRating.search.more': '{count} more results. Type more to narrow it down.',
  'saRating.player.none': 'Choose a player to see their rating and correction log.',
  'saRating.player.id': 'Player ID {id}',
  'saRating.player.rating': 'Current rating',
  'saRating.player.level': 'Level',
  'saRating.player.matches': 'Matches played',
  'saRating.history.title': 'Earlier corrections',
  'saRating.history.empty': 'This rating has never been corrected by hand.',
  'saRating.history.when': 'When',
  'saRating.history.from': 'From',
  'saRating.history.to': 'To',
  'saRating.history.change': 'Change',
  'saRating.history.reason': 'Reason',
  'saRating.history.noReason': 'No reason given',
  'saRating.form.title': 'New rating',
  'saRating.form.lead':
    'Use this only to fix a mistake, such as a player registered with the wrong starting rating.',
  'saRating.form.newRating': 'New rating',
  'saRating.form.levelUnchanged': 'The level does not change.',
  'saRating.form.reason': 'Reason',
  'saRating.form.reasonHint':
    'Required, at least {minimum} characters. Kept in the correction log.',
  'saRating.form.submit': 'Correct rating',
  'saRating.form.saving': 'Saving …',
  'saRating.form.confirm': "Set {name}'s rating from {from} to {to}? This applies in every club.",
  'saRating.form.confirmYes': 'Yes, correct it',
  'saRating.form.readOnly':
    'You can see the rating and the log. Correcting it requires the right to set ratings by hand.',
  'saRating.outcome.saved': "{name}'s rating has been changed from {from} to {to}.",
  'saRating.outcome.noChange':
    'The rating is already {elo}. Nothing was changed, and nothing was logged.',
  'saRating.error.reasonRequired': 'Write a reason of at least {minimum} characters.',
  'saRating.error.invalidRating': 'Enter the rating as a number.',
  'saRating.error.notFound': 'The player no longer exists. Reload the list.',
  'saRating.error.forbidden': 'Your account does not have the right to correct ratings.',
  'saRating.error.reauthenticate':
    'Your sign-in needs to be confirmed again. Sign in again and retry.',
  'saRating.error.server':
    'The server answered with an error. Reload the list to see whether the correction was saved before trying again.',
  'saRating.error.noAnswer':
    'No answer from the server. Reload the list to see whether the correction was saved.',
  // ── «The server is not responding» (#537) ─────────────────────────
  'serverDown.title': 'The server is not responding right now.',
  'serverDown.body':
    'It is probably restarting after an update. Wait a moment — do not reload the page, or you will lose what you are working on.',
  'serverDown.doNotReload': 'do not reload the page',
  // ── «New version available» (#537) ────────────────────────────────
  'newBuild.available': 'A new version of the app has been deployed. Reload when it suits you.',
  'newBuild.reload': 'Reload',
  'newBuild.refusedRead':
    'This version of the app can no longer load the calendar and the lists. Reload to see them.',
  // ── «Could not be loaded» (#1247) ─────────────────────────────────
  'listLoad.title': "Some of the club's data could not be loaded.",
  'listLoad.body':
    'These may look empty without being so: {lists}. Reload the page once the server responds.',
  'listLoad.bookings': 'bookings',
  'listLoad.activities': 'activities',
  'listLoad.singleMatches': 'single matches',
  'listLoad.plannedMatches': 'planned matches',
  // ── /sa «My account» — the staff password form (#383 surface) ─────
  'app.tagline': 'Booking and tournament platform',
  'app.signOut': 'Sign out',
  'app.signedInAs': 'Signed in as {name}',
  'app.club': 'Club',
  'app.changeClub': 'Change club',
  // ── dev-only staff sign-in (/sa, #445) ────────────────────────────
  'devLogin.title': 'Developer sign-in',
  'devLogin.explainer':
    'Development environments only. What you may do is decided by Starpoint as usual — signing in only confirms who you are.',
  'devLogin.email': 'E-mail',
  'devLogin.sendCode': 'Send code',
  'devLogin.sending': 'Sending …',
  'devLogin.code': 'Code from e-mail',
  'devLogin.codeSent': 'If that account exists, we have sent a code to {email}.',
  'devLogin.again': 'Use another e-mail',
  'devLogin.submit': 'Sign in',
  'devLogin.submitting': 'Signing in…',
  'devLogin.refused': 'Sign-in refused.',
  'devLogin.unavailable': 'Sign-in is not available here.',
  'app.loading': 'Loading …',
  'app.viewLoadFailed': 'This page could not be loaded. Reload the page.',
  'app.reload': 'Reload',
  // «WE COULD NOT ASK» (#1145): a restore that could not reach the server.
  'app.offline.title': 'Cannot reach the server',
  'app.offline.body':
    'We could not check whether you are signed in. We will try again when the connection is back.',
  'app.offline.retry': 'Try again',

  'auth.login.title': 'Sign in',
  'auth.login.lead': 'Welcome back to the club portal.',
  'auth.login.email': 'Email',
  'auth.login.otherWays': 'Other ways to sign in',
  'auth.login.usePassword': 'Sign in with a password',
  'auth.login.staff': 'Staff sign-in',
  // See the Norwegian catalog for why none of this copy may be warmer than it is.
  // «Passkey» is deliberately untranslated in both catalogs — see the Norwegian.
  'account.passkeys': 'Passkeys',
  'account.passkeys.lead':
    'Sign in without a password, using your fingerprint, face or your device PIN.',
  'account.passkeys.add': 'Add a passkey',
  'account.passkeys.adding': 'Follow the prompt on your device …',
  'account.passkeys.labelLabel': 'Device name (optional)',
  'account.passkeys.labelPlaceholder': 'Work laptop',
  'account.passkeys.added': 'Passkey added.',
  'account.passkeys.addedNotBackedUp':
    'Passkey added. It exists only on this device — if you lose the device, you lose the passkey.',
  'account.passkeys.cancelled': 'Cancelled. No passkey was added.',
  'account.passkeys.alreadyRegistered': 'This device already has a passkey for this account.',
  'account.passkeys.unsupported': 'This browser does not support passkeys.',
  'account.passkeys.failed': 'Could not add the passkey. Please try again.',
  'account.passkeys.stepUp':
    'Confirm it is you first. Sign in again before adding or removing a passkey.',
  // ── «INNLOGGINGER» (#1141) ─────────────────────────────────────────
  'account.sessions': 'Signed-in devices',
  'account.sessions.loading': 'Loading …',
  'account.sessions.listFailed': 'Could not load where you are signed in.',
  'account.sessions.onlyHere': 'You are only signed in here.',
  'account.sessions.elsewhere': 'You are signed in here and in {count} other places.',
  'account.sessions.signOutOthers': 'Sign out everywhere else',
  'account.sessions.confirm': 'Sign out everywhere except here?',
  'account.sessions.yes': 'Yes, sign out',
  'account.sessions.no': 'Cancel',
  'account.sessions.working': 'Signing out …',
  'account.sessions.done': 'Signed out of {count} other places.',
  'account.sessions.stepUp': 'Confirm it is you first. Sign in again before signing out elsewhere.',
  'account.sessions.failed': 'Could not sign out the others. Please try again.',
  'account.passkeys.network': 'Could not reach the server. Please try again.',
  'account.passkeys.listLoading': 'Loading …',
  'account.passkeys.listEmpty': 'You have no passkeys yet.',
  'account.passkeys.listFailed': 'Could not load your passkeys.',
  'account.passkeys.unnamed': 'Unnamed',
  'account.passkeys.addedOn': 'Added {date}',
  'account.passkeys.lastUsed': 'Last used {date}',
  'account.passkeys.neverUsed': 'Not used yet',
  'account.passkeys.remove': 'Remove',
  'account.passkeys.removeConfirm': 'Remove this one?',
  'account.passkeys.removeYes': 'Yes, remove',
  'account.passkeys.removeNo': 'Cancel',
  'account.passkeys.removing': 'Removing …',
  'account.passkeys.removed': 'Passkey removed.',
  'account.passkeys.removeGone': 'It was already gone. The list is up to date.',
  'account.passkeys.removeFailed': 'Could not remove the passkey. Please try again.',
  // See the Norwegian: `cancelled` covers two things the browser refuses to
  // distinguish, so the sentence claims neither.
  'auth.passkey.link': 'Sign in with a passkey',
  'auth.passkey.working': 'Follow the prompt on your device …',
  'auth.passkey.cancelled': 'Cancelled, or this device has no passkey for Starpoint.',
  'auth.passkey.refused': 'That passkey was not accepted. Try another way to sign in.',
  'auth.passkey.network': 'Could not reach the server. Please try again.',
  // ── Signing in to the player app (#710) ───────────────────────────
  'memberSignIn.tagline': 'Booking and tournaments for players',
  'memberSignIn.remember': 'Remember me on this device',
  'memberSignIn.rememberHint':
    'You stay signed in for 30 days. Leave it unticked on a computer other people use.',
  // ── SIGNING IN AS A PLAYER, WITHOUT A CLUB (#710) ──────────────────
  'playerSignIn.title': 'Sign in',
  'playerSignIn.lead': 'Enter your e-mail address and we will send a one-time code.',
  'playerSignIn.emailLabel': 'E-mail',
  'playerSignIn.send': 'Send one-time code',
  'playerSignIn.sending': 'Sending …',
  'playerSignIn.sentTitle': 'Check your e-mail',
  'playerSignIn.sentLead':
    'We have sent a one-time code to {email}. It is valid for {minutes} minutes.',
  'playerSignIn.codeLabel': 'One-time code',
  'playerSignIn.verify': 'Sign in',
  'playerSignIn.verifying': 'Signing in …',
  'playerSignIn.refused': 'Wrong or expired code.',
  'playerSignIn.network': 'Could not reach the server. Try again.',
  'playerSignIn.unavailable': 'Signing in is not available here.',
  'playerSignIn.resend': 'Send the code again',
  'playerSignIn.resent': 'We have sent a new code. The previous one no longer works.',
  'playerSignIn.wrongAddress': 'Wrong address? Start again',
  'playerSignIn.joinedTitle': 'You are now a member of {club}',
  'playerSignIn.joinedLead':
    'The club had invited this e-mail address, and your sign-in confirmed it.',
  'playerSignIn.joinedContinue': 'Continue',
  'memberInvitation.title': 'Invitation from {club}',
  'memberInvitation.titleGeneric': 'Invitation',
  'memberInvitation.opening': 'Opening the invitation …',
  'memberInvitation.leadMember':
    '{club} invites you as a member, as the player «{player}». We have sent a one-time code to the address the invitation went to. It is valid for {minutes} minutes.',
  'memberInvitation.leadLink':
    '{club} wants to link you to the player «{player}». We have sent a one-time code to the address the invitation went to. It is valid for {minutes} minutes.',
  'memberInvitation.codeLabel': 'One-time code',
  'memberInvitation.accept': 'Accept the invitation',
  'memberInvitation.accepting': 'Accepting …',
  'memberInvitation.resend': 'Send the code again',
  'memberInvitation.resent': 'We have sent a new code. The previous one no longer works.',
  'memberInvitation.notNow': 'Not now',
  'memberInvitation.toSignIn': 'Go to sign-in',
  'memberInvitation.notValid':
    'This invitation can no longer be used. Ask the club to send a new one.',
  'memberInvitation.wrongCode': 'Wrong or expired code.',
  'memberInvitation.identityAmbiguous':
    'The address belongs to an account that does not sign in by e-mail (for example with Microsoft). Ask the club for help.',
  'memberInvitation.mailFailed': 'We could not send the code. Try again shortly.',
  'memberInvitation.network': 'Could not reach the server. Try again.',
  'memberInvitation.unavailable': 'Invitations cannot be accepted here.',
  'memberInvitation.unknown': 'Something went wrong. Try again.',
  'memberInvitation.refused.title': 'Your address is confirmed',
  'memberInvitation.refused.playerClaimed':
    'But the invitation cannot be used: the player is already linked to another account.',
  'memberInvitation.refused.accountLinked':
    'But the invitation cannot be used: your account is already linked to another player.',
  'memberInvitation.refused.otherRoster':
    'But the invitation cannot be used: the player is now a member of another club.',
  'memberInvitation.refused.notValid':
    'But the invitation can no longer be used. Ask the club to send a new one.',
  // ── The actual wait on a throttled door (#778 Q7) — see the Norwegian ──
  'retryWait.unknown': 'Too many attempts. Try again shortly.',
  'retryWait.seconds.one': 'Too many attempts. Try again in {count} second.',
  'retryWait.seconds.other': 'Too many attempts. Try again in {count} seconds.',
  'retryWait.minutes.one': 'Too many attempts. Try again in {count} minute.',
  'retryWait.minutes.other': 'Too many attempts. Try again in {count} minutes.',
  'retryWait.aboutAnHour': 'Too many attempts. Try again in about an hour.',
  'retryWait.hours.other': 'Too many attempts. Try again in {count} hours.',

  // ── THE PLAYER'S HOME (#710) ──────────────────────────────────────
  // ── FINDING A CLUB (#710) ─────────────────────────────────────────
  'clubDiscovery.title': 'Find a club',
  'clubDiscovery.lead': 'Search for the club you want to play at.',
  'clubDiscovery.searchLabel': 'Club',
  'clubDiscovery.placeholder': 'Type the name of the club',
  'clubDiscovery.empty': 'No clubs matched that search.',
  'clubDiscovery.ask': 'Apply for membership',
  'clubDiscovery.asking': 'Sending …',
  'clubDiscovery.pending': 'Awaiting approval',
  'clubDiscovery.asked': 'The club has your application. You are in once they approve it.',
  'clubDiscovery.alreadyAsked': 'You already have an application with this club.',
  'clubDiscovery.alreadyMember': 'You are already a member here.',
  'clubDiscovery.noPlayer': 'We did not find a player profile on your account yet.',
  'clubDiscovery.tooMany': 'You have too many applications waiting for an answer.',
  'clubDiscovery.closed': 'This club is not taking applications right now.',
  'clubDiscovery.notYet': 'You can apply here again later.',
  'clubDiscovery.notYetFrom': 'You can apply again from {date}.',
  'clubDiscovery.blocked':
    'You cannot apply here again. Only an invitation from the club lets you in.',
  'clubDiscovery.withdraw': 'Withdraw the application',
  'clubDiscovery.withdrawing': 'Withdrawing …',
  'clubDiscovery.withdrawn':
    'Your application is withdrawn. You can apply again whenever you like.',
  'clubDiscovery.withdrawAnswered': 'The club has already answered your application.',
  'clubDiscovery.failed': 'That did not work. Try again.',
  // ── A PLAYER PROFILE, WHEN THE APPLICATION NEEDS ONE (#790) ───────
  'clubDiscovery.needsProfileTitle': 'What should you be called?',
  'clubDiscovery.needsProfileLead':
    'The club sees your name on player lists, bookings and results.',
  'clubDiscovery.nameLabel': 'Name',
  'clubDiscovery.createAndApply': 'Create profile and apply',
  'clubDiscovery.creating': 'Creating …',
  'clubDiscovery.nameRequired': 'Enter your name.',
  'clubDiscovery.cancel': 'Cancel',
  'clubDiscovery.back': 'Back',
  'clubDiscovery.unavailable': 'The search did not answer. Try again.',
  'clubDiscovery.select': 'Select',
  'clubDiscovery.open': 'Open {name}',
  'clubDiscovery.kindCentre': 'Centre',
  'clubDiscovery.kindClub': 'Club',
  'clubPicker.placeholder': 'Type the name of the club',
  'clubPicker.pickOne': 'Choose the club from the list.',
  // ── «Membership requests» (#710) ──────────────────────────────────
  'joinQueue.title': 'Membership requests ({count})',
  'joinQueue.lead': 'Players who have asked to join the club.',
  'joinQueue.asked': 'Asked {date}',
  'joinQueue.openProfile': 'Open {name}’s profile',
  'joinQueue.card.ratingLabel': 'Level and rating',
  'joinQueue.card.rating': 'Level {level} — {name} · ELO {elo}',
  'joinQueue.card.noRating': 'No rating yet',
  'joinQueue.card.contactLabel': 'Contact',
  'joinQueue.card.notInList':
    'The player is not in the club’s player list yet. The full profile with history appears once the application is approved.',
  'joinQueue.noContact': 'No contact details on file',
  'joinQueue.approve': 'Approve',
  'joinQueue.approving': 'Approving …',
  'joinQueue.reject': 'Decline',
  'joinQueue.rejecting': 'Declining …',
  'joinQueue.rejectTitle': 'Decline the request from {name}',
  'joinQueue.rejectLead':
    'Choose what declining means. Preselected: they can ask again in {days} days.',
  'joinQueue.policy.allowed': 'They can ask again at any time',
  'joinQueue.policy.after': 'They can ask again from a date',
  'joinQueue.policy.blocked':
    'They cannot ask again. Only an invitation from the club lets them in.',
  'joinQueue.fromDate': 'Can ask again from',
  'joinQueue.confirmReject': 'Decline',
  'joinQueue.cancel': 'Cancel',
  'joinQueue.alreadyDecided':
    'Somebody else at the club answered first. The list has been refreshed.',
  'joinQueue.gone': 'That request no longer exists. The list has been refreshed.',
  'joinQueue.askedBefore': 'Has asked before',
  'joinQueue.alreadyMember': '{name} is already a member. The list has been refreshed.',
  'joinQueue.rejected.show': 'Show declined requests ({count})',
  'joinQueue.rejected.hide': 'Hide declined requests',
  'joinQueue.rejected.on': 'Declined {date}',
  'joinQueue.rejected.policyAllowed': 'Can ask again at any time',
  'joinQueue.rejected.policyAfter': 'Can ask again from {date}',
  'joinQueue.rejected.policyBlocked': 'Cannot ask again',
  'joinQueue.rejected.reopen': 'Reopen',
  'joinQueue.rejected.reopening': 'Reopening …',
  'joinQueue.rejected.approve': 'Approve anyway',
  'joinQueue.notAllowed': 'You do not have access to answer requests in this club.',
  'joinQueue.datePast': 'Choose a date in the future — tomorrow at the earliest.',
  'joinQueue.policyRefused': 'The server did not accept that choice. Please choose again.',
  'joinQueue.protocol':
    'We did not understand the server’s answer, so this list may be incomplete. Please reload.',
  'joinQueue.unreachable': 'Could not reach the server. Please try again.',
  // ── «Members» — the club's roster, beside the join queue ──────────
  'clubMembers.title': 'Members ({count})',
  'clubMembers.lead': 'Players who are members of the club and can use it in the app.',
  'clubMembers.empty': 'Nobody is a member of the club yet.',
  'clubMembers.since': 'Member since {date}',
  'clubMembers.search': 'Search members',
  'clubMembers.noMatch': 'No members match the search.',
  'clubMembers.showAll': 'Show all ({count})',
  'clubMembers.showFewer': 'Show fewer',
  'memberImport.open': 'Upload member list',
  'memberImport.title': 'Upload member list',
  'memberImport.lead':
    'Paste names and e-mail addresses — one person per line — or choose a file (CSV). Everyone on the list becomes a member of the club and gets an e-mail with a link to start using Starpoint.',
  'memberImport.pasteLabel': 'Member list',
  'memberImport.pastePlaceholder': 'Kari Nordmann, kari@example.com\nOla Hansen; ola@example.com',
  'memberImport.file': 'Choose file …',
  'memberImport.fileRead': 'Read {count} lines from {name}.',
  'memberImport.rowsFound': '{count} people on the list',
  'memberImport.empty': 'Found no names and e-mail addresses in the text.',
  'memberImport.tooMany': 'The list can hold at most {max} people at a time. Split it up.',
  'memberImport.preview': 'Check the list',
  'memberImport.previewing': 'Checking the list …',
  'memberImport.previewLead':
    'This is what will happen to the list. Nothing has been sent yet — look it over and confirm.',
  'memberImport.colName': 'Name',
  'memberImport.colEmail': 'E-mail',
  'memberImport.colOutcome': 'What happens',
  'memberImport.plan.new': 'New — becomes a member and gets an invitation',
  'memberImport.plan.member_invited': 'Already a member — gets an invitation to claim the account',
  'memberImport.plan.invited': 'Known player — gets an invitation to become a member',
  'memberImport.plan.already_member': 'Already a member, with an account — nothing to do',
  'memberImport.plan.pending': 'Already has an invitation waiting',
  'memberImport.plan.ambiguous': 'Several players could be this person — skipped',
  'memberImport.plan.failed': 'Could not be added',
  'memberImport.plan.invalid': 'Invalid line',
  'memberImport.plan.invalid.name_required': 'Name missing',
  'memberImport.plan.invalid.email_invalid': 'Invalid e-mail address',
  'memberImport.plan.invalid.duplicate_in_upload':
    'The same e-mail appears more than once on the list',
  'memberImport.matchedByName': 'Linked to the player «{name}» (same name)',
  'memberImport.matchedByEmail': 'This is the player «{name}»',
  'memberImport.confirm': 'Add {count} and send invitations',
  'memberImport.confirmNothing': 'Nobody on the list needs to be added.',
  'memberImport.confirming': 'Adding and sending …',
  'memberImport.confirmingProgress': 'Adding and sending … {done} of {total}',
  'memberImport.stoppedPartway':
    'Stopped after {done} of {total}. What was done is kept — press the button again to continue. Nobody is invited twice.',
  'memberImport.back': 'Back',
  'memberImport.cancel': 'Cancel',
  'memberImport.close': 'Close',
  'memberImport.doneTitle': 'Done: {added} new members, {sent} invitations sent',
  'memberImport.doneHint': 'Those added are listed under «Members» and «All players».',
  'memberImport.done.sent': 'Invitation sent',
  'memberImport.done.notSent':
    'Added, but the e-mail could not be sent — send the invitation again from the player list',
  'memberImport.done.failed': 'Could not be added — try again',
  'memberImport.unreachable': 'Could not reach the server. Try again.',
  'memberImport.protocol':
    'We did not understand the server’s answer. Reload the page and try again.',
  // ── «Email — code — you're in» (#710) ─────────────────────────────
  'onboarding.tagline': 'Booking and tournaments for players',
  'onboarding.haveCode': 'I have a code from the club',
  'onboarding.network': 'Could not reach the server. Please try again.',
  'onboarding.protocol': 'We did not understand the server’s answer. Please try again.',
  'onboarding.grantExpired':
    'Too much time passed, or the page was reloaded. Start again — we will send you a new code.',
  'onboarding.register.title': 'Create an account',
  'onboarding.register.lead': 'We will email you a code. No password needed.',
  'onboarding.register.nameLabel': 'Name',
  'onboarding.register.emailLabel': 'Email',
  'onboarding.register.submit': 'Send code',
  'onboarding.register.sending': 'Sending …',
  'onboarding.register.invalidEmail': 'That does not look like an email address.',
  'onboarding.register.nameRequired': 'Please enter your name.',
  'onboarding.code.title': 'Check your email',
  'onboarding.code.lead':
    'We sent a code to {email}. It is valid for {minutes} minutes and can only be used once.',
  'onboarding.code.label': 'Code',
  'onboarding.code.submit': 'Confirm',
  'onboarding.code.verifying': 'Confirming …',
  'onboarding.code.refused': 'Wrong or expired code.',
  'onboarding.code.wrongAddress': 'Wrong email address? Start again',
  'onboarding.join.title': 'Join a club',
  'onboarding.join.lead':
    'Choose the club you play at. The club has to approve you before you can get in.',
  'onboarding.join.leadNamed':
    'Your account is ready, {name}. Choose the club you play at — the club has to approve you before you can get in.',
  'onboarding.join.clubLabel': 'Club',
  'onboarding.join.submit': 'Ask to join',
  'onboarding.join.asking': 'Sending …',
  'onboarding.join.unknownClub': 'No club found with that name.',
  'onboarding.join.alreadyMember': 'You are already a member of this club.',
  'onboarding.join.blocked': 'This club is not accepting your request. Please contact the club.',
  'onboarding.join.notYet': 'You cannot ask this club again yet. Please contact the club.',
  'onboarding.join.closed': 'Your request has already been answered. Please contact the club.',
  'onboarding.join.tooManyPending': 'You have too many requests waiting for an answer.',
  'onboarding.done.title': 'Request sent',
  'onboarding.done.lead': '{club} has your request.',
  'onboarding.done.next':
    'Somebody at the club has to approve it. Once they have, you get in by signing in with a one-time code sent to your email address.',
  'onboarding.done.back': 'Back',
  'auth.forgot.title': 'Forgot password',
  'auth.forgot.lead': 'Enter your email address and we will send you a link to choose a new one.',
  'auth.forgot.submit': 'Send link',
  'auth.forgot.working': 'Sending …',
  'auth.forgot.sent':
    'If that address is known to us, we have sent an email with a link to choose a new password.',
  'auth.forgot.back': 'Back to sign-in',
  'auth.scope.title': 'Choose a club',
  'auth.scope.lead': 'You have access to several clubs. Choose the one you want to work in.',
  'auth.noScope.title': 'No club access',
  'auth.noScope.lead': 'Your account is not linked to a club yet. Please contact your club.',

  'account.title': 'My account',
  'account.rights.title': 'Your access',
  'account.rights.lead':
    'The roles your account holds. What each role allows is decided by Starpoint and the organization.',
  'account.rights.platform': 'At Starpoint',
  'account.rights.organizations': 'In organizations',
  'account.rights.none': 'Your account has no role in any organization yet.',
  'account.rights.loading': 'Loading …',
  'account.rights.unknownOrg': 'Unknown organization',
  'roleSwitcher.label': 'Switch view',
  'roleSwitcher.player': 'Player',
  'roleSwitcher.place.player': 'The player app',
  'stepUp.title': 'Confirm it is you',
  'stepUp.lead':
    'You are still signed in. Before you can reach administrative work we need to confirm it is you,',
  'stepUp.lead.hours': 'and you signed in more than {hours} hours ago.',
  'stepUp.lead.minutes': 'and you signed in more than {minutes} minutes ago.',
  'stepUp.lead.noWindow': 'because it has been a while since you signed in.',
  'stepUp.lead.done': 'Confirmed. Carry on where you were — try the action again.',
  'stepUp.usePasskey': 'Use a passkey',
  'stepUp.sendCode': 'E-mail me a code',
  'stepUp.codeLabel': 'One-time code',
  'stepUp.verify': 'Confirm',
  'stepUp.resend': 'Send the code again',
  'stepUp.resent': 'We have sent a new code.',
  'stepUp.continue': 'Continue',
  'stepUp.working': 'One moment …',
  'stepUp.error.invalid_code': 'Wrong or expired code.',
  'stepUp.error.no_passkey': 'This account has no passkey. Use the e-mail code.',
  'stepUp.error.no_local_credential':
    'This account cannot be confirmed with a code. Contact Starpoint.',
  'stepUp.error.session_changed':
    'The sign-in changed while you were confirming. Please sign in again.',
  'stepUp.error.generic': 'That did not work. Please try again.',
  'account.rights.unavailable': 'Could not fetch your access right now.',
  'account.kind.CENTRE': 'Centre',
  'account.kind.CLUB': 'Club',
  'clubSettings.access.title': 'Access',
  'clubSettings.access.lead':
    'Grant and remove roles for the people in the organization — who is an administrator, a tournament manager, reception and so on.',
  'clubSettings.access.open': 'Manage access',
  'clubSettings.equipment.title': 'Equipment',
  'clubSettings.equipment.lead':
    'Ball machines and other equipment that can be booked beside the courts — with prices and rules.',
  'clubSettings.equipment.open': 'Manage equipment',
  'access.title': 'Access',
  'access.lead':
    'Who holds which roles in {organization}. You can only grant or remove roles whose rights you hold yourself.',
  'access.thisOrganization': 'this organization',
  'access.back': 'Back to settings',
  'access.loading': 'Loading access …',
  'access.forbidden': 'You do not manage access in this organization.',
  'access.unavailable': 'Could not fetch the access list right now.',
  'access.retry': 'Try again',
  'access.people.title': 'People',
  'access.people.lead':
    'Everyone who holds a role here, and everyone on the roster who has an account. New people are added by Starpoint.',
  'access.people.none': 'Nobody is linked to the organization yet.',
  'access.people.you': 'you',
  'access.people.disabled': 'Disabled account',
  'access.people.onRoster': 'On the roster',
  'access.people.noRoles': 'No role',
  'access.grant.title': 'Grant a role',
  'access.grant.person': 'Person',
  'access.grant.role': 'Role',
  'access.grant.choosePerson': 'Choose a person …',
  'access.grant.chooseRole': 'Choose a role …',
  'access.grant.submit': 'Grant the role',
  'access.grant.working': 'Saving …',
  'access.grant.done': '{name} now holds {role}.',
  'access.grant.alreadyHeld': '{name} already held {role}.',
  'access.revoke.question': 'Take {role} away from {name}?',
  'access.revoke.confirm': 'Remove the role',
  'access.people.unnamed': 'Account without a name ({id})',
  'access.revoke.done': '{name} no longer holds {role}.',
  'access.beyondYou': 'Beyond your authority',
  'access.beyondYou.help': 'This role carries rights you do not hold yourself: {capabilities}',
  'access.roles.title': 'Roles that can be granted here',
  'access.roles.lead':
    'What each role allows is decided by Starpoint. A role marked «beyond your authority» can only be granted by somebody who holds the rights it carries.',
  'access.roles.custom': 'Custom role',
  // ── EGNE ROLLER — the organization's own roles on «Tilganger» (2026-11-01) ──
  'access.ownRoles.title': 'Own roles',
  'access.ownRoles.lead':
    "Roles the organization defines itself. An own role can only give rights the organization has and that you hold yourself – never handing out roles, entering co-operations or the payment account; Starpoint's roles do that.",
  'access.ownRoles.none': 'The organization has no roles of its own yet.',
  'access.ownRoles.new': 'New role',
  'access.ownRoles.name': 'Name',
  'access.ownRoles.description': 'Description (optional)',
  'access.ownRoles.create': 'Create the role',
  'access.ownRoles.created': 'The role {role} has been created.',
  'access.ownRoles.editRights': 'Change rights',
  'access.ownRoles.rename': 'Rename',
  'access.ownRoles.saveName': 'Save the name',
  'access.ownRoles.renamed': 'The role is now called {role}.',
  'access.ownRoles.saveRights': 'Save the rights',
  'access.ownRoles.saved': 'The rights of {role} are saved.',
  'access.ownRoles.saving': 'Saving …',
  'access.ownRoles.cancel': 'Cancel',
  'access.ownRoles.delete': 'Delete the role',
  'access.ownRoles.deleteQuestion': 'Delete the role {role}?',
  'access.ownRoles.deleted': 'The role {role} has been deleted.',
  'access.ownRoles.holders': '{n} hold the role',
  'access.ownRoles.inUse':
    'Cannot be deleted while anybody holds it: take it away from them first.',
  'access.ownRoles.rights': '{n} rights',
  'access.ownRoles.noRights': 'No rights yet',
  'access.ownRoles.nothingOffered': 'There are no rights you can give an own role.',
  'access.ownRoles.notYours': 'You do not hold this yourself, so you cannot change it',
  'access.ownRoles.pending': '{added} added, {removed} removed – not saved yet',
  'access.ownRoles.error.role_in_use':
    'The role cannot be deleted while {n} hold it. Take it away from them first.',
  'access.ownRoles.error.beyond_organization':
    'The organization does not hold these rights itself, so an own role cannot give them: {capabilities}.',
  'access.ownRoles.error.reserved_capability':
    "An own role cannot give «{capabilities}» – only Starpoint's roles do that.",
  'access.ownRoles.error.beyond_own_authority':
    'You cannot give or take rights you do not hold yourself: {capabilities}.',
  'access.ownRoles.error.last_holder':
    'Then nobody who hands out roles here would hold these rights: {capabilities}. Give them to somebody else first.',
  'access.ownRoles.error.name_is_system_role':
    "The name is used by one of Starpoint's roles. Choose another name.",
  'access.ownRoles.error.name_has_no_usable_key': 'The name must contain letters or digits.',
  'access.ownRoles.error.not_found': 'The role no longer exists. The list has been fetched again.',
  // The short name of each organization right, as /sa's matrix names it.
  'capability.organization.read': 'See data',
  'capability.organization.write': 'Change setup and data',
  'capability.organization.rename': 'Rename the organization',
  'capability.organization.access.manage': 'Hand out roles',
  'capability.organization.links.manage': 'Enter co-operations',
  'capability.organization.audit.read': "Read the organization's log",
  'capability.court.manage': 'Create and change courts',
  'capability.asset.manage': 'Create and change equipment',
  'capability.court.set_out_of_service': 'Take a court out of service',
  'capability.court.override_out_of_service': 'Book a closed court',
  'capability.court.pair_device': 'Pair a Banepad',
  'capability.court.screen.control': 'Control the screens',
  'capability.booking.create': 'Book for others',
  'capability.booking.modify': "Change others' bookings",
  'capability.booking.delete': "Cancel others' bookings",
  'capability.booking.override_constraints': 'Book outside the rules',
  'capability.booking.rules.manage': 'Booking rules and prices',
  'capability.activity.manage': 'Run activities',
  'capability.planned_match.manage': 'Set up matches',
  'capability.tournament.manage': 'Run tournaments and series',
  'capability.result.record': 'Record results',
  'capability.result.correct': 'Correct a recorded result',
  'capability.rating.commit': 'Commit results to the rating',
  'capability.member.manage': 'Manage members',
  'capability.member.contact.read': 'See contact details',
  'capability.member.export': 'Export the member list',
  'capability.membership.approve': 'Approve memberships',
  'capability.membership.pricing.manage': 'Member prices and offers',
  'capability.messaging.send_as_organization': 'Reply as the organization',
  'capability.messaging.broadcast': 'Send to all members',
  'capability.messaging.moderate': 'Moderate messages',
  'capability.messaging.disable_organization': 'Turn messaging off',
  'capability.bug.manage': 'Handle bug reports',
  'capability.finance.read': 'See finances and reports',
  'capability.payment.refund': 'Refund',
  'capability.payment.manual_action': 'Intervene in a payment',
  'capability.payment.account.manage': 'The payment account',
  'errors.not_in_organization':
    'That person is not linked to this organization. New people are added by Starpoint.',
  'errors.unknown_role': 'That role cannot be granted in this organization.',
  'errors.beyond_own_authority':
    'That role carries rights you do not hold yourself, so you can neither grant nor remove it.',
  'errors.last_access_manager':
    'This is the last person who can manage access here. Give somebody else that role first.',
  'errors.last_holder':
    'Then nobody who hands out roles here would hold every right this role gives. Give somebody else that role first.',
  'errors.reauthentication_required': 'Sign in again to continue.',
  'clubSettings.links.title': 'Co-operation',
  'clubSettings.links.lead':
    'Co-operation between a centre and a club: what you give the other, and what you get back.',
  'clubSettings.links.open': 'Manage co-operation',
  // ── LOGG — the organization's own audit trail (#935, 2026-11-01) ──
  'clubSettings.auditLog.title': 'Log',
  'clubSettings.auditLog.lead':
    'Who did what in the organization — roles, bookings, settings and what Starpoint did for you.',
  'clubSettings.auditLog.open': 'View the log',
  'auditLog.title': 'Log',
  'auditLog.lead': 'Who did what in {organization}, newest first.',
  'auditLog.thisOrganization': 'the organization',
  'auditLog.notShown':
    "Sign-ins and people's own account changes are not shown, nor free text or the names of Starpoint staff. Your viewing of the log is logged too.",
  'auditLog.back': 'Back to settings',
  'auditLog.loading': 'Fetching the log …',
  'auditLog.forbidden': "You don't have access to the log in this organization.",
  'auditLog.unavailable': "Couldn't fetch the log right now.",
  'auditLog.empty': 'Nothing was logged in this period.',
  'auditLog.more': 'Show more',
  'auditLog.period': 'Period',
  'auditLog.periodDays': 'Last {days} days',
  'auditLog.areaLabel': 'Area',
  'auditLog.area.all': 'Everything',
  'auditLog.area.roles': 'Roles',
  'auditLog.area.bookings': 'Bookings',
  'auditLog.area.activities': 'Activities',
  'auditLog.area.members': 'Members',
  'auditLog.area.courts': 'Courts',
  'auditLog.area.settings': 'Settings',
  'auditLog.area.payments': 'Payments',
  'auditLog.area.starpoint': 'Starpoint support',
  'auditLog.actor.starpoint': 'Starpoint',
  'auditLog.actor.member': 'A member (the app)',
  'auditLog.actor.device': 'A court tablet',
  'auditLog.actor.system': 'The system',
  'auditLog.actor.provider': 'The payment provider',
  'auditLog.actor.unknownPerson': 'A user',
  'auditLog.subject.user': 'a user',
  'links.title': 'Co-operation',
  'links.lead.centre':
    'The clubs {organization} co-operates with. The centre proposes and chooses what the club gets with you; the club accepts and chooses what you get with it.',
  'links.lead.club':
    'The centres {organization} co-operates with. The centre proposes and chooses what you get there; you accept and choose what the centre gets with you.',
  'links.thisOrganization': 'this organization',
  'links.back': 'Back to settings',
  'links.loading': 'Loading co-operations …',
  'links.forbidden': 'You do not manage co-operation in this organization.',
  'links.unavailable': 'Could not fetch the co-operations right now.',
  'links.retry': 'Try again',
  'links.inUse':
    'Both directions are in use: the partner club’s activity and tournament leaders can book the centre’s courts from the club’s home page, and the centre’s activity managers can arrange activities in the club’s name from the centre’s home page.',
  'links.none.centre': 'No co-operation yet. Propose one below.',
  'links.none.club': 'No co-operation yet. A centre proposes, and the proposal appears here.',
  'links.status.proposed': 'Awaiting an answer',
  'links.status.proposedToYou': 'Proposed to you',
  'links.status.active': 'Active',
  'links.status.suspended': 'Paused',
  'links.gives': 'You give',
  'links.receives': 'You get',
  'links.nothing': 'Nothing',
  'links.notAnswered': 'Decided when the club accepts',
  'links.capabilities.none': 'Grants no rights.',
  'links.capability.link.booking.create': "book the centre's courts on the club's behalf",
  'links.capability.link.booking.extended_horizon': 'book further ahead than usual',
  'links.capability.link.booking.partner_rate': 'book at the agreed partner rate',
  'links.capability.link.activity.manage': "create and run activities in the other's name",
  'links.rate': 'Agreed court rate',
  'links.rate.value': 'NOK {amount} per hour',
  'links.rate.list': "The centre's list price",
  'links.rate.input': 'Agreed price per hour (NOK, empty = list price)',
  'links.rate.invalid': 'The price must be a whole number of kroner, 0 or more.',
  'links.save': 'Save',
  'links.saving': 'Saving …',
  'links.saved': 'The change is saved.',
  'links.accept.choose': 'What does the centre get with you?',
  'links.accept': 'Accept',
  'links.accepted': 'The co-operation with {name} has started.',
  'links.decline': 'Decline',
  'links.decline.question': 'Decline the proposal from {name}?',
  'links.withdraw': 'Withdraw',
  'links.withdraw.question': 'Withdraw the proposal to {name}?',
  'links.end': 'End the co-operation',
  'links.end.question': 'End the co-operation with {name}?',
  'links.ended': 'The co-operation with {name} has ended.',
  'links.propose.title': 'Propose co-operation',
  'links.propose.lead':
    'Find the club, choose what it gets with you, and send the proposal. The club answers on its side.',
  'links.propose.club': 'Club',
  'links.propose.gives': 'The club gets',
  'links.propose.submit': 'Send the proposal',
  'links.propose.done': 'The proposal is sent. The club answers on its side.',
  'errors.only_a_centre_proposes':
    'A co-operation is proposed by the centre. A club answers proposals.',
  'errors.only_the_club_accepts': 'It is the club that accepts a proposal.',
  'errors.not_proposed': 'The proposal has already been answered.',
  'errors.not_accepted': 'The club chooses what it gives when it accepts.',
  'errors.terms_are_the_centres': "The agreed price is the centre's to set.",
  'errors.link_exists': 'You already have a co-operation or a proposal with this club.',
  'errors.unknown_club': 'Could not find that club.',
  'errors.not_a_club':
    'That is a centre, not a club. A co-operation is between a centre and a club.',
  'errors.unknown_link_role': 'That choice cannot be given in a co-operation.',
  'partnerCourts.title': 'Courts at a partner centre',
  'partnerCourts.lead': 'The club co-operates with a centre and can book courts there.',
  'partnerCourts.open': 'Book courts at {centre}',
  'linkBooking.title': 'Book courts at {centre}',
  'linkBooking.lead':
    'The booking is made for {club}. The player pays nothing — the centre invoices the club.',
  'linkBooking.leadPending': 'Loading free times …',
  'linkBooking.back': 'Back',
  'linkBooking.loading': 'Loading free times …',
  'linkBooking.forbidden': 'You cannot book courts at this centre for the club.',
  'linkBooking.unavailable': 'Could not load free times right now.',
  'linkBooking.retry': 'Try again',
  'linkBooking.terms.partner': 'The price is the agreed partner rate.',
  'linkBooking.terms.list': 'The price is the centre’s list price.',
  'linkBooking.terms.window': 'The club can book {days} days ahead.',
  'linkBooking.date': 'Day',
  'linkBooking.duration': 'Duration',
  'linkBooking.minutes': '{minutes} minutes',
  'linkBooking.noSlots': 'No free courts that day for that duration.',
  'linkBooking.charge': 'NOK {amount} to the club',
  'linkBooking.court': 'Court {court}',
  'linkBooking.booked': 'Court {court} is booked on {date} at {time}.',
  'linkBooking.cancelled': 'The booking is cancelled.',
  'linkBooking.mine.title': 'Bookings for {club}',
  'linkBooking.mine.none': 'The club has no upcoming bookings here.',
  'linkBooking.cancel.question': 'Cancel the booking on {date} at {time}?',
  'linkBooking.cancel.confirm': 'Cancel it',
  'links.window': 'The partner club can book',
  'links.window.value': '{days} days ahead',
  'links.window.ordinary': 'The centre’s ordinary limit',
  'links.window.input': 'Days ahead the club can book (empty = the centre’s ordinary limit)',
  'links.window.invalid': 'The number of days must be a whole number from 1 to 365.',
  'errors.link_not_active': 'The co-operation is no longer active.',
  'errors.date_outside_booking_window': 'That day cannot be booked yet.',
  'errors.slot_not_available': 'That time is no longer free. Pick another.',
  'errors.slot_in_the_past': 'That time has already started.',
  'errors.duration_not_allowed': 'That duration is not offered here.',
  'errors.court_out_of_service': 'The court is out of service.',
  'errors.booking_started': 'The booking has started. Contact the centre to change it.',
  'errors.link_home_required': 'Choose which club the booking is for.',
  'errors.request_id_reused': 'The booking was sent twice with different contents. Try again.',
  'errors.booking_rejected': 'The booking was refused by the centre’s rules.',
  'errors.invalid_booking_window': 'The number of days must be a whole number from 1 to 365.',
  'errors.booking_has_payment': 'The booking has a payment attached. Contact the centre.',
  // A staff delete cancels any live payment at Stripe first (#1310).
  'errors.payment_cancel_unconfirmed':
    'Stripe did not confirm the open payment was cancelled – the member may have paid. The booking was not deleted. Try again in a few minutes.',
  'errors.booking_payment_open': 'The booking has a payment in progress. Try again in a moment.',
  'errors.payment_cancel_unavailable':
    'The booking has an open payment and card payment is not set up here, so it cannot be cancelled.',
  // ── "Activities at the club" — a centre arranges activities in a club's
  // name through a co-operation (slice 4d, 2026-10-21; §4k 1A–4A).
  'partnerActivities.title': 'Activities at a partner club',
  'partnerActivities.lead': 'The centre co-operates with a club and can arrange activities there.',
  'partnerActivities.open': 'Activities at {club}',
  'linkActivities.title': 'Activities at {club}',
  'linkActivities.lead':
    '{centre} arranges these in the club’s name. The club’s players sign up at the club.',
  'linkActivities.back': 'Back',
  'linkActivities.loading': 'Loading activities …',
  'linkActivities.forbidden': 'You cannot arrange activities at this club for the centre.',
  'linkActivities.unavailable': 'Could not load the activities right now.',
  'linkActivities.retry': 'Try again',
  'linkActivities.courts':
    'Courts are booked by the centre as usual — an activity here reserves no court.',
  'linkActivities.new.title': 'New activity',
  'linkActivities.edit.title': 'Change activity',
  'linkActivities.edit.stop': 'Stop editing',
  'linkActivities.field.name': 'Name',
  'linkActivities.field.kind': 'Activity type',
  'linkActivities.field.date': 'Date',
  'linkActivities.field.capacity': 'Maximum participants (empty = no limit)',
  'linkActivities.field.start': 'Starts',
  'linkActivities.field.end': 'Ends',
  'linkActivities.field.description': 'Description',
  'linkActivities.kind.arrangement': 'Event',
  'linkActivities.kind.coaching': 'Coaching',
  'linkActivities.create': 'Create activity',
  'linkActivities.save': 'Save changes',
  'linkActivities.created': '“{name}” is created at the club.',
  'linkActivities.saved': '“{name}” is saved.',
  'linkActivities.cancelled': '“{name}” is cancelled.',
  'linkActivities.none': 'The centre has no activities at the club yet.',
  'linkActivities.status.cancelled': 'Cancelled',
  'linkActivities.signups': '{count} signed up',
  'linkActivities.signupsOf': '{count} of {max} signed up',
  'linkActivities.waitlist': 'waiting list',
  'linkActivities.edit': 'Change',
  'linkActivities.cancel': 'Cancel',
  'linkActivities.cancel.question': 'Cancel “{name}”? Those signed up will see it is cancelled.',
  'linkActivities.cancel.confirm': 'Cancel it',
  'linkActivities.invalid.name': 'The activity needs a name.',
  'linkActivities.invalid.date': 'Choose a date.',
  'linkActivities.invalid.capacity': 'The maximum must be a whole number.',
  'errors.invalid_name': 'The activity needs a name (at most 120 characters).',
  'errors.invalid_date': 'The date is invalid.',
  'errors.invalid_time': 'The time is invalid, or the end is before the start.',
  'errors.invalid_capacity': 'The maximum participants must be a whole number of 1 or more.',
  'errors.invalid_kind': 'That activity type cannot be chosen here.',
  'errors.invalid_description': 'The description is too long.',
  'errors.date_in_the_past': 'That date has already passed.',
  'errors.activity_cancelled': 'The activity is cancelled and cannot be changed.',
  'errors.activity_not_found': 'The activity no longer exists.',
  'role.PLATFORM_SUPERADMIN': 'Global administrator',
  'role.PLATFORM_SUPPORT': 'Support',
  'role.PLATFORM_FINANCE': 'Finance',
  'role.PLATFORM_OPERATIONS': 'Operations',
  'role.PLATFORM_READONLY': 'Read-only (platform)',
  'role.TENANT_OWNER': 'Owner',
  'role.TENANT_ADMIN': 'Administrator',
  'role.TENANT_MANAGER': 'Manager',
  'role.TENANT_STAFF': 'Staff',
  'role.TENANT_READONLY': 'Read-only',
  'role.TENANT_RECEPTION': 'Reception',
  'role.TENANT_VENUE': 'Venue',
  'role.TENANT_TOURNAMENTS': 'Tournament director',
  'role.TENANT_ACTIVITIES': 'Activities lead',
  'role.TENANT_MEMBERSHIP': 'Membership manager',
  'role.TENANT_COMMUNICATION': 'Communication',
  'role.TENANT_FINANCE': 'Finance manager',
  'role.LINK_PARTNER_CLUB': 'Partner club',
  'role.LINK_HOST_CENTRE': 'Host centre',
  'account.language': 'Language',
  'account.language.help':
    'While you are signed in, your choice follows your account. Otherwise it is saved in this browser only.',
  'language.endonym.nb': 'Norsk',
  'language.endonym.en': 'English',
  'account.language.nb': 'Norwegian (Bokmål)',
  'account.language.en': 'English',
  'account.email.unknown': 'Unknown email',

  'auth.login.sessionExpired': 'Your session has expired. Please sign in again.',
  'auth.login.needsServer': 'Signing in requires a connection to the server.',
  'auth.forgot.checkEmail': 'Check your email',
  'auth.noScope.notFound': 'Could not load your access',
  'auth.noScope.none': 'No access yet',
  'auth.scope.selectOrg': 'Choose an organisation',
  'auth.scope.kind.centre': 'Centre',
  'auth.scope.kind.club': 'Club',
  'app.footer': '{brand} · Club platform',
  'dev.menu.title': 'DEV',
  'dev.menu.lead': 'Developer tools',
  'dev.menu.buildUnknown': 'Unknown build — no commit information.',
  'dev.menu.rights': 'Permissions — switch role',
  'dev.menu.report': 'Report a bug or suggestion',
  'dev.menu.archive': 'See all bugs and suggestions',
  'dev.menu.titleCounts': '{bugs} open bugs, {features} suggestions',
  'dev.menu.titleEmpty': 'Developer tools — roles, bugs and suggestions',
  'dev.menu.signedInAs': 'Signed in as {name}',
  'dev.menu.unknownAccount': 'unknown account',
  'dev.menu.roleIn': 'Role: {roles} in {organization}',
  'dev.menu.noRoleIn': 'No role in {organization}',
  'dev.menu.noOrganization': 'No organization selected',
  'dev.menu.roleLoading': 'Loading the role …',
  'dev.menu.roleUnknown': 'Could not fetch the role right now',
  'dev.rbac.open': 'RBAC scenario …',
  'dev.rbac.presets': 'Fixed personas',
  'dev.rbac.title': 'RBAC scenario',
  'dev.rbac.lead':
    'Pick roles in one or more organizations. A dedicated dev identity gets exactly this — whatever it held before is removed — and you are signed in as it.',
  'dev.rbac.close': 'Close',
  'dev.rbac.identity': 'Dev identity',
  'dev.rbac.player': 'With a player profile',
  'dev.rbac.filter': 'Filter organizations',
  'dev.rbac.ownRole': 'own role',
  'dev.rbac.selected': '{count} roles selected',
  'dev.rbac.clear': 'Clear selection',
  'dev.rbac.apply': 'Apply and sign in',
  'dev.rbac.applying': 'Switching …',
  'dev.rbac.loading': 'Loading organizations and roles …',
  'dev.rbac.unavailable': 'The scenario tool is not available here.',
  'dev.rbac.union.title': 'Effective rights now',
  'dev.rbac.union.lead': 'As the server answers for {name} (from /me).',
  'dev.rbac.union.none': 'No organization roles.',
  'dev.rbac.union.loading': 'Loading …',
  'dev.rbac.union.unknown': 'Could not fetch the rights right now.',
  'dev.rbac.union.roles': 'Roles: {roles}',
  'dev.rbac.union.from': 'from {roles}',
  'dev.rbac.error.role': 'A selected role does not belong to its organization. Nothing changed.',
  'dev.rbac.error.organization': 'A selected organization no longer exists. Nothing changed.',
  'dev.rbac.error.changed': 'The scenario changed at the same time. Try again.',
  'dev.rbac.error.identity': 'The dev identity is not a reserved dev account. Nothing changed.',
  'dev.rbac.error.generic': 'Could not apply the scenario.',
  'app.signOutBrowser': 'Sign out of this browser.',
  'app.retry': 'Try again',
  'app.version': 'App version',
  'app.clubLogo': 'Club logo',
  'app.messages': 'Messages',
  'app.messages.unread': 'Messages ({count} unread)',
  'app.pendingActions': 'Notifications',
  'app.pendingActions.waiting': 'Notifications ({count} waiting for you)',
  'app.pendingActions.heading': 'Waiting for you',
  'app.pendingActions.none': 'Nothing is waiting for you right now.',
  'app.pendingActions.joinRequests.one': '{count} membership request',
  'app.pendingActions.joinRequests.other': '{count} membership requests',
  'app.pendingActions.goTo': 'Go to {destination}',
  'app.noClub': 'No club',
  'app.courts': '{count} courts',
  'app.clubSettings': '{club} — click to edit club settings',
  'app.clubSettings.setup': 'Set up club information',
  'app.activeTournament': 'Live',
  'app.cancel': 'Cancel',
  'ui.confirm.confirm': 'Yes, continue',
  'ui.confirm.cancel': 'Cancel',
  'ui.confirm.question': 'Are you sure?',
  'ui.sortableHeader.sortBy': 'Sort by {column}',
  'tournament.wizard.setupSaveFailed': 'The setup was not saved. {reason}',
  'tournament.saveFailed.message': 'The tournament was not saved — try again',
  'tournament.saveFailed.reload': 'Reload',
  'auth.forgot.throttled': 'Too many attempts. Try again in {minutes} minutes.',
  'auth.forgot.network': 'Could not reach the server. Please try again.',
  'auth.forgot.acceptedLead':
    'If an account exists for that address, a link to choose a new password is on its way. The link is valid for {minutes} minutes.',
  'auth.noScope.lookupFailed':
    'You are signed in{as}, but we could not load which organisations you have access to. Please try again.',
  'auth.noScope.noOrg':
    'You are signed in{as}, but your account is not linked to any organisation. Ask an administrator for access.',
  'auth.noScope.as': ' as {email}',
  'auth.toLogin': 'To sign-in',
  'auth.scope.leadShort': 'You have access to several. Choose the one you want to work in.',
  // ── shared UI primitives (components/ui, #310) ────────────────────
  'ui.nav.menu': 'Menu',
  'ui.nav.pinLeft': 'Pin the menu to the left',
  'ui.nav.unpin': 'Unpin the menu',
  'ui.nav.item.home': 'Home',
  'ui.nav.item.createTournament': 'Create tournament',
  'ui.nav.item.bookings': 'Court booking',
  'ui.nav.item.activities': 'Activities',
  'ui.nav.item.banepad': 'Banepad',
  'ui.nav.item.mobile': 'Mobile app',
  'ui.nav.item.messages': 'Messages',
  'ui.nav.item.players': 'Player database',
  'ui.nav.item.archive': 'Tournament archive',
  'ui.nav.item.reporting': 'Reports',
  'ui.nav.appLabel': '{brand} app',
  'ui.nav.changelog': 'Changelog',
  'ui.nav.changelogTitle': 'Changelog for the {brand} app',
  'ui.nav.engineTitle': 'About {engine} — algorithms, ELO, the draw',
  'ui.status.ready': 'Ready to start',
  'ui.status.running': 'In progress',
  'ui.status.paused': 'Paused',
  'ui.status.finals': 'Finals',
  'ui.status.done': 'Finished',
  'ui.progress.label': 'Progress',
  'ui.progress.matches': '{done}/{total} matches · {pct}%',
  'ui.separator.newDay': 'New day',
  'ui.separator.start': 'Starts {time}',
  'ui.separator.minutes': '{count} minutes',
  'ui.separator.gap': 'Gap',
  'ui.separator.minutesShort': '{count} mins',
  'ui.numInput.lockedHint': 'Locked — controlled by the chosen setup',
  'ui.numInput.decrease': 'Decrease',
  'ui.numInput.increase': 'Increase',
  'ui.setupNumber.decrease': 'Fewer',
  'ui.setupNumber.increase': 'More',
  'ui.lockedField.edit': 'Change',
  'ui.lockedField.lock': 'Lock',
  'ui.dialog.close': 'Close',
  'ui.stepper.decrease': 'Decrease',
  'ui.stepper.increase': 'Increase',
  'km.datePicker.label': 'Choose a date',
  'km.divBadge.title': 'Division {division}',
  'km.datePicker.prevMonth': 'Previous month',
  'km.datePicker.nextMonth': 'Next month',
  'km.playerModal.recordResult': 'Register the result: {home} vs {away}',
  'singleMatches.picker.clearSlot': 'Remove {player}',
  'singleMatches.add.date': 'Date',
  'singleMatches.add.time': 'Time',
  'bugs.detail.newComment': 'New comment',
  'bugs.report.refused.closed': 'The report was not saved: reporting is not open here.',
  'bugs.report.refused.rateLimited':
    'The report was not saved: you have sent many reports in a short time. Try again later.',
  'bugs.report.refused.invalid':
    'The report was not saved: part of it could not be accepted. Check the title, description and image.',
  'bugs.report.refused.tooLarge': 'The report was not saved: it is too large. Try a smaller image.',
  'bugs.report.refused.failed': 'The report was not saved. Try again shortly.',
  'bugs.archive.club': 'Club: {name}',
  'bugs.archive.unknownClub': 'unknown club',
  'bugs.archive.reporter': 'Reported by {name}',
  'mobile.profile.reportBug': 'Report a problem or suggestion',
  'mobile.profile.reportBugHint': 'Tell us about something that does not work, or could be better',
  'archive.searchLabel': 'Search the archive',
  'archive.selectRow': 'Select {name}',
  'platform.organizations.stripeAccountLabel': 'Payment account',
  'platform.organizations.invite.identityAmbiguous':
    'This address already belongs to another account that does not sign in by e-mail (for example with Entra). No new account was created, and no access was granted.',
  'platform.organizations.invite.emailInUse':
    'Another account took this address just now. No new account was created, and no access was granted. Try again to see which account has it.',
  'paymentAccount.title': 'Payments',
  'paymentAccount.lead':
    'Your organization is the seller when someone pays for what it offers. The payment account is set up with Stripe, right here in StarPoint.',
  'paymentAccount.ownerOnly':
    'Only the organization’s owner can set up and manage the payment account.',
  'paymentAccount.notAvailable': 'Payments are not switched on in this environment.',
  'paymentAccount.unreachable': 'Could not reach the server. Try again.',
  'paymentAccount.providerUnavailable': 'Stripe is not answering right now. Try again shortly.',
  'paymentAccount.providerRefused': 'Stripe refused the request ({code}).',
  'paymentAccount.reauth':
    'Confirm it’s you, then continue. Setup requires a sign-in within the last 15 minutes.',
  'paymentAccount.error': 'Something went wrong ({code}).',
  'paymentAccount.loading': 'Getting the status from Stripe…',
  'paymentAccount.notConnected': 'The organization has no payment account yet.',
  'paymentAccount.open': 'Create payment account',
  'paymentAccount.opening':
    'An earlier attempt to create the account did not finish. Press to finish it — no new account is created.',
  'paymentAccount.finish': 'Finish creating it',
  'paymentAccount.ambiguous':
    'Stripe holds more than one account for the organization. StarPoint has to connect the right one before you can continue — please contact us.',
  'paymentAccount.inProgress': 'The account is being created right now. Try again in a moment.',
  'paymentAccount.working': 'One moment…',
  'paymentAccount.continue': 'Continue setup',
  'paymentAccount.manage': 'Manage the payment account',
  'paymentAccount.close': 'Close',
  'paymentAccount.refresh': 'Refresh status',
  'paymentAccount.stripeSignIn':
    'Stripe will ask you to sign in in a separate window. Stripe, not StarPoint, holds the identity and bank details.',
  'paymentAccount.native':
    'The payment account is set up in a web browser. Open StarPoint in a browser on a computer or phone.',
  'paymentAccount.noKey': 'The payment key is missing from this version of the app.',
  'paymentAccount.loadingStripe': 'Loading Stripe…',
  'paymentAccount.status.charges': 'Can take payments',
  'paymentAccount.status.payouts': 'Payouts to bank',
  'paymentAccount.status.yes': 'Yes',
  'paymentAccount.status.no': 'No',
  'paymentAccount.status.due': 'Details missing: {count}',
  'paymentAccount.status.pastDue':
    'Overdue: {count}. Payments may be paused until they are provided.',
  'paymentAccount.status.pending': 'Stripe is checking the details.',
  'paymentAccount.status.ready': 'The account is ready to take payments.',
  'paymentAccount.payouts.open': 'Payouts',
  'paymentAccount.payouts.lead':
    'The balance, the payout schedule and the payouts to the organization’s bank account. The schedule is not changed here.',
  'paymentAccount.payments.open': 'Payments and disputes',
  'paymentAccount.payments.lead':
    'The payments the organization has received, and disputes you can respond to. Refunds are made in StarPoint, not here.',
  'paymentAccount.afterSetup':
    'Payouts, payments and disputes become available here once the setup at Stripe has been submitted.',
  'ui.tennisBall.server': 'Serving player',
  'ui.eloWeight.label': 'ELO weight',
  'ui.eloWeight.lockHint': 'Click to lock — protects against an accidental change',
  'ui.eloWeight.unlockHint': 'Click to unlock the slider',
  'ui.eloWeight.unlocked': '🔓 Unlocked',
  'ui.eloWeight.edit': '🔒 Change',
  'ui.eloWeight.recommended': '(recommended: {percent}%)',
  'ui.eloWeight.level0.short': 'Practice match',
  'ui.eloWeight.level0.desc':
    'Practice match — does not count towards ELO. For training and casual play.',
  'ui.eloWeight.level50.short': 'Americano style',
  'ui.eloWeight.level50.desc': 'Americano style — short, rotating matches. Half ELO effect.',
  'ui.eloWeight.level100.short': 'Best of 1 set',
  'ui.eloWeight.level100.desc': 'Best of 1 set — one deciding set. Standard ELO effect.',
  'ui.eloWeight.level150.short': 'Shortened (super tie-break)',
  'ui.eloWeight.level150.desc':
    'Shortened match — a super tie-break instead of a full 3rd set. Raised ELO effect.',
  'ui.eloWeight.level200.short': 'Full best of 3',
  'ui.eloWeight.level200.desc':
    'Full match — best of 3 complete sets. Full ELO effect; the match weighs heaviest.',
  'ui.eloWeight.mixShort': 'Mixed: {below} / {above}',
  'ui.eloWeight.mixDesc': 'Between “{below}” ({belowPct} %) and “{above}” ({abovePct} %).',
  'ui.eloWeight.customDesc': '{percent} % ELO weight.',

  // ═══ BOOKING SURFACE (#310) — one contiguous block ═══
  // ── booking: shared controls ──────────────────────────────────────
  'booking.cancel': 'Cancel',
  'booking.close': 'Close',
  'ui.toast.dismiss': 'Dismiss message',
  'ui.skipToContent': 'Skip to content',
  // ── booking: court facility icons ─────────────────────────────────
  'booking.court.type.single': 'Singles court (1v1)',
  'booking.court.type.double': 'Doubles court (2v2)',
  'booking.court.runoff': 'You can run out of the court',
  'booking.court.streaming': 'Streaming equipment',
  // ── booking: mock payment terminal ────────────────────────────────
  'booking.payment.title': 'Payment',
  'booking.payment.mockNote': 'Mock payment terminal — no real transaction is made.',
  'booking.payment.payingFor': 'You are paying for',
  'booking.payment.total': 'Total',
  'booking.payment.amountKr': '{amount} kr',
  'booking.payment.declined': 'The payment was declined. Try again or choose another method.',
  'booking.payment.processing': 'Processing payment …',
  'booking.payment.checkVipps': 'Check the Vipps app',
  'booking.payment.contactBank': 'Contacting the bank',
  'booking.payment.chooseMethod': 'Choose a payment method',
  'booking.payment.vippsHint': 'You will get a notification on your phone to confirm',
  'booking.payment.card': 'Card',
  'booking.payment.cancelling': 'Cancel …',
  // ── booking: the rules explainer ──────────────────────────────────
  'booking.help.title': 'How court booking works',
  'booking.help.steps.title': '📅 Booking in three steps',
  'booking.help.steps.one.before': 'Choose a',
  'booking.help.steps.one.date': 'date',
  'booking.help.steps.two.before': 'Choose a',
  'booking.help.steps.two.start': 'start time',
  'booking.help.steps.two.after':
    'click a free time slot in the calendar, or browse the «Ledige tider» tab.',
  'booking.help.steps.three.before': 'In the window that opens, choose the',
  'booking.help.steps.three.duration': 'duration',
  'booking.help.steps.three.and': 'and',
  'booking.help.steps.three.court': 'court',
  'booking.help.steps.three.after': 'and pay.',
  'booking.help.times.title': '🎯 Permitted start times and durations',
  'booking.help.times.before': 'Bookings always start on the',
  'booking.help.times.halfHour': 'hour or the half hour',
  'booking.help.times.examples': '({open}, 07:30, 08:00 and so on). You can choose',
  'booking.help.times.durationsPrefix': 'durations of',
  'booking.help.times.durationsValue': '{list} minutes',
  'booking.help.times.durationsAny': 'any duration you need',
  'booking.help.times.open': 'The centre is open',
  'booking.help.gap.title': '⚖ The gap rule (explained)',
  'booking.help.gap.offBefore': 'The gap rule is',
  'booking.help.gap.offStrong': 'switched off',
  'booking.help.gap.offAfter': 'Bookings may have any amount of air between them, including none.',
  'booking.help.gap.onBefore': 'A gap between two bookings on the same court must be either',
  'booking.help.gap.onZero': '0 minutes',
  'booking.help.gap.onMiddle': '(back to back) or',
  'booking.help.gap.onMore': 'longer than {min} minutes',
  'booking.help.gap.onAfter':
    'This stops small dead gaps nobody can use from appearing in the calendar.',
  'booking.help.gap.exampleTitle': 'Example — an existing booking 12:00–13:00:',
  'booking.help.gap.ok': '✓ OK',
  'booking.help.gap.rejected': '✗ Refused',
  'booking.help.gap.example1': 'A new booking 13:00–14:30 (straight after, 0 min gap)',
  'booking.help.gap.example2':
    'A new booking 13:30–14:30 (30 min gap — too small for anyone else to fit)',
  'booking.help.gap.example3':
    'A new booking 14:30–16:00 (90 min gap after — room for exactly one more booking)',
  'booking.help.smart.title': '💚 Why can I not always choose any court?',
  'booking.help.smart.p1Before':
    'Sometimes a court is free on paper but cannot be chosen. That is because we',
  'booking.help.smart.p1Strong': 'allocate bookings smartly',
  'booking.help.smart.p1After':
    'if your time fits more snugly on another free court, you are steered there.',
  'booking.help.smart.p2':
    'An example: you want to book 90 minutes. Court 1 is free for a two-hour window, while court 2 has exactly 90 minutes free. You are placed on court 2 — that way no small, unusable gap is left on court 1, and someone else can still use the large window later.',
  'booking.help.smart.p3Before': 'The result:',
  'booking.help.smart.p3Strong': 'as many people as possible get to play',
  'booking.help.smart.p3After':
    'You pay the same whichever court you get — the courts are equally good.',
  'booking.help.multi.title': '🏟 When several courts are free',
  'booking.help.multi.body':
    'We show every court that is free at the time you picked. Choose whichever you like. If only one court is free because the others would create small dead gaps, that court is shown as the only option.',
  'booking.help.price.title': '💰 Price and price zones',
  'booking.help.price.p1a': 'The price is worked out from a',
  'booking.help.price.p1Default': 'default price',
  'booking.help.price.p1b': '(base price per hour) plus any',
  'booking.help.price.p1Zones': 'price zones',
  'booking.help.price.p1c': 'an admin has defined. You always see the total — or',
  'booking.help.price.p1Free': '«Free»',
  'booking.help.price.p1d': '— before you confirm.',
  'booking.help.price.defaultLabel': 'Default price:',
  'booking.help.price.defaultText': 'applies to any time no zone covers. May be set to 0 (free).',
  'booking.help.price.zoneLabel': 'Price zone:',
  'booking.help.price.zoneText1':
    'a period (evening primetime, say) with its own price. A zone may either',
  'booking.help.price.zoneReplace': 'replace',
  'booking.help.price.zoneText2': 'the default price or be',
  'booking.help.price.zoneOnTop': 'added on top',
  'booking.help.price.zoneText3': 'of it.',
  'booking.help.price.holidayLabel': 'Public holidays:',
  'booking.help.price.holidayText':
    'each zone may include them, skip them, or apply only on them. The holiday set (NO/SE/DK) is set centrally.',
  'booking.help.price.rangeLabel': 'Time range:',
  'booking.help.price.rangeText':
    'a zone with no time range applies around the clock. Set a range (17:00–21:00, say) to make it apply to primetime only.',
  'booking.help.price.example':
    'Example: default price 0 (free) + a primetime zone at 200 kr/h from 17:00. A booking 16:00–18:00 then costs 0 for 16–17 and 200 for 17–18 = 200 kr.',
  'booking.help.facilities.title': '🎾 Court facilities',
  'booking.help.facilities.lead':
    'Each court shows icons for what it supports, to help you choose the right one:',
  'booking.help.facilities.double': 'Doubles court (2v2) — or',
  'booking.help.facilities.single': 'singles court (1v1)',
  'booking.help.facilities.runoff': 'You can run out behind the court',
  'booking.help.facilities.streaming': 'Streaming equipment fitted',
  'booking.help.calendar.title': '📋 Calendar view',
  'booking.help.calendar.p1a':
    'The calendar view shows every court as a row, with time running across. A red',
  'booking.help.calendar.nowLine': '«now» line',
  'booking.help.calendar.p1b':
    'marks the current time (today only), and bookings that have passed are greyed out. Scroll sideways to see the whole day — the court names stay pinned on the left.',
  'booking.help.calendar.p2a': 'Bookings are',
  'booking.help.calendar.p2Strong': 'colour-coded by who made them:',
  'booking.help.calendar.legend.user': 'Ordinary user',
  'booking.help.calendar.legend.admin': 'Centre / admin',
  'booking.help.calendar.legend.tournament': 'Tournament',
  'booking.help.calendar.legend.series': 'Recurring (series)',
  'booking.help.recurring.title': '🔁 Recurring bookings',
  'booking.help.recurring.p1a': 'An admin can create',
  'booking.help.recurring.p1Strong': 'recurring bookings',
  'booking.help.recurring.p1b':
    '(«every Monday at 18:00» or «every other day», say). They are marked with a 🔁 icon and belong together as a series.',
  'booking.help.recurring.p2a':
    'If you click a booking in a series, you are asked whether you want to change or cancel',
  'booking.help.recurring.p2One': 'that one occurrence only',
  'booking.help.recurring.p2Or': 'or',
  'booking.help.recurring.p2All': 'the whole series',
  'booking.help.recurring.p2b':
    'When you change the whole series you can also adjust the pattern itself (how often, which days, when it ends).',
  'booking.help.price2.title': '💰 Price',
  'booking.help.price2.p1a': 'The price is worked out from the',
  'booking.help.price2.zones': 'price zones',
  'booking.help.price2.p1b':
    'an admin has defined. Zones can set the price per weekday, time range (evening primetime, say) and public holidays. You see the total before you pay.',
  'booking.help.admin.title': '⚙ Admin mode',
  'booking.help.admin.body':
    'When admin mode is on (ticked at the top), the payment flow is skipped. An admin can then enter bookings on behalf of players, book several courts at once, create recurring series, override rules, and set the status (free, awaiting payment, paid) directly.',
  // Weekday shorthands, for DISPLAY only — never a stored value or a
  // comparison key (docs/architecture/DOMAIN_RULES.md §4).
  'booking.weekday.short.sun': 'Sun',
  'booking.weekday.short.mon': 'Mon',
  'booking.weekday.short.tue': 'Tue',
  'booking.weekday.short.wed': 'Wed',
  'booking.weekday.short.thu': 'Thu',
  'booking.weekday.short.fri': 'Fri',
  'booking.weekday.short.sat': 'Sat',
  // ── booking: the price-zone editor ───────────────────────────────
  'booking.zones.title': 'Price zones',
  'booking.zones.lead':
    'Define what a court costs at different times. The higher priority wins where zones overlap.',
  'booking.zones.holidayPopup.title': 'Public holidays ({country})',
  'booking.zones.holidayPopup.lead':
    'Upcoming holidays in the active set. Change the country and the days under Settings.',
  'booking.zones.holidayPopup.empty': 'No upcoming holidays in the set.',
  'booking.zones.holidayPopup.fallbackName': 'Public holiday',
  'booking.zones.defaultPrice': 'Default price',
  'booking.zones.defaultPriceHelp': 'Applies to any time no zone below covers.',
  'booking.zones.defaultPriceFree': 'Currently: free.',
  'booking.zones.free': 'Free',
  'booking.zones.perHour': 'kr/h',
  'booking.zones.holidaysToggle': '📅 Public holidays ({country})',
  'booking.zones.hide': '▲ Hide',
  'booking.zones.showDays': '▼ Show days',
  'booking.zones.holidaysInlineLead': 'The set is managed under Settings. Upcoming days:',
  'booking.zones.holidaysInlineEmpty': 'No upcoming holidays.',
  'booking.zones.customHoliday': 'Custom',
  'booking.zones.empty':
    'No price zones yet. The price is not set, and nothing can be paid until you add at least one zone.',
  'booking.zones.collapseZone': 'Collapse zone',
  'booking.zones.expandZone': 'Expand zone',
  'booking.zones.zoneName': 'Zone name',
  'booking.zones.zoneNamePlaceholder': 'Primetime, say',
  'booking.zones.price': 'Price',
  'booking.zones.priorityHelp': 'Where two zones overlap, the one with the highest priority wins',
  'booking.zones.priority': 'Prio.',
  'booking.zones.priorityInputHelp': 'A higher number wins where zones overlap',
  'booking.zones.confirmDelete': 'Confirm deletion',
  'booking.zones.delete': 'Delete',
  'booking.zones.deleteZone': 'Delete zone',
  'booking.zones.summary.onlyHolidays': 'Public holidays only',
  'booking.zones.summary.allDays': 'Every day',
  'booking.zones.summary.allDay': 'Around the clock',
  'booking.zones.summary.modeAdd': '+ default',
  'booking.zones.summary.modeReplace': 'replaces the default',
  'booking.zones.priceMode': 'How the price works',
  'booking.zones.priceModeReplace': 'Replace the default price',
  'booking.zones.priceModeAdd': 'On top of the default',
  'booking.zones.priceModeReplaceHelp':
    'During these hours it costs {price} kr/h — the default price is ignored.',
  'booking.zones.priceModeAddHelp': 'During these hours it costs the default price + {price} kr/h.',
  'booking.zones.weekdays': 'Weekdays (empty = all)',
  'booking.zones.holidays': 'Public holidays',
  'booking.zones.showDaysCountry': 'Show days ({country})',
  'booking.zones.onlyHolidaysNoteBefore': 'This zone applies',
  'booking.zones.onlyHolidaysNoteStrong': 'on public holidays only',
  'booking.zones.onlyHolidaysNoteAfter': '— the weekdays above are ignored.',
  'booking.zones.holidayWhen': 'When a chosen day falls on a public holiday:',
  'booking.zones.holidayInclude': 'Include',
  'booking.zones.holidaySkip': 'Skip',
  'booking.zones.onlyHolidaysCheckbox': 'A zone for public holidays only',
  'booking.zones.tipBefore': 'Tip: set the price above to',
  'booking.zones.tipStrong': '0 / Free',
  'booking.zones.tipAfter':
    'to make booking free on public holidays. Or give holidays a price of their own.',
  'booking.zones.timeRanges': 'Time ranges (empty = around the clock)',
  'booking.zones.rangeFrom': 'From',
  'booking.zones.rangeTo': 'To',
  'booking.zones.removeRange': 'Remove time range',
  'booking.zones.addTimeRange': '+ Add a time range',
  'booking.zones.noRangeBefore': '⚠ With no time range this zone applies',
  'booking.zones.noRangeStrong': 'around the clock',
  'booking.zones.noRangeAfter':
    'and overrides the default price for every hour. Add a time range (17:00–21:00, say) to make it apply to primetime only.',
  'booking.zones.addZone': '+ New price zone',
  'booking.zones.saveAll': 'Save all changes',
  // ── booking: the booking-rules editor ────────────────────────────
  'booking.config.title': 'Booking settings',
  'booking.config.lead': 'Set the rules for bookings and which courts are open.',
  'booking.config.tab.general': '⚙️ General',
  'booking.config.tab.courts': '🎾 Courts',
  'booking.config.on': 'On',
  'booking.config.off': 'Off',
  'booking.config.minutes': '{n} min',
  'booking.config.days': '{n} days',
  'booking.config.custom.addMinutes': 'Add your own number of minutes',
  'booking.config.custom.addDays': 'Add your own number of days',
  'booking.config.custom.placeholderMinutes': 'min',
  'booking.config.custom.placeholderDays': 'days',
  'booking.config.custom.add': 'Add',
  'booking.config.custom.remove': 'Remove {v}',
  'booking.config.custom.errInvalid': 'Enter a whole number.',
  'booking.config.custom.errRange': 'Must be between {min} and {max}.',
  'booking.config.custom.errDuplicate': 'That choice already exists.',
  'booking.config.courtLabel': 'Court {n}',
  'booking.config.gap.label': 'Smallest permitted gap',
  'booking.config.gap.help':
    'Two bookings on the same court must either run back to back, or leave at least this much between them. That avoids small gaps nobody can book.',
  'booking.config.gap.none': 'Off (no restriction)',
  'booking.config.smart.label': 'Smart booking',
  'booking.config.smart.sub': '(on top of the gap rule)',
  'booking.config.smart.help':
    'Maximises court utilisation: the user does not pick a court, the system takes the one that leaves the least unused time. A 90-minute booking will not land in a two-hour opening when another court has exactly 90 minutes free. The gap rule still applies.',
  'booking.config.direct.label': 'Allow booking straight from the calendar',
  'booking.config.direct.help':
    'On: the user books by clicking straight into the court matrix. Off: the matrix is display only, and booking goes through «Free slots». An admin always books directly.',
  'booking.config.durations.label': 'Permitted durations',
  'booking.config.durations.count': '{n} selected',
  'booking.config.durations.help':
    'Durations the customer may choose. An admin always has all of them.',
  'booking.config.durations.required': '⚠ At least one duration must be selected.',
  'booking.config.hours.label': 'Opening hours',
  'booking.config.hours.help': 'When in the day courts can be booked.',
  'booking.config.hours.unsaved':
    'The opening hours are not saved yet. Booking opens when you save them — even if you keep the times shown here.',
  'booking.config.hours.allDay': 'Open around the clock (24/7)',
  'booking.config.hours.open': 'Opens',
  'booking.config.hours.close': 'Closes',
  'booking.config.hours.overMidnight':
    '↳ Closes the next day at {close} ({hours} hours open in one stretch).',
  'booking.config.availability.label': 'Time range for the availability indicator',
  'booking.config.availability.help':
    'Which hours the coloured dots on the day buttons count. If the centre is open around the clock you can leave out the night, so «free» reflects the hours people actually play. Empty = the opening hours.',
  'booking.config.availability.from': 'From',
  'booking.config.availability.to': 'To',
  'booking.config.availability.reset': 'Reset (use the opening hours)',
  'booking.config.window.label': 'How far ahead can be booked',
  'booking.config.window.help':
    'How many days ahead ordinary users can book, counting today. Days beyond that are locked. An admin books with no limit.',
  'booking.config.cancellation.label': 'Cancellation deadline',
  'booking.config.cancellation.help':
    'How long before the start a player may cancel their own booking in the app. After the deadline the player has to contact the club.',
  'booking.config.cancellation.hours': '{n} h before',
  'booking.config.cancellation.untilStart': 'Until the start',
  'booking.config.holidays.label': 'Public holidays',
  'booking.config.holidays.help':
    "Which country's public holidays apply — to holiday price zones and to «skip public holidays» in recurring bookings.",
  'booking.config.holidays.no': '🇳🇴 Norwegian',
  'booking.config.holidays.se': '🇸🇪 Swedish',
  'booking.config.holidays.dk': '🇩🇰 Danish',
  'booking.config.holidays.empty': 'No upcoming holidays in the set.',
  'booking.config.holidays.custom': 'Custom',
  'booking.config.holidays.remove': 'Remove',
  'booking.config.holidays.add': 'Add',
  'booking.config.holidays.reset': 'Reset your own changes',
  'booking.config.courts.count': 'Number of courts',
  'booking.config.courts.countValue': '{n} courts',
  'booking.config.courts.help':
    'How many courts the system handles. It need not match the {n} registered.',
  'booking.config.courts.option.one': '{n} court',
  'booking.config.courts.option.other': '{n} courts',
  'booking.config.facilities.label': 'Facilities (default for every court)',
  'booking.config.facilities.help':
    'The default for every court. Can be overridden per court below. Shown as icons wherever the court appears.',
  'booking.config.facilities.courtType': 'Court type',
  'booking.config.facilities.double': 'Doubles (2v2)',
  'booking.config.facilities.single': 'Singles (1v1)',
  'booking.config.facilities.runoff': 'You can run out of the court',
  'booking.config.facilities.streaming': 'Streaming equipment',
  'booking.config.perCourt.label': 'Customise per court (name, sponsor, facilities)',
  'booking.config.perCourt.help':
    'Give the court a nickname («Centre court», say) and optionally a sponsor with a logo. The logo is pasted as a URL or uploaded.',
  'booking.config.perCourt.nicknamePlaceholder': 'Nickname (default: Court {n})',
  'booking.config.perCourt.sponsorPlaceholder': 'Sponsor name (optional)',
  'booking.config.perCourt.logoPlaceholder': 'Logo URL (optional)',
  'booking.config.perCourt.nicknameLabel': 'Nickname for court {n}',
  'booking.config.perCourt.sponsorLabel': 'Sponsor name for court {n}',
  'booking.config.perCourt.logoLabel': 'Logo URL for court {n}',
  'booking.config.perCourt.upload': 'Upload',
  'booking.config.perCourt.removeLogo': 'Remove the logo',
  'booking.config.perCourt.facilities': 'Facilities',
  'booking.config.perCourt.resetOverrides': '↺ Reset to the default ({n} overridden)',
  'booking.config.perCourt.defaultOption': 'Default ({value})',
  'booking.config.perCourt.type': 'Type',
  'booking.config.perCourt.runoff': 'Run-off',
  'booking.config.perCourt.streaming': 'Streaming',
  'booking.config.perCourt.doubleShort': 'Doubles',
  'booking.config.perCourt.singleShort': 'Singles',
  'booking.config.activeCourts.label': 'Active courts',
  'booking.config.activeCourts.all': 'All ({n})',
  'booking.config.activeCourts.someOf': '{n} of {total}',
  'booking.config.activeCourts.help': 'Click to close a court for booking (maintenance, say).',
  'booking.config.resetAll': 'Reset to the default values',
  'booking.config.save': 'Save settings',
  // ── booking: the new/edit booking modal ─────────────────────────
  'booking.edit.titleCreate': 'New booking',
  'booking.edit.titleEdit': 'Edit booking',
  'booking.edit.confirm.title': 'Cancel this booking?',
  'booking.edit.confirm.seriesTitle': 'Cancel the whole series?',
  'booking.edit.confirm.body':
    'This booking will be removed permanently. The action cannot be undone.',
  'booking.edit.confirm.seriesBody':
    'This cancels all {count} bookings in the series. The action cannot be undone.',
  'booking.edit.confirm.confirm': 'Yes, cancel it',
  'booking.edit.confirm.withAssets': 'The equipment booked with it ({n}) is removed too.',
  // «Avbestill og refunder» (#284): staff cancel a PAID booking and the customer
  // gets the whole payment back. The amount is the payment's, from the server.
  'booking.edit.refund.open': 'Cancel and refund',
  'booking.edit.refund.openAmount': 'Cancel and refund {amount}',
  'booking.edit.refund.title': 'Cancel and refund?',
  'booking.edit.refund.body':
    'The booking is cancelled and the court freed. The whole payment, {amount}, goes back to the customer.',
  'booking.edit.refund.bodyNoAmount':
    'The booking is cancelled and the court freed. The whole payment goes back to the customer.',
  'booking.edit.refund.withAssets': 'The equipment booked with it ({n}) is cancelled too.',
  'booking.edit.refund.irreversible': 'A refund cannot be undone.',
  'booking.edit.refund.confirm': 'Yes, cancel and refund',
  'booking.edit.refund.working': 'Refunding …',
  'booking.edit.refund.retry': 'Try again',
  'booking.edit.refund.keep': 'Keep the booking',
  'booking.edit.refund.close': 'Close',
  'booking.edit.refund.done': 'Cancelled. {amount} has been refunded to the customer.',
  'booking.edit.refund.alreadyCancelled': 'The booking is already cancelled.',
  'booking.edit.refund.unconfirmed':
    'The refund has been sent but is not confirmed yet. The booking stays until the payment is reconciled – press again later to check.',
  'booking.edit.refund.inProgress':
    'The payment is still being confirmed. Wait a moment and try again.',
  'booking.edit.refund.failed':
    'The payment provider refused the refund, so the booking is kept. Try again.',
  'booking.edit.refund.stateMoved':
    'The booking changed while you pressed, so nothing was refunded. Close it, open it again and retry.',
  'booking.edit.refund.unavailable': 'Refunds are not available – no payment provider is set up.',
  'booking.edit.refund.notFound': 'The booking was not found. It may already be cancelled.',
  'booking.edit.refund.notPaid':
    'The booking has no payment to refund. Use «Cancel booking» instead.',
  'booking.edit.refund.reauth':
    'Confirm it is you in the window that opened, then press «Try again».',
  'booking.edit.refund.forbidden': 'You do not have access to refund for this club.',
  'booking.edit.refund.error':
    'No answer came back. Nothing is confirmed refunded – check the booking before you try again.',
  'booking.edit.scope.seriesBefore': 'Changing',
  'booking.edit.scope.seriesStrong': 'the whole series',
  'booking.edit.scope.seriesAfter':
    '({count} bookings). Time, duration and name are updated on all of them — the date and court are kept per occurrence.',
  'booking.edit.scope.occBefore': 'Changing',
  'booking.edit.scope.occStrong': 'this occurrence only',
  'booking.edit.scope.occAfter': 'of the series.',
  'booking.edit.date': 'Date',
  'booking.edit.start': 'Start',
  'booking.edit.startHour': 'Hour',
  'booking.edit.startMinute': 'Minute',
  'booking.edit.duration': 'Duration',
  'booking.edit.until': 'to',
  'booking.edit.noneFree': 'None free',
  'booking.edit.durationOption': '{n} min',
  'booking.edit.durationOptionBusy': '{n} min — no court available',
  'booking.edit.durationOptionAdmin': '{n} min (admin)',
  'booking.edit.durationOptionAdminBusy': '{n} min (admin) — none available',
  'booking.edit.customEnd': 'Custom end time',
  'booking.edit.suggest.body':
    '{duration} min is not possible at {start} on any court. {suggest} min does fit here.',
  'booking.edit.suggest.adjust': 'Adjust to {suggest} min',
  'booking.edit.warn.betterBefore':
    'Court {court} is free, but would leave unnecessarily much empty time here.',
  'booking.edit.warn.betterCourt': 'Court {court}',
  'booking.edit.warn.betterAfter':
    'fits more snugly — that way more people fit. Choose a green court below.',
  'booking.edit.warn.gapOf': 'a {min}-minute',
  'booking.edit.warn.gapSmall': 'a small',
  'booking.edit.warn.deadGap':
    'Court {court} is free at {start}, but a booking until {end} would leave {gap} gap nobody else can use. Choose another court below, or adjust the time or duration.',
  'booking.edit.warn.busy':
    'Court {court} is not free {start}–{end}. Choose another court below, or change the time or duration.',
  'booking.edit.adminOverride.label': 'Admin override:',
  'booking.edit.adminOverride.better':
    'Court {court} is selected, but court {better} would have packed the day more tightly. You can book this one anyway.',
  'booking.edit.adminOverride.deadGap':
    'This booking leaves {gap} gap nobody else can use. You can book it anyway.',
  'booking.edit.adminOverride.smart':
    'This choice breaks the smart-booking rules, but you can book it anyway.',
  'booking.edit.chooseCourt': 'Choose a court',
  'booking.override.withheld':
    "The club's booking rules stopped {count} of {total} reservations ({reason}). You do not hold the right «Book outside the rules», so the rules cannot be overridden from here.",
  'booking.override.refused':
    'You do not hold the right «Book outside the rules», so nothing was reserved. Ask an administrator to make the reservation, or to give you the right.',
  'booking.edit.chooseCourtCount': 'Choose a court ({n} selected)',
  'booking.edit.multiCourtHint': 'Click several courts to book them all at once.',
  'booking.edit.smartInfo.summary': 'This padel centre uses Smart booking',
  'booking.edit.smartInfo.question': 'Why is a court locked even though there is free time on it?',
  'booking.edit.smartInfo.p1':
    'When you book, we look at every court at once and place you so as to leave as few «dead» gaps as possible — short intervals too small for anyone else to book. That means a court can be free right now and still locked, because another court gives a tighter fit.',
  'booking.edit.smartInfo.p2Before': 'That way',
  'booking.edit.smartInfo.p2Strong': 'as many people as possible get to play',
  'booking.edit.smartInfo.p2After':
    'during the day. You pay the same whichever court you get — the courts are equally good. Choose one of the green courts below.',
  'booking.edit.nextFree': 'Next free at {time}',
  'booking.edit.noneLeftToday': 'No free slots left today',
  'booking.edit.outOfService': 'Out of service',
  'booking.edit.courtFree': 'Free',
  'booking.edit.courtBusy': 'Taken',
  'booking.edit.betterBadge': 'Court {court} is better',
  'booking.edit.betterBadgeAdmin': '⚠ Court {court} is better',
  'booking.edit.gapBadge': 'Creates a gap',
  'booking.edit.gapBadgeAdmin': '⚠ Creates a gap',
  'booking.edit.outOfServiceNote': 'This court is marked out of service',
  'booking.edit.outOfServiceNoteReason': 'This court is marked out of service — {reason}',
  'booking.edit.sponsoredBy': 'Sponsored by {sponsor}',
  'booking.edit.smartLockedAdmin':
    'Creates a small gap. Court {court} is smarter — but you can choose this one.',
  'booking.edit.smartLocked': 'Free, but leaves a small gap here. Court {court} fits more snugly.',
  'booking.edit.gapMinutes': '{min} min',
  'booking.edit.gapSmallHole': 'a small gap',
  'booking.edit.deadGapAdmin': 'Leaves {gap} — you can still choose this one.',
  'booking.edit.deadGap': 'Free now, but this booking would leave {gap} nobody else can use.',
  'booking.edit.noCourtsFree': 'No courts are free at this time. Choose another time.',
  'booking.edit.summary': 'The booking runs from {start} to {end} ({duration} min).',
  'booking.edit.price': 'Price',
  'booking.edit.free': 'Free',
  'booking.edit.priceNotSet': 'Price not set',
  'booking.edit.priceNotSetHint':
    'The club has no price zones yet. The booking is saved without payment.',
  'booking.edit.priceKr': '{n} kr',
  'booking.edit.priceRow': '{from}–{to} · {zone}: {kr} kr',
  'booking.edit.titleAdmin': 'Title (admin)',
  'booking.edit.titlePlaceholder': 'E.g. Maintenance, A-squad training, Blocked',
  'booking.edit.titleHelp': 'Shown on the booking so it is clear an admin set it.',
  'booking.edit.bookedBy': 'Booked by (name)',
  'booking.edit.bookedByPlaceholder': 'E.g. Anders Olsen',
  'booking.edit.phone': 'Phone',
  'booking.edit.email': 'Email',
  'booking.edit.optional': '(optional)',
  'booking.edit.loggedInBooker': 'The booking is registered to the user you are signed in as.',
  'booking.edit.notes': 'Notes',
  'booking.edit.notesCourtHint':
    'Notes are shown on the screen at the court. Do not put personal information here.',
  'booking.edit.match.register': 'Register a match on this booking',
  'booking.edit.match.registerHelp':
    'A single match (4 players). Choose whether you enter the players yourself or run open signup through Activities.',
  'booking.edit.match.modeLocal': 'Enter the players yourself',
  'booking.edit.match.modeLocalSub': 'You pick the four now',
  'booking.edit.match.modeActivity': 'Through activities',
  'booking.edit.match.modeActivitySub': 'Open signup + payment',
  'booking.edit.match.rules':
    'Match rules — the default for live scoring on Banepad (changeable there mid-match).',
  'booking.edit.match.format': 'Format',
  'booking.edit.match.format.super': 'Super tie-break',
  'booking.edit.match.format.superSub': 'Best of 3 — deciding super tie-break to 10',
  'booking.edit.match.format.full': '3 full sets',
  'booking.edit.match.format.fullSub': 'Best of 3 — a full deciding set to 6',
  'booking.edit.match.deuce': 'Scoring at 40–40',
  'booking.edit.match.pickFour':
    'Choose four different players to register the match. The booking is saved either way.',
  'booking.edit.match.pricePerPlayer': 'Price per player:',
  'booking.edit.match.priceNote': '(court price divided by 4)',
  'booking.edit.match.deregisterHours': 'Withdrawal deadline (hours)',
  'booking.edit.match.signupCloseHours': 'Signup closes (hours before)',
  'booking.edit.match.levelReq': 'Level requirement — who may sign up',
  'booking.edit.match.levelFrom': 'From level',
  'booking.edit.match.levelTo': 'To level',
  'booking.edit.match.levelAll': 'All',
  'booking.edit.match.level': 'Level {n}',
  'booking.edit.match.levelHelp': 'Empty = every level may sign up.',
  'booking.edit.match.payment': 'Payment',
  'booking.edit.match.paySplit': 'Each player pays their own',
  'booking.edit.match.paySplitSub':
    'Each of the four pays {n} kr under Activities. You are not charged the full court price now.',
  'booking.edit.match.payAll': 'I pay for everyone',
  'booking.edit.match.payAllSub':
    'You pay the full court price now. The others settle {n} kr with you.',
  'booking.edit.match.autoCancel':
    'The activity is cancelled automatically if fewer than four have signed up by the deadline. Your court is kept.',
  'booking.edit.match.tooLate':
    'The signup deadline has already passed for this booking ({hours} h before the start). Choose a later time, a shorter deadline, or enter the players yourself.',
  'booking.edit.paymentStatus': 'Payment status',
  'booking.edit.status.free': 'Free',
  'booking.edit.status.pending': 'Pending',
  'booking.edit.status.paid': 'Paid',
  'booking.edit.markPaid': '✓ Mark as paid ({n} kr)',
  'booking.paymentLink.title': 'Payment link',
  'booking.paymentLink.help':
    'Choose the member the booking is for. They get an e-mail with a link and pay by card in the app. The booking stays until it is paid.',
  'booking.paymentLink.member': 'Member',
  'booking.paymentLink.choose': 'Choose a member …',
  'booking.paymentLink.send': 'Send payment link',
  'booking.paymentLink.sending': 'Sending …',
  'booking.paymentLink.loadingMembers': 'Loading members …',
  'booking.paymentLink.membersUnavailable': 'Could not load the member list. Try again later.',
  'booking.paymentLink.noMembers': 'The club has no members to send the link to.',
  'booking.paymentLink.sent': 'Payment link sent to {name} ({amount} kr).',
  'booking.paymentLink.linkedNoMail':
    'The booking is linked to {name} and can be paid in the app ({amount} kr), but the e-mail was not sent. Let the member know.',
  'booking.paymentLink.error.payments_unavailable': 'Card payment is not set up here yet.',
  'booking.paymentLink.error.not_on_roster': 'This person is not a member of the club.',
  'booking.paymentLink.error.player_has_no_account':
    'The member has no account and cannot sign in to pay.',
  'booking.paymentLink.error.booking_not_found':
    'The booking was not found. It may have been deleted.',
  'booking.paymentLink.error.already_paid': 'The booking is already paid.',
  'booking.paymentLink.error.nothing_to_pay': 'There is nothing to pay for this booking.',
  'booking.paymentLink.error.booking_not_linkable':
    'This booking was made by another organization and cannot be sent as a payment link.',
  'booking.paymentLink.error.booking_linked_to_another_player':
    'The booking is already linked to another member.',
  'booking.paymentLink.error.member_payment_window':
    'The member booked this themselves and is paying for it. Wait until their payment window has passed.',
  'booking.paymentLink.error.organization_not_connected':
    'The club has not connected a payment account yet (Club settings → Payment).',
  'booking.paymentLink.error.unpriceable': 'The price for this time cannot be worked out.',
  'booking.paymentLink.error.price_mismatch':
    'The booking’s price is not what the member would be charged by the price zones (a series discount, say). The link was not sent.',
  'booking.paymentLink.error.equipment_not_payable':
    'The booking has priced equipment the link cannot collect. The link was not sent.',
  'booking.paymentLink.error.awaiting_verification':
    'A payment for this booking is waiting to be confirmed. Try again in a few minutes.',
  'booking.paymentLink.error.reservation_not_payable': 'The booking can no longer be paid.',
  'booking.paymentLink.error.state_moved':
    'The booking changed while the link was being sent. Close it, open it again and retry.',
  'booking.paymentLink.error.forbidden': 'You do not have access to change this booking.',
  'booking.paymentLink.error.unknown': 'Could not send the payment link. Try again.',
  // «Withdraw payment link» (#1310): the booking goes back to the club.
  'booking.paymentLink.withdraw': 'Withdraw payment link',
  'booking.paymentLink.withdrawing': 'Withdrawing …',
  'booking.paymentLink.withdrawn':
    'The payment link is withdrawn. The booking is the club’s again.',
  'booking.paymentLink.withdrawnCancelled':
    'The payment link is withdrawn and the open payment cancelled. The booking is the club’s again.',
  'booking.paymentLink.error.not_linked': 'This booking has no payment link to withdraw.',
  'booking.paymentLink.error.payment_unconfirmed':
    'Stripe did not confirm the payment was cancelled – the member may have paid. Nothing was changed. Check the payment in a few minutes.',
  'booking.paymentLink.error.payment_open':
    'The member opened the payment again just now. Try again.',
  'booking.paymentLink.error.withdrawUnknown': 'Could not withdraw the payment link. Try again.',
  'booking.dispute.title': 'The payment is disputed: {state}',
  'booking.dispute.amount': 'Disputed amount {amount}, opened {date}.',
  'booking.dispute.dueBy': 'Respond by {date}. The owner responds under Club settings → Payment.',
  'booking.dispute.withdrawn': '{amount} has been withdrawn from the balance, plus a {fee} fee.',
  'booking.dispute.reinstated': '{amount} has been returned to the balance.',
  'booking.dispute.paymentUnchanged':
    'The payment status above is not changed by the dispute. What a lost dispute means for the settlement is not decided yet.',
  'booking.dispute.state.inquiryNeedsResponse': 'inquiry awaiting a response',
  'booking.dispute.state.inquiryUnderReview': 'inquiry answered, under review',
  'booking.dispute.state.inquiryClosed': 'inquiry closed',
  'booking.dispute.state.needsResponse': 'awaiting a response',
  'booking.dispute.state.underReview': 'answered, under review',
  'booking.dispute.state.won': 'won',
  'booking.dispute.state.lost': 'lost',
  'booking.dispute.state.prevented': 'prevented',
  'booking.dispute.state.unrecognised': 'unknown status',
  'booking.edit.recur.toggle': '🔁 Recurring booking',
  'booking.edit.recur.toggleSeries': '🔁 Recurring booking — change the pattern',
  'booking.edit.recur.rebuildWarning':
    'If you change the pattern, the whole series is rebuilt from the start date. Earlier changes to individual occurrences are lost.',
  'booking.edit.recur.repeats': 'Repeats',
  'booking.edit.recur.daily': 'Daily',
  'booking.edit.recur.weekly': 'Weekly',
  'booking.edit.recur.monthly': 'Monthly',
  'booking.edit.recur.yearly': 'Yearly',
  'booking.edit.recur.every': 'Every',
  'booking.edit.recur.unitDay': 'day(s)',
  'booking.edit.recur.unitWeek': 'week(s)',
  'booking.edit.recur.unitMonth': 'month(s)',
  'booking.edit.recur.unitYear': 'year(s)',
  'booking.edit.recur.onDays': 'On days',
  'booking.edit.recur.day.mon': 'Mon',
  'booking.edit.recur.day.tue': 'Tue',
  'booking.edit.recur.day.wed': 'Wed',
  'booking.edit.recur.day.thu': 'Thu',
  'booking.edit.recur.day.fri': 'Fri',
  'booking.edit.recur.day.sat': 'Sat',
  'booking.edit.recur.day.sun': 'Sun',
  'booking.edit.recur.daysHelp': 'Empty = the same weekday as the start date.',
  'booking.edit.recur.ends': 'Ends',
  'booking.edit.recur.endCount': 'After a count',
  'booking.edit.recur.endDate': 'On a date',
  'booking.edit.recur.endNever': 'Never',
  'booking.edit.recur.occurrences': 'occurrences',
  'booking.edit.recur.neverCap': 'Capped at 366 occurrences as a safeguard.',
  'booking.edit.recur.holidayWhen': 'When a date falls on a public holiday',
  'booking.edit.recur.holidayInclude': 'Include it anyway',
  'booking.edit.recur.holidaySkip': 'Skip it',
  'booking.edit.recur.holidaySet': 'Holiday set: {country} (changed under settings).',
  'booking.edit.recur.collisions': 'Colliding bookings are skipped. You get a summary afterwards.',
  'booking.edit.cancelBooking': 'Cancel booking',
  'booking.edit.save': 'Save',
  'booking.edit.create': 'Create',
  'booking.edit.checkAndCreate': 'Check and create',
  'booking.edit.createActivity': 'Create activity',
  'booking.edit.toPayment': 'To payment ({n} kr)',
  'booking.edit.invalid.duration': 'Invalid duration (the end time must be after the start).',
  'booking.edit.invalid.identity': 'Fill in a name or a title.',
  'booking.edit.invalid.courtTaken': 'The chosen court and time are not free.',
  'booking.edit.invalid.signupPassed': 'The signup deadline has already passed for this booking.',
  'booking.edit.invalid.missing': 'Required information is missing.',
  // ── booking: the booking screen itself ──────────────────────────
  'booking.view.weekday.sun': 'Sun',
  'booking.view.weekday.mon': 'Mon',
  'booking.view.weekday.tue': 'Tue',
  'booking.view.weekday.wed': 'Wed',
  'booking.view.weekday.thu': 'Thu',
  'booking.view.weekday.fri': 'Fri',
  'booking.view.weekday.sat': 'Sat',
  'booking.view.courtName': 'Court {n}',
  'booking.view.courtStateError': 'Could not change court state',
  'booking.view.alert.demoFull':
    'No more bookings fit — the calendar is full, or the buffer rule is in the way.',
  'booking.view.backToProgram': 'Back to the match programme',
  'booking.view.home': 'Home',
  'booking.view.serie.label': 'Reserving a court for a league match',
  'booking.view.serie.discount': '{pct}% off the court price',
  'booking.view.serie.window':
    'Bookable up to {days} days ahead. Choose a free slot below — the discounted price is shown at payment.',
  'booking.view.eyebrow': 'Court booking',
  'booking.view.title': 'Reserve a court',
  'booking.view.lead': 'Choose a free slot, or create a new booking.',
  'booking.view.adminTitle':
    'Admin mode: book freely and create recurring bookings without following the rules.',
  'booking.view.adminOn': '🔓 Admin on',
  'booking.view.closedFrom': 'Closed from {time}',
  'booking.view.overrideHours': '⚠ Outside the opening hours ({open}–{close}).',
  'booking.view.overrideSmart': '⚠ Goes against smart booking — another court fits better.',
  'booking.view.overrideLead': 'Admin mode lets you book it anyway:',
  'booking.view.adminOff': '🔒 Admin off',
  'booking.view.newBooking': '+ New booking',
  'booking.view.zones': '💰 Price zones ({n})',
  'booking.view.hoursNotSet.title': 'Booking is not open yet',
  'booking.view.hoursNotSet.body':
    'Save the opening hours in the booking settings. Until then no booking can be made — not by members, and not here.',
  'booking.view.hoursNotSet.action': 'Set opening hours',
  'booking.view.settings': '⚙️ Settings',
  'booking.view.help': '❓ Help',
  'booking.view.demoTitle': 'Create demo bookings for the selected date',
  'booking.view.clearAllTitle': 'Clear the whole calendar (deletes EVERY booking)',
  'booking.view.jumpToDate': 'Jump to a specific date',
  'booking.calendar.prevMonth': 'Previous month',
  'booking.calendar.nextMonth': 'Next month',
  'booking.view.custom': 'Custom',
  'booking.view.past': 'past',
  'booking.view.dayTitleLocked': '{date} — bookable from {days} days ahead',
  'booking.view.dayTitleFree': '{date} — early {early}, midday {mid}, evening {evening} free',
  'booking.view.today': 'Today',
  'booking.view.dotsTitle': 'Early · midday · evening',
  'booking.view.viewSlots': '🎯 Free slots',
  'booking.view.viewMatrix': '📋 Calendar view',
  'booking.view.courtHeader': 'Court',
  'booking.view.outOfService': 'Out of service',
  'booking.view.reopenTitle': 'Put {court} back in service',
  'booking.view.closeTitle': 'Mark {court} out of service',
  'booking.view.open': 'Open',
  'booking.view.close': 'Close',
  'booking.view.courtIsOut': '{court} is out of service',
  'booking.view.reasonPlaceholder': 'What is wrong? (optional)',
  'booking.view.noteStaffOnly': 'The note is shown to staff, not to customers.',
  'booking.view.updateNote': 'Update the note',
  'booking.view.markOut': 'Mark out of service',
  'booking.view.setInService': 'Put in service',
  'booking.view.hoverSuggest': 'Not here — {court} fits ({dur}m)',
  'booking.view.hoverIndivisible': 'Not here — it creates an unusable gap',
  'booking.view.hoverNone': '{time} · no duration available',
  'booking.view.anonymous': 'Anonymous',
  'booking.view.singleMatch': 'Single match',
  'booking.view.passedSuffix': ' (past)',
  'booking.view.singleMatchBadge': '⚡ Single match',
  'booking.view.slotsTitle': 'Free slots',
  'booking.view.slotsLead': 'Tap a time to book it.',
  'booking.view.minutes': '{n} min',
  'booking.view.groupTitle': 'Group the times into Morning / Late morning / Afternoon / Evening',
  'booking.view.grouping': 'Grouping',
  'booking.view.pickDuration': 'Choose at least one duration above to see the free slots.',
  'booking.view.noSlots': 'No free slots for {dur} on this day.',
  'booking.view.daypart.morning': 'Morning',
  'booking.view.daypart.morningRange': 'before 12:00',
  'booking.view.daypart.lateMorning': 'Late morning',
  'booking.view.daypart.lateMorningRange': '12:00 – 15:00',
  'booking.view.daypart.afternoon': 'Afternoon',
  'booking.view.daypart.afternoonRange': '15:00 – 18:00',
  'booking.view.daypart.evening': 'Evening',
  'booking.view.daypart.eveningRange': '18:00 and later',
  'booking.view.availability': 'Availability:',
  'booking.view.legendGood': 'Good',
  'booking.view.legendHalf': 'Half full',
  'booking.view.legendFilling': 'Filling up',
  'booking.view.legendAlmostFull': 'Nearly full',
  'booking.view.slotTitle': '{time} · {courts}{durations} · {price}',
  'booking.view.slotCourtsOne': '{n} of {total} court free',
  'booking.view.slotCourtsMany': '{n} of {total} courts free',
  'booking.view.slotDurations': ' · {list} min',
  'booking.view.priceKr': '{n} kr',
  'booking.view.slotPriceDiscount': '{n} kr (-{pct}%)',
  'booking.view.freeLower': 'free',
  'booking.view.free': 'Free',
  'booking.view.priceNotSet': 'Price not set',
  'booking.view.allCourts': 'Every court',
  'booking.view.lastCourt': 'Last court!',
  'booking.view.someFree': '{n} of {total} free',
  'booking.view.footerCalendar':
    'Click a free cell to reserve a court at that time. Click a booking to change or delete it.',
  'booking.view.footerSlots':
    'Click a time to reserve — the first free court is chosen automatically.',
  // ── booking: equipment beside the courts (docs/domains/ASSET_BOOKING.md) ──
  'booking.view.assets': 'Equipment',
  'booking.view.assetHeader': 'Equipment',
  'booking.view.assetRowInactive': 'Unavailable',
  'booking.view.newAssetBooking': 'Book equipment',
  'booking.view.assetLegend': 'Equipment',
  'booking.view.alert.assetFailed': 'The court was booked, but the equipment could not be: {error}',
  'booking.view.alert.assetSaveFailed': 'Could not save the equipment booking: {error}',
  'booking.view.assetTitle': '{asset} · {start}–{end} · {who}',
  'booking.view.assetLaneBook': 'Book {asset} — click a free time',
  'booking.view.slotAssetsFree': '{n} equipment free',
  'booking.view.slotAssetsNone': 'equipment taken',
  'booking.view.filter.label': 'Show',
  'booking.view.filter.all': 'Everything',
  'booking.view.filter.courts': 'Courts only',
  'booking.view.filter.assets': 'Equipment only',
  'booking.view.filter.kindAll': 'All kinds',
  'booking.assets.title': 'Equipment',
  'booking.assets.lead':
    'Ball machines and other equipment that can be booked beside the courts. Equipment belongs to this centre — a club may own equipment kept here, and then changes it from its own side.',
  'booking.assets.empty': 'No equipment registered yet.',
  'booking.assets.add': 'New equipment',
  'booking.assets.name': 'Name',
  'booking.assets.kind': 'Category',
  'booking.assets.kind.ball_machine': 'Ball machine',
  'booking.assets.kind.racket': 'Loan racket',
  'booking.assets.kind.camera': 'Camera',
  'booking.assets.kind.other': 'Other',
  'booking.assets.description': 'Description',
  'booking.assets.active': 'Bookable',
  'booking.assets.inactive': 'Unavailable',
  'booking.assets.buffer': 'Smallest gap between two bookings',
  'booking.assets.bufferNone': 'No rule',
  'booking.assets.bufferMin': '{n} min',
  'booking.assets.pricing': 'Price',
  'booking.assets.defaultPrice': 'Default price per hour (kr)',
  'booking.assets.zones': '{n} price zone(s)',
  'booking.assets.editZones': 'Edit price zones',
  'booking.assets.noZones': 'No price zones — the default price applies all day.',
  'booking.assets.free': 'Free',
  'booking.assets.pricePerHour': '{n} kr/h',
  'booking.assets.ownedBy': 'Owned by {name}',
  'booking.assets.atVenue': 'Kept at {name}',
  'booking.assets.save': 'Save',
  'booking.assets.saving': 'Saving …',
  'booking.assets.delete': 'Delete',
  'booking.assets.deleteConfirm': 'Delete {name}?',
  'booking.assets.edit': 'Edit',
  'booking.assets.back': 'Back to the list',
  'booking.assets.notOwner': 'Owned by {name} and changed there.',
  'booking.assets.error.asset_has_bookings':
    'This equipment has bookings and cannot be deleted. Mark it unavailable instead.',
  'booking.assets.error.forbidden_pricing': 'You may not change equipment prices.',
  'booking.assets.error.generic': 'Could not save: {error}',
  'booking.assetBooking.title.create': 'Book equipment',
  'booking.assetBooking.title.edit': 'Edit equipment booking',
  'booking.assetBooking.asset': 'Equipment',
  'booking.assetBooking.date': 'Date',
  'booking.assetBooking.start': 'Start time',
  'booking.assetBooking.duration': 'Duration',
  'booking.assetBooking.durationMin': '{n} min',
  'booking.assetBooking.bookedBy': 'Booked by',
  'booking.assetBooking.notes': 'Note',
  'booking.assetBooking.attach': 'Attach to a court booking',
  'booking.assetBooking.attachNone': 'None — the equipment is booked on its own',
  'booking.assetBooking.attachOption': '{court} · {start}–{end} · {who}',
  'booking.assetBooking.attachHint':
    'The equipment must sit inside the court booking’s time, and is deleted with it.',
  'booking.assetBooking.price': 'Price',
  'booking.assetBooking.priceKr': '{n} kr',
  'booking.assetBooking.free': 'Free',
  'booking.assetBooking.paymentStatus': 'Payment',
  'booking.assetBooking.status.free': 'Free',
  'booking.assetBooking.status.pending': 'Unpaid',
  'booking.assetBooking.status.paid': 'Paid',
  'booking.assetBooking.save': 'Save',
  'booking.assetBooking.saving': 'Saving …',
  'booking.assetBooking.delete': 'Delete booking',
  'booking.assetBooking.unavailable': 'taken',
  'booking.assetBooking.noAssets':
    'No equipment is registered. Add equipment under “Equipment” in admin mode.',
  'booking.assetBooking.anonymous': 'no name',
  'booking.assetBooking.error.asset_taken': 'The equipment is already booked at this time.',
  'booking.assetBooking.error.dead_gap':
    'The booking would leave too short a gap before or after another booking of this equipment.',
  'booking.assetBooking.error.outside_opening_hours':
    'Outside the centre’s opening hours ({open}–{close}).',
  'booking.assetBooking.error.booking_hours_not_set':
    'Booking is not open: save the opening hours in the booking settings first.',
  'booking.assetBooking.error.off_grid': 'Start and end must be on the full or half hour.',
  'booking.assetBooking.error.end_before_start': 'The end must be after the start.',
  'booking.assetBooking.error.missing_fields': 'Choose equipment, a date, a start and a duration.',
  'booking.assetBooking.error.asset_inactive': 'This equipment is not available for booking.',
  'booking.assetBooking.error.asset_not_found': 'This equipment no longer exists.',
  'booking.assetBooking.error.asset_outside_booking':
    'The equipment must be booked inside the court booking’s time.',
  'booking.assetBooking.error.booking_not_found': 'The court booking no longer exists.',
  'booking.assetBooking.error.payment_status_not_operator_controlled':
    'Paid is set only by a verified payment.',
  'booking.assetBooking.error.payment_status_contradicts_price':
    'The payment status does not match the price.',
  'booking.assetBooking.error.asset_conflict':
    'The court booking cannot be moved: {asset} is taken at the new time.',
  'booking.assetBooking.error.generic': 'The equipment booking was refused.',
  'booking.edit.assets': 'Equipment for this booking',
  'booking.edit.assetsHint': 'Choose equipment to go with the court booking. Its price is added.',
  'booking.edit.assetTaken': 'taken',
  'booking.edit.assetPrice': '+ {n} kr',
  'booking.edit.assetFree': 'free',
  'booking.edit.assetsAttached': 'Equipment on this booking',
  'booking.edit.assetRemove': 'Remove',
  'booking.view.overrideTitle': 'Court {court} is out of service',
  'booking.view.overrideBody':
    'You can book this court anyway. Only do so if you know the court is usable — the customer is told nothing about it being marked out of service.',
  'booking.view.overrideConfirm': 'Book anyway',
  'booking.view.conflictBooked': 'Booked',
  'booking.view.conflictBusy': 'Taken',
  'booking.view.alert.collision': 'Could not create it: it collides with an existing booking.',
  'booking.view.alert.createFailed': 'Could not create it: {error}',
  'booking.view.clearAll.title': 'Clear the whole calendar?',
  'booking.view.clearAll.before': 'This deletes',
  'booking.view.clearAll.strong': 'all {count} bookings',
  'booking.view.clearAll.after':
    'in the whole system — not just the demo ones, and not just the selected day. The action cannot be undone.',
  'booking.view.clearAll.confirm': 'Yes, delete everything',
  'booking.view.serieMatch.title': 'League-match reservation',
  'booking.view.serieMatch.sub': 'A court reserved for a league match',
  'booking.view.serieMatch.goProgram': 'Go to the match programme',
  'booking.view.serieMatch.openMatch': 'Open the match in the league',
  'booking.view.serieMatch.edit': 'Change or cancel the booking',
  'booking.view.serieMatch.editSub': 'Applies to this reservation only ({date})',
  'booking.view.series.title': 'Recurring booking',
  'booking.view.series.lead':
    'This booking is part of a series of {count} bookings. What do you want to change?',
  'booking.view.series.goTournament': 'Go to the tournament',
  'booking.view.series.openTournament': 'Open the tournament this reservation belongs to',
  'booking.view.series.onlyThis': 'This occurrence only',
  'booking.view.series.onlyThisSub': 'Change or cancel {date} only',
  'booking.view.series.whole': 'The whole series ({count})',
  'booking.view.series.wholeSub': 'Changes and cancellations apply to every booking in the series',
  'booking.view.alert.seriesRebuilt': 'Series updated: {created} bookings{failed}.',
  'booking.view.alert.seriesFailedPart': ', {n} failed (collision)',
  'booking.view.alert.seriesUpdated': 'Updated {created} of {total} in the series.{failed}',
  'booking.view.alert.seriesFailedSentence': ' {n} failed (collision).',
  'booking.view.alert.seriesUpdateFailed': 'Could not update the series.',
  'booking.view.alert.updateFailed': 'Could not update it: {error}',
  'booking.view.alert.seriesCancelled': 'Cancelled {n} bookings in the series.',
  'booking.view.alert.seriesCancelFailed': 'Could not cancel the series.',
  'booking.view.alert.deleteFailed': 'Could not delete it: {error}',
  // Deleting a paid booking is refused (`booking_has_payment`, #284): point to
  // «Cancel and refund», which is its door.
  'booking.view.alert.hasPayment':
    'The booking is paid and cannot be deleted. Open it and use «Cancel and refund», so the customer gets the money back.',
  'booking.view.alert.hasPaymentNoRight':
    'The booking is paid and cannot be deleted. It has to be cancelled with «Cancel and refund», which needs access to refunds – ask someone who has it.',
  'booking.view.alert.createdOne': 'Created {n} booking.',
  'booking.view.alert.createdMany': 'Created {n} bookings.',
  'booking.view.alert.cancelledExisting': ' {n} existing ones were cancelled (overridden).',
  'booking.view.alert.failedCount': ' {n} failed.',
  'booking.view.alert.saveFailed': 'Could not save it: {error}',
  'booking.view.alert.paidButNotSaved':
    'Payment went through, but the booking could not be saved: {error}',
  'booking.view.confirm.title': 'Confirm the bookings',
  'booking.view.confirm.counts': '{total} bookings in total · {free} free · {conflicts} colliding',
  'booking.view.confirm.conflictsAfter': 'of {total} collide with existing bookings:',
  'booking.view.confirm.noConflicts': 'No collisions — all {n} bookings are free.',
  'booking.view.confirm.skip': 'Skip {skip} — book {book}',
  'booking.view.confirm.override': 'Override — cancel {n} and book them all',
  'booking.view.confirm.createAll': 'Create {n} bookings',
  'booking.view.paymentSummary': 'Court {court}, {date}, {start}–{end}',
  // ═══ end of the booking surface block ═══

  'errors.not_configured': 'This feature is not set up in this environment. Contact StarPoint.',
  'errors.throttled': 'Too many attempts. Wait a moment and try again.',
  'errors.unsupported_auth_method': 'This account does not support that sign-in method.',
  'errors.invalid_credentials': 'Sign-in refused. Check the e-mail address and the code.',
  'errors.account_disabled': 'This account is disabled. Please contact your club.',
  'errors.account_closed': 'This account is closed. Contact StarPoint if you want to know more.',
  'errors.rate_limited': 'Too many attempts. Wait a moment and try again.',
  'errors.invalid_token': 'That link is invalid or has expired. Ask for a new one.',
  'errors.network': 'Could not reach the server. Please try again.',
  'errors.unknown': 'Something went wrong. Please try again.',
  'errors.too_many_rows': 'The list has too many people at a time. Split it up.',
  'errors.payload_too_large': 'The list is too large to send in one piece. Split it up.',
  'errors.body_too_large':
    'What you are saving is too large to send. Contact us if it happens again.',
  'errors.rows_required': 'Found nobody to add.',
  'errors.name_too_short': 'Type the full name, at least three characters.',
  'errors.reason_required': 'Write a reason, at least three characters.',
  'errors.reason_too_long': 'The reason is too long.',
  'errors.name_required': 'Enter a name.',
  'errors.invalid_email': 'That e-mail address does not look right.',
  'errors.invalid_gender': 'Choose man, woman or not given.',
  'errors.field_too_long': 'One of the fields is too long.',
  'errors.player_self_managed':
    'The player has their own account and changes this themself. Contact StarPoint support if it needs correcting.',
  'errors.email_required': 'Enter an e-mail address.',
  'errors.member_invite_retired':
    'Invitations by code are retired. Reload the page and invite by e-mail.',
  'errors.not_found': 'Could not find what you were looking for.',
  // ── Card payment for a court booking (#284) ──────────────────────
  'booking.pay.title': 'Pay by card',
  'booking.pay.total': 'To pay',
  'booking.pay.amount': '{amount} {currency}',
  'booking.pay.opening': 'Preparing payment …',
  'booking.pay.payNow': 'Pay now',
  'booking.pay.orCard': 'or pay by card',
  'booking.pay.confirming': 'Paying …',
  'booking.pay.cancel': 'Cancel',
  'booking.pay.close': 'Close',
  'booking.pay.restart': 'Cancel and start the payment again',
  'booking.pay.declined': 'The payment was declined. Try another card.',
  'booking.pay.submitted': 'Payment received and being confirmed. The court is yours.',
  'booking.pay.submittedShort': 'Confirming payment …',
  'booking.pay.paid': 'Paid',
  'booking.pay.stillConfirming':
    'Payment received. Confirmation is taking longer than usual — the court is yours, and the payment will show here as soon as it is confirmed.',
  'booking.pay.heldNote': 'The court is held for you while you pay.',
  'booking.pay.heldFor': 'Held for {time}',
  'booking.pay.heldNoteTimed': 'The court is held for you for {time} while you pay.',
  'booking.pay.holdLost': 'The reservation expired – the court has been released',
  'booking.pay.return.checking': 'Checking the payment …',
  'booking.pay.return.paid': 'The payment is registered. The court is yours.',
  'booking.pay.return.confirming': 'Payment received and being confirmed …',
  'booking.pay.return.failed':
    'The payment was not completed. The court is still held for you — try again under Reservations.',
  'booking.pay.return.dismiss': 'Close',
  'booking.pay.notConfigured': 'Card payment is not set up here yet.',
  'booking.pay.unreachable':
    'Could not reach the payment provider. The court is still reserved — try again.',
  // ── The server's refusal codes, as sentences (#310a) ─────────────
  'errors.already_paid': 'This court time has already been paid for.',
  'errors.nothing_to_pay': 'This court time is free of charge.',
  'errors.unpriceable': 'The club has not set a price for this time. Please contact the club.',
  'errors.organization_not_connected':
    'The club cannot accept payment yet. Please contact the club.',
  'errors.not_your_booking': 'This court time is not yours.',
  'errors.booking_not_identity_linked':
    'This court time cannot be paid for in the app. Please contact the club.',
  'errors.reservation_expired': 'The reservation has expired. Please book the time again.',
  'errors.reservation_not_payable': 'This court time cannot be paid for right now.',
  'errors.awaiting_verification':
    'An earlier payment has not been settled yet. Wait a moment and try again.',
  'errors.attempt_terms_changed':
    'The price changed after you started paying. Cancel the old payment and start again.',
  'errors.payment_refused': 'The payment was declined. Try another card.',
  'errors.provider_unknown':
    'We do not know whether the payment went through. Wait a moment before trying again — the court is still reserved.',
  'errors.asset_paid_with_booking': 'The equipment is paid together with the court booking.',

  // ── players (#310a) ───────────────────────────────────────────────
  'players.common.cancel': 'Cancel',
  'players.common.save': 'Save',
  'players.common.name': 'Name',
  'players.common.gender': 'Gender',
  'players.common.startElo': 'Starting ELO',
  'players.common.namePlaceholder': 'First name Last name',
  'players.common.searchName': 'Search name…',
  'players.common.searchLabel': 'Search for a player',
  'players.common.clearSearch': 'Clear the search',
  'players.common.male': 'Man',
  'players.common.female': 'Woman',
  'players.common.genderShort.male': 'M',
  'players.common.genderShort.female': 'W',
  'players.common.level': 'Level {level}',
  'players.common.levelTitle': 'Level {level} — {name}',
  'players.common.lastPlayed': 'played {days}d ago',
  'players.common.win': 'Win',
  'players.common.loss': 'Loss',
  'players.common.noAvailablePlayers': 'No available players',
  'players.common.dupe.before': 'There is already a player called',
  'players.common.dupe.ghost': ' (ghost)',
  'players.common.dupe.after': '. To avoid a duplicate, use the existing one.',
  'players.common.useExisting': 'Use the existing player',
  'players.common.boostTitle':
    'New-player boost {boost} — decays linearly over matches, reaching 1x once the player has played {count} matches.',
  'players.common.statusNew': 'NEW PLAYER',
  'players.common.statusComeback': 'BACK',
  'players.common.matchesLeft.one': '{count} match left',
  'players.common.matchesLeft.other': '{count} matches left',
  'players.common.nearlyEstablished': 'Nearly established',
  'players.common.comebackTitle':
    'After more than {days} days of inactivity, {name} gets a raised ELO effect that decays as they play more matches.',

  'players.swap.title': 'Swap out player',
  'players.swap.toNewPlayer': '→ new player',
  'players.swap.availableHint': 'Available players (top 10 by ELO)',
  'players.swap.noMatches': 'No matches for "{query}"',

  'players.replacementBadge.unknownTime': 'unknown time',
  'players.replacementBadge.buttonTitle': 'Substitute — click for details',
  'players.replacementBadge.kicker': 'Substitute',
  'players.replacementBadge.title': 'Player swapped mid-tournament',
  'players.replacementBadge.swappedOut': 'Swapped out',
  'players.replacementBadge.replacedBy': 'Replaced by',
  'players.replacementBadge.time': 'Time',
  'players.replacementBadge.phase': 'Tournament phase',
  'players.replacementBadge.finals': 'Finals',
  'players.replacementBadge.phaseN': 'Phase {phase}',
  'players.replacementBadge.roundSuffix': ', Round {round}',
  'players.replacementBadge.matchesPlayedLabel': 'Matches played when the swap happened',
  'players.replacementBadge.matchesProgress': '{done} of {total} matches done',
  'players.replacementBadge.firstMatchFor': 'First match for {name}',
  'players.replacementBadge.theSubstitute': 'the substitute',
  'players.replacementBadge.matchPrefix': 'Match {number} · ',
  'players.replacementBadge.final': 'Final',
  'players.replacementBadge.roundShort': ' · R{round}',
  'players.replacementBadge.court': 'Court {court}',
  'players.replacementBadge.missingInfo': 'Missing information about the swap',
  'players.replacementBadge.close': 'Close',

  'players.newPlayer.title': 'New player',
  'players.newPlayer.lockedGenderNote': '(required by the class)',
  'players.newPlayer.emailLabel': 'Email (optional)',
  'players.newPlayer.emailPlaceholder': 'optional — for signing in later',
  'players.newPlayer.emailSet': 'The player can sign in with this email.',
  'players.newPlayer.emailEmpty':
    'Without an email the player is created as a «ghost». You can add an email and a sign-in later.',
  'players.newPlayer.startEloHint':
    'Level: {level} · Adjusted automatically after the first matches',
  'players.newPlayer.submit': 'Create and add',

  'players.matchRow.baseChangeTitle': 'Base change (without the new-player boost)',
  'players.matchRow.round.semi': 'Semi',
  'players.matchRow.round.quarter': 'Quarter',
  'players.matchRow.round.r16': 'R16',
  'players.matchRow.round.r32': 'R32',
  'players.matchRow.final': 'Final',
  'players.matchRow.finalN': 'Final {number}',
  'players.matchRow.league': 'League',
  'players.matchRow.group': 'Grp',
  'players.matchRow.knockout': 'KO',
  'players.matchRow.eloBefore': 'ELO before the match',
  'players.matchRow.eloCurrent': 'Current ELO',
  'players.matchRow.teamAvgTitle': "The team's average ELO before the match{strongest}",
  'players.matchRow.strongestTeam': ' (strongest team)',
  'players.matchRow.eloChangeTitle': 'ELO change after the match',
  'players.matchRow.notPlayed': 'Not played',
  'players.matchRow.draw': 'Draw',

  'players.allMatches.genderMen': 'Men',
  'players.allMatches.genderWomen': 'Women',
  'players.allMatches.genderMix': 'Mixed',
  'players.allMatches.tournamentFallback': 'Tournament',
  'players.allMatches.singleMatch': 'Single match',
  'players.allMatches.adjustment': 'Adjustment',
  'players.allMatches.empty': 'No matches played yet',
  'players.allMatches.format': 'Format',
  'players.allMatches.all': 'All',
  'players.allMatches.count': '{shown} of {total} matches — newest first',
  'players.allMatches.noMatchesForFilter': 'No matches fit the filter',
  'players.allMatches.manualAdjustment': 'Manual ELO adjustment',

  'players.edit.fillIn': 'Fill in player',
  'players.edit.slotNumber': 'Slot #{number}',
  'players.edit.override': "Override the player's details",
  'players.edit.tabSearch': 'Search the database',
  'players.edit.tabNew': 'Create new',
  'players.edit.searchHint': "Type the name to search the club's player database",
  'players.edit.noMatches': 'No matches for "{query}".',
  'players.edit.createAs': 'Create "{query}" as new',
  'players.edit.newLevelHint': 'Level: {level}. Adjusted automatically after the first matches.',
  'players.edit.levelShort': 'level {level}',
  'players.edit.ratingFollowsResults':
    '💡 The rating follows from recorded results and the engine’s arithmetic. It is shared across the whole platform, so a manual correction is made by Starpoint.',
  'players.edit.createAndFill': 'Create and fill in',

  'players.playerEdit.createTitle': 'Create new player',
  'players.playerEdit.editTitle': 'Edit player',
  'players.playerEdit.confirmDelete.before': 'Are you sure you want to delete',
  'players.playerEdit.confirmDelete.after':
    'from the database? This cannot be undone. The player will still appear in previously archived tournaments.',
  'players.playerEdit.deleteConfirm': 'Yes, delete the player',
  'players.playerEdit.firstName': 'First name',
  'players.playerEdit.lastName': 'Last name',
  'players.playerEdit.firstNamePlaceholder': 'Anna',
  'players.playerEdit.lastNamePlaceholder': 'Berg',
  'players.playerEdit.email': 'Email address',
  'players.playerEdit.emailHint': '— for signing in eventually',
  'players.playerEdit.emailPlaceholder': 'anna@example.com',
  'players.playerEdit.emailInvalid': 'Invalid email address',
  'players.playerEdit.emailSet': 'The player can link a sign-in to this email later.',
  'players.playerEdit.emailEmpty':
    'Without an email a «ghost» is created — the player exists in the system, but nobody can sign in until the actual person registers as the owner.',
  'players.playerEdit.maleOption': 'Man ♂',
  'players.playerEdit.femaleOption': 'Woman ♀',
  'players.playerEdit.genderLocked': "Gender is locked by the class's registration rule.",
  'players.playerEdit.eloRating': 'ELO rating',
  'players.playerEdit.ratingFollowsResults':
    'The rating changes with recorded results. It is shared across the whole platform, so a manual correction is made by Starpoint.',
  'players.playerEdit.levelBadge': 'LEVEL {level}',
  'players.playerEdit.selfManaged':
    'The player has their own account and changes their name, e-mail, phone and gender themself. Contact StarPoint support if it needs correcting.',
  'players.playerEdit.missingFirstName': 'First name missing',
  'players.playerEdit.missingLastName': 'Last name missing',
  'players.playerEdit.emailInvalidOrEmpty': 'The email is invalid (or leave it empty)',
  'players.playerEdit.eloRange': 'ELO must be between 100 and 4000',
  'players.playerEdit.cannotSave': 'Cannot save',
  'players.playerEdit.delete': 'Delete',
  'players.playerEdit.create': 'Create',

  'players.detail.notFound': 'Player not found',
  'players.detail.eloStart': 'Start',
  'players.detail.gamesDiff': 'Difference: {diff}',
  'players.detail.group': 'Group {letter}',
  'players.detail.profile': 'Player profile',
  'players.detail.swappedOutTitle': 'Swapped out for {name}',
  'players.detail.swappedOutBadge': '⊘ SWAPPED OUT',
  'players.detail.replacementTitle': 'Replacing {name}',
  'players.detail.replacementBadge': '↻ SUBSTITUTE',
  'players.detail.eloChangeInTournament': '({change} in this tournament)',
  'players.detail.startEloTitle': 'Started the tournament on {elo}',
  'players.detail.newPlayerTitle':
    'As a new player, {name} gets a raised ELO effect that decays linearly from {boost}x (match 1) to 1x once they have played {count} matches.',
  'players.detail.replacedOutHeading': 'The player has been swapped out',
  'players.detail.replacedOutBefore': 'Replaced by',
  'players.detail.replacedOutAfter':
    ". Only matches BEFORE the swap count towards {name}'s ELO. Results from matches after the swap go to the substitute.",
  'players.detail.replacementHeading': 'Substitute for {name}',
  'players.detail.replacementBody':
    "Brought in as a replacement mid-way. Only matches FROM the swap onwards count towards {name}'s ELO.",
  'players.detail.placeholderHeading': 'Reserved slot',
  'players.detail.placeholderBody':
    'This slot has not been filled with a named player yet. Click the button below to fill it in — every upcoming match in the slot transfers to the new player.',
  'players.detail.fillNameTitle': 'Fill in the name for this reserved slot',
  'players.detail.replaceTitle': 'Swap this player out for a substitute',
  'players.detail.fillName': 'Fill in name',
  'players.detail.replacePlayer': 'Swap the player out',
  'players.detail.fullHistoryTitle':
    "See the player's full history across every tournament (player-database profile)",
  'players.detail.fullHistory': 'Full history',
  'players.detail.statsWinLossDraw': 'Wins-losses-draws',
  'players.detail.statsWinLoss': 'Wins-losses',
  'players.detail.statsMatchesPlayed': 'Matches played',
  'players.detail.statsHomeAway': 'Home–Away',
  'players.detail.eloDevelopment': 'ELO development this tournament',
  'players.detail.afterEachMatch': 'After each of {count} completed matches',
  'players.detail.upcoming': 'Upcoming matches ({count})',
  'players.detail.completed': 'Completed ({count})',
  'players.detail.newestFirst': 'Latest first',
  'players.detail.noMatches': 'No matches found for this player yet',

  'players.history.back': 'Back',
  'players.history.kicker': 'Player profile · Full history',
  'players.history.kickerFemale': ' · Woman',
  'players.history.kickerMale': ' · Man',
  'players.history.newBadge': 'NEW',
  'players.history.eloAtYearEndTitle': 'ELO at the end of {year}',
  'players.history.eloYear': 'ELO {year}:',
  'players.history.current': 'Current:',
  'players.history.changeYearTitle': 'Change in {year}',
  'players.history.changeYear': 'Change {year}:',
  'players.history.newPlayerTitle':
    'As a new player, {name} gets a raised ELO effect that decays linearly from {boost}x (match 1) to 1x once they have played {count} matches. In americano (50% ELO weight) the boost decays over {doubled} matches.',
  'players.history.close': 'Close',
  'players.history.tournaments': 'Tournaments',
  'players.history.totalMatches': 'Matches in total',
  'players.history.wins': 'Wins',
  'players.history.losses': 'Losses',
  'players.history.eloDevelopmentYear': 'ELO development in {year}',
  'players.history.tabMatches': 'All matches ({count})',
  'players.history.tabTournaments': 'Tournaments ({count})',
  'players.history.tabMerits': 'Merits ({count})',
  'players.history.tabMeritsTitle': 'Placements and medals from tournaments',
  'players.history.noTournaments': '{name} has not taken part in any tournaments yet',
  'players.history.unknownDate': 'Unknown date',
  'players.history.placement': 'Place {position}',
  'players.history.tournamentFallback': 'Tournament',
  'players.history.matchSummary': '{matches} matches · {wins} wins · {losses} losses',
  'players.history.drawsSuffix': ' · {draws} drawn',
  'players.history.eloInTournament': 'ELO in the tournament',
  'players.history.goToTournament': 'Go to the tournament',
  'players.history.showMatches': 'Matches in {name}',
  'players.history.noMatches': 'No matches',

  'players.merits.empty':
    'No merits yet — good placements in finished tournaments (and league seasons) show up here.',
  'players.merits.trophies': 'Trophies',
  'players.merits.tournamentFallback': 'Tournament',
  'players.merits.placementDate': 'Place {position} · {date}',

  'players.view.home': 'Home',
  'players.view.title': 'Player database',
  'players.view.lead':
    'Every player registered in the club. The ELO ranking updates after each finished tournament.',
  'players.view.create': 'Create player',
  'players.view.totalPlayers': 'Players in total',
  'players.view.active': 'Active',
  'players.view.activeHint': 'have played at least one tournament',
  'players.view.avgElo': 'Average ELO',
  'players.view.tournaments': 'Tournaments',
  'players.view.tournamentsHint': 'in the history',
  'players.view.tabsLabel': 'Players and members',
  'players.view.tabPlayers': 'All players',
  'players.view.tabMembers': 'Members',
  'players.view.searchPlaceholder': 'Search for a player...',
  'players.view.filterAll': 'All',
  'players.view.filterWomen': 'Women',
  'players.view.filterMen': 'Men',
  'players.view.levelFilterTitle': 'Filter by player level',
  'players.view.allLevels': 'All levels ({count})',
  'players.view.levelOption': 'Level {level} — {name} ({count})',
  'players.view.activeOnlyTitle': 'Show only players who have played a match in the last 30 days',
  'players.view.activeOnly': 'Active only',
  'players.view.colPlayer': 'Player',
  'players.view.colLevel': 'Level',
  'players.view.colGender': 'Gender',
  'players.view.colEloYear': 'ELO {year}',
  'players.view.colEloYearTitle': 'ELO at the end of {year}',
  'players.view.colNow': 'Now',
  'players.view.trendTitle': 'Trend over the last 30 days',
  'players.view.colTrend': 'Trend',
  'players.view.colTournaments': 'Tournaments',
  'players.view.rankTitle': 'Position in the ELO ranking',
  'players.view.customPlayer': 'Custom player',
  'players.view.newBadge': 'NEW',
  'players.view.levelPillTitle': 'Level {level}: {name} — {short}',
  'players.view.genderWoman': 'Woman',
  'players.view.genderMan': 'Man',
  'players.view.noTrend': 'No matches in the last 30 days',
  'players.view.trendUp': '+{change} ELO over the last 30 days',
  'players.view.trendDown': '{change} ELO over the last 30 days',
  'players.view.trendFlat': '{change} ELO over the last 30 days — stable',
  'players.view.noTournamentsCell': 'none',
  'players.view.invitePlayer': 'Invite to the player app',
  'players.invite.title': 'Invite {name}',
  'players.invite.explainConsent':
    'We send an invitation to the e-mail address. The player becomes a member only when they accept it from their own e-mail.',
  'players.invite.emailLabel': 'E-mail address',
  'players.invite.emailRequired': "Enter the player's e-mail address.",
  'players.invite.send': 'Send invitation',
  'players.invite.cancel': 'Cancel',
  'players.invite.working': 'Sending …',
  'players.invite.sent': 'The invitation was sent to {email}. The link works until {date}.',
  'players.invite.notYetMember':
    'The player is not a member of {club} yet. They become one when they accept the invitation.',
  'players.invite.claimNote':
    "When the player accepts, their account is linked to the player in the club's list.",
  'players.invite.notSent':
    'The invitation could not be sent. An earlier invitation still works. Try again.',
  'players.invite.close': 'Close',
  'players.invite.unreachable': 'Could not reach the server. Try again.',
  'players.lookup.title': 'Find a player from another club',
  'players.lookup.explain':
    "The list shows the club's members and the players in the club's own events. Type the full name to find another player. You see the name and ELO, not contact details. The player stays in the list once they are in one of the club's events.",
  'players.lookup.nameLabel': 'Full name',
  'players.lookup.search': 'Search',
  'players.lookup.working': 'Searching …',
  'players.lookup.none': 'No player has exactly that name.',
  'players.lookup.tooShort': 'Type the full name, at least three characters.',
  'players.lookup.unreachable': 'Could not reach the server. Try again.',
  'players.lookup.elo': 'ELO rating: {elo}',
  'players.lookup.adopt': 'Add to the list',
  'players.lookup.alreadyListed': 'Already in the list',
  'account.myProfile.title': 'My profile',
  'account.myProfile.loading': 'Loading your profile …',
  'account.myProfile.name': 'Name',
  'account.myProfile.email': 'E-mail on the profile',
  'account.myProfile.emailNote':
    'This is the address your clubs see. It does not change the e-mail address you sign in with.',
  'account.myProfile.phone': 'Phone',
  'account.myProfile.gender': 'Gender',
  'account.myProfile.genderUnset': 'Not given',
  'account.myProfile.genderMale': 'Man',
  'account.myProfile.genderFemale': 'Woman',
  'account.myProfile.save': 'Save profile',
  'account.myProfile.saving': 'Saving …',
  'account.myProfile.saved': 'Your profile is saved.',
  'account.myProfile.nameRequired': 'Enter your name.',
  'account.myProfile.unreachable': 'Could not reach the server. Try again.',
  'account.close.title': 'Close the account',
  'account.close.lead':
    'Once the account is closed, you cannot sign in again. All your sign-ins end and your passkeys are removed.',
  'account.close.kept':
    'Your results, rating, bookings and memberships are not deleted. Asking for your personal data to be erased is a separate request.',
  'account.close.start': 'Close the account …',
  'account.close.confirmLead':
    'Are you sure? You are signed out at once, and the account cannot be used again.',
  'account.close.confirm': 'Yes, close the account',
  'account.close.cancel': 'Cancel',
  'account.close.working': 'Closing …',
  'account.close.done': 'The account is closed. You are signed out.',
  'account.close.continue': 'Done',
  'account.close.reauth':
    'You need to confirm it is you before the account can be closed. Confirm, then press the button again.',
  'account.close.lastRoleManager':
    'The account cannot be closed because it is the last one that can grant StarPoint roles.',
  'account.close.failed': 'The account was not closed. Try again.',
  'account.close.unreachable': 'Could not reach the server. The account was not closed.',
  // ── «DOWNLOAD MY DATA» — THE PERSON'S OWN EXPORT (#1062) ───────────
  'account.export.title': 'Download my data',
  'account.export.lead':
    'Download a file with what StarPoint holds about you: the account, how you sign in, your player profile, rating adjustments, single matches, bug reports and the log of what was done with the account.',
  'account.export.notIncluded':
    'The clubs’ own records about you – bookings, payments, memberships – are not in it. The file names the clubs that hold them; ask them directly.',
  'account.export.start': 'Download my data',
  'account.export.working': 'Fetching …',
  'account.export.done': 'The file is downloaded. It holds personal data – keep it safe.',
  'account.export.reauth':
    'You need to confirm it is you before the data can be downloaded. Confirm, then press the button again.',
  'account.export.unreachable': 'Could not reach the server. Try again.',
  'account.export.failed': 'The data could not be downloaded. Try again.',
  // ── «DELETE MY ACCOUNT AND MY DATA» — ERASURE (#1062) ──────────────
  'account.erase.title': 'Delete my account and my data',
  'account.erase.lead':
    'The account is closed, and what StarPoint holds about you is deleted. This cannot be undone.',
  'account.erase.start': 'Delete my account and my data …',
  'account.erase.dialogTitle': 'Delete your account and your data?',
  'account.erase.deletedHeading': 'Deleted',
  'account.erase.deleted1':
    'Your account, your e-mail address and every way to sign in, passkeys included.',
  'account.erase.deleted2':
    'The name and contact details on your player profile. The profile is unlinked from you for good.',
  'account.erase.deleted3': 'Your name in bug reports you sent.',
  'account.erase.keptHeading': 'Kept',
  'account.erase.kept1':
    'Results and the rating history, without a name – other players’ ratings were computed from them.',
  'account.erase.kept2':
    'The clubs’ bookkeeping records and their own records, such as bookings and payments. They are the club’s and are not deleted from here – ask the club about them.',
  'account.erase.kept3': 'A record that the account was deleted, with counts and no name.',
  'account.erase.newAccount':
    'If you sign in again later with the same e-mail, you get a new, empty account that is not linked to the old history.',
  'account.erase.exportFirst': 'Want a copy first? Download your data before you delete.',
  'account.erase.typeLabel': 'Type {word} to confirm',
  'account.erase.word': 'DELETE',
  'account.erase.confirm': 'Delete for good',
  'account.erase.cancel': 'Cancel',
  'account.erase.working': 'Deleting …',
  'account.erase.done': 'Your account and your data are deleted. You are signed out.',
  'account.erase.continue': 'Done',
  'account.erase.reauth':
    'You need to confirm it is you before the account can be deleted. Confirm, then press «Delete for good» again.',
  'account.erase.lastRoleManager':
    'The account cannot be deleted because it is the last one able to grant StarPoint roles.',
  'account.erase.unreachable':
    'Could not reach the server. We do not know whether the account was deleted – reload the page.',
  'account.erase.failed': 'The account was not deleted. Nothing changed – try again.',
  // A player whose person erased their data (#1062): the name is empty in the
  // database, and the screen shows this neutral label instead.
  'players.erased': 'Deleted player',
  'players.view.editPlayer': 'Edit player',
  'players.view.noSearchMatches': 'No players match the search',

  'players.replace.fillSlotKicker': 'Fill in reserved slot',
  'players.replace.swapKicker': 'Swap out player',
  'players.replace.fillTitle': 'Choose a player for {name}',
  'players.replace.swapTitle': 'Choose a substitute for {name}',
  'players.replace.bandHint': 'the suggestions match this level',
  'players.replace.fillLead':
    'Every upcoming match in this slot transfers to the new player. No replacement history is stored — it is as if the player had always held the slot.',
  'players.replace.swapLead':
    "The substitute takes over the player's slot in every remaining match. ELO changes from finished matches stay with the original player; only new matches affect the substitute.",
  'players.replace.searchPlaceholder': 'Search name...',
  'players.replace.standbyHeading': 'Standby reserves',
  'players.replace.standbyFirst': 'suggested first',
  'players.replace.standbyExplainBefore':
    'Suggestions are ranked by how close the reserve is in ELO to the player being replaced: first within ±100 (if several are, the one who signed up',
  'players.replace.standbyExplainStrong': 'first',
  'players.replace.standbyExplainAfter':
    ' goes), then ±200, then ±300 and so on. A reserve far down the list can therefore end up at the top if they are closest in level — and be called in even though many signed up before them.',
  'players.replace.order': 'no. {order}',
  'players.replace.fromOriginal': '{diff} from the original',
  'players.replace.notToday': ' · not available today',
  'players.replace.canToday': 'available today',
  'players.replace.otherDay': 'another day',
  'players.replace.otherPlayers': 'Other players',
  'players.replace.noSearchMatches': 'No players match the search',
  'players.replace.confirmFill': 'Fill the slot',
  'players.replace.confirmSwap': 'Confirm the swap',

  // ── the member app (#310) — the nb block carries the key layout ───
  'mobile.common.player': 'Player',
  'mobile.common.openSlot': 'open',
  'mobile.common.close': 'Close',
  'mobile.common.back': 'Back',
  'mobile.common.cancel': 'Cancel',
  'mobile.common.save': 'Save',
  'mobile.common.saving': 'Saving …',
  'mobile.common.noMatches': 'No results',
  'mobile.common.centres': 'Padel centres',
  'mobile.common.matches': 'Matches',
  'mobile.common.activity': 'Activity',
  'mobile.common.tournament': 'Tournament',
  'mobile.common.waitlist': 'Waiting list',
  'mobile.common.signedUp': 'Signed up',
  'mobile.common.round': 'Round {round}',

  'mobile.tab.home': 'Home',
  'mobile.tab.play': 'Play',
  'mobile.tab.reservations': 'Reservations',
  'mobile.tab.matches': 'Matches',
  'mobile.tab.messages': 'Messages',
  'mobile.tab.profile': 'Profile',
  'mobile.tab.navLabel': 'Main menu',
  'mobile.tab.unreadAria': '{label} ({count} unread)',

  'mobile.app.pickUser': 'Choose user',
  'mobile.app.profileAria': 'Profile · {name} ({initials})',
  'mobile.phoneFrame.close': 'Close preview',
  'mobile.emoji.pick': 'Choose emoji',
  'mobile.emoji.insert': 'Insert {emoji}',
  'mobile.emoji.group.padel': 'Padel',
  'mobile.emoji.group.smileys': 'Smileys',
  'mobile.emoji.group.handsHearts': 'Hands & hearts',

  'mobile.memberPicker.title': 'Who are you?',
  'mobile.memberPicker.switch': 'Switch user',
  'mobile.memberPicker.hint': 'Choose the player you want to use the app as.',
  'mobile.memberPicker.hintFirst':
    'Choose the player you want to use the app as. Sign-ups and bookings are made as this player.',
  'mobile.memberPicker.hintSwitch': 'Everything in the app is done as the chosen player.',
  'mobile.memberPicker.search': 'Search by name …',
  'mobile.memberPicker.searchLabel': 'Search by name',

  'mobile.home.greetMorning': 'Good morning',
  'mobile.home.greetDay': 'Hi',
  'mobile.home.greetEvening': 'Good evening',
  'mobile.home.eloCardAria': 'Your ELO and trend',
  'mobile.home.yourLevel': 'Your level',
  'mobile.home.topLevel': 'Top level at the club',
  'mobile.home.eloAndTrend': 'ELO & trend ›',
  'mobile.home.inProgress': 'In progress',
  'mobile.home.startingSoon': 'Starting soon',
  'mobile.home.matchLeft': '{count} match left for you',
  'mobile.home.matchesLeft': '{count} matches left for you',
  'mobile.home.allPlayed': 'You have played all your matches',
  'mobile.home.nextForYou': 'Next for you',
  'mobile.home.nextMatch': 'Next match · {name}',
  'mobile.home.emptyTitle': 'Nothing planned yet',
  'mobile.home.emptyHint': 'Book a court or sign up for an activity below.',
  'mobile.home.bookCourt': 'Book a court',
  'mobile.home.bookCourtSub': 'Available times today',
  'mobile.home.signUp': 'Sign up',
  'mobile.home.signUpSub': 'Americano, courses …',
  'mobile.home.seeAll': 'All',
  'mobile.home.onWaitlist': 'You are on the waiting list',
  'mobile.home.youAreSignedUp': 'You are signed up',

  'mobile.reservations.title': 'Reservations',
  'mobile.reservations.upcoming': 'Upcoming',
  'mobile.reservations.past': 'Past',
  'mobile.reservations.emptyUpcoming': 'No reservations yet',
  'mobile.reservations.emptyPast': 'Nothing in the past',
  'mobile.reservations.emptyUpcomingHint': 'Book a court or sign up under Play.',
  'mobile.reservations.emptyPastHint': 'Bookings and sign-ups you have had end up here.',
  'mobile.reservations.singleMatch': 'Single match',
  'mobile.reservations.courtBooking': 'Court booking',
  'mobile.reservations.unpaid': 'Not paid',
  'mobile.reservations.paid': 'Paid',
  'mobile.reservations.cancel.open': 'Cancel',
  'mobile.reservations.cancel.title': 'Cancel the booking?',
  'mobile.reservations.cancel.refund': 'You get {amount} kr back.',
  'mobile.reservations.cancel.free': 'The booking is not paid, so nothing is charged.',
  'mobile.reservations.cancel.until': 'You can cancel until {when}.',
  'mobile.reservations.cancel.confirm': 'Yes, cancel',
  'mobile.reservations.cancel.keep': 'Keep the booking',
  'mobile.reservations.cancel.working': 'Cancelling …',
  'mobile.reservations.cancel.done': 'Cancelled',
  'mobile.reservations.cancel.doneRefund': 'Cancelled – {amount} kr is on its way back to you.',
  'mobile.reservations.cancel.tooLate': 'Too late to cancel – contact the club.',
  'mobile.reservations.cancel.unconfirmed':
    'The refund is not confirmed yet. The court is still yours until the club has checked the payment.',
  'mobile.reservations.cancel.failed':
    'We could not refund the payment, so the booking is kept. Try again or contact the club.',
  'mobile.reservations.cancel.error':
    'Something went wrong. The booking is not cancelled – try again.',
  'mobile.reservations.cancel.unavailable':
    'Cancelling is not available right now – contact the club.',
  'mobile.reservations.openSlot': '{count} open slot',
  'mobile.reservations.openSlots': '{count} open slots',
  'mobile.reservations.equipmentBooking': 'Equipment',
  'mobile.reservations.withEquipment': 'With {names}',

  'mobile.history.allClubs': 'All clubs',
  'mobile.history.noClubLead': 'No club chosen. Here is what you have done in every club.',
  'mobile.history.chooseClub': 'Choose a club',
  'mobile.history.lead': 'Your own bookings and sign-ups, at every club you have played at.',
  'mobile.history.matchesLead': 'Your single matches, at every club you have played at.',
  'mobile.history.loading': 'Loading your history …',
  'mobile.history.failed': 'Could not load your history.',
  'mobile.history.retry': 'Try again',
  'mobile.history.empty': 'Nothing yet',
  'mobile.history.emptyHint': 'Bookings and sign-ups you make at a club end up here.',
  'mobile.history.emptyMatches': 'No single matches yet',
  'mobile.history.emptyMatchesHint':
    'Single matches you play end up here. Tournaments are shown under each club.',
  'mobile.history.asPartner': 'Signed up as a partner',
  'mobile.history.days': '{count} days',
  'mobile.history.you': 'You',
  'mobile.history.youAnd': 'You and {name}',
  'mobile.history.versus': 'vs {names}',

  'mobile.matches.played': 'Played',
  'mobile.matches.emptyUpcoming': 'No upcoming matches',
  'mobile.matches.emptyUpcomingHint':
    'Book a court and register a match, or sign up for a tournament.',
  'mobile.matches.emptyPlayed': 'No matches played yet',
  'mobile.matches.emptyPlayedHint': 'Single matches and tournaments you have played end up here.',
  'mobile.matches.singleMatches': 'Single matches',
  'mobile.matches.addPlayer': 'Add player',
  'mobile.matches.addPlayerHint': 'Choose who takes the slot in the match.',
  'mobile.matches.add': 'Add',
  'mobile.matches.removePlayer': 'Remove {name}',
  'mobile.matches.openTag': '{count} OPEN',
  'mobile.matches.openTagPlural': '{count} OPEN SLOTS',
  'mobile.matches.fillSlotsHint': 'Add team-mates, or leave the slots open under Matches.',
  'mobile.matches.enterResult': 'Enter result',
  'mobile.matches.needOneSet': 'Enter at least one set with a winner.',
  'mobile.matches.saveFailed': 'Could not save the result.',
  'mobile.matches.saveResult': 'Save result',
  'mobile.matches.setNo': 'Set {n}',
  'mobile.matches.setTeamAria': 'Set {n}, team {team}',
  'mobile.matches.setYouAria': 'Set {n}, you',
  'mobile.matches.setThemAria': 'Set {n}, them',
  'mobile.matches.setsYouThem': 'Sets (you – them)',
  'mobile.matches.superTieBreakNote': 'a super tie-break counts as a set',
  'mobile.matches.playedTag': 'PLAYED',
  'mobile.matches.wonTag': 'WIN',
  'mobile.matches.lostTag': 'LOSS',
  'mobile.matches.walkoverTag': 'WO',
  'mobile.matches.notPlayedTag': 'NOT PLAYED',
  'mobile.matches.nextMatch': 'Next match',
  'mobile.matches.ofTotal': 'of {total}',
  'mobile.matches.inProgress': ' · in progress',
  'mobile.matches.notStarted': ' · not started',
  'mobile.matches.leftForYou': ' · {count} left for you',
  'mobile.matches.allPlayedSuffix': ' · all played',
  'mobile.matches.noneScheduled': 'No matches scheduled for you yet',
  'mobile.matches.organiserRecords': 'The organiser records the result.',
  'mobile.matches.you': 'You',
  'mobile.matches.them': 'Them',
  'mobile.matches.pointsYou': 'Points, you',
  'mobile.matches.pointsThem': 'Points, them',

  'mobile.league.title': 'League',
  'mobile.league.ongoing': 'Season in progress',
  'mobile.league.pastSeasons': 'Past seasons',
  'mobile.league.emptyTitle': 'You are not in a league right now',
  'mobile.league.emptyHint': 'Sign up for a season under Play → Activities when sign-up opens.',
  'mobile.league.matchCount': '{count} matches',
  'mobile.league.ofTotal': ' · of {total}',

  'mobile.elo.title': 'Your ELO',
  'mobile.elo.noPlayer': 'No player selected.',
  'mobile.elo.level': 'Level',
  'mobile.elo.topLevelShort': 'Top level',
  'mobile.elo.topLevel': 'You are at the top level at the club',
  'mobile.elo.pointsToNext': '{points} points to {level} · {name}',
  'mobile.elo.trend': 'Trend',
  'mobile.elo.days30': '30 days',
  'mobile.elo.days90': '90 days',
  'mobile.elo.noTrendYet': 'Play a few matches and the curve will appear here.',
  'mobile.elo.recentChanges': 'Recent changes',
  'mobile.elo.emptyTitle': 'No ELO changes yet',
  'mobile.elo.emptyHint': 'Results from matches and tournaments end up here.',
  'mobile.elo.versus': ' · against {names}',
  'mobile.elo.levels': 'The levels',
  'mobile.elo.explainer':
    'ELO changes after every match that counts: you win more against stronger opposition and lose more against weaker. Friendly matches and walkovers do not count.',
  'mobile.elo.chartAria': 'ELO over time',

  'mobile.profile.noPlayers': 'No players in the database yet.',
  'mobile.profile.statElo': 'ELO',
  'mobile.profile.statWins': 'Wins',
  'mobile.profile.statTournaments': 'Tournaments',
  'mobile.profile.levelLine': 'Level {level} · {name}',
  'mobile.profile.last5': '· last 5:',
  'mobile.profile.pickFavourite': 'Choose a favourite centre',
  'mobile.profile.centresHint': 'Search, filter and pick a favourite',
  'mobile.profile.levelTraits': 'What this level looks like',
  'mobile.profile.notifications': 'Notifications',
  'mobile.profile.signedInAs': 'Signed in as',
  'mobile.profile.switchProfile': 'Switch profile',
  'mobile.profile.signOut': 'Sign out',
  'mobile.identity.failedTitle': 'We could not load your profile',
  'mobile.identity.failedBody':
    'Nothing is wrong with your account — the server simply did not answer.',
  'mobile.identity.retry': 'Try again',
  'mobile.identity.noneTitle': 'You do not have a player profile yet',
  'mobile.identity.noneBody': 'Choose the name you want to play under, and you are ready.',
  'mobile.identity.noneMemberBody': 'Contact your club to get started.',
  'mobile.identity.nameLabel': 'Name',
  'mobile.identity.create': 'Create player profile',
  'mobile.identity.creating': 'Creating …',
  'mobile.identity.nameRequired': 'Enter your name.',
  'mobile.identity.createFailed': 'That did not work. Try again.',
  'mobile.club.loading': 'Loading the club …',
  'mobile.club.failedTitle': 'We could not load the club',
  'mobile.club.failedBody':
    'Your bookings and matches are still there — the server simply did not answer.',

  // ── the active club context — the 📍 selector (owner, 2026-10-16) ──
  'mobile.clubContext.title': 'Club',
  'mobile.clubContext.lead': 'Choose which club the app shows courts, activities and matches from.',
  'mobile.clubContext.choose': 'Choose club',
  'mobile.clubContext.chooseOther': 'Choose another club',
  'mobile.clubContext.selectorAria': 'Club: {name}. Change club',
  'mobile.clubContext.none': 'No club',
  'mobile.clubContext.noneHint': 'Only what is your own',
  'mobile.clubContext.yours': 'Your clubs',
  'mobile.clubContext.loading': 'Loading your clubs …',
  'mobile.clubContext.empty': 'You are not in any club yet.',
  'mobile.clubContext.failed': 'We could not load your clubs.',
  'mobile.clubContext.findClub': 'Find a club',
  'mobile.clubContext.findClubHint': 'Search clubs and centres, and ask to join',
  'mobile.clubContext.adminTitle': 'Manage organization',
  'mobile.clubContext.adminEnter': 'Open',
  'mobile.admin.title': 'Administration',
  'mobile.admin.lead': 'Organizations you can manage',
  'mobile.clubContext.neededTitle': 'Choose a club',
  'mobile.clubContext.neededLead':
    'Courts, activities and matches belong to a club. Choose which one to see.',
  'mobile.clubContext.homeNone': 'No club selected — bookings and activities are shown per club.',
  'mobile.profile.demoNote': 'Demo — real sign-in arrives once user accounts are in place.',

  'mobile.push.granted':
    'Notifications are on. You will hear about matches, bookings and sign-ups.',
  'mobile.push.denied':
    'Notifications are blocked. Turn them on under Settings → Starpoint → Notifications.',
  'mobile.push.unavailable': 'Not available on this device (simulator).',
  'mobile.push.undetermined':
    'Hear about it when something happens: matches, bookings and sign-ups.',
  'mobile.push.unsupported': 'Notifications require the Starpoint app.',
  'mobile.push.checking': 'Checking …',
  'mobile.push.enable': 'Turn on notifications',

  'mobile.centres.title': 'Padel centres',
  'mobile.centres.lead':
    'Choose a favourite centre and which centres you want to see activities from. More centres appear as they come on board.',
  'mobile.centres.search': 'Search for a centre …',
  'mobile.centres.searchLabel': 'Search for a centre',
  'mobile.centres.all': 'All centres',
  'mobile.centres.favouriteOnly': 'Favourite only',
  'mobile.centres.open': 'Open {name}',
  'mobile.centres.yourClub': 'Your club · ',
  'mobile.centres.openActivity': '{count} open activity',
  'mobile.centres.openActivities': '{count} open activities',
  'mobile.centres.hiddenSuffix': ' · hidden',
  'mobile.centres.favourite': 'Set as favourite',
  'mobile.centres.unfavourite': 'Remove as favourite',
  'mobile.centres.noMatches': 'No centres match the search',

  'mobile.centre.tabAbout': 'About',
  'mobile.centre.tabBook': 'Book',
  'mobile.centre.tabActivities': 'Activities',
  'mobile.centre.tabMatches': 'Matches',
  'mobile.centre.switch': 'Switch centre',
  'mobile.centre.openingHours': 'Opening hours',
  'mobile.centre.today': 'Today',
  'mobile.centre.contact': 'Contact',
  'mobile.centre.courts': 'Courts',
  'mobile.centre.noCourts': 'No courts registered',
  'mobile.centre.courtKind': 'Padel · doubles · indoor',
  'mobile.centre.openActivityHere': '{count} open activity here now',
  'mobile.centre.openActivitiesHere': '{count} open activities here now',
  'mobile.centre.bookingComingTitle': 'Booking at this centre is on the way',
  'mobile.centre.bookingComingHint': 'For now, courts can only be booked at your own club.',
  'mobile.centre.matchesLead':
    'Single matches that members have posted on a booking here. You can sign up for open slots — post your own by booking a court and ticking «register a match».',
  'mobile.centre.noMatchesTitle': 'No matches posted',
  'mobile.centre.noMatchesHint': 'Book a court under «Book» and tick «register a match».',
  'mobile.centre.levelGate': 'Level {min}–{max} ·',
  'mobile.centre.friendlyMatch': 'Friendly match · does not count towards ELO',
  'mobile.centre.countsTowardsElo': 'Counts towards ELO',
  'mobile.centre.countsTowardsEloPct': 'Counts towards ELO ({pct}%)',
  'mobile.centre.outsideLevel': 'Outside the level requirement (you are N{level}).',
  'mobile.centre.joinMatch': 'Sign me up',
  'mobile.centre.leaveMatch': 'Sign me off',
  'mobile.venue.loading': 'Loading …',
  'mobile.venue.failed': 'Could not load the details. Try again.',
  'mobile.venue.retry': 'Try again',
  'mobile.venue.notFound': 'This place could not be found.',
  'mobile.venue.everyDay': 'Every day',
  'mobile.venue.noHours': 'Not entered yet',
  'mobile.venue.courtsOne': '1 padel court',
  'mobile.venue.courtsMany': '{count} padel courts',
  'mobile.venue.courtNumber': 'Court {number}',
  'mobile.venue.courtDouble': 'Padel · doubles',
  'mobile.venue.courtSingle': 'Padel · singles',
  'mobile.venue.booking': 'Court booking',
  'mobile.venue.memberHere': 'You are a member here.',
  'mobile.venue.book': 'Book a court',
  'mobile.venue.select': 'Go to the club',
  'mobile.venue.bookingSoonTitle': 'Booking without a membership is coming',
  'mobile.venue.bookingSoonHint': 'For now, only members can book courts here in the app.',
  'mobile.venue.membership': 'Membership',
  'mobile.venue.freeTimes': 'Free times',
  'mobile.venue.freeTimesFor': 'Free start times, {minutes} min',
  'mobile.venue.chooseDay': 'Choose a day',
  'mobile.venue.today': 'Today',
  'mobile.venue.noFreeTimes': 'No free times this day.',
  'mobile.venue.availabilityFailed': 'Could not load free times. Try again.',
  'mobile.venue.freeCourts': 'Free: {courts}',

  'mobile.activities.title': 'Activities',
  'mobile.activities.saveFailed': 'Something went wrong. Try again.',
  'mobile.activities.paymentNotReady':
    'Payment is not ready yet. Contact the club to sign up for this activity.',
  'mobile.activities.pickCentre': 'Choose padel centre',
  'mobile.activities.myLevelFilter': 'Suits my level (N{level})',
  'mobile.activities.hiddenCount': '{count} hidden',
  'mobile.activities.outsideLevelCount': '{count} outside my level',
  'mobile.activities.empty': 'No open activities',
  'mobile.activities.emptyHint': 'Come back later — new ones are posted regularly.',
  'mobile.activities.emptyAtCentre': 'No open activities at this centre',
  'mobile.activities.emptyAtCentreHint': 'Choose «All centres» to see the rest.',
  'mobile.activities.openForSignup': 'Open for sign-up',
  'mobile.activities.levelRange': 'Level {min}–{max}',
  'mobile.activities.allLevels': 'All levels',
  'mobile.activities.free': 'Free',
  'mobile.activities.perPlayer': 'per player',
  'mobile.activities.onWaitlistCount': ' · {count} on the waiting list',
  'mobile.activities.onWaitlist': 'On the waiting list',
  'mobile.activities.signOff': 'Sign off',
  'mobile.activities.partnerSignup': 'Sign-up with a partner or class — use the activity page.',
  'mobile.activities.signingUp': 'Signing up …',
  'mobile.activities.joinWaitlist': 'Put me on the waiting list',
  'mobile.activities.signUpPriced': 'Sign up · {price} kr',

  'mobile.book.anyLength': 'All',
  'mobile.book.freeTimes': 'Available times · {day}',
  'mobile.book.noTimes': 'No available times',
  'mobile.book.noTimesHint': 'Try another day or a shorter duration.',
  'mobile.book.notOpen': 'Booking is not open yet',
  'mobile.book.notOpenHint':
    'The club has not entered its opening hours. Once they are saved, you can book here.',
  'mobile.book.priceNotSet': 'Price not set',
  'mobile.book.priceNotSetHint':
    'The club has not set its prices yet. Once they are set, you can book the court here.',
  'mobile.book.courtFree': '{count} court available',
  'mobile.book.courtsFree': '{count} courts available',
  'mobile.book.pickCourtOpen': 'Choose an available court below for {time} — then pick how long.',
  'mobile.book.pickCourtFixed': 'Choose an available court below for {from}–{to}.',
  'mobile.book.pickTimeFirst':
    'Choose a time and the court list shows what is available. A yellow edge = the last available court.',
  'mobile.book.freeCourtsAt': 'Available courts · {time}',
  'mobile.book.freeCourtsBetween': 'Available courts · {from}–{to}',
  'mobile.book.noCourts': 'No available courts',
  'mobile.book.noCourtsHint': 'Try another time or duration.',
  'mobile.book.chosen': 'Chosen',
  'mobile.book.pickLength': 'Pick a duration',
  'mobile.book.lengthCount': '{count} duration',
  'mobile.book.lengthCountPlural': '{count} durations',
  'mobile.book.freeBookIt': 'Available · book',
  'mobile.book.taken': 'Taken',
  'mobile.book.timeCount': '{count} time',
  'mobile.book.timeCountPlural': '{count} times',
  'mobile.book.confirmedAria': 'Booking confirmed',
  'mobile.book.expand': 'Expand the booking',
  'mobile.book.collapse': 'Minimise the booking',
  'mobile.book.courtIsYours': 'The court is yours!',
  'mobile.book.courtHeldUnpaid': 'The court is reserved. Pay to complete the booking.',
  'mobile.book.withMatchSuffix': ' · with a match',
  'mobile.book.bookShort': 'Book',
  'mobile.book.bookedFor': '{court} · booked for {name}',
  'mobile.book.you': 'you',
  'mobile.book.registerMatch': 'Register a match on this booking',
  'mobile.book.registerMatchHint':
    'You are added as a player, and the three other slots are posted under Matches so others can sign up.',
  'mobile.book.levelGateHeading':
    'Level requirement for those signing up · recommended N{min}–N{max} (your level ±1)',
  'mobile.book.levelMin': 'Min',
  'mobile.book.levelMax': 'Max',
  'mobile.book.levelMinAria': 'Lowest level',
  'mobile.book.levelMaxAria': 'Highest level',
  'mobile.book.levelRecommended': 'N{min}–N{max} · recommended',
  'mobile.book.useRecommended': 'Use recommended',
  'mobile.book.booking': 'Booking …',
  'mobile.book.payAndBook': 'Pay {price} kr and book',
  'mobile.book.bookIt': 'Book the court',
  'mobile.book.failed': 'Could not book — try another time.',
  'mobile.book.matchPosted': 'The match is posted under Matches — 3 open slots.',
  'mobile.book.matchNotRegistered': 'The booking is fine, but the match was not registered.',
  'mobile.book.fromTime': 'from {time}',
  'mobile.book.tapLength': 'tap the duration you want',
  'mobile.book.minutesUntil': '{minutes} minutes until {end}',
  'mobile.book.minutesUnavailable': '{minutes} minutes not available',
  'mobile.book.freeLower': 'free',
  'mobile.book.equipment.heading': 'Add equipment',
  'mobile.book.equipment.hint':
    'Equipment is booked for the whole court time and paid with the court.',
  'mobile.book.equipment.noneFree': 'No equipment is free at this time.',
  'mobile.book.equipment.added': 'With {names}',
  'mobile.book.equipment.error.taken':
    'The equipment you chose is no longer free. Choose again — the court was not booked.',
  'mobile.book.equipment.error.unavailable':
    'The equipment you chose cannot be booked now. Choose again — the court was not booked.',
  'mobile.book.equipment.error.generic':
    'The equipment could not be booked with the court. The court was not booked.',
  'mobile.book.equipment.alone.modeAria': 'What would you like to book?',
  'mobile.book.equipment.alone.modeCourt': 'Court',
  'mobile.book.equipment.alone.modeEquipment': 'Equipment',
  'mobile.book.equipment.alone.freeTimes': 'Available equipment · {day}',
  'mobile.book.equipment.alone.freeAt': 'Available equipment · {from}–{to}',
  'mobile.book.equipment.alone.none': 'No equipment available',
  'mobile.book.equipment.alone.noneHint': 'Try another day or a shorter duration.',
  'mobile.book.equipment.alone.pick': 'Pick equipment below for {from}–{to}.',
  'mobile.book.equipment.alone.bookedFor': '{asset} · booked for {name}',
  'mobile.book.equipment.alone.book': 'Book the equipment',
  'mobile.book.equipment.alone.sheetAria': 'Book equipment',
  'mobile.book.equipment.alone.confirmedAria': 'Equipment booking confirmed',
  'mobile.book.equipment.alone.yours': 'The equipment is yours!',
  'mobile.book.equipment.alone.heldUnpaid':
    'The equipment is reserved. Pay to complete the booking.',
  'mobile.book.equipment.alone.error.taken': 'The equipment is no longer free. Pick another time.',
  'mobile.book.equipment.alone.error.unavailable': 'The equipment cannot be booked right now.',
  'mobile.book.equipment.alone.error.generic': 'The equipment could not be booked.',

  'mobile.messaging.clubFallback': 'The club',
  'mobile.messaging.courtPad': 'Court {court} · banepad',
  'mobile.messages.unavailableTitle': 'Messages are not available yet',
  'mobile.messages.unavailableLead':
    'We are working on messages for members. Contact your club in the meantime.',
  'mobile.messages.pickWhoFirst': 'Choose who you are first',
  'mobile.messages.pickWhoFirstHint': 'Your messages follow your profile.',
  'mobile.messages.offTitle': 'Messages are turned off for you',
  'mobile.messages.offLead':
    'You neither send nor receive messages, you do not appear when others search, and you have been taken out of the groups. Turn them back on whenever you like.',
  'mobile.messages.turnOn': 'Turn on messages',
  'mobile.messages.newShort': 'New',
  'mobile.messages.search': 'Search conversations',
  'mobile.messages.filterAll': 'All',
  'mobile.messages.filterUnread': 'Unread',
  'mobile.messages.filterGroups': 'Groups',
  'mobile.messages.leftGroups': 'You have left the automatic groups at {clubs}.',
  'mobile.messages.rejoin': 'Join again',
  'mobile.messages.emptySearch': 'No conversations match',
  'mobile.messages.emptySearchHint': 'Try another name.',
  'mobile.messages.emptyUnread': 'Nothing unread',
  'mobile.messages.emptyUnreadHint': 'You are up to date.',
  'mobile.messages.emptyGroups': 'No groups yet',
  'mobile.messages.emptyAll': 'No messages yet',
  'mobile.messages.emptyAllHint':
    'Send a message to a fellow player, create a group, or write to the club.',
  'mobile.messages.muted': 'Muted',
  'mobile.messages.autoGroupPreview': '{n} members · automatic group',
  'mobile.messages.unreadAria': '{count} unread',
  'mobile.messages.turnOffForMe': 'Turn off messages for me',
  'mobile.messages.turnOff': 'Turn off messages',
  'mobile.messages.turnOffConfirmTitle': 'Turn off messages?',
  'mobile.messages.turnOffConfirmLead':
    'You will neither send nor receive messages, you will not appear when others search for members, and you will be taken out of your groups. You can turn them back on from the same place.',

  'mobile.newMessage.title': 'New message',
  'mobile.newMessage.lead': 'Pick one for a conversation, several for a group.',
  'mobile.newMessage.toClub': 'Write to the club',
  'mobile.newMessage.search': 'Search member',
  'mobile.newMessage.noMembers': 'No members match.',
  'mobile.newMessage.groupNameOptional': 'Group name (optional)',
  'mobile.newMessage.groupName': 'Group name',
  'mobile.newMessage.writeTo': 'Write to {name}',
  'mobile.newMessage.theMember': 'the member',
  'mobile.newMessage.startGroup': 'Start a group with {count} members',

  'mobile.chat.yourClub': 'Your club',
  'mobile.chat.memberCount': '{n} member',
  'mobile.chat.memberCountMany': '{n} members',
  'mobile.chat.autoGroupLevel': 'Automatic group · {members} at level {level}',
  'mobile.chat.autoGroupAll': 'Automatic group · all {members}',
  'mobile.chat.groupSubtitle': '{n} members · {names}',
  'mobile.chat.unknown': 'Unknown',
  'mobile.chat.groupSettings': 'Group settings',
  'mobile.chat.autoGroupNote': 'Automatic group — the members are kept up to date by actual level.',
  'mobile.chat.mute': 'Mute the group',
  'mobile.chat.unmute': 'Turn notifications on',
  'mobile.chat.muteHint': 'The group stays, but does not count as unread.',
  'mobile.chat.unmuteHint': 'Unread messages count towards the tab badge again.',
  'mobile.chat.leaveAuto': 'Leave the automatic groups',
  'mobile.chat.leaveAutoHint':
    'Applies to both the level group and «All» at {club}. You can join again from the inbox.',
  'mobile.chat.theClub': 'the club',
  'mobile.chat.emptyClub': 'Messages from the club arrive here. You can write to them too.',
  'mobile.chat.emptyDm': 'No messages yet — say hello!',
  'mobile.chat.toEveryone': ' · to everyone',
  'mobile.chat.blocked':
    '{name} has turned messages off. The conversation is closed until it is turned back on.',
  'mobile.chat.theMember': 'The member',
  'mobile.chat.emoji': 'Emoji',
  'mobile.chat.composerPlaceholder': 'Write a message …',
  'mobile.chat.composerAria': 'Write a message',
  'mobile.chat.send': 'Send',

  // ══ the home surface (#310a) — the nb block carries the key layout ══
  // ── shared across the home screens ────────────────────────────────
  'home.common.cancel': 'Cancel',
  'home.common.delete': 'Delete',
  'home.common.ago.justNow': 'just now',
  'home.common.ago.minutes': '{min} min ago',
  'home.common.ago.hour': '{count} hour ago',
  'home.common.ago.hours': '{count} hours ago',
  'home.common.ago.day': '{count} day ago',
  'home.common.ago.days': '{count} days ago',
  'home.weekday.short.sun': 'Sun',
  'home.weekday.short.mon': 'Mon',
  'home.weekday.short.tue': 'Tue',
  'home.weekday.short.wed': 'Wed',
  'home.weekday.short.thu': 'Thu',
  'home.weekday.short.fri': 'Fri',
  'home.weekday.short.sat': 'Sat',
  'home.month.short.jan': 'Jan',
  'home.month.short.feb': 'Feb',
  'home.month.short.mar': 'Mar',
  'home.month.short.apr': 'Apr',
  'home.month.short.may': 'May',
  'home.month.short.jun': 'Jun',
  'home.month.short.jul': 'Jul',
  'home.month.short.aug': 'Aug',
  'home.month.short.sep': 'Sep',
  'home.month.short.oct': 'Oct',
  'home.month.short.nov': 'Nov',
  'home.month.short.dec': 'Dec',
  'home.weekday.long.sun': 'Sunday',
  'home.weekday.long.mon': 'Monday',
  'home.weekday.long.tue': 'Tuesday',
  'home.weekday.long.wed': 'Wednesday',
  'home.weekday.long.thu': 'Thursday',
  'home.weekday.long.fri': 'Friday',
  'home.weekday.long.sat': 'Saturday',
  'home.month.long.jan': 'January',
  'home.month.long.feb': 'February',
  'home.month.long.mar': 'March',
  'home.month.long.apr': 'April',
  'home.month.long.may': 'May',
  'home.month.long.jun': 'June',
  'home.month.long.jul': 'July',
  'home.month.long.aug': 'August',
  'home.month.long.sep': 'September',
  'home.month.long.oct': 'October',
  'home.month.long.nov': 'November',
  'home.month.long.dec': 'December',

  // ── saved setups (DraftsCard) ─────────────────────────────────────
  'home.drafts.headingOne': 'Saved setup',
  'home.drafts.headingMany': '{count} saved setups',
  'home.drafts.hint': 'Click to start a new tournament from a saved setup',
  'home.drafts.deleteTitle': 'Delete "{name}"?',
  'home.drafts.deleteLead': 'This saved setup will be deleted. This cannot be undone.',
  'home.drafts.deleteAria': 'Delete this saved setup',
  'home.drafts.playerOne': ' · {count} player',
  'home.drafts.playerMany': ' · {count} players',
  'home.drafts.groupsReady': ' · groups ready',

  // ── running and paused tournaments (PausedTournamentsCard) ────────
  'home.paused.untitled': 'Tournament',
  'home.paused.players': '{count} players',
  'home.paused.matchesPlayed': '{done}/{total} matches played',
  'home.paused.notStarted': 'not started',
  'home.paused.groups': '{count} groups',
  'home.paused.round': 'Round {round}',
  'home.paused.placementMatches': 'Placement matches',
  'home.paused.inProgress': 'In progress',
  'home.paused.pausedSince': 'Paused {since}',
  'home.paused.reopenTitle': 'Open this tournament again',
  'home.paused.resumeTitle': 'Resume this tournament',
  'home.paused.open': 'Open',
  'home.paused.resume': 'Resume',
  'home.paused.pause': 'Pause',
  'home.paused.pauseTitle': 'Pause this tournament — it moves to «Paused» and can be resumed later',
  'home.paused.archiveTitleAttr':
    'Save the tournament to the archive and close it. It can be re-opened from the archive with «Continue tournament».',
  'home.paused.saveAndClose': 'Save and close',
  'home.paused.endAndDeleteTitle': 'End and delete this tournament',
  'home.paused.deletePausedTitle': 'Delete paused tournament',
  'home.paused.runningOne': 'Tournament in progress',
  'home.paused.runningMany': '{count} tournaments in progress',
  'home.paused.runningHint': 'Click «Open» to continue — several can run at the same time',
  'home.paused.openNow': 'Open now',
  'home.paused.status.incomplete': 'Incomplete',
  'home.paused.status.ready': 'Ready to start',
  'home.paused.status.running': 'In progress',
  'home.paused.status.paused': 'Paused',
  'home.paused.status.finals': 'Finals',
  'home.paused.status.done': 'Finished',
  'home.paused.status.active': 'Active',
  'home.paused.collapseTitle': 'Hide paused tournaments',
  'home.paused.expandTitle': 'Show paused tournaments',
  'home.paused.pausedOne': 'Paused tournament',
  'home.paused.pausedMany': '{count} paused tournaments',
  'home.paused.resumeAnytime': 'Can be resumed at any time',
  'home.paused.tapToShow': 'Tap to show',
  'home.paused.deleteAllTitleAttr': 'Delete all parked tournaments',
  'home.paused.deleteAll': 'Delete all',
  'home.paused.deleteConfirmTitle': 'Delete paused tournament?',
  'home.paused.deleteLeadPre': 'Do you want to delete',
  'home.paused.deleteLeadPost': '? All data will be lost.',
  'home.paused.theTournament': 'the tournament',
  'home.paused.aTournament': 'tournament',
  'home.paused.archiveConfirmTitle': 'Save and close «{name}»?',
  'home.paused.archiveLeadPre': 'This tournament is',
  'home.paused.archiveUnfinished': 'not finished',
  'home.paused.archiveLeadMid': '. It will be saved and moved to the',
  'home.paused.archiveWord': 'archive',
  'home.paused.archiveLeadPost': ', and disappears from the home screen.',
  'home.paused.archiveNote':
    'No data is lost. Some tournament types can be resumed from the archive at a later point.',
  'home.paused.archiveConfirm': 'Save and archive',
  'home.paused.deleteAllConfirmTitle': 'Delete all paused tournaments?',
  'home.paused.deleteAllLeadPre': 'This deletes',
  'home.paused.deleteAllCount': 'all {count} paused tournaments',
  'home.paused.deleteAllLeadPost': 'permanently. All data is lost and cannot be recovered.',
  'home.paused.deleteAllWarning': 'This cannot be undone. Press «Delete all» to confirm.',
  'home.paused.deleteAllCta': 'Delete all {count}',

  // ── the landing screen itself (HomeView) ──────────────────────────
  'home.view.leadWithClub':
    'You are signed in at {club}. This is where you run booking and tournaments from one place.',
  'home.view.leadNoClub':
    'Welcome to {brand} — the platform for courts, booking and tournaments. Set up your club information from the club button in the top right, or start a tournament straight away.',
  'home.view.deleteUnsavedTitle': 'Delete without saving?',
  'home.view.deleteUnsavedPre': 'All data for',
  'home.view.deleteUnsavedPost': 'will be deleted permanently. This cannot be undone.',
  'home.view.theTournament': 'the tournament',
  'home.view.deletePermanently': 'Delete permanently',
  'home.view.activeSeries': 'Active league',
  'home.view.seriesFallback': 'League',
  'home.view.seriesClassDiv': '{name} · {count} div',
  'home.view.seriesMatches': '{done}/{total} matches',
  'home.view.seriesDeadline': 'Deadline {date}',
  'home.view.tournamentsEyebrow': 'Tournaments',
  'home.view.createTournament': 'Create tournament',
  'home.view.tournamentsLead': 'Tap anywhere to see them all — or pick a format beside it.',
  'home.view.seeAllFormats': 'See all formats',
  'home.view.pickFormat': 'Pick a format',
  'home.view.miniTournaments': 'Mini tournaments',
  'home.view.bookingEyebrow': 'Court booking',
  'home.view.reserveCourt': 'Reserve a court',
  'home.view.bookingLead': 'Tap anywhere to open — or pick a day beside it.',
  'home.view.bookingsActiveOne': ' {count} active booking.',
  'home.view.bookingsActiveMany': ' {count} active bookings.',
  'home.view.open': 'Open',
  'home.view.availability7': 'Availability, 7 days',
  'home.view.dayDotsTitle': '{day} — early · midday · evening',
  'home.view.today': 'Today',
  'home.view.activitiesEyebrow': 'Activities',
  'home.view.activitiesOpenCount': '{count} open',
  'home.view.signUp': 'Sign up',
  'home.view.activitiesLead': 'Tap anywhere to open — or pick an activity beside it.',
  'home.view.nearestActivities': 'Nearest activities',
  'home.view.noUpcoming': 'Nothing coming up yet',
  'home.view.activityFallback': 'Activity',
  'home.view.dateNotSet': 'Date not set',
  'home.view.whenDate': '{wd} {day} {mo}',
  'home.view.whenTime': ' at {hh}:{mm}',
  'home.view.banepadEyebrow': 'Banepad',
  'home.view.screensAtCourts': 'Screens at the courts',
  'home.view.banepadLead':
    'See what is happening on every court live — or open the pad for one court.',
  'home.view.openCentral': 'Open the hub',
  'home.view.courtsNow': 'Courts now',
  'home.view.court': 'Court {court}',
  'home.view.live': 'Live',
  'home.view.next': 'Next',
  'home.view.booked': 'Booked',
  'home.view.courtAvailable': 'Available',
  'home.view.databaseEyebrow': 'Database',
  'home.view.playerDatabase': 'Player database',
  'home.view.playersCount': '{count} players',
  'home.view.playersFallback': 'View and edit players',
  'home.view.archiveEyebrow': 'Archive',
  'home.view.tournamentArchive': 'Tournament archive',
  'home.view.archiveSummaryOne': '{count} tournament · {matches} matches',
  'home.view.archiveSummaryMany': '{count} tournaments · {matches} matches',
  'home.view.loading': 'Loading…',
  'home.view.howItWorks': 'How it works',
  'home.view.step1': 'Set up the tournament (name, dates, times, courts)',
  'home.view.step2': 'Pick players from the database',
  'home.view.step3': 'The system suggests groups based on ELO',
  'home.view.step4': 'Generate the match schedule automatically',
  'home.view.step5': 'Start and run the tournament',
  'home.view.features': 'Features',
  'home.view.feature1': 'Auto-generated matches and court allocation',
  'home.view.feature2': 'Live result entry',
  'home.view.feature3': 'Promotion and relegation after every phase',
  'home.view.feature4': 'Finals and placement matches for everyone',
  'home.view.feature5': 'Final ranking with an ELO update',
  'home.view.whatIsElo': 'What is ELO?',
  'home.view.eloExplainerPre':
    'ELO is a rating system that puts a number on the level of a player. Beat a better player and you gain more points — lose to a weaker one and you lose more. After every match the ELO of both players is adjusted automatically. When two players form a team, the strength of the team is',
  'home.view.eloAverage': 'the average of their ELO',
  'home.view.eloExplainerPost': '— so that two average players can meet one strong and one weak.',
  'home.view.eloLevel': 'LEVEL {level}',
  'home.view.eloScale':
    'Padel24 uses a 9-level scale from 500 (complete beginner) to 3000+ (top level in the club). ELO is updated automatically after every match and is used for grouping and team composition.',
  'home.view.deleteTournamentTitle': 'Delete tournament?',
  'home.view.deleteArchivedPre': 'Are you sure you want to delete',
  'home.view.deleteArchivedPost': '? All data will be deleted permanently and cannot be recovered.',
  'home.view.thisTournament': 'this tournament',
  'home.view.yesDeletePermanently': 'Yes, delete permanently',
  'home.view.recentTournaments': 'Recent tournaments',
  'home.view.seeAll': 'See all ({count})',
  'home.view.unknownDate': 'Unknown date',
  'home.view.done': 'Finished',
  'home.view.tournamentFallback': 'Tournament',
  'home.view.matchesRegistered': '{count} matches recorded',
  'home.view.playersAndMatches': '{players} players · {matches} matches',
  'home.view.deleteTournamentAria': 'Delete tournament',

  // ── the mini-tournament format picker (MiniTournamentsView) ───────
  'home.mini.back': 'Back',
  'home.mini.title': 'Mini tournaments',
  'home.mini.lead':
    'Short rotation formats played in a single session. What they share: minimal admin, 1-3 hours per run.',
  'home.mini.eloWeightPre': 'The ELO weight is',
  'home.mini.eloWeightValue': '50%',
  'home.mini.eloWeightPost':
    'of Padel24 and single matches, since these matches are typically shorter and therefore less representative.',
  'home.mini.available': 'Available',
  'home.mini.comingSoon': 'Coming soon',
  'home.mini.hideDetails': 'Hide details',
  'home.mini.showDetails': 'Show details',
  'home.mini.less': 'Less',
  'home.mini.moreInfo': 'More info',
  'home.mini.filterByProperty': 'Filter by property',
  'home.mini.hideAllInfoTitle': 'Hide the info on all of them',
  'home.mini.showAllInfoTitle': 'Show the info on all of them',
  'home.mini.hideAllInfo': 'Hide all info',
  'home.mini.showAllInfo': 'Show all info',
  'home.mini.reset': 'Reset ({count})',
  'home.mini.noMatchCombo': 'No formats match that combination. Try removing a filter.',
  'home.mini.showingCount': 'Showing {shown} of {total} formats',
  'home.mini.noMatchFilters': 'No formats match the active filters',
  'home.mini.otherFormats': 'Other formats',
  'home.mini.icon.team': 'Fixed partner (the pair is kept for the whole tournament)',
  'home.mini.icon.random': 'Balanced rotation — everyone meets everyone over time',
  'home.mini.icon.skill': 'Level-based rotation — you meet players around your own position',
  'home.mini.icon.mix': 'Mixed genders (one woman + one man in every team)',
  'home.mini.icon.club': 'Between two padel clubs',
  'home.mini.filter.random': 'Balanced rotation',
  'home.mini.filter.skill': 'Level-based',
  'home.mini.filter.team': 'Fixed partner',
  'home.mini.filter.mix': 'Mixed genders',
  'home.mini.filter.club': 'Club against club',
  'home.mini.shared.oneWomanOneMan': 'Every team = 1 woman + 1 man',
  'home.mini.shared.genderOverride': 'A temporary gender override when the balance is uneven',
  'home.mini.shared.fixedPairs': 'Fixed partner pairs for the whole tournament',
  'home.mini.shared.twoVsTwo': '2 from club A vs 2 from club B on every court',
  'home.mini.shared.clubLogoName': 'Club logo + name for both clubs',
  'home.mini.shared.clubScoreIndividual':
    'Combined club score at the top, individual standings as usual',
  'home.mini.americano.tagline': 'Everyone against everyone, rotating partners',
  'home.mini.americano.desc':
    'Classic americano: the players are spread across the courts and combined so that everyone plays with everyone at least once. Teams and opponents rotate after every round. Points or wins count towards the final ranking.',
  'home.mini.americano.f1': '4 players per court, rotating partners',
  'home.mini.americano.f2': 'Choose between Total, Race, Bingo or Time',
  'home.mini.americano.f3': 'Configurable final (Balanced or Top against bottom)',
  'home.mini.americano.f4': 'Optional play-off ranking or overall standings',
  'home.mini.mixAmericano.tagline': 'Like Americano, with mixed genders required',
  'home.mini.mixAmericano.desc':
    'The same rotation as Americano, but every team is one woman and one man. It needs a balanced gender split — the algorithm handles an imbalance with a temporary gender override.',
  'home.mini.mixAmericano.f2': 'Optimal anti-repeat matchmaking (the same as Americano)',
  'home.mini.mixAmericano.f4': 'Play by points, games, time or sets',
  'home.mini.mexicano.tagline': 'Closer matches based on the standings',
  'home.mini.mexicano.desc':
    'Like Americano, but the next round is generated from the standings — you meet players around your own position. That makes the matches closer as the tournament goes on. A random round can be dropped in to break a player loop.',
  'home.mini.mexicano.f1': '4 players per court, partners decided by the standings',
  'home.mini.mexicano.f2': 'Pool size 4/6/8 to control the variation',
  'home.mini.mexicano.f3': 'Random first rounds, and every Nth round (optional)',
  'home.mini.mexicano.f4': 'A "Random round now" button to break a player loop',
  'home.mini.mixicano.tagline': 'Mexicano with mixed genders',
  'home.mini.mixicano.desc':
    'Like Mexicano, but every team has one woman and one man. It needs a balanced gender split — the algorithm handles an imbalance with a temporary gender override.',
  'home.mini.mixicano.f2': 'Pool size 4/6/8, as in Mexicano',
  'home.mini.mixicano.f4': '"Random round now" + the same ranking as Mexicano',
  'home.mini.teamAmericano.tagline': 'Fixed teams, every team meets every other',
  'home.mini.teamAmericano.desc':
    'The players form fixed partner pairs. Every team meets every other team at least once. Results are totalled per team.',
  'home.mini.teamAmericano.f2': 'Every team meets every other team',
  'home.mini.teamAmericano.f3': 'Standings are calculated per team (not per player)',
  'home.mini.teamAmericano.f4':
    'Final play-offs: 1st team vs 2nd team (gold), 3rd vs 4th (bronze), and so on.',
  'home.mini.teamMexicano.tagline': 'Fixed teams, closer matches',
  'home.mini.teamMexicano.desc':
    'Fixed partner pairs as in Team Americano, but the next round is generated from the team ranking — strong teams meet strong teams. The matches get closer the further into the tournament you get.',
  'home.mini.teamMexicano.f2': 'The team ranking drives the matchmaking (strong teams meet strong)',
  'home.mini.teamMexicano.f3': 'Pool 4/6/8 — the same as Mexicano, but at team level',
  'home.mini.teamMexicano.f4': 'Play by points, games or time',
  'home.mini.clubAmericano.tagline': 'Club against club, rotating partners',
  'home.mini.clubAmericano.desc':
    'Half the players represent club A, the other half club B. Every court is always 2 from club A against 2 from club B. Partners rotate within the club, and the clubs compete on a combined score.',
  'home.mini.clubAmericano.f4': 'Drag and drop to move players between clubs',
  'home.mini.clubTeamAmericano.tagline': 'Club against club, fixed partners',
  'home.mini.clubTeamAmericano.desc':
    'Teams from two clubs meet each other. Every team is a fixed pair of partners. Round robin: every A team meets every B team at least once. The clubs compete on a combined score.',
  'home.mini.clubTeamAmericano.f1': 'Fixed partner pairs from each club',
  'home.mini.clubTeamAmericano.f2': 'Round-robin matching: every A team meets every B team',
  'home.mini.clubTeamAmericano.f4':
    'Combined club score at the top, team standings as a leaderboard',
  'home.mini.clubMexicano.tagline': 'Two clubs, closer matches',
  'home.mini.clubMexicano.desc':
    'Like Club Americano, but the matching follows Mexicano rules: the best from club A meets the best from club B, the second best meets the second best, and so on. That makes the matches more competitive as the tournament goes on.',
  'home.mini.clubMexicano.f2': 'The best from club A meets the best from club B',
  'home.mini.clubMexicano.f3': 'Pool size controls the partner variation within the club',

  // ══ Activities (ActivitiesView) ═══════════════════════════════════
  'activities.when.notSet': 'Time not set',
  'activities.when.days': '{count} days · from {from}',
  'activities.unknownPlayer': 'Unknown player',
  'activities.level.exact': 'Level {level} – {name}',
  'activities.level.range': 'Level {min}–{max}',
  'activities.levelBadge': 'Level {level} — {name}',
  'activities.km.rule.women': 'two women',
  'activities.km.rule.mix': 'one woman + one man',
  'activities.km.rule.men': 'open — women may play up',
  'activities.swapThisPlayer': 'Substitute this player',
  'activities.woman': 'Woman',
  'activities.man': 'Man',
  'activities.overQuotaTitle': 'Signed up beyond the quota',
  'activities.extra': 'extra',
  'activities.paid': 'Paid',
  'activities.refunded': 'Refunded',
  'activities.manual': 'Manual',
  'activities.pendingPayment': 'Pending',
  'activities.coveredByBooker': 'Covered — settle up with {booker}',
  'activities.bookerFallback': 'the booker',
  'activities.remove': 'Remove',
  'activities.day': 'Day',
  'activities.searchName': 'Search by name…',
  'activities.noMatches': 'No matches.',
  'activities.noAvailablePlayers': 'No available players.',
  'activities.createPlayer': 'Create «{name}»',
  'activities.clubPlayersCount': '{confirmed}/{max} players',
  'activities.date.dayMonth': '{wd} {day} {mo}',
  'activities.date.dayMonthYear': '{wd} {day} {mo} {year}',
  'activities.date.dayNumMonth': '{wd} {day}/{month}',
  'activities.date.closeAt': '{wd} {day}/{month} at {hh}:{mm}',
  'activities.date.dayMonthAt': '{wd} {day} {mo} at {hh}:{mm}',

  // ── the shell around a single activity ────────────────────────────
  'activities.allActivities': 'All activities',
  'activities.adminOn': '🔓 Admin on',
  'activities.adminOff': '🔒 Admin off',
  'activities.toggleAdminTitle': 'Switch between the member view and admin',
  'activities.full': 'FULL',
  'activities.freeSlots': 'PLACES AVAILABLE',
  'activities.venueFallback': 'The padel centre',
  'activities.courtsReserved': 'Courts are reserved for this activity',
  'activities.reserveCourts': 'Reserve courts',
  'activities.signedUpCount': 'Signed up ({count}{max})',
  'activities.waitlistCount': 'Waitlist ({count}{max})',
  'activities.noSignupsYet': 'No sign-ups yet.',
  'activities.signMeOff': 'Sign me off',
  'activities.signMeUp': 'Sign me up',
  'activities.joinWaitlist': 'Put me on the waitlist',
  'activities.addParticipant': 'Add participant',
  'activities.searchPlayer': 'Search player …',
  'activities.previewNeeds4': 'At least 4 players to preview the tournament',
  'activities.previewTournament': 'Preview the tournament',
  'activities.preview': 'Preview',
  'activities.starting': 'Starting…',

  // ── admin: which courts to release when locking ───────────────────
  'activities.release.pick':
    'Choose which courts are released ({chosen} chosen — you need {needed} for {players} players):',
  'activities.release.court': 'Court {court} · {state}',
  'activities.release.released': '✕ released',
  'activities.release.kept': '✓ kept',
  'activities.release.keepMinOne':
    'You must keep at least {needed} court for the {players} signed up. Choose fewer to release.',
  'activities.release.keepMinMany':
    'You must keep at least {needed} courts for the {players} signed up. Choose fewer to release.',
  'activities.release.keepingMore':
    'You are keeping {kept} courts — more than the {needed} needed.',

  // ── league season sign-up ─────────────────────────────────────────
  'activities.signupOpenBadge': 'SIGN-UP OPEN',
  'activities.seasonFallbackName': 'League — season',
  'activities.seasonStart': 'Season starts',
  'activities.signupDeadlineSuffix': ' · sign-up deadline {date}',
  'activities.noSeasonFee': 'Free — no season fee',
  'activities.seasonFee': 'Season fee: {amount} kr per team',
  'activities.seasonLeadPre': 'Sign-up for the next league season. Sign-up is',
  'activities.seasonLeadBold': 'by team',
  'activities.seasonLeadPost':
    '(pairs), and the season fee is paid per team before the season starts.',
  'activities.signedInAs': 'Signed in as',
  'activities.noProfile': 'no profile',
  'activities.youAreSignedUpWith': 'You are signed up with',
  'activities.seasonFeePaid': 'Season fee paid',
  'activities.notPaid': 'Not paid',
  'activities.payAmount': 'Pay {amount} kr',
  'activities.withdrawTeam': 'Withdraw the team',
  'activities.pickPartner': 'Pick a partner',
  'activities.signUpYourTeam': 'Sign up your team',
  'activities.priceSuffix': ' · {amount} kr',
  'activities.addTeamAdmin': 'Add team (admin)',
  'activities.partnerFor': 'Partner for',
  'activities.pickP1ThenPartner':
    'Pick player 1, then the partner. The team is added as signed up (not paid).',
  'activities.signedUpTeams': 'Teams signed up ({count})',
  'activities.noTeamsYet': 'No teams signed up yet.',
  'activities.undo': 'undo',
  'activities.markPaid': 'mark as paid',
  'activities.removeLower': 'remove',
  'activities.newTeamsNote':
    'New teams signing up here must be entered into the league by an admin (in the league module) before the next season. Existing teams are managed in the «Sesongpåmelding» tab of the league.',
  'activities.paidSeasonFee': 'Have paid the season fee ({count})',
  'activities.nonePaidForSeason': 'No team has paid for season {season} yet.',
  'activities.activeUnpaid': 'Active teams that have not paid ({count})',
  'activities.activeUnpaidLead':
    'Teams playing the current season that have not paid the season fee for season {season} yet.',
  'activities.allActiveTeamsPaid': 'Every active team has paid.',

  // ── the activity detail page ──────────────────────────────────────
  'activities.activityFallback': 'Activity',
  'activities.allLevels': 'All levels',
  'activities.free': 'Free',
  'activities.badge.cancelled': 'CANCELLED',
  'activities.badge.locked': 'LOCKED',
  'activities.badge.closed': 'CLOSED',
  'activities.badge.closedAuto': 'CLOSED (AUTO)',
  'activities.badge.waitlistOpen': 'WAITLIST OPEN',
  'activities.badge.open': 'OPEN',
  'activities.partOfSeries': 'Part of the series',
  'activities.seriesNameSuffix': ' «{name}»',
  'activities.occurrenceOf': ' — time {index} of {total}',
  'activities.occurrenceRolling': ' — time {index} (rolling)',
  'activities.when.notSetYet': 'Time not set yet',
  'activities.courtSuffix': ' · court {courts}',
  'activities.pricePerTeam': '{amount} kr/team',
  'activities.pricePerTeamRange': '{min}–{max} kr/team',
  'activities.pricePerPlayer': '{amount} kr/player',
  'activities.deregisterCutoff': '· sign off at least {hours} h before',
  'activities.signupAutoCloses': 'Sign-up closes automatically {at} ({when}).',
  'activities.hoursBeforeStart': '{hours} h before the start',
  'activities.atStart': 'at the start',
  'activities.signupClosesAt': 'Sign-up closes {at}.',
  'activities.signupClosedDeadlinePassed': 'Sign-up is closed (deadline passed{at}).',
  'activities.signupClosedHow': 'Sign-up is closed{how}.',
  'activities.closedAutoHours': ' (automatically, {hours} h before the start)',
  'activities.closedAutoAtStart': ' (automatically, at the start)',
  'activities.closedManually': ' manually',
  'activities.mixCounts': '{men}/{menMax} men · {women}/{womenMax} women',
  'activities.signedUpLower': 'signed up',
  'activities.waitlistSuffix': ' · {count} on the waitlist',
  'activities.aboutTournament': 'About the tournament',

  // ── deadline, minimum and the go/no-go decision ───────────────────
  'activities.cancelledTooFew': '✕ Cancelled — too few players',
  'activities.cancelledLead':
    '{count} signed up{min}. The courts have been released, and players who paid are marked as refunded.',
  'activities.butMinRequired': ', but at least {min} are required',
  'activities.reopenSignup': 'Re-open sign-up',
  'activities.confirmedGoesAhead': '✓ Confirmed — the tournament goes ahead',
  'activities.confirmedLead': '{count} signed up. Sign-up is closed.',
  'activities.adjustCourtsHint': ' You adjust the courts under «Baner & plasser».',
  'activities.adjustCourtsAnytimeHint':
    ' You can adjust the courts at any time under «Baner & plasser».',
  'activities.deadlineAndMinimum': 'Sign-up deadline & minimum',
  'activities.minimumMarker': 'Minimum {count}',
  'activities.deadlinePassed': 'DEADLINE PASSED',
  'activities.tier.full': '✓ full',
  'activities.tier.enough': '✓ enough players',
  'activities.tier.enoughCanFill': '✓ enough — can fill up to {max}',
  'activities.tier.missing': '{count} short of the minimum',
  'activities.progressCount': '{count}{of} signed up{min}',
  'activities.ofMax': ' of {max}',
  'activities.minSuffix': ' · min {min}',
  'activities.closedEnough':
    'Sign-up closed with enough players ({count}{min}). The tournament goes ahead.',
  'activities.closedTooFew':
    'Sign-up closed with too few players ({count}{min}). The activity can be cancelled and every court released.',
  'activities.confirmGoesAhead': 'Confirm — the tournament goes ahead',
  'activities.cancelAndRelease': 'Cancel & release every court',
  'activities.awaitingClubDecision': 'Waiting for the club to confirm the outcome.',
  'activities.atTheDeadline': 'At the deadline',
  'activities.whenSignupCloses': 'When sign-up closes',
  'activities.autoDecidePre': '{when} ({deadline}) it is decided automatically: at least',
  'activities.autoDecidePost':
    'signed up → the tournament goes ahead; fewer → it is called off, every court is released and everyone who paid is refunded.',

  // ── courts & slots (admin) ────────────────────────────────────────
  'activities.courtsAndSlots': 'Courts & places',
  'activities.courtsBookedOne': '{count} court booked',
  'activities.courtsBookedMany': '{count} courts booked',
  'activities.courtListSuffix': ' (court {courts})',
  'activities.slotsCount': '{count} places',
  'activities.capacityFollowsCourts': 'capacity follows the courts',
  'activities.releaseOneCourt': '− Release a court',
  'activities.openOneMoreCourt': '+ Open one more court',
  'activities.courtButtonsHelp':
    '«Open one more court» reserves an available court at the time of the activity, adds 4 new places and re-opens sign-up — even when the activity is locked. «Release a court» removes the highest-numbered court and lowers the capacity.',
  'activities.waitlistEnoughForOneCourt':
    '{count} are on the waitlist — enough for {courts} extra court. «Open one more court» lets in 4 at a time.',
  'activities.waitlistEnoughForCourts':
    '{count} are on the waitlist — enough for {courts} extra courts. «Open one more court» lets in 4 at a time.',

  // ── the demo profile banner + the member-side sign-up ─────────────
  'activities.switchProfile': 'Switch profile',
  'activities.pickDemoProfile': 'Pick the profile you want to simulate (demo only):',
  'activities.searchProfile': 'Search profile…',
  'activities.signupClosedForThis': 'Sign-up is closed for this activity.',
  'activities.swapPartner': 'Substitute partner',
  'activities.youAre': 'You are',
  'activities.standbyFree': 'an on-call reserve (free)',
  'activities.standby': 'on-call reserve',
  'activities.queueNumberSuffix': ' · no. {n}',
  'activities.queueNumberParen': ' (no. {n})',
  'activities.onWaitlistReserve': 'on the waitlist (reserve)',
  'activities.partnerLabel': 'partner:',
  'activities.putMeOnWaitlist': 'Put me on the waitlist',
  'activities.signYourselfOff': 'Sign yourself off',
  'activities.alsoStandby': 'I also want to be an on-call reserve',
  'activities.youAreNumberInQueue': ' — you are no. {n} in the queue',
  'activities.standbyExplain1': 'You are a normal reserve and pay for the reserve place',
  'activities.amountParen': ' ({amount} kr)',
  'activities.standbyExplain2':
    '. If you are used as a reserve you play the tournament as normal. If you are',
  'activities.not': 'not',
  'activities.standbyExplain3':
    'used as a reserve, the on-call role takes over: you may be contacted to step in if somebody is injured mid-tournament — and you then get',
  'activities.refundedWord': 'a refund of',
  'activities.standbyExplain4': 'the reserve amount. On-call reserves must be',
  'activities.standbyNearby': 'near the venue and able to turn up at short notice',
  'activities.standbyExplain5': '. On-call reserves are numbered in the order they sign up.',
  'activities.standbyListFull': 'The on-call list is full ({count}/{max}).',
  'activities.whichDays': 'Which days can you turn up?',
  'activities.standbyDaysNote':
    'You appear as an on-call reserve in the tournament only on the days you ticked.',
  'activities.saveChoice': 'Save choice',
  'activities.undoCapital': 'Undo',
  'activities.unsavedChanges': 'Unsaved changes',
  'activities.levelRequirement': 'Level requirement: {label}',
  'activities.yourLevelIs':
    'You are level {level}{name} and are {relation} the level requirement, so you cannot sign yourself up. Get in touch with the club if you think this is wrong.',
  'activities.nameParen': ' ({name})',
  'activities.relationUnder': 'under',
  'activities.relationOver': 'over',
  'activities.relationOutside': 'outside',
  'activities.genderSlotsFull':
    '{slots} are full — you go on the waitlist and move up if {gender} signs off.',
  'activities.womensPlaces': 'The places for women',
  'activities.mensPlaces': 'The places for men',
  'activities.aWoman': 'a woman',
  'activities.aMan': 'a man',
  'activities.readyMix':
    'Ready to sign up. {men} places available for men · {women} places available for women.',
  'activities.fullGoesToWaitlist':
    'The activity is full — you go on the waitlist and move up if a place becomes available.{waitlist}',
  'activities.waitlistNowSuffix': ' {count} are on the waitlist right now.',
  'activities.readyForSignup': 'Ready to sign up.{free}',
  'activities.freePlacesSuffix': ' {count} places available.',
  'activities.priceSentence': ' Price: {amount} kr.',
  'activities.waitlistTip':
    'Tip: every 4 players on the waitlist are enough for a whole extra court — the club can then open one more court and let more people in.',
  'activities.waitlistTipCountOne':
    ' {count} are on the waitlist right now ({courts} extra court possible).',
  'activities.waitlistTipCountMany':
    ' {count} are on the waitlist right now ({courts} extra courts possible).',
  'activities.yourSignups': 'Your sign-ups ({count})',
  'activities.classFallback': 'Class',
  'activities.waitlistDotSuffix': ' · waitlist',

  // ── club championship: classes, the pair rule and playing up ──────
  'activities.km.levelExact': 'level {level}',
  'activities.km.levelRange': 'level {min}–{max}',
  'activities.km.alreadyInCategory': 'You are already signed up in this category',
  'activities.km.classFull': 'The class is full',
  'activities.km.requiresLevel': 'Requires {label} — your level is outside it',
  'activities.km.womenOnly': 'Only women can sign up in this class',
  'activities.km.teamPriceSuffix': ' · {amount} kr/team',
  'activities.km.signedUpSuffix': ' · signed up',
  'activities.km.fullSuffix': ' · FULL',
  'activities.km.playUpTipPre': 'The men’s class is',
  'activities.km.openToAll': 'open to everyone',
  'activities.km.playUpTipMid':
    '. As a woman you may «play up» here — that is entirely allowed, but remember this is a men’s class, and you get a warning when you sign up. For a women’s class of its own, pick',
  'activities.km.womenClass': 'Women',
  'activities.km.pairsNote': 'You sign up as a pair. Women can also sign up in the men’s class.',
  'activities.km.pickPartnerFor': 'Pick a partner — {label}',
  'activities.km.switchClass': 'Switch class',
  'activities.km.womanUpWarnPre': '⚠ You are signing up in',
  'activities.km.mensClass': 'the men’s class',
  'activities.km.womanUpWarnPost':
    ' as a woman. That is allowed, but note that this is a men’s class.',
  'activities.km.pairRule': '{label} — a pair needs {rule}.',
  'activities.km.pickPartnerForPlayer': 'Pick a partner for {name}.',
  'activities.km.pickPlayer1': 'Pick player 1.',
  'activities.km.addingWomanWarn':
    '⚠ You are adding a woman to the men’s class. Allowed, but it is marked as «playing up».',
  'activities.km.noPlayersForClass': 'No available players for this class.',

  // ── club and club-team sign-up ────────────────────────────────────
  'activities.clubTeam.pickYourClub': 'Pick the club you represent',
  'activities.clubTeam.availableCount': '{count} available',
  'activities.clubTeam.signupNote':
    'You sign up as a team (or alone → paired with somebody from the same club). Singles are never paired across clubs.',
  'activities.clubTeam.pickPartnerIn': 'Pick a partner — {club}',
  'activities.clubTeam.youRepresent': 'You represent',
  'activities.clubTeam.switchClub': 'Switch club',
  'activities.clubTeam.aloneNote':
    'If you sign up alone, you are paired with another single from {club} at the start.',
  'activities.club.evenNote':
    'An equal number from each club play. If your club is full, you go on the waitlist.',
  'activities.signUpWithPartner': 'Sign up with a partner',
  'activities.signUpAlone': 'Sign up alone',
  'activities.pickYourPartner': 'Pick your partner',
  'activities.signUpWithPartnerSelf': 'Sign up with a partner',
  'activities.signUpAloneSelf': 'Sign up alone',
  'activities.waitlistFull': 'Waitlist full',
  'activities.waitlistWithPartner': 'Waitlist with a partner',
  'activities.waitlistAlone': 'Waitlist (alone)',
  'activities.signupFull': 'Sign-up full',
  'activities.bothFull': 'Sign-up is full and so is the waitlist — you cannot sign up right now.',
  'activities.fullWaitlisted': 'The activity is full — you go on the waitlist.',
  'activities.aloneNote':
    'If you sign up alone, you are put together with another single at the start. Pairs take priority for the last places.',
  'activities.waitlistParen': ' (waitlist)',
  'activities.availableSuffixOne': ' · {count} available',
  'activities.availableSuffixMany': ' · {count} available',
  'activities.waitlistAndPay': 'Put me on the waitlist · pay {amount} kr',
  'activities.signUpAndPay': 'Sign me up · pay {amount} kr',

  // ── the Padel24 standby-reserve prompt ────────────────────────────
  'activities.standbyListIsFull': 'The on-call list is full',
  'activities.standbyOnlyTitle': 'Be an on-call reserve only — free, without playing',
  'activities.standbyOnlyButton': 'On-call reserve – free',
  'activities.becomeStandbyFree': 'Become an on-call reserve (free)',
  'activities.youGoOnWaitlist': 'You go on the waitlist',
  'activities.standbyOnlyExplain':
    'You are not put on the waitlist and pay nothing. You are only an on-call reserve and may be contacted to step in if somebody is injured mid-tournament.',
  'activities.fullSoReserve': 'The activity is full, so you go on the waitlist as a reserve.',
  'activities.reservePaymentNote':
    ' You pay {amount} kr for the reserve place, but get a refund if you are not used.',
  'activities.standbyReservesWord': 'On-call reserves',
  'activities.standbyPromptExplain':
    'If you are not used as a reserve, you can step in if somebody is injured mid-tournament — and the reserve amount is then refunded. On-call reserves',
  'activities.mustBe': 'must be',
  'activities.standbyCallOrderPre': 'When somebody has to be replaced, the on-call reserve who is',
  'activities.closestInLevel': 'closest in level (ELO)',
  'activities.standbyCallOrderPost':
    'to that player is called in — first within ±100, then ±200, ±300 and so on. So you can be called in even if you are far down the list, if you are the closest in level at that moment.',
  'activities.signMeUpAsStandby': 'Sign me up as an on-call reserve →',
  'activities.continueToPayment': 'Continue to payment →',
  'activities.putMeOnWaitlistArrow': 'Put me on the waitlist →',
  'activities.paymentSummary': '{name} — {label}{reserve}',
  'activities.reservePlaceSuffix': ' · reserve place (refunded if you are not used)',
  'activities.recordResult': 'Record result',
  'activities.errorPrefix': 'Error: {message}',
  'activities.unknownError': 'unknown error',

  // ── admin: setup, court time and the manual add list ──────────────
  'activities.setup': 'Setup',
  'activities.tuneSetup': 'Tune the setup',
  'activities.save': 'Save',
  'activities.nameLabel': 'Name',
  'activities.maxPlayersLabel': 'Max players',
  'activities.pricePerPlayerLabel': 'Price per player (kr)',
  'activities.dateLabel': 'Date',
  'activities.fromLabel': 'From',
  'activities.toLabel': 'To',
  'activities.padel24SetupNote':
    'The date, court time and courts are controlled in the Padel24 setup — press «Tune oppsett».',
  'activities.deregisterCutoffLabel': 'Sign-off deadline (hours before the start)',
  'activities.signupCloseHoursLabel': 'Close sign-up automatically (hours before the start)',
  'activities.signupCloseHoursHelp':
    'Sign-up closes automatically when this many hours remain until the reserved start time. You can also close and open it manually.',
  'activities.courtTime': 'Court time',
  'activities.courtsReservedInCalendarOne':
    '{count} court reserved in the padel-centre calendar · {when}',
  'activities.courtsReservedInCalendarMany':
    '{count} courts reserved in the padel-centre calendar · {when}',
  'activities.editInCalendar': 'Edit in the calendar',
  'activities.release': 'Release',
  'activities.noCourtsUsed': 'This activity uses no courts.',
  'activities.pickCourtsVisually':
    'Pick the time and the courts visually in the real booking calendar. Reserved courts are tagged to this activity.',
  'activities.reserveCourtsInCalendar': 'Reserve courts in the calendar',
  'activities.addSingle': 'Add a single',
  'activities.addPair': 'Add a pair',
  'activities.add': 'Add',
  'activities.addPlayerManually': 'Add a player manually',
  'activities.searchNameOrBrowse': 'Search by name — or browse the list below…',
  'activities.nounPairs': 'pairs',
  'activities.nounSingles': 'singles',
  'activities.nounPlayers': 'players',
  'activities.fillWithRandom': 'Fill with random {noun}',
  'activities.withinLevelSuffix': ' within the level',
  'activities.pairsCount': '{count} pairs',
  'activities.available': 'available',
  'activities.inLevelSuffix': ' in level',
  'activities.levelEloParen': ' (level {level}, {elo} ELO)',
  'activities.unknownLevelParen': ' (unknown level)',
  'activities.outsideLevelAsk': 'is {relation} the level requirement{label}. Add anyway?',
  'activities.addAnyway': 'Add anyway',
  'activities.quotaFullPre': 'is full. Add as',
  'activities.quotaFullPost': '(override the quota)?',
  'activities.addAsExtra': 'Add as extra',
  'activities.addAsColon': 'add as:',
  'activities.single': 'Single',
  'activities.pickPlayer2Suffix': '? Pick player 2 in the list below.',
  'activities.pickInListSuffix': '? Pick in the list.',
  'activities.underLevel': 'under level',
  'activities.overLevel': 'over level',
  'activities.mixColMen': 'Men',
  'activities.mixColWomen': 'Women',
  'activities.searchIn': 'Search {what}…',
  'activities.searchTo': 'Search for {club}…',
  'activities.searchInClub': 'Search in {club}…',
  'activities.none': 'None.',

  // ── the confirmed / waitlist roster ───────────────────────────────
  'activities.km.womanInMensClass': '⚠ A woman is playing in the men’s class',
  'activities.noPairsYet': 'No pairs yet.',
  'activities.noneYet': 'None yet.',
  'activities.singlesPairedAtStart': 'Signed up alone · paired at the start ({count})',
  'activities.oneSingleMissingPartnerInClub': 'One single is missing a partner in {club}.',
  'activities.oneSingleMissingPartner':
    'One single is missing a partner — one more is needed to make a team.',
  'activities.waitlistHeading': 'Waitlist ({what})',
  'activities.playersCount': '{count} players',
  'activities.swapPlayer': 'Substitute player',
  'activities.swappingOut': 'Substituting',
  'activities.isKeptPickNew': 'is kept. Pick a new partner:',

  // ── the admin action row ──────────────────────────────────────────
  'activities.moreOptions': '· more options',
  'activities.openSignup': 'Open sign-up',
  'activities.closeSignup': 'Close sign-up',
  'activities.sessionCompleted': 'The session is done — it now sits under «Tidligere».',
  'activities.sessionRunning': '● The session is running',
  'activities.endSession': 'End the session',
  'activities.noSignupsCanStart': 'No sign-ups yet — you can still start the session.',
  'activities.startSession': 'Start session',
  'activities.startTournament': 'Start the tournament',
  'activities.blocked.km': 'At least 2 pairs in one class to preview the club championship',
  'activities.blocked.clubTeam':
    'Needs the same number of teams in each club (at least 1 each) — now {now}',
  'activities.blocked.club': 'Needs the same number from each club (at least 2 each) — now {now}',
  'activities.blocked.mix': 'At least 2 men and 2 women to start',
  'activities.blocked.min4': 'At least 4 players to start',
  'activities.danglingSingleInClub':
    'One single is left without a partner in a club — they are not paired at the start.',

  // ── deleting one activity, or a recurring series ──────────────────
  'activities.deleteSeriesActivity': 'Delete series activity',
  'activities.partOfSeriesShort': 'Part of series',
  'activities.whatToDelete': '. What do you want to delete?',
  'activities.deleteScope.thisOnly': 'This one only',
  'activities.deleteScope.oneActivityBookingOne': '· 1 activity, {bookings} booking',
  'activities.deleteScope.oneActivityBookingMany': '· 1 activity, {bookings} bookings',
  'activities.deleteScope.thisAndFuture': 'This one and future ones',
  'activities.deleteScope.wholeSeries': 'The whole series',
  'activities.deleteScope.activitiesOne': '· {count} activity,',
  'activities.deleteScope.activitiesMany': '· {count} activities,',
  'activities.deleteScope.bookingsOne': ' {count} booking',
  'activities.deleteScope.bookingsMany': ' {count} bookings',
  'activities.scope.thisActivity': 'this activity',
  'activities.scope.thisAndFuture': 'this one and every future one in the series',
  'activities.scope.wholeSeries': 'the whole series',
  'activities.confirmDeletion': 'Confirm deletion',
  'activities.aboutToDelete': 'You are about to delete {scope}',
  'activities.deleteCountsOne': ' — {count} activity and {bookings} {bookingWord}',
  'activities.deleteCountsMany': ' — {count} activities and {bookings} {bookingWord}',
  'activities.bookingWordOne': 'court reservation',
  'activities.bookingWordMany': 'court reservations',
  'activities.cannotBeUndone': 'This cannot be undone.',
  'activities.alsoRemoveFutureBookings':
    'Also remove future court reservations (historical ones are kept).',
  'activities.yesDelete': 'Yes, delete',

  // ── the activity list ─────────────────────────────────────────────
  'activities.badge.completed': 'DONE',
  'activities.badge.inProgress': 'IN PROGRESS',
  'activities.openToAllLevels': 'Open to every level',
  'activities.levelOnly': 'Level {level}',
  'activities.eloLevelTitle': '{label} ELO level',
  'activities.seg.thisWeek': 'This week',
  'activities.seg.nextWeek': 'Next week',
  'activities.seg.later': 'Later',
  'activities.seg.noFixedTime': 'No fixed time',
  'activities.seg.past': 'Past',
  'activities.create.coaching': 'Coaching',
  'activities.create.arrangement': 'Event',
  'activities.create.newActivity': 'New activity',
  'activities.toHome': 'To the home screen',
  'activities.eyebrow.activities': 'Activities',
  'activities.eyebrow.tournaments': 'Tournaments',
  'activities.eyebrow.signups': 'Sign-ups',
  'activities.title.activities': 'Activities',
  'activities.title.tournaments': 'Tournament sign-up',
  'activities.title.signups': 'Sign-ups and activities',
  'activities.theCentre': 'the centre',
  'activities.lead.activities':
    'Coaching, events and custom activities at {club}. These can be started regardless of how many have signed up.',
  'activities.lead.tournaments':
    'Tournaments open for sign-up at {club}. Take an available place, or join the waitlist if it is full.',
  'activities.lead.signups':
    'Every sign-up and activity at {club} — tournaments and custom activities together.',
  'activities.createActivity': 'Create activity',
  'activities.tab.all': 'All',
  'activities.tab.tournaments': 'Tournaments',
  'activities.tab.activities': 'Activities',
  'activities.createPickerLead':
    'Pick a type. Custom activities are set up here; tournament formats open their own setup (with sign-up via Activities pre-selected).',
  'activities.customSection': 'Custom',
  'activities.create.coachingHint': 'Training with sign-up, courts + price',
  'activities.create.arrangementHint': 'A social event, party or similar',
  'activities.create.customHint': 'Entirely free setup',
  'activities.tournamentFormatsSection': 'Tournament formats',
  'activities.leagueNotFromHere':
    'A league is not created from here — it is started from its own module.',

  // ── the custom-activity setup form ────────────────────────────────
  'activities.newOfType': 'New {type}',
  'activities.namePlaceholder': 'e.g. Beginners course on Thursday',
  'activities.placeOptional': 'Place (optional)',
  'activities.maxParticipantsLabel': 'Max participants (empty = no limit)',
  'activities.egEight': 'e.g. 8',
  'activities.maxWaitlistLabel': 'Max on the waitlist (empty = unlimited)',
  'activities.egFour': 'e.g. 4',
  'activities.allowWaitlist': 'Allow a waitlist when it is full',
  'activities.reserveCourtsForThis': 'Reserve courts for this activity',
  'activities.reserveCourtsLater':
    'Courts can be reserved from the activity detail page after it is created.',
  'activities.descriptionOptional': 'Description (optional)',
  'activities.descriptionPlaceholder': 'Information for the participants …',
  'activities.backArrow': '← Back',

  // ── the empty state, the filters and the list cards ───────────────
  'activities.emptyTitle': 'No activities yet',
  'activities.emptyLead':
    'When a tournament is set to «Vent på påmelding» it appears here with its available places and waitlist. That is configured in the tournament setup (we start with Americano).',
  'activities.filter.sort': 'Sort',
  'activities.filter.status': 'Status',
  'activities.filter.type': 'Type',
  'activities.filter.venue': 'Padel centre',
  'activities.filter.all': 'All',
  'activities.sort.newest': 'Newest first',
  'activities.sort.oldest': 'Oldest first',
  'activities.sort.courtTime': 'Court time (first)',
  'activities.sort.name': 'Name (A–Z)',
  'activities.status.openSignup': 'Sign-up open',
  'activities.status.waitlistOpen': 'Waitlist open',
  'activities.status.closed': 'Closed',
  'activities.status.cancelled': 'Cancelled',
  'activities.resetFilters': 'Reset filters',
  'activities.visibleOfTotal': '{shown} of {total}',
  'activities.deleteAll': 'Delete all',
  'activities.wipeTitle': 'Delete ALL activities?',
  'activities.wipeLeadPre':
    'This deletes all {count} activities (including series and sign-ups) and',
  'activities.wipeLeadBold': 'releases every court reservation',
  'activities.wipeLeadPost': 'belonging to them.',
  'activities.deleting': 'Deleting…',
  'activities.yesDeleteAll': 'Yes, delete them all',
  'activities.noMatchFilter': 'No activities match the filter.',
  'activities.clickToShow': 'click to show',
  'activities.seasonStartsOn': 'Season starts {date}',
  'activities.seasonPeriodSuffix': '· season (a period)',
  'activities.priceKr': '{amount} kr',
  'activities.partOfSeriesTitle': 'Part of series{name}',
  'activities.seriesTag': '↻ Series',
  'activities.occurrenceShort': ' · time {index}',
  'activities.fullWord': 'Full',
  'activities.waitlistOpenSuffix': ' · waitlist open',
  'activities.freeCountOne': '{count} available',
  'activities.freeCountMany': '{count} available',
  'activities.onWaitlistSuffix': ' · {count}{max} on the waitlist',
  'activities.km.signUpInAClass': 'Sign up in a class',
  'activities.signedUpHeading': 'Signed up',
  'activities.pairsParen': '({count} pairs)',
  'activities.mixCountsParen': '({men}/{menMax} men · {women}/{womenMax} women)',
  'activities.playersWordSuffix': ' players',
  // ══ seriespill — league play (#310a) — the nb block carries the layout ══
  // ── the standings banner pinned inside a Banepad court pad ──────────
  'seriespill.pad.division': 'Division',
  'seriespill.pad.class': 'Class',
  'seriespill.pad.table': 'Table',
  'seriespill.pad.playedShort': '{count}m',
  'seriespill.pad.noResults': 'No results yet',
  // ── editing one round/season deadline ───────────────────────────────
  'seriespill.deadline.seasonTitle': 'Season deadline',
  'seriespill.deadline.startTitle': 'Start of round {round}',
  'seriespill.deadline.roundTitle': 'Deadline for round {round}',
  'seriespill.deadline.close': 'Close',
  'seriespill.deadline.roundStartLabel': 'Round start (date)',
  'seriespill.deadline.seasonLabel': 'Season deadline (date)',
  'seriespill.deadline.deadlineLabel': 'Deadline (date)',
  'seriespill.deadline.othersQuestion': 'The other deadlines?',
  'seriespill.deadline.keepOthers': 'Leave the others as they are',
  'seriespill.deadline.keepOthersHint': 'Changes only this one.',
  'seriespill.deadline.shiftLater': 'Move every later equivalent',
  'seriespill.deadline.shiftLaterHint':
    'Shifts every following deadline by the same amount, forwards or backwards.',
  'seriespill.deadline.cancel': 'Cancel',
  'seriespill.deadline.save': 'Save deadline',
  // ── the markdown rule-note editor ───────────────────────────────────
  'seriespill.note.defaultTitle': 'Edit rule note',
  'seriespill.note.close': 'Close',
  'seriespill.note.h1': 'Large heading',
  'seriespill.note.h2': 'Heading',
  'seriespill.note.bold': 'Bold',
  'seriespill.note.italic': 'Italic',
  'seriespill.note.bulletList': 'Bulleted list',
  'seriespill.note.bulletListLabel': '• List',
  'seriespill.note.numberedList': 'Numbered list',
  'seriespill.note.numberedListLabel': '1. List',
  'seriespill.note.quote': 'Quote',
  'seriespill.note.quoteLabel': '❝ Quote',
  'seriespill.note.insertLink': 'Insert link',
  'seriespill.note.linkLabel': '🔗 Link',
  'seriespill.note.insertImage': 'Insert image (URL)',
  'seriespill.note.imageLabel': '🖼 Image',
  'seriespill.note.image': 'Image',
  'seriespill.note.link': 'Link',
  'seriespill.note.altPlaceholder': 'Image caption (alt)',
  'seriespill.note.linkTextPlaceholder': 'Link text',
  'seriespill.note.urlPlaceholder': 'https://…',
  'seriespill.note.urlLabel': 'Address (URL)',
  'seriespill.note.insert': 'Insert',
  'seriespill.note.cancel': 'Cancel',
  'seriespill.note.save': 'Save note',
  'seriespill.note.editorPlaceholder':
    'Write a rule note. Markdown: **bold**, *italic*, # heading, - bullet, 1. numbered, > quote, [text](url), ![alt](image-url).',
  // The toolbar writes these into the note itself when nothing is selected,
  // so they are scaffolding the operator overwrites — not stored business data.
  'seriespill.note.phText': 'text',
  'seriespill.note.phBold': 'bold text',
  'seriespill.note.phItalic': 'italic',
  'seriespill.note.phImage': 'image',
  'seriespill.note.phLink': 'link',
  // ── the mid-season substitution badge (mirrors players.replacementBadge.*) ───
  'seriespill.replacement.playerFallback': 'Player',
  'seriespill.replacement.kicker': 'Substitute',
  'seriespill.replacement.buttonTitle': 'Substitute — click for details',
  'seriespill.replacement.title': 'Player swapped mid-season',
  'seriespill.replacement.unknownTime': 'unknown time',
  'seriespill.replacement.swappedOut': 'Swapped out',
  'seriespill.replacement.replacedBy': 'Replaced by',
  'seriespill.replacement.time': 'Time',
  'seriespill.replacement.matchesPlayedLabel': 'Matches played when the swap happened',
  'seriespill.replacement.matchesProgress': '{done} of {total} matches done',
  'seriespill.replacement.firstMatchFor': 'First match for {name}',
  'seriespill.replacement.theSubstitute': 'the substitute',
  'seriespill.replacement.round': 'Round {round}',
  'seriespill.replacement.upcoming': 'Upcoming',
  'seriespill.replacement.close': 'Close',
  // ── shared across the league screens ────────────────────────────────
  'seriespill.common.genderMen': 'Men',
  'seriespill.common.genderWomen': 'Women',
  'seriespill.common.genderMix': 'Mixed',
  // The standings-table column heads. Norwegian abbreviates the words
  // (S/V/U/T/P); English uses the football-table convention, where P is
  // PLAYED and points are Pts — so they cannot both be «P».
  'seriespill.table.pair': 'Pair',
  'seriespill.table.played': 'P',
  'seriespill.table.wins': 'W',
  'seriespill.table.draws': 'D',
  'seriespill.table.losses': 'L',
  'seriespill.table.points': 'Pts',
  // ── the archived-seasons browser ────────────────────────────────────
  'seriespill.history.title': '{name} — Season {season}',
  'seriespill.history.allTitle': 'Past seasons',
  'seriespill.history.empty': 'No past seasons yet.',
  'seriespill.history.season': 'Season {season}',
  'seriespill.history.divisionCountOne': '{count} division',
  'seriespill.history.divisionCountMany': '{count} divisions',
  'seriespill.history.tabTable': '🏆 Table',
  'seriespill.history.tabMatches': '🎾 Matches',
  'seriespill.history.noData': 'No data.',
  'seriespill.history.round': 'Round {round}',
  'seriespill.history.drawShort': 'D',
  'seriespill.history.noMatches': 'No matches played.',
  'seriespill.history.back': 'Back to all seasons',
  // ── one player's league profile ─────────────────────────────────────
  'seriespill.player.nameFallback': 'Player',
  'seriespill.player.kicker': 'Player profile · league',
  'seriespill.player.swappedOutTag': '⊘ SWAPPED OUT',
  'seriespill.player.substituteTag': '↻ SUBSTITUTE',
  'seriespill.player.substituteTitle': 'Substitute — brought in this season',
  'seriespill.player.eloInSeason': '({delta} in the league)',
  'seriespill.player.pairChip': '{cls} · {div} · with {partner}',
  'seriespill.player.swapAction': 'Substitute the player',
  'seriespill.player.fullProfile': 'Full profile',
  'seriespill.player.notInSeries': 'This player is not in this league.',
  'seriespill.player.statWinLoss': 'Wins-losses',
  'seriespill.player.statSets': 'Sets',
  'seriespill.player.statPlayed': 'Matches played',
  'seriespill.player.statElo': 'ELO in the league',
  'seriespill.player.eloChartTitle': 'ELO progression in the league',
  'seriespill.player.eloChartCaption': 'After each of {count} completed matches',
  // The ELO chart axis labels. «K» is kamp — the English one cannot be a K.
  'seriespill.player.eloStart': 'Start',
  'seriespill.player.eloAfterMatch': 'M{n}',
  'seriespill.player.upcomingHeader': 'Upcoming matches ({count})',
  'seriespill.player.completedHeader': 'Completed ({count})',
  'seriespill.player.latestFirst': 'Latest first',
  'seriespill.player.noMatches': 'No matches found for this player yet.',
  'seriespill.player.voided': 'Voided',
  'seriespill.player.notPlayed': 'Not played',
  'seriespill.player.lossWo': 'Loss (WO)',
  'seriespill.player.draw': 'Draw',
  'seriespill.player.win': 'Win',
  'seriespill.player.loss': 'Loss',
  // The countdown under an upcoming match deadline.
  'seriespill.player.overdue': 'Overdue',
  'seriespill.player.today': 'Today!',
  'seriespill.player.daysOne': '{days} day',
  'seriespill.player.daysMany': '{days} days',
  'seriespill.player.inMonths': 'in {months} mo',
  // Weekday and month names the league screens spell out by hand, plus the
  // date shape they build from them. DISPLAY ONLY — never stored or compared
  // (docs/architecture/DOMAIN_RULES.md §4). See components/seriespill/dateNames.ts.
  'seriespill.date.weekdayLong0': 'Sunday',
  'seriespill.date.weekdayLong1': 'Monday',
  'seriespill.date.weekdayLong2': 'Tuesday',
  'seriespill.date.weekdayLong3': 'Wednesday',
  'seriespill.date.weekdayLong4': 'Thursday',
  'seriespill.date.weekdayLong5': 'Friday',
  'seriespill.date.weekdayLong6': 'Saturday',
  'seriespill.date.weekdayShort0': 'Sun',
  'seriespill.date.weekdayShort1': 'Mon',
  'seriespill.date.weekdayShort2': 'Tue',
  'seriespill.date.weekdayShort3': 'Wed',
  'seriespill.date.weekdayShort4': 'Thu',
  'seriespill.date.weekdayShort5': 'Fri',
  'seriespill.date.weekdayShort6': 'Sat',
  'seriespill.date.monthShort0': 'Jan',
  'seriespill.date.monthShort1': 'Feb',
  'seriespill.date.monthShort2': 'Mar',
  'seriespill.date.monthShort3': 'Apr',
  'seriespill.date.monthShort4': 'May',
  'seriespill.date.monthShort5': 'Jun',
  'seriespill.date.monthShort6': 'Jul',
  'seriespill.date.monthShort7': 'Aug',
  'seriespill.date.monthShort8': 'Sep',
  'seriespill.date.monthShort9': 'Oct',
  'seriespill.date.monthShort10': 'Nov',
  'seriespill.date.monthShort11': 'Dec',
  'seriespill.date.long': '{wd} {day} {mon} {year}',
  // ── next season's sign-up and payment tracking ──────────────────────
  'seriespill.signup.intro':
    'Every class has its own season, price and sign-up. Set the price per team, publish the next season to Activities with a start date, and follow the payments — all per class.',
  'seriespill.signup.noClasses': 'No classes yet.',
  'seriespill.signup.classHeading': '{name} — season {season}',
  'seriespill.signup.classSub':
    'Sign-up for next season. The current season ({season}) is under way.',
  'seriespill.signup.teamsPaid': '{paid}/{total} teams paid',
  'seriespill.signup.incomeSuffix': ' · {amount} kr in',
  'seriespill.signup.pricePerTeam': 'Price per team (kr)',
  'seriespill.signup.pricePlaceholder': 'e.g. 1500',
  'seriespill.signup.savePrice': 'Save price',
  'seriespill.signup.feeSaved': 'Season fee saved',
  'seriespill.signup.seasonFeeWord': 'season fee',
  'seriespill.signup.free': 'Free',
  'seriespill.signup.feeKr': '{fee} kr',
  'seriespill.signup.inActivities': 'Sign-up in Activities',
  'seriespill.signup.published': '✓ Published',
  'seriespill.signup.seasonStart': 'Season start',
  'seriespill.signup.signupDeadline': 'Sign-up deadline (optional)',
  'seriespill.signup.updateInActivities': 'Update in Activities',
  'seriespill.signup.publishToActivities': 'Publish to Activities →',
  'seriespill.signup.seeInActivities': 'See it in Activities →',
  'seriespill.signup.remove': 'Remove',
  'seriespill.signup.publishedStart': 'Published, starting {date}',
  'seriespill.signup.publishedDeadline': ' · deadline {date}',
  'seriespill.signup.publishedNewTeams': ' {count} new teams signed up through Activities.',
  'seriespill.signup.noTeams': 'No teams in this class yet.',
  'seriespill.signup.withdrawnSuffix': ' · withdrawn',
  'seriespill.signup.paidSuffix': ' · paid {date}',
  'seriespill.signup.paidBySelf': ' (self-service)',
  'seriespill.signup.paidByAdmin': ' (admin)',
  'seriespill.signup.withdrawnTag': 'Withdrawn',
  'seriespill.signup.rejoin': 'Sign up again',
  'seriespill.signup.cancel': 'Cancel',
  'seriespill.signup.undo': 'undo',
  'seriespill.signup.undoAction': 'Undo',
  'seriespill.signup.undoWithdrawTitle': 'Undo the withdrawal',
  'seriespill.signup.undoPaidTitle': 'Admin: undo',
  'seriespill.signup.signedUpTag': 'Signed up',
  'seriespill.signup.paidTag': 'Paid',
  'seriespill.signup.unpaidTag': 'Not paid',
  'seriespill.signup.selfPayTitle': 'Self-service payment (DEMO)',
  'seriespill.signup.confirmFree': 'Confirm (free)',
  'seriespill.signup.pay': 'Pay {fee}',
  'seriespill.signup.withdraw': 'Withdraw',
  'seriespill.signup.withdrawTitle': 'The team withdraws from next season',
  'seriespill.signup.markPaid': 'mark as paid',
  'seriespill.signup.closeMore': 'Close',
  'seriespill.signup.markPaidTitle': 'Admin: mark as paid without a payment flow',
  'seriespill.signup.more': 'more ▾',
  'seriespill.signup.moreTitle': 'More options',
  // ── starting the next season: promotion, relegation and placement ───
  'seriespill.transition.title': '{name} — start season {season}',
  'seriespill.transition.lead':
    'Season {season} for {name} is finished — confirm the placement, adjust if you need to, and start the next season.',
  'seriespill.transition.close': 'Close',
  // The short level badge stays «N{level}» in English too — see mobile.activities.myLevelFilter.
  'seriespill.transition.levelTag': 'N{level}',
  'seriespill.transition.levelTagTitle': '{name}: Level {level} — {levelName}',
  'seriespill.transition.avgElo': 'avg {avg}',
  'seriespill.transition.avgEloTitle': "The team's average ELO",
  'seriespill.transition.divAvg': 'Avg {avg}',
  'seriespill.transition.divAvgTitle':
    "The division's average rating (as proposed for next season)",
  'seriespill.transition.pairsNextSeason': '{count} pairs into next season',
  'seriespill.transition.newTag': 'new',
  'seriespill.transition.promotedTag': '↑ promoted',
  'seriespill.transition.relegatedTag': '↓ relegated',
  'seriespill.transition.manualUpTag': '✋ Moved up by hand ↑',
  'seriespill.transition.manualUpTitle': 'Moved up by hand from {div}',
  'seriespill.transition.manualDownTag': '✋ Moved down by hand ↓',
  'seriespill.transition.manualDownTitle': 'Moved down by hand from {div}',
  'seriespill.transition.suggestedDivFallback': 'the suggested division',
  'seriespill.transition.moveTitle': 'Move the pair to another division',
  'seriespill.transition.remove': 'Remove',
  'seriespill.transition.removeTitle': 'Remove the pair (drop-out)',
  'seriespill.transition.emptyDivision':
    'No pairs in this division — the season will have no matches here.',
  'seriespill.transition.falloutHeading': 'Dropped out of season {season}',
  'seriespill.transition.withdrawnTag': 'withdrawn',
  'seriespill.transition.unpaidTag': 'not paid',
  'seriespill.transition.putBackIn': 'Put back into…',
  'seriespill.transition.moveLabel': 'Move {pair} to division',
  'seriespill.transition.putBackInLabel': 'Put {pair} back into a division',
  'seriespill.transition.placeInLabel': 'Place {pair} into a division',
  'seriespill.transition.player1Label': 'Player 1',
  'seriespill.transition.player2Label': 'Player 2',
  'seriespill.transition.divisionLabel': 'Division',
  'seriespill.transition.falloutNote':
    'Dropped-out pairs are left out of next season unless you put them back. The places stay empty — no other pair is moved automatically.',
  'seriespill.transition.falloutNoteUnpaid':
    ' Teams marked «not paid» are left out because the season fee is unpaid — they count as dropped out, but can be put back by hand if needed.',
  'seriespill.transition.unplacedHeading': 'Not placed yet — new teams from the sign-up ({count})',
  'seriespill.transition.placeIn': 'Place into…',
  'seriespill.transition.unplacedNote':
    'Place each new team in the right division from its average ELO and the players’ levels — so the strongest do not end up in the bottom division.',
  'seriespill.transition.addPairHeading': 'Add a new pair',
  'seriespill.transition.noFreePlayers':
    'No players left to form a new pair from (everyone is already placed).',
  'seriespill.transition.player1': 'Player 1…',
  'seriespill.transition.player2': 'Player 2…',
  'seriespill.transition.add': 'Add',
  'seriespill.transition.pairAvgLabel': 'Pair average: ',
  'seriespill.transition.suggestedDivLabel': ' · suggested division: ',
  'seriespill.transition.suggestedDivTail':
    ' ★ (closest average) — only a suggestion, you can choose freely.',
  // «nytte» is what the screen says today (`nytt` + `e`) and the migration keeps
  // it — see issue #513. Fixing the wording here would change the rendered
  // Norwegian, which is not what this PR is for.
  'seriespill.transition.unplacedWarnOne':
    '{count} new team is not placed yet. Place them all (or leave them unassigned) before you start the season.',
  'seriespill.transition.unplacedWarnMany':
    '{count} new teams are not placed yet. Place them all (or leave them unassigned) before you start the season.',
  'seriespill.transition.reset': 'Reset',
  'seriespill.transition.resetTitle': "Reset to the engine's promotion/relegation proposal",
  'seriespill.transition.cancel': 'Cancel',
  'seriespill.transition.start': 'Start season {season} →',
  'seriespill.transition.startDisabledTitle': 'Place every new team first',
  // ── the generated rule summary ──────────────────────────────────────
  'seriespill.rules.title': 'League rules',
  'seriespill.rules.subtitle': 'Generated from the setup',
  'seriespill.rules.noClasses': 'No classes.',
  'seriespill.rules.sharedRules': 'Shared rules',
  'seriespill.rules.sharedRulesHint': 'apply to every class',
  'seriespill.rules.perClass': 'Specific to a class',
  'seriespill.rules.overriddenCount': '{count} overridden',
  'seriespill.rules.divisionFallback': 'division {level}',
  // A per-division line: the division, then the rule that applies to it.
  'seriespill.rules.divLine': '{div}: {text}',
  'seriespill.rules.divLineDot': '{div}: {text}.',
  // Section headings. They are also the KEYS of the icon/colour map in
  // SeriespillRulesModal, which is built from these same entries.
  'seriespill.rules.secFormat': 'Format',
  'seriespill.rules.secMatchFormat': 'Match format',
  'seriespill.rules.secDeuce': 'Deciding at 40–40',
  'seriespill.rules.secPoints': 'Points in the table',
  'seriespill.rules.secReveal': 'Announcing rounds',
  'seriespill.rules.secDeadlines': 'Deadlines',
  'seriespill.rules.secUnplayed': 'Unplayed matches',
  'seriespill.rules.secCourt': 'Court and time',
  'seriespill.rules.secMixServe': 'Serving in Mixed',
  'seriespill.rules.secUpDown': 'Promotion and relegation',
  'seriespill.rules.secRating': 'Rating (ELO)',
  'seriespill.rules.secInOut': 'Disagreeing on in or out',
  'seriespill.rules.secOwnRules': 'Own rules',
  // The match formats. «super-tiebreak» is a super TIE-BREAK in English
  // (ITF style, hyphenated) and a game is a game inside a set — never a match.
  'seriespill.rules.fmtBo3Super':
    'Best of 3 sets. At one set all, the match is decided by a super tie-break to 10 points (win by 2).',
  'seriespill.rules.fmtBo3Full':
    'Best of 3 sets. A deciding third set is played as a full set to 6 games.',
  'seriespill.rules.fmtBo1Set6': 'One set to 6 games (win by 2; tie-break at 6–6).',
  'seriespill.rules.fmtBo1Short': 'One short set to 4 games (win by 2).',
  'seriespill.rules.formatRoundRobin':
    'Fixed pairs play a round robin (everyone against everyone) inside their division.',
  'seriespill.rules.formatDouble': 'Double round: every pair meets twice (home and away).',
  'seriespill.rules.formatSingle': 'Single round: every pair meets once.',
  'seriespill.rules.pointsWinLoss': 'A win gives {win} points, a loss gives {loss} points.',
  'seriespill.rules.pointsWalkover': 'A walkover (WO) gives the winner {points} points.',
  'seriespill.rules.pointsRanking':
    'The table is ranked on points, then set difference, then wins.',
  'seriespill.rules.revealRollingOne':
    'The schedule is announced as it goes: up to {count} round ahead is shown at a time.',
  'seriespill.rules.revealRollingMany':
    'The schedule is announced as it goes: up to {count} rounds ahead are shown at a time.',
  'seriespill.rules.revealLead': ' A round opens {value} {unit} before it starts.',
  'seriespill.rules.revealPlayedAlways':
    'Played matches are always shown, even if they lie further ahead.',
  'seriespill.rules.revealAll': 'Every round and the whole schedule are visible from the start.',
  // Time units, spelled out because the sentences above build them by hand.
  'seriespill.rules.unitDay': 'day',
  'seriespill.rules.unitDays': 'days',
  'seriespill.rules.unitWeek': 'week',
  'seriespill.rules.unitWeeks': 'weeks',
  'seriespill.rules.unitMonth': 'month',
  'seriespill.rules.unitMonths': 'months',
  'seriespill.rules.seasonStart': 'Season start: {date}.',
  'seriespill.rules.seasonDeadline': 'The whole season must be finished by {date}.',
  'seriespill.rules.roundInterval': 'A new round opens every {value} {unit}.',
  'seriespill.rules.noDeadlines':
    'No fixed round deadlines — play the matches when it suits, within the season.',
  'seriespill.rules.unplayedVoid':
    'Matches not played when a round closes are voided (they do not count).',
  'seriespill.rules.unplayedWalkover':
    'Matches not played when a round closes count as a 0–2 loss for both pairs (double walkover).',
  'seriespill.rules.forfeitAuto': 'The round closes automatically when its deadline passes.',
  'seriespill.rules.forfeitManual': 'The round is closed by an administrator.',
  'seriespill.rules.courtAllocated': 'Courts are allocated on fixed playing days.',
  'seriespill.rules.courtSelfBooked':
    'The pairs agree a time between themselves and book a court for each match.',
  'seriespill.rules.mixServe': 'Women serve to women and men to men.',
  'seriespill.rules.upDownSingleDivision': 'Only one division — no promotion or relegation.',
  'seriespill.rules.upDownAuto':
    'After the season the best teams move up a division and the weakest move down.',
  'seriespill.rules.upDownAutoCount':
    'How many is worked out automatically from the number of teams in the division (fewer teams = fewer up/down, more teams = more).',
  'seriespill.rules.upDownAutoDiv':
    '{div}: up/down is worked out automatically from the number of teams.',
  'seriespill.rules.upDownFixedDiv': '{div}: {up} up / {down} down.',
  'seriespill.rules.ratingWeight':
    "The results count towards the players' ELO rating. League play is weighted heavily ({percent} %).",
  'seriespill.rules.inOut':
    'If you disagree on whether the ball was in or out, the point is replayed.',
  'seriespill.rules.noteHeading': 'Rule note',
  'seriespill.rules.noteShared': 'Shared rule note',
  'seriespill.rules.noteAppend': 'Addition · {name}',
  'seriespill.rules.noteOverride': 'Rule note · {name}',
  'seriespill.rules.noteOverrideSuffix': ' (replaces the shared one)',
  // ── per-class settings: overrides, divisions and pairs ──────────────
  'seriespill.classSettings.title': 'Settings — {name}',
  'seriespill.classSettings.lead':
    'By default the class follows the league settings. Switch on «Override» per field to differ.',
  'seriespill.classSettings.close': 'Close',
  'seriespill.classSettings.override': 'Override',
  'seriespill.classSettings.overridden': '✓ Overridden',
  'seriespill.classSettings.overrideOnTitle': 'Override the league default for this class',
  'seriespill.classSettings.overrideResetTitle': 'Click to reset to the league default',
  'seriespill.classSettings.followsMain': 'Follows the main settings.',
  'seriespill.classSettings.confirmDeleteDivision': 'Delete this division and every pair in it?',
  'seriespill.classSettings.tabSettings': 'Settings',
  'seriespill.classSettings.tabBooking': 'Court booking',
  'seriespill.classSettings.tabDivisions': 'Divisions & players',
  'seriespill.classSettings.secCourtMode': 'Court mode',
  'seriespill.classSettings.courtFree': 'Free booking',
  'seriespill.classSettings.courtFreeHint': 'The pairs agree and book a court themselves.',
  'seriespill.classSettings.courtAllocated': 'Allocated courts',
  'seriespill.classSettings.courtAllocatedHint': 'Courts are set aside on fixed days (calendar).',
  'seriespill.classSettings.allocNoteA': 'Playing days and courts are set up',
  'seriespill.classSettings.allocNoteSlot': 'per time slot',
  'seriespill.classSettings.allocNoteB': ' under',
  'seriespill.classSettings.allocNotePath': 'Main settings → Court booking',
  'seriespill.classSettings.allocNoteC':
    '— that is where you pick which classes each time slot covers.',
  'seriespill.classSettings.inSlots': 'This class is included in: {list}.',
  'seriespill.classSettings.noSlots':
    '⚠ The class is not in any time slot yet — it gets no slots until you add it to one under Court booking.',
  'seriespill.classSettings.saveAndOpenBooking': 'Save and open Court booking →',
  'seriespill.classSettings.secDates': 'Dates & round deadline',
  'seriespill.classSettings.rollingDeadlines': 'Rolling round deadlines',
  'seriespill.classSettings.fixedDeadline': 'Fixed season deadline',
  'seriespill.classSettings.seasonStart': 'Season start',
  'seriespill.classSettings.seasonDeadline': 'Season deadline',
  'seriespill.classSettings.estimatedEnd': 'Estimated season end',
  'seriespill.classSettings.estimatedEndNA': 'Worked out once there are pairs and rounds',
  'seriespill.classSettings.roundDeadlineRolling': 'Round deadline (rolling)',
  'seriespill.classSettings.every': 'Every',
  'seriespill.classSettings.unitDays': 'day(s)',
  'seriespill.classSettings.unitWeeks': 'week(s)',
  'seriespill.classSettings.unitMonths': 'month(s)',
  'seriespill.classSettings.autoDeadlineNote':
    'The season deadline is set automatically to the estimated season end',
  'seriespill.classSettings.autoDeadlineDate': ' ({date})',
  'seriespill.classSettings.secUnplayed': 'Unplayed matches & closing',
  'seriespill.classSettings.unplayedLabel': 'Unplayed matches when a round closes',
  'seriespill.classSettings.unplayedWo': 'Both lose 0–2 (WO)',
  'seriespill.classSettings.unplayedWoHint': 'Both teams get a loss. Does not affect ELO.',
  'seriespill.classSettings.unplayedDraw': 'Draw',
  'seriespill.classSettings.unplayedDrawHint':
    'Recorded as a draw. 0 points each, counts as played. Does not affect ELO.',
  'seriespill.classSettings.roundEndLabel': 'Closing a round',
  'seriespill.classSettings.roundEndManual': 'By hand',
  'seriespill.classSettings.roundEndManualHint':
    'You close the round yourself with the «Close round» button in the schedule.',
  'seriespill.classSettings.roundEndAuto': 'Automatically at the deadline',
  'seriespill.classSettings.roundEndAutoHint':
    'The round closes automatically when its deadline passes. Unplayed matches get the result chosen above.',
  'seriespill.classSettings.secReveal': 'Announcing rounds',
  'seriespill.classSettings.revealAll': 'Every round',
  'seriespill.classSettings.revealAllHint': 'The whole schedule is visible from the start.',
  'seriespill.classSettings.revealRolling': 'Before each round',
  'seriespill.classSettings.revealRollingHint':
    'Future rounds are announced just before they start.',
  'seriespill.classSettings.announce': 'Announce',
  'seriespill.classSettings.beforeRoundStarts': 'before the round starts',
  'seriespill.classSettings.show': 'Show',
  'seriespill.classSettings.roundsAhead': 'round(s) ahead',
  'seriespill.classSettings.secRoundRobin': 'Round-robin format',
  'seriespill.classSettings.singleRound': 'Single',
  'seriespill.classSettings.doubleRound': 'Double',
  'seriespill.classSettings.homeAway': '(h/a)',
  'seriespill.classSettings.everyPairMeets': 'Every pair meets every other ',
  'seriespill.classSettings.onceStrong': 'once',
  'seriespill.classSettings.twiceStrong': 'twice',
  'seriespill.classSettings.secMatchFormat': 'Match format',
  'seriespill.classSettings.fmtSuperTiebreak': 'Super tie-break',
  'seriespill.classSettings.fmtSuperTiebreakHint':
    'Best of 3, super tie-break to 10 at one set all (FIP).',
  'seriespill.classSettings.fmtFullThird': 'Full third set',
  'seriespill.classSettings.fmtFullThirdHint': 'Best of 3, a full third set to 6.',
  'seriespill.classSettings.secDeuce': 'Scoring at 40–40',
  'seriespill.classSettings.deuceAdvantage': 'Advantage',
  'seriespill.classSettings.deuceGolden': 'Golden point',
  // Starpoint is the product, not a word — it is never translated.
  'seriespill.classSettings.deuceStarpoint': 'Starpoint',
  'seriespill.classSettings.secMixServe': 'Mixed: serving rule',
  'seriespill.classSettings.mixServeFree': 'Free',
  'seriespill.classSettings.mixServeSame': 'Woman↔woman',
  'seriespill.classSettings.mixServeHint': 'Women serve to women, men to men.',
  'seriespill.classSettings.ruleNote': 'Rule note',
  'seriespill.classSettings.noteInherit': 'Follow the shared one',
  'seriespill.classSettings.noteAppend': 'Add to it',
  'seriespill.classSettings.noteOverride': 'Override',
  'seriespill.classSettings.noteInheritHint': 'The class uses the shared rule note.',
  'seriespill.classSettings.noteAppendHint':
    'Shown in addition to the shared rule note, marked for this class.',
  'seriespill.classSettings.noteOverrideHint': 'Replaces the shared rule note for this class.',
  'seriespill.classSettings.fullscreenEditor': '✎ Full-screen editor',
  'seriespill.classSettings.notePlaceholder':
    'Write freely, or use the full-screen editor. Markdown: **bold**, *italic*, # heading, - bullet, [text](url), ![alt](image-url).',
  'seriespill.classSettings.noteEditorTitle': 'Rule note — {name}',
  'seriespill.classSettings.classFallback': 'class',
  'seriespill.classSettings.secEloWeight': 'ELO weight',
  'seriespill.classSettings.eloWeightHint':
    "Decides how much this class's matches move the players' ELO. The league default is 200 %.",
  'seriespill.classSettings.secAutoMode': 'Auto mode (season transition)',
  'seriespill.classSettings.autoMode': '⚡ Auto mode',
  'seriespill.classSettings.autoModeHint':
    'When the class locks (everyone has played, or the deadline passes) the next season starts automatically without an admin confirming it.',
  'seriespill.classSettings.divisionsHeading': 'Divisions & sign-up',
  'seriespill.classSettings.demo': 'Demo',
  'seriespill.classSettings.demoTargetTitle':
    'Where the demo pairs go: into one chosen division, or N pairs into EVERY division',
  'seriespill.classSettings.demoAllDivisions': 'in every division',
  'seriespill.classSettings.demoInDivision': 'in {name}',
  'seriespill.classSettings.demoAddPairs': '+ {count} pairs',
  'seriespill.classSettings.demoPairsAllTitle':
    '{count} pairs from available players in EVERY division',
  'seriespill.classSettings.demoPairsOneTitle':
    '{count} pairs from available players in the chosen division',
  'seriespill.classSettings.addDivision': '+ Add division',
  'seriespill.classSettings.addPairHeading': 'Add a pair',
  'seriespill.classSettings.playerSlot': 'Player {slot}',
  'seriespill.classSettings.pickBelow': '(pick below)',
  'seriespill.classSettings.newSuffix': ' · New',
  'seriespill.classSettings.clearPlayer': 'Clear the chosen player',
  'seriespill.classSettings.searchBelow': 'Search below to choose…',
  'seriespill.classSettings.putIn': 'Put into',
  'seriespill.classSettings.movePairLabel': 'Move {pair} to division',
  'seriespill.classSettings.divisionNameLabel': 'Division name',
  'seriespill.classSettings.intervalCountLabel': 'Number',
  'seriespill.classSettings.intervalUnitLabel': 'Unit',
  'seriespill.classSettings.searchPlayer': 'Search for player {slot}…',
  'seriespill.classSettings.newPlayer': '+ New player',
  'seriespill.classSettings.includeWomen':
    'Search among women too (women may play in the men’s class)',
  'seriespill.classSettings.noMatchesA': 'No matches for "',
  'seriespill.classSettings.noMatchesB': '".',
  'seriespill.classSettings.createPlayer': 'Create "{name}" as a new player',
  'seriespill.classSettings.noFreePlayers': 'No players left — everyone is already picked.',
  'seriespill.classSettings.genderMale': '♂ Man',
  'seriespill.classSettings.genderFemale': '♀ Woman',
  'seriespill.classSettings.genderMix': '⚥ Mixed',
  'seriespill.classSettings.newTag': 'NEW',
  'seriespill.classSettings.pick': 'Pick →',
  'seriespill.classSettings.addPair': '🤝 Add the pair',
  'seriespill.classSettings.pairCount': '{count} pairs',
  'seriespill.classSettings.delete': 'Delete',
  'seriespill.classSettings.upDown': 'Promotion/relegation',
  'seriespill.classSettings.upDownAutoTitle':
    'Auto: worked out from the number of teams in the division. Switch it off to set your own numbers.',
  'seriespill.classSettings.upDownAuto': '⚙ Auto',
  'seriespill.classSettings.upDownManual': 'Manual',
  'seriespill.classSettings.autoUpCount': '{count} up',
  'seriespill.classSettings.autoDownCount': '{count} down',
  'seriespill.classSettings.teamCountSuffix': ' · {count} teams',
  'seriespill.classSettings.promote': 'Promoted',
  'seriespill.classSettings.relegate': 'Relegated',
  'seriespill.classSettings.divDeuce': 'Scoring (40–40)',
  'seriespill.classSettings.inherit': 'Inherit',
  'seriespill.classSettings.deuceGoldenShort': 'Gold',
  'seriespill.classSettings.deuceStarpointShort': 'Star',
  'seriespill.classSettings.fmtSuperTieShort': 'Super tie',
  'seriespill.classSettings.fmtFullThirdShort': 'Full 3rd',
  'seriespill.classSettings.divMixServe': 'Mixed serve',
  'seriespill.classSettings.on': 'On',
  'seriespill.classSettings.off': 'Off',
  'seriespill.classSettings.divRuleNote': 'Rule note (division)',
  'seriespill.classSettings.divRuleNotePlaceholder': "Optional — shown in the division's rules",
  'seriespill.classSettings.noPairs': 'No pairs. Use «Add a pair» above.',
  'seriespill.classSettings.removePair': 'Remove',
  'seriespill.classSettings.cancel': 'Cancel',
  'seriespill.classSettings.save': 'Save changes',
  // ── the league-wide (shared) settings ───────────────────────────────
  'seriespill.mainSettings.title': 'Main settings',
  'seriespill.mainSettings.lead':
    'These apply to the whole league. Classes inherit them unless they override.',
  'seriespill.mainSettings.close': 'Close',
  'seriespill.mainSettings.tabSettings': '⚙ Settings',
  'seriespill.mainSettings.tabBooking': '🎾 Court booking',
  'seriespill.mainSettings.name': 'League name',
  // The warning shown when a main setting moves under a class override.
  'seriespill.mainSettings.ovrMatchFormat': 'Match format',
  'seriespill.mainSettings.ovrDeuce': 'Scoring at 40–40',
  'seriespill.mainSettings.ovrMixServe': 'Mixed serving rule',
  'seriespill.mainSettings.ovrRoundRobin': 'Round robin',
  'seriespill.mainSettings.ovrEloMultiplier': 'ELO multiplier',
  'seriespill.mainSettings.ovrUnplayed': 'Unplayed matches',
  'seriespill.mainSettings.ovrReveal': 'Announcing',
  'seriespill.mainSettings.ovrRuleNote': 'Rule note',
  'seriespill.mainSettings.ovrDialogTitle': 'Some classes override this',
  'seriespill.mainSettings.ovrDialogLead':
    'You changed settings that some classes override. Decide per class whether it keeps its own value or follows the new main setting.',
  'seriespill.mainSettings.ovrClassValue': "The class's value: ",
  'seriespill.mainSettings.ovrMainValue': ' · New main setting:',
  'seriespill.mainSettings.ovrKeep': "Keep the class's",
  'seriespill.mainSettings.ovrFollow': 'Follow the main setting',
  'seriespill.mainSettings.ovrConfirm': 'Confirm and save',
  'seriespill.mainSettings.fmtFullSet': 'Full set',
  'seriespill.mainSettings.fmtOneSet': 'One set',
  'seriespill.mainSettings.fmtShortSet': 'Short set',
  'seriespill.mainSettings.courtModeHeading': 'Court mode',
  'seriespill.mainSettings.courtFree': 'Free booking',
  'seriespill.mainSettings.courtFreeHint': 'The pairs agree and book a court themselves.',
  'seriespill.mainSettings.courtAllocated': 'Allocated courts',
  'seriespill.mainSettings.courtAllocatedHint': 'Courts are set aside on fixed days (calendar).',
  'seriespill.mainSettings.perksHeading': 'Free booking — perks for league teams',
  'seriespill.mainSettings.perksLead':
    'When a team books a court for a league match from the schedule, it can get a discount on the ordinary court price and book further ahead than ordinary members. Applies to every class on free booking.',
  'seriespill.mainSettings.discount': 'Discount (%)',
  'seriespill.mainSettings.discountPlaceholder': 'e.g. 10',
  'seriespill.mainSettings.window': 'Booking window (days ahead)',
  'seriespill.mainSettings.windowPlaceholder': 'e.g. 14',
  'seriespill.mainSettings.maxActive': 'Max active reservations per player',
  'seriespill.mainSettings.maxActivePlaceholder': 'e.g. 2',
  'seriespill.mainSettings.maxActiveLabel': 'Max active reservations:',
  'seriespill.mainSettings.maxActiveA': ' Limits how many',
  'seriespill.mainSettings.maxActiveEm': 'upcoming (unplayed)',
  'seriespill.mainSettings.maxActiveB':
    ' courts a team may have reserved at once. The limit applies to ',
  'seriespill.mainSettings.maxActiveStrong': 'each of the four players',
  'seriespill.mainSettings.maxActiveC':
    ' in the match — if one of them already has that many upcoming reservations, they must play one off before they can reserve a court for the next match. Empty = no limit.',
  'seriespill.mainSettings.emptyFieldsNote':
    'An empty field = no discount / the standard booking window (7 days) / no limit. Free booking only.',
  'seriespill.mainSettings.perClassHeading': 'Override per class (optional)',
  'seriespill.mainSettings.perClassNote':
    'An empty field uses the league default above ({disc}% discount / {win} days).',
  'seriespill.mainSettings.discountShort': 'Discount %',
  'seriespill.mainSettings.windowShort': 'Days ahead',
  'seriespill.mainSettings.matchDuration': 'Length per match (shared)',
  'seriespill.mainSettings.minutes': '{min} min',
  'seriespill.mainSettings.bookingLinkLead': 'Playing days and courts are set up under',
  'seriespill.mainSettings.bookingLinkLabel': 'Court booking',
  'seriespill.mainSettings.slotRoomCount': '. {count} time slots set up.',
  'seriespill.mainSettings.seasonDefaults': 'Default season settings',
  'seriespill.mainSettings.seasonDefaultsHint': 'Classes use these unless they override them.',
  'seriespill.mainSettings.seasonStart': 'Season start',
  'seriespill.mainSettings.fixedCalendarNote':
    '📆 Fixed calendar: matches take fixed dates, times and courts from the booking calendar when they are reserved. No round deadline.',
  'seriespill.mainSettings.rollingDeadlines': 'Rolling round deadlines',
  'seriespill.mainSettings.fixedDeadline': 'Fixed season deadline',
  'seriespill.mainSettings.seasonDeadline': 'Season deadline',
  'seriespill.mainSettings.estimatedEnd': 'Estimated season end',
  'seriespill.mainSettings.estimatedEndNA': 'Worked out once there are pairs and rounds',
  'seriespill.mainSettings.roundDeadlineRolling': 'Round deadline (rolling)',
  'seriespill.mainSettings.every': 'Every',
  'seriespill.mainSettings.unitDays': 'day(s)',
  'seriespill.mainSettings.unitWeeks': 'week(s)',
  'seriespill.mainSettings.unitMonths': 'month(s)',
  'seriespill.mainSettings.autoDeadlineNote':
    'The season deadline is set automatically to the estimated season end',
  'seriespill.mainSettings.autoDeadlineDate': ' ({date})',
  'seriespill.mainSettings.unplayedLabel': 'Unplayed matches when a round closes',
  'seriespill.mainSettings.unplayedWo': 'Both lose 0–2 (WO)',
  'seriespill.mainSettings.unplayedWoHint': 'Both teams get a loss. Does not affect ELO.',
  'seriespill.mainSettings.unplayedDraw': 'Draw',
  'seriespill.mainSettings.unplayedDrawHint':
    'Recorded as a draw. 0 points each, counts as played. Does not affect ELO.',
  'seriespill.mainSettings.roundEndLabel': 'Closing a round',
  'seriespill.mainSettings.roundEndManual': 'By hand',
  'seriespill.mainSettings.roundEndManualHint':
    'You close the round yourself with the «Close round» button in the schedule.',
  'seriespill.mainSettings.roundEndAuto': 'Automatically at the deadline',
  'seriespill.mainSettings.roundEndAutoHint':
    'The round closes automatically when its deadline passes. Unplayed matches get the result chosen above.',
  'seriespill.mainSettings.revealLabel': 'Announcing rounds',
  'seriespill.mainSettings.revealAll': 'Every round',
  'seriespill.mainSettings.revealAllHint': 'The whole schedule is visible from the start.',
  'seriespill.mainSettings.revealRolling': 'Before each round',
  'seriespill.mainSettings.revealRollingHint':
    'Future rounds are announced just before they start.',
  'seriespill.mainSettings.announce': 'Announce',
  'seriespill.mainSettings.beforeRoundStarts': 'before the round starts',
  'seriespill.mainSettings.show': 'Show',
  'seriespill.mainSettings.roundsAhead': 'round(s) ahead',
  'seriespill.mainSettings.roundRobinLabel': 'Round-robin format',
  'seriespill.mainSettings.singleRound': 'Single',
  'seriespill.mainSettings.doubleRound': 'Double',
  'seriespill.mainSettings.homeAway': '(h/a)',
  'seriespill.mainSettings.everyPairMeets': 'Every pair meets every other ',
  'seriespill.mainSettings.onceStrong': 'once',
  'seriespill.mainSettings.twiceStrong': 'twice',
  'seriespill.mainSettings.rulesHeading': 'Playing rules (league default)',
  'seriespill.mainSettings.rulesHint': 'Classes and divisions inherit these unless they differ.',
  'seriespill.mainSettings.matchFormatLabel': 'Match format',
  'seriespill.mainSettings.fmtSuperTiebreak': 'Super tie-break',
  'seriespill.mainSettings.fmtSuperTiebreakHint':
    'Best of 3, super tie-break to 10 at one set all (FIP).',
  'seriespill.mainSettings.fmtFullThird': 'Full third set',
  'seriespill.mainSettings.fmtFullThirdHint': 'Best of 3, a full third set to 6.',
  'seriespill.mainSettings.deuceLabel': 'Scoring at 40–40',
  'seriespill.mainSettings.deuceAdvantage': 'Advantage',
  'seriespill.mainSettings.deuceGolden': 'Golden point',
  'seriespill.mainSettings.deuceStarpoint': 'Starpoint',
  'seriespill.mainSettings.mixServeLabel': 'Mixed: serving rule',
  'seriespill.mainSettings.mixServeFree': 'Free',
  'seriespill.mainSettings.mixServeSame': 'Woman↔woman',
  'seriespill.mainSettings.mixServeHint': 'Women serve to women, men to men.',
  'seriespill.mainSettings.on': 'On',
  'seriespill.mainSettings.off': 'Off',
  'seriespill.mainSettings.sharedNoteLabel': 'Shared rule note (markdown)',
  'seriespill.mainSettings.fullscreenEditor': '✎ Full-screen editor',
  'seriespill.mainSettings.notePlaceholder':
    'Write freely, or use the full-screen editor. Markdown: **bold**, *italic*, # heading, - bullet, [text](url), ![alt](image-url).',
  'seriespill.mainSettings.noteEditorTitle': 'Shared rule note',
  'seriespill.mainSettings.eloWeightHeading': 'ELO weight for the league',
  'seriespill.mainSettings.eloWeightHint':
    "Decides how much this league's matches move the players' ELO. Changing it later applies to new matches only. The league default is 200 % (a full season, weighted heaviest).",
  'seriespill.mainSettings.autoMode': '⚡ Auto mode',
  'seriespill.mainSettings.autoModeHint':
    'When a class locks (everyone has played, or the deadline passes) the next season starts automatically without an admin confirming it. The engine uses the standard promotion/relegation.',
  'seriespill.mainSettings.pauseHeading': 'Pause the league',
  'seriespill.mainSettings.pauseLead':
    'Hide the league from the home screen for now. Matches, pairs and standings are kept — you can resume it later without losing anything. No ELO is committed.',
  'seriespill.mainSettings.pauseAction': '⏸ Pause the league',
  'seriespill.mainSettings.retireHeading': 'Retire the league',
  'seriespill.mainSettings.retireLead':
    'Ends the league for good. ELO is committed to the player database and the league is archived. No further seasons.',
  'seriespill.mainSettings.retireConfirm': 'Confirm retiring it',
  'seriespill.mainSettings.retireAction': 'Retire the league',
  'seriespill.mainSettings.cancel': 'Cancel',
  'seriespill.mainSettings.save': 'Save changes',
  // ── the new-league setup wizard ─────────────────────────────────────
  'seriespill.setup.back': 'Back',
  'seriespill.setup.title': 'New league',
  'seriespill.setup.lead':
    'Fixed pairs in divisions, round robin (everyone plays everyone). Promotion and relegation at the end of the season — the league carries on.',
  'seriespill.setup.step1': 'Basics',
  'seriespill.setup.step2': 'Sign-up',
  'seriespill.setup.step3': 'Preview',
  // Errors the wizard puts under the step navigation.
  'seriespill.setup.errPickTwo': 'Pick two players for the pair.',
  'seriespill.setup.errSamePlayer': 'Pick two different players.',
  'seriespill.setup.thePlayer': 'The player',
  'seriespill.setup.errAlreadyInClass':
    '{name} is already signed up in this class. A player can only be in one division per class.',
  'seriespill.setup.errPickClassTab': 'Pick a class tab first.',
  'seriespill.setup.errNoFreePlayers': 'No available players left in the database.',
  'seriespill.setup.errDivisionsFull': 'Every division is already full ({perDiv} pairs each).',
  'seriespill.setup.errNotEnoughPlayers':
    'Not enough available players to form pairs in this class.',
  'seriespill.setup.errPartialAdd':
    'Added {added} of {wanted} pairs — no more available players of the right gender.',
  'seriespill.setup.errPickClass': 'Pick at least one class.',
  'seriespill.setup.errNameAndClass': 'Give it a name and pick at least one class.',
  // Per-division warnings on the sign-up step.
  'seriespill.setup.issueEmpty': 'Empty — sign-ups can be added later',
  'seriespill.setup.issueOnePair':
    'Only 1 pair in division {level} — at least 2 are needed to play',
  'seriespill.setup.issueTooFew': '{count} pairs in division {level} — at least 4 recommended',
  'seriespill.setup.issueTooMany':
    '{count} pairs in division {level} — 10 at most recommended, to keep the season manageable',
  'seriespill.setup.nameLabel': 'Name',
  'seriespill.setup.namePlaceholder': 'Seriespill 2026',
  'seriespill.setup.classesLabel': 'Classes',
  'seriespill.setup.classesHint':
    'Pick at least one class to play in — tap a card to switch it on.',
  'seriespill.setup.selected': 'Selected',
  'seriespill.setup.notSelected': 'Not selected',
  'seriespill.setup.divisionCount': 'Number of divisions:',
  'seriespill.setup.courtsLabel': 'Courts',
  'seriespill.setup.courtFree': 'Free booking',
  'seriespill.setup.courtFreeHint': 'The pairs agree and book a court themselves.',
  'seriespill.setup.courtAllocated': 'Allocated courts',
  'seriespill.setup.courtAllocatedHint': 'Courts are set aside on fixed days (calendar).',
  'seriespill.setup.playingDays': 'Fixed playing days (repeat every week)',
  'seriespill.setup.timeWindow': 'Time window',
  'seriespill.setup.matchDuration': 'Length per match',
  'seriespill.setup.minutes': '{min} min',
  'seriespill.setup.courtsReserved': 'Courts set aside',
  'seriespill.setup.pickADay': 'Pick at least one playing day. 1 slot = 1 match.',
  'seriespill.setup.slotSummaryOne':
    '{slots} slots per court × {courts} courts = {matches} match per playing day. The matches are placed automatically when the league starts.',
  'seriespill.setup.slotSummaryMany':
    '{slots} slots per court × {courts} courts = {matches} matches per playing day. The matches are placed automatically when the league starts.',
  'seriespill.setup.seasonStart': 'Season start',
  'seriespill.setup.deadlineLabel': 'Deadline',
  'seriespill.setup.rollingDeadline': 'Rolling round deadline',
  'seriespill.setup.fixedDeadline': 'Fixed season deadline',
  'seriespill.setup.newRoundEvery': 'A new round every',
  'seriespill.setup.unitDays': 'day(s)',
  'seriespill.setup.unitWeeks': 'week(s)',
  'seriespill.setup.unitMonths': 'month(s)',
  'seriespill.setup.estEndLabel': 'Estimated season end:',
  'seriespill.setup.estEndRounds': ' · the largest division needs {rounds} rounds.',
  'seriespill.setup.estEndUnknown':
    'The estimated season end is worked out once a season start and at least one division with 2 or more pairs are set.',
  'seriespill.setup.fixedDeadlineHint':
    'One fixed date for the whole season. No rolling round deadlines.',
  'seriespill.setup.revealLabel': 'Announcing rounds',
  'seriespill.setup.revealRolling': 'Before each round',
  'seriespill.setup.revealRollingHint': 'Future rounds are announced just before they start.',
  'seriespill.setup.revealAll': 'Every round',
  'seriespill.setup.revealAllHint': 'The whole schedule is visible from the start.',
  'seriespill.setup.revealAllNote': 'The whole schedule is shown from the start.',
  'seriespill.setup.announce': 'Announce',
  'seriespill.setup.beforeRoundStarts': 'before the round starts',
  'seriespill.setup.show': 'Show',
  'seriespill.setup.roundsAhead': 'round(s) ahead',
  'seriespill.setup.roundRobinLabel': 'Round-robin format',
  'seriespill.setup.singleRound': 'Single',
  'seriespill.setup.doubleRound': 'Double',
  'seriespill.setup.homeAway': '(home/away)',
  'seriespill.setup.everyPairMeets': 'Each pair meets every other pair ',
  'seriespill.setup.onceStrong': 'once',
  'seriespill.setup.twiceStrong': 'twice',
  'seriespill.setup.singleRoundHint': '. A shorter season, fewer matches.',
  'seriespill.setup.doubleRoundHint': '. Like a football league — twice as many matches.',
  'seriespill.setup.matchFormatLabel': 'Match format',
  'seriespill.setup.fmtSuperTiebreak': 'Super tie-break',
  'seriespill.setup.fmtSuperTiebreakA': 'Best of 3. At ',
  'seriespill.setup.fmtSuperTiebreakStrong': 'one set all',
  'seriespill.setup.fmtSuperTiebreakB': ', the match is decided by a super tie-break to 10 (FIP).',
  'seriespill.setup.fmtFullSet': 'Full set',
  'seriespill.setup.fmtFullSetA': 'Best of 3. A deciding third set is played as a',
  'seriespill.setup.fmtFullSetStrong': 'full set to 6 games',
  'seriespill.setup.deuceLabel': 'Scoring at 40–40',
  'seriespill.setup.deuceAdvantage': 'Advantage',
  'seriespill.setup.deuceAdvantageTag': '(the usual one)',
  'seriespill.setup.deuceAdvantageA': 'Win ',
  'seriespill.setup.deuceAdvantageStrong': 'two points in a row',
  'seriespill.setup.deuceAdvantageB': ' at 40–40.',
  'seriespill.setup.deuceGolden': 'Golden point',
  'seriespill.setup.deuceGoldenStrong': 'One',
  'seriespill.setup.deuceGoldenHint': ' deciding point at the first 40–40.',
  'seriespill.setup.deuceStarpoint': 'Starpoint',
  'seriespill.setup.deuceStarpointA': 'Advantage the first two times, ',
  'seriespill.setup.deuceStarpointStrong': 'golden point at the third',
  'seriespill.setup.deuceStarpointB': ' 40–40.',
  'seriespill.setup.unplayedLabel': 'Unplayed matches when a round closes',
  'seriespill.setup.unplayedWo': 'Both lose 0–2 (WO)',
  'seriespill.setup.unplayedWoHint': 'Both teams get a loss. Does not affect ELO.',
  'seriespill.setup.unplayedDraw': 'Draw',
  'seriespill.setup.unplayedDrawHint':
    'Recorded as a draw. 0 points each, counts as played. Does not affect ELO.',
  'seriespill.setup.roundEndLabel': 'Closing a round',
  'seriespill.setup.roundEndManual': 'By hand',
  'seriespill.setup.roundEndManualHint':
    'You close the round yourself with the «Close round» button in the schedule.',
  'seriespill.setup.roundEndAuto': 'Automatically at the deadline',
  'seriespill.setup.roundEndAutoHint':
    'The round closes automatically when its deadline passes. Unplayed matches get the result chosen above.',
  'seriespill.setup.pairCount': '{count} pairs',
  'seriespill.setup.pairCountOk': '· OK',
  'seriespill.setup.addPairIn': 'Add a pair to {name}',
  'seriespill.setup.demo': 'Demo',
  'seriespill.setup.demoAdd': '+ {count} pairs',
  'seriespill.setup.demoAddTitle': 'Add {count} pairs from the available players in the database',
  'seriespill.setup.playerSlot': 'Player {slot}',
  'seriespill.setup.pickBelow': '(pick below)',
  'seriespill.setup.newSuffix': ' · New',
  'seriespill.setup.clearPlayer': 'Clear the chosen player',
  'seriespill.setup.searchBelow': 'Search below to choose…',
  'seriespill.setup.putIn': 'Put into',
  'seriespill.setup.divisionName': 'Division {level}',
  'seriespill.setup.divisionShort': 'Div {level}',
  'seriespill.setup.searchPlayer': 'Search for player {slot}…',
  'seriespill.setup.newPlayer': '+ New player',
  'seriespill.setup.includeWomen': 'Search among women too (women may play in the men’s class)',
  'seriespill.setup.noMatchesA': 'No matches for "',
  'seriespill.setup.noMatchesB': '".',
  'seriespill.setup.createPlayer': 'Create "{name}" as a new player',
  'seriespill.setup.noFreePlayers': 'No players left — everyone is already picked.',
  'seriespill.setup.genderMale': '♂ Man',
  'seriespill.setup.genderFemale': '♀ Woman',
  'seriespill.setup.genderMix': '⚥ Mixed',
  'seriespill.setup.newTag': 'NEW',
  'seriespill.setup.pick': 'Pick →',
  'seriespill.setup.addPair': '🤝 Add the pair',
  'seriespill.setup.pickHint': 'Search and click «Pick →» to fill player 1 and 2',
  'seriespill.setup.pickHintDivisions':
    '. The default division is chosen above — you can move pairs between divisions in the list below',
  'seriespill.setup.noPairsHere': 'No pairs here yet. Use «Add the pair» above.',
  'seriespill.setup.removePair': 'Remove',
  'seriespill.setup.readyTitle': 'Ready to start',
  'seriespill.setup.sumName': 'Name',
  'seriespill.setup.sumClasses': 'Classes',
  'seriespill.setup.sumDivisions': 'Divisions in total',
  'seriespill.setup.sumPairs': 'Pairs in total',
  'seriespill.setup.sumMatches': 'Matches this season',
  'seriespill.setup.sumCourtMode': 'Court mode',
  'seriespill.setup.sumPlayingDays': 'Playing days',
  'seriespill.setup.sumTimeWindow': '{start}–{end} · {minutes} min · {courts} courts',
  'seriespill.setup.sumFormat': 'Format',
  'seriespill.setup.sumFormatDouble': 'Double (home/away)',
  'seriespill.setup.sumFormatSingle': 'Single (everyone plays everyone)',
  'seriespill.setup.sumDeuce': 'At 40–40',
  'seriespill.setup.sumRoundDeadline': 'Round deadline',
  'seriespill.setup.sumEvery': 'Every {value} {unit}',
  'seriespill.setup.sumEstimatedEnd': 'Season end (estimated)',
  'seriespill.setup.sumSeasonDeadline': 'Season deadline',
  'seriespill.setup.divPairSummary': 'Div {level}: {count} pairs',
  'seriespill.setup.recommendations': 'Recommendations ({count})',
  'seriespill.setup.recommendationLine': '• {name}: {msg}',
  'seriespill.setup.recommendationNote':
    '4–10 pairs per division is the recommendation. You can start anyway — it is only guidance.',
  'seriespill.setup.status': 'Status',
  'seriespill.setup.statDivisions': 'Divisions',
  'seriespill.setup.statPairs': 'Pairs',
  'seriespill.setup.statMatches': 'Matches',
  'seriespill.setup.hardErrorsNote': '⚠ {count} division(s) need more pairs',
  'seriespill.setup.softWarningsNote': '! {count} recommendation(s) — see step 3',
  'seriespill.setup.guidance': 'Guidance',
  'seriespill.setup.guidanceNote':
    '4–10 pairs per division is the recommendation. Fewer means few matches, more means long seasons. You can adjust afterwards.',
  'seriespill.setup.cancel': '← Cancel',
  'seriespill.setup.previous': '← Previous',
  'seriespill.setup.next': 'Next →',
  'seriespill.setup.create': 'Create the league',
  'seriespill.setup.pausedTitle': 'Paused leagues ({count})',
  'seriespill.setup.pausedLead':
    'You have leagues on pause. You can resume one of them instead of creating a new one.',
  'seriespill.setup.pausedClassOne': '{count} class',
  'seriespill.setup.pausedClassMany': '{count} classes',
  'seriespill.setup.pausedPairs': ' · {count} pairs ·',
  'seriespill.setup.pausedAt': ' paused {date}',
  'seriespill.setup.resume': 'Resume →',
  'seriespill.setup.deletePaused': 'Delete',
  'seriespill.setup.deletePausedTitle': 'Delete the paused league for good',
  'seriespill.setup.confirmDeletePaused': 'Delete this paused league for good?',
  // Month names in full, for the single-date summary in the booking panel.
  'seriespill.date.monthLong0': 'January',
  'seriespill.date.monthLong1': 'February',
  'seriespill.date.monthLong2': 'March',
  'seriespill.date.monthLong3': 'April',
  'seriespill.date.monthLong4': 'May',
  'seriespill.date.monthLong5': 'June',
  'seriespill.date.monthLong6': 'July',
  'seriespill.date.monthLong7': 'August',
  'seriespill.date.monthLong8': 'September',
  'seriespill.date.monthLong9': 'October',
  'seriespill.date.monthLong10': 'November',
  'seriespill.date.monthLong11': 'December',
  // ── the court-booking panel (time slots, reserving, placing matches) ───
  'seriespill.booking.heading': 'Court booking · time slots',
  'seriespill.booking.courtN': 'Court {num}',
  'seriespill.booking.minutes': '{min} min',
  'seriespill.booking.modeIntegrated': 'Use Court booking',
  'seriespill.booking.modeIntegratedHint':
    'Reserves in the centre calendar. Checks that the courts are available.',
  'seriespill.booking.modeStandalone': 'Standalone',
  'seriespill.booking.modeStandaloneHint':
    'Reserves nothing in the calendar and checks no availability. You set the time, the number of courts and the venue yourself — at another centre, for instance.',
  'seriespill.booking.startsFrom': 'Bookings start from',
  'seriespill.booking.usesSeasonStart': '(uses the season start)',
  'seriespill.booking.roomsLeadSeries':
    'Each time slot is a set of fixed playing days. A time slot can cover one class or several. ',
  'seriespill.booking.roomsLeadSingle': 'Pick a date, a start time and the courts. ',
  'seriespill.booking.sharedDuration': 'Shared length: ',
  'seriespill.booking.sharedDurationTail': ' (change it under Settings). 1 slot = 1 match.',
  'seriespill.booking.courtNumbersMustMatch': ' The court numbers must match the club’s courts.',
  'seriespill.booking.uncoveredClasses': '⚠ With no time slot: {classes} — these get no slots.',
  // The needs panel: how many slots the setup books against how many matches there are.
  'seriespill.booking.playsLead': 'Each player plays',
  'seriespill.booking.playsPerMonthOne': ' match/month with the current setup',
  'seriespill.booking.playsPerMonthMany': ' matches/month with the current setup',
  'seriespill.booking.playsDetail': '({players} players · ≈ {slots} slots/month)',
  'seriespill.booking.seasonMatchesLead': '{count} matches this season',
  'seriespill.booking.seasonMatchesClasses': ' ({classes})',
  'seriespill.booking.seasonMatchesNeed': ' → that needs {count} slots. You are booking',
  'seriespill.booking.slotsSurplus':
    '⚠ {count} more than needed — you can cut slots, courts or days.',
  'seriespill.booking.slotsShort': '⚠ {count} too few to play every match this season.',
  'seriespill.booking.slotsExact': '✓ Exactly enough.',
  // Errors from reserving and placing.
  'seriespill.booking.errNoDates':
    'A start date or a season deadline is missing — set those first.',
  'seriespill.booking.errReadCalendar': 'The calendar could not be read.',
  'seriespill.booking.errNoSlots': 'No slots — check the time slots, days and dates.',
  'seriespill.booking.errNoReserved': 'No reserved slots — reserve first.',
  'seriespill.booking.errPlace': 'The matches could not be placed.',
  'seriespill.booking.errClearPlacement': 'The placement could not be reset.',
  'seriespill.booking.errClearBookings': 'The bookings could not be removed.',
  'seriespill.booking.errReserveAndPlace': 'Reserving and placing failed.',
  'seriespill.booking.errRemoveSurplus': 'The surplus bookings could not be removed.',
  'seriespill.booking.reservationFallback': 'Reservation',
  // Holidays.
  'seriespill.booking.holidaysSkipped': 'Public holidays that are skipped ({country})',
  'seriespill.booking.noHolidays': 'No public holidays in the season.',
  'seriespill.booking.dayMonth': '{day} {month}',
  'seriespill.booking.holidayNameSuffix': ' · {name}',
  'seriespill.booking.holidaySetNote':
    'The same holiday set as the bookings use. Add or remove days under booking settings → Public holidays.',
  'seriespill.booking.holidayLanding': 'When a date lands on a public holiday',
  'seriespill.booking.holidayInclude': 'Include it anyway',
  'seriespill.booking.holidaySkip': 'Skip it',
  'seriespill.booking.holidaySet': 'Holiday set: {country} (change it in settings).',
  // One time slot: its summary line and its editor.
  'seriespill.booking.noDate': 'no date',
  'seriespill.booking.noDays': 'no days',
  'seriespill.booking.noClasses': 'no classes',
  'seriespill.booking.everyNthWeek': ' (every {n} weeks)',
  'seriespill.booking.freqDaily': 'Daily',
  'seriespill.booking.freqWeekly': 'Weekly',
  'seriespill.booking.freqMonthly': 'Monthly',
  'seriespill.booking.freqYearly': 'Yearly',
  'seriespill.booking.roomFallback': 'Time slot {n}',
  'seriespill.booking.standardRoom': 'Standard time slot',
  'seriespill.booking.slotsByCourts': '{count} slots × {courts} courts',
  'seriespill.booking.courtsOnly': '{courts} courts',
  'seriespill.booking.plannedSlots': '{count} slots',
  'seriespill.booking.bookedCount': '{count} booked',
  'seriespill.booking.freeCountOne': '· {count} free',
  'seriespill.booking.freeCountMany': '· {count} free',
  'seriespill.booking.collapse': '▲ hide',
  'seriespill.booking.expand': '▼ tune',
  'seriespill.booking.removeRoom': 'Remove',
  'seriespill.booking.recurring': 'Recurring booking',
  'seriespill.booking.repeats': 'Repeats',
  'seriespill.booking.every': 'Every',
  'seriespill.booking.unitDays': 'day(s)',
  'seriespill.booking.unitWeeks': 'week(s)',
  'seriespill.booking.unitMonths': 'month(s)',
  'seriespill.booking.unitYears': 'year(s)',
  'seriespill.booking.onDays': 'On days',
  'seriespill.booking.endsLabel': 'Ends',
  'seriespill.booking.endAfterCount': 'After a number',
  'seriespill.booking.endOnDate': 'On a date',
  'seriespill.booking.endNever': 'Never',
  'seriespill.booking.occurrences': 'occurrences',
  'seriespill.booking.repeatsUntilDeadline': 'Repeats until the season deadline ({date}).',
  'seriespill.booking.date': 'Date',
  'seriespill.booking.from': 'From',
  'seriespill.booking.to': 'to',
  'seriespill.booking.pickADate': 'Pick a date',
  'seriespill.booking.pickADateDot': 'Pick a date.',
  'seriespill.booking.pickADay': 'Pick at least one playing day.',
  'seriespill.booking.niceDate': '{wd} {day} {month} {year}',
  'seriespill.booking.durHours': '{h}h',
  'seriespill.booking.durMinutes': ' {m}min',
  'seriespill.booking.endBeforeStart': '⚠ The end time must be after the start',
  'seriespill.booking.atTime': '{start}–{end} · {dur}',
  'seriespill.booking.startTime': 'Start time',
  'seriespill.booking.slotCount': 'Number of slots',
  'seriespill.booking.slotCountDecrease': 'Fewer slots',
  'seriespill.booking.slotCountIncrease': 'More slots',
  'seriespill.booking.removeCourt': 'Remove court {num}',
  'seriespill.booking.end': 'End',
  'seriespill.booking.slotDurationNote': '({min} min/slot — recommended, adjustable)',
  // The day timeline for placing a single-date booking.
  'seriespill.booking.placeInCalendar': 'Place it in the calendar',
  'seriespill.booking.prevDay': 'Previous day',
  'seriespill.booking.nextDay': 'Next day',
  'seriespill.booking.backToToday': 'Back to today',
  'seriespill.booking.today': 'Today',
  'seriespill.booking.showAllCourts': 'Show every court',
  'seriespill.booking.notSelectedSuffix': ' (not selected)',
  'seriespill.booking.busy': 'busy',
  'seriespill.booking.dragHint': 'Drag to move · drag the edges to change the length',
  'seriespill.booking.findNextFree': '⏭ Find the next free day',
  'seriespill.booking.findAnyDay': 'Any day',
  'seriespill.booking.findWeekday': 'Weekdays only',
  'seriespill.booking.findWeekend': 'Weekends only',
  'seriespill.booking.noFreeDay': 'No free day found within a year.',
  'seriespill.booking.searchFailed': 'The search failed.',
  'seriespill.booking.freeStartTimes': 'Available start times (click to place):',
  'seriespill.booking.noFreeSlotsOne':
    'No free slots for {count} court at this length on this day.',
  'seriespill.booking.noFreeSlotsMany':
    'No free slots for {count} courts at this length on this day.',
  // Standalone venue fields.
  'seriespill.booking.courtCount': 'Number of courts',
  'seriespill.booking.courtCountOne': '{count} court',
  'seriespill.booking.courtCountMany': '{count} courts',
  'seriespill.booking.courtCountHint':
    'How many courts you have available (4 players per court). Standalone checks no availability.',
  'seriespill.booking.whereLabel': 'Where is it played?',
  'seriespill.booking.venuePlaceholder': 'Venue / centre (e.g. Smaalenene Padelklubb)',
  'seriespill.booking.addressPlaceholder': 'Street address — search and pick',
  'seriespill.booking.searching': 'searching…',
  'seriespill.booking.addressHint':
    'Search for a street address and pick from the list — the postcode and town are filled in automatically (Kartverket/Geonorge, free). Needs a connection; it works on the published site. You can also type the address freely.',
  // Court selection and the availability legend.
  'seriespill.booking.courtsLabel': 'Courts',
  'seriespill.booking.courtBusyTitle': 'Not available (already booked)',
  'seriespill.booking.courtFreeTitle': 'Available',
  'seriespill.booking.courtWasteTitle': 'Creates unused time — allowed as an admin',
  'seriespill.booking.gapTitle': '{mins} min gap {from}–{to}',
  'seriespill.booking.busySuffix': '· busy',
  'seriespill.booking.pickClubCourts':
    'Pick which of the club’s courts are used. Court names are changed in the booking settings.',
  'seriespill.booking.legendGreen': '● Green = available',
  'seriespill.booking.legendYellow': '● Yellow = breaks smart booking (allowed as an admin)',
  'seriespill.booking.legendRed': '● Red = not available',
  'seriespill.booking.smartBooking': 'Smart booking:',
  'seriespill.booking.smartOn': 'on',
  'seriespill.booking.smartOff': 'off',
  'seriespill.booking.smartDetail': ' · buffer {buffer} min · lengths {durations} min',
  'seriespill.booking.smartOffDetail': ' — only hard clashes are checked',
  'seriespill.booking.redSelected':
    '⚠ {courts} are selected but not available — they will be skipped when reserving. Remove them, or pick another time slot.',
  'seriespill.booking.forcedCourts':
    'ℹ You need more courts than there are «perfect» free ones, so {courts} are approved even though they leave a little unused time around the block — unavoidable when every court has to be used.',
  'seriespill.booking.yellowConsequence':
    '⚠ What the selected yellow courts cost (you are booking as an admin):',
  'seriespill.booking.wastedGap': '{mins} min of unused time {from}–{to}',
  'seriespill.booking.wastedGapDead': ' (too short for anyone else to book)',
  'seriespill.booking.resultLeaves': 'Result — it leaves {gaps}.',
  'seriespill.booking.resultIsolated':
    'Result — a more fragmented calendar; the court sits apart from the other bookings (more unused time overall).',
  'seriespill.booking.adminAllowed': 'An admin is allowed to — the court is reserved anyway.',
  'seriespill.booking.courtNamePlaceholder': 'Court {num} (optional name)',
  'seriespill.booking.addCourt': '+ Add a court',
  // Which classes a time slot covers.
  'seriespill.booking.appliesToClasses': 'Applies to classes',
  'seriespill.booking.noAllocatedClasses': 'No class is set to allocated courts yet.',
  'seriespill.booking.notAllocatable':
    '{classes} are on free booking and cannot be added. Change the court mode in the class settings to make them available.',
  'seriespill.booking.roomSummaryOne':
    '{slots} slots/court × {courts} courts = {matches} match/day. {start}–{end}. Start times: {times}.',
  'seriespill.booking.roomSummaryMany':
    '{slots} slots/court × {courts} courts = {matches} matches/day. {start}–{end}. Start times: {times}.',
  'seriespill.booking.pastMidnight':
    '⚠ it runs past midnight — cut the number of slots or move the start time.',
  'seriespill.booking.oneRoundFor':
    'One round for {classes} = {matches} matches · {slots} slots/week →',
  'seriespill.booking.weeksBetweenOne': '~{weeks} week between rounds',
  'seriespill.booking.weeksBetweenMany': '~{weeks} weeks between rounds',
  'seriespill.booking.noPairsYet':
    'No pairs in the selected classes yet — the week estimate appears once pairs are added.',
  'seriespill.booking.addRoom': '+ Add a time slot',
  // Reserving and placing.
  'seriespill.booking.checkingCalendar': 'Checking the calendar…',
  'seriespill.booking.reservedOne': '✓ {count} court reserved',
  'seriespill.booking.reservedMany': '✓ {count} courts reserved',
  'seriespill.booking.updateReservation': 'Update the reservation',
  'seriespill.booking.reserveCourts': 'Reserve courts',
  'seriespill.booking.placing': 'Placing…',
  'seriespill.booking.placeMatches': 'Place the matches in the slots',
  'seriespill.booking.clearPlacement': 'Reset the placement',
  'seriespill.booking.clearBookings': 'Remove the bookings',
  'seriespill.booking.reserveHelpWithPlace':
    'Reserve the courts first (that holds the times in the centre calendar). Place the matches as a separate step afterwards (usually once sign-up is settled). Placing only fills matches that have no date.',
  'seriespill.booking.reserveHelp':
    'Reserve the selected courts for the tournament in the calendar.',
  'seriespill.booking.clearedPlacement': '✓ Reset the placement for {count} matches.',
  'seriespill.booking.clearedBookings': '✓ Removed {count} bookings from this league.',
  'seriespill.booking.placedResult': '✓ {count} matches got a date, time and court',
  'seriespill.booking.placedMissing': ' · ⚠ {count} have no slot',
  'seriespill.booking.standaloneHelp':
    'Standalone: the matches are placed on the courts and days you specify, with no calendar reservation. «Place» only fills matches that have no date.',
  // The confirmation before clearing a placement or the bookings.
  'seriespill.booking.confirmTitle': 'Are you sure?',
  'seriespill.booking.confirmClearA': 'This resets the date, time and court for',
  'seriespill.booking.confirmClearBOne': ' placed match in the league.',
  'seriespill.booking.confirmClearBMany': ' placed matches in the league.',
  'seriespill.booking.confirmClearNote':
    'The court bookings themselves stay in the calendar — you can place the matches again afterwards. Finished matches are untouched.',
  'seriespill.booking.confirmRemoveA': 'This removes ',
  'seriespill.booking.confirmRemoveBOne':
    'reserved booking for the league from the centre calendar.',
  'seriespill.booking.confirmRemoveBMany':
    'reserved bookings for the league from the centre calendar.',
  'seriespill.booking.period': 'Period: {span}',
  'seriespill.booking.confirmRemoveNote':
    'This cannot be undone. Placed matches keep their date, time and court until you reset the placement.',
  'seriespill.booking.cancel': 'Cancel',
  // The calendar booking list.
  'seriespill.booking.calendarBookings': 'Bookings in the calendar',
  'seriespill.booking.showBookings': 'Show bookings',
  'seriespill.booking.refresh': 'Refresh',
  'seriespill.booking.showBookingsHint':
    'Click «Show bookings» to see every reserved time in the centre calendar.',
  'seriespill.booking.noBookings': 'No bookings in the calendar yet.',
  'seriespill.booking.pickDateForBookings':
    'Pick a date and a time slot above to see the relevant bookings.',
  'seriespill.booking.noBookingsInWindow': 'No bookings in the tournament’s time slot yet.',
  'seriespill.booking.relevantOne': '{count} relevant booking ·',
  'seriespill.booking.relevantMany': '{count} relevant bookings ·',
  'seriespill.booking.fromThisTournament': 'from this tournament.',
  'seriespill.booking.weekdayDayMonth': '{wd} {day} {month}',
  'seriespill.booking.bookingCountOne': '{count} booking',
  'seriespill.booking.bookingCountMany': '{count} bookings',
  'seriespill.booking.ofWhichSeries': ' · {count} league',
  'seriespill.booking.timeAndCourt': '{start}–{end} · court {court}',
  'seriespill.booking.seriesLabel': 'League',
  // The reserve-preview modal.
  'seriespill.booking.reserveModalTitle': 'Reserve in the calendar',
  'seriespill.booking.willReserveOne': ' court will be reserved in the calendar',
  'seriespill.booking.willReserveMany': ' courts will be reserved in the calendar',
  'seriespill.booking.andPlaceMatches': ', and the matches will be placed in them',
  'seriespill.booking.reservationName': 'The reservation will be called:',
  'seriespill.booking.tournamentId': 'Tournament ID: {id}',
  'seriespill.booking.thisIsReserved': 'This is what gets reserved:',
  'seriespill.booking.dateTimeCourts': '{date} · {time} · court {courts}',
  'seriespill.booking.andMoreDays': '… and {count} more days',
  'seriespill.booking.andMore': '… and {count} more',
  'seriespill.booking.conflictsHeading': '⚠ {count} slots are already taken and will be skipped:',
  'seriespill.booking.conflictLine': '{date} · court {court} · {start}–{end}',
  'seriespill.booking.noConflicts': 'No clashes — every slot is free.',
  'seriespill.booking.working': 'Working…',
  'seriespill.booking.reserveCourtsCount': 'Reserve courts ({count})',
  // The status modal after reserving and/or placing.
  'seriespill.booking.statusPlaced': 'The matches are placed',
  'seriespill.booking.statusReservedAndPlaced': 'Reserved and placed',
  'seriespill.booking.statusReserved': 'Courts reserved',
  'seriespill.booking.statSlotsReserved': 'Slots reserved',
  'seriespill.booking.statSlotsAvailable': 'Slots available',
  'seriespill.booking.statSkipped': 'Skipped (taken)',
  'seriespill.booking.statPlaced': 'Matches placed',
  'seriespill.booking.statUnplaced': 'Matches with no free slot',
  'seriespill.booking.statEmpty': 'Empty slots (surplus)',
  'seriespill.booking.reservedInCalendar': 'Reserved in the calendar:',
  'seriespill.booking.freedOne': '✓ Freed {count} surplus booking in the calendar.',
  'seriespill.booking.freedMany': '✓ Freed {count} surplus bookings in the calendar.',
  'seriespill.booking.surplusQuestion':
    'You reserved more courts than there are matches. Do you want to free the empty times in the calendar?',
  'seriespill.booking.removing': 'Removing…',
  'seriespill.booking.removeSurplus': 'Remove {count} surplus',
  'seriespill.booking.done': 'Done',
  // ── the live league screen: standings, schedule, calendar ───────────
  'seriespill.view.kicker': 'League',
  'seriespill.view.home': 'Home',
  'seriespill.view.back': 'Back',
  'seriespill.view.noDivisions': 'No divisions in this league.',
  'seriespill.view.showPlayer': 'Show the player',
  'seriespill.view.matchAction': 'Result: {home} vs {away}',
  'seriespill.view.cancel': 'Cancel',
  'seriespill.view.all': 'All',
  'seriespill.view.courtN': 'Court {court}',
  'seriespill.view.courtsCount': '{count} courts',
  'seriespill.view.courtSuffix': ' · Court {court}',
  'seriespill.view.weekdayDayMonth': '{wd} {day} {month}',
  'seriespill.view.season': 'Season {season}',
  'seriespill.view.round': 'Round {round}',
  'seriespill.view.ofRounds': 'of {total}',
  'seriespill.view.divisionName': 'Division {level}',
  'seriespill.view.classDivision': '{gender} Division {level}',
  'seriespill.view.today': 'today',
  'seriespill.view.inDaysOne': 'in {days} day',
  'seriespill.view.inDaysMany': 'in {days} days',
  'seriespill.view.agoDaysOne': '{days} day ago',
  'seriespill.view.agoDaysMany': '{days} days ago',
  'seriespill.view.inDaysShort': 'in {days} d',
  'seriespill.view.agoDaysShort': '{days} d ago',
  // The standings table.
  'seriespill.view.tableLabel': 'Table',
  'seriespill.view.divAvg': '· Avg {avg}',
  'seriespill.view.divAvgTitle': "The division's average rating",
  'seriespill.view.matchesPlayed': ' · {played}/{total} matches played',
  'seriespill.view.noPairsInDivision':
    'No pairs in this division yet. Use «Settings {name}» at the top to add some.',
  'seriespill.view.colSets': 'Sets',
  'seriespill.view.colForm': 'Form',
  'seriespill.view.formWin': 'Win',
  'seriespill.view.formLoss': 'Loss',
  'seriespill.view.formDraw': 'Draw',
  'seriespill.view.formWinShort': 'W',
  'seriespill.view.formLossShort': 'L',
  'seriespill.view.formDrawShort': 'D',
  // One round card in the schedule.
  'seriespill.view.editRoundStartTitle': 'Click to change when the round starts',
  'seriespill.view.announcedOn': 'Announced {date} ✎',
  'seriespill.view.beforeRoundStarts': 'before the round starts',
  'seriespill.view.announceNow': 'Announce the round now',
  'seriespill.view.startsOn': 'Starts {date} ✎',
  'seriespill.view.roundDone': 'Finished',
  'seriespill.view.roundLeft': '{left} of {total} left',
  'seriespill.view.activeNow': 'Active now',
  'seriespill.view.partialHidden':
    'Only played matches are shown — upcoming ones in this round are announced later.',
  'seriespill.view.avgElo': 'Avg ELO {elo}',
  'seriespill.view.voided': 'Voided',
  'seriespill.view.draw': 'Draw',
  'seriespill.view.result': 'Result',
  'seriespill.view.courtReserved': 'Court reserved',
  'seriespill.view.change': 'change',
  'seriespill.view.maxActiveReached': 'Max active reached',
  'seriespill.view.maxActiveTitle':
    'The maximum number of active reservations is reached for: {names}. Play one of the upcoming reservations off first.',
  'seriespill.view.playerCount': '{count} players',
  'seriespill.view.reserveCourt': 'Reserve a court',
  'seriespill.view.endRoundLeadOne': '{count} unplayed match gets',
  'seriespill.view.endRoundLeadMany': '{count} unplayed matches get',
  'seriespill.view.unplayedVoided': 'the status «voided» (it does not count)',
  'seriespill.view.unplayedWo': 'a 0–2 loss (WO) for both teams',
  'seriespill.view.endRoundQuestion': '. Close round {round}?',
  'seriespill.view.endRoundYes': 'Yes, close the round',
  'seriespill.view.endRoundNow': 'Close round {round} now',
  'seriespill.view.editDeadlineTitle': 'Click to change the deadline',
  'seriespill.view.deadlineOn': 'Deadline {date}',
  'seriespill.view.autoSuffix': '· auto',
  'seriespill.view.editRoundStart': 'Change when the round starts',
  'seriespill.view.startOn': 'Start {date} ✎',
  'seriespill.view.editDeadline': 'Change the deadline',
  'seriespill.view.deadlineWithLeft': 'Deadline {date} · {left} ✎',
  'seriespill.view.hideRoundAgain': 'Hide the round again',
  'seriespill.view.unannounce': '↩ Un-announce',
  'seriespill.view.playedRange': 'Played {from}–{to}',
  'seriespill.view.playedOn': 'Played {date}',
  'seriespill.view.roundEndsNote': '· the round ends when the last match is played',
  // The schedule, per division and round-major.
  'seriespill.view.scheduleFor': 'Schedule — {name}',
  'seriespill.view.scheduleAllDivisions': 'Schedule — every division',
  'seriespill.view.noPairsHere': 'No pairs in this division.',
  'seriespill.view.noPairsHint':
    'Use «Settings {name}» at the top to add pairs. Once there are at least 2, the schedule is generated automatically.',
  'seriespill.view.noMatchesYet': 'No matches yet.',
  'seriespill.view.hideDoneRounds': 'Hide finished rounds',
  'seriespill.view.showDoneRoundsOne': 'Show {count} finished round',
  'seriespill.view.showDoneRoundsMany': 'Show {count} finished rounds',
  'seriespill.view.hideFutureRounds': 'Hide upcoming rounds',
  'seriespill.view.showFutureRoundsOne': 'Show {count} upcoming round',
  'seriespill.view.showFutureRoundsMany': 'Show {count} upcoming rounds',
  // The team search.
  'seriespill.view.searchTeam': 'Search for a team…',
  'seriespill.view.allTeams': 'All teams',
  'seriespill.view.teamsMatch': '{count} teams match',
  'seriespill.view.clearTeamFilter': 'Clear the team filter',
  // The temporary development tools.
  'seriespill.view.devBanner': '⚠ Temporary (under development)',
  'seriespill.view.repairReport':
    '✓ Repaired: {ids} ids renewed, {dupes} duplicates removed, {relinked} matches relinked ({before}→{after} pairs)',
  'seriespill.view.repairTitle':
    'Gives every pair a unique id and relinks the matches (fixes duplicate pairs from older data)',
  'seriespill.view.repairAction': '🔧 Repair pair ids',
  'seriespill.view.deleteSeries': '🗑 Delete the whole league',
  'seriespill.view.deleteConfirm': 'Delete EVERYTHING and start over?',
  'seriespill.view.deleteYes': 'Yes, delete',
  // The hero header.
  'seriespill.view.allClasses': 'All classes',
  'seriespill.view.classAndSeason': '{name} · Season {season}',
  'seriespill.view.lockReasonCompleted': 'Everyone has played',
  'seriespill.view.lockReasonDeadline': 'The deadline has passed',
  'seriespill.view.seasonLocked': 'Season locked',
  'seriespill.view.seasonActive': 'Season active',
  'seriespill.view.freeBooking': 'Free booking',
  'seriespill.view.allocatedStandalone': 'Allocated · standalone',
  'seriespill.view.allocatedBooking': 'Allocated · Court booking',
  'seriespill.view.doubleRound': 'Double round',
  'seriespill.view.singleRound': 'Single round',
  'seriespill.view.editSeasonDeadlineTitle': 'Click to change the season deadline',
  'seriespill.view.seasonDeadlineOn': 'Deadline {date}',
  'seriespill.view.setSeasonDeadline': 'Set a deadline',
  'seriespill.view.rules': '📋 Rules',
  'seriespill.view.rulesTitle': 'See the league rules',
  'seriespill.view.pastSeasonsOne': '🗓 {count} past season →',
  'seriespill.view.pastSeasonsMany': '🗓 {count} past seasons →',
  'seriespill.view.historyTitle': 'See past seasons — tables and matches',
  'seriespill.view.mainSettings': 'Main settings',
  'seriespill.view.mainSettingsTitle':
    'Main settings for the whole league (name, dates, auto mode, retiring it)',
  'seriespill.view.classSettings': 'Settings {name}',
  'seriespill.view.classSettingsTitle': 'Settings for {name}: divisions, pairs, dates',
  'seriespill.view.theClass': 'the class',
  'seriespill.view.startSeason': '⚡ Start season {season} →',
  'seriespill.view.startSeasonTitle':
    'The season for {name} is locked. Confirm to start the next one with promotion and relegation.',
  'seriespill.view.seasonRunning': 'Season {season} is under way',
  'seriespill.view.seasonRunningTitle':
    'Season {season} is under way — the button becomes active once every match is played or the deadline is reached.',
  'seriespill.view.autoModeOn': '⚡ Auto mode on',
  'seriespill.view.demoRound': '🎲 Random (this round)',
  'seriespill.view.demoRoundTitle':
    'Demo: fill the next round of unplayed matches in the shown divisions — everyone plays one match',
  'seriespill.view.demoAll': '🎲 Random (everything)',
  'seriespill.view.demoAllTitle':
    'Demo: fill random results for every unplayed match in the shown divisions and classes',
  'seriespill.view.divisionCountOne': '{count} division',
  'seriespill.view.divisionCountMany': '{count} divisions',
  'seriespill.view.addClassLabel': 'Add a class:',
  'seriespill.view.addClassTitle':
    'Add a {name} class to the league (it starts empty — add pairs under Settings)',
  'seriespill.view.readyToStart': '⚡ Ready to start the season',
  'seriespill.view.readyToStartHint':
    'Pairs are registered, but the schedule has not been generated yet. Generate the matches for every division with at least 2 pairs.',
  'seriespill.view.startSeasonAction': 'Start the season →',
  'seriespill.view.divisionChevron': 'Division ›',
  // The tab bar.
  'seriespill.view.tabTable': '📊 Table',
  'seriespill.view.tabSchedule': '🎾 Schedule',
  'seriespill.view.tabCalendar': '📆 Calendar',
  'seriespill.view.tabBanepad': '📲 Banepad',
  'seriespill.view.tabSignup': '💳 Season sign-up',
  'seriespill.view.progress': '{played}/{total} matches · {pct}%',
  'seriespill.view.noPadMatches':
    'No matches are placed on courts yet. Place matches on a court to use the Banepad.',
  // The prompt after a round is closed by hand.
  'seriespill.view.roundEnded': 'Round {round} closed',
  'seriespill.view.nextRoundPlanned':
    'The next round (round {round}) is planned to start {date}. Keep the deadline, or move it so the next round starts now?',
  'seriespill.view.afterScheduledDeadline': 'at the scheduled deadline',
  'seriespill.view.keepDeadline': 'Keep the deadline',
  'seriespill.view.startRoundNow': 'Start round {round} now',
  // The calendar tab.
  'seriespill.view.noRoomsLead': 'No time slots set up yet. Go to',
  'seriespill.view.mainSettingsBookingLink': 'Main settings → Court booking',
  'seriespill.view.noRoomsTail': 'to set up playing days and courts.',
  'seriespill.view.noReservedSlots': 'No reserved slots yet. Reserve the courts under',
  'seriespill.view.noMatchesInFilter': 'No matches in this filter.',
  'seriespill.view.releaseUnusedOne': 'Release {count} unused court',
  'seriespill.view.releaseUnusedMany': 'Release {count} unused courts',
  'seriespill.view.freeSlot': 'free',
  'seriespill.view.close': 'Close',
  'seriespill.view.swap': 'Swap',
  'seriespill.view.drawSuffix': ' · draw',
  'seriespill.view.playedSuffix': ' · played',
  'seriespill.view.reservedTimes': 'Reserved times · who plays',
  'seriespill.view.filledOfTotal': '{filled}/{total} filled',
  'seriespill.view.noUpcoming': 'No upcoming matches',
  'seriespill.view.noUpcomingAllPlayed': ' — everything is played. See the history below.',
  'seriespill.view.noUpcomingDot': '.',
  'seriespill.view.calendarHistory': '📦 History · played / past ({count})',
  'seriespill.view.reservedTimesNote':
    'Shows only playing days and times that are reserved, and follows the current class/division filter. Green rows hold a match; «free» = a reserved slot with no match yet.',
  'seriespill.view.unusedCourtsOne': '{count} reserved court with no match.',
  'seriespill.view.unusedCourtsMany': '{count} reserved courts with no match.',
  'seriespill.view.releaseBackOne': 'Release {count} unused',
  'seriespill.view.releaseBackMany': 'Release {count} unused',
  'seriespill.view.weeklyTemplate': 'Weekly template · reserved courts',
  'seriespill.view.weeklyTemplateNote':
    'Fixed slots that repeat every week. Shared length {min} min · 1 slot = 1 match. The matches are placed into these slots when the generator runs.',
  'seriespill.view.timeBetweenRounds': 'Time between rounds',
  'seriespill.view.matchesPerRound': '{matches} matches/round · {slots} slots/week',
  'seriespill.view.noPairsYet': 'no pairs yet',
  'seriespill.view.weeksOne': '~{weeks} week',
  'seriespill.view.weeksMany': '~{weeks} weeks',
  'seriespill.view.roundDeadlineIsOne': '⚠ the round deadline is {weeks} week',
  'seriespill.view.roundDeadlineIsMany': '⚠ the round deadline is {weeks} weeks',
  'seriespill.view.cadenceNote':
    'Worked out from the matches in one round divided by the available slots per week.',
  'seriespill.view.configuredDeadlineOne': 'Configured round deadline: {weeks} week.',
  'seriespill.view.configuredDeadlineMany': 'Configured round deadline: {weeks} weeks.',
  'seriespill.view.colTime': 'Time',
  'seriespill.view.roomsWithoutClasses':
    '⚠ One or more time slots have no class attached — no matches will be placed in them.',
  // The move-match dialog.
  'seriespill.view.moveMatch': 'Move the match',
  'seriespill.view.currentSlot': 'Current: {date} {time} · Court {court} ·',
  'seriespill.view.swapSafe': 'Safe',
  'seriespill.view.swapClash': 'Clash (same time / back to back)',
  'seriespill.view.clashSameTime': 'same time',
  'seriespill.view.clashBackToBack': 'back to back',
  'seriespill.view.noOtherSlots': 'No other reserved slots to move to.',
  'seriespill.view.safeOfTotal': '{safe} safe / {total}',
  'seriespill.view.timeAndCourt': '{time} · Court {court}',
  'seriespill.view.swapWith': '↔ {name}',
  // Results and substitutions.
  'seriespill.view.errRecordResult': 'The result could not be recorded',
  'seriespill.view.errSubstituteNotFound': 'The substitute was not found in the player list.',
  'seriespill.view.errSwapFailed': 'The substitution could not be made.',
  'seriespill.view.playerFallback': 'Player',
  'seriespill.view.theOldPlayer': 'the previous player',
  'seriespill.view.swapDone':
    '{original} was substituted by {replacement}. Played rounds keep {originalOld}; {replacement} takes over from the next unplayed round and shows in the table with a substitution icon.',
  // ── tournament: scheduled breaks (BreakRow) ──────────────────────
  'tournament.break.namePlaceholder': 'Break name',
  'tournament.break.afterPhase': 'After phase',
  'tournament.break.afterPhaseTitle':
    'The break starts as soon as the chosen phase has finished (no idle time)',
  'tournament.break.fixedTime': 'Fixed time',
  'tournament.break.fixedTimeTitle':
    'The break starts at the given clock time (may leave idle time if matches finish earlier)',
  'tournament.break.phaseSelectTitle': 'Which phase the break follows',
  'tournament.break.nameLabel': 'Name of the break',
  'tournament.break.startLabel': 'When the break starts',
  'tournament.break.durationLabel': 'Length of the break in minutes',
  'tournament.break.remove': 'Remove the break',
  'tournament.break.phaseOption': 'Phase {phase}',
  'tournament.break.afterPhaseOption': 'After phase {phase}',
  'tournament.break.afterPhaseHint':
    'The break starts as soon as phase {phase} has finished — no idle time before it.',
  'tournament.break.timeHint':
    'The break starts at exactly {time}. If matches finish before that, there will be idle time.',

  // ── tournament: the half-built tournament (BrokenTournamentView) ──
  'tournament.broken.back': 'Back to home',
  'tournament.broken.title': 'This tournament was never finished',
  'tournament.broken.lead':
    'This tournament has no matches — it was probably never finished in the wizard, or the data is corrupt. You can delete it and start a new one.',
  'tournament.broken.name': 'Name:',
  'tournament.broken.players': 'Players:',
  'tournament.broken.groups': 'Groups:',
  'tournament.broken.matches': 'Matches:',
  'tournament.broken.missing': 'missing',
  'tournament.broken.confirmDelete': 'Delete this tournament permanently? It cannot be restored.',
  'tournament.broken.delete': 'Delete the tournament',
  'tournament.broken.afterDelete':
    'Once it is deleted you can start a new tournament from the home screen.',

  // ── tournament: the club-vs-club banner (ClubPadBanner) ──────────
  'tournament.clubPad.record': '{wins}W · {losses}L · {ties}D',
  'tournament.clubPad.clubA': 'Club A',
  'tournament.clubPad.clubB': 'Club B',
  'tournament.clubPad.matchesPlayed': '{n} matches',

  // ── tournament: confirming a result (ConfirmResultModal) ─────────
  'tournament.confirm.headerFinals': 'Court {court} · Final',
  'tournament.confirm.headerPhase': 'Court {court} · Phase {phase}',
  'tournament.confirm.headerPhaseRound': 'Court {court} · Phase {phase} · R{round}',
  'tournament.confirm.title': 'Confirm the result',
  'tournament.confirm.draw': 'Draw: {s1} – {s2}',
  'tournament.confirm.winnerLabel': 'Winner:',
  'tournament.confirm.explainer':
    'Once you confirm, the ELO rating is updated for both teams and the result is added to the schedule. You can change it later by pressing "Edit" on the previous match.',
  'tournament.confirm.cancel': 'Cancel',
  'tournament.confirm.submit': 'Confirm and send',

  // ── tournament: one player in a match row (PlayerCell) ───────────
  'tournament.playerCell.groupTitle': 'Group {group}',
  'tournament.playerCell.showProfile': "Show {name}'s profile",
  'tournament.playerCell.eloTitle': 'ELO: {elo}',
  'tournament.playerCell.luckyUp':
    'Lucky Looser — won group {group} and moved up here, to the bronze final',
  'tournament.playerCell.luckyDown': 'Moved down from group {group} (last place)',
  'tournament.playerCell.newbieBoost':
    "New-player boost {boost} — falls off linearly and reaches 1x once the player has played {threshold} matches (adjusted for the tournament's ELO weight).",
  'tournament.playerCell.tba': 'TBA',
  'tournament.playerCell.position': 'Place {position}',
  'tournament.playerCell.luckyHintUp':
    'Lucky Looser — the winner of group {group} moves up and plays here',
  'tournament.playerCell.luckyHintDown':
    'The last-placed player in group {group} moves down to here',

  // ── tournament: the saved-setup dropdown (SetupDropdown) ─────────
  'tournament.setupDropdown.load': 'Load a setup…',
  'tournament.setupDropdown.loadTitle': 'Load a saved setup',
  'tournament.setupDropdown.confirmDelete': 'Delete "{name}"?',
  'tournament.setupDropdown.delete': 'Delete',
  'tournament.setupDropdown.cancel': 'Cancel',
  'tournament.setupDropdown.metaPlayersCourts': '{players} pl · {courts} courts',
  'tournament.setupDropdown.metaCourts': '{courts} courts',
  'tournament.setupDropdown.metaFallback': 'Setup',
  'tournament.setupDropdown.deleteTitle': 'Delete the setup',

  // ── tournament: the seeding cards (SeedGroupCard) ────────────────
  'tournament.seedGroup.title': 'Group {letter}',
  'tournament.seedGroup.meta': '{players} players · ELO ⌀ {elo}',
  'tournament.seedGroup.top': 'Top',
  'tournament.seedGroup.bottom': 'Bottom',
  'tournament.seedGroup.suggested': 'Suggested: group {group}',
  'tournament.seedGroup.unlockTitle': 'Locked — click to unlock',
  'tournament.seedGroup.lockTitle': 'Lock to this group',
  'tournament.seedGroup.dropHere': 'Drop players here',

  // ── tournament: the match-list header (UniversalMatchListHeader) ──
  'tournament.matchList.colCourt': 'Court',
  'tournament.matchList.home': 'Home team',
  'tournament.matchList.away': 'Away team',
  'tournament.matchList.serves': 'Serves first in this round',
  'tournament.matchList.player1': 'Player 1',
  'tournament.matchList.player2': 'Player 2',
  'tournament.matchList.teamElo': 'Team ELO',
  'tournament.matchList.score': 'Score',
  'tournament.matchList.vs': 'vs',

  // ── tournament: the tiebreak-order editor (TiebreakOrderEditor) ──
  'tournament.tiebreak.defaultTitle': 'Ranking within the group',
  'tournament.tiebreak.activeCount': '{count} active',
  'tournament.tiebreak.dragTitle': 'Drag to reorder',
  'tournament.tiebreak.offShort': 'off',
  'tournament.tiebreak.disableTitle': 'Click to turn off',
  'tournament.tiebreak.enableTitle': 'Click to turn on',
  'tournament.tiebreak.on': 'On',
  'tournament.tiebreak.off': 'Off',
  'tournament.tiebreak.reset': 'Reset to the default',

  // ── americano: the ranking-order note (AmericanoSetupView, AmericanoSettingsModal) ──
  'americano.ranking.h2hNote':
    'Head-to-head is always applied last, wherever it sits in the list: it never decides a place, only the order of players who share one.',

  // ── americano: the «Lagre som mal» dialog's name field (AmericanoSetupView, #900) ──
  'americano.setup.templateName': 'Template name',

  // ── help: Americano points & ranking (AmericanoScoringSection) ──
  'help.americanoScoring.title': 'Points and ranking',
  'help.americanoScoring.intro.before':
    'The result of every match is added up on a leaderboard. Players are ranked by the chosen tiebreak order — which is',
  'help.americanoScoring.intro.strong': 'fully adjustable',
  'help.americanoScoring.intro.after': 'in Americano (unlike Padel24).',
  'help.americanoScoring.primary.title': 'Primary ranking',
  'help.americanoScoring.primary.headMode': 'Mode',
  'help.americanoScoring.primary.headCounts': 'What counts',
  'help.americanoScoring.primary.winsFirst': 'Wins first (default)',
  'help.americanoScoring.primary.winsFirstOrder': '1) Wins, 2) point difference, 3) total points',
  'help.americanoScoring.primary.pointsFirst': 'Points first',
  'help.americanoScoring.primary.pointsFirstOrder': '1) Total points, 2) wins, 3) point difference',
  'help.americanoScoring.primary.draws':
    'A draw counts as half a win in the ranking — one win and one draw rank ahead of one win and one loss. The leaderboard shows draws in their own column. The same applies to a bench round with the «Uavgjort + snitt» reward.',
  'help.americanoScoring.h2h.title': 'Head-to-head',
  'help.americanoScoring.h2h.intro':
    'Head-to-head never decides a place. Players who are level on every other criterion share the place. With head-to-head turned on, it only decides the order they are listed in within that shared place — always last, wherever it sits in the ranking list.',
  'help.americanoScoring.h2h.headSetting': 'Setting',
  'help.americanoScoring.h2h.headEffect': 'Effect',
  'help.americanoScoring.h2h.on': 'On (default)',
  'help.americanoScoring.h2h.onEffect':
    'Players who share a place are listed by their results against each other',
  'help.americanoScoring.h2h.off': 'Off',
  'help.americanoScoring.h2h.offEffect':
    'Players who share a place are listed in a technical order — not a ranking',
  'help.americanoScoring.h2h.cardTitle': 'Head-to-head in Americano',
  'help.americanoScoring.h2h.cardBody':
    'Worked out from every match in which the players were opponents on opposite teams; draws do not count. Each player sharing the place gets their wins against the others in that shared place minus their losses to them, and the highest is listed first. If A has beaten B twice and B has beaten A once, A is listed above B — but they still share the place. If it goes round in a circle (A beat B, B beat C, C beat A), they are all level.',

  // ── tournament: groups & seeding, wizard step 3 (Step3Groups) ────
  'tournament.groups.generating': 'Generating groups…',
  'tournament.groups.title': 'Groups & seeding',
  'tournament.groups.lead':
    '{count} groups suggested from ELO. Drag and drop to move a player. Lock the ones that must not change.',
  'tournament.groups.regenerate': 'Suggest again',
  'tournament.groups.lockedCount': '({count} locked)',
  'tournament.groups.hintDrag': 'Drag a player card to another group',
  'tournament.groups.hintLock': 'Click the lock to keep a player in the group when regenerating',
  'tournament.groups.hintArrow': 'An orange arrow means the player is not in the suggested group',

  // ── tournament: the final ranking (FinalRankingTab) ──────────────
  'tournament.ranking.finalsPending': 'The finals are drawn after the last group phase.',
  'tournament.ranking.position': 'Place {position}',
  'tournament.ranking.positionRange': 'Places {from}–{to}',
  'tournament.ranking.title': 'The tournament result',
  'tournament.ranking.lead':
    'The ranking follows the finals. The winners of final 1 take 1st place and the losers 2nd; final 2 plays for 3rd and 4th, and so on.',
  'tournament.ranking.pendingFinals':
    '{done} of {total} finals finished — {left} finals still undecided',
  'tournament.ranking.winnersLabel': '🏆 Tournament winners',
  'tournament.ranking.wonGoldFinal': 'Won the gold final {score}',
  'tournament.ranking.colPlace': 'Place',
  'tournament.ranking.colPlayers': 'Players',
  'tournament.ranking.colFinal': 'Final',
  'tournament.ranking.colResult': 'Result',
  'tournament.ranking.waiting': '(pending)',
  'tournament.ranking.notPlayed': 'Not played',

  // ── tournament: the compact match card (MatchCard) ───────────────
  'tournament.matchCard.courtShort': 'C{court}',
  'tournament.matchCard.final': 'Final',
  'tournament.matchCard.phaseRound': 'P{phase}·R{round}',
  'tournament.matchCard.cross': 'Cross {group}',
  'tournament.matchCard.group': 'Grp {group}',
  'tournament.matchCard.luckyLooserTitle':
    'Bronze final with a Lucky Looser — the winner of the group below plays here',
  'tournament.matchCard.luckyLooser': 'LUCKY LOOSER',
  'tournament.matchCard.done': '✓ Done',
  'tournament.matchCard.vs': 'vs',
  'tournament.matchCard.cancel': 'Cancel',
  'tournament.matchCard.save': 'Save',
  'tournament.matchCard.edit': 'Edit',
  'tournament.matchCard.enterScore': 'Enter the score',
  'tournament.matchCard.playersLater':
    'The players are decided once the earlier phases have finished',

  // ── tournament: a player's own match row (MatchRow) ──────────────
  'tournament.matchRow.final': 'Final',
  'tournament.matchRow.phase': 'Phase {phase}',
  'tournament.matchRow.round': 'Round {round}',
  'tournament.matchRow.roundShort': 'R{round}',
  'tournament.matchRow.thisPlayer': 'This player',
  'tournament.matchRow.groupTitle': 'Group {group}',
  'tournament.matchRow.newbieBoost':
    "New-player boost {boost} — falls off linearly from {from}x (match 1) to 1x once the player has played {threshold} matches (adjusted for the tournament's ELO weight).",
  'tournament.matchRow.teamEloTitle': "The team's average ELO before the match",
  'tournament.matchRow.teamEloTitleStrongest':
    "The team's average ELO before the match (the stronger team)",
  'tournament.matchRow.eloChangeTitle': 'ELO change after the match',
  'tournament.matchRow.court': 'Court {court}',
  'tournament.matchRow.vs': 'vs',
  'tournament.matchRow.notPlayed': 'Not played',
  'tournament.matchRow.draw': 'Draw',
  'tournament.matchRow.win': 'Win',
  'tournament.matchRow.loss': 'Loss',

  // ── tournament: the full match table (MatchTable) ────────────────
  'tournament.matchTable.empty': 'No matches to show with this filter.',
  'tournament.matchTable.home': 'Home team',
  'tournament.matchTable.homeServes': 'The home team serves first',
  'tournament.matchTable.away': 'Away team',
  'tournament.matchTable.colTime': 'Time',
  'tournament.matchTable.colMatch': 'Match',
  'tournament.matchTable.colCourt': 'Court',
  'tournament.matchTable.colPlayer1': 'Player 1',
  'tournament.matchTable.colPlayer2': 'Player 2',
  'tournament.matchTable.colTeamElo': 'Team ELO',
  'tournament.matchTable.colScore': 'Score',
  'tournament.matchTable.colVs': 'vs',
  'tournament.matchTable.breakLabel': 'Break',
  'tournament.matchTable.rotation': 'Match rotation {n}',

  // ── tournament: one match-table row (MatchTableRow) ──────────────
  'tournament.matchTableRow.final': 'Final',
  'tournament.matchTableRow.finalNumbered': 'Final {number}',
  'tournament.matchTableRow.phase': 'Phase {phase}',
  'tournament.matchTableRow.roundShort': 'R{round}',
  'tournament.matchTableRow.unscheduled': 'Not scheduled',
  'tournament.matchTableRow.vs': 'vs',

  // ── tournament: the ELO standings tab (StandingsTab) ─────────────
  'tournament.standings.title': 'Tournament performance',
  'tournament.standings.lead':
    'Players ranked by their ELO change over the tournament — the biggest gain first.',
  'tournament.standings.biggestGain': 'Biggest gain',
  'tournament.standings.biggestLoss': 'Biggest drop',
  'tournament.standings.playersUp': 'Players up',
  'tournament.standings.playersDown': 'Players down',
  'tournament.standings.ofTotal': 'of {total}',
  'tournament.standings.colPlayer': 'Player',
  'tournament.standings.colGroup': 'Grp',
  'tournament.standings.colPlayed': 'P',
  'tournament.standings.colPlayedTitle': 'Matches played',
  'tournament.standings.colWins': 'W',
  'tournament.standings.colWinsTitle': 'Wins',
  'tournament.standings.colLosses': 'L',
  'tournament.standings.colLossesTitle': 'Losses',
  'tournament.standings.colDraws': 'D',
  'tournament.standings.colDrawsTitle': 'Draws',
  'tournament.standings.colStart': 'Start',
  'tournament.standings.colNow': 'Now',
  'tournament.standings.colChange': 'Change',

  // ── tournament: the phase-1 group editor (Padel24GroupEditor) ────
  'tournament.groupEditor.title': 'Groups · Preview',
  'tournament.groupEditor.redraw': 'Draw again',
  'tournament.groupEditor.redrawTitle': 'Draw the phase 1 matches again from the updated groups',
  'tournament.groupEditor.drawn': '✓ The matches are drawn',
  'tournament.groupEditor.dirtyBefore':
    'You have changed the groups, so the schedule no longer fits. Press',
  'tournament.groupEditor.dirtyButton': '«Draw again»',
  'tournament.groupEditor.dirtyAfter': 'when you have finished moving players.',
  'tournament.groupEditor.hint':
    'Drag a player (or use the arrows) to move them. Everyone else shifts one place, and the groups always keep the same size — across group boundaries too.',
  'tournament.groupEditor.groupTitle': 'Group {letter}1',
  'tournament.groupEditor.playerCount': '{count} players',
  'tournament.groupEditor.levelShort': 'L{level}',
  'tournament.groupEditor.levelTitle': 'Level {level} · {name}',
  'tournament.groupEditor.eloTitle': 'ELO',
  'tournament.groupEditor.moveUp': 'Move up',
  'tournament.groupEditor.moveDown': 'Move down',

  // ── tournament: the full schedule tab (FullPlanTab) ──────────────
  'tournament.fullPlan.title': 'The full schedule',
  'tournament.fullPlan.subtitle': '{matches} matches · {done} finished',
  'tournament.fullPlan.statMatches': 'Matches in total',
  'tournament.fullPlan.statPlayers': 'Players',
  'tournament.fullPlan.statRounds': 'Rounds',
  'tournament.fullPlan.statDone': 'Finished',
  'tournament.fullPlan.tabAll': 'Everything',
  'tournament.fullPlan.tabPhase': 'Phase {phase}',
  'tournament.fullPlan.tabFinals': 'Finals',
  'tournament.fullPlan.searchPlaceholder': 'Search for a player…',
  'tournament.fullPlan.clearSearch': 'Clear the search',
  'tournament.fullPlan.showLabel': 'Show:',
  'tournament.fullPlan.allDays': 'All days',
  'tournament.fullPlan.hideCompleted': 'Hide finished',
  'tournament.fullPlan.hideUndrawn': 'Hide undrawn',
  'tournament.fullPlan.showElo': 'Show ELO',
  'tournament.fullPlan.lockTitle': 'Some finals show early — but cannot be entered yet',
  'tournament.fullPlan.lockBefore':
    'Players can see which finals they are in ahead of time, but results cannot be entered until',
  'tournament.fullPlan.lockStrong': 'every group has finished the last phase',

  // ── tournament: the groups tab (GroupsTab) ───────────────────────
  'tournament.groupsTab.headerFinal': 'Groups · Final standings',
  'tournament.groupsTab.headerPhase': 'Groups · Phase {phase}',
  'tournament.groupsTab.previewTag': '(preview)',
  'tournament.groupsTab.previewTitle': 'Preview — {drawn} of {total} groups drawn so far',
  'tournament.groupsTab.showPhaseTitle': 'Show the groups in phase {phase}',
  'tournament.groupsTab.phaseButton': 'Phase {phase}',
  'tournament.groupsTab.historical': '📜 Historical view — the final state of phase {phase}',
  'tournament.groupsTab.previewLead':
    '✨ Preview of phase {phase} — the groups fill up as they are drawn. The standings start from zero when the phase officially begins.',
  'tournament.groupsTab.promotionLead':
    'The top {count} move up · the bottom {count} move down after the phase',
  'tournament.groupsTab.groupTitle': 'Group {label}',
  'tournament.groupsTab.metaDrawn': '{players} players · drawn',
  'tournament.groupsTab.metaWaiting': 'Waiting for the draw',
  'tournament.groupsTab.metaPhase': '{players} players · phase {phase}',
  'tournament.groupsTab.drawnTag': 'Drawn',
  'tournament.groupsTab.notDrawnTag': 'Not drawn',
  'tournament.groupsTab.topTag': 'Top',
  'tournament.groupsTab.drawnLater': 'Drawn once the neighbouring groups have finished.',
  'tournament.groupsTab.colPlayer': 'Player',
  'tournament.groupsTab.colElo': 'ELO',
  'tournament.groupsTab.colWins': 'W/P',
  'tournament.groupsTab.colWinsTitle': 'Wins / played',
  'tournament.groupsTab.colDiff': 'Diff',
  'tournament.groupsTab.colDiffTitle': 'Game difference',
  'tournament.groupsTab.aheadBy': 'Ahead of {next} on: {reason}',
  'tournament.groupsTab.why.wins': 'more wins',
  'tournament.groupsTab.why.gamesDiff': 'better game difference',
  'tournament.groupsTab.why.h2h': 'won the head-to-head',
  'tournament.groupsTab.why.gamesWon': 'more games won',
  'tournament.groupsTab.why.oppStrength': 'tougher opposition this phase',
  'tournament.groupsTab.why.draw': 'a draw — everything else is level',
  'tournament.groupsTab.colGames': 'Games',
  'tournament.groupsTab.colGamesTitle': 'Games won',
  'tournament.groupsTab.newPlayerTag': 'NEW',
  'tournament.groupsTab.newPlayerTitle': 'New in this group — moved up or down from another group',

  // ── tournament: the group filter (GroupFilterDropdown) ───────────
  'tournament.groupFilter.label': 'Group:',
  'tournament.groupFilter.all': 'All groups',
  'tournament.groupFilter.group': 'Group {letter}',

  // ── tournament: the event log (LogTab) ───────────────────────────
  'tournament.log.title': 'Event log ({count})',
  // ── Tournament event log (#519) ──────────────────────────────────
  'tournament.log.event.started': 'Tournament started — phase {phase}, round {round}',
  'tournament.log.event.created':
    'Tournament created — {matches} matches planned. Preview: adjust freely, then press «Start tournament».',
  'tournament.log.event.phase1Redrawn': 'Phase 1 redrawn after a group change',
  'tournament.log.event.paused': 'Paused',
  'tournament.log.event.resumed': 'Resumed',
  'tournament.log.event.result': 'Result: {team1} {score1}–{score2} {team2}{elo}',
  'tournament.log.event.finalResult': 'Final: {team1} {score1}–{score2} {team2}{elo}',
  'tournament.log.event.resultBanepad': '[Banepad] Result: {team1} {score1}–{score2} {team2}{elo}',
  'tournament.log.event.resultBanepadEdit':
    '[Banepad edit] Result: {team1} {score1}–{score2} {team2}{elo}',
  'tournament.log.event.groupDoneFinalsReady':
    'Group {group} finished phase {phase} — the group’s finals are ready',
  'tournament.log.event.allGroupsDoneBonus':
    'All groups finished — bonus points awarded to the top 3 in each group (+3/+2/+1 towards the final ranking)',
  'tournament.log.event.allGroupsDoneFinalsDrawn': 'All groups finished — finals drawn',
  'tournament.log.event.allFinalsDone': 'All finals finished — tournament complete',
  'tournament.log.event.completed': 'Tournament complete!',
  'tournament.log.event.finishedRanked': 'Tournament finished — final ranking generated',
  'tournament.log.event.phaseDoneFinalsReady': 'Phase {phase} finished — finals ready',
  'tournament.log.event.phaseStartedHistory':
    '→ Phase {phase} starting — matches drawn from partner/opponent history',
  'tournament.log.event.phaseRound': '→ Phase {phase}, round {round}',
  'tournament.log.event.phaseDoneBonusPerGroup':
    'Phase {phase} finished — bonus points awarded to the top 3 in each group (+3/+2/+1 towards the final ranking)',
  'tournament.log.event.phaseDoneBonusPromotion':
    'Phase {phase} finished — bonus points awarded (+3/+2/+1 for the top 3) and promotion/relegation applied',
  'tournament.log.event.finalsNamed': 'Finals and placement matches are ready, with names',
  'tournament.log.event.groupPhaseDrawn':
    '🎯 Group {group}: phase {phase} drawn — other groups are unaffected',
  'tournament.log.event.groupsDrawnOne': '🎯 Drew 1 group for phase {phase}: {groups}',
  'tournament.log.event.groupsDrawnMany': '🎯 Drew {count} groups for phase {phase}: {groups}',
  'tournament.log.event.groupFinalsDrawn':
    '🏆 Group {group}: finals drawn from the final ranking in phase {phase}',
  'tournament.log.event.groupFinalsDrawnOne': '🏆 Drew finals for 1 group: {groups}',
  'tournament.log.event.groupFinalsDrawnMany': '🏆 Drew finals for {count} groups: {groups}',
  'tournament.log.event.substitution':
    '⇄ Substitution: {original} → {replacement} (ELO {eloFrom} → {eloTo}) · Phase {phase}, Round {round} · After {completed}/{total} matches',
  'tournament.log.event.americanoSubstitutionOne':
    '⇄ Substitution: {original} → {replacement} (ELO {eloFrom} → {eloTo}) · Round {round} · The original had played {count} match',
  'tournament.log.event.americanoSubstitutionMany':
    '⇄ Substitution: {original} → {replacement} (ELO {eloFrom} → {eloTo}) · Round {round} · The original had played {count} matches',
  'tournament.log.event.placeholderFilled':
    '✓ Reserved place filled: {placeholder} → {player} (ELO {elo})',
  'tournament.log.event.parkedReadyNewTournament':
    'Saved (ready to start) — set aside when a new tournament started',
  'tournament.log.event.setAsideRunningNewTournament': 'Set aside (still in progress)',
  'tournament.log.event.autoParkedNewTournament': 'Parked automatically (a new tournament started)',
  'tournament.log.event.parkedReady': 'Saved (ready to start) and set aside',
  'tournament.log.event.pausedParked': 'Paused and set aside',
  'tournament.log.event.setAsideRunningSwitched':
    'Set aside (still in progress) — switched to another tournament',
  'tournament.log.event.autoParkedSwitched':
    'Parked automatically (switched to another tournament)',
  'tournament.log.event.resumedFromParked': 'Resumed from pause',
  'tournament.log.event.pausedFromHome': 'Paused from the home page',
  'tournament.log.event.restoredFromArchive': 'Restored from the archive and resumed',
  'tournament.log.event.resetToReady': 'Reset to ready to start',
  'tournament.log.event.previewBuilt': 'Preview built — {matches} matches planned',
  'tournament.log.event.markedInjured': '{player} marked as injured — left out of new rounds',
  'tournament.log.event.returnedFromInjury': '{player} back from injury',
  'tournament.log.event.roundDeleted':
    'Round {round} deleted — {matches} matches removed, ELO rolled back, later rounds renumbered',

  // ── tournament: the matchup header (MatchupLines) ────────────────
  'tournament.matchup.home': 'Home',
  'tournament.matchup.away': 'Away',
  'tournament.matchup.avgElo': 'avg ELO',
  'tournament.matchup.vs': 'VS',
  'tournament.matchup.tba': 'TBA',
  'tournament.matchup.placeholder': 'Place {position} in G{group}',

  // ── tournament: the format picker (CreateTournamentView) ─────────
  'tournament.create.back': 'Back',
  'tournament.create.title': 'Create a tournament',
  'tournament.create.lead': "Pick a tournament type. Every format affects the players' ELO.",
  'tournament.create.comingSoon': 'Coming soon',
  'tournament.create.available': 'Available',
  'tournament.create.eloWeightTitle': 'ELO weight: {weight} — how much the format moves ELO',
  'tournament.create.eloWeightPill': 'ELO {weight}',
  'tournament.create.padel24.name': 'Padel24',
  'tournament.create.padel24.tagline': "The club's main format",
  'tournament.create.padel24.desc':
    "Padel24 is the club's own tournament structure, with several groups at different levels. Each phase draws new partners within the group, and after the phase the best move up a group and the weakest move down. It can run in one session or be spread over several days. Best for 20-80 players.",
  'tournament.create.padel24.f1': '10 players per group (adjustable, 6-20)',
  'tournament.create.padel24.f2': 'Several phases — new partners within the group each time',
  'tournament.create.padel24.f3': 'Promotion and relegation between groups after each phase',
  'tournament.create.padel24.f4': 'Banepad with an automatic whistle and break handling',
  'tournament.create.km.name': 'Club championship',
  'tournament.create.km.tagline': 'A cup format with knockout rounds',
  'tournament.create.km.desc':
    'A traditional tournament with group play and knockout rounds, played over a weekend or a few weeks. A club champion is crowned per class. Several classes (Men, Women, Mixed) with divisions (A/B/C) can run in the same tournament.',
  'tournament.create.km.f1': 'Snake seeding of the groups from ELO or a manual ranking',
  'tournament.create.km.f2': 'FIP-style knockout (group winners meet no earlier than the final)',
  'tournament.create.km.f3': 'Configurable match format and time per phase',
  'tournament.create.km.f4': 'Best of 3 with a super tie-break (the FIP standard)',
  'tournament.create.league.name': 'League',
  'tournament.create.league.tagline': 'A league across a whole season',
  'tournament.create.league.desc':
    "The club's own league of fixed pairs, played over a longer period. Classes (Men, Women, Mixed) with several divisions — a round robin where every pair meets every other. A table-based overall standing, with promotion and relegation between divisions at the end of the season. Courts can be allocated to fixed days, or the pairs can book and play on their own.",
  'tournament.create.league.f1': 'Fixed pairs — team strength from the average ELO',
  'tournament.create.league.f2': 'Classes with several divisions (1st, 2nd, 3rd div …)',
  'tournament.create.league.f3': 'Round robin: everyone meets everyone, round by round',
  'tournament.create.league.f4': 'Promotion and relegation between divisions (adjustable)',
  'tournament.create.league.f5': 'A season deadline, with optional starpoints (gold points)',
  'tournament.create.league.f6': 'Courts on fixed days — or free booking',
  'tournament.create.mini.name': 'Mini tournaments',
  'tournament.create.mini.tagline': 'Short rotation formats',
  'tournament.create.mini.desc':
    'Short tournament formats played in a single session — Americano, Mexicano, Mix Mexicano and variants. What they share: minimal admin, 1-3 hours to run, and either rotating partners or fixed teams.',
  'tournament.create.mini.f1': 'Americano — everyone plays with everyone once',
  'tournament.create.mini.f2': 'Mexicano — closer matches, based on the standings',
  'tournament.create.mini.f3': 'Team formats with a fixed partner',
  'tournament.create.mini.f4': 'Bingo, timed mode and points mode',
  'tournament.create.single.name': 'Single match',
  'tournament.create.single.tagline': 'Log one match that was played',
  'tournament.create.single.desc':
    'Record a padel match played outside a tournament. Choose freely how much it counts towards ELO (0–200%) — from a practice match (0%) to a full best of 3 (200%). ELO updates immediately for all four players.',
  'tournament.create.single.f1': 'Choose the ELO weight per match (0–200%)',
  'tournament.create.single.f2': 'Enter the games per set',
  'tournament.create.single.f3': 'The winner takes 2 sets',
  'tournament.create.single.f4': 'ELO updates immediately',

  // ── tournament: the wizard summary, step 5 (Step5Start) ──────────
  'tournament.start.title': 'Summary',
  'tournament.start.lead':
    'Check that everything is right, then press «Create tournament». You land on a preview where everything can still be adjusted before you start.',
  'tournament.start.drawMirror': 'Mirror rotation (R1 partners become R2 opponents)',
  'tournament.start.drawVaried': 'Varied (the standard rules)',
  'tournament.start.pacingEven': 'Evenly spread',
  'tournament.start.pacingBackToBack': 'Back to back',
  'tournament.start.pacingOneGap': 'One match gap',
  'tournament.start.pacingPacked': 'Packed',
  'tournament.start.orderBestFirst': 'The best group first',
  'tournament.start.orderBestLast': 'The best group last (the climax)',
  'tournament.start.finalsPairing12': '1 + 2 vs 3 + 4 (best against weakest)',
  'tournament.start.finalsPairing13': '1 + 3 vs 2 + 4 (the most even)',
  'tournament.start.statPlayers': 'Players',
  'tournament.start.statGroups': 'Groups',
  'tournament.start.statPhases': 'Phases',
  'tournament.start.statMatchesPerPlayer': 'Matches/player',
  'tournament.start.dayValue':
    '{date} · {start}–{end} · {courts} courts · {duration}m/match · {phases}{final}{breaks}',
  'tournament.start.dayPhase': '{count} phase',
  'tournament.start.dayPhases': '{count} phases',
  'tournament.start.dayFinal': ' + final',
  'tournament.start.dayBreaks': ' · {count} break(s)',
  'tournament.start.betweenMatches': 'Time between matches',
  'tournament.start.minutes': '{minutes} min',
  'tournament.start.totalMatches': 'Matches in total',
  'tournament.start.settingsTitle': 'The chosen settings',
  'tournament.start.playersPerGroup': 'Players per group',
  'tournament.start.promotion': 'Up/down between phases',
  'tournament.start.promotionValue': '{count} players',
  'tournament.start.drawMode': 'Draw mode',
  'tournament.start.pacing': 'Pacing within a phase',
  'tournament.start.groupOrder': 'Order of the groups',
  'tournament.start.finalsPairing': 'Partner pattern in finals',
  'tournament.start.luckyLooser': 'Lucky Looser',
  'tournament.start.on': 'On',
  'tournament.start.off': 'Off',
  'tournament.start.eloWeight': 'ELO weight',
  'tournament.start.maxCourts': 'Maximum courts',
  'tournament.start.drawRules': 'Draw rules',
  'tournament.start.ruleNoSamePartner': 'avoid the same partner',
  'tournament.start.ruleNoSameOpponent': 'avoid the same opponent',
  'tournament.start.ruleBalanceHomeAway': 'balance home/away',
  'tournament.start.ruleBalanceElo': 'balance ELO',
  'tournament.start.ruleNone': 'none',
  'tournament.start.plannedNote':
    'Every match is already planned, with times and courts. Phase 1 (the A+B, C+D cross-pairing) has real players. Later phases hold placeholders that fill in automatically as results are entered.',

  // ── tournament: one configurable day (DayBlock) ──────────────────
  'tournament.day.slots': '· {total} slots ({perCourt}/court × {courts})',
  'tournament.day.removeDay': 'Remove the day',
  'tournament.day.nameLabel': 'Name of the day',
  'tournament.day.date': 'Date',
  'tournament.day.start': 'Start',
  'tournament.day.end': 'End',
  'tournament.day.courts': 'Courts',
  'tournament.day.courtsLockedHint': 'Controlled by the court choice in «Pick a setup»',
  'tournament.day.phases': 'Number of phases',
  'tournament.day.matchesPerPlayer': '{count} matches/player',
  'tournament.day.matchesPerPlayerFinal': ' ({count}+1 final)',
  'tournament.day.matchDuration': 'Min/match on this day',
  'tournament.day.slotMath': 'slot: {slot}m ({match}+{between})',
  'tournament.day.finalsDay': 'Finals day',
  'tournament.day.finalsDayNote':
    '— the placement matches are played after the last phase (+1 match/player)',
  'tournament.day.timeUse': 'Time use',
  'tournament.day.exceeds': '(runs past {time})',
  'tournament.day.matchesCount': '{count} matches/player',
  'tournament.day.matchesCountFinal': ' (incl. the final)',
  'tournament.day.matchesCountOverMax': ' (over the max of {max}!)',
  'tournament.day.unusedTime': 'Unused time:',
  'tournament.day.unusedHours': '{hours}h ',
  'tournament.day.unusedMinutes': '{minutes}m',
  'tournament.day.moveStart': '⤴ Move the start to {time}',
  'tournament.day.moveEnd': '⤵ Move the end to {time}',
  'tournament.day.notEnoughTime': 'Not enough time for {phases} phases.',
  'tournament.day.notEnoughTimeFinal': 'Not enough time for {phases} phases + the final.',
  'tournament.day.extendEnd': '⤢ Extend the end to {time}',
  'tournament.day.breaks': 'Breaks ({count})',
  'tournament.day.addBreak': 'Add a break',
  'tournament.day.noBreaks': 'No breaks',
  'tournament.day.breakAdviceTitle': 'Recommended: a break between the phases',
  'tournament.day.breakAdvice':
    'With this many courts each phase is short ({rotations} match rotations). This day has several phases and no break between them — the players will not have time to see their next match.',
  'tournament.day.addBreakShort': '+ Add a break',
  'tournament.day.undoRemove': '↩ Undo the removal',

  // ── tournament: weekday and month names, DISPLAY only (Step2Players)
  'tournament.weekday.sun': 'Sunday',
  'tournament.weekday.mon': 'Monday',
  'tournament.weekday.tue': 'Tuesday',
  'tournament.weekday.wed': 'Wednesday',
  'tournament.weekday.thu': 'Thursday',
  'tournament.weekday.fri': 'Friday',
  'tournament.weekday.sat': 'Saturday',
  'tournament.month.jan': 'Jan',
  'tournament.month.feb': 'Feb',
  'tournament.month.mar': 'Mar',
  'tournament.month.apr': 'Apr',
  'tournament.month.may': 'May',
  'tournament.month.jun': 'Jun',
  'tournament.month.jul': 'Jul',
  'tournament.month.aug': 'Aug',
  'tournament.month.sep': 'Sep',
  'tournament.month.oct': 'Oct',
  'tournament.month.nov': 'Nov',
  'tournament.month.dec': 'Dec',

  // ── tournament: sign-up and publishing, step 2 (Step2Players) ────
  'tournament.signup.howTitle': 'How do the players sign up?',
  'tournament.signup.modeManual': 'Run it outside',
  'tournament.signup.modeManualDesc':
    'You add the players yourself and start the tournament right away. Right when the participants are already lined up.',
  'tournament.signup.modeActivity': 'Through Activities',
  'tournament.signup.modeActivityDesc':
    'Publish with «Vent på påmelding». The players sign up themselves, with a waiting list.',
  'tournament.signup.dateFormat': '{weekday} {day} {month} {year}',
  'tournament.signup.publishedTitle': 'Sign-up (published)',
  'tournament.signup.publishTitle': 'Publish for self-service sign-up',
  'tournament.signup.publishedLead':
    'The tournament is published. Players sign up themselves (on a waiting list once it is full). You can adjust the price and deadline here, or go to the sign-up page.',
  'tournament.signup.publishLead':
    'Players sign up themselves, one at a time. When the sign-up is ready you start the tournament from the activity — the setup is then filled with everyone who signed up.',
  'tournament.signup.price': 'Price per player (kr)',
  'tournament.signup.pricePlaceholder': 'e.g. 200',
  'tournament.signup.maxPlayers': 'Maximum players',
  'tournament.signup.playerCount': '{count} players',
  'tournament.signup.maxPlayersNote':
    'The maximum follows the preset chosen under Grunnoppsett ({count} players). Anyone who signs up beyond that goes on the waiting list.',
  'tournament.signup.levelTitle': 'Level — who may sign up',
  'tournament.signup.deadlineTitle': 'Sign-up deadline',
  'tournament.signup.deadlineDays': 'Days before the start',
  'tournament.signup.deadlineDate': 'A specific date',
  'tournament.signup.deadlineNone': 'No deadline',
  'tournament.signup.daysBeforeStart': 'days before the start',
  'tournament.signup.noStartDate':
    'Set a date under Grunnoppsett/Banetilgang first, and the deadline is worked out from the start.',
  'tournament.signup.closesOn': 'Sign-up closes',
  'tournament.signup.closesStart': ' (start {date})',
  'tournament.signup.reservesTitle': 'Reserves (waiting list)',
  'tournament.signup.allowReserves': 'Allow reserves',
  'tournament.signup.maxReserves': 'Maximum number of reserves',
  'tournament.signup.maxStandby': 'Maximum number of standby reserves',
  'tournament.signup.reservesP1':
    'Once the activity is full, anyone signing up joins the waiting list (as a reserve) up to this number. Reserves pay for the reserve place, but are refunded if they are not used. A reserve may',
  'tournament.signup.reservesStrong1': 'also',
  'tournament.signup.reservesP2': 'tick the box for',
  'tournament.signup.reservesStrong2': 'standby reserve',
  'tournament.signup.reservesP3':
    '— if they are not used as a reserve, they can step in mid-tournament should somebody get injured (and the reserve fee is refunded). A standby reserve has to be near the venue and able to turn up at short notice, and may choose which days they are available. Standby reserves are numbered in the order they sign up.',
  'tournament.signup.noReserves': 'No waiting list — sign-up closes once the activity is full.',
  'tournament.signup.publishedTag': '✓ Published',
  'tournament.signup.saveChanges': 'Save the changes',
  'tournament.signup.viewSignup': 'See the sign-up →',
  'tournament.signup.publishCta': 'Publish for sign-up →',
  'tournament.signup.calendarNote':
    'Court time and the calendar come from the setup (Grunnoppsett/Banetilgang). The sign-up only gathers the players.',

  // ── tournament: the match setup, wizard step 4 (Step4Matches) ────
  'tournament.matches.generating': 'Generating the schedule…',
  'tournament.matches.title': 'Match setup',
  'tournament.matches.lead':
    'Every match, in one view. Placeholders fill in automatically as results are entered.',
  'tournament.matches.statMatches': 'Matches in total',
  'tournament.matches.statPlayers': 'Players',
  'tournament.matches.statRounds': 'Rounds',
  'tournament.matches.statEnd': 'Estimated end',
  'tournament.matches.phase1Drawn': 'Phase 1 drawn — {count} matches',
  'tournament.matches.phase1Pending': 'Phase 1 is waiting for the draw — {count} matches',
  'tournament.matches.phase1DrawnLead':
    'You can draw again for a fresh set of random combinations.',
  'tournament.matches.phase1PendingLead':
    'The matches are drawn using the rules you chose in the basic setup.',
  'tournament.matches.activeRules': 'Active rules:',
  'tournament.matches.noRules': 'No rules active — fully random',
  'tournament.matches.closeRules': 'Close',
  'tournament.matches.editRules': 'Edit the rules',
  'tournament.matches.redraw': 'Draw again',
  'tournament.matches.drawPhase1': 'Draw phase 1',
  'tournament.matches.ruleMirror': 'Mirror rotation in R2',
  'tournament.matches.ruleNoSamePartner': 'Avoid the same partner',
  'tournament.matches.ruleNoSameOpponent': 'Avoid the same opponent',
  'tournament.matches.ruleBalanceHomeAway': 'Balance home/away',
  'tournament.matches.ruleBalanceElo': 'Avoid lopsided ELO matches',
  'tournament.matches.modeVaried': 'Varied',
  'tournament.matches.modeVariedDesc': 'Spread partners and opponents widely',
  'tournament.matches.modeMirror': 'Mirror rotation',
  'tournament.matches.modeMirrorDesc': 'Former partners become opponents',
  'tournament.matches.rulesNote':
    'Changes take effect on the next draw — press "Draw again" to see the new schedule.',
  'tournament.matches.tabAll': 'Everything',
  'tournament.matches.tabPhase': 'Phase {phase}',
  'tournament.matches.tabFinals': 'Finals',
  'tournament.matches.showLabel': 'Show:',
  'tournament.matches.allDays': 'All days',
  'tournament.matches.group': 'Group {letter}',
  'tournament.matches.showElo': 'Show ELO',
  'tournament.matches.explainTitle': 'How it works',
  'tournament.matches.explain1Label': 'Phase 1 (rounds 1-2)',
  'tournament.matches.explain1Rest': ': cross-pairing between groups (A with B, C with D)',
  'tournament.matches.explain2Label': 'Phases 2-{phases} (rounds 3-{rounds})',
  'tournament.matches.explain2Rest': ': within the groups',
  'tournament.matches.explain3':
    'After each phase: the top {count} move up, the bottom {count} move down',
  'tournament.matches.explain4':
    'The finals are played after the last round (placement matches per group)',
  'tournament.matches.perPlayerTitle': 'Matches per player',
  'tournament.matches.allEqual': 'Every player gets {count} matches',
  'tournament.matches.crossMatches': '{count} cross matches + ',
  'tournament.matches.inGroupMatches': '{count} within the group',
  'tournament.matches.plusFinal': ' + the final',
  'tournament.matches.variation': 'Variation: {min}–{max} matches in phase 1',

  // ── tournament: a player's tournament profile (TournamentPlayerModal)
  'tournament.playerModal.chartStart': 'Start',
  'tournament.playerModal.chartMatch': 'M{n}',
  'tournament.playerModal.title': 'Tournament profile',
  'tournament.playerModal.elo': 'ELO {elo}',
  'tournament.playerModal.reservedDot': '· Reserved place',
  'tournament.playerModal.close': 'Close',
  'tournament.playerModal.replacedOut': 'Substituted out',
  'tournament.playerModal.replacedOutLead':
    'The player was substituted out part-way through. Only matches BEFORE the substitution count for them.',
  'tournament.playerModal.substitute': 'Substitute',
  'tournament.playerModal.substituteLead':
    'Came in as a replacement. Only matches FROM the substitution onwards count for them.',
  'tournament.playerModal.reserved': 'Reserved place',
  'tournament.playerModal.reservedLead':
    'This place has no named player yet. Click the name in the matches to fill it in.',
  'tournament.playerModal.winsLossesDraws': 'Wins-losses-draws',
  'tournament.playerModal.winsLosses': 'Wins-losses',
  'tournament.playerModal.diff': 'Diff: {diff}',
  'tournament.playerModal.matchesPlayed': 'Matches played',
  'tournament.playerModal.homeAway': 'Home–Away',
  'tournament.playerModal.eloProgress': 'ELO over time',
  'tournament.playerModal.eloProgressNote': 'After each of {count} finished matches',
  'tournament.playerModal.injured': 'Injured',
  'tournament.playerModal.injuredLead': '— left out of new rounds. ELO and points are kept.',
  'tournament.playerModal.fillNameTitle': 'Fill in the name for this reserved place',
  'tournament.playerModal.replaceTitle': 'Substitute this player for someone else',
  'tournament.playerModal.fillName': 'Fill in the name',
  'tournament.playerModal.replace': 'Substitute the player',
  'tournament.playerModal.markBackTitle':
    'Mark the player as back from injury — they will join the coming rounds',
  'tournament.playerModal.markInjuredTitle':
    'Mark as injured — left out of new rounds, existing points are kept',
  'tournament.playerModal.markBack': 'Mark as back',
  'tournament.playerModal.markInjured': 'Mark as injured',
  'tournament.playerModal.fullHistoryTitle':
    "See the player's full history across every tournament",
  'tournament.playerModal.fullHistory': 'Full history',
  'tournament.playerModal.upcoming': 'Upcoming ({count})',
  'tournament.playerModal.finished': 'Finished ({count})',
  'tournament.playerModal.latestFirst': 'Latest first',
  'tournament.playerModal.noMatches': 'No matches found for this player yet',
  'tournament.playerModal.confirmBackTitle': 'Mark {name} as back?',
  'tournament.playerModal.confirmInjuredTitle': 'Mark {name} as injured?',
  'tournament.playerModal.confirmBackLead':
    'The player will join the coming rounds. Existing points and statistics are unchanged.',
  'tournament.playerModal.confirmInjuredLead':
    'The player is left out of new rounds. Existing ELO, points and match history are kept — they show at the bottom of the leaderboard with an "Injured" tag.',
  'tournament.playerModal.confirmTooFew':
    '⚠️ Fewer than 4 active players are left after this — the tournament cannot generate new rounds.',
  'tournament.playerModal.cancel': 'Cancel',

  // ── tournament: the Padel24 wizard shell (Padel24Wizard) ─────────
  'tournament.wizard.occupied': 'Booked',
  'tournament.wizard.consDemoteOne':
    '{count} player moves from a confirmed place to the reserve list (the last to sign up). They keep their order and move up again if you raise the number.',
  'tournament.wizard.consDemoteMany':
    '{count} players move from a confirmed place to the reserve list (the last to sign up). They keep their order and move up again if you raise the number.',
  'tournament.wizard.consPromoteOne': '{count} reserve moves up to a confirmed place.',
  'tournament.wizard.consPromoteMany': '{count} reserves move up to a confirmed place.',
  'tournament.wizard.consMaxChanged': 'The maximum changes from {from} to {to}.',
  'tournament.wizard.consOutsideOne':
    '{count} player who has already signed up falls outside the new level requirement. They keep their place, but new players outside the level cannot sign themselves up.',
  'tournament.wizard.consOutsideMany':
    '{count} players who have already signed up fall outside the new level requirement. They keep their places, but new players outside the level cannot sign themselves up.',
  'tournament.wizard.consNewLevel': 'The new level requirement applies to new sign-ups.',
  'tournament.wizard.consImmediateOne':
    'The changes take effect on the sign-up immediately ({count} signed up).',
  'tournament.wizard.consImmediateMany':
    'The changes take effect on the sign-up immediately ({count} signed up).',
  'tournament.wizard.cancel': 'Cancel',
  'tournament.wizard.savedTitle': 'Saved!',
  'tournament.wizard.unsavedTitle': 'Unsaved changes — click to save as "{name}"',
  'tournament.wizard.nothingToSaveTitle': 'No changes to save',
  'tournament.wizard.saved': 'Saved',
  'tournament.wizard.save': 'Save',
  'tournament.wizard.helpTitle': 'Open the help for the Padel24 format',
  'tournament.wizard.help': 'Help',
  'tournament.wizard.back': 'Back',
  'tournament.wizard.next': 'Next',
  'tournament.wizard.createTitle':
    'Create the tournament — you land on a preview where the groups and the draw can still be adjusted before you start',
  'tournament.wizard.create': 'Create tournament →',
  'tournament.wizard.stepSetup': 'Basic setup',
  'tournament.wizard.stepPlayers': 'Players',
  'tournament.wizard.fillAll': 'Fill in every field',
  'tournament.wizard.pickAtLeast8': 'Pick at least 8 ({count}/8)',
  'tournament.wizard.mustBeEven': 'The number has to be even',
  'tournament.wizard.confirmTitle': 'Save the changes to the sign-up?',
  'tournament.wizard.confirmLead':
    'The activity is open for sign-up. The changes have these consequences:',
  'tournament.wizard.saveChanges': 'Save the changes',

  // ── tournament: the basic setup, wizard step 1 (Step1Setup) ──────
  'tournament.setup.title': 'Basic setup',
  'tournament.setup.lead': 'Name, days, courts, match length and breaks.',
  'tournament.setup.savePublished': 'Save the changes',
  'tournament.setup.savePublishedTitle':
    'Save the changes to the published sign-up (you get a confirmation with the consequences)',
  'tournament.setup.publishedBannerBefore':
    'The activity is open for sign-up. If you change the preset (courts/players) or the court choice, press',
  'tournament.setup.publishedBannerAfter':
    '— you get a confirmation with the consequences. Remember to update the court reservations per day so they match the new number of courts.',
  'tournament.setup.mismatchDayOne': '{count} day',
  'tournament.setup.mismatchDayMany': '{count} days',
  'tournament.setup.mismatchLead':
    '({days}) have court reservations that no longer match the number of courts. Scroll down to each day and press',
  'tournament.setup.mismatchButton': '«Oppdater reservasjon»',
  'tournament.setup.name': 'Tournament name',
  'tournament.setup.organiser': 'Organiser',
  'tournament.setup.auto': 'Auto',
  'tournament.setup.pickPreset': 'Pick a setup',
  'tournament.setup.perGroupShort': '{count} pl/group',
  'tournament.setup.pickPresetLead':
    'Padel24 works best with fixed player counts. Pick how many players you are planning for. The time setup (Fri 16:00-23:00 + Sat 9:00-17:00) stays as it is.',
  'tournament.setup.playersWord': 'players',
  'tournament.setup.groupsCount': '{count} groups',
  'tournament.setup.suitOddTitle':
    'An odd number of groups — the last group plays within itself in phase 1 instead of cross-pairing with a neighbour. That works perfectly well; cross-pairing every group is only marginally better.',
  'tournament.setup.suitManyTitle':
    'Many groups — harder to climb from the bottom to the top, and this player count cannot be split into fewer, larger groups.',
  'tournament.setup.minimum': 'minimum',
  'tournament.setup.courtsWord': 'courts',
  'tournament.setup.smallGroupsTitle': 'Smaller groups (12 per group)',
  'tournament.setup.smallGroupsOn': 'On: {groups} groups of 12 players. 4 move up/down per phase.',
  'tournament.setup.smallGroupsOff':
    'Standard: {groups} groups of {size}. Turn on for {small} smaller groups of 12.',
  'tournament.setup.smallGroupsToggle': 'Toggle smaller groups',
  'tournament.setup.smallGroupsStdLabel': 'Standard ({groups} groups of {size}):',
  'tournament.setup.smallGroupsStdBody':
    'Fewer groups to climb through, and {promotion} players move up/down per phase — players can climb further over the tournament.',
  'tournament.setup.smallGroupsSmallLabel': 'Smaller groups (12):',
  'tournament.setup.smallGroupsSmallBody':
    'A narrower ELO span in each group gives closer matches, but more groups to climb through and only 4 up/down per phase.',
  'tournament.setup.suitHeading': '{label}: {players} players',
  'tournament.setup.suitBody':
    'This player count can only be split into {groups} small groups of 12 — there is no good way to make fewer, larger ones. With {groups} groups it is {degree}hard for players in the lowest groups to reach the top over the tournament. Consider a nearby player count (96 or 120, say) that splits into fewer, larger groups.',
  'tournament.setup.suitDegreeVery': 'very ',
  'tournament.setup.suitDegreeQuite': 'quite ',
  'tournament.setup.nonStandard': 'Non-standard player count: {players}',
  'tournament.setup.nonStandardLead':
    'We recommend picking one of the setups above for the best use of the courts.',
  'tournament.setup.oddGroupNote':
    'An odd number of groups: the last group plays within itself in phase 1 instead of cross-pairing with a partner group. That works perfectly well.',
  'tournament.setup.optMinimum': 'Standard',
  'tournament.setup.optMinimumPros':
    'The longest breaks between matches, an even flow, and the fewest courts.',
  'tournament.setup.optMinimumCons': 'Takes the whole day — a late finish.',
  'tournament.setup.optComfortable': 'Roomy',
  'tournament.setup.optComfortablePros':
    'A shorter day, still with a gap between your own matches.',
  'tournament.setup.optComfortableCons':
    'Needs about 50% more courts. Some rotations leave a court free.',
  'tournament.setup.optShort': 'Efficient',
  'tournament.setup.optShortPros': 'A short day, with every match packed close together.',
  'tournament.setup.optShortCons':
    'Twice as many courts. Little rest — R1 and R2 are close together, so a break between phases is recommended.',
  'tournament.setup.optFast': 'Compressed',
  'tournament.setup.optFastPros': 'A very short day.',
  'tournament.setup.optFastCons': 'Many courts, and almost no rest for the players.',
  'tournament.setup.optAllAtOnce': 'Everyone at once ⚡',
  'tournament.setup.optAllAtOncePros':
    'As short as it gets — the whole phase in two match rotations.',
  'tournament.setup.optAllAtOnceCons':
    'Needs one court per four players. No gap; R1 and R2 run back to back.',
  'tournament.setup.courtOptions': 'Court options',
  'tournament.setup.colCourts': 'Courts',
  'tournament.setup.colDescription': 'Description',
  'tournament.setup.colRotations': 'Rot/phase',
  'tournament.setup.colDay1': 'Day 1',
  'tournament.setup.colDay2': 'Day 2',
  'tournament.setup.recommended': 'Recommended',
  'tournament.setup.freeCourtsOne': '· {free} free court in {rotations} of {total} rot.',
  'tournament.setup.freeCourtsMany': '· {free} free courts in {rotations} of {total} rot.',
  'tournament.setup.rotationsValue': '{count}',
  'tournament.setup.utilisation': '· Court utilisation {percent}%.',
  'tournament.setup.courtOptionsFooter':
    'A maximum of 4 matches/day (+1 final on the last day). Rot/phase is the number of match rotations per phase — fewer is faster. «One match gap» needs at least 4.',
  'tournament.setup.tooFewCourts': 'You have too few courts ({courts} of the {needed} needed)',
  'tournament.setup.tooFewCourtsLead':
    'With {players} players you need at least {needed} courts to fit inside the time available.',
  'tournament.setup.formatTitle': 'Tournament format',
  'tournament.setup.plusFinal': ' + the final',
  'tournament.setup.phasesSummary': '{phases} phases{final} = {matches} matches/player',
  'tournament.setup.phasesWanted': 'Wanted: {phases} phases{final} ({matches} matches)',
  'tournament.setup.autoDistribute': 'Distribute automatically',
  'tournament.setup.phasesMissing': 'Missing {count} phase(s)',
  'tournament.setup.phasesTooMany': '{count} phase(s) too many',
  'tournament.setup.capacity': 'Capacity',
  'tournament.setup.playerCount': 'Number of players',
  'tournament.setup.lockedByPreset':
    'Controlled by the chosen setup — change it under «Pick a setup»',
  'tournament.setup.totalPhases': 'Phases in total',
  'tournament.setup.maxPerDay': 'Max matches/day',
  'tournament.setup.capacityLine':
    '{players} players · {groupMatches} group matches{final} · max {max}/day (the final +1)',
  'tournament.setup.oddPlayers': '⚠ An odd number — padel needs an even one!',
  'tournament.setup.includeFinal': 'Include a final',
  'tournament.setup.finalYes': '✓ Yes (+1 match)',
  'tournament.setup.finalNo': 'No',
  'tournament.setup.finalNote': 'Placement matches for everyone.',
  'tournament.setup.groupsTitle': 'Groups',
  'tournament.setup.autoTune': 'Auto-tune',
  'tournament.setup.perGroup': 'Per group',
  'tournament.setup.promotionPerPhase': 'Up/down per phase',
  'tournament.setup.groupsLabel': '{count} groups:',
  'tournament.setup.unevenGroup': 'Group {letter} has only {count} players',
  'tournament.setup.oddGroups':
    '{count} groups is an odd number — group {letter} cannot be cross-paired and plays within itself in phase 1. An even number of groups gives a more even format.',
  'tournament.setup.promotionLine': '↑{up} up · {stay} stay · ↓{up} down',
  'tournament.setup.recommendedValue': '(recommended: {value})',
  'tournament.setup.drawRulesTitle': 'Draw rules',
  'tournament.setup.drawRulesLead':
    'When a new phase starts, the matches for the new groups are drawn. Choose the draw mode and which rules apply.',
  'tournament.setup.modeVaried': 'Varied',
  'tournament.setup.modeVariedDesc': 'Spread partners and opponents widely',
  'tournament.setup.modeMirror': 'Mirror rotation',
  'tournament.setup.modeMirrorDesc': 'Former partners become opponents in the next round',
  'tournament.setup.mirrorHowTitle': 'How mirror rotation works',
  'tournament.setup.mirrorHow1': 'Round 1: a random pairing that follows the rules below',
  'tournament.setup.mirrorHow2':
    'Round 2: every player meets their round 1 partner as an opponent (with a new partner)',
  'tournament.setup.activeRules': 'Active rules (in priority order)',
  'tournament.setup.ruleNoSamePartner': 'Avoid the same partner',
  'tournament.setup.ruleNoSamePartnerDesc':
    'Players get new partners rather than repeating a pairing',
  'tournament.setup.ruleNoSameOpponent': 'Avoid the same opponent',
  'tournament.setup.ruleNoSameOpponentDesc':
    'Players meet new opponents rather than repeating an opposing pair',
  'tournament.setup.ruleBalanceHomeAway': 'Balance home/away',
  'tournament.setup.ruleBalanceHomeAwayDesc':
    'Try to give everyone the same number of home and away matches',
  'tournament.setup.ruleBalanceElo': 'Avoid lopsided ELO matches',
  'tournament.setup.ruleBalanceEloDesc':
    'Stops extreme imbalance between teams (a tolerance of 100 ELO)',
  'tournament.setup.finalsPairingTitle': 'Partner pattern in the finals',
  'tournament.setup.finalsPairingLead':
    'After the last phase, places 1–4 play final 1, places 5–8 play final 2, and so on. Choose how partners are paired:',
  'tournament.setup.pairing13': '1 + 3 vs 2 + 4',
  'tournament.setup.pairing13Desc': 'The best and the weakest on each team (the most even)',
  'tournament.setup.pairing12': '1 + 2 vs 3 + 4',
  'tournament.setup.pairing12Desc': 'The best against the weakest (the most dramatic)',
  'tournament.setup.luckyLooser': 'Lucky Looser',
  'tournament.setup.luckyOn': 'On',
  'tournament.setup.luckyOff': 'Off',
  'tournament.setup.luckyLead':
    "Each group's winner moves up and plays the bronze final (8th place) in the group above. That group's own players from 8th place down each shift one place (8→9, 9→10 …), so the last of them drops to 1st place in the group below. It affects the finals draw only.",
  'tournament.setup.luckyToggle': 'Toggle Lucky Looser',
  'tournament.setup.luckyNote':
    'A player from the group below gets a shot at the bronze final in a higher group. The finals are drawn together once every group has finished (they cannot be drawn per group).',
  'tournament.setup.rankingTitle': 'Ranking within the group (phase to phase)',
  'tournament.setup.rankingNote':
    'After each phase the players in a group are ranked in this order — the winner of each group moves up and the last moves down. Injured players are left out of the ranking. Head-to-head is only used when the two players have actually met as opponents, and it counts meetings from ALL phases — not just the phase the table shows. If everything is still level, it goes to a draw, and the group table marks the row with an icon showing what decided it.',
  'tournament.setup.eloWeightTitle': 'ELO weight for the tournament',
  'tournament.setup.eloWeightLead':
    "Decides how much the matches in this tournament move the players' ELO. Changing it later only affects new matches.",
  'tournament.setup.pacingTitle': 'Match timing',
  'tournament.setup.pacingAllAtOnce': 'Everyone plays at once',
  'tournament.setup.pacingPacked': 'Tightly packed',
  'tournament.setup.pacingAllAtOnceLead':
    'With this many courts the whole phase fits in one match rotation — everyone plays at once. There is no order or gap left to choose.',
  'tournament.setup.pacingPackedLead':
    'With {courts} courts the phase runs in only {rotations} match rotations. The matches are packed as tightly and evenly as possible — there is no room for a gap between R1 and R2, so placement is not a choice here.',
  'tournament.setup.pacingOneGap': 'One match gap (recommended)',
  'tournament.setup.pacingOneGapDesc': 'R1, wait one match, then R2 on the same court',
  'tournament.setup.pacingOneGapWait': '~40 min',
  'tournament.setup.pacingBackToBack': 'Back to back',
  'tournament.setup.pacingBackToBackDesc': 'R1 and R2 one straight after the other',
  'tournament.setup.pacingBackToBackWait': '~5 min',
  'tournament.setup.pacingLead': 'How the two matches within a phase are spaced in time.',
  'tournament.setup.waitLabel': 'Wait:',
  'tournament.setup.orderTitle': 'Order within a phase',
  'tournament.setup.orderLead':
    'Which group plays first? It applies to the finals too — final 1 (gold) is placed the other way round: last when the best group plays last, first when the best group plays first.',
  'tournament.setup.orderBestLast': 'Best last (the climax)',
  'tournament.setup.orderBestLastDesc':
    'The weakest group opens the phase and group A closes it. Final 1 (gold) is played at the very end.',
  'tournament.setup.orderBestFirst': 'Best first',
  'tournament.setup.orderBestFirstDesc':
    'Group A opens the phase and the weakest closes it. Final 1 (gold) is played first.',
  'tournament.setup.padFlowTitle': 'Banepad — rotate on',
  'tournament.setup.padFlowLead':
    'What moves a court on to the next match? It can also be overridden per court as you go.',
  'tournament.setup.padFlowResult': 'The result',
  'tournament.setup.padFlowResultDesc':
    'The court switches as soon as the result is entered. It rolls on without waiting for the clock.',
  'tournament.setup.padFlowResultHint': 'Recommended for Padel24 — a fast flow',
  'tournament.setup.padFlowTime': 'The clock',
  'tournament.setup.padFlowTimeDesc':
    'The court switches when the next match\'s start time arrives. Players see "next match in X min" in between.',
  'tournament.setup.padFlowTimeHint': 'Useful when the schedule is tight',
  'tournament.setup.betweenTitle': 'Time between matches',
  'tournament.setup.minutes': 'Minutes',
  'tournament.setup.betweenNote': 'Changing teams plus a warm-up. The match length is set per day.',
  'tournament.setup.courtsTitle': 'Courts',
  'tournament.setup.perDay': 'Per day',
  'tournament.setup.allDays': 'All days',
  'tournament.setup.lockedByCourtChoice': 'Controlled by the court choice under «Pick a setup»',
  'tournament.setup.courtsFromTable':
    'The number of courts comes from the court-options table under «Pick a setup».',
  'tournament.setup.courtsClubNormal': "The club's usual: {courts}. It can be overridden per day.",
  'tournament.setup.courtsFromClubSettings': 'Set as the default in the club settings.',
  'tournament.setup.courtsPerDayNote': 'It differs per day. Adjust it below, or',
  'tournament.setup.courtsSameForAll': 'use the same for all',
  'tournament.setup.daysTitle': 'Days',
  'tournament.setup.addDay': 'Add a day',
  'tournament.setup.summaryTitle': 'Summary',
  'tournament.setup.sumPlayers': 'Players',
  'tournament.setup.sumPhases': 'Phases',
  'tournament.setup.sumPhasesValue': '{phases} ({matches} matches)',
  'tournament.setup.sumMatchesPerPlayer': 'Matches/player',
  'tournament.setup.sumPlusOneFinal': ' + 1 final',
  'tournament.setup.sumGroupCount': 'Number of groups',
  'tournament.setup.sumGroupCountValue': '{count} groups',
  'tournament.setup.sumUneven': ' (uneven)',
  'tournament.setup.sumPerGroup': 'Per group',
  'tournament.setup.sumPlayersValue': '{count} players',
  'tournament.setup.sumPromotion': 'Up/down',
  'tournament.setup.sumMaxPerDay': 'Max/day',
  'tournament.setup.sumPlusFinal': ' (+1 final)',
  'tournament.setup.sumPhaseMismatch': 'The days hold {actual} phases; {wanted} were wanted.',
  'tournament.setup.sumBetween':
    'Time between matches: {minutes} min. The match length varies per day.',
  'tournament.setup.sumCapacity': 'Maximum capacity',
  'tournament.setup.sumCapacityNote': 'The most the current days, times and courts allow.',
  'tournament.setup.sumOverCapacity': 'The planned {players} is over the maximum!',
  'tournament.setup.sumBreaks': 'Breaks',
  'tournament.setup.sumBreakAfterPhase': 'after phase {phase}',
  'tournament.setup.sumBreakMinutes': '{minutes}m',

  // ── tournament: picking the players (TournamentPlayerSelection) ──
  'tournament.pick.clubA': 'Club A',
  'tournament.pick.clubB': 'Club B',
  'tournament.pick.lastTournament': 'The last tournament',
  'tournament.pick.title': 'Players',
  'tournament.pick.lead': 'Pick named players, reserve places, or fill up with demo players.',
  'tournament.pick.quickTools': 'Quick tools',
  'tournament.pick.filledCount': '{filled}/{total} filled · {left} left',
  'tournament.pick.reserveTitle': 'Reserve places',
  'tournament.pick.reserveLead':
    'Empty places with a start number — fill in the names as the sign-ups arrive.',
  'tournament.pick.count': 'Number:',
  'tournament.pick.reserve': 'Reserve',
  'tournament.pick.reservedSoFarOne': '{count} reserved so far',
  'tournament.pick.reservedSoFarMany': '{count} reserved so far',
  'tournament.pick.demoTitle': 'Demo players',
  'tournament.pick.demoLead': 'A random pick from the database — useful for testing the setup.',
  'tournament.pick.fillRest': 'Fill the rest ({count})',
  'tournament.pick.orAdd': 'or add',
  'tournament.pick.add': 'Add',
  'tournament.pick.searchPlaceholder': 'Search for a name… (Enter or a click adds them)',
  'tournament.pick.clearSearch': 'Clear the search',
  'tournament.match.enterScore': 'Enter the result',
  'tournament.pick.noHits': 'No match for "{search}".',
  'tournament.pick.createPlayer': 'Create a new player?',
  'tournament.pick.newPlayer': 'New player',
  'tournament.pick.clearAll': 'Clear all',
  'tournament.pick.addAllTitle': 'Add everyone who is not already selected',
  'tournament.pick.addAll': '+ Add everyone',
  'tournament.pick.lastTournamentMeta': '{name} · {count} players',
  'tournament.pick.recentlyPlayed': 'Played recently',
  'tournament.pick.teams': 'Teams',
  'tournament.pick.ok': '✓ OK',
  'tournament.pick.minPlayers': 'min {min}',
  'tournament.pick.notEven': 'not an even number',
  'tournament.pick.playerCountOne': '{count} player',
  'tournament.pick.playerCountMany': '{count} players',
  'tournament.pick.avgElo': 'Average ELO:',
  'tournament.pick.noTeams': 'No teams formed yet.',
  'tournament.pick.noTeamsLead': 'Pick players and they are paired automatically:',
  'tournament.pick.noTeamsExample': '1+2 = team 1, 3+4 = team 2, …',
  'tournament.pick.colPlayer1': 'Player 1',
  'tournament.pick.colPlayer2': 'Player 2',
  'tournament.pick.colAvg': 'Avg',
  'tournament.pick.sortedByAvgElo': 'Sorted by average ELO (click to clear)',
  'tournament.pick.sortByAvgElo': 'Click to sort by average ELO',
  'tournament.pick.clickToSwap': 'Click to substitute',
  'tournament.pick.levelTitle': 'Level {level} — {name}',
  'tournament.pick.teamLabel': 'Team {number}',
  'tournament.pick.waitingForPartner': 'waiting for a partner',
  'tournament.pick.teamAvgEloTitle': "The team's average ELO",
  'tournament.pick.removeTeamTitle': 'Remove the whole of team {number}',
  'tournament.pick.allTeamsPlay': 'All {teams} teams play every round',
  'tournament.pick.onCourtsOne': 'on {courts} court.',
  'tournament.pick.onCourtsMany': 'on {courts} courts.',
  'tournament.pick.rotationTitle': 'Sit-out rotation:',
  'tournament.pick.rotationPlaying': '{teams} teams play,',
  'tournament.pick.rotationSitting': '{teams} sit out',
  'tournament.pick.rotationNote':
    'each round. The app rotates so everyone sits out the same number of times.',
  'tournament.pick.timeHoursMinutes': '{hours} h {minutes} min',
  'tournament.pick.timeHours': '{hours} h',
  'tournament.pick.timeMinutes': '{minutes} min',
  'tournament.pick.roundRobinTitle': 'A full round robin:',
  'tournament.pick.roundRobinRoundOne': 'round',
  'tournament.pick.roundRobinRoundMany': 'rounds',
  'tournament.pick.roundRobinMatchesOne': '({matches} match in total) ·',
  'tournament.pick.roundRobinMatchesMany': '({matches} matches in total) ·',
  'tournament.pick.oddTeamsTitle': 'An odd number of teams ({count}):',
  'tournament.pick.oddTeamsLead':
    'one team has to sit out more often. If you run a knockout, the last team in the ranking gets no final — consider "Total points" as the winning criterion instead.',
  'tournament.pick.entrants': 'Entrants',
  'tournament.pick.balanced': '✓ Balanced',
  'tournament.pick.unevenBalance': 'Unbalanced',
  'tournament.pick.maleCount': '♂ {count}',
  'tournament.pick.femaleCount': '♀ {count}',
  'tournament.pick.noPlayers': 'No players picked yet.',
  'tournament.pick.noPlayersLead': 'Use the search, the quick lists or the quick tools.',
  'tournament.pick.menColumn': '♂ Men ({count})',
  'tournament.pick.noMen': 'No men picked',
  'tournament.pick.womenColumn': '♀ Women ({count})',
  'tournament.pick.noWomen': 'No women picked',
  'tournament.pick.clickToFill': 'Click to fill in',
  'tournament.pick.clickToEdit': 'Click to edit',
  'tournament.pick.clickToFillName': 'Click to fill in the name',
  'tournament.pick.remove': 'Remove',
  'tournament.pick.unevenSplit': 'Uneven split',
  'tournament.pick.divisibleByFour': 'The number has to divide by 4',
  'tournament.pick.notDivisible': 'not divisible by {divisor}',
  'tournament.pick.clubCount': '{club}: {count}',
  'tournament.pick.autoBalanceTitle': 'Split them alternately by ELO into A/B',
  'tournament.pick.autoBalance': '↔ Auto',
  'tournament.pick.namedCount': '{count} named',
  'tournament.pick.emptyCount': '{count} empty',
  'tournament.pick.newTag': 'New',
  'tournament.pick.notFilled': 'Not filled in — click to choose',
  'tournament.pick.removeFromEntrants': 'Remove from the entrants',
  'tournament.pick.group': 'Group {letter}',
  'tournament.pick.dragToOtherClub': 'Drag to move to the other club',
  'tournament.pick.moveToOtherClub': 'Move to the other club',
  'tournament.pick.dropPlayersHere': 'Drag players here',
  'tournament.pick.missingOnePlayer': '· 1 player short',
  'tournament.pick.moveTeamTitle': 'Move the whole team to the other club',
  'tournament.pick.moveClub': 'Move club',
  'tournament.pick.teamCount': '{count} teams',
  'tournament.pick.eloLevel': 'ELO {elo} · level {level}',
  'tournament.pick.newSuffix': '· New',

  // ── tournament: the running tournament (TournamentViewActive) ────
  'tournament.active.toastStartFirst': 'Start the tournament before entering results.',
  'tournament.active.toastRecordFailed': 'The result could not be recorded',
  'tournament.active.home': 'Home',
  'tournament.active.helpTitle': 'Open the help for the Padel24 format',
  'tournament.active.help': 'Help',
  'tournament.active.archivedTitle':
    'You are looking at an archived tournament — it cannot be changed',
  'tournament.active.archived': 'Archived · Read-only',
  'tournament.active.statusFinals': 'Placement matches',
  'tournament.active.statusDone': 'Finished',
  'tournament.active.statusRunning': 'Phase {phase}/{phasesTotal} · Round {round}/{roundsTotal}',
  'tournament.active.resumeTitle':
    'Make the tournament active again so you can keep playing. It shows as «In progress» on the home page, and the archived copy is kept until you save again.',
  'tournament.active.resume': 'Continue the tournament',
  'tournament.active.randomRotationTitle':
    'Fill in random results for the earliest match rotation only (the same time, every court)',
  'tournament.active.randomRotation': '🎲 Random (rotation)',
  'tournament.active.randomPhaseTitle':
    'Fill in random results for every open match in this phase. Once the last phase is done, click again to play the finals.',
  'tournament.active.randomPhase': '🎲 Random (this phase)',
  'tournament.active.randomAllTitle': 'Simulate the whole tournament to the end',
  'tournament.active.randomAll': '🎲 Random (the whole tournament)',
  'tournament.active.standbyTitle':
    'Show the standby reserves — players who can step in if somebody is injured',
  'tournament.active.standbyButton': 'Standby reserves ({count})',
  'tournament.active.backToSetupTitle':
    'Go back to the setup and change the settings (possible as long as no results have been entered)',
  'tournament.active.backToSetup': 'Back to the setup',
  'tournament.active.start': 'Start the tournament',
  'tournament.active.revertTitle':
    'Set it back to «Ready to start» — this re-opens sign-up if the tournament came from an activity',
  'tournament.active.revert': 'Back to ready to start',
  'tournament.active.pauseTitle':
    'Pause the tournament — park it so you can pick it up another day. (Going home does NOT pause it — it keeps showing as «In progress».)',
  'tournament.active.pause': 'Pause',
  'tournament.active.continue': 'Continue',
  'tournament.active.complete': 'Complete the tournament',
  'tournament.active.finishTitle': 'Finish the tournament',
  'tournament.active.finish': 'Finish',
  'tournament.active.previewTitle': 'Preview',
  'tournament.active.previewLead':
    'The tournament is created but not started. Adjust the groups, the draw and the setup freely — results cannot be entered yet. Press «Start the tournament» when you are ready; that locks the setup and opens the Banepad.',
  'tournament.active.saveForLaterTitle':
    'Save the tournament under «Pausede turneringer» and pick it up later — without starting it',
  'tournament.active.saveForLater': 'Save for later',
  'tournament.active.previewSignupLead':
    'Built from the sign-up. Sign-up and reserves are still open, and the status has not changed. You can adjust the setup, go back to the sign-up, or press «Start the tournament» when you are ready — that closes the sign-up.',
  'tournament.active.backToSignup': 'Back to the sign-up',
  'tournament.active.doneTitle': 'The tournament is over! 🎉',
  'tournament.active.doneLead':
    "Every match has been played and the final ranking is ready. Save the tournament to update the players' ELO and keep it in the archive — or delete it without saving.",
  'tournament.active.saveAndExit': 'Save and finish',
  'tournament.active.saveAndExitDesc':
    'The tournament moves to the tournament history on the home page. You can open it again to see the results.',
  'tournament.active.deleteWithoutSaving': 'Delete without saving',
  'tournament.active.deleteWithoutSavingDesc': 'All the data is lost. This cannot be undone.',
  'tournament.active.earlyDrawTitle': 'Draw the next phase early — one group at a time',
  'tournament.active.earlyDrawLead':
    'A group can be drawn once it and the neighbouring groups directly above and below have all finished (players move up and down between neighbours). The matches fill their original slots in the schedule.',
  'tournament.active.drawAllTitle': 'Draw phase {phase} for all {groups} ready groups at once',
  'tournament.active.drawAll': 'Draw them all ({count})',
  'tournament.active.drawGroupTitle': 'Draw phase {phase} for group {group}',
  'tournament.active.drawGroup': 'Draw phase {phase} for {group}',
  'tournament.active.waitingNeighbours': 'Waiting for neighbouring groups',
  'tournament.active.and': ' and ',
  'tournament.active.waitingNeighbourTitle':
    'Group {group} has finished, but is waiting for group {missing} to finish too',
  'tournament.active.waitingNeighbour': '{group} ready — waiting for {missing}',
  'tournament.active.noGroupsReady':
    'No group is ready to draw yet — wait until the neighbouring groups have finished too.',
  'tournament.active.earlyFinalsTitle': 'Draw the finals early — one group at a time',
  'tournament.active.earlyFinalsLead':
    "The finals are within a group (final 1 = the top of A, final 2 = the next in A, and so on) and depend only on that group's own final ranking. As soon as the group has finished phase {phase}, its finals can be drawn — you do not have to wait for the others.",
  'tournament.active.drawAllFinalsTitle': 'Draw the finals for all {groups} ready groups at once',
  'tournament.active.drawAllFinals': 'Draw all the finals ({count})',
  'tournament.active.drawFinalsTitle': 'Draw the finals for group {group}',
  'tournament.active.drawFinals': 'Draw the finals for {group}',
  'tournament.active.waitingLastPhase': 'Waiting for the last phase',
  'tournament.active.waitingGroupMatches': 'Group {group} — waiting for its own matches to finish',
  'tournament.active.ruleMirror': 'Mirror rotation in R2',
  'tournament.active.ruleNoSamePartner': 'Avoid the same partner',
  'tournament.active.ruleNoSameOpponent': 'Avoid the same opponent',
  'tournament.active.ruleBalanceHomeAway': 'Balance home/away',
  'tournament.active.ruleBalanceElo': 'Avoid lopsided ELO matches',
  'tournament.active.readyForFinals': 'Ready for the finals',
  'tournament.active.phaseAwaitingDraw': 'Phase {phase} is waiting for the draw',
  'tournament.active.readyForFinalsLead':
    "Every group match has been played. The finals are placed from the last phase's standings.",
  'tournament.active.phaseAwaitingDrawLead':
    'New groups and matches are drawn using the active rules.',
  'tournament.active.activeRules': 'Active rules:',
  'tournament.active.noRules': 'No rules active — fully random',
  'tournament.active.closeRules': 'Close',
  'tournament.active.editRules': 'Edit the rules',
  'tournament.active.toFinals': 'To the finals',
  'tournament.active.generateNextPhase': 'Generate the next phase',
  'tournament.active.modeVaried': 'Varied',
  'tournament.active.modeVariedDesc': 'Spread partners and opponents widely',
  'tournament.active.modeMirror': 'Mirror rotation',
  'tournament.active.modeMirrorDesc': 'Former partners become opponents',
  'tournament.active.tabPlan': 'Schedule',
  'tournament.active.tabPlanDesc': 'The whole tournament',
  'tournament.active.tabGroups': 'Groups',
  'tournament.active.tabStandings': 'Performance',
  'tournament.active.tabRanking': 'Result',
  'tournament.active.tabBanepad': 'Banepad',
  'tournament.active.tabLog': 'Log',
  'tournament.active.finishLead': 'What do you want to do with {name}?',
  'tournament.active.thisTournament': 'this tournament',
  'tournament.active.cancel': 'Cancel',
  'tournament.active.deleteTitle': 'Delete permanently?',
  'tournament.active.deleteBefore': 'Every match, result and statistic for',
  'tournament.active.theTournament': 'the tournament',
  'tournament.active.deleteAfter': 'will be deleted permanently. This cannot be undone.',
  'tournament.active.back': 'Back',
  'tournament.active.deletePermanently': 'Delete permanently',
  'tournament.active.startConfirmTitle': 'Start the tournament?',
  'tournament.active.startActivity1': 'This',
  'tournament.active.startActivityStrong': 'closes the sign-up',
  'tournament.active.startActivity2': 'and removes',
  'tournament.active.startActivity3':
    'from the activities. The setup is locked and the Banepad opens for results. You can set it back to «Ready to start» as long as no results have been entered.',
  'tournament.active.startPlain1': 'The setup',
  'tournament.active.startPlainStrong': 'is locked',
  'tournament.active.startPlain2':
    'and the Banepad opens for results. You can set the tournament back to «Ready to start» as long as no results have been entered.',
  'tournament.active.revertConfirmTitle': 'Back to ready to start?',
  'tournament.active.readyToStart': '«Ready to start»',
  'tournament.active.revertActivity1': 'The tournament is set back to',
  'tournament.active.revertActivity2': ', and',
  'tournament.active.theActivity': 'the activity',
  'tournament.active.revertActivity3': 'goes back into the activities with sign-up open again.',
  'tournament.active.revertPlain1': 'The tournament is set back to',
  'tournament.active.revertPlain2':
    '(a preview). You can adjust the groups, the draw and the setup again.',
  'tournament.active.revertConfirm': 'Set it back',
  'tournament.active.playerProfile': 'Player profile',
  'tournament.active.close': 'Close',
  'tournament.active.weekday.sun': 'Sun',
  'tournament.active.weekday.mon': 'Mon',
  'tournament.active.weekday.tue': 'Tue',
  'tournament.active.weekday.wed': 'Wed',
  'tournament.active.weekday.thu': 'Thu',
  'tournament.active.weekday.fri': 'Fri',
  'tournament.active.weekday.sat': 'Sat',
  'tournament.active.day': 'Day',
  'tournament.active.queueNumber': 'no. {number}',
  'tournament.active.standbyOnly': '· standby only',
  'tournament.active.levelPrefix': 'Level {level} · ',
  'tournament.active.eloValue': '{elo} ELO',
  'tournament.active.standbyDays': ' · {days}',
  'tournament.active.used': 'used',
  'tournament.active.availableToday': 'free today',
  'tournament.active.anotherDay': 'another day',
  'tournament.active.standbyHeading': 'Standby reserves',
  'tournament.active.standbySubheading': 'Can step in if somebody is injured',
  'tournament.active.standbyExplainer':
    'If somebody is injured, the system suggests the reserve closest in ELO to the player being replaced — first within ±100 (the earliest sign-up first if several), then ±200, ±300 and so on. That is why a reserve far down the list can be called in, if they are the closest in level. The numbers show the queue place (no.), level/ELO and a phone number for a quick call.',
  'tournament.active.noStandby': 'No standby reserves.',
  'tournament.active.alreadyUsed': 'Already used ({count})',
  'tournament.active.standbyFooter':
    "They are suggested first on a substitution (level match plus queue order). «Free today» means the reserve said they are available for today's date.",
  // ── /diagnostics/passkeys — the WebAuthn probe (#676) ─────────────
  'probe.title': 'Passkey diagnostics',
  'probe.lead':
    'Shows what this browser or app supports for passkeys. Nothing is sent to the server, and no account is used. Copy the report at the bottom into the issue.',
  'probe.reading': 'Reading …',
  'probe.absent': 'not present',
  'probe.threw': 'failed: {name}: {message}',
  'probe.unknown': 'unknown',
  'probe.yes': 'yes',
  'probe.no': 'no',
  'probe.env.title': 'Environment',
  'probe.env.origin': 'Origin',
  'probe.env.shell': 'Starpoint app',
  'probe.env.noShell': 'not in the app (ordinary browser)',
  'probe.env.webView': 'Android WebView',
  'probe.env.chromium': 'WebView / Chromium version',
  'probe.env.android': 'Android version',
  'probe.env.highEntropy': 'Details from the browser',
  'probe.env.userAgent': 'User agent',
  'probe.caps.title': 'Support',
  'probe.caps.publicKeyCredential': 'PublicKeyCredential',
  'probe.caps.credentials': 'navigator.credentials',
  'probe.caps.platformAuthenticator':
    'Built-in authenticator (isUserVerifyingPlatformAuthenticatorAvailable)',
  'probe.caps.conditionalMediation': 'Autofill (isConditionalMediationAvailable)',
  'probe.caps.clientCapabilities': 'getClientCapabilities',
  'probe.ceremony.title': 'Try a ceremony',
  'probe.ceremony.lead':
    'Press one at a time and note whether the phone shows a passkey sheet. The error name and the time below are the measurement.',
  'probe.ceremony.get': 'Sign in (get) for {rpId}',
  'probe.ceremony.create': 'Make a test passkey (create) for {rpId}',
  'probe.ceremony.createWarning':
    '«Make a test passkey» stores a real passkey on the device, labelled «Starpoint WebView probe». Delete it in the password manager afterwards.',
  'probe.ceremony.running': 'Waiting for the device …',
  'probe.ceremony.completed': 'Completed.',
  'probe.ceremony.unavailable': 'WebAuthn is not available here.',
  'probe.hint.completed':
    'The ceremony ran all the way through. The origin above is what the platform signed — it decides what the server would have to accept.',
  'probe.hint.notAllowed':
    'Refused or cancelled. If a passkey sheet appeared first, WebAuthn works here. If it came back instantly with no sheet, the request was refused.',
  'probe.hint.notSupported': 'This context cannot run the ceremony.',
  'probe.hint.security':
    'Refused by a security rule: the RP ID does not fit the origin, or the domain does not vouch for the app (assetlinks.json).',
  'probe.hint.aborted': 'Aborted before it finished.',
  'probe.hint.invalidState': 'The device already holds a passkey that was excluded.',
  'probe.hint.unavailable': 'The API does not exist in this context.',
  'probe.hint.other': 'Unexpected error — include its name and message in the report.',
  'probe.report.title': 'Report',
  'probe.report.lead': 'Everything above, as JSON. Copy all of it and paste it into the issue.',
  'probe.report.copy': 'Copy the report',
  'probe.report.copied': 'Copied.',
  'probe.report.copyFailed': 'Could not copy. Tap the field, select all and copy it yourself.',
  'probe.reload': 'Reload the page',
  'probe.back': 'To the member app',
};

export const LOCALES = ['nb', 'en'] as const;
export type Locale = (typeof LOCALES)[number];

/** Norwegian is the source and the fallback — see the header. */
export const DEFAULT_LOCALE: Locale = 'nb';

export const CATALOGS: Record<Locale, Record<MessageKey, string>> = { nb, en };

/** The BCP-47 tag for formatting. The catalog key is not one. */
export const INTL_TAG: Record<Locale, string> = { nb: 'nb-NO', en: 'en-GB' };
